/* ============================================
   ROOM DESIGNER — Core Application
   Asset file: custom-room-designer.js

   Script load order: math → storage → tools → mobile → door → render → wallseg → main → overlay
   Modules: RoomDesignerMath, RoomDesignerStorage, RoomDesignerTools,
            RoomDesignerMobile, RoomDesignerDoor, RoomDesignerRender,
            RoomDesignerWallSeg
   ============================================ */

/*
 * SECTION MAP — update line numbers when major sections shift
 * ─────────────────────────────────────────────────────────────
 * L47-273      Constructor + init() + resizeCanvas()
 * L275-1139    setupEventListeners() — keyboard, mouse, touch, wheel, popover handlers
 * L1141-1694   Tool switching + zoom/pan/snap + coordinate/measurement helpers
 * L1695-3120   Mouse handlers (handleMouseDown, _processDown, _processMove, _processUp)
 * L3122-3484   Eraser tool (subtract, flatten cutouts, rebuild room)
 * L3486-3920   Selection tool (box select, hit testing, delete/copy/paste)
 * L3922-4178   Room/wall management + save/load stubs → storage.js
 * L4180-4627   Geometry stubs → math.js/tools.js + room topology helpers
 * L4716-5715   Room finalization (overlap merge, door relinking, area calc)
 * L5716-6252   Measurements panel + undo/redo + clear all + status bar
 * L6253-7241   Rendering pipeline (_doRender) — calls into render/door/mobile/wallseg modules
 * L7243-7505   Helper stubs + geometry stubs + nudge selection
 * L7507-7546   Initialization (DOMContentLoaded → new RoomDesigner())
 *
 * Extracted to modules (loaded via <script defer> before this file):
 *   custom-room-designer-render.js   — drawRoom, drawGrid, selection handles, labels, objects
 *   custom-room-designer-door.js     — door tool, door drawing, swing preview, gap clearing
 *   custom-room-designer-mobile.js   — mobile toolbar, joystick, handle cycling, door phases
 *   custom-room-designer-wallseg.js  — wall segment tool, click handling, snap, rendering
 *
 * Delegating stubs are scattered throughout — look for one-liner methods
 * calling RoomDesignerMath.*, RoomDesignerStorage.*, or RoomDesignerTools.*
 * The real logic lives in the corresponding module file.
 */

class RoomDesigner {
  constructor() {
    this.canvas = document.getElementById('roomCanvas');
    this.ctx = this.canvas.getContext('2d');
    this.container = document.getElementById('canvasContainer');

    this.worldWidthFeet = 150;
    this.worldHeightFeet = 150;
    this.basePixelsPerFoot = 20;

    this.zoomLevel = 1.0;
    this.minZoom = 0.096;
    this.maxZoom = 30;
    this.panX = 0;
    this.panY = 0;
    this.isPanning = false;
    this.panStart = null;

    this.unit = localStorage.getItem('rd_unit') || 'feet-inches';
    console.log('[RD] metric-snap-v4 loaded, unit=' + this.unit);
    this.shiftHeld = false;

    this.mode = 'select';
    this.activeTool = 'select';
    this.rooms = [];
    this._clickableLabels = [];
    this.currentRoom = null;
    this.currentWall = null;
    this.mouseWorld = null;
    this.mouseScreen = null;
    this.hasPlacedFirstPoint = false;
    this._lastRoomClosedAt = 0;
    this.selectedElement = null;
    this.isDragging = false;
    this.dragStart = null;
    this.doorType = 'single';
    this.doorWidth = 36;
    this.objectType = 'door';
    this.rectWidth = 12;
    this.rectDepth = 12;
    this.circleDiameter = 12;
    this.roomType = 'add';
    this.objects = [];
    this._equipmentImages = {};
    this._equipmentLoading = false;
    this._brandColors = {}; // { vendorName: { powderCoat: '#hex', upholstery: '#hex' } }
    this.wallSegmentGroups = [];
    this.currentWallSegmentGroup = null;
    this.showMeasurements = localStorage.getItem('rd_showMeasurements') !== '0';
    this._measurementsVisible = false; // independent flag: true when user draws a measurement while wall dims are off
    this.showRoomLabels = localStorage.getItem('rd_showRoomLabels') !== '0';
    this.showGrid = localStorage.getItem('rd_showGrid') !== '0';
    this.wallThickness = 0.375; // 4.5 inches in feet
    this.wallSegmentThickness = 1/3; // 4 inches in feet (wall segments)
    this.hollowWalls = localStorage.getItem('rd_hollowWalls') !== '0';
    this.hideOverage = localStorage.getItem('rd_hideOverage') === '1';
    this.showSeamLines = localStorage.getItem('rd_showSeamLines') !== '0';
    this.showMaterialOverlay = localStorage.getItem('rd_showMaterialOverlay') !== '0';
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — product cards are no longer a setting; the
    // drawer slot went to Equipment Labels. Deliberately NOT read from storage:
    // anyone who switched the old toggle off would otherwise be stuck with the
    // cards gone forever, with nothing left in the UI to turn them back on.
    this.showProductCards = true;
    this.showEquipmentLabels = localStorage.getItem('rd_showEquipmentLabels') !== '0';
    this.glassLabels = localStorage.getItem('rd_glassLabels') !== '0';
    this._labelText = '';
    this._labelSize = 'medium';

    // Portal mode: set by rep-quote-gym-designer section (window.__rdPortalMode = true)
    if (window.__rdPortalMode) {
      this._repMode = true;
      this._portalMode = true;
    } else {
      // Rep mode: activated via ?rep=1, persisted in sessionStorage
      const _urlParams = new URLSearchParams(window.location.search);
      if (_urlParams.get('rep') === '1') {
        sessionStorage.setItem('rd_rep_mode', '1');
        // Strip ?rep=1 from URL, preserve hash (for #plan= share links)
        _urlParams.delete('rep');
        const _cleanSearch = _urlParams.toString();
        const _cleanUrl = window.location.pathname + (_cleanSearch ? '?' + _cleanSearch : '') + window.location.hash;
        history.replaceState(null, '', _cleanUrl);
      }
      this._repMode = sessionStorage.getItem('rd_rep_mode') === '1';
      this._portalMode = false;
    }

    this.canvasRotation = 0; // 0,1,2,3 → 0°,90°,180°,270°
    this._canvasRotationRad = 0; // current render angle (interpolated during animation)
    this._rotateAnimating = false;

    this.undoStack = [];
    this.redoStack = [];
    this._placementRedoStack = [];
    this._wheelRAF = null;
    this.doorPlacement = null;
    this._wsSnapSticky = null; // Sticky wall lock for wall segment placement
    this._wsLineSide = null;   // 'inside' or 'outside' — tracks which side of room wall a dividing line started on
    this._wsEscPendingDiscard = false; // Two-press Esc discard for invalid chain endpoint
    this._pinnedPopover = null;
    this._popoverTimers = {};
    this._popoverIdleTimer = null;

    this.selectionStart = null;
    this.selectionEnd = null;
    this.selectedWalls = new Set();
    this.selectedDoors = new Set();
    this.selectedObjects = new Set();
    this.isSelecting = false;
    this._selectCorner1 = null;
    this._selectPreExisting = null;
    this._boxSelectCooldownUntil = 0;
    this._selectClickStart = null;
    this._selectDragging = false;
    this._selectDragStart = null;
    this._selectDragDoorInfo = null;
    this._selectDragOrigPositions = null;
    this._selectResizing = false;
    this._selectResizeInfo = null;
    this._selectRotating = false;
    this._selectRotateInfo = null;
    this._turfLogoDrag = null;       // null | 'move' | 'rotate' | 'nw' | 'ne' | 'se' | 'sw'
    this._turfLogoDragObj = null;
    this._turfLogoDragStart = null;
    this._turfLogoDragOrigLogo = null;
    this._turfLogoDragGroupOrig = null;
    this._turfLogoFocusedIdx = null;   // primary logo index (gets handles) into focused obj's turfLogos[]
    this._turfLogoSelectedIdxs = new Set(); // all selected logo indices (shift-click multi-select)
    this._turfLogoDragLogoIdx = null;  // which logo in the array is being dragged
    this._clipboard = null;
    this._pasteCount = 0;
    this._pastePreview = null; // { objects, wallGroups, centroid, offset }

    this._wallEndpointDragging = false;
    this._wallEndpointDragInfo = null;
    this._wallEndpointFloating = false; // click-move-click mode (endpoint follows mouse without button held)
    this._wallGrabDragging = false;
    this._wallGrabDragInfo = null;
    this._wallCornerDragging = false;
    this._wallCornerDragInfo = null;
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — curved walls (grouped-facet model)
    this._curveBulgeDragging = false;
    this._curveBulgeDragInfo = null;
    // Edge auto-pan while drawing
    this._edgePanRaf = null;
    this._edgePanV = null;
    this._suppressEdgePan = false;
    // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
    this._doorSwingEdit = null;
    this.darkMode = localStorage.getItem('rd_darkMode') === '1';
    this.largeUI = localStorage.getItem('rd_largeUI') === '1';
    this.penDrawMode = 'rectangle';
    this._wallSegmentType = 'wall';
    this._rectCorner1 = null;
    this._rectCorner1Confirmed = false;
    this._rectCorner2 = null;
    this._rectAutoPlaced = false;
    this._rectIntroAnimating = false;
    this._rectIntroAnimStart = 0;
    this._rectLockAnimating = false;
    this._rectLockAnimStart = 0;
    this._rectLockAnimCorner = null;
    this._rectPulseTimer = null;
    this._checkPulseTimer = null;
    this._arrowPulseTimer = null;
    this._freehandAutoPlaced = false;
    this._freehandStartConfirmed = false;
    this._doorMobile = null;
    this._doorMobilePrompt = false;
    this._penCancelledIdle = false;

    // Mobile select handle state
    this._selectHandleMode = null;    // null | 'cycling' | 'wholeMove' | 'wallEndpoint' | 'wallCorner' | 'wallGrab' | 'resize' | 'rotate' | 'objectMove'
    this._selectHandleInfo = null;    // Context object for the active handle
    this._selectRotateDialActive = false;
    this._selectHandleCycleList = [];   // Ordered array of cycle item descriptors
    this._selectHandleCycleIndex = 0;   // Current position in cycle list

    // Touch state
    this._isMobile = window.matchMedia('(max-width: 768px)').matches && window.matchMedia('(pointer: coarse)').matches;
    this._isPinching = false;
    this._pinchStartDist = 0;
    this._pinchStartZoom = 1;
    this._pinchMidX = 0;
    this._pinchMidY = 0;
    this._touchStartPos = null;
    this._touchMoved = false;
    this._touchMovedDist = 0;
    this._lastTapTime = 0;
    this._lastTapPos = null;
    this._touchActiveId = null;
    this._shiftToggle = false;
    this._touchBoxSelect = false;
    this._touchFloorBoxSelect = false;
    this._mobilePopoverOpen = false;
    this._isMobileToolbar = false;
    this._panAnimId = null;

    // Overlay hooks (installed by custom-room-designer-overlay.js)
    this._drawRoomOverlayHook = null;  // called per-room after fill, before walls
    this._beforeRoomsHook = null;      // called before rooms draw (clear deferred queues)
    this._afterRoomsHook = null;       // called after all rooms, before wall segments
    this._afterWallSegmentsHook = null; // called after wall segments drawn (scissors on top)
    this._onZoomHook = null;           // called whenever zoom changes (buttons, keys, wheel, pinch)
    this._onPanHook = null;            // called whenever pan changes (drag, middle-click, pinch)
    this._drawBackgroundHook = null;   // called after grid, before rooms (background image)
    this._afterObjectsHook = null;     // called after objects drawn (turf overage on top)
    this._floorMode = false;           // when true, main mouse/touch handlers skip
    this._measurements = [];            // persistent measurement lines [{start, end, dist, id, offset}]
    this._measureNextId = 1;            // auto-incrementing ID for measurements
    this._selectedMeasurements = new Set(); // highlighted measurements (by id)
    this._measureStart = null;          // first click point of in-progress measurement
    this._measureEnd = null;            // second click point (set on completion)
    this._measurePhase = 0;            // 0=idle, 1=start placed, 2=end placed (choosing offset)
    this._measurePendingEnd = null;    // holds end point during phase 2
    this._measureDragging = null;      // {id, startOffset, baseline} while dragging label offset
    this._equipmentMode = false;        // when true, canvas clicks place equipment
    this._pendingEquipmentId = null;    // equipment ID selected in panel, awaiting placement
    this._pendingEquipmentPos = null;   // {x,y} locked position during rotation preview
    this._pendingEquipmentRotation = 0; // rotation angle in radians for placement preview
    this._lastEquipmentRotation = null; // remembers last-used angle for shift-click rapid stamping
    this._equipmentGhostBlocked = false; // true when ghost overlaps a wall (red tint, click blocked)
    this._equipmentDragBlocked = false;  // true when dragged equipment overlaps a wall (red tint, revert on drop)
    this._turfDragBlockedByTurf = false; // true when drag-block is due to turf-on-turf (needs full red, not split)
    this._equipmentNudgeBlocked = false; // true briefly when arrow key nudge is blocked by wall (red flash)
    this._equipmentResizeBlocked = false; // true when turf resize overlaps a wall (red tint, revert on release)

    // Turf draw state
    this._turfBrush = null;    // { productId, colorId, hex } when in turf-draw mode
    this._turfCorner1 = null;  // first corner world point during two-click draw

    // Pending ladder ghost placement state (two-click flow)
    this._pendingLadder = null;          // { turfObj, lengthFt, heightFt } or null
    this._pendingLadderPos = null;       // { x, y } turf-local position locked on first click
    this._pendingLadderGhostPos = null;  // { x, y } turf-local position during follow phase
    this._pendingLadderRotation = 0;     // rotation in radians (snaps to 90° increments)
    this._pendingLadderBlocked = false;  // true when ghost is outside turf bounds (red tint)

    // View-only hover callout state
    this._hoverCalloutObj = null;   // currently hovered equipment object
    this._hoverCalloutEl = null;    // DOM element of the callout
    this._hoverCalloutTimer = null; // debounce timer (180ms)
    this._hoverCalloutGraceTimer = null; // grace period for cursor travel to card

    this.init();
  }

  init() {
    // Mobile edit-mode gate: show overlay and skip full initialization
    const hasShareLink = window.location.hash && window.location.hash.startsWith('#plan=');
    if (this._isMobile && !hasShareLink && !this._mobileBypassUsed) {
      const loadOv = document.getElementById('rdLoadingOverlay');
      if (loadOv) {
        const elapsed = performance.now();
        setTimeout(() => { loadOv.classList.add('rd-loaded'); setTimeout(() => { loadOv.remove(); setTimeout(() => document.querySelector('.room-designer').classList.add('rd-ui-ready'), 200); }, 600); }, Math.max(0, 2650 - elapsed));
      }
      const blockOv = document.getElementById('rdMobileBlockOverlay');
      if (blockOv) blockOv.classList.add('visible');
      this._setupMobileBypass(blockOv);
      return;
    }

    // Module instantiation MUST happen before resizeCanvas() — resizeCanvas triggers
    // render() → _doRender() which calls methods installed by these modules.
    if (window.RoomDesignerRender) this._render = new RoomDesignerRender(this);
    if (window.RoomDesignerDoor) this._door = new RoomDesignerDoor(this);
    if (window.RoomDesignerWallSeg) this._wallSeg = new RoomDesignerWallSeg(this);
    if (window.RoomDesignerMobile) this._mobile = new RoomDesignerMobile(this);
    if (window.RoomDesignerEquipment) this._equipment = new RoomDesignerEquipment(this);
    this._loadEquipmentImages();
    this._loadTextureOverlays();
    this.resizeCanvas();
    // Center grid on mobile so the auto-placed dot lands dead-center, 3/5 up
    if (this._isMobile && window.innerWidth <= 480) {
      this._centerGridForMobile();
    }
    window.addEventListener('resize', () => this.resizeCanvas());
    this.setupEventListeners();
    if (this._portalMode) {
      document.querySelector('.room-designer').classList.add('portal-mode');
    }
    if (this.darkMode) {
      document.querySelector('.room-designer').classList.add('dark-mode');
      document.querySelectorAll('.theme-btn').forEach(b => b.classList.toggle('active', b.dataset.value === 'dark'));
    }
    if (this.largeUI) {
      document.querySelector('.room-designer').classList.add('large-ui');
      document.querySelectorAll('.size-btn').forEach(b => b.classList.toggle('active', b.dataset.value === 'large'));
    }
    // Restore unit selection UI
    document.querySelectorAll('.unit-btn').forEach(b => b.classList.toggle('active', b.dataset.value === this.unit));
    // Restore display toggle UI
    document.getElementById('toggleMeasurements').classList.toggle('active', this.showMeasurements);
    document.getElementById('toggleRoomLabels').classList.toggle('active', this.showRoomLabels);
    document.getElementById('toggleGrid').classList.toggle('active', this.showGrid);
    document.getElementById('toggleHollowWalls').classList.toggle('active', this.hollowWalls);
    document.getElementById('toggleHideOverage').classList.toggle('active', !this.hideOverage);
    document.getElementById('toggleSeamLines').classList.toggle('active', this.showSeamLines);
    document.getElementById('toggleMaterialOverlay').classList.toggle('active', this.showMaterialOverlay);
    const _eqLblBtn = document.getElementById('toggleEquipmentLabels');
    if (_eqLblBtn) _eqLblBtn.classList.toggle('active', this.showEquipmentLabels);
    // Storage module handles designs panel, auto-load, and auto-save.
    // It installs _serializeDesign, _loadDesignData, _triggerAutoSave, etc. on this instance.
    if (window.RoomDesignerStorage) {
      this._storage = new RoomDesignerStorage(this);
    } else {
      // Fallback if storage module not loaded
      this.startNewRoom();
    }
    // Default to hand tool on phone-sized screens (no blue highlight on load)
    if (this._isMobile && window.innerWidth <= 480) {
      this.setActiveTool('hand');
      document.getElementById('handToolBtn').classList.remove('active');
    } else {
      this.setActiveTool('select');
    }
    this._syncWallSegmentBtn();
    this.render();

    // Dismiss loading overlay for presentation mode share links.
    // Spinner shows while assets load behind it, up to 6s max.
    // If assets finish early, dismiss as soon as minimum display (1.5s) is met.
    // If assets take >6s, dismiss at 6s — remaining assets load progressively.
    const overlay = document.getElementById('rdLoadingOverlay');
    if (overlay) {
      const startTime = performance.now();
      const minDisplay = 1500;
      const maxDisplay = 6000;
      const hasShareLink = window.location.hash && window.location.hash.startsWith('#plan=');

      const _dismissOverlay = () => {
        overlay.classList.add('rd-loaded');
        setTimeout(() => {
          overlay.remove();
          setTimeout(() => document.querySelector('.room-designer').classList.add('rd-ui-ready'), 200);
        }, 600);
      };

      if (hasShareLink) {
        const _checkAssets = () => {
          let pending = 0;
          if (this._equipmentLoading) pending++;
          for (const obj of (this.objects || [])) {
            if (obj.type === 'equipment' && obj.equipmentId) {
              const entry = this._equipmentImages && this._equipmentImages[obj.equipmentId];
              if (!entry || !entry.loaded) pending++;
            }
          }
          const ov = window.roomDesignerOverlay;
          if (ov) {
            for (const room of this.rooms) {
              if (room.flooring) {
                const product = ov._getProduct(room.flooring.productId);
                if (product) {
                  const color = product.colors.find(c => c.id === room.flooring.colorId);
                  if (color && color.textureUrl) {
                    const entry = ov._textureImages[color.textureUrl];
                    if (entry && !entry.loaded && !entry.failed) pending++;
                  }
                }
              }
            }
          }
          return pending <= 0;
        };

        const _pollAssets = setInterval(() => {
          const elapsed = performance.now() - startTime;
          const pastMin = elapsed >= minDisplay;
          const pastMax = elapsed >= maxDisplay;
          if (pastMax || (pastMin && _checkAssets())) {
            clearInterval(_pollAssets);
            this.render();
            requestAnimationFrame(() => requestAnimationFrame(_dismissOverlay));
          }
        }, 300);
      } else {
        const remaining = Math.max(0, minDisplay - (performance.now() - startTime));
        setTimeout(_dismissOverlay, remaining);
      }
    } else {
      document.querySelector('.room-designer').classList.add('rd-ui-ready');
    }

  }

  _setupMobileBypass(overlay) {
    if (!overlay) return;
    const corners = [];
    const SEQUENCE = [0, 1, 2, 3]; // TL, TR, BR, BL (clockwise)
    let timer = null;
    overlay.addEventListener('click', (e) => {
      const rect = overlay.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const halfW = rect.width / 2;
      const halfH = rect.height / 2;
      // Grid: TL=0, TR=1, BL=2, BR=3
      const corner = (y < halfH ? 0 : 2) + (x >= halfW ? 1 : 0);
      // Map to clockwise order: TL=0, TR=1, BR=2, BL=3
      const mapped = [0, 1, 3, 2][corner];
      corners.push(mapped);
      clearTimeout(timer);
      timer = setTimeout(() => { corners.length = 0; }, 4000);
      if (corners.length >= 4) {
        const last4 = corners.slice(-4);
        if (last4[0] === 0 && last4[1] === 1 && last4[2] === 2 && last4[3] === 3) {
          overlay.classList.remove('visible');
          overlay.style.display = 'none';
          const root = this.canvas.closest('.room-designer');
          if (root) root.classList.remove('mobile-block-pending');
          this._mobileBypassUsed = true;
          this.init();
        }
      }
    });
  }

  resizeCanvas() {
    const dpr = window.devicePixelRatio || 1;
    this._dpr = dpr;
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    this.canvas.width = w * dpr;
    this.canvas.height = h * dpr;
    this.canvas.style.width = w + 'px';
    this.canvas.style.height = h + 'px';
    this._cssWidth = w;
    this._cssHeight = h;
    this.render();
  }

  /* ── EVENT LISTENERS ── */

  setupEventListeners() {
    this.canvas.addEventListener('mousedown', this.handleMouseDown.bind(this));
    this.canvas.addEventListener('mousemove', this.handleMouseMove.bind(this));
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — edge auto-pan must not depend on the canvas
    // being the topmost element under the cursor. .tool-controls covers the strip
    // from 98px to 12px off the RIGHT edge, so driving that way put the cursor on the
    // toolbar and the canvas stopped receiving mousemove — cursor updates dried up
    // and the pan stalled right where it was needed. Top/left/bottom have no such
    // overlay, which is why only the right edge felt reluctant.
    //
    // Tracking on the document instead means any overlay, now or later, can sit over
    // the canvas without starving the pan.
    document.addEventListener('mousemove', (e) => {
      if (!this._isDrawInProgress()) return;
      const r = this.canvas.getBoundingClientRect();
      if (!r.width || !r.height) return;
      this._lastMouseClientX = e.clientX; this._lastMouseClientY = e.clientY;
      const sx = e.clientX - r.left, sy = e.clientY - r.top;
      // The SCREEN point has to be updated here too, not only in the canvas handler.
      // The rAF tick re-runs _processMove at _lastMouseScreenX/Y every frame, so while
      // the pointer sat over the toolbar — the only overlay on any edge — those were
      // frozen at wherever it crossed underneath, ~98px in. The view kept sliding but
      // the rubber-band stopped following the cursor, which reads as the right edge
      // being sticky. Only the canvas handler may call _processMove for events it
      // already saw; for the rest, drive it from here so the preview tracks under any
      // overlay, pinned drawer included.
      if (sx >= 0 && sy >= 0 && sx <= r.width && sy <= r.height) {
        this._lastMouseScreenX = sx; this._lastMouseScreenY = sy;
        if (e.target !== this.canvas) this._processMove(sx, sy);
      }
      this._updateEdgeAutoPan(sx, sy, r.width, r.height);
    });
    // Stop only when the pointer has genuinely left the canvas AREA. A bare
    // mouseleave is the wrong signal: moving onto an overlaying sibling like the
    // toolbar fires it too, which killed the pan at the exact moment you were
    // driving toward the right edge to use it.
    const leaveIfOutside = (e) => {
      const r = this.canvas.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right ||
          e.clientY < r.top || e.clientY > r.bottom) this._stopEdgeAutoPan();
    };
    this.canvas.addEventListener('mouseleave', leaveIfOutside);
    this.canvas.addEventListener('pointerleave', leaveIfOutside);
    window.addEventListener('blur', () => this._stopEdgeAutoPan());
    window.addEventListener('resize', () => this._invalidateChromeRects());
    // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
    this.canvas.addEventListener('mouseup', this.handleMouseUp.bind(this));
    this.canvas.addEventListener('mouseleave', (e) => {
      if (this._hoverCalloutEl && (!e.relatedTarget || !this._hoverCalloutEl.contains(e.relatedTarget))) this._dismissHoverCallout();
    });
    document.addEventListener('mouseup', (e) => {
      if (e.target === this.canvas) return; // already handled by canvas listener
      if (this._wallEndpointDragging || this._wallCornerDragging || this._wallGrabDragging || this._curveBulgeDragging || this._selectDragging || this._selectResizing || this._selectRotating) {
        this._setOverlayPointerEvents(true);
        this._processUp();
      }
    });
    this.canvas.addEventListener('wheel', this.handleWheel.bind(this), { passive: false });
    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());

    // Touch events
    this.canvas.addEventListener('touchstart', this._handleTouchStart.bind(this), { passive: false });
    this.canvas.addEventListener('touchmove', this._handleTouchMove.bind(this), { passive: false });
    this.canvas.addEventListener('touchend', this._handleTouchEnd.bind(this), { passive: false });
    this.canvas.addEventListener('touchcancel', this._handleTouchCancel.bind(this), { passive: false });
    this.canvas.addEventListener('dblclick', (e) => {
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — double-click fills the area under the cursor
      // with the armed turf, the paint-bucket gesture. It reads cleanly against the
      // two-click rectangle: the first click of the pair has already set corner 1,
      // and filling clears it, so the half-started rectangle is abandoned rather
      // than left dangling.
      if (this.activeTool === 'turf-draw' && this._turfBrush) {
        e.preventDefault();
        const r = this.canvas.getBoundingClientRect();
        const world = this.screenToWorld(e.clientX - r.left, e.clientY - r.top);
        this._fillZoneWithTurf(world);
        return;
      }
      // Double-click to finish wall segment chain (connected or free-standing partition)
      if (this.activeTool === 'pen' && this.penDrawMode === 'wallSegment') {
        const hidden = this._finishWallSegmentChain();
        if (hidden) this.updateStatus('Dividing line hidden — enable Seam Lines in Settings to see it', { urgent: true });
        else this.updateStatus('Interior wall finished. Click to start a new one.');
        this.render();
        return;
      }
      if (this.activeTool !== 'select') return;
      if (this._floorMode) return;
      const rect = this.canvas.getBoundingClientRect();
      const sx = e.clientX - rect.left, sy = e.clientY - rect.top;
      const world = this.screenToWorld(sx, sy);
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — double-click an area label to retype it.
      // Deliberately NOT a single click, which is how a space name is renamed:
      // a label is a draggable object first, and a single click has to select it.
      // Customize only, matching where the tool and the hit-test live.
      if (window.roomDesignerOverlay && window.roomDesignerOverlay.currentStep === 'finishing') {
        const lblHit = this._hitTestAreaLabel(world);
        if (lblHit) { e.preventDefault(); this._openAreaLabelEditor(lblHit); return; }
      }
      const hit = this._hitTestSelect(world);
      if (hit && hit.type === 'door' && hit.item.type !== 'roller') {
        const room = this._findRoomForWall(hit.item.wall);
        if (room) {
          this._doorSwingEdit = { door: hit.item, room };
          this.selectedWalls = new Set(); this.selectedDoors = new Set([hit.item]); this.selectedObjects = new Set();
          this._updateActionButtons();
          this.updateStatus('Click to choose new swing direction, or click on the door line for closed. Esc to cancel.');
          this.render();
        }
      }
    });

    document.addEventListener('keydown', (e) => {
      // Let inputs/textareas handle their own keys (except Escape)
      const tag = e.target && e.target.tagName;
      if ((tag === 'INPUT' || tag === 'TEXTAREA') && e.key !== 'Escape') return;
      // View-only mode: only allow zoom keys and Escape
      if (this._viewOnly) {
        const allowed = ['+', '-', '=', '_', 'Escape', 'Shift', 'r', 'R'];
        if (!allowed.includes(e.key)) return;
      }
      if (e.key === 'Shift') { this.shiftHeld = true; }
      if (e.key === '+' || e.key === '=') { e.preventDefault(); this.zoom(1.2); }
      if (e.key === '-' || e.key === '_') { e.preventDefault(); this.zoom(1 / 1.2); }
      if ((e.key === 'r' || e.key === 'R') && !e.ctrlKey && !e.metaKey) { e.preventDefault(); this._rotateCanvas90(); }
      if (e.key === 'Escape') {
        if (document.fullscreenElement) { document.exitFullscreen(); return; }
        e.preventDefault(); this.handleEscape();
      }
      if (e.key === 'Enter' && this.activeTool === 'pen' && this.penDrawMode === 'wallSegment') {
        const tag = (e.target && e.target.tagName) || '';
        if (tag !== 'INPUT' && tag !== 'TEXTAREA') {
          e.preventDefault();
          this._finishWallSegmentChain();
          this.setActiveTool(this._isMobile && window.innerWidth <= 480 ? 'hand' : 'select');
        }
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        if (e.shiftKey) { this.redo(); }
        else {
          if (this._undoWallSegment && this._undoWallSegment()) { /* handled at chain level */ }
          else this.undo();
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') { e.preventDefault(); this.redo(); }
      if ((e.key === 'Delete' || e.key === 'Backspace') && this.mode === 'measure') {
        if (this._deleteSelectedMeasurements()) { e.preventDefault(); return; }
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && this.activeTool === 'select') {
        // Block deletion on Customize tab (selection is for customization, not removal) — except when a turf logo is focused
        if (window.roomDesignerOverlay && window.roomDesignerOverlay.currentStep === 'finishing' && this._turfLogoFocusedIdx == null && !this._selectionIsOnlyLabels()) { e.preventDefault(); return; }
        if (this._selectedMeasurements.size > 0) { e.preventDefault(); this._deleteSelectedMeasurements(); return; }
        e.preventDefault();
        const hasFloor = this._floorMode && window.roomDesignerOverlay && window.roomDesignerOverlay._floorSelections.length > 0;
        const hasObjects = this.selectedObjects.size > 0 || this.selectedDoors.size > 0 || this.selectedWalls.size > 0;
        this._deleteWithEquipmentCheck(hasFloor, hasObjects);
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'g' || e.key === 'G') && this.activeTool === 'select') {
        e.preventDefault();
        if (e.shiftKey) { this.ungroupSelected(); } else { this.groupSelected(); }
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C') && this.activeTool === 'select') {
        const tag = (e.target && e.target.tagName) || '';
        if (tag !== 'INPUT' && tag !== 'TEXTAREA') { e.preventDefault(); this.copySelected(); }
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'v' || e.key === 'V')) {
        const tag = (e.target && e.target.tagName) || '';
        if (tag !== 'INPUT' && tag !== 'TEXTAREA') { e.preventDefault(); this.pasteClipboard(); }
      }
      // Arrow keys — nudge selected objects (1" / 1cm default, Shift = 1ft / 0.25m)
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        const tag = (e.target && e.target.tagName) || '';
        if (tag !== 'INPUT' && tag !== 'TEXTAREA' && this.activeTool === 'select' && this.selectedObjects.size > 0) {
          const _onFinishing = window.roomDesignerOverlay && window.roomDesignerOverlay.currentStep === 'finishing';
          // Turf logo nudge: arrow keys move selected logos within the turf
          if (this._turfLogoFocusedIdx != null && this.selectedObjects.size === 1) {
            const _tObj = [...this.selectedObjects][0];
            if (_tObj.type === 'turf' && _tObj.turfLogos) {
              e.preventDefault();
              const step = e.shiftKey ? 1 : 0.083333333; // 1ft or 1"
              let dx = 0, dy = 0;
              if (e.key === 'ArrowUp') dy = -step;
              else if (e.key === 'ArrowDown') dy = step;
              else if (e.key === 'ArrowLeft') dx = -step;
              else if (e.key === 'ArrowRight') dx = step;
              // Convert world delta to turf-local delta
              const _or = _tObj.rotation || 0;
              const _co = Math.cos(-_or), _so = Math.sin(-_or);
              const ldx = dx * _co - dy * _so, ldy = dx * _so + dy * _co;
              if (!this._arrowNudgeTimer) this.saveUndoState();
              clearTimeout(this._arrowNudgeTimer);
              this._arrowNudgeTimer = setTimeout(() => { this._arrowNudgeTimer = null; }, 500);
              const idxs = this._turfLogoSelectedIdxs.size > 0 ? this._turfLogoSelectedIdxs : new Set([this._turfLogoFocusedIdx]);
              for (const idx of idxs) {
                const logo = _tObj.turfLogos[idx];
                if (!logo) continue;
                logo.x = (logo.x || 0) + ldx;
                logo.y = (logo.y || 0) + ldy;
              }
              this.render();
            }
          } else if (!_onFinishing || this._selectionIsOnlyLabels()) {
            // Normal object nudge — blocked on the Customize tab, EXCEPT for area
            // labels, whose tool lives on that tab: nudging is how you settle one
            // against a zone edge, and the block exists to stop you shoving the
            // flooring around while customizing it, not to pin an annotation.
            e.preventDefault();
            const metric = this.unit === 'meters' || this.unit === 'm-cm' || this.unit === 'cm';
            let step = e.shiftKey ? (metric ? 0.82020997 : 1) : (metric ? 0.032808399 : 0.083333333);
            // Platform on tile flooring: nudge by tile size
            let _nudgeTileStep = 0;
            if (this.selectedObjects.size === 1) {
              const _nObj = [...this.selectedObjects][0];
              if (_nObj.type === 'platform' && window.roomDesignerOverlay) {
                let _nRoom = null;
                for (const room of (this.rooms || [])) {
                  if (!room.closed) continue;
                  const geo = this._getRoomGeoCache(room);
                  if (geo.roomPolygon && this.pointInPolygon(_nObj.x, _nObj.y, geo.roomPolygon)) { _nRoom = room; break; }
                }
                const _nZone = _nRoom ? window.roomDesignerOverlay._getZoneFlooringAtPoint(_nRoom, _nObj.x, _nObj.y) : null;
                if (_nZone && _nZone.product.type === 'tile') {
                  _nudgeTileStep = _nZone.product.widthFt || 2;
                  step = _nudgeTileStep;
                }
              }
            }
            let dx = 0, dy = 0;
            if (e.key === 'ArrowUp') dy = -step;
            else if (e.key === 'ArrowDown') dy = step;
            else if (e.key === 'ArrowLeft') dx = -step;
            else if (e.key === 'ArrowRight') dx = step;
            // Save undo once per burst (debounced)
            if (!this._arrowNudgeTimer) this.saveUndoState();
            clearTimeout(this._arrowNudgeTimer);
            this._arrowNudgeTimer = setTimeout(() => { this._arrowNudgeTimer = null; }, 500);
            this.selectedObjects.forEach(obj => { obj.x += dx; obj.y += dy; });
            // Block nudge if any equipment or turf now overlaps a wall
            let _nudgeBlocked = false;
            this.selectedObjects.forEach(obj => {
              if (_nudgeBlocked) return;
              if (obj.type === 'equipment') {
                const tw = obj.tightWidth || obj.width, td = obj.tightDepth || obj.depth;
                const info = this._equipmentImages && this._equipmentImages[obj.equipmentId];
                if (this._equipmentOverlapsWall(obj.x, obj.y, tw, td, obj.rotation || 0, info || null)) _nudgeBlocked = true;
              } else if (obj.type === 'turf' || obj.type === 'platform') {
                if (this._equipmentOverlapsWall(obj.x, obj.y, obj.width, obj.depth, obj.rotation || 0, null) || this._turfOverlapsTurf(obj.x, obj.y, obj.width, obj.depth, obj.rotation || 0, obj)) _nudgeBlocked = true;
                if (!_nudgeBlocked && obj.type === 'platform' && this._platformOverlapsPlatform(obj.x, obj.y, obj.width, obj.depth, obj.rotation || 0, obj)) _nudgeBlocked = true;
              }
            });
            if (_nudgeBlocked) {
              this.selectedObjects.forEach(obj => { obj.x -= dx; obj.y -= dy; });
              this._equipmentNudgeBlocked = true;
              clearTimeout(this._equipmentNudgeBlockedTimer);
              this._equipmentNudgeBlockedTimer = setTimeout(() => { this._equipmentNudgeBlocked = false; this.render(); }, 300);
              this.updateStatus('Cannot move — overlaps a wall, turf, or platform', { error: true });
            }
            // Show proximity dims briefly after nudge
            this._equipmentNudgeShowDims = true;
            clearTimeout(this._equipmentNudgeDimsTimer);
            this._equipmentNudgeDimsTimer = setTimeout(() => { this._equipmentNudgeShowDims = false; this.render(); }, 1000);
            this.render();
          }
        }
      }
      // Single-key tool shortcuts (ignore when typing in inputs or when modifier keys held)
      if (!e.ctrlKey && !e.metaKey && !e.altKey) {
        const tag = (e.target && e.target.tagName) || '';
        if (tag !== 'INPUT' && tag !== 'TEXTAREA') {
          const k = e.key.toLowerCase();
          if (k === 's') { e.preventDefault(); this.setActiveTool('select'); }
          else if (k === 'd') { e.preventDefault(); document.getElementById('penToolBtn').click(); }
          else if (k === 'o') { e.preventDefault(); document.getElementById('objectsToolBtn').click(); }
          else if (k === 'l') { e.preventDefault(); const _lb = document.getElementById('labelToolBtn'); if (_lb && _lb.style.display !== 'none') _lb.click(); }
          else if (k === 'e') { e.preventDefault(); this.setActiveTool('eraser'); }
          else if (k === 'm') { e.preventDefault(); this.setActiveTool('measure'); }
          else if (k === 'p') { e.preventDefault(); this.setActiveTool('hand'); }
          else if (e.key === ']' && this.activeTool === 'select') { e.preventDefault(); this.sendToFront(); }
          else if (e.key === '[' && this.activeTool === 'select') { e.preventDefault(); this.sendToBack(); }
          else if (e.key === 'F11') { e.preventDefault(); this._toggleFullscreen(); }
          else if (k === 'h') {
            e.preventDefault();
            const kd = document.getElementById('shortcutsDrawer'); const kt = document.getElementById('shortcutsToggle');
            if (kd && kd.classList.contains('open')) { kd.classList.remove('open'); if (kt) kt.classList.remove('open'); }
            else if (kd) {
              // Close other drawers
              const sd = document.getElementById('settingsDrawer'); const st = document.getElementById('settingsToggle');
              if (sd) { sd.classList.remove('open'); } if (st) st.classList.remove('open');
              const dd = document.getElementById('designsDrawer'); const dt = document.getElementById('designsToggle');
              if (dd) { dd.classList.remove('open'); } if (dt) dt.classList.remove('open');
              kd.classList.add('open'); if (kt) kt.classList.add('open');
            }
          }
        }
      }
    });

    document.addEventListener('keyup', (e) => {
      if (e.key === 'Shift') { this.shiftHeld = this._shiftToggle; }
    });

    const allPopIds = ['penPopover', 'objectsPopover', 'labelPopover', 'zoomPopover', 'floorRotatePopover', 'bgImagePopover'];
    const _isDragging = () => this._selectRotating || this._selectDragging || this._selectResizing || this._wallEndpointDragging || this._wallEndpointFloating || this._wallCornerDragging || this._wallGrabDragging || this._curveBulgeDragging || this._turfLogoDrag;
    const showPop = (id) => {
      if (_isDragging()) return;
      clearTimeout(this._popoverTimers[id]);
      this._popoverZCounter = (this._popoverZCounter || 20) + 1;
      const el = document.getElementById(id);
      el.style.zIndex = this._popoverZCounter;
      el.classList.add('visible');
    };

    // Hover-show with dwell delay: if another popover is already visible,
    // require a brief dwell (~350ms) before opening a new one.
    // This prevents accidental triggers when the mouse crosses a button
    // while moving toward an already-open popover.
    this._popoverHoverTimer = null;
    const hoverShowPop = (id, setupFn) => {
      // Suppress popovers during active drag interactions (rotation, resize, move)
      if (this._selectRotating || this._selectDragging || this._selectResizing || this._wallEndpointDragging || this._wallEndpointFloating || this._wallCornerDragging || this._wallGrabDragging || this._curveBulgeDragging) return;
      clearTimeout(this._popoverHoverTimer);
      const anotherVisible = allPopIds.some(otherId => {
        if (otherId === id) return false;
        const el = document.getElementById(otherId);
        return el && el.classList.contains('visible');
      });
      if (anotherVisible) {
        this._popoverHoverTimer = setTimeout(() => { if (setupFn) setupFn(); showPop(id); }, 350);
      } else {
        if (setupFn) setupFn();
        showPop(id);
      }
    };
    const cancelHoverShow = () => { clearTimeout(this._popoverHoverTimer); };

    const popoverPinNames = { zoomPopover: 'zoom', penPopover: 'pen', objectsPopover: 'door', labelPopover: 'label', floorRotatePopover: 'floorRotate', bgImagePopover: 'bgImage', measurePopover: 'measure' };
    const hidePop = (id) => {
      this._popoverTimers[id] = setTimeout(() => {
        if (this._pinnedPopover !== popoverPinNames[id]) {
          document.getElementById(id).classList.remove('visible');
        }
      }, 250);
    };

    const syncPinIcons = () => {
      const toolMap = { pen: 'penToolBtn', door: 'objectsToolBtn', label: 'labelToolBtn', zoom: 'zoomToggleBtn', floorRotate: 'floorRotateBtn', bgImage: 'bgImageBtn', measure: 'measureToolBtn' };
      for (const [name, id] of Object.entries(toolMap)) {
        const el = document.getElementById(id);
        if (el) el.classList.toggle('pinned', this._pinnedPopover === name);
      }
    };

    // Expose popover helpers so overlay can hook into the same system
    this._showPop = showPop;
    this._hidePop = hidePop;
    this._hoverShowPop = hoverShowPop;
    this._cancelHoverShow = cancelHoverShow;
    this._syncPinIcons = syncPinIcons;

    const pinToggle = (name, popId) => {
      const all = { zoom: 'zoomPopover', pen: 'penPopover', door: 'objectsPopover', label: 'labelPopover', floorRotate: 'floorRotatePopover', bgImage: 'bgImagePopover', measure: 'measurePopover' };
      Object.entries(all).forEach(([k, v]) => {
        if (k !== name) { document.getElementById(v).classList.remove('visible'); clearTimeout(this._popoverTimers[v]); }
      });
      // Close any open drawers (settings, designs, shortcuts) when pinning a popover
      const drawers = [
        ['settingsDrawer', 'settingsToggle', 'settingsDrawer'],
        ['designsDrawer', 'designsToggle', 'designsDrawer'],
        ['shortcutsDrawer', 'shortcutsToggle', 'shortcutsDrawer']
      ];
      for (const [dId, tId, timerId] of drawers) {
        const d = document.getElementById(dId); const t = document.getElementById(tId);
        if (d) d.classList.remove('open'); if (t) t.classList.remove('open');
        clearTimeout(this._popoverTimers[timerId]);
      }
      if (this._pinnedPopover === name) {
        this._pinnedPopover = null;
        document.getElementById(popId).classList.remove('visible');
      } else {
        this._pinnedPopover = name;
        showPop(popId);
      }
      // When zoom or floorRotate is pinned, deactivate all other tool buttons
      const toolBtnIds = ['selectToolBtn', 'handToolBtn', 'penToolBtn', 'objectsToolBtn', 'labelToolBtn', 'eraserBtn', 'measureToolBtn'];
      if (this._pinnedPopover === 'zoom') {
        this.activeTool = 'select';
        this.mode = 'select';
        this.canvas.style.cursor = 'default';
        this.currentWall = null;
        this.hasPlacedFirstPoint = false;
        toolBtnIds.forEach(id => { const _b = document.getElementById(id); if (_b) _b.classList.remove('active'); });
        document.getElementById('zoomToggleBtn').classList.add('active');
      } else if (this._pinnedPopover === 'bgImage') {
        this._preBgTool = this.activeTool;
        this._preBgMode = this.mode;
        this.activeTool = 'select';
        this.mode = 'select';
        this.canvas.style.cursor = 'default';
        toolBtnIds.forEach(id => { const _b = document.getElementById(id); if (_b) _b.classList.remove('active'); });
        document.getElementById('zoomToggleBtn').classList.remove('active');
      } else if (this._pinnedPopover === 'floorRotate') {
        // Save tool before compass, switch to pointer behavior (no button lit)
        this._preCompassTool = this.activeTool;
        this._preCompassMode = this.mode;
        this.activeTool = 'select';
        this.mode = 'select';
        this.canvas.style.cursor = 'default';
        toolBtnIds.forEach(id => { const _b = document.getElementById(id); if (_b) _b.classList.remove('active'); });
        document.getElementById('zoomToggleBtn').classList.remove('active');
      } else {
        // Restore tool only when actually closing a popover (not switching to another)
        if (!this._pinnedPopover) {
          const savedTool = this._preCompassTool || this._preBgTool;
          const savedMode = this._preCompassMode || this._preBgMode;
          if (savedTool) {
            this.activeTool = savedTool;
            this.mode = savedMode || savedTool;
            if (this.activeTool === 'hand') this.canvas.style.cursor = 'grab';
            else if (this.activeTool === 'roller' && window.roomDesignerOverlay) this.canvas.style.cursor = window.roomDesignerOverlay._paintRollerCursor;
            else this.canvas.style.cursor = 'default';
          }
          document.getElementById('selectToolBtn').classList.toggle('active', this.activeTool === 'select');
          document.getElementById('handToolBtn').classList.toggle('active', this.activeTool === 'hand');
        }
        this._preCompassTool = null;
        this._preCompassMode = null;
        this._preBgTool = null;
        this._preBgMode = null;
        document.getElementById('zoomToggleBtn').classList.remove('active');
      }
      // Sync rotate button active state (created by overlay module)
      const rotBtn = document.getElementById('floorRotateBtn');
      if (rotBtn) rotBtn.classList.toggle('active', this._pinnedPopover === 'floorRotate');
      // Sync background image button active state (created by background module)
      const bgBtn = document.getElementById('bgImageBtn');
      if (bgBtn) bgBtn.classList.toggle('active', this._pinnedPopover === 'bgImage');
      syncPinIcons();
      if (this._syncDrawerPins) this._syncDrawerPins();
    };
    this._pinToggle = pinToggle;

    ['zoomPopover', 'penPopover', 'objectsPopover', 'measurePopover'].forEach(id => {
      const el = document.getElementById(id);
      if (!el || this._isMobile) return;
      el.addEventListener('mouseenter', () => clearTimeout(this._popoverTimers[id]));
      el.addEventListener('mouseleave', () => {
        if (this._pinnedPopover !== popoverPinNames[id]) hidePop(id);
      });
    });

    const zoomToggleBtn = document.getElementById('zoomToggleBtn');
    zoomToggleBtn.addEventListener('click', () => {
      pinToggle('zoom', 'zoomPopover');
      if (this._isMobileToolbar) {
        this._updateMobilePopoverState();
      }
    });
    if (!this._isMobile) {
      zoomToggleBtn.addEventListener('mouseenter', () => { if (this._pinnedPopover !== 'zoom') hoverShowPop('zoomPopover'); });
      zoomToggleBtn.addEventListener('mouseleave', () => { cancelHoverShow(); if (this._pinnedPopover !== 'zoom') hidePop('zoomPopover'); });
    }
    const zoomInBtn = document.getElementById('zoomInBtn');
    const zoomOutBtn = document.getElementById('zoomOutBtn');
    const zoomFitBtn = document.getElementById('zoomFitBtn');
    zoomInBtn.addEventListener('click', () => { if (this._isMobile) this._tapFlash(zoomInBtn); this.zoom(1.3); });
    zoomOutBtn.addEventListener('click', () => { if (this._isMobile) this._tapFlash(zoomOutBtn); this.zoom(1 / 1.3); });
    zoomFitBtn.addEventListener('click', () => {
      if (this._isMobile) this._tapFlash(zoomFitBtn);
      this.fitToView();
      if (this._isMobileToolbar) {
        // Switch to hand tool for easy panning, keep zoom button blue
        this._zoomFitPanMode = true;
        this.activeTool = 'hand';
        this.mode = 'hand';
        this.canvas.style.cursor = 'grab';
        this.currentWall = null;
        this.hasPlacedFirstPoint = false;
        this._dismissMobilePopovers();
        document.getElementById('penToolBtn').classList.remove('active');
        document.getElementById('objectsToolBtn').classList.remove('active');
        document.getElementById('handToolBtn').classList.remove('active');
        document.getElementById('selectToolBtn').classList.remove('active');
        document.getElementById('zoomToggleBtn').classList.add('active');
        this._updateJoystickVisibility();
        this._updateFloatingDelete();
        this._updateFloatingCancel();
        this._updateFloatingRepeat();
      }
    });
    const fullscreenBtn = document.getElementById('fullscreenBtn');
    if (fullscreenBtn) {
      fullscreenBtn.addEventListener('click', () => this._toggleFullscreen());
      document.addEventListener('fullscreenchange', () => {
        const isFs = !!document.fullscreenElement;
        fullscreenBtn.title = isFs ? 'Exit Fullscreen (Esc)' : 'Fullscreen (F11)';
        fullscreenBtn.innerHTML = isFs
          ? '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M4 14h3a2 2 0 012 2v3M20 10h-3a2 2 0 01-2-2V5M14 20v-3a2 2 0 012-2h3M10 4v3a2 2 0 01-2 2H5"/></svg>'
          : '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M8 3H5a2 2 0 00-2 2v3M21 8V5a2 2 0 00-2-2h-3M16 21h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"/></svg>';
        this.resizeCanvas();
        this.render();
      });
    }
    const rotateViewBtn = document.getElementById('rotateViewBtn');
    if (rotateViewBtn) rotateViewBtn.addEventListener('click', () => { if (this._isMobile) this._tapFlash(rotateViewBtn); this._rotateCanvas90(); });
    const voRotateViewBtn = document.getElementById('voRotateViewBtn');
    if (voRotateViewBtn) voRotateViewBtn.addEventListener('click', () => { if (navigator.vibrate) navigator.vibrate(50); voRotateViewBtn.classList.add('tap-flash'); setTimeout(() => voRotateViewBtn.classList.remove('tap-flash'), 180); this._rotateCanvas90(); });
    document.getElementById('selectToolBtn').addEventListener('click', () => {
      if (this._isMobileToolbar && this.activeTool === 'select') {
        const hasSelection = this.selectedWalls.size > 0 || this.selectedDoors.size > 0 || this.selectedObjects.size > 0;
        if (hasSelection) {
          // Second tap with selection: deselect all, enter silent pan mode
          this._deactivateSelectHandle();
          this.selectedWalls = new Set();
          this.selectedDoors = new Set();
          this.selectedObjects = new Set();
          this.selectionStart = null;
          this.selectionEnd = null;
          this.isSelecting = false;
          this._selectCorner1 = null;
          this.activeTool = 'hand';
          this.mode = 'hand';
          this.canvas.style.cursor = 'grab';
          document.getElementById('selectToolBtn').classList.remove('active');
          // Don't highlight hand tool button — silent pan mode
          this._updateJoystickVisibility();
          this._updateSelectionStatus();
          this.render();
          return;
        }
      }
      this.setActiveTool('select');
    });
    document.getElementById('handToolBtn').addEventListener('click', () => this.setActiveTool('hand'));

    this._penToolClicked = false;
    this._mobileActionChosen = false;
    this._doorToolClicked = false;
    this._mobileObjectChosen = false;
    const syncPenButtons = () => {
      if (this._penToolClicked && (this.activeTool === 'pen' || (this.activeTool === 'eraser' && this._isMobileToolbar))) {
        // On mobile with eraser active, don't highlight rectangle/freehand
        const eraserActive = this.activeTool === 'eraser';
        document.querySelectorAll('.pen-draw-mode-btn').forEach(b => {
          const match = b.dataset.value === this.penDrawMode;
          const segMatch = b.dataset.value !== 'wallSegment' || (b.dataset.segType || 'wall') === this._wallSegmentType;
          b.classList.toggle('active', !eraserActive && !this._penCancelledIdle && match && segMatch);
        });
        document.querySelectorAll('.pen-mode-btn').forEach(b => {
          if (b.dataset.value === 'erase') {
            b.classList.toggle('active', eraserActive);
          } else if ((this._isMobileToolbar && !this._mobileActionChosen) || this._penCancelledIdle) {
            b.classList.remove('active');
          } else {
            b.classList.toggle('active', this.activeTool === 'pen' && b.dataset.value === this.roomType);
          }
        });
        // Show/hide action options based on draw method vs eraser or wallSegment mode
        const hideActions = eraserActive || this.penDrawMode === 'wallSegment';
        document.getElementById('penActionOptions').style.display = hideActions ? 'none' : '';
        // Hide pen badge in wallSegment mode
        const penBadge = document.getElementById('penBadge');
        if (penBadge && this.penDrawMode === 'wallSegment') penBadge.style.display = 'none';
      } else {
        document.querySelectorAll('.pen-draw-mode-btn').forEach(b => b.classList.remove('active'));
        document.querySelectorAll('.pen-mode-btn').forEach(b => b.classList.remove('active'));
        // Hide action options when no tool is active
        document.getElementById('penActionOptions').style.display = 'none';
      }
    };
    const syncObjectsButtons = () => {
      if (this._doorToolClicked && this.activeTool === 'door') {
        document.querySelectorAll('.objects-type-btn').forEach(b => b.classList.toggle('active', b.dataset.value === this.objectType));
        // On mobile, only highlight door sub-type if a door placement is active (user has picked a sub-type)
        if (this._isMobileToolbar && this.objectType === 'door' && !this._doorMobile) {
          document.querySelectorAll('.door-type-btn').forEach(b => b.classList.remove('active'));
        } else {
          document.querySelectorAll('.door-type-btn').forEach(b => b.classList.toggle('active', b.dataset.value === this.doorType));
        }
        document.getElementById('objectsDoorOptions').style.display = this.objectType === 'door' ? '' : 'none';
        document.getElementById('objectsRectOptions').style.display = this.objectType === 'rectangle' ? '' : 'none';
        document.getElementById('objectsCircleOptions').style.display = this.objectType === 'circle' ? '' : 'none';
      } else if (this._isMobileToolbar) {
        // Mobile first open: nothing pre-selected, no sub-options visible
        document.querySelectorAll('.objects-type-btn').forEach(b => b.classList.remove('active'));
        document.querySelectorAll('.door-type-btn').forEach(b => b.classList.remove('active'));
        document.getElementById('objectsDoorOptions').style.display = 'none';
        document.getElementById('objectsRectOptions').style.display = 'none';
        document.getElementById('objectsCircleOptions').style.display = 'none';
      } else {
        document.querySelectorAll('.objects-type-btn').forEach(b => b.classList.remove('active'));
        document.querySelectorAll('.door-type-btn').forEach(b => b.classList.remove('active'));
        document.getElementById('objectsDoorOptions').style.display = 'none';
        document.getElementById('objectsRectOptions').style.display = 'none';
        document.getElementById('objectsCircleOptions').style.display = 'none';
      }
    };

    const penToolBtn = document.getElementById('penToolBtn');
    penToolBtn.addEventListener('click', () => {
      // Always clear rect pulse hint when draw button is tapped
      if (this._rectPulseTimer) { clearTimeout(this._rectPulseTimer); this._rectPulseTimer = null; }
      penToolBtn.classList.remove('rect-pulse-hint', 'rect-pulse-subtract');
      // Mobile: first open just shows popover without activating tool or selecting defaults
      if (this._isMobileToolbar && ((this.activeTool !== 'pen' && this.activeTool !== 'eraser') || !this._penToolClicked)) {
        this._penToolClicked = false;
        this._mobileActionChosen = false;
        this._doorToolClicked = false;
        document.getElementById('penActionOptions').style.display = 'none';
        syncObjectsButtons();
        pinToggle('pen', 'penPopover');
        syncPenButtons();
        this._updateMobilePopoverState();
        return;
      }
      this._penToolClicked = true;
      if (this.activeTool !== 'pen') this.setActiveTool('pen');
      // If already pinned, unpin; otherwise pin (keeps popover open after hover)
      pinToggle('pen', 'penPopover');
      syncPenButtons();
      if (this._isMobileToolbar) {
        this._updateMobilePopoverState();
      }
    });
    if (!this._isMobile) {
      penToolBtn.addEventListener('mouseenter', () => { if (this._pinnedPopover !== 'pen') hoverShowPop('penPopover', syncPenButtons); });
      penToolBtn.addEventListener('mouseleave', () => { cancelHoverShow(); if (this._pinnedPopover !== 'pen') hidePop('penPopover'); });
    }
    document.getElementById('eraserBtn').addEventListener('click', () => this.setActiveTool('eraser'));
    const measureBtn = document.getElementById('measureToolBtn');
    const measurePopover = document.getElementById('measurePopover');
    const measureClearBtn = document.getElementById('measureClearBtn');
    if (measureBtn) {
      measureBtn.addEventListener('click', () => {
        if (this.activeTool !== 'measure') this.setActiveTool('measure');
        this._updateMeasurePopoverCount();
        pinToggle('measure', 'measurePopover');
        if (this._isMobileToolbar) this._updateMobilePopoverState();
      });
      if (!this._isMobile) {
        measureBtn.addEventListener('mouseenter', () => {
          if (this._pinnedPopover !== 'measure') {
            this._updateMeasurePopoverCount();
            hoverShowPop('measurePopover');
          }
        });
        measureBtn.addEventListener('mouseleave', () => { cancelHoverShow(); if (this._pinnedPopover !== 'measure') hidePop('measurePopover'); });
      }
    }
    if (measureClearBtn) {
      measureClearBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this._measurements.length > 0) this.saveUndoState();
        this._measurements = [];
        this._selectedMeasurements.clear();
        this._measurePhase = 0; this._measureStart = null; this._measurePendingEnd = null;
        this._updateMeasureClearBtn();
        this.render();
      });
    }

    // Shift toggle (mobile only — visible via CSS)
    const shiftToggleBtn = document.getElementById('shiftToggleBtn');
    if (shiftToggleBtn) {
      shiftToggleBtn.addEventListener('click', () => {
        this.shiftHeld = !this.shiftHeld;
        this._shiftToggle = this.shiftHeld;
        shiftToggleBtn.classList.toggle('active', this.shiftHeld);
      });
    }

    const objectsToolBtn = document.getElementById('objectsToolBtn');
    objectsToolBtn.addEventListener('click', () => {
      // Mobile: first open just shows popover without activating tool or selecting defaults
      if (this._isMobileToolbar && (this.activeTool !== 'door' || !this._doorToolClicked)) {
        this._doorToolClicked = false;
        this._mobileObjectChosen = false;
        this._penToolClicked = false;
        this._mobileActionChosen = false;
        document.getElementById('penActionOptions').style.display = 'none';
        syncPenButtons();
        pinToggle('door', 'objectsPopover');
        syncObjectsButtons();
        this._updateMobilePopoverState();
        return;
      }
      this._doorToolClicked = true;
      if (this.activeTool !== 'door') this.setMode('door');
      pinToggle('door', 'objectsPopover');
      syncObjectsButtons();
      if (this._isMobileToolbar) {
        this._updateMobilePopoverState();
      }
    });
    if (!this._isMobile) {
      objectsToolBtn.addEventListener('mouseenter', () => { if (this._pinnedPopover !== 'door') hoverShowPop('objectsPopover', syncObjectsButtons); });
      objectsToolBtn.addEventListener('mouseleave', () => { cancelHoverShow(); if (this._pinnedPopover !== 'door') hidePop('objectsPopover'); });
    }

    /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — Area Label tool.
       Its own toolbar button rather than a fourth type in Place Objects, because
       the Objects tool is hidden on the Equipment tab (_swapToolbarButtons) and a
       label naming a training zone is exactly the thing you want to drop while
       looking at the equipment you have just laid out. */
    const labelToolBtn = document.getElementById('labelToolBtn');
    if (labelToolBtn) {
      const syncLabelPop = () => this._syncLabelPopover();
      labelToolBtn.addEventListener('click', () => {
        if (this.activeTool !== 'label') this.setActiveTool('label');
        pinToggle('label', 'labelPopover');
        syncLabelPop();
        const input = document.getElementById('labelTextInput');
        if (input && !this._isMobileToolbar) { input.focus(); input.select(); }
        if (this._isMobileToolbar) this._updateMobilePopoverState();
      });
      if (!this._isMobile) {
        labelToolBtn.addEventListener('mouseenter', () => { if (this._pinnedPopover !== 'label') hoverShowPop('labelPopover', syncLabelPop); });
        labelToolBtn.addEventListener('mouseleave', () => { cancelHoverShow(); if (this._pinnedPopover !== 'label') hidePop('labelPopover'); });
      }
      const labelInput = document.getElementById('labelTextInput');
      if (labelInput) {
        labelInput.addEventListener('input', () => {
          this._labelText = labelInput.value;
          document.querySelectorAll('#labelPopover .label-preset-btn').forEach(b => {
            b.classList.toggle('active', b.dataset.value === this._labelText.trim());
          });
        });
        // Enter arms the tool rather than submitting anything — the click on the
        // plan is what places it, and there is no form here to submit to.
        labelInput.addEventListener('keydown', (e) => {
          e.stopPropagation();
          if (e.key === 'Enter') { e.preventDefault(); labelInput.blur(); if (this.activeTool !== 'label') this.setActiveTool('label'); }
        });
        labelInput.addEventListener('keyup', (e) => e.stopPropagation());
        labelInput.addEventListener('keypress', (e) => e.stopPropagation());
      }
      document.querySelectorAll('#labelPopover .label-preset-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          this._labelText = btn.dataset.value;
          if (this.activeTool !== 'label') this.setActiveTool('label');
          this._syncLabelPopover();
          this.updateStatus(`"${this._labelText}" ready — click the plan to place it.`);
        });
      });
      document.querySelectorAll('#labelPopover .label-style-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          // Glass or solid is a look for the whole SET, exactly like size — it is
          // stored on the tool rather than per label, so there is nothing to undo
          // and nothing to re-measure; the pill is the same shape either way.
          this.glassLabels = (btn.dataset.value === 'glass');
          localStorage.setItem('rd_glassLabels', this.glassLabels ? '1' : '0');
          this._syncLabelPopover();
          this.render();
        });
      });
      document.querySelectorAll('#labelPopover .label-size-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          this._labelSize = btn.dataset.value;
          /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — size is a property of the DRAWING, not
             of one label. Ryan's call, and it is the right model: labels are a set
             of peers naming zones on the same plan, so one being bigger than its
             neighbour is a mistake rather than a choice. Picking a size restyles
             every label already placed, and arms that size for the next one. One
             undo entry for the lot, and only if something actually changed. */
          const all = this.objects.filter(o => o.type === 'label');
          const changed = all.filter(o => (o.size || 'medium') !== this._labelSize);
          if (changed.length) {
            this.saveUndoState();
            changed.forEach(o => { o.size = this._labelSize; this._measureLabelBox(o); });
            this._triggerAutoSave && this._triggerAutoSave();
            this.updateStatus(`All ${all.length} label${all.length === 1 ? '' : 's'} resized.`);
          }
          this._syncLabelPopover();
          this.render();
        });
      });
    }

    const settingsToggle = document.getElementById('settingsToggle');
    const settingsDrawer = document.getElementById('settingsDrawer');
    const _drawerPinned = () => this._pinnedPopover === 'settings' || this._pinnedPopover === 'designs' || this._pinnedPopover === 'shortcuts';
    this._isDrawerPinned = _drawerPinned;
    const _syncDrawerPins = () => {
      const map = { settings: 'settingsToggle', designs: 'designsToggle', shortcuts: 'shortcutsToggle', pen: 'penToolBtn', door: 'objectsToolBtn', label: 'labelToolBtn', zoom: 'zoomToggleBtn', floorRotate: 'floorRotateBtn' };
      for (const [name, id] of Object.entries(map)) {
        const el = document.getElementById(id);
        if (el) el.classList.toggle('pinned', this._pinnedPopover === name);
      }
    };
    this._syncDrawerPins = _syncDrawerPins;
    settingsToggle.addEventListener('click', () => {
      if (this._isMobile) this._tapFlash(settingsToggle);
      clearTimeout(this._popoverTimers['settingsDrawer']);
      if (this._pinnedPopover === 'settings') {
        this._pinnedPopover = null;
        settingsDrawer.classList.remove('open');
        settingsToggle.classList.remove('open');
      } else {
        ['zoomPopover', 'penPopover', 'objectsPopover'].forEach(id => { document.getElementById(id).classList.remove('visible'); clearTimeout(this._popoverTimers[id]); });
        const dd = document.getElementById('designsDrawer'); const dt = document.getElementById('designsToggle');
        if (dd) dd.classList.remove('open'); if (dt) dt.classList.remove('open');
        clearTimeout(this._popoverTimers['designsDrawer']);
        const kd = document.getElementById('shortcutsDrawer'); const kt = document.getElementById('shortcutsToggle');
        if (kd) kd.classList.remove('open'); if (kt) kt.classList.remove('open');
        clearTimeout(this._popoverTimers['shortcutsDrawer']);
        this._pinnedPopover = 'settings';
        settingsDrawer.classList.add('open');
        settingsToggle.classList.add('open');
      }
      _syncDrawerPins();
      syncPinIcons();
      if (this._isMobileToolbar) {
        this._updateMobilePopoverState();
      }
    });
    const openSettingsHover = () => {
      if (_isDragging()) return;
      if (!_drawerPinned()) {
        clearTimeout(this._popoverTimers['settingsDrawer']);
        // Instantly close designs + shortcuts drawers to prevent stacking
        const dd = document.getElementById('designsDrawer'); const dt = document.getElementById('designsToggle');
        clearTimeout(this._popoverTimers['designsDrawer']);
        if (dd) { dd.style.transition = 'none'; dd.classList.remove('open'); dd.offsetHeight; dd.style.transition = ''; }
        if (dt) dt.classList.remove('open');
        const kd = document.getElementById('shortcutsDrawer'); const kt = document.getElementById('shortcutsToggle');
        clearTimeout(this._popoverTimers['shortcutsDrawer']);
        if (kd) { kd.style.transition = 'none'; kd.classList.remove('open'); kd.offsetHeight; kd.style.transition = ''; }
        if (kt) kt.classList.remove('open');
        settingsDrawer.classList.add('open');
        settingsToggle.classList.add('open');
      }
    };
    const closeSettingsHover = () => {
      if (!_drawerPinned()) {
        this._popoverTimers['settingsDrawer'] = setTimeout(() => {
          if (!_drawerPinned()) {
            settingsDrawer.classList.remove('open');
            settingsToggle.classList.remove('open');
          }
        }, 250);
      }
    };
    if (!this._isMobile) {
      settingsToggle.addEventListener('mouseenter', openSettingsHover);
      settingsToggle.addEventListener('mouseleave', closeSettingsHover);
      settingsDrawer.addEventListener('mouseenter', openSettingsHover);
      settingsDrawer.addEventListener('mouseleave', closeSettingsHover);
    }

    document.querySelectorAll('.unit-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.unit-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active'); this.unit = btn.dataset.value; localStorage.setItem('rd_unit', this.unit); this.updateMeasurements(); this.render();
      });
    });

    // Badge is pointer-events:none; clicks pass through to penToolBtn

    document.querySelectorAll('.pen-mode-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        this._penToolClicked = true;
        this._clearPopoverIdlePulse();
        const badge = document.getElementById('penBadge');
        // Erase mode (mobile only) — switch to eraser but keep pen button visually active
        if (btn.dataset.value === 'erase') {
          document.querySelectorAll('.pen-mode-btn').forEach(b => b.classList.remove('active'));
          document.querySelectorAll('.pen-draw-mode-btn').forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          if (this._isMobileToolbar) {
            this._mobileActionChosen = false;
            document.getElementById('penActionOptions').style.display = 'none';
          }
          badge.innerHTML = '<svg width="10" height="10" viewBox="0 2 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="10" width="16" height="10" rx="2" transform="rotate(-30 12 15)"/><line x1="4.5" y1="14.5" x2="19.5" y2="14.5" stroke-width="1.5" transform="rotate(-30 12 15)"/></svg>';
          badge.className = 'pen-badge erase';
          this.setActiveTool('eraser');
          // Keep pen popover open and pen button visually active on mobile
          document.getElementById('penToolBtn').classList.add('active');
          document.getElementById('eraserBtn').classList.remove('active');
          if (this._isMobileToolbar) {
            document.getElementById('penPopover').classList.add('visible');
            this._pinnedPopover = 'pen';
            this._updateMobilePopoverState();
          }
          return;
        }
        document.querySelectorAll('.pen-mode-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active'); this.roomType = btn.dataset.value; this._resetRectState(); this._resetFreehandState();
        this._penCancelledIdle = false;
        if (this._isMobileToolbar) this._mobileActionChosen = true;
        if (this.currentRoom) this.currentRoom.type = btn.dataset.value;
        badge.textContent = this.roomType === 'add' ? '+' : '−';
        badge.className = 'pen-badge' + (this.roomType === 'subtract' ? ' subtract' : '');
        const jdpad = document.getElementById('joystickDpad');
        if (jdpad) jdpad.classList.toggle('subtract', this.roomType === 'subtract');
        // Mobile rectangle: auto-place starting dot
        if (this._isMobileToolbar && this.penDrawMode === 'rectangle') {
          if (this.activeTool !== 'pen') {
            this.setActiveTool('pen');
            showPop('penPopover');
            this._pinnedPopover = 'pen';
          }
          syncPenButtons();
          this._mobileRectAutoPlace();
          return;
        }
        // Mobile freehand: auto-place starting dot
        if (this._isMobileToolbar && this.penDrawMode !== 'rectangle') {
          if (this.activeTool !== 'pen') {
            this.setActiveTool('pen');
            showPop('penPopover');
            this._pinnedPopover = 'pen';
          }
          syncPenButtons();
          this._mobileFreehandAutoPlace();
          return;
        }
        if (this.activeTool !== 'pen') {
          this.setActiveTool('pen');
        } else if (this.mode !== 'draw') {
          // Re-enter draw mode (e.g. after cancel or mode change)
          this.mode = 'draw';
          this.canvas.style.cursor = 'crosshair';
          if (!this.currentRoom || this.currentRoom.walls.length === 0 || this.currentRoom.closed) this.promoteUnclosedRoom();
          this.updateStatus(this.penDrawMode === 'rectangle' ? 'Click two corners to draw a rectangular perimeter. Esc to exit' : 'Click to place perimeter points. Esc to exit');
        }
        // Always ensure popover is visible and pinned on desktop
        showPop('penPopover');
        this._pinnedPopover = 'pen';
        syncPenButtons();
      });
    });

    const doorWidthInput = document.getElementById('doorWidth');
    document.querySelectorAll('.door-type-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        this._doorToolClicked = true;
        this._mobileObjectChosen = true;
        this._clearPopoverIdlePulse();
        document.querySelectorAll('.door-type-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active'); this.doorType = btn.dataset.value;
        if (btn.dataset.value === 'double') { this.doorWidth = 72; doorWidthInput.value = 72; }
        else if (btn.dataset.value === 'roller') { this.doorWidth = 120; doorWidthInput.value = 120; }
        else { this.doorWidth = 36; doorWidthInput.value = 36; }
        if (this.activeTool !== 'door') {
          this.setMode('door');
          // On mobile, auto-place now that a door type is selected
          if (this._isMobileToolbar) this._mobileDoorAutoPlace();
        } else if (this._isMobileToolbar) {
          // Door type changed while already in door mode on mobile — restart auto-place
          this._resetDoorMobileState();
          this._mobileDoorAutoPlace();
        }
        if (this._isMobileToolbar) this._startPopoverIdlePulse();
      });
    });
    const doorMaxWidth = () => this.doorType === 'roller' ? 216 : this.doorType === 'double' ? 96 : 48;
    doorWidthInput.addEventListener('input', (e) => {
      const val = parseInt(e.target.value);
      this.doorWidth = isNaN(val) ? 36 : Math.max(12, Math.min(doorMaxWidth(), val));
    });
    doorWidthInput.addEventListener('change', (e) => {
      this.doorWidth = Math.max(12, Math.min(doorMaxWidth(), this.doorWidth));
      e.target.value = this.doorWidth;
    });
    doorWidthInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') e.target.blur(); });

    // Objects type toggle (door / rectangle / circle)
    document.querySelectorAll('.objects-type-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        this._doorToolClicked = true;
        // Column/Pillar auto-complete (no Row 2), Door needs a style pick
        this._mobileObjectChosen = (btn.dataset.value !== 'door');
        this._clearPopoverIdlePulse();
        document.querySelectorAll('.objects-type-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.objectType = btn.dataset.value;
        document.getElementById('objectsDoorOptions').style.display = btn.dataset.value === 'door' ? '' : 'none';
        document.getElementById('objectsRectOptions').style.display = btn.dataset.value === 'rectangle' ? '' : 'none';
        document.getElementById('objectsCircleOptions').style.display = btn.dataset.value === 'circle' ? '' : 'none';
        if (this.activeTool !== 'door') {
          this.setMode('door');
          // On mobile, auto-place pillar/column preview immediately
          if (this._isMobileToolbar && (btn.dataset.value === 'rectangle' || btn.dataset.value === 'circle')) {
            this._mobileObjectAutoPlace();
            this.render();
          }
        } else if (this._isMobileToolbar) {
          // Already in door mode — clean slate for the new object type
          this._resetDoorMobileState();
          this.mouseWorldRaw = null; this.mouseWorld = null;
          if (btn.dataset.value === 'door') {
            // Switching back to door — force user to re-select a door sub-type
            document.querySelectorAll('.door-type-btn').forEach(b => b.classList.remove('active'));
            this.updateStatus('Select a door type, then tap a wall.');
            this._updateJoystickVisibility();
          } else {
            // Pillar/column — auto-place preview at screen center
            this._mobileObjectAutoPlace();
          }
          this._startPopoverIdlePulse();
          this.render();
        }
      });
    });

    // Rectangle options
    const rectWidthInput = document.getElementById('rectWidth');
    const rectDepthInput = document.getElementById('rectDepth');
    rectWidthInput.addEventListener('input', (e) => { const v = parseInt(e.target.value); this.rectWidth = isNaN(v) ? 12 : Math.max(6, Math.min(240, v)); });
    rectWidthInput.addEventListener('change', (e) => { this.rectWidth = Math.max(6, Math.min(240, this.rectWidth)); e.target.value = this.rectWidth; });
    rectWidthInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') e.target.blur(); });
    rectDepthInput.addEventListener('input', (e) => { const v = parseInt(e.target.value); this.rectDepth = isNaN(v) ? 12 : Math.max(6, Math.min(240, v)); });
    rectDepthInput.addEventListener('change', (e) => { this.rectDepth = Math.max(6, Math.min(240, this.rectDepth)); e.target.value = this.rectDepth; });
    rectDepthInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') e.target.blur(); });

    // Circle options
    const circleDiamInput = document.getElementById('circleDiameter');
    circleDiamInput.addEventListener('input', (e) => { const v = parseInt(e.target.value); this.circleDiameter = isNaN(v) ? 12 : Math.max(6, Math.min(120, v)); });
    circleDiamInput.addEventListener('change', (e) => { this.circleDiameter = Math.max(6, Math.min(120, this.circleDiameter)); e.target.value = this.circleDiameter; });
    circleDiamInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') e.target.blur(); });

    document.getElementById('toggleMeasurements').addEventListener('click', (e) => {
      this.showMeasurements = !this.showMeasurements;
      if (!this.showMeasurements) this._measurementsVisible = false; // toggling off hides everything
      this._clearTabStash('_stashedMeasurements');
      localStorage.setItem('rd_showMeasurements', this.showMeasurements ? '1' : '0'); e.target.classList.toggle('active', this.showMeasurements); this.render();
    });
    document.getElementById('toggleRoomLabels').addEventListener('click', (e) => {
      this.showRoomLabels = !this.showRoomLabels; localStorage.setItem('rd_showRoomLabels', this.showRoomLabels ? '1' : '0'); e.target.classList.toggle('active', this.showRoomLabels); this.render();
    });
    document.getElementById('toggleGrid').addEventListener('click', (e) => {
      this.showGrid = !this.showGrid; localStorage.setItem('rd_showGrid', this.showGrid ? '1' : '0'); e.target.classList.toggle('active', this.showGrid); this.render();
    });
    document.getElementById('toggleHollowWalls').addEventListener('click', (e) => {
      this.hollowWalls = !this.hollowWalls; localStorage.setItem('rd_hollowWalls', this.hollowWalls ? '1' : '0'); e.target.classList.toggle('active', this.hollowWalls); this.render();
    });
    document.getElementById('toggleHideOverage').addEventListener('click', (e) => {
      this.hideOverage = !this.hideOverage; localStorage.setItem('rd_hideOverage', this.hideOverage ? '1' : '0'); e.target.classList.toggle('active', !this.hideOverage);
      this._clearTabStash('_stashedHideOverage');
      if (this._equipmentMode) this._equipKeepOverage = !this.hideOverage;
      this.render();
    });
    document.getElementById('toggleSeamLines').addEventListener('click', (e) => {
      this.showSeamLines = !this.showSeamLines; localStorage.setItem('rd_showSeamLines', this.showSeamLines ? '1' : '0'); e.target.classList.toggle('active', this.showSeamLines);
      this._clearTabStash('_stashedSeamLines');
      if (this._equipmentMode) this._equipKeepSeams = this.showSeamLines;
      if (this._floorMode) this._floorSeamUserOff = !this.showSeamLines;
      this.render();
    });
    document.getElementById('toggleMaterialOverlay').addEventListener('click', (e) => {
      this.showMaterialOverlay = !this.showMaterialOverlay; localStorage.setItem('rd_showMaterialOverlay', this.showMaterialOverlay ? '1' : '0'); e.target.classList.toggle('active', this.showMaterialOverlay);
      this._clearTabStash('_stashedMaterialOverlay');
      // Remember preference when toggled on equipment tab (sticky across visits)
      if (this._equipmentMode) localStorage.setItem('rd_equipOverlayKeepOn', this.showMaterialOverlay ? '1' : '0');
      this.render();
    });
    const _eqLblToggle = document.getElementById('toggleEquipmentLabels');
    if (_eqLblToggle) _eqLblToggle.addEventListener('click', (e) => {
      this.showEquipmentLabels = !this.showEquipmentLabels;
      localStorage.setItem('rd_showEquipmentLabels', this.showEquipmentLabels ? '1' : '0');
      e.target.classList.toggle('active', this.showEquipmentLabels);
      // Hiding them must also drop any selection, or the toolbar keeps offering
      // actions (rotate, delete, send-to-front) for something nothing can see.
      if (!this.showEquipmentLabels && this.selectedObjects.size > 0) {
        [...this.selectedObjects].forEach(o => { if (o.type === 'label') this.selectedObjects.delete(o); });
        this._updateActionButtons();
      }
      this.render();
    });

    document.querySelectorAll('.theme-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.theme-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.darkMode = btn.dataset.value === 'dark';
        localStorage.setItem('rd_darkMode', this.darkMode ? '1' : '0');
        document.querySelector('.room-designer').classList.toggle('dark-mode', this.darkMode);
        // Portal dark mode sync
        if (this._portalMode) {
          localStorage.setItem('rep_portal_theme', this.darkMode ? 'dark' : 'light');
          var wrap = document.querySelector('.rep-quote-portal-wrap');
          if (wrap) {
            wrap.classList.toggle('rep-quote-dark', this.darkMode);
            wrap.style.background = this.darkMode ? '#000F1C' : '';
            wrap.style.colorScheme = this.darkMode ? 'dark' : '';
          }
          document.body.style.background = this.darkMode ? '#000F1C' : '';
          var mainC = document.getElementById('main-content');
          if (mainC) mainC.style.background = this.darkMode ? '#000F1C' : '';
        }
        this._rebuildEquipmentTheme();
        if (this.mode === 'measure') this._applyMeasureCursor();
        this.updateMeasurements();
        this.render();
      });
    });

    document.querySelectorAll('.size-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.size-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.largeUI = btn.dataset.value === 'large';
        localStorage.setItem('rd_largeUI', this.largeUI ? '1' : '0');
        document.querySelector('.room-designer').classList.toggle('large-ui', this.largeUI);
        this.render();
      });
    });

    document.querySelectorAll('.pen-draw-mode-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const newMode = btn.dataset.value;
        // If switching away from freehand with unclosed walls, show warning
        if (this.penDrawMode === 'freehand' && newMode !== 'freehand' && this.currentRoom && this.currentRoom.walls.length > 0 && !this.currentRoom.closed && this.currentRoom.type !== 'subtract') {
          this._warnIncompleteRoom(() => {
            // Discard walls and proceed with mode switch
            this.currentRoom = { walls: [], doors: [], cutouts: [], type: this.roomType, closed: false };
            this.currentWall = null; this.hasPlacedFirstPoint = false;
            this.updateMeasurements();
            this._applyPenDrawModeSwitch(btn, newMode);
          }, () => {
            // Continue drawing — do nothing, user is back in freehand
          });
          return;
        }
        this._applyPenDrawModeSwitch(btn, newMode);
      });
    });

    const undoBtnEl = document.getElementById('undoBtn');
    const redoBtnEl = document.getElementById('redoBtn');
    undoBtnEl.addEventListener('click', () => {
      if (this._undoWallSegment && this._undoWallSegment()) return;
      this.undo();
    });
    redoBtnEl.addEventListener('click', () => { this.redo(); });
    const undoBtnMobile = document.getElementById('undoBtnMobile');
    const redoBtnMobile = document.getElementById('redoBtnMobile');
    if (undoBtnMobile) undoBtnMobile.addEventListener('click', () => {
      this._tapFlash(undoBtnMobile);
      if (this._undoWallSegment && this._undoWallSegment()) return;
      this.undo();
    });
    if (redoBtnMobile) redoBtnMobile.addEventListener('click', () => { this._tapFlash(redoBtnMobile); this.redo(); });
    document.getElementById('clearBtn').addEventListener('click', () => this.clearAll());

    // Shortcuts drawer (desktop only — hidden on mobile)
    const shortcutsToggle = document.getElementById('shortcutsToggle');
    const shortcutsDrawer = document.getElementById('shortcutsDrawer');
    if (this._isMobile) shortcutsToggle.style.display = 'none';
    shortcutsToggle.addEventListener('click', () => {
      if (this._isMobile) this._tapFlash(shortcutsToggle);
      clearTimeout(this._popoverTimers['shortcutsDrawer']);
      if (this._pinnedPopover === 'shortcuts') {
        this._pinnedPopover = null; shortcutsDrawer.classList.remove('open'); shortcutsToggle.classList.remove('open');
        if (this._clearHelpSearch) this._clearHelpSearch();
      } else {
        ['zoomPopover', 'penPopover', 'objectsPopover'].forEach(id => { document.getElementById(id).classList.remove('visible'); clearTimeout(this._popoverTimers[id]); });
        document.getElementById('settingsDrawer').classList.remove('open'); document.getElementById('settingsToggle').classList.remove('open');
        clearTimeout(this._popoverTimers['settingsDrawer']);
        const dd = document.getElementById('designsDrawer'); const dt = document.getElementById('designsToggle');
        if (dd) dd.classList.remove('open'); if (dt) dt.classList.remove('open');
        clearTimeout(this._popoverTimers['designsDrawer']);
        this._pinnedPopover = 'shortcuts'; shortcutsDrawer.classList.add('open'); shortcutsToggle.classList.add('open');
      }
      _syncDrawerPins();
      syncPinIcons();
      if (this._isMobileToolbar) this._updateMobilePopoverState();
    });
    if (!this._isMobile) {
      const openShortcutsHover = () => {
        if (_isDragging()) return;
        if (!this._isDrawerPinned()) {
          clearTimeout(this._popoverTimers['shortcutsDrawer']);
          const sd = document.getElementById('settingsDrawer'); const st = document.getElementById('settingsToggle');
          clearTimeout(this._popoverTimers['settingsDrawer']);
          if (sd) { sd.style.transition = 'none'; sd.classList.remove('open'); sd.offsetHeight; sd.style.transition = ''; }
          if (st) st.classList.remove('open');
          const dd = document.getElementById('designsDrawer'); const dt = document.getElementById('designsToggle');
          clearTimeout(this._popoverTimers['designsDrawer']);
          if (dd) { dd.style.transition = 'none'; dd.classList.remove('open'); dd.offsetHeight; dd.style.transition = ''; }
          if (dt) dt.classList.remove('open');
          shortcutsDrawer.classList.add('open'); shortcutsToggle.classList.add('open');
        }
      };
      const closeShortcutsHover = () => {
        if (!this._isDrawerPinned()) {
          this._popoverTimers['shortcutsDrawer'] = setTimeout(() => {
            if (!this._isDrawerPinned()) {
              shortcutsDrawer.classList.remove('open'); shortcutsToggle.classList.remove('open');
              if (this._clearHelpSearch) this._clearHelpSearch();
            }
          }, 250);
        }
      };
      shortcutsToggle.addEventListener('mouseenter', openShortcutsHover);
      shortcutsToggle.addEventListener('mouseleave', closeShortcutsHover);
      shortcutsDrawer.addEventListener('mouseenter', openShortcutsHover);
      shortcutsDrawer.addEventListener('mouseleave', closeShortcutsHover);
    }

    // ─── Help search in shortcuts drawer ───
    {
      const helpInput = document.getElementById('rdHelpSearchInput');
      const helpClear = document.getElementById('rdHelpSearchClear');
      const helpResults = document.getElementById('rdHelpResults');
      const helpResultsList = document.getElementById('rdHelpResultsList');
      const helpNoResults = document.getElementById('rdHelpNoResults');
      const helpShortcuts = document.getElementById('rdHelpShortcuts');
      const helpBrowse = document.getElementById('rdHelpBrowseLink');
      let kbIndex = null;
      let helpDebounce = null;
      const MAX_RESULTS = 10;

      const buildKbIndex = () => {
        if (kbIndex) return kbIndex;
        const kbSection = document.querySelector('.gld-kb');
        if (!kbSection) return null;
        const items = kbSection.querySelectorAll('.gld-kb__accordion');
        kbIndex = [];
        let currentCat = '';
        items.forEach(acc => {
          const catEl = acc.closest('.gld-kb__category');
          if (catEl) {
            const catTitle = catEl.querySelector('.gld-kb__category-title');
            if (catTitle) currentCat = catTitle.textContent.trim();
          }
          const titleEl = acc.querySelector('.gld-kb__accordion-title');
          const contentEl = acc.querySelector('.gld-kb__accordion-content');
          if (!titleEl) return;
          kbIndex.push({
            id: acc.id || '',
            title: titleEl.textContent.trim(),
            tags: (acc.dataset.tags || '').toLowerCase(),
            text: (contentEl ? contentEl.textContent : '').toLowerCase(),
            category: currentCat
          });
        });
        return kbIndex;
      };

      const showResults = (matches, total) => {
        helpResultsList.innerHTML = '';
        helpNoResults.style.display = matches.length ? 'none' : '';
        matches.forEach(item => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'rd-help-result-btn';
          btn.innerHTML = '<span class="rd-help-result-btn__title"></span>' +
            (item.category ? '<span class="rd-help-result-btn__cat"></span>' : '');
          btn.querySelector('.rd-help-result-btn__title').textContent = item.title;
          const catSpan = btn.querySelector('.rd-help-result-btn__cat');
          if (catSpan) catSpan.textContent = item.category;
          btn.addEventListener('click', () => {
            if (item.id) window.location.hash = '#' + item.id;
            this._clearHelpSearch();
            shortcutsDrawer.classList.remove('open');
            shortcutsToggle.classList.remove('open');
            this._pinnedPopover = null;
            if (this._syncDrawerPins) this._syncDrawerPins();
            if (this._syncPinIcons) this._syncPinIcons();
          });
          helpResultsList.appendChild(btn);
        });
        if (total > MAX_RESULTS) {
          const note = document.createElement('div');
          note.className = 'rd-help-more-note';
          note.textContent = '+ ' + (total - MAX_RESULTS) + ' more — refine your search';
          helpResultsList.appendChild(note);
        }
        helpResults.style.display = '';
        helpShortcuts.style.display = 'none';
      };

      const doHelpSearch = (query) => {
        const q = query.trim().toLowerCase();
        if (!q) { this._clearHelpSearch(); return; }
        const idx = buildKbIndex();
        if (!idx) { showResults([], 0); return; }
        const all = idx.filter(item =>
          item.title.toLowerCase().includes(q) ||
          item.tags.includes(q) ||
          item.text.includes(q)
        );
        showResults(all.slice(0, MAX_RESULTS), all.length);
      };

      helpInput.addEventListener('input', () => {
        helpClear.style.display = helpInput.value ? '' : 'none';
        clearTimeout(helpDebounce);
        helpDebounce = setTimeout(() => doHelpSearch(helpInput.value), 200);
      });

      helpInput.addEventListener('mousedown', () => {
        this._pinnedPopover = 'shortcuts';
        if (_syncDrawerPins) _syncDrawerPins();
        if (syncPinIcons) syncPinIcons();
      });

      helpInput.addEventListener('keydown', (e) => {
        e.stopPropagation();
        if (e.key === 'Escape') {
          if (helpInput.value) {
            helpInput.value = '';
            helpClear.style.display = 'none';
            this._clearHelpSearch();
          } else {
            shortcutsDrawer.classList.remove('open');
            shortcutsToggle.classList.remove('open');
            this._pinnedPopover = null;
            if (this._syncDrawerPins) this._syncDrawerPins();
            if (this._syncPinIcons) this._syncPinIcons();
          }
        }
      });

      helpClear.addEventListener('click', () => {
        helpInput.value = '';
        helpClear.style.display = 'none';
        this._clearHelpSearch();
        helpInput.focus();
      });

      helpBrowse.addEventListener('click', (e) => {
        e.preventDefault();
        const kb = document.querySelector('.gld-kb');
        if (kb) kb.scrollIntoView({ behavior: 'smooth' });
        shortcutsDrawer.classList.remove('open');
        shortcutsToggle.classList.remove('open');
        this._pinnedPopover = null;
        if (this._syncDrawerPins) this._syncDrawerPins();
        if (this._syncPinIcons) this._syncPinIcons();
      });

      this._clearHelpSearch = () => {
        const inp = document.getElementById('rdHelpSearchInput');
        const res = document.getElementById('rdHelpResults');
        const sc = document.getElementById('rdHelpShortcuts');
        const clr = document.getElementById('rdHelpSearchClear');
        if (inp) inp.value = '';
        if (res) res.style.display = 'none';
        if (sc) sc.style.display = '';
        if (clr) clr.style.display = 'none';
      };
    }

    // Desktop delete button
    const desktopDeleteBtn = document.getElementById('desktopDeleteBtn');
    if (desktopDeleteBtn) {
      this._desktopDeleteBtn = desktopDeleteBtn;
      desktopDeleteBtn.addEventListener('click', () => {
        // Block deletion on Customize tab — except a focused turf logo, or a
        // selection that is nothing but area labels (the tool lives on this tab).
        if (window.roomDesignerOverlay && window.roomDesignerOverlay.currentStep === 'finishing' && this._turfLogoFocusedIdx == null && !this._selectionIsOnlyLabels()) return;
        const hasFloor = this._floorMode && window.roomDesignerOverlay && window.roomDesignerOverlay._floorSelections.length > 0;
        const hasObjects = this.selectedObjects.size > 0 || this.selectedDoors.size > 0 || this.selectedWalls.size > 0;
        this._deleteWithEquipmentCheck(hasFloor, hasObjects);
      });
    }

    // Desktop action buttons (send-to-front/back, group/ungroup)
    this._sendToFrontBtn = document.getElementById('sendToFrontBtn');
    this._sendToBackBtn = document.getElementById('sendToBackBtn');
    this._groupBtn = document.getElementById('groupBtn');
    this._ungroupBtn = document.getElementById('ungroupBtn');
    if (this._sendToFrontBtn) this._sendToFrontBtn.addEventListener('click', () => this.sendToFront());
    if (this._sendToBackBtn) this._sendToBackBtn.addEventListener('click', () => this.sendToBack());
    if (this._groupBtn) this._groupBtn.addEventListener('click', () => this.groupSelected());
    if (this._ungroupBtn) this._ungroupBtn.addEventListener('click', () => this.ungroupSelected());

    // Mobile: tap outside popovers to dismiss
    if (this._isMobile) {
      document.addEventListener('click', (e) => {
        if (!this._pinnedPopover) return;
        const popovers = ['penPopover', 'objectsPopover', 'zoomPopover'];
        const settingsD = document.getElementById('settingsDrawer');
        const settingsT = document.getElementById('settingsToggle');
        const inPopover = popovers.some(id => {
          const el = document.getElementById(id);
          return el && el.contains(e.target);
        });
        const inSettings = (settingsD && settingsD.contains(e.target)) || (settingsT && settingsT.contains(e.target));
        const designsD = document.getElementById('designsDrawer'); const designsT = document.getElementById('designsToggle');
        const inDesigns = (designsD && designsD.contains(e.target)) || (designsT && designsT.contains(e.target));
        const inToolbar = e.target.closest('.tool-controls');
        if (!inPopover && !inSettings && !inDesigns && !inToolbar) {
          // Close all popovers
          popovers.forEach(id => {
            document.getElementById(id).classList.remove('visible');
            clearTimeout(this._popoverTimers[id]);
          });
          settingsD.classList.remove('open');
          settingsT.classList.remove('open');
          if (designsD) designsD.classList.remove('open');
          if (designsT) designsT.classList.remove('open');
          this._pinnedPopover = null;
          if (this._syncDrawerPins) this._syncDrawerPins();
          // Restore zoom button if needed
          document.getElementById('zoomToggleBtn').classList.remove('active');
          if (this.activeTool === 'select') {
            document.getElementById('selectToolBtn').classList.add('active');
          }
        }
      });
    }
  }

  /* ── TOOL MANAGEMENT ── */

  handleEscape() {
    // Cancel mobile select handle mode
    if (this._selectHandleMode && this._selectHandleMode !== 'cycling' && this._selectHandleMode !== 'wholeMove') {
      // Specific handle active — revert manipulation, return to cycling
      if (this._selectRotateDialActive) this._exitRotateDial();
      this.undo();
      const newList = this._buildHandleCycleList();
      if (newList.length > 1 || (newList.length === 1 && newList[0].mode !== 'wholeMove')) {
        this._selectHandleMode = 'cycling';
        this._selectHandleInfo = null;
        this._selectHandleCycleList = newList;
        this._selectHandleCycleIndex = Math.min(this._selectHandleCycleIndex, newList.length - 1);
        this._setJoystickArrowsEnabled(true);
        this._clearArrowPulse();
        this._clearCheckPulse();
        this._startArrowPulse(3000);
        this._startCheckPulse(3000);
      } else {
        // No cycling list to return to — fall back to evaluate
        this._selectHandleMode = null;
        this._selectHandleInfo = null;
        this._selectHandleCycleList = [];
        this._selectHandleCycleIndex = 0;
        this._evaluateSelectJoystickMode();
      }
      this.render(); return;
    }
    if (this._selectHandleMode === 'cycling') {
      // Exit cycling, keep selection
      this._selectHandleMode = null;
      this._selectHandleInfo = null;
      this._selectHandleCycleList = [];
      this._selectHandleCycleIndex = 0;
      this._clearArrowPulse();
      this._clearCheckPulse();
      this._updateJoystickVisibility();
      this.render(); return;
    }
    if (this._selectHandleMode === 'wholeMove') {
      this._deactivateSelectHandle();
      this.undo();
      this.render(); return;
    }
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — Esc abandons a close-up region pick and hands
    // control back to the dialog, which reopens with nothing added.
    if (this._regionPick) { this._endRegionPick(null); return; }
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — Esc disarms the label tool. It stays active
    // after a placement so you can drop a run of them, so there has to be a way out.
    if (this.activeTool === 'label') {
      this.setActiveTool('select');
      this.updateStatus('Label tool off.');
      this.render(); return;
    }
    // Cancel in-progress measurement (measure mode only)
    if (this.mode === 'measure') {
      if (this._measurePhase === 2) {
        this._measurePendingEnd = null; this._measurePhase = 1;
        this.updateStatus('Click the second point to set the distance.');
        this.render(); return;
      }
      if (this._measurePhase === 1) {
        this._measureStart = null; this._measureEnd = null; this._measurePhase = 0;
        this.updateStatus('Measurement cancelled. Click to start a new measurement.');
        this.render(); return;
      }
    }
    // Deselect measurements (works in BOTH measure mode AND select mode)
    if (this._selectedMeasurements.size > 0) {
      this._selectedMeasurements.clear();
      this.render(); return;
    }
    // Cancel in-progress floor box select
    if (this._floorMode && window.roomDesignerOverlay && window.roomDesignerOverlay._cancelFloorBoxSelect()) {
      return;
    }
    // Clear floor selections
    if (this._floorMode && window.roomDesignerOverlay && window.roomDesignerOverlay._floorSelections.length > 0) {
      window.roomDesignerOverlay._floorSelections = [];
      window.roomDesignerOverlay._floorInfoPinned = false;
      window.roomDesignerOverlay._hideFloorInfo();
      this._updateFloatingDelete();
      this.render();
      return;
    }
    // Cancel paste preview
    if (this._pastePreview) {
      this._pastePreview = null;
      this._pasteCardinalSnap = null;
      this.canvas.style.cursor = 'default';
      this.updateStatus('Paste cancelled.');
      this.render(); return;
    }
    // Cancel pending ladder/dot drill ghost placement
    if (this._pendingLadder) {
      const _escLabel = this._pendingLadder.itemType === 'dotdrill' ? 'dot drill' : 'ladder';
      if (this._pendingLadderPos) {
        // Cancel rotation phase → back to follow
        this._pendingLadderPos = null;
        this._pendingLadderRotation = 0;
        this.canvas.style.cursor = 'crosshair';
        this.updateStatus('Click on the turf to place ' + _escLabel + '.');
        this.render(); return;
      }
      // Cancel entirely
      this._pendingLadder = null;
      this._pendingLadderPos = null;
      this._pendingLadderGhostPos = null;
      this._pendingLadderRotation = 0;
      this.canvas.style.cursor = 'default';
      this.updateStatus(_escLabel.charAt(0).toUpperCase() + _escLabel.slice(1) + ' placement cancelled.');
      this.render(); return;
    }
    // Cancel pending equipment placement — rotation phase first, then deselect
    if (this._equipmentMode && this._pendingEquipmentId) {
      if (this._pendingEquipmentPos) {
        // Cancel rotation phase → back to ghost-follow (keep remembered angle)
        this._pendingEquipmentPos = null;
        this._pendingEquipmentRotation = this._lastEquipmentRotation || 0;
        this.canvas.style.cursor = 'crosshair';
        this.updateStatus('Click the canvas to place equipment.');
        this.render(); return;
      }
      this._pendingEquipmentId = null;
      this._pendingEquipmentRotation = 0;
      this._lastEquipmentRotation = null;
      this._lastPlacedEquipPos = null;
      this.canvas.style.cursor = 'crosshair';
      document.querySelectorAll('.rd-equipment-swatch').forEach(c => c.classList.remove('selected'));
      this.updateStatus('Click an equipment item in the panel to select it.');
      this.render(); return;
    }
    // Cancel turf draw mode
    if (this.activeTool === 'turf-draw') {
      if (this._turfCorner1) {
        // Cancel first corner, stay in turf-draw mode
        this._turfCorner1 = null;
        this.updateStatus('Turf corner cancelled. Click to place first corner.');
        this.render(); return;
      }
      // Exit turf-draw mode entirely
      this._resetTurfState();
      this.activeTool = 'select';
      this.mode = 'select';
      this.canvas.style.cursor = 'default';
      // Clear turf swatch highlight
      if (window.roomDesignerOverlay) {
        window.roomDesignerOverlay._activeBrush = null;
        window.roomDesignerOverlay._updatePanelSelection();
      }
      this.updateStatus('Turf draw cancelled.');
      this.render(); return;
    }
    // Cancel platform placement mode — rotation phase first, then full cancel
    if (this.activeTool === 'platform-place') {
      if (this._pendingPlatformPos) {
        // Cancel rotation phase → back to ghost-follow
        this._pendingPlatformPos = null;
        this._pendingPlatformRotation = 0;
        this.canvas.style.cursor = 'crosshair';
        this.updateStatus('Click inside a compatible room to place platform.');
        this.render(); return;
      }
      this._resetPlatformState();
      this.activeTool = 'select';
      this.mode = 'select';
      this.canvas.style.cursor = 'default';
      this.updateStatus('Platform placement cancelled.');
      this.render(); return;
    }
    // Cancel in-progress two-click box select
    if (this._selectCorner1) {
      this._selectCorner1 = null;
      this._selectPreExisting = null;
      this.isSelecting = false;
      this.selectionStart = null; this.selectionEnd = null;
      this.updateStatus('Click to select items. Shift+click to multi-select. Click to box-select. Delete to remove.');
      this.render(); return;
    }
    // Cancel in-progress turf logo drag
    if (this._turfLogoDrag && this._turfLogoDragObj) {
      const _tObj = this._turfLogoDragObj;
      const _orig = this._turfLogoDragOrigLogo;
      const _dragLogo = _tObj.turfLogos && _tObj.turfLogos[this._turfLogoDragLogoIdx];
      if (_dragLogo) {
        _dragLogo.x = _orig.x; _dragLogo.y = _orig.y;
        _dragLogo.width = _orig.w; _dragLogo.height = _orig.h;
        _dragLogo.rotation = _orig.r;
        _dragLogo._snapX = false; _dragLogo._snapY = false; _dragLogo._snapGapRunY = null; _dragLogo._resizing = false;
      }
      this._turfLogoDrag = null; this._turfLogoDragObj = null;
      this._turfLogoDragStart = null; this._turfLogoDragOrigLogo = null;
      this._turfLogoDragGroupOrig = null; this._turfLogoDragLogoIdx = null;
      this._turfLogoFocusedIdx = null;
      this._turfLogoSelectedIdxs.clear();
      this.undo();
      this.canvas.style.cursor = 'default'; this.render(); return;
    }
    // Cancel in-progress rotate
    if (this._selectRotating && this._selectRotateInfo) {
      if (this._selectRotateInfo.group) {
        // Revert group rotation
        for (const snap of this._selectRotateInfo.originals) {
          snap.obj.x = snap.x; snap.obj.y = snap.y; snap.obj.rotation = snap.rotation;
        }
      } else {
        this._selectRotateInfo.obj.rotation = this._selectRotateInfo.origRotation;
      }
      this._selectRotating = false; this._selectRotateInfo = null;
      this.undo(); // Revert the undo state saved at start
      this.canvas.style.cursor = 'default'; this.render(); return;
    }
    // Cancel in-progress resize
    if (this._selectResizing && this._selectResizeInfo) {
      const info = this._selectResizeInfo;
      const obj = info.obj;
      obj.x = info.origX; obj.y = info.origY;
      if (obj.type === 'rectangle' || obj.type === 'equipment' || obj.type === 'turf' || obj.type === 'platform') { obj.width = info.origWidth; obj.depth = info.origDepth; obj.rotation = info.origRotation; }
      else if (obj.type === 'circle') { obj.diameter = info.origDiameter; }
      this._selectResizing = false; this._selectResizeInfo = null;
      this.undo();
      this.canvas.style.cursor = 'default'; this.render(); return;
    }
    // Cancel in-progress wall endpoint drag or floating
    if ((this._wallEndpointDragging || this._wallEndpointFloating) && this._wallEndpointDragInfo) {
      const info = this._wallEndpointDragInfo;
      if (info.whichEnd === 'start') { info.wall.start.x = info.origX; info.wall.start.y = info.origY; }
      else { info.wall.end.x = info.origX; info.wall.end.y = info.origY; }
      // Restore door params from snapshots
      for (const ds of info.doorSnapshots) {
        ds.door.hingeParam = this.getWallParam(ds.hingePos, ds.door.wall);
        ds.door.endParam = this.getWallParam(ds.endPos, ds.door.wall);
      }
      this._wallEndpointDragging = false; this._wallEndpointFloating = false; this._wallEndpointDragInfo = null;
      this.undo();
      this.canvas.style.cursor = 'default'; this.render(); return;
    }
    // Cancel in-progress wall corner drag
    if (this._wallCornerDragging && this._wallCornerDragInfo) {
      const info = this._wallCornerDragInfo;
      for (const aw of info.affectedWalls) {
        aw.wall.start.x = aw.origStartX; aw.wall.start.y = aw.origStartY;
        aw.wall.end.x = aw.origEndX; aw.wall.end.y = aw.origEndY;
      }
      for (const ds of info.doorSnapshots) {
        ds.door.hingeParam = this.getWallParam(ds.hingePos, ds.door.wall);
        ds.door.endParam = this.getWallParam(ds.endPos, ds.door.wall);
      }
      this._wallCornerDragging = false; this._wallCornerDragInfo = null;
      this.undo();
      this.canvas.style.cursor = 'default'; this.render(); return;
    }
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — cancel in-progress curve bulge drag.
    // No manual geometry restore: the drag may have created or collapsed a facet
    // group, so the wall SET can differ. undo() replaces rooms wholesale from the
    // state pushed at drag start and re-links door refs, which covers every case.
    if (this._curveBulgeDragging && this._curveBulgeDragInfo) {
      this._curveBulgeDragging = false; this._curveBulgeDragInfo = null;
      this.selectedWalls = new Set();
      this.undo();
      this.canvas.style.cursor = 'default'; this.render(); return;
    }
    // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
    // Cancel in-progress wall grab drag
    if (this._wallGrabDragging && this._wallGrabDragInfo) {
      const info = this._wallGrabDragInfo;
      info.wall.start.x = info.origStartX; info.wall.start.y = info.origStartY;
      info.wall.end.x = info.origEndX; info.wall.end.y = info.origEndY;
      // Restore connected walls
      for (const co of info.connStartOrig) { co.wall.start.x = co.startX; co.wall.start.y = co.startY; co.wall.end.x = co.endX; co.wall.end.y = co.endY; }
      for (const co of info.connEndOrig) { co.wall.start.x = co.startX; co.wall.start.y = co.startY; co.wall.end.x = co.endX; co.wall.end.y = co.endY; }
      // Restore door params
      for (const ds of info.doorSnapshots) {
        const wall = ds.door.wall;
        ds.door.hingeParam = this.getWallParam(ds.hingePos, wall);
        ds.door.endParam = this.getWallParam(ds.endPos, wall);
      }
      this._wallGrabDragging = false; this._wallGrabDragInfo = null;
      this.undo();
      this.canvas.style.cursor = 'default'; this.render(); return;
    }
    // Esc closes a pinned drawer (settings/designs/shortcuts) — unless mouse is hovering, then leave open until mouse-out
    const _drawerElMap = { settings: ['settingsDrawer','settingsToggle'], designs: ['designsDrawer','designsToggle'], shortcuts: ['shortcutsDrawer','shortcutsToggle'] };
    if (this._pinnedPopover && _drawerElMap[this._pinnedPopover]) {
      const [dId, tId] = _drawerElMap[this._pinnedPopover];
      const drawer = document.getElementById(dId); const toggle = document.getElementById(tId);
      this._pinnedPopover = null;
      if (this._syncDrawerPins) this._syncDrawerPins();
      if (this._syncPinIcons) this._syncPinIcons();
      const isHovered = (drawer && drawer.matches(':hover')) || (toggle && toggle.matches(':hover'));
      if (!isHovered) {
        if (drawer) drawer.classList.remove('open');
        if (toggle) toggle.classList.remove('open');
      }
      if (this._clearHelpSearch) this._clearHelpSearch();
      return;
    }
    document.getElementById('objectsPopover').classList.remove('visible');
    document.getElementById('zoomPopover').classList.remove('visible');
    document.getElementById('penPopover').classList.remove('visible');
    const mPop = document.getElementById('measurePopover'); if (mPop) mPop.classList.remove('visible');
    const wasZoomPinned = this._pinnedPopover === 'zoom';
    this._pinnedPopover = null;
    document.getElementById('zoomToggleBtn').classList.remove('active');
    if (this._syncPinIcons) this._syncPinIcons();
    if (this._syncDrawerPins) this._syncDrawerPins();
    if (this._isMobileToolbar) this._updateMobilePopoverState();
    if (document.activeElement) document.activeElement.blur();
    if (this.activeTool === 'select') {
      document.getElementById('selectToolBtn').classList.add('active');
      if (this.selectedWalls.size > 0 || this.selectedDoors.size > 0 || this.selectedObjects.size > 0) {
        this.selectedWalls = new Set(); this.selectedDoors = new Set(); this.selectedObjects = new Set();
        this.updateStatus('Selection cleared.');
        this._updateActionButtons();
        this.render();
        // Sync turf panel controls after selection cleared
        const _ov = window.roomDesignerOverlay;
        if (_ov) { const _b = document.getElementById('rdSidePanelBody'); if (_b) _ov._syncTurfPanelToSelection(_b); }
      } else if (wasZoomPinned) {
        this.updateStatus('Click to select items. Shift+click to multi-select. Click to box-select. Delete to remove.');
      }
      return;
    }
    if (this._doorSwingEdit) {
      this._doorSwingEdit = null;
      this.updateStatus('Swing edit cancelled.');
      this.render(); return;
    }
    if (this._selectHandleMode === 'doorSwingEdit') {
      // Cancel swing edit, revert to doorGrab
      this.undo();
      const door = this._selectHandleInfo ? this._selectHandleInfo.door : null;
      if (door && this.selectedDoors.has(door)) {
        this._selectHandleMode = 'doorGrab';
        this._selectHandleInfo = { door };
        this._setJoystickArrowsDirs(this._getDoorWallArrows(door.wall));
      } else {
        this._deactivateSelectHandle();
      }
      this._clearArrowPulse();
      this._clearCheckPulse();
      this._startArrowPulse(3000);
      this._startCheckPulse(3000);
      this.updateStatus('Swing edit cancelled.');
      this.render(); return;
    }
    if (this.doorPlacement) {
      if (this._doorMobile) {
        // Mobile door: escape backs up one phase
        if (this._doorMobile.phase === 3) { this._doorMobile.phase = 1; this.doorPlacement = null; this._doorMobile.swingOptions = null; this._doorMobile.swingIdx = null; this._doorMobile.swingOption = null; this.updateStatus('Slide door along wall, then tap \u2713.'); this._updateDoorMobileArrows(); this.render(); return; }
        this._resetDoorMobileState(); this.updateStatus('Door placement cancelled.'); this.render(); return;
      }
      if (this.doorPlacement.phase === 3) {
        this.doorPlacement.endParam = undefined;
        this.doorPlacement.phase = 2;
        this.updateStatus('Returned to end point selection. Click to set door end point.');
        this.render(); return;
      }
      this.doorPlacement = null; this.updateStatus('Door placement cancelled.'); this.render(); return;
    }
    if (this._doorMobile) { this._resetDoorMobileState(); this.updateStatus('Door placement cancelled.'); this.render(); return; }
    const _escapeFallback = (this._isMobile && window.innerWidth <= 480) ? 'hand' : 'select';
    if (this.mode === 'door') { this.setActiveTool(_escapeFallback); return; }
    if (this.activeTool === 'pen' || this.mode === 'draw') {
      // Wall segment mode escape handling
      if (this.penDrawMode === 'wallSegment') {
        if (this.currentWallSegmentGroup && this.currentWallSegmentGroup.segments.length > 0) {
          // Second Esc after invalid endpoint warning — discard chain
          if (this._wsEscPendingDiscard) {
            this._wsEscPendingDiscard = false;
            this.currentWallSegmentGroup = null; this.currentWall = null; this.hasPlacedFirstPoint = false; this._wsSnapSticky = null;
            this.updateStatus('Interior wall discarded. Click to start a new one, or Esc to exit.');
            this.render(); return;
          }
          // Finalize current chain (connected or free-standing partition).
          // CUSTOM_ROOMDESIGNER_BY_CLAUDE — asked BEFORE committing, because the
          // commit is what clears the chain. A free-standing wall is a legitimate
          // thing to draw, so it's kept and merely described, not refused.
          const _freeStanding = !this._isChainCommittable(this.currentWallSegmentGroup);
          const hidden = this._finishWallSegmentChain();
          if (hidden) this.updateStatus('Dividing line hidden — enable Seam Lines in Settings to see it', { urgent: true });
          else if (_freeStanding) this.updateStatus('Free-standing wall placed — it divides nothing yet. Draw off it any time, or drag an end onto a wall to connect it.');
          else this.updateStatus('Interior wall finished. Click to start a new one, or Esc to exit.');
          this.render(); return;
        }
        if (this.currentWall && this.hasPlacedFirstPoint) {
          // Cancel current unplaced segment
          this.currentWall = null; this.hasPlacedFirstPoint = false;
          this.updateStatus('Segment cancelled. Click to start, or Esc to exit.');
          this.render(); return;
        }
        // Second Esc — exit to select tool
        this.setActiveTool(_escapeFallback); return;
      }
      if (this._rectCorner1) { this._resetRectState(); this.updateStatus('Rectangle cancelled.'); this._updateJoystickVisibility(); this.render(); return; }
      // Freehand with unclosed walls — show warning instead of silently promoting
      if (this._freehandAutoPlaced) {
        this.currentWall = null; this.hasPlacedFirstPoint = false; this._resetFreehandState();
        if (this.currentRoom && this.currentRoom.walls.length > 0 && !this.currentRoom.closed && this.currentRoom.type !== 'subtract') {
          this._warnIncompleteRoom(() => {
            // Discard
            this.currentRoom = { walls: [], doors: [], cutouts: [], type: this.roomType, closed: false };
            this.updateStatus('Walls discarded.'); this._updateJoystickVisibility(); this.updateMeasurements(); this.render();
          }, () => {
            // Continue drawing — restore first-point state so user can resume
            this._freehandAutoPlaced = true;
          });
          return;
        }
        // Subtract mode or no walls — just discard
        if (this.currentRoom && this.currentRoom.walls.length > 0) {
          this.currentRoom.walls = this.currentRoom.walls.filter(w => this.getDistance(w.start, w.end) >= 1 / 12);
          this.currentRoom.closed = false; if (this.currentRoom.walls.length > 0) this.rooms.push(this.currentRoom);
          this.currentRoom = { walls: [], doors: [], cutouts: [], type: this.roomType, closed: false };
        }
        this.updateStatus('Freehand cancelled.'); this._updateJoystickVisibility(); this.updateMeasurements(); this.render(); return;
      }
      if (this.hasPlacedFirstPoint && this.currentWall) {
        this.currentWall = null; this.hasPlacedFirstPoint = false;
        if (this.currentRoom && this.currentRoom.walls.length > 0) {
          if (this.currentRoom.type === 'subtract') {
            this.currentRoom = { walls: [], doors: [], cutouts: [], type: this.roomType, closed: false };
            this.updateStatus('Subtraction cancelled. Click to start a new wall, or press Esc for cursor tool.');
          } else {
            // Show incomplete room warning for freehand unclosed walls
            this._warnIncompleteRoom(() => {
              this.currentRoom = { walls: [], doors: [], cutouts: [], type: this.roomType, closed: false };
              this.updateStatus('Walls discarded. Click to start a new wall, or press Esc for cursor tool.');
              this.updateMeasurements(); this.render();
            }, () => {
              // Continue drawing — do nothing
            });
            return;
          }
          this.updateMeasurements(); this.render();
        } else { this.updateStatus('Wall cancelled. Click to start a new wall, or press Esc for cursor tool.'); this.render(); }
      } else if (this.currentRoom && this.currentRoom.walls.length > 0) {
        if (this.currentRoom.type === 'subtract') {
          this.currentRoom = { walls: [], doors: [], cutouts: [], type: this.roomType, closed: false };
          this.updateStatus('Subtraction cancelled. Press Esc again for cursor tool.');
        } else {
          // Show incomplete room warning for freehand unclosed walls
          this._warnIncompleteRoom(() => {
            this.currentRoom = { walls: [], doors: [], cutouts: [], type: this.roomType, closed: false };
            this.updateStatus('Walls discarded. Press Esc again for cursor tool.');
            this.updateMeasurements(); this.render();
          }, () => {
            // Continue drawing
          });
          return;
        }
        this.updateMeasurements(); this.render();
      } else { this.setActiveTool(_escapeFallback); }
    } else { this.setActiveTool(_escapeFallback); }
  }

  setActiveTool(tool) {
    // Gate: if switching away from pen tool with freehand unclosed walls, show warning
    if (this.activeTool === 'pen' && tool !== 'pen' && this.penDrawMode === 'freehand' && this.currentRoom && this.currentRoom.walls.length > 0 && !this.currentRoom.closed && this.currentRoom.type !== 'subtract') {
      this._warnIncompleteRoom(() => {
        // Discard walls and proceed
        this.currentRoom = { walls: [], doors: [], cutouts: [], type: this.roomType, closed: false };
        this.currentWall = null; this.hasPlacedFirstPoint = false;
        this.updateMeasurements();
        this._doSetActiveTool(tool);
      }, () => { /* Continue drawing — stay on pen tool */ });
      return;
    }
    // Finish any in-progress wall segment chain when switching tools.
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — this used to need a guard against switching
    // while the Shape Not Closed dialog was still open, which stranded the chain in
    // currentWallSegmentGroup: drawn, but never committed, so nothing could click,
    // select or curve it. That dialog is gone — the chain always commits now — so
    // the guard is unreachable and has been removed with it. The parity fixes it
    // came with STAY: _hitTestSelect and _findWallContainer both still include the
    // in-progress chain, so anything visible is selectable whatever put it there.
    if (this.penDrawMode === 'wallSegment') this._finishWallSegmentChain();
    this._doSetActiveTool(tool);
  }

  _doSetActiveTool(tool) {
    if (this._labelEditorInput) this._labelEditorInput.blur();
    if (this._pendingEquipmentId) this._clearPendingEquipment();
    if (this._pendingLadder) { this._pendingLadder = null; this._pendingLadderPos = null; this._pendingLadderGhostPos = null; this._pendingLadderRotation = 0; }
    const measurePop = document.getElementById('measurePopover');
    if (measurePop) measurePop.classList.remove('visible');
    if (this._pinnedPopover === 'measure') { this._pinnedPopover = null; if (this._syncPinIcons) this._syncPinIcons(); }
    this.activeTool = tool;
    if (tool === 'select') this._boxSelectCooldownUntil = Date.now() + 300;
    this._zoomFitPanMode = false;
    this._penCancelledIdle = false;
    this._showRepeatButton = false;
    this._lastPlacedObject = null;
    this._pastePreview = null;
    if (window.roomDesignerOverlay) window.roomDesignerOverlay._clearFloorState();
    this._deactivateSelectHandle();
    this._wsSnapSticky = null;
    this._resetRectState(); this._resetFreehandState(); this._resetDoorMobileState(); this._resetTurfState(); this._resetPlatformState();
    document.getElementById('selectToolBtn').classList.toggle('active', tool === 'select');
    document.getElementById('handToolBtn').classList.toggle('active', tool === 'hand');
    document.getElementById('penToolBtn').classList.toggle('active', tool === 'pen');
    document.getElementById('objectsToolBtn').classList.toggle('active', tool === 'door');
    const _lblToolBtn = document.getElementById('labelToolBtn');
    if (_lblToolBtn) _lblToolBtn.classList.toggle('active', tool === 'label');
    document.getElementById('eraserBtn').classList.toggle('active', tool === 'eraser');
    const measureToolBtn = document.getElementById('measureToolBtn');
    if (measureToolBtn) measureToolBtn.classList.toggle('active', tool === 'measure');
    const floorEraserBtn = document.getElementById('floorEraserBtn');
    if (floorEraserBtn) floorEraserBtn.classList.remove('active');
    const floorRotateBtn = document.getElementById('floorRotateBtn');
    if (floorRotateBtn) floorRotateBtn.classList.remove('active');
    const floorRotatePop = document.getElementById('floorRotatePopover');
    if (floorRotatePop) floorRotatePop.classList.remove('visible');
    if (window.roomDesignerOverlay && window.roomDesignerOverlay._resetWallSnap) window.roomDesignerOverlay._resetWallSnap();
    // brandPalette popover removed — colors now in Finishing Touches side panel
    // Hide pen badge (+/−) when pen tool is not active or wallSegment mode
    const penBadge = document.getElementById('penBadge');
    if (penBadge) penBadge.style.display = (tool === 'pen' && this.penDrawMode !== 'wallSegment') || tool === 'eraser' ? '' : 'none';
    if (tool !== 'door') document.getElementById('objectsPopover').classList.remove('visible');
    if (tool !== 'label') { const _lp = document.getElementById('labelPopover'); if (_lp) _lp.classList.remove('visible'); }
    if (tool !== 'pen') document.getElementById('penPopover').classList.remove('visible');
    document.getElementById('zoomPopover').classList.remove('visible');
    document.getElementById('zoomToggleBtn').classList.remove('active');
    const bgImagePop = document.getElementById('bgImagePopover');
    if (bgImagePop) bgImagePop.classList.remove('visible');
    const bgImageBtn = document.getElementById('bgImageBtn');
    if (bgImageBtn) { bgImageBtn.classList.remove('active'); bgImageBtn.classList.remove('pinned'); }
    // Restore tool saved by bgImage popover
    this._preBgTool = null;
    this._preBgMode = null;
    // Close any pinned drawer
    const _drawerIds = [['settingsDrawer','settingsToggle'],['designsDrawer','designsToggle'],['shortcutsDrawer','shortcutsToggle']];
    for (const [dId, tId] of _drawerIds) {
      const d = document.getElementById(dId); const t = document.getElementById(tId);
      if (d) d.classList.remove('open'); if (t) { t.classList.remove('open'); t.classList.remove('pinned'); }
    }
    if (this._clearHelpSearch) this._clearHelpSearch();
    this._pinnedPopover = null;
    if (this._syncPinIcons) this._syncPinIcons();
    if (this._syncDrawerPins) this._syncDrawerPins();
    this._turfLogoFocusedIdx = null;
    this._turfLogoSelectedIdxs.clear();
    if (tool !== 'select') { this.selectedWalls = new Set(); this.selectedDoors = new Set(); this.selectedObjects = new Set(); this.selectionStart = null; this.selectionEnd = null; this.isSelecting = false; this._selectCorner1 = null; this._selectPreExisting = null; this._selectClickStart = null; this._selectRotating = false; this._selectRotateInfo = null; this._selectResizing = false; this._selectResizeInfo = null; this._selectDragOrigPositions = null; this._wallEndpointDragging = false; this._wallEndpointFloating = false; this._wallEndpointDragInfo = null; this._wallGrabDragging = false; this._wallGrabDragInfo = null; this._wallCornerDragging = false; this._wallCornerDragInfo = null; this._curveBulgeDragging = false; this._curveBulgeDragInfo = null; }
    this._stopEdgeAutoPan(); // CUSTOM_ROOMDESIGNER_BY_CLAUDE — tool switch ends any slide
    this._updateActionButtons();
    if (tool === 'select') {
      this.canvas.style.cursor = 'default'; this.currentWall = null; this.hasPlacedFirstPoint = false; this.mode = 'select';
      this.updateStatus(this._floorMode ? 'Click a floored room to select it. Delete to remove flooring.' : 'Click to select items. Shift+click to multi-select. Click to box-select. Delete to remove.');
    } else if (tool === 'hand') {
      this.canvas.style.cursor = 'grab'; this.currentWall = null; this.hasPlacedFirstPoint = false; this.mode = 'hand';
      this.updateStatus(this._floorMode ? 'Pan to explore your layout.' : 'Hand tool: Click and drag to pan.');
    } else if (tool === 'pen') {
      this.canvas.style.cursor = 'crosshair'; this.mode = 'draw';
      if (this.penDrawMode === 'wallSegment') {
        this.updateStatus('Click to place interior wall points. Enter, Esc, or double-click to finish.');
      } else {
        if (!this.currentRoom || this.currentRoom.walls.length === 0 || this.currentRoom.closed) this.promoteUnclosedRoom();
        this.updateStatus(this.penDrawMode === 'rectangle' ? 'Click two corners to draw a rectangular perimeter. Esc to exit' : 'Click to place perimeter points. Esc to exit');
      }
    } else if (tool === 'eraser') {
      this.canvas.style.cursor = 'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2724%27 height=%2724%27 viewBox=%270 0 24 24%27%3E%3Crect x=%274%27 y=%2710%27 width=%2716%27 height=%2710%27 rx=%272%27 fill=%27%23ff6b6b%27 stroke=%27%23333%27 stroke-width=%271.5%27 transform=%27rotate(-30 12 15)%27/%3E%3Crect x=%274%27 y=%2714%27 width=%2716%27 height=%276%27 rx=%271%27 fill=%27%23ffaaaa%27 stroke=%27%23333%27 stroke-width=%271%27 transform=%27rotate(-30 12 15)%27/%3E%3C/svg%3E") 12 20, pointer';
      this.currentWall = null; this.hasPlacedFirstPoint = false; this.mode = 'eraser';
      this.updateStatus('Eraser: Click on an object to delete it.');
    } else if (tool === 'label') {
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — mode stays 'select' so nothing downstream
      // that branches on mode has to learn a new one; the activeTool check in
      // handleMouseDown is what routes the click, and it runs first.
      this.canvas.style.cursor = 'crosshair';
      this.currentWall = null; this.hasPlacedFirstPoint = false; this.mode = 'select';
      this.updateStatus(this._labelText
        ? `"${this._labelText}" ready — click the plan to place it.`
        : 'Type a label — or pick one — then click the plan to place it.');
    } else if (tool === 'measure') {
      this._applyMeasureCursor(); this.mode = 'measure';
      this._labelHover = false; // Clear stale label-hover so it can't block measurement cursors
      this._measureStart = null; this._measureEnd = null; this._selectedMeasurements.clear(); this._measurePhase = 0; this._measurePendingEnd = null; this._measureDragging = null;
      this.updateStatus('Click two points to measure the distance between them.');
    }
    this._updateJoystickVisibility();
    this._updateFloatingDelete();
    this._updateFloatingCancel();
    this._updateFloatingRepeat();
    if (this._isMobileToolbar) this._updateMobilePopoverState();
    this.render();
  }

  _swapToolbarButtons(step) {
    const drawOnlyIds = ['penToolBtn', 'objectsToolBtn', 'shiftToggleBtn', 'bgImageBtn'];
    const floorOnlyIds = ['floorRotateBtn'];
    const equipOnlyIds = [];
    const finishingOnlyIds = [];
    const zoomBtn = document.getElementById('zoomToggleBtn');

    if (step === 'floor') {
      for (const id of drawOnlyIds) {
        const el = document.getElementById(id);
        if (el) el.style.display = 'none';
      }
      for (const id of floorOnlyIds) {
        const el = document.getElementById(id);
        if (el) el.style.display = '';
      }
      for (const id of equipOnlyIds) {
        const el = document.getElementById(id);
        if (el) el.style.display = 'none';
      }
      for (const id of finishingOnlyIds) {
        const el = document.getElementById(id);
        if (el) el.style.display = 'none';
      }
      if (zoomBtn) zoomBtn.style.order = '';
    } else if (step === 'equipment') {
      // Equipment step: hide draw + floor + finishing tools, keep select/hand/zoom/eraser
      for (const id of drawOnlyIds) {
        const el = document.getElementById(id);
        if (el) el.style.display = 'none';
      }
      for (const id of floorOnlyIds) {
        const el = document.getElementById(id);
        if (el) el.style.display = 'none';
      }
      for (const id of equipOnlyIds) {
        const el = document.getElementById(id);
        if (el) el.style.display = '';
      }
      for (const id of finishingOnlyIds) {
        const el = document.getElementById(id);
        if (el) el.style.display = 'none';
      }
      if (zoomBtn) zoomBtn.style.order = '';
    } else if (step === 'finishing') {
      // Finishing touches: hide draw + floor + equipment tools, show palette, keep select/hand/zoom
      for (const id of drawOnlyIds) {
        const el = document.getElementById(id);
        if (el) el.style.display = 'none';
      }
      for (const id of floorOnlyIds) {
        const el = document.getElementById(id);
        if (el) el.style.display = 'none';
      }
      for (const id of equipOnlyIds) {
        const el = document.getElementById(id);
        if (el) el.style.display = 'none';
      }
      for (const id of finishingOnlyIds) {
        const el = document.getElementById(id);
        if (el) el.style.display = '';
      }
      if (zoomBtn) zoomBtn.style.order = '';
    } else {
      for (const id of drawOnlyIds) {
        const el = document.getElementById(id);
        if (el) el.style.display = '';
      }
      for (const id of floorOnlyIds) {
        const el = document.getElementById(id);
        if (el) el.style.display = 'none';
      }
      for (const id of equipOnlyIds) {
        const el = document.getElementById(id);
        if (el) el.style.display = 'none';
      }
      for (const id of finishingOnlyIds) {
        const el = document.getElementById(id);
        if (el) el.style.display = 'none';
      }
      if (zoomBtn) zoomBtn.style.order = '';
    }

    /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — the Label tool lives on CUSTOMIZE only.
       Labelling is the last pass over a finished plan, not something you do while
       still drawing it, and the earlier steps each want the canvas click for their
       own tool. It is handled here rather than through finishingOnlyIds because
       leaving the tool active into a step that hides it would strand a button you
       cannot reach — so it also drops back to select on the way out. */
    const lblBtn = document.getElementById('labelToolBtn');
    if (lblBtn) {
      const labelStep = (step === 'finishing');
      lblBtn.style.display = labelStep ? '' : 'none';
      if (!labelStep && this.activeTool === 'label') this.setActiveTool('select');
    }
  }

  promoteUnclosedRoom() {
    for (let i = 0; i < this.rooms.length; i++) {
      if (!this.rooms[i].closed && this.rooms[i].walls.length > 0) {
        if (this.currentRoom && this.currentRoom.walls.length > 0) this.rooms.push(this.currentRoom);
        this.currentRoom = this.rooms.splice(i, 1)[0]; this.currentWall = null; this.hasPlacedFirstPoint = false; this.updateMeasurements(); return;
      }
    }
  }

  setMode(mode) {
    this.mode = mode; this.doorPlacement = null; this._doorMobile = null; this._doorMobilePrompt = false;
    // Kill any in-progress joystick hold-repeat from previous tool
    clearTimeout(this._joystickHoldTimer); clearTimeout(this._joystickHoldInterval);
    this._joystickHoldTimer = null; this._joystickHoldInterval = null;
    // Clear stale rect pulse from draw button when switching modes
    if (this._rectPulseTimer) { clearTimeout(this._rectPulseTimer); this._rectPulseTimer = null; }
    const _penBtn = document.getElementById('penToolBtn');
    if (_penBtn) _penBtn.classList.remove('rect-pulse-hint', 'rect-pulse-subtract');
    if (mode === 'door') {
      this.activeTool = 'door'; this.canvas.style.cursor = 'crosshair';
      document.getElementById('selectToolBtn').classList.remove('active');
      document.getElementById('handToolBtn').classList.remove('active');
      document.getElementById('penToolBtn').classList.remove('active');
      document.getElementById('eraserBtn').classList.remove('active');
      document.getElementById('objectsToolBtn').classList.add('active');
      const _measureBtn = document.getElementById('measureToolBtn');
      if (_measureBtn) _measureBtn.classList.remove('active');
      const _measurePop = document.getElementById('measurePopover');
      if (_measurePop) _measurePop.classList.remove('visible');
      if (this._pinnedPopover === 'measure') { this._pinnedPopover = null; if (this._syncPinIcons) this._syncPinIcons(); }
      const _bgPop = document.getElementById('bgImagePopover');
      if (_bgPop) _bgPop.classList.remove('visible');
      const _bgBtn = document.getElementById('bgImageBtn');
      if (_bgBtn) _bgBtn.classList.remove('active');
      this._preBgTool = null; this._preBgMode = null;
      const _penBadge = document.getElementById('penBadge');
      if (_penBadge) _penBadge.style.display = 'none';
      this.currentWall = null; this.hasPlacedFirstPoint = false;
      // On mobile, clear mouse position so joystick waits for first tap
      // (door auto-place is triggered by door-type sub-button clicks, not here)
      if (this._isMobileToolbar) { this.mouseWorldRaw = null; this.mouseWorld = null; }
      if (this.objectType === 'rectangle') this.updateStatus('Click to place a column.');
      else if (this.objectType === 'circle') this.updateStatus('Click to place a pillar.');
      else this.updateStatus('Hover over a wall and click to set the door hinge point.');
      this._updateJoystickVisibility();
      this._updateFloatingDelete();
      this._updateFloatingCancel();
      this._updateFloatingRepeat();
      if (this._isMobileToolbar) this._updateMobilePopoverState();
    }
  }

  /* ── COORDINATE SYSTEM & ZOOM ── */

  zoom(factor) {
    // Zoom toward last known mouse position if available, otherwise canvas center
    const cx = (this._lastMouseScreenX != null) ? this._lastMouseScreenX : this._cssWidth / 2;
    const cy = (this._lastMouseScreenY != null) ? this._lastMouseScreenY : this._cssHeight / 2;
    const wb = this.screenToWorld(cx, cy);
    this.zoomLevel = Math.max(this.minZoom, Math.min(this.maxZoom, this.zoomLevel * factor));
    const wa = this.worldToScreen(wb.x, wb.y);
    this.panX += cx - wa.x;
    this.panY += cy - wa.y;
    document.getElementById('zoomLevel').textContent = Math.round(this.zoomLevel * 100) + '%';
    if (this._onZoomHook) this._onZoomHook();
    if (this._hoverCalloutEl) this._dismissHoverCallout();
    this.render();
  }

  /* CUSTOM_GYMLAYOUT_BY_CLAUDE (Ryan 2026-10-10) — `inset` shrinks the fitted zoom so the plan
     sits with breathing room (0.9 = fit minus 10%). Every design load uses LOAD_FIT_INSET;
     the zoom button still fits edge to edge. */
  fitToView({ inset = 1 } = {}) {
    const allPoints = [];
    [...this.rooms, this.currentRoom].forEach(room => {
      if (!room) return; room.walls.forEach(wall => { allPoints.push(wall.start); allPoints.push(wall.end); });
    });
    for (const obj of this.objects) {
      if (obj.type === 'rectangle' || obj.type === 'equipment' || obj.type === 'turf' || obj.type === 'platform') {
        const hw = obj.width / 2, hd = obj.depth / 2;
        const rot = obj.rotation || 0;
        const corners = [[-hw, -hd], [hw, -hd], [hw, hd], [-hw, hd]];
        for (const [lx, ly] of corners) {
          const p = this._rotatePoint(obj.x + lx, obj.y + ly, obj.x, obj.y, rot);
          allPoints.push(p);
        }
      } else if (obj.type === 'circle') {
        const r = obj.diameter / 2;
        allPoints.push({ x: obj.x - r, y: obj.y - r });
        allPoints.push({ x: obj.x + r, y: obj.y + r });
      }
    }
    if (allPoints.length === 0) return;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    allPoints.forEach(p => { minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y); });
    const padding = 3; minX -= padding; minY -= padding; maxX += padding; maxY += padding;
    const contentWidth = maxX - minX; const contentHeight = maxY - minY;
    if (contentWidth <= 0 || contentHeight <= 0) return;
    // Account for side panel covering left portion of canvas (desktop only — mobile is bottom sheet)
    let visibleLeft = 0, visibleWidth = this._cssWidth;
    if (!this._isMobileToolbar) {
      const panel = document.querySelector('.rd-side-panel');
      if (panel && (panel.classList.contains('open') || panel.classList.contains('minimized'))) {
        visibleLeft = panel.offsetWidth;
        visibleWidth = this._cssWidth - visibleLeft;
      }
    }
    // When rotated 90°/270°, content width/height swap relative to screen
    const a = this._canvasRotationRad;
    const cosA = Math.abs(Math.cos(a)), sinA = Math.abs(Math.sin(a));
    const screenContentW = contentWidth * cosA + contentHeight * sinA;
    const screenContentH = contentWidth * sinA + contentHeight * cosA;
    const scaleX = visibleWidth / (screenContentW * this.basePixelsPerFoot);
    const scaleY = this._cssHeight / (screenContentH * this.basePixelsPerFoot);
    this.zoomLevel = Math.min(scaleX, scaleY);
    // View-only: scale down to 80% so labels and bottom buttons aren't clipped
    if (this._viewOnly) this.zoomLevel *= 0.80;
    if (inset > 0 && inset < 1) this.zoomLevel *= inset;
    this.zoomLevel = Math.max(this.minZoom, Math.min(this.maxZoom, this.zoomLevel));
    const centerX = (minX + maxX) / 2; const centerY = (minY + maxY) / 2;
    const screenCenter = this.worldToScreen(centerX, centerY);
    this.panX += (visibleLeft + visibleWidth / 2) - screenCenter.x;
    this.panY += this._cssHeight / 2 - screenCenter.y;
    document.getElementById('zoomLevel').textContent = Math.round(this.zoomLevel * 100) + '%'; this.render();
  }

  _rotateCanvas90() {
    if (this._rotateAnimating) return;
    const oldStep = this.canvasRotation;
    this.canvasRotation = (this.canvasRotation + 1) % 4;
    const targetRad = this.canvasRotation * Math.PI / 2;
    // Keep pan centered on the same world point
    const cx = this._cssWidth / 2, cy = this._cssHeight / 2;
    if (this._viewOnly) {
      // Animated spin for view-only (presentation quality)
      this._rotateAnimating = true;
      const startRad = this._canvasRotationRad;
      const animTargetRad = startRad + Math.PI / 2; // always +90° forward
      const wb = this.screenToWorld(cx, cy);
      const duration = 500;
      const startTime = performance.now();
      const startPanX = this.panX, startPanY = this.panY;
      const easeInOutCubic = t => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      const animate = (now) => {
        const t = Math.min((now - startTime) / duration, 1);
        const et = easeInOutCubic(t);
        this._canvasRotationRad = startRad + (animTargetRad - startRad) * et;
        // Re-center on the same world point
        const wa = this.worldToScreen(wb.x, wb.y);
        this.panX += cx - wa.x;
        this.panY += cy - wa.y;
        this._doRender();
        if (t < 1) {
          requestAnimationFrame(animate);
        } else {
          this._canvasRotationRad = targetRad; // normalize (e.g. 2π → 0)
          this._rotateAnimating = false;
          this.render();
        }
      };
      requestAnimationFrame(animate);
    } else {
      // Instant snap for edit mode
      const wb = this.screenToWorld(cx, cy);
      this._canvasRotationRad = targetRad;
      const wa = this.worldToScreen(wb.x, wb.y);
      this.panX += cx - wa.x;
      this.panY += cy - wa.y;
      this.render();
    }
  }

  _toggleFullscreen() {
    const el = document.querySelector('.room-designer');
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      el.requestFullscreen().catch(() => {});
    }
  }

  handleWheel(e) {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
    const rect = this.canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left; const my = e.clientY - rect.top;
    const wb = this.screenToWorld(mx, my);
    this.zoomLevel = Math.max(this.minZoom, Math.min(this.maxZoom, this.zoomLevel * factor));
    const wa = this.worldToScreen(wb.x, wb.y);
    this.panX += mx - wa.x;
    this.panY += my - wa.y;
    document.getElementById('zoomLevel').textContent = Math.round(this.zoomLevel * 100) + '%';
    if (this._onZoomHook) this._onZoomHook();
    if (this._hoverCalloutEl) this._dismissHoverCallout();
    if (!this._wheelRAF) { this._wheelRAF = requestAnimationFrame(() => { this._wheelRAF = null; this.render(); }); }
  }

  screenToWorld(sx, sy) {
    const scale = this.basePixelsPerFoot * this.zoomLevel;
    const a = this._canvasRotationRad;
    if (a === 0) return { x: (sx - this.panX) / scale, y: (sy - this.panY) / scale };
    const dx = sx - this.panX, dy = sy - this.panY;
    const cos = Math.cos(-a), sin = Math.sin(-a);
    return { x: (dx * cos - dy * sin) / scale, y: (dx * sin + dy * cos) / scale };
  }
  worldToScreen(wx, wy) {
    const scale = this.basePixelsPerFoot * this.zoomLevel;
    const a = this._canvasRotationRad;
    if (a === 0) return { x: wx * scale + this.panX, y: wy * scale + this.panY };
    const sx = wx * scale, sy = wy * scale;
    const cos = Math.cos(a), sin = Math.sin(a);
    return { x: sx * cos - sy * sin + this.panX, y: sx * sin + sy * cos + this.panY };
  }
  get pixelsPerFoot() { return this.basePixelsPerFoot * this.zoomLevel; }
  get _hitRadius() { return this._isMobile ? 20 : 8; }
  get _handleSize() { return this._isMobile ? 12 : 6; }
  get _endpointDotSize() { return this._isMobile ? 8 : 4; }

  /* ── TURF FIELD CENTER ── */

  // Returns {x, y} in turf-local coords — center of usable field area (excluding sled lanes)
  _getTurfFieldCenter(obj) {
    if (!obj || obj.type !== 'turf' || !obj.turfSledLanes) return { x: 0, y: 0 };
    const SLW = 3.25, SDW = 0.5;
    const count = obj.turfSledLaneCount || 2;
    const bw = count * SLW + (count - 1) * SDW;
    const edges = this._rotateEdges(obj.turfSledLaneEdges || ['right'], obj.rotation || 0);
    const rotated = obj.turfMarkingsRotated || false;
    // Sled widths per edge in markings-local space (edge names used directly by renderer)
    const slL = edges.includes('left') ? bw : 0;
    const slR = edges.includes('right') ? bw : 0;
    const slT = edges.includes('top') ? bw : 0;
    const slB = edges.includes('bottom') ? bw : 0;
    // When rotated, markings space is ctx.rotate(-π/2) from turf-local:
    // markings (mx,my) → turf (my, -mx), so axes swap and X flips
    if (rotated) return { x: (slT - slB) / 2, y: (slR - slL) / 2 };
    return { x: (slL - slR) / 2, y: (slT - slB) / 2 };
  }

  /* ── GRID & SNAPPING ── */

  snapToGrid(wx, wy) {
    const spi = this.basePixelsPerFoot * this.zoomLevel / 12;
    const fine = spi >= 3 || this._bgTraceActive;
    // Compute grid step in feet — metric uses 0.25 m / 1 cm, imperial uses 1 ft / 1 in
    const gridFt = (this.unit === 'meters' || this.unit === 'm-cm' || this.unit === 'cm')
      ? (fine ? 0.032808399 : 0.82020997) // 1 cm or 25 cm in feet (exact: 0.01*3.2808399, 0.25*3.2808399)
      : (fine ? 0.083333333 : 1);          // 1 inch or 1 foot
    return {
      x: Math.round(wx / gridFt) * gridFt,
      y: Math.round(wy / gridFt) * gridFt
    };
  }

  /**
   * CUSTOM_ROOMDESIGNER_BY_CLAUDE — how far one rotation step turns something.
   *
   * The granularity follows the DRAWING, per Ryan: a plain rectangular room keeps
   * exactly the behaviour it always had, which covers most jobs, and a room built
   * from curves shifts the whole pair one notch finer — because there is no square
   * to line up with in the first place, and a platform genuinely needs to sit at
   * something like 34° while the rolls run at 0.
   *
   *                     default        Shift
   *   square drawing    22.5°          90°        (equipment — unchanged)
   *                     90°            90°        (platforms — unchanged)
   *   curved drawing    3.75°          22.5°      (both)
   *
   * The curved default was subdivided once more at Ryan's request — a room of curves
   * asks for angles that a 7.5° ladder still couldn't quite hit. Shift deliberately
   * stays at 22.5° rather than following it down: that keeps a genuinely coarse snap
   * within reach, which is what you want when a piece DOES need to line up square.
   *
   * Nothing is ever unreachable: 7.5, 22.5 and 90 are all whole multiples of 3.75
   * (2, 6 and 24 steps).
   *
   * "Curved" is a property of the whole drawing rather than the room a piece sits
   * in. Deliberate: equipment can sit outside any room, a platform can straddle a
   * zone, and "why is this one rotating differently from that one" is a far worse
   * mystery than one rule you can state in a sentence.
   */
  _rotationStep(isPlatform) {
    if (!this._drawingHasCurves()) {
      // Untouched legacy behaviour.
      if (isPlatform) return Math.PI / 2;                              // 90° only
      return this.shiftHeld ? Math.PI / 2 : Math.PI / 8;               // 90° : 22.5°
    }
    return this.shiftHeld ? Math.PI / 8 : Math.PI / 48;                // 22.5° : 3.75°
  }

  /** Does anything in this drawing curve? Drives _rotationStep — see the table there. */
  _drawingHasCurves() {
    for (const room of (this.rooms || [])) {
      if (!room) continue;
      for (const w of (room.walls || [])) if (w.curveId) return true;
      if (room.cutouts) for (const c of room.cutouts) for (const w of (c.walls || [])) if (w.curveId) return true;
    }
    for (const g of (this.wallSegmentGroups || [])) {
      for (const s of ((g && g.segments) || [])) if (s.curveId) return true;
    }
    return false;
  }

  /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — axis handling for wall drags.
     Shift, or the shift-lock toggle, forces the drag square, exactly as it does
     everywhere else in the tool. WITHOUT it, square is a magnet rather than a cage:
     inside about 5 degrees it snaps to the axis, and beyond that any angle is free.

     It used to be a cage. The test was `screenPixelsPerInch < 3`, and the default
     20px/ft is 1.67px/in — so it was true at every zoom anyone actually works at,
     and you had to be past ~1.8x zoom before a diagonal became possible at all.
     Worse, `shiftHeld` was folded into the SAME condition, so the modifier that
     normally means "constrain" was the only thing you could reach, and there was no
     way to opt out: once a divider was drawn it could never be re-angled.

     Coarse zoom is a reason for a coarser GRID, which snapToGrid already applies.
     It is not a reason to allow only four directions. */
  _axisSnapDelta(dx, dy, force) {
    const ax = Math.abs(dx), ay = Math.abs(dy);
    if (force) return ax >= ay ? { dx: dx, dy: 0 } : { dx: 0, dy: dy };
    const TAN = 0.0875;                    // tan(5deg)
    if (ay <= ax * TAN) return { dx: dx, dy: 0 };
    if (ax <= ay * TAN) return { dx: 0, dy: dy };
    return { dx: dx, dy: dy };
  }

  constrainToAxis(start, end) {
    if (!this.shiftHeld) return end;
    const dx = Math.abs(end.x - start.x); const dy = Math.abs(end.y - start.y);
    return dx >= dy ? { x: end.x, y: start.y } : { x: start.x, y: end.y };
  }

  /* ── MEASUREMENT HELPERS ── */

  _isMetric() { return this.unit === 'm-cm' || this.unit === 'meters' || this.unit === 'cm'; }

  formatMeasurement(feet) {
    if (this.unit === 'feet') return `${feet.toFixed(2)} ft`;
    if (this.unit === 'inches') return `${(feet * 12).toFixed(1)} in`;
    if (this.unit === 'meters') return `${(feet * 0.3048).toFixed(2)} m`;
    if (this.unit === 'cm') return `${(feet * 30.48).toFixed(1)} cm`;
    if (this.unit === 'm-cm') {
      const totalCm = Math.round(feet * 30.48);
      const m = Math.floor(totalCm / 100);
      const cm = totalCm - m * 100;
      return `${m}m ${cm}cm`;
    }
    const totalInches = Math.round(feet * 12); const ft = Math.floor(totalInches / 12); const inches = totalInches - ft * 12;
    return inches === 0 ? `${ft}'` : `${ft}' ${inches}"`;
  }

  /**
   * Returns viewport-safe clamping bounds in CSS pixels, accounting for open
   * popovers (pen/objects/zoom) so labels never underlap them.
   * Result: { left, top, right, bottom } — the safe zone for label positions.
   */
  _getLabelClampBounds() {
    const cw = this._cssWidth || (this.canvas.width / (this._dpr || 1));
    const ch = this._cssHeight || (this.canvas.height / (this._dpr || 1));
    const baseMargin = Math.min(cw, ch) * 0.05;
    let left = baseMargin, top = baseMargin, right = cw - baseMargin, bottom = ch - baseMargin;
    // Check for open popovers that overlay the canvas
    const popIds = ['penPopover', 'objectsPopover', 'zoomPopover', 'measurePopover'];
    const canvasRect = this.canvas.getBoundingClientRect();
    for (const id of popIds) {
      const el = document.getElementById(id);
      if (!el || !el.classList.contains('visible')) continue;
      const pr = el.getBoundingClientRect();
      // Skip if popover doesn't actually overlap canvas
      if (pr.right <= canvasRect.left || pr.left >= canvasRect.right ||
          pr.bottom <= canvasRect.top || pr.top >= canvasRect.bottom) continue;
      // Convert popover edges to canvas-relative CSS pixels
      const pLeft = pr.left - canvasRect.left;
      const pRight = pr.right - canvasRect.left;
      const pTop = pr.top - canvasRect.top;
      const pBottom = pr.bottom - canvasRect.top;
      const gap = 12; // breathing room between label and popover edge
      // Push the safe zone inward from whichever side the popover sits on
      if (pLeft < cw * 0.5) {
        // Popover on left half — push left bound rightward
        left = Math.max(left, pRight + gap);
      } else {
        // Popover on right half — push right bound leftward
        right = Math.min(right, pLeft - gap);
      }
      if (pTop < ch * 0.5) {
        top = Math.max(top, pBottom + gap);
      } else {
        bottom = Math.min(bottom, pTop - gap);
      }
    }
    return { left, top, right, bottom };
  }

  /**
   * Clamp a world-space point so its label stays within the safe viewport zone.
   * @param {number} wx - world X
   * @param {number} wy - world Y
   * @param {number} scale - current worldToScreen scale (basePixelsPerFoot * zoomLevel)
   * @param {object} [bounds] - pre-computed bounds from _getLabelClampBounds() (optional, avoids re-query)
   * @returns {{x: number, y: number}} clamped world coords
   */
  _clampLabelToViewport(wx, wy, scale, bounds) {
    const b = bounds || this._getLabelClampBounds();
    const s = this.worldToScreen(wx, wy);
    let sx = Math.max(b.left, Math.min(b.right, s.x));
    let sy = Math.max(b.top, Math.min(b.bottom, s.y));
    return this.screenToWorld(sx, sy);
  }

  formatArea(sqFeet) {
    if (this.unit === 'inches') return `${(sqFeet * 144).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} sq in`;
    if (this.unit === 'meters' || this.unit === 'm-cm') return `${(sqFeet * 0.092903).toFixed(2).replace(/\.00$/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',')} sq m`;
    if (this.unit === 'cm') return `${(sqFeet * 929.03).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} sq cm`;
    return `${sqFeet.toFixed(2).replace(/\.00$/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',')} sq ft`;
  }

  getDistance(p1, p2) { return RoomDesignerMath.getDistance(p1, p2); }

  /* ── Measurement helpers ── */

  _hitTestMeasurement(worldPoint) {
    const scale = this.basePixelsPerFoot * this.zoomLevel;
    const hitRadius = (this._isMobile ? 20 : 10) / scale;
    for (let i = this._measurements.length - 1; i >= 0; i--) {
      const m = this._measurements[i];
      // Compute the actual dimension line (accounts for offset)
      const dx = m.end.x - m.start.x, dy = m.end.y - m.start.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      let dimStart = m.start, dimEnd = m.end;
      if (m.offset && m.offset !== 0 && len > 0.01) {
        const nx = -dy / len, ny = dx / len;
        dimStart = { x: m.start.x + nx * m.offset, y: m.start.y + ny * m.offset };
        dimEnd = { x: m.end.x + nx * m.offset, y: m.end.y + ny * m.offset };
      }
      // Hit-test the dimension line segment
      const dist = RoomDesignerMath.distanceToWall(worldPoint, { start: dimStart, end: dimEnd });
      if (dist < hitRadius) return m;
      // Also hit-test the extension lines (for offset measurements)
      if (m.offset && m.offset !== 0 && len > 0.01) {
        const extDist1 = RoomDesignerMath.distanceToWall(worldPoint, { start: m.start, end: dimStart });
        if (extDist1 < hitRadius) return m;
        const extDist2 = RoomDesignerMath.distanceToWall(worldPoint, { start: m.end, end: dimEnd });
        if (extDist2 < hitRadius) return m;
      }
      // Hit-test the label midpoint
      const lp = this._getMeasureLabelPos(m);
      const labelDist = Math.sqrt((worldPoint.x - lp.x) ** 2 + (worldPoint.y - lp.y) ** 2);
      if (labelDist < hitRadius * 1.5) return m;
    }
    return null;
  }

  _findNearestMeasurementEndpoint(worldPoint) {
    const scale = this.basePixelsPerFoot * this.zoomLevel;
    const snapRadius = (this._isMobile ? 20 : 10) / scale;
    let best = null, bestDist = snapRadius;
    for (const m of this._measurements) {
      for (const pt of [m.start, m.end]) {
        const d = Math.sqrt((worldPoint.x - pt.x) ** 2 + (worldPoint.y - pt.y) ** 2);
        if (d < bestDist) { bestDist = d; best = { x: pt.x, y: pt.y }; }
      }
    }
    return best;
  }

  _hitTestMeasurementLabel(worldPoint) {
    const scale = this.basePixelsPerFoot * this.zoomLevel;
    const hitRadius = (this._isMobile ? 25 : 12) / scale;
    for (let i = this._measurements.length - 1; i >= 0; i--) {
      const m = this._measurements[i];
      const lp = this._getMeasureLabelPos(m);
      const d = Math.sqrt((worldPoint.x - lp.x) ** 2 + (worldPoint.y - lp.y) ** 2);
      if (d < hitRadius) return m;
      // Also check the grab handle zone above the label for selected measurements
      if (this._selectedMeasurements.has(m.id)) {
        const fontSize = (this.largeUI ? 14.5 : 12) / scale;
        const pad = 4 / scale;
        const hGap = 3 / scale;
        const hSize = this._handleSize / scale;
        const handleCenterY = lp.y - fontSize / 2 - pad - hGap - hSize / 2;
        const dh = Math.sqrt((worldPoint.x - lp.x) ** 2 + (worldPoint.y - handleCenterY) ** 2);
        if (dh < hitRadius) return m;
      }
    }
    return null;
  }

  _getMeasureLabelPos(m) {
    // Returns the world position of the measurement label (midpoint of the dimension line)
    if (m.offset && m.offset !== 0) {
      const dx = m.end.x - m.start.x, dy = m.end.y - m.start.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len > 0.01) {
        const nx = -dy / len, ny = dx / len;
        return { x: (m.start.x + m.end.x) / 2 + nx * m.offset, y: (m.start.y + m.end.y) / 2 + ny * m.offset };
      }
    }
    return { x: (m.start.x + m.end.x) / 2, y: (m.start.y + m.end.y) / 2 };
  }

  _applyMeasureCursor() {
    this.canvas.style.cursor = 'crosshair';
  }

  _updateMeasureClearBtn() {
    this._updateMeasurePopoverCount();
    // Hide popover if no measurements left AND tool isn't active/pinned
    if (this._measurements.length === 0 && this.mode !== 'measure' && this._pinnedPopover !== 'measure') {
      const pop = document.getElementById('measurePopover');
      if (pop) pop.classList.remove('visible');
    }
  }

  _updateMeasurePopoverCount() {
    const el = document.getElementById('measureCount');
    if (el) {
      const n = this._measurements.length;
      el.textContent = n === 0 ? 'No measurements yet' : (n === 1 ? '1 measurement' : n + ' measurements');
    }
    // Show/hide Clear All button
    const clearBtn = document.getElementById('measureClearBtn');
    if (clearBtn) clearBtn.style.display = this._measurements.length > 0 ? '' : 'none';
  }

  _deleteSelectedMeasurements() {
    if (this._selectedMeasurements.size === 0) return false;
    this.saveUndoState();
    this._measurements = this._measurements.filter(m => !this._selectedMeasurements.has(m.id));
    this._selectedMeasurements.clear();
    this._updateMeasureClearBtn();
    this.render();
    return true;
  }

  /* ── MOUSE HANDLERS ── */

  handleMouseDown(e) {
    if (!this._viewOnly && this._floorMode && this.activeTool !== 'hand' && this.activeTool !== 'select' && this.activeTool !== 'floor-eraser' && this.activeTool !== 'turf-draw' && this.activeTool !== 'platform-place' && this.mode !== 'measure' && e.button === 0) return;
    const rect = this.canvas.getBoundingClientRect();
    const sx = e.clientX - rect.left; const sy = e.clientY - rect.top;
    const world = this.screenToWorld(sx, sy); const snapped = this.snapToGrid(world.x, world.y);
    this._processDown(sx, sy, world, snapped, e.button, e.shiftKey);
    // Disable pointer events on overlaying UI during drag interactions
    if (this._selectRotating || this._selectDragging || this._selectResizing) {
      this._setOverlayPointerEvents(false);
    }
  }

  /** Hit-test any turf logo body (not handles) in reverse z-order. Returns { obj, logoIdx } or null. */
  _hitTestAnyTurfLogoBody(world) {
    const _onFinishing = window.roomDesignerOverlay && window.roomDesignerOverlay.currentStep === 'finishing';
    if (!this._floorMode && !_onFinishing) return null;
    for (let i = this.objects.length - 1; i >= 0; i--) {
      const obj = this.objects[i];
      if (obj.type !== 'turf' || !obj.turfLogos || obj.turfLogos.length === 0) continue;
      // world → turf-local
      const or = obj.rotation || 0;
      const co = Math.cos(-or), so = Math.sin(-or);
      const dx = world.x - obj.x, dy = world.y - obj.y;
      const tlx = dx * co - dy * so, tly = dx * so + dy * co;
      // Check logos in reverse (last = top z-order)
      for (let li = obj.turfLogos.length - 1; li >= 0; li--) {
        const logo = obj.turfLogos[li];
        if ((!logo.image && logo.type !== 'ladder' && logo.type !== 'dotdrill') || !logo.width || !logo.height) continue;
        const lx = logo.x || 0, ly = logo.y || 0, lr = logo.rotation || 0;
        const cl = Math.cos(-lr), sl = Math.sin(-lr);
        const ldx = tlx - lx, ldy = tly - ly;
        const llx = ldx * cl - ldy * sl, lly = ldx * sl + ldy * cl;
        if (Math.abs(llx) <= logo.width / 2 && Math.abs(lly) <= logo.height / 2) {
          // Ladders/dot drills only clickable on Customize tab, not Floor tab
          if ((logo.type === 'ladder' || logo.type === 'dotdrill') && !_onFinishing) continue;
          return { obj, logoIdx: li };
        }
      }
    }
    return null;
  }

  _processDown(sx, sy, world, snapped, button, shiftKey) {
    // View-only mode: always allow pan (even on equipment). Defer equipment
    // color panel to mouseup so click-hold-drag still pans the canvas.
    if (this._viewOnly) {
      if (this._hoverCalloutEl) this._dismissHoverCallout();
      // Hit-test for equipment/turf with color options (defer action to mouseup)
      this._viewOnlyHitEquipId = null;
      this._viewOnlyHitTurfObj = null;
      const pad = Math.max(0, 8 / this.pixelsPerFoot);
      for (const obj2 of this.objects) {
        if (obj2.type === 'equipment') {
          const halfW = obj2.width / 2, halfD2 = obj2.depth / 2;
          const rot2 = obj2.rotation || 0;
          const local = this._rotatePoint(world.x, world.y, obj2.x, obj2.y, -rot2);
          if (local.x >= obj2.x - halfW - pad && local.x <= obj2.x + halfW + pad &&
              local.y >= obj2.y - halfD2 - pad && local.y <= obj2.y + halfD2 + pad) {
            const eqInfo = this._equipmentImages && this._equipmentImages[obj2.equipmentId];
            const overlay = window.roomDesignerOverlay;
            if (eqInfo && eqInfo.vendor && overlay && overlay._getBrandColorConfig) {
              const vendorConf = overlay._getBrandColorConfig()[eqInfo.vendor];
              if (vendorConf) this._viewOnlyHitEquipId = obj2.equipmentId;
            }
            break;
          }
        } else if (obj2.type === 'turf' && obj2.turfProductId) {
          const halfW = obj2.width / 2, halfD2 = obj2.depth / 2;
          const rot2 = obj2.rotation || 0;
          const local = this._rotatePoint(world.x, world.y, obj2.x, obj2.y, -rot2);
          if (local.x >= obj2.x - halfW - pad && local.x <= obj2.x + halfW + pad &&
              local.y >= obj2.y - halfD2 - pad && local.y <= obj2.y + halfD2 + pad) {
            this._viewOnlyHitTurfObj = obj2;
            break;
          }
        }
      }
      // Always start panning — equipment click resolved on mouseup if no drag
      this.isPanning = true; this.panStart = { x: sx, y: sy }; this._viewOnlyPanDist = 0; this.canvas.style.cursor = 'grabbing'; return;
    }
    if (button === 1) { this.isPanning = true; this.panStart = { x: sx, y: sy }; this.canvas.style.cursor = 'grabbing'; return; }
    if (button === 2 && this.mode !== 'eraser') { this.isPanning = true; this.panStart = { x: sx, y: sy }; this.canvas.style.cursor = 'grabbing'; return; }
    // Commit paste preview on left-click
    if (this._pastePreview && button === 0) { this._commitPastePreview(); return; }
    // Handle pending ladder ghost placement (two-click flow)
    if (this._pendingLadder && button === 0) {
      const pl = this._pendingLadder;
      const tObj = pl.turfObj;
      // Convert world pos to turf-local
      const or = tObj.rotation || 0;
      const co = Math.cos(-or), so = Math.sin(-or);
      const dx = this.mouseWorldRaw.x - tObj.x, dy = this.mouseWorldRaw.y - tObj.y;
      const tlx = dx * co - dy * so, tly = dx * so + dy * co;
      // Check click is inside turf bounds
      const halfW = tObj.width / 2, halfD = tObj.depth / 2;
      if (Math.abs(tlx) > halfW || Math.abs(tly) > halfD) {
        this.updateStatus('Click inside the turf to place the ladder.');
        return;
      }
      if (!this._pendingLadderPos) {
        // First click — lock position, enter rotation phase
        const lr = this._pendingLadderRotation || 0;
        const lhw = pl.lengthFt / 2, lhh = pl.heightFt / 2;
        const absC = Math.abs(Math.cos(lr)), absS = Math.abs(Math.sin(lr));
        const bbHW = lhw * absC + lhh * absS, bbHH = lhw * absS + lhh * absC;
        const cx = Math.max(-halfW + bbHW, Math.min(halfW - bbHW, tlx));
        const cy = Math.max(-halfD + bbHH, Math.min(halfD - bbHH, tly));
        this._pendingLadderPos = { x: cx, y: cy };
        this.updateStatus('Move mouse to rotate, click to confirm.');
        this.render(); return;
      } else {
        // Second click — create the actual item
        const _plType = pl.itemType || 'ladder';
        const _plLabel = _plType === 'dotdrill' ? 'dot drill' : 'ladder';
        this.saveUndoState();
        if (!tObj.turfLogos) tObj.turfLogos = [];
        const newItem = { type: _plType, x: this._pendingLadderPos.x, y: this._pendingLadderPos.y,
          rotation: this._pendingLadderRotation, width: pl.lengthFt, height: pl.heightFt };
        tObj.turfLogos.push(newItem);
        // Focus the newly placed item
        this._turfLogoFocusedIdx = tObj.turfLogos.length - 1;
        if (!this._turfLogoSelectedIdxs) this._turfLogoSelectedIdxs = new Set();
        this._turfLogoSelectedIdxs.clear();
        this._turfLogoSelectedIdxs.add(this._turfLogoFocusedIdx);
        // Ensure turf is selected
        if (!this.selectedObjects.has(tObj)) {
          this.selectedWalls = new Set(); this.selectedDoors = new Set(); this.selectedObjects = new Set();
          this.selectedObjects.add(tObj);
        }
        // Reset to follow phase so user can place another (keep rotation)
        this._pendingLadderPos = null;
        this._pendingLadderGhostPos = null;
        this._pendingLadderBlocked = false;
        this.canvas.style.cursor = 'crosshair';
        this.updateStatus(_plLabel.charAt(0).toUpperCase() + _plLabel.slice(1) + ' placed. Click to place another, or press Escape to finish.');
        // Update panel info if overlay is available
        if (window.roomDesignerOverlay) {
          const container = document.getElementById('rdCustomizePanel');
          if (container) {
            if (window.roomDesignerOverlay._updateAgilityLadderInfo) window.roomDesignerOverlay._updateAgilityLadderInfo(container);
            if (window.roomDesignerOverlay._updateDotDrillInfo) window.roomDesignerOverlay._updateDotDrillInfo(container);
          }
        }
        this.render(); return;
      }
    }
    // Block select-tool routing when equipment placement is pending — click listener handles placement
    if (this._equipmentMode && this._pendingEquipmentId) return;
    // Label editor open — block all canvas interactions; click-away will blur/commit the editor
    if (this._labelEditorInput) { this._labelEditorInput.blur(); return; }
    // Click-move-click: second click commits the floating endpoint
    if (this._wallEndpointFloating && this._wallEndpointDragInfo && button === 0) {
      this._commitWallEndpointDrag();
      return;
    }
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — picking a PDF close-up region owns the canvas
    // completely while it is armed; nothing else should interpret the drag.
    if (this._regionPick && button === 0) {
      this._regionPick.c1 = { x: world.x, y: world.y };
      this._regionPick.c2 = { x: world.x, y: world.y };
      this.render();
      return;
    }
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — the Label tool owns its clicks outright, and
    // this sits ABOVE the room/zone name hit-test on purpose: clicking a space name
    // while the label tool is armed should drop a label there, not open the rename
    // box for the space underneath it.
    if (this.activeTool === 'label' && button === 0) {
      const existing = this._hitTestAreaLabel(world);   // topmost pill under the cursor
      if (existing) this._openAreaLabelEditor(existing);
      else this._placeAreaLabel(snapped);
      return;
    }
    // Hit-test clickable labels (room/zone names) for inline editing — works from any tool
    if (button === 0 && !this._floorMode && this._clickableLabels) {
      const pad = 8; // extra px padding for easier clicking
      for (const lbl of this._clickableLabels) {
        if (Math.abs(sx - lbl.sx) < lbl.hw + pad && Math.abs(sy - lbl.sy) < lbl.hh + pad) {
          this._openLabelEditor(lbl);
          return;
        }
      }
    }
    if (this.activeTool === 'select' && button === 0 && this._doorSwingEdit) {
      // Apply swing edit
      const door = this._doorSwingEdit.door;
      const swing = this._computeSwingForDoor(door, world);
      this.saveUndoState();
      door.swing = swing;
      this._doorSwingEdit = null;
      this.updateStatus('Door swing updated!');
      this.render(); this.updateMeasurements();
      return;
    }
    if (this.activeTool === 'select' && button === 0) {
      // Drag-to-adjust offset: check if clicking on a selected measurement label
      if (this._selectedMeasurements.size > 0) {
        const dragHit = this._hitTestMeasurementLabel(world);
        if (dragHit && this._selectedMeasurements.has(dragHit.id)) {
          this.saveUndoState();
          this._measureDragging = { id: dragHit.id, startOffset: dragHit.offset || 0 };
          this.canvas.style.cursor = 'grabbing';
          this.render(); return;
        }
      }
      // Hit-test measurements in select mode (works on all tabs)
      if (this._measurements.length > 0) {
        const hitM = this._hitTestMeasurement(world);
        if (hitM) {
          if (this.shiftHeld) {
            // Shift+click: toggle in multi-select
            if (this._selectedMeasurements.has(hitM.id)) this._selectedMeasurements.delete(hitM.id);
            else this._selectedMeasurements.add(hitM.id);
          } else {
            // Plain click: replace selection (toggle if already sole selection)
            if (this._selectedMeasurements.size === 1 && this._selectedMeasurements.has(hitM.id)) this._selectedMeasurements.clear();
            else { this._selectedMeasurements.clear(); this._selectedMeasurements.add(hitM.id); }
          }
          this.render(); return;
        }
        if (!this.shiftHeld && this._selectedMeasurements.size > 0) { this._selectedMeasurements.clear(); this.render(); }
      }
      // On mobile, skip handle hit-tests — resize/reshape/extend only via joystick
      if (!this._isMobile) {
      // 0a. Hit-test any turf logo body (direct click on logo — no prior selection needed)
      const logoBodyHit = this._hitTestAnyTurfLogoBody(world);
      if (logoBodyHit) {
        // Select the turf if not already selected
        if (!this.selectedObjects.has(logoBodyHit.obj)) {
          this.selectedWalls = new Set(); this.selectedDoors = new Set(); this.selectedObjects = new Set();
          this.selectedObjects.add(logoBodyHit.obj);
        }
        const logo = logoBodyHit.obj.turfLogos[logoBodyHit.logoIdx];
        // Shift-click: toggle logo in multi-selection set
        if (shiftKey) {
          if (this._turfLogoSelectedIdxs.has(logoBodyHit.logoIdx)) {
            this._turfLogoSelectedIdxs.delete(logoBodyHit.logoIdx);
            // If we just removed the primary, pick another from set (or null)
            if (this._turfLogoFocusedIdx === logoBodyHit.logoIdx) {
              const remaining = [...this._turfLogoSelectedIdxs];
              this._turfLogoFocusedIdx = remaining.length > 0 ? remaining[remaining.length - 1] : null;
            }
          } else {
            this._turfLogoSelectedIdxs.add(logoBodyHit.logoIdx);
            this._turfLogoFocusedIdx = logoBodyHit.logoIdx;
          }
          if (this._updateFloatingDelete) this._updateFloatingDelete();
          this.render();
          return;
        }
        // Normal click: if logo already in multi-selection, keep selection; otherwise single-select
        if (!this._turfLogoSelectedIdxs.has(logoBodyHit.logoIdx)) {
          this._turfLogoSelectedIdxs.clear();
          this._turfLogoSelectedIdxs.add(logoBodyHit.logoIdx);
        }
        this._turfLogoFocusedIdx = logoBodyHit.logoIdx;
        this.saveUndoState();
        this._turfLogoDrag = 'move';
        this._turfLogoDragObj = logoBodyHit.obj;
        this._turfLogoDragLogoIdx = logoBodyHit.logoIdx;
        this._turfLogoDragStart = { x: world.x, y: world.y };
        this._turfLogoDragOrigLogo = {
          x: logo.x || 0, y: logo.y || 0,
          w: logo.width, h: logo.height,
          r: logo.rotation || 0
        };
        this._turfLogoDragGroupOrig = null;
        if (this._turfLogoSelectedIdxs.size > 1) {
          this._turfLogoDragGroupOrig = new Map();
          for (const idx of this._turfLogoSelectedIdxs) {
            if (idx === logoBodyHit.logoIdx) continue;
            const gl = logoBodyHit.obj.turfLogos[idx];
            if (gl) this._turfLogoDragGroupOrig.set(idx, { x: gl.x || 0, y: gl.y || 0 });
          }
        }
        if (this._updateFloatingDelete) this._updateFloatingDelete();
        this.canvas.style.cursor = 'grabbing';
        return;
      }
      // 0b. Hit-test turf logo handles (only when logo is focused)
      if (this._turfLogoFocusedIdx != null) {
        const logoHit = this._hitTestTurfLogoHandle(sx, sy);
        if (logoHit) {
          const logo = logoHit.obj.turfLogos[this._turfLogoFocusedIdx];
          this.saveUndoState();
          this._turfLogoDrag = logoHit.name;
          this._turfLogoDragObj = logoHit.obj;
          this._turfLogoDragLogoIdx = this._turfLogoFocusedIdx;
          this._turfLogoDragStart = { x: world.x, y: world.y };
          this._turfLogoDragOrigLogo = {
            x: logo.x || 0, y: logo.y || 0,
            w: logo.width, h: logo.height,
            r: logo.rotation || 0
          };
          // Store base angle for delta-based rotation (same as equipment)
          if (logoHit.name === 'rotate') {
            const _objR = logoHit.obj.rotation || 0;
            const _co2 = Math.cos(-_objR), _so2 = Math.sin(-_objR);
            const _dx2 = world.x - logoHit.obj.x, _dy2 = world.y - logoHit.obj.y;
            const _tlx2 = _dx2 * _co2 - _dy2 * _so2, _tly2 = _dx2 * _so2 + _dy2 * _co2;
            this._turfLogoDragBaseAngle = Math.atan2(_tly2 - (logo.y || 0), _tlx2 - (logo.x || 0));
          }
          // Group rotate: store originals for all selected logos
          this._turfLogoDragGroupOrig = null;
          if (logoHit.name === 'rotate' && this._turfLogoSelectedIdxs.size > 1) {
            this._turfLogoDragGroupOrig = new Map();
            for (const idx of this._turfLogoSelectedIdxs) {
              const gl = logoHit.obj.turfLogos[idx];
              if (gl) this._turfLogoDragGroupOrig.set(idx, { x: gl.x || 0, y: gl.y || 0, r: gl.rotation || 0 });
            }
          }
          this.canvas.style.cursor = 'grabbing';
          return;
        }
      }
      // Clicking outside a logo clears logo focus
      this._turfLogoFocusedIdx = null;
      this._turfLogoSelectedIdxs.clear();
      // 1. Hit-test rotate handle (checked BEFORE object hit-test so handles layer on top)
      const handleHit = this._hitTestHandles(sx, sy);
      if (handleHit && handleHit.type === 'rotate') {
        this.saveUndoState();
        this._selectRotating = true;
        this._equipmentRotateBlocked = false;
        if (handleHit.group) {
          // Group rotate — store centroid and original positions
          const objs = [...this.selectedObjects].filter(o => o.type === 'equipment' || o.type === 'platform');
          const cx = objs.reduce((s, o) => s + o.x, 0) / objs.length;
          const cy = objs.reduce((s, o) => s + o.y, 0) / objs.length;
          const originals = objs.map(o => ({ obj: o, x: o.x, y: o.y, rotation: o.rotation || 0 }));
          const baseAngle = Math.atan2(world.y - cy, world.x - cx);
          // Compute initial bounding box circumradius (frozen for smooth orbit)
          let grMinX = Infinity, grMinY = Infinity, grMaxX = -Infinity, grMaxY = -Infinity;
          for (const o of objs) {
            const hw = o.width / 2, hd = o.depth / 2, rot = o.rotation || 0;
            for (const [lx, ly] of [[-hw,-hd],[hw,-hd],[hw,hd],[-hw,hd]]) {
              const p = this._rotatePoint(o.x + lx, o.y + ly, o.x, o.y, rot);
              if (p.x < grMinX) grMinX = p.x; if (p.x > grMaxX) grMaxX = p.x;
              if (p.y < grMinY) grMinY = p.y; if (p.y > grMaxY) grMaxY = p.y;
            }
          }
          // Use distance from centroid to top edge (not full diagonal) — keeps handle tight
          const initCircumR = cy - grMinY;
          this._selectRotateInfo = { group: true, cx, cy, originals, baseAngle, initCircumR };
        } else {
          const singleBase = Math.atan2(world.y - handleHit.obj.y, world.x - handleHit.obj.x);
          this._selectRotateInfo = { obj: handleHit.obj, origRotation: handleHit.obj.rotation || 0, origX: handleHit.obj.x, origY: handleHit.obj.y, baseAngle: singleBase };
        }
        this.canvas.style.cursor = 'grabbing';
        return;
      }
      // 2. Hit-test resize handles (skip in equipment mode — equipment has no resize)
      if (!this._equipmentMode && handleHit && handleHit.type === 'resize') {
        this.saveUndoState();
        const obj = handleHit.obj;
        const oppName = this._getOppositeHandle(handleHit.handle);
        const handles = this._getResizeHandles(obj);
        const oppH = handles.find(h => h.name === oppName);
        this._selectResizing = true;
        this._selectResizeInfo = {
          obj,
          handle: handleHit.handle,
          anchorWx: oppH ? oppH.wx : obj.x,
          anchorWy: oppH ? oppH.wy : obj.y,
          origX: obj.x, origY: obj.y,
          origWidth: obj.width, origDepth: obj.depth,
          origDiameter: obj.diameter,
          origRotation: obj.rotation || 0
        };
        this.canvas.style.cursor = 'grabbing';
        return;
      }
      // 3-5: Wall handle hit-tests (skip in equipment mode and floor mode)
      if (!this._equipmentMode && !this._floorMode) {
      // 3. Hit-test wall endpoint dots
      const epHit = this._hitTestWallEndpoints(sx, sy);
      if (epHit) {
        this.saveUndoState();
        const anchor = epHit.whichEnd === 'start' ? { x: epHit.wall.end.x, y: epHit.wall.end.y } : { x: epHit.wall.start.x, y: epHit.wall.start.y };
        this._wallEndpointDragging = true;
        // Snapshot doors on this wall for absolute size preservation
        const epDoorSnapshots = [];
        if (epHit.room) {
          for (const door of epHit.room.doors) {
            if (door.wall === epHit.wall) {
              epDoorSnapshots.push({ door, hingePos: this.paramToPoint(epHit.wall, door.hingeParam), endPos: this.paramToPoint(epHit.wall, door.endParam) });
            }
          }
        }
        this._wallEndpointDragInfo = {
          wall: epHit.wall, room: epHit.room, whichEnd: epHit.whichEnd,
          origX: epHit.pt.x, origY: epHit.pt.y,
          anchor, current: { x: epHit.pt.x, y: epHit.pt.y }, snapTarget: null,
          doorSnapshots: epDoorSnapshots,
          downSx: sx, downSy: sy // track click position for click-vs-drag detection
        };
        this.canvas.style.cursor = 'crosshair';
        return;
      }
      // 4. Hit-test wall corner handles
      const cornerHit = this._hitTestWallCornerHandles(sx, sy);
      if (cornerHit) {
        this.saveUndoState();
        const room = cornerHit.room;
        const key = (p) => `${(Math.round(p.x * 1200) / 1200).toFixed(4)},${(Math.round(p.y * 1200) / 1200).toFixed(4)}`;
        const cornerKey = key({ x: cornerHit.wx, y: cornerHit.wy });
        // Find ALL walls in this room that share this corner point
        const affectedWalls = [];
        room.walls.forEach(w => {
          if (key(w.start) === cornerKey) affectedWalls.push({ wall: w, whichEnd: 'start', origStartX: w.start.x, origStartY: w.start.y, origEndX: w.end.x, origEndY: w.end.y });
          else if (key(w.end) === cornerKey) affectedWalls.push({ wall: w, whichEnd: 'end', origStartX: w.start.x, origStartY: w.start.y, origEndX: w.end.x, origEndY: w.end.y });
        });
        // Snapshot doors on all affected walls
        const doorSnapshots = [];
        for (const aw of affectedWalls) {
          for (const door of room.doors) {
            if (door.wall === aw.wall) {
              doorSnapshots.push({ door, hingePos: this.paramToPoint(aw.wall, door.hingeParam), endPos: this.paramToPoint(aw.wall, door.endParam) });
            }
          }
        }
        this._wallCornerDragging = true;
        this._wallCornerDragInfo = {
          room, origX: cornerHit.wx, origY: cornerHit.wy,
          affectedWalls, doorSnapshots
        };
        this.canvas.style.cursor = 'move';
        return;
      }
      // 5. Hit-test wall grab handle
      const grabHit = this._hitTestWallGrabHandle(sx, sy);
      if (grabHit) {
        this.saveUndoState();
        const wall = grabHit.wall;
        const room = this._findRoomForWall(wall);
        const connStart = room ? this._getWallsConnectedAt(wall, room, 'start') : [];
        const connEnd = room ? this._getWallsConnectedAt(wall, room, 'end') : [];
        // Snapshot connected walls' original positions
        const connStartOrig = connStart.map(w => ({ wall: w, startX: w.start.x, startY: w.start.y, endX: w.end.x, endY: w.end.y }));
        const connEndOrig = connEnd.map(w => ({ wall: w, startX: w.start.x, startY: w.start.y, endX: w.end.x, endY: w.end.y }));
        // Snapshot door positions on connected walls
        const doorSnapshots = [];
        if (room) {
          for (const cw of [...connStart, ...connEnd]) {
            for (const door of room.doors) {
              if (door.wall === cw) {
                const hingePos = this.paramToPoint(cw, door.hingeParam);
                const endPos = this.paramToPoint(cw, door.endParam);
                doorSnapshots.push({ door, hingePos, endPos });
              }
            }
          }
        }
        this._wallGrabDragging = true;
        this._wallGrabDragInfo = {
          wall, room,
          origStartX: wall.start.x, origStartY: wall.start.y,
          origEndX: wall.end.x, origEndY: wall.end.y,
          connStart, connEnd, connStartOrig, connEndOrig,
          dragStartX: world.x, dragStartY: world.y,
          doorSnapshots
        };
        this.canvas.style.cursor = 'move';
        return;
      }
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — 6. Hit-test the curve bulge handle
      const bulgeHit = this._hitTestCurveBulgeHandle(sx, sy);
      if (bulgeHit) {
        const CV = window.RoomDesignerCurve;
        const container = this._findWallContainer(bulgeHit.wall);
        if (container) {
          this.saveUndoState();
          const chord = bulgeHit.handle.chord;
          const scale = this.basePixelsPerFoot * this.zoomLevel;
          // Chord termini are captured once and held for the whole drag — only the
          // bulge changes, never the wall's endpoints.
          const chordStart = { x: chord.start.x, y: chord.start.y };
          const chordEnd = { x: chord.end.x, y: chord.end.y };
          this._curveBulgeDragging = true;
          this._curveBulgeDragInfo = {
            wall: bulgeHit.wall,
            owner: container.owner,
            // Deliberately NOT caching container.list — see _curveApplySag.
            curveId: chord.curveId,
            chordStart, chordEnd,
            baseSag: chord.sag,
            // Delta-drag: record where on the chord normal the grab started, so a
            // parked handle (offset off a straight wall) doesn't jump on mousedown.
            grabSag: CV.sagittaFrom(chordStart, chordEnd, world),
            // CUSTOM_ROOMDESIGNER_BY_CLAUDE — how shallow a bow may get before it
            // collapses back to straight. This screen-px term dominates FLAT_EPS at
            // normal zoom (6px at 20px/ft was 3.6in of arch), so lowering it is what
            // actually lets a gentle curve survive.
            flatWorld: 2.5 / scale,
            warnedDoors: false,
            moved: false
          };
          this.canvas.style.cursor = 'grabbing';
          return;
        }
      }
      // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
      } // end !this._equipmentMode && !this._floorMode wall handle guard
      } // end !this._isMobile handle hit-test guard
      // 5. Two-click box-select / object click-select + drag
      if (this._selectCorner1) {
        // Second click — finalize box selection
        this.selectionEnd = world;
        this._updateSelection();
        this._selectCorner1 = null;
        this._selectPreExisting = null;
        this.isSelecting = false;
        this._boxSelectCooldownUntil = Date.now() + 300;
        this._updateSelectionStatus(); this.render();
        return;
      }
      const hit = this._hitTestSelect(world);
      if (hit) {
        // Clicked on an item — set up for click/drag (selection on mouseup)
        this._selectClickStart = { sx, sy, world, shiftKey, hit };
      } else {
        // In floor mode, block box-select when clicking on existing floor or turf
        if (this._floorMode) {
          for (const obj of this.objects) {
            if (obj.type !== 'turf') continue;
            const rot = obj.rotation || 0;
            const cos = Math.cos(-rot), sin = Math.sin(-rot);
            const dx = world.x - obj.x, dy = world.y - obj.y;
            const lx = dx * cos - dy * sin, ly = dx * sin + dy * cos;
            if (Math.abs(lx) <= obj.width / 2 && Math.abs(ly) <= obj.depth / 2) return;
          }
          for (let i = this.rooms.length - 1; i >= 0; i--) {
            const room = this.rooms[i];
            if (!room.closed || room.type !== 'add') continue;
            const geo = this._getRoomGeoCache(room);
            if (!geo.roomPolygon || geo.roomPolygon.length < 3) continue;
            if (!this.pointInPolygon(world.x, world.y, geo.roomPolygon)) continue;
            const hasFlooring = room.flooring
              || (room.zoneFlooring && room.zoneFlooring.some(zf => zf.productId));
            if (hasFlooring) return;
          }
        }
        // On Customize tab, just deselect — no box select
        if (window.roomDesignerOverlay && window.roomDesignerOverlay.currentStep === 'finishing') {
          this.selectedWalls = new Set(); this.selectedDoors = new Set(); this.selectedObjects = new Set();
          this._selectedMeasurements.clear();
          this._updateActionButtons(); this._updateSelectionStatus(); this.render();
          return;
        }
        // Clicked on empty space — start two-click box select
        // Brief cooldown after completing a box-select or switching to select tool
        // prevents accidental re-entry (e.g. after turf draw or rapid clicks)
        if (Date.now() < this._boxSelectCooldownUntil) {
          if (!shiftKey) {
            this.selectedWalls = new Set(); this.selectedDoors = new Set(); this.selectedObjects = new Set();
            this._selectedMeasurements.clear();
          }
          this._updateSelectionStatus(); this.render();
          return;
        }
        // CUSTOM_GYMLAYOUT_BY_CLAUDE (Ryan 2026-06-12) — the first click away
        // from an existing selection only DESELECTS; it must not also start
        // the box-select rectangle (clicking empty canvas to drop a selection
        // kept arming a marquee). A second click — now with nothing selected —
        // begins the two-click box select as before. Shift-click is exempt:
        // it explicitly means "add to selection".
        if (!shiftKey && (this.selectedObjects.size || this.selectedWalls.size || this.selectedDoors.size || this._selectedMeasurements.size)) {
          this.selectedWalls = new Set(); this.selectedDoors = new Set(); this.selectedObjects = new Set();
          this._selectedMeasurements.clear();
          this._updateActionButtons(); this._updateSelectionStatus(); this.render();
          return;
        }
        if (shiftKey) {
          // Shift held — preserve current selection to merge with box-select results
          this._selectPreExisting = {
            walls: new Set(this.selectedWalls),
            doors: new Set(this.selectedDoors),
            objects: new Set(this.selectedObjects),
            measurements: new Set(this._selectedMeasurements)
          };
        } else {
          this.selectedWalls = new Set(); this.selectedDoors = new Set(); this.selectedObjects = new Set();
          this._selectedMeasurements.clear();
          this._selectPreExisting = null;
        }
        this._selectCorner1 = { x: world.x, y: world.y };
        this.selectionStart = { x: world.x, y: world.y };
        this.selectionEnd = { x: world.x, y: world.y };
        this.isSelecting = true;
        this.updateStatus(this._isMobile ? 'Tap to complete selection.' : 'Move mouse and click to complete selection.');
      }
      return;
    }
    if (this.activeTool === 'turf-draw' && button === 0) {
      this._handleTurfDraw(snapped);
      return;
    }
    if (this.activeTool === 'platform-place' && button === 0) {
      this._handlePlatformPlace(this.mouseWorldRaw || snapped);
      return;
    }
    if (this.mode === 'eraser' && button === 0) {
      // In floor mode, overlay's mousedown handler already catches eraser clicks
      if (!this._floorMode) this.handleErase(world);
      return;
    }
    if (this.mode === 'measure' && button === 0) {
      // Drag-to-adjust offset in measure mode: check if clicking on a selected measurement label
      if (this._measurePhase === 0 && this._selectedMeasurements.size > 0) {
        const dragHit = this._hitTestMeasurementLabel(world);
        if (dragHit && this._selectedMeasurements.has(dragHit.id)) {
          this.saveUndoState();
          this._measureDragging = { id: dragHit.id, startOffset: dragHit.offset || 0 };
          this.canvas.style.cursor = 'grabbing';
          this.render(); return;
        }
      }
      if (this._measurePhase === 0) {
        // Phase 0 → idle: hit-test existing measurement body/label for selection, or start new
        const hitM = this._hitTestMeasurement(world);
        if (hitM) {
          // Check if click is near an endpoint — if so, start a new measurement from that shared endpoint
          const nearEp = this._findNearestMeasurementEndpoint(world);
          if (nearEp) {
            this._selectedMeasurements.clear();
            this._measureStart = { x: nearEp.x, y: nearEp.y };
            this._measurePhase = 1;
            this.updateStatus('Click the second point to set the distance.');
            this.render(); return;
          }
          // Otherwise select/deselect
          if (this.shiftHeld) {
            if (this._selectedMeasurements.has(hitM.id)) this._selectedMeasurements.delete(hitM.id);
            else this._selectedMeasurements.add(hitM.id);
          } else {
            if (this._selectedMeasurements.size === 1 && this._selectedMeasurements.has(hitM.id)) this._selectedMeasurements.clear();
            else { this._selectedMeasurements.clear(); this._selectedMeasurements.add(hitM.id); }
          }
          this.render(); return;
        }
        // No hit — check for shared endpoint snap from existing measurements
        const nearEp = this._findNearestMeasurementEndpoint(world);
        this._selectedMeasurements.clear();
        this._measureStart = nearEp ? { x: nearEp.x, y: nearEp.y } : { x: snapped.x, y: snapped.y };
        this._measurePhase = 1;
        this._measureEnd = null;
        this.updateStatus('Click the second point to set the distance.');
      } else if (this._measurePhase === 1) {
        // Phase 1 → end point placed, now choose offset
        let endPt = { x: snapped.x, y: snapped.y };
        if (this.shiftHeld) endPt = this.constrainToAxis(this._measureStart, endPt);
        // Snap to existing measurement endpoint if near one
        const nearEp = this._findNearestMeasurementEndpoint({ x: endPt.x, y: endPt.y });
        if (nearEp) endPt = { x: nearEp.x, y: nearEp.y };
        this._measurePendingEnd = endPt;
        this._measurePhase = 2;
        this.updateStatus('Move mouse to set offset distance, then click to place. Hold Shift for inline (0 offset).');
      } else if (this._measurePhase === 2) {
        // Phase 2 → place the measurement with computed offset
        const start = this._measureStart;
        const end = this._measurePendingEnd;
        const dx = end.x - start.x, dy = end.y - start.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        // Compute perpendicular offset from mouse to baseline
        let offset = 0;
        if (dist > 0.01) {
          const len = dist;
          const nx = -dy / len, ny = dx / len; // perpendicular unit vector
          offset = (this.mouseWorldRaw.x - start.x) * nx + (this.mouseWorldRaw.y - start.y) * ny;
          // Snap offset: if shift held, force to 0; otherwise snap to nearest inch
          if (this.shiftHeld) {
            offset = 0;
          } else {
            offset = Math.round(offset * 12) / 12; // snap to inch
          }
        }
        this.saveUndoState();
        this._measurements.push({ start, end, dist, id: this._measureNextId++, offset });
        this._measurementsVisible = true;
        this._measureStart = null; this._measurePendingEnd = null; this._measureEnd = null;
        this._measurePhase = 0;
        this._updateMeasureClearBtn();
        this.updateStatus('Distance: ' + this.formatMeasurement(dist) + ' — Click to start a new measurement.');
      }
      this.render();
      return;
    }
    if (this.mode === 'door' && button === 0) {
      if (this.objectType === 'rectangle' || this.objectType === 'circle') { this.handleObjectPlace(snapped); return; }
      this.handleDoorClick(world); return;
    }
    if (this.activeTool === 'hand' && button === 0) { this.isPanning = true; this.panStart = { x: sx, y: sy }; this.canvas.style.cursor = 'grabbing'; return; }
    if (this.activeTool === 'pen' && this.mode === 'draw') {
      if (this.penDrawMode === 'wallSegment') this._handleWallSegmentClick(snapped, world);
      else if (this.penDrawMode === 'rectangle') this._handleRectDraw(snapped);
      else this.addWallPoint(snapped);
    }
  }

  /* ── EDGE AUTO-PAN WHILE DRAWING ─────────────────────────────────
     CUSTOM_ROOMDESIGNER_BY_CLAUDE
     Once a point is down, pushing the cursor to the edge of the canvas slides the
     view instead of making you zoom out, pan, zoom back in and re-find your place.
     The cursor stays put and the world moves under it, so the rubber-band keeps
     tracking — which is why the tick re-runs _processMove at the SAME screen point
     rather than leaving the preview frozen where it was. */

  /** True while a click-to-click drawing operation is mid-chain. */
  _isDrawInProgress() {
    // Calibration is tested FIRST, ahead of the tool gate below: opening the
    // blueprint panel calls setActiveTool('select') so clicks land on the image, so
    // a calibration trace always runs with select active and would otherwise be
    // gated out — even though tracing an outline is drawing in every sense here.
    const bg = window.roomDesignerBackground;
    if (bg && bg._calibrating) {
      if (bg._calMode === 'area') return !!(bg._calPoly && bg._calPoly.length && !bg._calPolyClosed);
      return bg._calStep === 2;
    }
    // Select and hand never draw, so nothing should slide or fade while you are
    // picking things up or panning. This is a guard as well as a preference:
    // _measurePhase is only reset on switching TO measure, so a half-placed
    // measurement left behind used to read as an in-progress draw for the whole of
    // the following select session.
    if (this.activeTool === 'select' || this.activeTool === 'hand') return false;
    // Pen AND dividing walls/lines both drive currentWall + hasPlacedFirstPoint.
    if (this.currentWall || this.hasPlacedFirstPoint) return true;
    // Rectangle and freehand carry their own state and drive NEITHER of those, which
    // is why the edge slide did nothing while dragging out a rectangle — the one
    // gesture that most often runs off the screen. This is the same triple mobile.js
    // already uses for "is something being drawn" (_updateFloatingCancel), so the two
    // now agree rather than each keeping their own idea of it.
    if (this._rectCorner1) return true;
    if (this._freehandAutoPlaced) return true;
    if (this._measurePhase === 1) return true;
    if (this.doorPlacement) return true;
    return false;
  }

  _updateEdgeAutoPan(sx, sy, w, h) {
    if (this._suppressEdgePan || this.isPanning || !this._isDrawInProgress()) { this._stopEdgeAutoPan(); return; }
    // Dimensions come from the caller's LIVE rect. _cssWidth/_cssHeight are only
    // refreshed by resizeCanvas — init, window resize, fullscreen — so any layout
    // change that resizes the canvas without a window resize leaves them stale, and
    // a stale width is a RIGHT-EDGE-ONLY fault by construction: the left and top
    // bands are `sx < MARGIN`, anchored at zero, and cannot be wrong. The right and
    // bottom are `sx > w - MARGIN`, so a width remembered too small puts the band
    // short of the visible edge — and then `sx > w` below stops the pan dead as you
    // push past it. That is exactly "works on three edges, reluctant on the fourth".
    if (!(w > 0)) w = this._cssWidth || this.canvas.clientWidth;
    if (!(h > 0)) h = this._cssHeight || this.canvas.clientHeight;
    if (!(w > 0) || !(h > 0) || sx < 0 || sy < 0 || sx > w || sy > h) { this._stopEdgeAutoPan(); return; }

    const MARGIN = 55;   // px band around the edge that triggers the slide
    const MAX = 16;      // px per frame at full deflection
    const ramp = (d) => Math.min(1, Math.max(0, (MARGIN - d) / MARGIN));

    // THROTTLE — how deep into an edge band the cursor sits. Either axis can open it,
    // and the deeper of the two wins, so speed is the same at full deflection whatever
    // direction you're pushing. (Driving a corner used to give MAX on BOTH axes at
    // once, i.e. 1.41x speed on the diagonal alone.)
    let rx = 0, ry = 0;
    if (sx < MARGIN) rx = ramp(sx);
    else if (sx > w - MARGIN) rx = ramp(w - sx);
    if (sy < MARGIN) ry = ramp(sy);
    else if (sy > h - MARGIN) ry = ramp(h - sy);
    const speed = MAX * Math.max(rx, ry);

    // STEERING — where the cursor sits relative to the middle of the canvas. This is
    // what makes it a joystick rather than four buttons. The bands themselves already
    // allowed a diagonal, but ONLY inside the 55x55 square where two of them overlap,
    // so in practice you got the one wall you were nearest. Now anywhere along the
    // bottom edge steers with you: dead centre is straight down, and the further
    // across you are the more it leans, reaching 45 degrees in the corner.
    //
    // Offsets are a FRACTION of the half-width/half-height, not raw pixels, so the
    // feel doesn't depend on the canvas shape — the corner of a wide monitor is still
    // 45 degrees rather than mostly-sideways.
    let vx = 0, vy = 0;
    if (speed > 0) {
      const fx = (sx - w / 2) / (w / 2);
      const fy = (sy - h / 2) / (h / 2);
      const m = Math.hypot(fx, fy) || 1;
      vx = -(fx / m) * speed;    // negative: panX decreasing walks the view right
      vy = -(fy / m) * speed;
    }

    // Kill the floating UI while drawing. Two triggers: a band wider than the pan
    // band, so the toolbar is already inert by the time an edge-scroll reaches it;
    // and proximity to any chrome, which is what catches a PINNED flyout or drawer
    // sitting out over the middle of the canvas where no edge band would fire.
    const UI_BAND = MARGIN + 60;
    const nearEdge = sx < UI_BAND || sy < UI_BAND || sx > w - UI_BAND || sy > h - UI_BAND;
    this._setEdgeUiSuppressed(nearEdge || this._cursorOverChrome());

    if (!vx && !vy) { this._edgePanV = null; this._cancelEdgePanRaf(); return; }
    this._edgePanV = { vx: vx, vy: vy };
    this._startEdgeAutoPan();
  }

  /**
   * Is the cursor over (or just shy of) any floating chrome?
   * Must be a geometric test, not a hover test: once suppressed the elements are
   * pointer-events:none, so they'd never report a hover and the suppression would
   * flicker off the moment it took effect. Suppression doesn't change layout, so the
   * rects stay stable and the state is steady.
   */
  _cursorOverChrome() {
    const cx = this._lastMouseClientX, cy = this._lastMouseClientY;
    if (cx == null || cy == null) return false;
    const root = document.querySelector('.room-designer');
    if (!root) return false;
    if (!this._chromeSel) {
      this._chromeSel = [
        // Only chrome that FLOATS OVER the canvas belongs here. .rd-step-bar is
        // excluded on purpose: it sits above the canvas container in normal flow,
        // so it can't be in the way — and its rect hugs the canvas top edge, which
        // meant the 26px proximity pad fired suppression along the whole top strip.
        '.tool-controls', '.settings-panel', '.zoom-controls-row', '.settings-toggle',
        '.designs-toggle',
        '.pen-popover', '.door-popover', '.objects-popover', '.zoom-popover',
        '.measure-popover', '.floor-rotate-popover', '.brand-palette-popover',
        '.settings-drawer', '.designs-drawer', '.shortcuts-drawer'
      ].join(',');
    }
    // Rects are CACHED. getBoundingClientRect forces a synchronous layout flush, and
    // measuring ~16 elements on every mousemove — while the pan loop is also
    // re-rendering the canvas each frame — janked the whole thing into a crawl.
    // It only showed on the right edge because that is where the chrome lives.
    // Chrome layout doesn't move mid-draw, so a short-lived cache is plenty.
    const now = (this._rafNow || 0) + 1;
    this._rafNow = now;
    if (!this._chromeRects || now - (this._chromeRectsAt || -1e9) > 12) {
      this._chromeRectsAt = now;
      const els = root.querySelectorAll(this._chromeSel);
      const rects = [];
      for (let i = 0; i < els.length; i++) {
        const r = els[i].getBoundingClientRect();
        if (r.width < 2 || r.height < 2) continue;   // collapsed / hidden
        rects.push(r);
      }
      this._chromeRects = rects;
    }
    const PAD = 26;
    const rects = this._chromeRects;
    for (let i = 0; i < rects.length; i++) {
      const r = rects[i];
      if (cx >= r.left - PAD && cx <= r.right + PAD &&
          cy >= r.top - PAD && cy <= r.bottom + PAD) return true;
    }
    return false;
  }

  /** Chrome may open/close/move; drop the cache so it re-measures. */
  _invalidateChromeRects() { this._chromeRects = null; }

  /** Fade + disable the edge-anchored chrome so it can't intercept the cursor. */
  _setEdgeUiSuppressed(on) {
    if (this._edgeUiSuppressed === on) return;
    this._edgeUiSuppressed = on;
    const root = document.querySelector('.room-designer');
    if (root) root.classList.toggle('rd-edge-pan-suppress', !!on);
  }

  _startEdgeAutoPan() {
    if (this._edgePanRaf) return;
    const tick = () => {
      this._edgePanRaf = null;
      if (!this._edgePanV || !this._isDrawInProgress()) { this._edgePanV = null; return; }
      this.panX += this._edgePanV.vx;
      this.panY += this._edgePanV.vy;
      // Re-run the move at the same SCREEN point: the cursor hasn't moved, the world
      // has, so the in-progress preview must be recomputed against the new world
      // position under it. Goes through _processMove so wrapped handlers (blueprint
      // calibration) update too.
      const sx = this._lastMouseScreenX, sy = this._lastMouseScreenY;
      if (sx != null && sy != null) this._processMove(sx, sy);
      this.render();
      this._edgePanRaf = requestAnimationFrame(tick);
    };
    this._edgePanRaf = requestAnimationFrame(tick);
  }

  _cancelEdgePanRaf() {
    if (this._edgePanRaf) { cancelAnimationFrame(this._edgePanRaf); this._edgePanRaf = null; }
  }

  _stopEdgeAutoPan() {
    this._edgePanV = null;
    this._cancelEdgePanRaf();
    this._setEdgeUiSuppressed(false);
  }
  // END CUSTOM_ROOMDESIGNER_BY_CLAUDE

  handleMouseMove(e) {
    const rect = this.canvas.getBoundingClientRect();
    const sx = e.clientX - rect.left; const sy = e.clientY - rect.top;
    this._lastMouseScreenX = sx; this._lastMouseScreenY = sy;
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — client coords too, for hit-testing chrome rects
    this._lastMouseClientX = e.clientX; this._lastMouseClientY = e.clientY;
    this._processMove(sx, sy);
    this._updateEdgeAutoPan(sx, sy, rect.width, rect.height); // CUSTOM_ROOMDESIGNER_BY_CLAUDE
    // Measurement hover cursors — runs AFTER _processMove (including any wrapper) as a failsafe
    this._updateMeasureHoverCursor();
    // View-only hover callout for equipment
    if (this._viewOnly) this._updateViewOnlyHover(sx, sy);
  }

  /* Measurement hover cursor logic — separated so it can run both inside _processMove
     and as a failsafe after _processMove in handleMouseMove. Sets pointer cursor on
     measurement lines and all-scroll cursor on grab handles of selected measurements. */
  _updateMeasureHoverCursor() {
    if (this.isPanning || this._selectOpen || this._labelHover || this._measureDragging) return;
    if (!this.mouseWorldRaw) return;
    // Works in measure mode (phase 0) AND in select mode (measurements selectable on all tabs)
    const inMeasureIdle = this.mode === 'measure' && this._measurePhase === 0;
    const inSelect = this.activeTool === 'select';
    if (!inMeasureIdle && !inSelect) return;
    // Grab handle on selected measurement's label — all-scroll cursor
    if (this._selectedMeasurements.size > 0) {
      const dragHover = this._hitTestMeasurementLabel(this.mouseWorldRaw);
      if (dragHover && this._selectedMeasurements.has(dragHover.id)) {
        this.canvas.style.cursor = 'all-scroll';
        this._measureHoverGrab = true;
        return;
      }
    }
    if (this._measureHoverGrab) this._measureHoverGrab = false;
    // Pointer cursor when hovering over any measurement line (signifies clickable)
    const hoverM = this._hitTestMeasurement(this.mouseWorldRaw);
    if (hoverM) {
      this.canvas.style.cursor = 'pointer';
      this._measureHoverLine = true;
    } else if (this._measureHoverLine) {
      this._measureHoverLine = false;
      this.canvas.style.cursor = 'crosshair';
    }
  }

  _processMove(sx, sy) {
    if (this._selectOpen) return;
    if (this._regionPick) {
      if (this._regionPick.c1) {
        const w = this.screenToWorld(sx, sy);
        this._regionPick.c2 = { x: w.x, y: w.y };
        this.render();
      }
      this.canvas.style.cursor = 'crosshair';
      return;
    }
    // Label hover — show I-beam cursor when over a clickable label
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — not while the Label tool is armed. This block
    // owns the cursor, and its miss branch resets it to 'default', so crossing a
    // space-name pill would quietly cost the crosshair for the rest of the gesture.
    if (this.activeTool !== 'label' && this._clickableLabels && this._clickableLabels.length > 0 && !this._floorMode && !this._labelEditorInput) {
      let overLabel = false;
      const pad = 8;
      for (const lbl of this._clickableLabels) {
        if (Math.abs(sx - lbl.sx) < lbl.hw + pad && Math.abs(sy - lbl.sy) < lbl.hh + pad) {
          overLabel = true; break;
        }
      }
      if (overLabel) { this.canvas.style.cursor = 'text'; this._labelHover = true; }
      else if (this._labelHover) { this._labelHover = false; this.canvas.style.cursor = 'default'; }
    } else if (this._labelHover) {
      // Clear stale _labelHover when clickableLabels is empty (prevents blocking measurement cursors)
      this._labelHover = false;
    }
    if (this.isPanning) { const dx = sx - this.panStart.x, dy = sy - this.panStart.y; this.panX += dx; this.panY += dy; this.panStart = { x: sx, y: sy }; if (this._viewOnlyPanDist !== undefined) this._viewOnlyPanDist += Math.abs(dx) + Math.abs(dy); if (this._onPanHook) this._onPanHook(); if (this._hoverCalloutEl) this._dismissHoverCallout(); this.render(); return; }
    const world = this.screenToWorld(sx, sy);
    let snapped = this.snapToGrid(world.x, world.y);
    this.mouseScreen = { x: sx, y: sy };
    if (this.activeTool === 'pen' && this.mode === 'draw' && this.currentWall && this.hasPlacedFirstPoint) snapped = this.constrainToAxis(this.currentWall.start, snapped);
    this.mouseWorld = snapped; this.mouseWorldRaw = world;
    // Measure drag-to-adjust offset
    if (this._measureDragging && this.mouseWorldRaw) {
      const m = this._measurements.find(mm => mm.id === this._measureDragging.id);
      if (m) {
        const dx = m.end.x - m.start.x, dy = m.end.y - m.start.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len > 0.01) {
          const nx = -dy / len, ny = dx / len;
          let offset = (this.mouseWorldRaw.x - m.start.x) * nx + (this.mouseWorldRaw.y - m.start.y) * ny;
          offset = this.shiftHeld ? Math.round(offset) : Math.round(offset * 12) / 12; // shift = snap to foot, else inch
          m.offset = offset;
        }
      }
      this.render(); return;
    }
    // Measure tool rubber-band (phases 1 and 2)
    if (this.mode === 'measure' && (this._measurePhase === 1 || this._measurePhase === 2)) { this.render(); return; }
    // Sticky wall rail for wall segment placement (before AND after first click)
    if (this.activeTool === 'pen' && this.mode === 'draw' && this.penDrawMode === 'wallSegment' && world) {
      const scale = this.basePixelsPerFoot * this.zoomLevel;
      const spi = scale / 12;
      const acquireT = spi >= 3 ? (1 / 12) : (4 / 12);
      const breakT = Math.min(Math.max(40 / scale, 0.5), 2.0);
      // After first click, check proximity from the axis-constrained point
      const checkPt = this.hasPlacedFirstPoint ? snapped : world;
      // Helper: compute snap point on wall, respecting axis constraint after first click
      const _wsStickySnap = (w, rawParam) => {
        let cp = Math.max(0, Math.min(1, rawParam));
        // Dividing walls: clamp param away from room wall endpoints (2" inset)
        const _isRoomWall = this._wallSegmentType !== 'line' && this._wsSnapSticky && this._wsSnapSticky.room;
        let _ipVal = 0; // inset param threshold for post-clamp
        if (_isRoomWall) {
          const _wl = this.getDistance(w.start, w.end);
          if (_wl > 0) { _ipVal = (2/12) / _wl; cp = Math.max(_ipVal, Math.min(1 - _ipVal, cp)); }
        }
        // Post-clamp helper: after grid snap, ensure result stays outside inset zone
        const _postClamp = (pt) => {
          if (!_isRoomWall || _ipVal === 0) return pt;
          const finalParam = this.getWallParam(pt, w);
          if (finalParam < _ipVal) return this.paramToPoint(w, _ipVal);
          if (finalParam > 1 - _ipVal) return this.paramToPoint(w, 1 - _ipVal);
          return pt;
        };
        if (this.hasPlacedFirstPoint && this.shiftHeld && this.currentWall) {
          // Find where the axis-constrained line intersects the target wall
          const start = this.currentWall.start;
          const wx = w.end.x - w.start.x, wy = w.end.y - w.start.y;
          const isHoriz = Math.abs(snapped.y - start.y) < 0.001;
          const isVert = Math.abs(snapped.x - start.x) < 0.001;
          if (isHoriz && Math.abs(wy) > 0.0001) {
            const s = (start.y - w.start.y) / wy;
            if (s >= -0.01 && s <= 1.01) {
              const sc = Math.max(0, Math.min(1, s));
              const pt = this.snapToGrid(w.start.x + sc * wx, start.y);
              pt.y = start.y; // preserve exact axis
              return _postClamp(pt);
            }
          } else if (isVert && Math.abs(wx) > 0.0001) {
            const s = (start.x - w.start.x) / wx;
            if (s >= -0.01 && s <= 1.01) {
              const sc = Math.max(0, Math.min(1, s));
              const pt = this.snapToGrid(start.x, w.start.y + sc * wy);
              pt.x = start.x; // preserve exact axis
              return _postClamp(pt);
            }
          }
        }
        return _postClamp(this.paramToPoint(w, this.snapParamToGrid(w, cp)));
      };
      let locked = false;
      if (this._wsSnapSticky) {
        const w = this._wsSnapSticky.wall;
        const param = this.getWallParam(checkPt, w);
        const proj = this.paramToPoint(w, param);
        if (this.getDistance(checkPt, proj) < breakT) {
          this.mouseWorld = _wsStickySnap(w, param);
          locked = true;
        } else { this._wsSnapSticky = null; }
      }
      if (!locked) {
        for (const room of [...this.rooms, this.currentRoom]) {
          if (!room || !room.walls) continue;
          for (const wall of room.walls) {
            const param = this.getWallParam(checkPt, wall);
            const proj = this.paramToPoint(wall, param);
            const wallAcquireT = (this._wallSegmentType === 'line') ? acquireT + this.wallThickness : acquireT;
            if (this.getDistance(checkPt, proj) < wallAcquireT) {
              this._wsSnapSticky = { wall, room };
              this.mouseWorld = _wsStickySnap(wall, param);
              locked = true; break;
            }
          }
          if (locked) break;
        }
        if (!locked) {
          for (const group of this.wallSegmentGroups) {
            // Divider walls can't start on divider lines
            if (this._wallSegmentType === 'wall' && (group.type || 'wall') === 'line') continue;
            for (const seg of group.segments) {
              const param = this.getWallParam(checkPt, seg);
              const proj = this.paramToPoint(seg, param);
              if (this.getDistance(checkPt, proj) < acquireT) {
                this._wsSnapSticky = { wall: seg, room: null, group };
                this.mouseWorld = _wsStickySnap(seg, param);
                locked = true; break;
              }
            }
            if (locked) break;
          }
        }
      }
    } else if (this._wsSnapSticky) { this._wsSnapSticky = null; }
    // Turf logo drag interaction (move/resize/rotate)
    if (this.activeTool === 'select' && this._turfLogoDrag && this._turfLogoDragObj) {
      const _tObj = this._turfLogoDragObj;
      const _orig = this._turfLogoDragOrigLogo;
      const _logo = _tObj.turfLogos && _tObj.turfLogos[this._turfLogoDragLogoIdx];
      if (!_logo) { this._turfLogoDrag = null; return; }
      const _or = _tObj.rotation || 0;
      const _co = Math.cos(-_or), _so = Math.sin(-_or);
      if (this._turfLogoDrag === 'move') {
        this._ladderSnapGaps = null;
        const wdx = world.x - this._turfLogoDragStart.x;
        const wdy = world.y - this._turfLogoDragStart.y;
        const ldx = wdx * _co - wdy * _so;
        const ldy = wdx * _so + wdy * _co;
        let newLx = _orig.x + ldx, newLy = _orig.y + ldy;
        // Shift constrains movement to one axis (lock the smaller delta)
        if (this.shiftHeld) {
          if (Math.abs(ldx) >= Math.abs(ldy)) newLy = _orig.y;
          else newLx = _orig.x;
        }
        const lr = _logo.rotation || 0;
        const lhw = (_logo.width || _orig.w) / 2;
        const lhh = (_logo.height || _orig.h) / 2;
        const absC = Math.abs(Math.cos(lr)), absS = Math.abs(Math.sin(lr));
        const bbHW = lhw * absC + lhh * absS;
        const bbHH = lhw * absS + lhh * absC;
        const halfW = _tObj.width / 2, halfD = _tObj.depth / 2;
        newLx = Math.max(-halfW + bbHW, Math.min(halfW - bbHW, newLx));
        newLy = Math.max(-halfD + bbHH, Math.min(halfD - bbHH, newLy));
        const snapPx = 8;
        const snapT = snapPx / (this.basePixelsPerFoot * this.zoomLevel);
        if (_logo.type === 'ladder' || _logo.type === 'dotdrill') {
          // Ladders/dot drills snap to specific points: quadrant centers, dead center, center-line midpoints
          _logo._snapX = false;
          _logo._snapY = false;
          const _qcx = halfW / 2, _qcy = halfD / 2; // quadrant center offsets
          const _snapPts = [
            { x: 0, y: 0 },                  // dead center
            { x: -_qcx, y: -_qcy },          // quadrant TL
            { x:  _qcx, y: -_qcy },          // quadrant TR
            { x: -_qcx, y:  _qcy },          // quadrant BL
            { x:  _qcx, y:  _qcy },          // quadrant BR
            { x: 0, y: -_qcy },              // mid of top half of vertical line
            { x: 0, y:  _qcy },              // mid of bottom half of vertical line
            { x: -_qcx, y: 0 },              // mid of left half of horizontal line
            { x:  _qcx, y: 0 },              // mid of right half of horizontal line
          ];
          for (const _sp of _snapPts) {
            const _dsx = Math.abs(newLx - _sp.x) < snapT;
            const _dsy = Math.abs(newLy - _sp.y) < snapT;
            if (_dsx && _dsy) {
              newLx = _sp.x; newLy = _sp.y;
              _logo._snapX = true; _logo._snapY = true;
              break;
            }
          }
          // Independent center-line axis snaps (each axis snaps independently)
          if (!_logo._snapX && Math.abs(newLx) < snapT) { newLx = 0; _logo._snapX = true; }
          if (!_logo._snapY && Math.abs(newLy) < snapT) { newLy = 0; _logo._snapY = true; }
        } else {
          // Snap to field center (accounts for sled lane offset)
          const _fc = this._getTurfFieldCenter(_tObj);
          _logo._snapX = Math.abs(newLx - _fc.x) < snapT;
          _logo._snapY = Math.abs(newLy - _fc.y) < snapT;
          if (_logo._snapX) newLx = _fc.x;
          if (_logo._snapY) newLy = _fc.y;
        }
        // Extra snap targets for ladders/dot drills: field center X, end zone center Y
        if (_logo.type === 'ladder' || _logo.type === 'dotdrill') {
          const _markRot = _tObj.turfMarkingsRotated || false;
          const _cH = _markRot ? halfD : halfW;
          const _rH = _markRot ? halfW : halfD;
          const _tSW = 1 / (this.basePixelsPerFoot * this.zoomLevel); // turfStrokeW approx
          const _in = _tSW;
          // Sled lane widths (always non-rotated / lengthwise)
          const _SLW = 3.25, _SDW = 0.5;
          let _slRunMax = 0;
          if (_tObj.turfSledLanes) {
            const _sc = _tObj.turfSledLaneCount || 2;
            const _bw = (c) => c * _SLW + (c - 1) * _SDW;
            const _edges = this._rotateEdges(_tObj.turfSledLaneEdges || ['right'], _tObj.rotation || 0);
            if (_edges.includes('top') || _edges.includes('bottom')) _slRunMax = _bw(_sc);
          }
          // Snap to field center (uses _getTurfFieldCenter which handles sled lane offset)
          const _fc = this._getTurfFieldCenter(_tObj);
          if (Math.abs(_fc.x) > 0.01 || Math.abs(_fc.y) > 0.01) {
            if (!_logo._snapX && Math.abs(newLx - _fc.x) < snapT) { newLx = _fc.x; _logo._snapX = true; }
            if (!_logo._snapY && Math.abs(newLy - _fc.y) < snapT) { newLy = _fc.y; _logo._snapY = true; }
          }
          // Field line + gap center snaps (along run axis)
          _logo._snapGapRunY = null;
          if (_tObj.turfLines) {
            const _lineOrigin = _tObj.turfLineOrigin || (_tObj.turfHalfwayLine !== false ? 'center' : 'offset');
            // Mirrors lineSpacing in _drawTurfMarkingsInner -- the tight (1') hash
            // grid moves the full-width lines to every 5 FEET, and a logo has to
            // snap to the lines actually drawn.
            const _lineSpc = (_tObj.turfHashSpacing === 1) ? 5 : 15; // feet
            const _gridOff = (_lineOrigin === 'offset') ? _lineSpc / 2 : 0;
            const _runLim = _rH - _in - _slRunMax;
            const _mrkLim = _runLim;
            if (_lineOrigin === 'edge') {
              // Edge-to-edge: lines from bottom edge every 15'
              const _startY = -_mrkLim;
              for (let _ey = _startY + _lineSpc; _ey < _mrkLim - 0.01; _ey += _lineSpc) {
                if (_markRot) {
                  if (!_logo._snapX && Math.abs(newLx - _ey) < snapT) { newLx = _ey; _logo._snapX = true; _logo._snapGapRunY = _ey; }
                } else {
                  if (!_logo._snapY && Math.abs(newLy - _ey) < snapT) { newLy = _ey; _logo._snapY = true; _logo._snapGapRunY = _ey; }
                }
              }
              // Gap centers between edge-based lines
              for (let _ey = _startY + _lineSpc / 2; _ey < _mrkLim - 0.01; _ey += _lineSpc) {
                if (_markRot) {
                  if (!_logo._snapX && Math.abs(newLx - _ey) < snapT) { newLx = _ey; _logo._snapX = true; _logo._snapGapRunY = _ey; }
                } else {
                  if (!_logo._snapY && Math.abs(newLy - _ey) < snapT) { newLy = _ey; _logo._snapY = true; _logo._snapGapRunY = _ey; }
                }
              }
            } else {
              // Field line snaps (on the lines themselves, skip 0 — already handled by center-line snap)
              for (let _fl = _gridOff || _lineSpc; _fl <= _mrkLim + 0.01; _fl += _lineSpc) {
                if (_markRot) {
                  if (!_logo._snapX && Math.abs(newLx - _fl) < snapT) { newLx = _fl; _logo._snapX = true; _logo._snapGapRunY = _fl; }
                  if (!_logo._snapX && Math.abs(newLx + _fl) < snapT) { newLx = -_fl; _logo._snapX = true; _logo._snapGapRunY = -_fl; }
                } else {
                  if (!_logo._snapY && Math.abs(newLy - _fl) < snapT) { newLy = _fl; _logo._snapY = true; _logo._snapGapRunY = _fl; }
                  if (!_logo._snapY && Math.abs(newLy + _fl) < snapT) { newLy = -_fl; _logo._snapY = true; _logo._snapGapRunY = -_fl; }
                }
              }
              // Gap centers: midpoints between consecutive field lines
              const _gapOff = _gridOff + _lineSpc / 2; // first gap center
              for (let _gd = _gapOff; _gd <= _mrkLim - _lineSpc / 2 + 0.01; _gd += _lineSpc) {
                if (_markRot) {
                  if (!_logo._snapX && Math.abs(newLx - _gd) < snapT) { newLx = _gd; _logo._snapX = true; _logo._snapGapRunY = _gd; }
                  if (!_logo._snapX && Math.abs(newLx + _gd) < snapT) { newLx = -_gd; _logo._snapX = true; _logo._snapGapRunY = -_gd; }
                } else {
                  if (!_logo._snapY && Math.abs(newLy - _gd) < snapT) { newLy = _gd; _logo._snapY = true; _logo._snapGapRunY = _gd; }
                  if (!_logo._snapY && Math.abs(newLy + _gd) < snapT) { newLy = -_gd; _logo._snapY = true; _logo._snapGapRunY = -_gd; }
                }
              }
            }
          }
          // Same-type side-by-side snap with whole-foot gap (ladders↔ladders, dot drills↔dot drills)
          this._ladderSnapGaps = null;
          if (_tObj.turfLogos) {
            const _myIdx = this._turfLogoDragLogoIdx;
            const _myRot = ((_logo.rotation || 0) % Math.PI + Math.PI) % Math.PI; // normalize to [0, π)
            const _myW = _logo.width, _myH = _logo.height;
            // Determine if ladder is horizontal or vertical (after rotation)
            const _isH = Math.abs(_myRot % Math.PI) < 0.01 || Math.abs(_myRot % Math.PI - Math.PI) < 0.01;
            // width = ladder length (long), height = rung width (short), regardless of rotation
            const _myLongHalf = _myW / 2;
            const _myShortHalf = _myH / 2;
            // Find the closest ladder on each side (short axis) for snapping
            let _nearBelow = null, _nearAbove = null; // below = lesser short-axis pos, above = greater
            for (let _oi = 0; _oi < _tObj.turfLogos.length; _oi++) {
              if (_oi === _myIdx) continue;
              const _other = _tObj.turfLogos[_oi];
              if (_other.type !== _logo.type) continue;
              const _oRot = ((_other.rotation || 0) % Math.PI + Math.PI) % Math.PI;
              if (Math.abs(_myRot - _oRot) > 0.05 && Math.abs(_myRot - _oRot - Math.PI) > 0.05) continue;
              const _oIsH = Math.abs(_oRot % Math.PI) < 0.01 || Math.abs(_oRot % Math.PI - Math.PI) < 0.01;
              if (_oIsH !== _isH) continue;
              const _ox = _other.x || 0, _oy = _other.y || 0;
              const _oShortHalf = _other.height / 2;
              const longOverlap = _isH
                ? Math.abs(newLx - _ox) < Math.max(_myLongHalf, _other.width / 2)
                : Math.abs(newLy - _oy) < Math.max(_myLongHalf, _other.width / 2);
              if (!longOverlap) continue;
              const shortDist = _isH ? Math.abs(newLy - _oy) : Math.abs(newLx - _ox);
              const rawGap = shortDist - _myShortHalf - _oShortHalf;
              if (rawGap < -0.5 || rawGap > 10) continue;
              const oPos = _isH ? _oy : _ox;
              const myPos = _isH ? newLy : newLx;
              if (oPos <= myPos) {
                if (!_nearBelow || oPos > _nearBelow.pos) _nearBelow = { idx: _oi, other: _other, ox: _ox, oy: _oy, shortHalf: _oShortHalf, pos: oPos, rawGap };
              } else {
                if (!_nearAbove || oPos < _nearAbove.pos) _nearAbove = { idx: _oi, other: _other, ox: _ox, oy: _oy, shortHalf: _oShortHalf, pos: oPos, rawGap };
              }
            }
            // Snap to closest neighbor(s) and always show gap measurements
            const gaps = [];
            const _neighbors = [_nearBelow, _nearAbove].filter(Boolean);
            // First pass: attempt ladder-to-ladder snapping (may override field line snap)
            for (const _nb of _neighbors) {
              const _ox = _nb.ox, _oy = _nb.oy, _oShortHalf = _nb.shortHalf;
              const snappedGap = Math.max(0, Math.round(_nb.rawGap));
              const finalDist = snappedGap + _myShortHalf + _oShortHalf;
              const _sign = _isH ? (newLy >= _oy ? 1 : -1) : (newLx >= _ox ? 1 : -1);
              const snappedPos = (_isH ? _oy : _ox) + _sign * finalDist;
              if (Math.abs((_isH ? newLy : newLx) - snappedPos) < snapT * 2) {
                if (_isH) { newLy = snappedPos; _logo._snapY = true; }
                else { newLx = snappedPos; _logo._snapX = true; }
                if (_isH && !_logo._snapX && Math.abs(newLx - _ox) < snapT) { newLx = _ox; _logo._snapX = true; }
                if (!_isH && !_logo._snapY && Math.abs(newLy - _oy) < snapT) { newLy = _oy; _logo._snapY = true; }
              }
            }
            // Second pass: compute gap measurements from final position (always, regardless of snap)
            for (const _nb of _neighbors) {
              const _ox = _nb.ox, _oy = _nb.oy, _oShortHalf = _nb.shortHalf;
              const _sign = _isH ? (newLy >= _oy ? 1 : -1) : (newLx >= _ox ? 1 : -1);
              const actualDist = _isH ? Math.abs(newLy - _oy) : Math.abs(newLx - _ox);
              const actualGap = Math.max(0, actualDist - _myShortHalf - _oShortHalf);
              const myPos = _isH ? newLy : newLx;
              const _midLong = _isH ? (newLx + _ox) / 2 : (newLy + _oy) / 2;
              const e1 = _isH
                ? { x: _midLong, y: _oy + _sign * _oShortHalf }
                : { x: _ox + _sign * _oShortHalf, y: _midLong };
              const e2 = _isH
                ? { x: _midLong, y: myPos - _sign * _myShortHalf }
                : { x: myPos - _sign * _myShortHalf, y: _midLong };
              gaps.push({ gap: actualGap, axis: _isH ? 'y' : 'x', e1, e2 });
            }
            // Equidistant highlighting: find existing ladder pairs with matching gaps
            if (gaps.length > 0) {
              const _activeGap = gaps[0].gap; // gap from dragged item to its neighbor
              // Collect all placed items of same type (same orientation, excluding the one being dragged)
              const _placedLadders = [];
              for (let _pi = 0; _pi < _tObj.turfLogos.length; _pi++) {
                if (_pi === _myIdx) continue;
                const _pl = _tObj.turfLogos[_pi];
                if (_pl.type !== _logo.type) continue;
                const _pRot = ((_pl.rotation || 0) % Math.PI + Math.PI) % Math.PI;
                const _pIsH = Math.abs(_pRot % Math.PI) < 0.01 || Math.abs(_pRot % Math.PI - Math.PI) < 0.01;
                if (_pIsH !== _isH) continue;
                _placedLadders.push({ idx: _pi, x: _pl.x || 0, y: _pl.y || 0, w: _pl.width, h: _pl.height, shortHalf: _pl.height / 2 });
              }
              // Sort by short-axis position for pairwise comparison
              _placedLadders.sort((a, b) => _isH ? a.y - b.y : a.x - b.x);
              // Check each adjacent pair for matching gap (within 0.1 ft tolerance)
              for (let _qi = 0; _qi < _placedLadders.length - 1; _qi++) {
                const _la = _placedLadders[_qi], _lb = _placedLadders[_qi + 1];
                const _pairDist = _isH ? Math.abs(_lb.y - _la.y) : Math.abs(_lb.x - _la.x);
                const _pairGap = _pairDist - _la.shortHalf - _lb.shortHalf;
                if (_pairGap >= 0 && Math.abs(_pairGap - _activeGap) < 0.1) {
                  const _pMidLong = _isH ? (_la.x + _lb.x) / 2 : (_la.y + _lb.y) / 2;
                  const _pSign = _isH ? (_lb.y >= _la.y ? 1 : -1) : (_lb.x >= _la.x ? 1 : -1);
                  const pe1 = _isH
                    ? { x: _pMidLong, y: _la.y + _pSign * _la.shortHalf }
                    : { x: _la.x + _pSign * _la.shortHalf, y: _pMidLong };
                  const pe2 = _isH
                    ? { x: _pMidLong, y: _lb.y - _pSign * _lb.shortHalf }
                    : { x: _lb.x - _pSign * _lb.shortHalf, y: _pMidLong };
                  gaps.push({ gap: _pairGap, axis: _isH ? 'y' : 'x', e1: pe1, e2: pe2 });
                }
              }
              this._ladderSnapGaps = { turfObj: _tObj, gaps };
            }
          }
        }
        _logo.x = newLx;
        _logo.y = newLy;
        if (this._turfLogoDragGroupOrig && this._turfLogoDragGroupOrig.size > 0) {
          const dxL = newLx - _orig.x;
          const dyL = newLy - _orig.y;
          for (const [idx, origPos] of this._turfLogoDragGroupOrig) {
            const gl = _tObj.turfLogos[idx];
            if (!gl) continue;
            gl.x = origPos.x + dxL;
            gl.y = origPos.y + dyL;
          }
        }
      } else if (this._turfLogoDrag === 'rotate') {
        // Delta-based rotation — same approach as equipment
        const dx = world.x - _tObj.x, dy = world.y - _tObj.y;
        const tlx = dx * _co - dy * _so, tly = dx * _so + dy * _co;
        const lx = _orig.x, ly = _orig.y;
        const currentAngle = Math.atan2(tly - ly, tlx - lx);
        const delta = currentAngle - (this._turfLogoDragBaseAngle || 0);
        let angle = (_orig.r || 0) + delta;
        // Store raw angle for free-swinging handle display
        this._turfLogoRawRotation = angle;
        // Ladders always snap to 90° increments; logos use shift for 90° else fine steps
        const step = (_logo.type === 'ladder' || _logo.type === 'dotdrill' || this.shiftHeld) ? Math.PI / 2 : Math.PI / 64;
        angle = Math.round(angle / step) * step;
        _logo.rotation = angle;
        // Group rotate: rotate all other selected logos around group centroid
        if (this._turfLogoDragGroupOrig && this._turfLogoDragGroupOrig.size > 1) {
          const delta = angle - (_orig.r || 0);
          // Compute centroid from original positions
          let gcx = 0, gcy = 0;
          for (const [, op] of this._turfLogoDragGroupOrig) { gcx += op.x; gcy += op.y; }
          gcx /= this._turfLogoDragGroupOrig.size;
          gcy /= this._turfLogoDragGroupOrig.size;
          const cosD = Math.cos(delta), sinD = Math.sin(delta);
          for (const [idx, op] of this._turfLogoDragGroupOrig) {
            if (idx === this._turfLogoDragLogoIdx) continue;
            const gl = _tObj.turfLogos[idx];
            if (!gl) continue;
            const rx = op.x - gcx, ry = op.y - gcy;
            gl.x = gcx + rx * cosD - ry * sinD;
            gl.y = gcy + rx * sinD + ry * cosD;
            gl.rotation = op.r + delta;
          }
          // Also orbit the focused logo's position around centroid
          const myOrig = this._turfLogoDragGroupOrig.get(this._turfLogoDragLogoIdx);
          if (myOrig) {
            const rx = myOrig.x - gcx, ry = myOrig.y - gcy;
            _logo.x = gcx + rx * cosD - ry * sinD;
            _logo.y = gcy + rx * sinD + ry * cosD;
          }
        }
      } else {
        const dx = world.x - _tObj.x, dy = world.y - _tObj.y;
        const tlx = dx * _co - dy * _so, tly = dx * _so + dy * _co;
        const lx = _orig.x, ly = _orig.y;
        const dist = Math.sqrt((tlx - lx) ** 2 + (tly - ly) ** 2);
        const origDiag = Math.sqrt((_orig.w / 2) ** 2 + (_orig.h / 2) ** 2);
        if (origDiag > 0) {
          const scaleFactor = dist / origDiag;
          const newW = Math.max(1, _orig.w * scaleFactor);
          const newH = Math.max(1, _orig.h * scaleFactor);
          _logo.width = newW;
          _logo.height = newH;
          _logo._resizing = true;
        }
      }
      this.render();
      return;
    }
    // Rotate interaction
    if (this.activeTool === 'select' && this._selectRotating && this._selectRotateInfo) {
      if (this._selectRotateInfo.group) {
        // Group rotation — rotate all selected equipment around centroid
        const gi = this._selectRotateInfo;
        const currentAngle = Math.atan2(world.y - gi.cy, world.x - gi.cx);
        let delta = currentAngle - gi.baseAngle;
        const step = this._rotationStep(false);   // group rotate is equipment
        delta = Math.round(delta / step) * step;
        for (const snap of gi.originals) {
          // Rotate position around centroid
          const cos = Math.cos(delta), sin = Math.sin(delta);
          const rx = snap.x - gi.cx, ry = snap.y - gi.cy;
          snap.obj.x = gi.cx + rx * cos - ry * sin;
          snap.obj.y = gi.cy + rx * sin + ry * cos;
          snap.obj.rotation = snap.rotation + delta;
        }
        this.canvas.style.cursor = 'grabbing';
        this.render(); return;
      }
      const obj = this._selectRotateInfo.obj;
      const currentAngle = Math.atan2(world.y - obj.y, world.x - obj.x);
      const delta = currentAngle - this._selectRotateInfo.baseAngle;
      let newRotation = this._selectRotateInfo.origRotation + delta;
      if (obj.type === 'equipment' || obj.type === 'turf' || obj.type === 'platform' || obj.type === 'label') {
        const step = this._rotationStep(obj.type === 'platform');
        newRotation = Math.round(newRotation / step) * step;
      } else if (this.shiftHeld) {
        newRotation = Math.round(newRotation / (Math.PI / 2)) * (Math.PI / 2);
      }
      obj.rotation = newRotation;
      // Platform on tiles: re-snap position after rotation so corners stay on tile seams
      if (obj.type === 'platform') {
        const _rovl = window.roomDesignerOverlay;
        if (_rovl) {
          let _rRoom = null;
          for (const room of (this.rooms || [])) {
            if (!room.closed) continue;
            const geo = this._getRoomGeoCache(room);
            if (geo.roomPolygon && this.pointInPolygon(obj.x, obj.y, geo.roomPolygon)) { _rRoom = room; break; }
          }
          const _rZone = _rRoom ? _rovl._getZoneFlooringAtPoint(_rRoom, obj.x, obj.y) : null;
          if (_rZone && _rZone.product.type === 'tile') {
            const _rtW = _rZone.product.widthFt || 2;
            const _rtH = _rZone.product.heightFt || _rtW;
            const _rGeo = this._getRoomGeoCache(_rRoom);
            const _rSnap = _rovl._snapPlatformToTileGrid(obj.x, obj.y, obj.width, obj.depth, newRotation, _rtW, _rtH, _rZone.flooringAngle, _rGeo.roomPolygon);
            obj.x = _rSnap.x;
            obj.y = _rSnap.y;
          }
        }
      }
      // Wall collision check for turf/platform during rotation
      if (obj.type === 'turf' || obj.type === 'platform') {
        let _rotBlocked = this._equipmentOverlapsWall(obj.x, obj.y, obj.width, obj.depth, newRotation, null) || this._turfOverlapsTurf(obj.x, obj.y, obj.width, obj.depth, newRotation, obj);
        // Platform zone containment after rotation
        if (!_rotBlocked && obj.type === 'platform') {
          const _rovl2 = window.roomDesignerOverlay;
          if (_rovl2) {
            let _rRoom2 = null;
            for (const room of (this.rooms || [])) {
              if (!room.closed) continue;
              const geo = this._getRoomGeoCache(room);
              if (geo.roomPolygon && this.pointInPolygon(obj.x, obj.y, geo.roomPolygon)) { _rRoom2 = room; break; }
            }
            if (_rRoom2) {
              const _rot90 = (Math.round(newRotation / (Math.PI / 2)) % 2 !== 0);
              const _rW = _rot90 ? obj.depth : obj.width, _rD = _rot90 ? obj.width : obj.depth;
              const _rCenter = _rovl2._getZoneFlooringAtPoint(_rRoom2, obj.x, obj.y);
              const _rCenterPoly = _rCenter && _rCenter.zone ? _rCenter.zone.polygon : null;
              const _rCorners = [
                { x: obj.x - _rW/2, y: obj.y - _rD/2 }, { x: obj.x + _rW/2, y: obj.y - _rD/2 },
                { x: obj.x + _rW/2, y: obj.y + _rD/2 }, { x: obj.x - _rW/2, y: obj.y + _rD/2 }
              ];
              for (const c of _rCorners) {
                const _cZone = _rovl2._getZoneFlooringAtPoint(_rRoom2, c.x, c.y);
                const _cPoly = _cZone && _cZone.zone ? _cZone.zone.polygon : null;
                if (_cPoly !== _rCenterPoly) { _rotBlocked = true; break; }
              }
            }
          }
        }
        this._equipmentRotateBlocked = _rotBlocked;
      }
      this.canvas.style.cursor = 'grabbing';
      this.render(); return;
    }
    // Resize interaction
    if (this.activeTool === 'select' && this._selectResizing && this._selectResizeInfo) {
      const info = this._selectResizeInfo;
      const obj = info.obj;
      const step = this._currentGridStep();
      const minSize = 1 / 12; // 1 inch

      if (obj.type === 'rectangle' || obj.type === 'equipment' || obj.type === 'turf' || obj.type === 'platform') {
        const rot = info.origRotation;
        // Rotate mouse into object's local frame relative to anchor
        const localMouse = this._rotatePoint(world.x, world.y, info.anchorWx, info.anchorWy, -rot);
        const localAnchor = this._rotatePoint(info.anchorWx, info.anchorWy, info.anchorWx, info.anchorWy, -rot);

        const handle = info.handle;
        let newW = info.origWidth, newD = info.origDepth;
        let newCx = info.origX, newCy = info.origY;

        // Compute raw new dimensions from mouse distance to anchor
        const rawDx = localMouse.x - localAnchor.x;
        const rawDy = localMouse.y - localAnchor.y;

        const isW = handle.includes('w');
        const isE = handle.includes('e');
        const isN = handle.includes('n');
        const isS = handle.includes('s');
        const isCorner = (isW || isE) && (isN || isS);

        if (this.shiftHeld && isCorner) {
          // Aspect-ratio-locked resize for corner handles
          const aspect = info.origWidth / info.origDepth;
          const rawW = Math.abs(rawDx), rawD = Math.abs(rawDy);
          const scaleX = rawW / info.origWidth, scaleY = rawD / info.origDepth;
          if (scaleX >= scaleY) {
            newW = Math.max(minSize, Math.round(rawW / step) * step);
            newD = Math.max(minSize, Math.round((newW / aspect) / step) * step);
          } else {
            newD = Math.max(minSize, Math.round(rawD / step) * step);
            newW = Math.max(minSize, Math.round((newD * aspect) / step) * step);
          }
        } else {
          if (isE || isW) {
            const rawW = Math.abs(rawDx);
            newW = Math.max(minSize, Math.round(rawW / step) * step);
          }
          if (isN || isS) {
            const rawD = Math.abs(rawDy);
            newD = Math.max(minSize, Math.round(rawD / step) * step);
          }
        }

        // Recompute center so anchor stays fixed, following mouse direction
        const halfW = newW / 2, halfD = newD / 2;
        let localCx, localCy;
        if (isE || isW) localCx = rawDx >= 0 ? localAnchor.x + halfW : localAnchor.x - halfW;
        else localCx = localAnchor.x;
        if (isS || isN) localCy = rawDy >= 0 ? localAnchor.y + halfD : localAnchor.y - halfD;
        else localCy = localAnchor.y;

        // Rotate center back to world space
        const worldCenter = this._rotatePoint(localCx, localCy, info.anchorWx, info.anchorWy, rot);
        obj.x = worldCenter.x;
        obj.y = worldCenter.y;
        obj.width = newW;
        obj.depth = newD;
        // Check wall collision for turf during resize
        if (obj.type === 'turf') {
          this._equipmentResizeBlocked = this._equipmentOverlapsWall(obj.x, obj.y, obj.width, obj.depth, obj.rotation || 0, null) || this._turfOverlapsTurf(obj.x, obj.y, obj.width, obj.depth, obj.rotation || 0, obj);
        }
      } else if (obj.type === 'circle') {
        // Project mouse onto the handle's axis, following mouse direction
        const handle = info.handle;
        let rawDiam, rawDir;
        if (handle === 'n' || handle === 's') {
          const rawDy = world.y - info.anchorWy;
          rawDiam = Math.abs(rawDy);
          rawDir = rawDy >= 0 ? 1 : -1;
        } else {
          const rawDx = world.x - info.anchorWx;
          rawDiam = Math.abs(rawDx);
          rawDir = rawDx >= 0 ? 1 : -1;
        }
        const newDiam = Math.max(minSize, Math.round(rawDiam / step) * step);
        obj.diameter = newDiam;
        // Recompute center so anchor stays fixed, following mouse direction
        if (handle === 'n' || handle === 's') { obj.x = info.anchorWx; obj.y = info.anchorWy + rawDir * newDiam / 2; }
        else { obj.y = info.anchorWy; obj.x = info.anchorWx + rawDir * newDiam / 2; }
      }
      this.canvas.style.cursor = 'grabbing';
      this.render(); return;
    }
    // Wall endpoint dragging (includes click-move-click floating mode)
    if (this.activeTool === 'select' && (this._wallEndpointDragging || this._wallEndpointFloating) && this._wallEndpointDragInfo) {
      const info = this._wallEndpointDragInfo;
      let target = this.snapToGrid(world.x, world.y);
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — square is a MAGNET, not a cage. See
      // _axisSnapDelta: this used to force 0/90/180/270 at every working zoom.
      const _ep = this._axisSnapDelta(target.x - info.anchor.x, target.y - info.anchor.y,
                                      this.shiftHeld || this._shiftToggle);
      target = { x: info.anchor.x + _ep.dx, y: info.anchor.y + _ep.dy };
      // Temporarily restore original endpoint so getOpenEndpoints isn't polluted
      if (info.whichEnd === 'start') { info.wall.start.x = info.origX; info.wall.start.y = info.origY; }
      else { info.wall.end.x = info.origX; info.wall.end.y = info.origY; }
      // Check for snap to another open endpoint (wall is at original pos)
      info.snapTarget = this._findSnapEndpoint(target, info.wall, info.room);
      // For wall segment endpoints: also snap to room walls and other segment walls
      if (!info.snapTarget && info.room && info.room.segments) {
        const isLine = (info.room.type || 'wall') === 'line';
        const snap = this._findWallSurfaceSnap(target, info.wall, info.room, { rawPos: world, isLine });
        if (snap) {
          // Post-process: edge-snap for lines on room walls, grid-quantize for midpoints
          if (isLine && snap.snapType === 'midpoint' && snap.room && snap.room.walls && typeof this._quantizeWallEdgeSnap === 'function') {
            const edgePt = this._quantizeWallEdgeSnap(snap.wall, snap.param, world, snap.room);
            snap.x = edgePt.x; snap.y = edgePt.y;
          } else if (snap.snapType === 'midpoint' && typeof this._quantizeWallSnap === 'function') {
            const qPt = this._quantizeWallSnap(snap.wall, snap.param);
            snap.x = qPt.x; snap.y = qPt.y;
          }
          info.snapTarget = snap;
        }
      }
      let finalPos = info.snapTarget ? { x: info.snapTarget.x, y: info.snapTarget.y } : target;
      // Enforce minimum wall length for doors
      if (info.room && info.doorSnapshots.length > 0) {
        const minLen = this._getMinWallLengthForDoors(info.wall, info.room);
        if (minLen > 0) {
          const dx = finalPos.x - info.anchor.x, dy = finalPos.y - info.anchor.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < minLen) {
            const dirX = dist > 0 ? dx / dist : 0, dirY = dist > 0 ? dy / dist : 0;
            finalPos = { x: info.anchor.x + dirX * minLen, y: info.anchor.y + dirY * minLen };
          }
        }
      }
      // Set the final endpoint position
      if (info.whichEnd === 'start') { info.wall.start.x = finalPos.x; info.wall.start.y = finalPos.y; }
      else { info.wall.end.x = finalPos.x; info.wall.end.y = finalPos.y; }
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — dragging a curve terminus re-fits the arc
      this._refitCurvesTouching(info.room, [info.wall]);
      // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
      // Recompute door params from snapshots (preserve door width, push along wall)
      this._recomputeAllDoorParamsFromSnapshots(info.doorSnapshots);
      info.current = { x: finalPos.x, y: finalPos.y };
      this.canvas.style.cursor = 'crosshair';
      this.render(); return;
    }
    // Wall corner dragging
    if (this.activeTool === 'select' && this._wallCornerDragging && this._wallCornerDragInfo) {
      const info = this._wallCornerDragInfo;
      let target = this.snapToGrid(world.x, world.y);
      // Auto-constrain to axis when not zoomed to inch scale (behave as if shift held)
      const _spiCo = this.basePixelsPerFoot * this.zoomLevel / 12;
      if (this.shiftHeld || _spiCo < 3) {
        const _adx = Math.abs(target.x - info.origX), _ady = Math.abs(target.y - info.origY);
        target = _adx >= _ady ? { x: target.x, y: info.origY } : { x: info.origX, y: target.y };
      }
      // Enforce minimum wall length for doors on affected walls
      if (info.room) {
        for (const aw of info.affectedWalls) {
          const minLen = this._getMinWallLengthForDoors(aw.wall, info.room);
          if (minLen <= 0) continue;
          const fixedPt = aw.whichEnd === 'start' ? { x: aw.wall.end.x, y: aw.wall.end.y } : { x: aw.wall.start.x, y: aw.wall.start.y };
          const ddx = target.x - fixedPt.x, ddy = target.y - fixedPt.y;
          const dist = Math.sqrt(ddx * ddx + ddy * ddy);
          if (dist < minLen && dist > 0) {
            const dirX = ddx / dist, dirY = ddy / dist;
            target = { x: fixedPt.x + dirX * minLen, y: fixedPt.y + dirY * minLen };
          }
        }
      }
      // Move all walls' shared endpoints to the new corner position
      for (const aw of info.affectedWalls) {
        if (aw.whichEnd === 'start') { aw.wall.start.x = target.x; aw.wall.start.y = target.y; }
        else { aw.wall.end.x = target.x; aw.wall.end.y = target.y; }
      }
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — a curve terminus may have just moved with
      // this corner; re-fit the arc to its new chord before doors are recomputed.
      this._refitCurvesTouching(info.room, info.affectedWalls.map(aw => aw.wall));
      // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
      // Recompute door params on affected walls (preserve door width, push along wall)
      this._recomputeAllDoorParamsFromSnapshots(info.doorSnapshots);
      this.canvas.style.cursor = 'move';
      this.render(); return;
    }
    // Wall grab handle dragging
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — curve bulge drag
    if (this.activeTool === 'select' && this._curveBulgeDragging && this._curveBulgeDragInfo) {
      const CV = window.RoomDesignerCurve;
      const info = this._curveBulgeDragInfo;
      // Delta from where the handle was grabbed, so a parked handle doesn't jump.
      const cursorSag = CV.sagittaFrom(info.chordStart, info.chordEnd, world);
      let sag;
      if (this.shiftHeld) {
        // Hold Shift to flatten. Nudging a bow back to dead straight by hand is
        // fiddly, so Shift is a magnet to zero rather than a drag modifier.
        sag = 0;
        // Re-baseline every frame so RELEASING Shift resumes from flat under the
        // current cursor instead of snapping back to whatever bow it had before.
        info.baseSag = 0;
        info.grabSag = cursorSag;
        if (!info.warnedFlat) {
          info.warnedFlat = true;
          this.updateStatus('Shift — holding the wall straight. Release to curve again.');
        }
      } else {
        sag = info.baseSag + (cursorSag - info.grabSag);
        // Snap the bulge depth to whole inches so the radius lands on a buildable
        // number rather than an arbitrary float.
        sag = Math.round(sag * 12) / 12;
      }
      info.moved = true;
      this._curveApplySag(info, sag);
      // No explicit cache bust needed — _roomGeoFingerprint hashes every wall
      // coordinate, so the geo cache self-invalidates on reshape.
      this.render();
      return;
    }
    // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
    if (this.activeTool === 'select' && this._wallGrabDragging && this._wallGrabDragInfo) {
      const info = this._wallGrabDragInfo;
      let dx = world.x - info.dragStartX, dy = world.y - info.dragStartY;
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — same magnet as the endpoint drag, and for the
      // same reason: a divider could only ever be slid square, at any zoom you'd
      // actually work at, with no way to opt out. See _axisSnapDelta.
      const _gr = this._axisSnapDelta(dx, dy, this.shiftHeld || this._shiftToggle);
      const shiftAxisH = _gr.dy === 0 && _gr.dx !== 0;
      const shiftAxisV = _gr.dx === 0 && _gr.dy !== 0;
      dx = _gr.dx; dy = _gr.dy;
      // Snap wall midpoint to grid
      const midX = (info.origStartX + info.origEndX) / 2 + dx;
      const midY = (info.origStartY + info.origEndY) / 2 + dy;
      const snappedMid = this.snapToGrid(midX, midY);
      let snapDx = snappedMid.x - (info.origStartX + info.origEndX) / 2;
      let snapDy = snappedMid.y - (info.origStartY + info.origEndY) / 2;
      // When shift is held, don't let grid snap skew the locked axis
      if (shiftAxisH) snapDy = 0;
      if (shiftAxisV) snapDx = 0;
      // Enforce minimum wall length for doors on connected walls
      if (info.room) {
        const checkConnected = (connOrig, movedOrigX, movedOrigY, newMovedX, newMovedY) => {
          const origMK = `${(Math.round(movedOrigX * 1200) / 1200).toFixed(4)},${(Math.round(movedOrigY * 1200) / 1200).toFixed(4)}`;
          for (const co of connOrig) {
            const minLen = this._getMinWallLengthForDoors(co.wall, info.room);
            if (minLen <= 0) continue;
            const coSK = `${(Math.round(co.startX * 1200) / 1200).toFixed(4)},${(Math.round(co.startY * 1200) / 1200).toFixed(4)}`;
            const coEK = `${(Math.round(co.endX * 1200) / 1200).toFixed(4)},${(Math.round(co.endY * 1200) / 1200).toFixed(4)}`;
            let movingPt, fixedPt;
            if (coSK === origMK) { movingPt = { x: newMovedX, y: newMovedY }; fixedPt = { x: co.endX, y: co.endY }; }
            else if (coEK === origMK) { movingPt = { x: newMovedX, y: newMovedY }; fixedPt = { x: co.startX, y: co.startY }; }
            else continue;
            const cdx = movingPt.x - fixedPt.x, cdy = movingPt.y - fixedPt.y;
            const newLen = Math.sqrt(cdx * cdx + cdy * cdy);
            if (newLen < minLen) return false;
          }
          return true;
        };
        const newStartX = info.origStartX + snapDx, newStartY = info.origStartY + snapDy;
        const newEndX = info.origEndX + snapDx, newEndY = info.origEndY + snapDy;
        if (!checkConnected(info.connStartOrig, info.origStartX, info.origStartY, newStartX, newStartY) ||
            !checkConnected(info.connEndOrig, info.origEndX, info.origEndY, newEndX, newEndY)) {
          // Binary search for max valid translation magnitude
          let lo = 0, hi = 1;
          for (let iter = 0; iter < 20; iter++) {
            const mid = (lo + hi) / 2;
            const tDx = snapDx * mid, tDy = snapDy * mid;
            const tSX = info.origStartX + tDx, tSY = info.origStartY + tDy;
            const tEX = info.origEndX + tDx, tEY = info.origEndY + tDy;
            if (checkConnected(info.connStartOrig, info.origStartX, info.origStartY, tSX, tSY) &&
                checkConnected(info.connEndOrig, info.origEndX, info.origEndY, tEX, tEY)) {
              lo = mid;
            } else { hi = mid; }
          }
          snapDx *= lo; snapDy *= lo;
        }
      }
      // Translate the wall rigidly
      info.wall.start.x = info.origStartX + snapDx;
      info.wall.start.y = info.origStartY + snapDy;
      info.wall.end.x = info.origEndX + snapDx;
      info.wall.end.y = info.origEndY + snapDy;
      // Update connected walls at start endpoint
      const key = (p) => `${(Math.round(p.x * 1200) / 1200).toFixed(4)},${(Math.round(p.y * 1200) / 1200).toFixed(4)}`;
      for (const co of info.connStartOrig) {
        const origSK = key({ x: info.origStartX, y: info.origStartY });
        if (key({ x: co.startX, y: co.startY }) === origSK) { co.wall.start.x = info.wall.start.x; co.wall.start.y = info.wall.start.y; }
        else if (key({ x: co.endX, y: co.endY }) === origSK) { co.wall.end.x = info.wall.start.x; co.wall.end.y = info.wall.start.y; }
      }
      // Update connected walls at end endpoint
      for (const co of info.connEndOrig) {
        const origEK = key({ x: info.origEndX, y: info.origEndY });
        if (key({ x: co.startX, y: co.startY }) === origEK) { co.wall.start.x = info.wall.end.x; co.wall.start.y = info.wall.end.y; }
        else if (key({ x: co.endX, y: co.endY }) === origEK) { co.wall.end.x = info.wall.end.x; co.wall.end.y = info.wall.end.y; }
      }
      // Recompute door params on connected walls (preserve door width, push along wall)
      this._recomputeAllDoorParamsFromSnapshots(info.doorSnapshots);
      this.canvas.style.cursor = 'move';
      this.render(); return;
    }
    if (this.activeTool === 'select' && this._selectDragging) {
      // Snap-to-grid drag for objects (using original positions)
      if (this._selectDragOrigPositions) {
        let totalDx = world.x - this._selectDragStart.x, totalDy = world.y - this._selectDragStart.y;
        if (this.shiftHeld) { if (Math.abs(totalDx) >= Math.abs(totalDy)) totalDy = 0; else totalDx = 0; }
        // If any equipment is selected, snap delta to 1" to preserve relative spacing
        let hasEquipment = false;
        this.selectedObjects.forEach(obj => { if (obj.type === 'equipment') hasEquipment = true; });
        if (hasEquipment) {
          const inch = 1 / 12;
          totalDx = Math.round(totalDx / inch) * inch;
          totalDy = Math.round(totalDy / inch) * inch;
          this.selectedObjects.forEach(obj => {
            const orig = this._selectDragOrigPositions.get(obj);
            if (!orig) return;
            obj.x = orig.x + totalDx;
            obj.y = orig.y + totalDy;
          });
        } else {
          const _ov2 = window.roomDesignerOverlay;
          this.selectedObjects.forEach(obj => {
            const orig = this._selectDragOrigPositions.get(obj);
            if (!orig) return;
            const desired = { x: orig.x + totalDx, y: orig.y + totalDy };
            let snappedPos;
            // Platform on tile flooring: snap to tile grid instead of default grid
            if (obj.type === 'platform' && _ov2) {
              let _dragRoom = null;
              for (const room of (this.rooms || [])) {
                if (!room.closed) continue;
                const geo = this._getRoomGeoCache(room);
                if (geo.roomPolygon && this.pointInPolygon(desired.x, desired.y, geo.roomPolygon)) { _dragRoom = room; break; }
              }
              const _dragZone = _dragRoom ? _ov2._getZoneFlooringAtPoint(_dragRoom, desired.x, desired.y) : null;
              if (_dragZone && _dragZone.product.type === 'tile') {
                const _dtW = _dragZone.product.widthFt || 2;
                const _dtH = _dragZone.product.heightFt || _dtW;
                const _dragGeo = this._getRoomGeoCache(_dragRoom);
                snappedPos = _ov2._snapPlatformToTileGrid(desired.x, desired.y, obj.width, obj.depth, obj.rotation || 0, _dtW, _dtH, _dragZone.flooringAngle, _dragGeo.roomPolygon);
              } else {
                snappedPos = this.snapToGrid(desired.x, desired.y);
              }
            } else {
              snappedPos = this.snapToGrid(desired.x, desired.y);
            }
            /* Shift+drag turf: only snap the moving axis, lock the other to orig */
            if (this.shiftHeld && (obj.type === 'turf' || obj.type === 'platform')) {
              if (totalDy === 0) snappedPos.y = orig.y; // horizontal slide — lock Y
              else snappedPos.x = orig.x;               // vertical slide — lock X
            }
            obj.x = snappedPos.x;
            obj.y = snappedPos.y;
          });
        }
        // Wall collision check for dragged equipment or turf
        let hasDragCollidable = hasEquipment;
        if (!hasDragCollidable) this.selectedObjects.forEach(obj => { if (obj.type === 'turf' || obj.type === 'platform') hasDragCollidable = true; });
        if (hasDragCollidable) {
          let blocked = false;
          let turfOnTurf = false;
          this.selectedObjects.forEach(obj => {
            if (blocked) return;
            if (obj.type === 'equipment') {
              const tw = obj.tightWidth || obj.width, td = obj.tightDepth || obj.depth;
              const info = this._equipmentImages && this._equipmentImages[obj.equipmentId];
              if (this._equipmentOverlapsWall(obj.x, obj.y, tw, td, obj.rotation || 0, info || null)) blocked = true;
            } else if (obj.type === 'turf' || obj.type === 'platform') {
              const wallHit = this._equipmentOverlapsWall(obj.x, obj.y, obj.width, obj.depth, obj.rotation || 0, null);
              const turfHit = this._turfOverlapsTurf(obj.x, obj.y, obj.width, obj.depth, obj.rotation || 0, obj);
              const platHit = obj.type === 'platform' && this._platformOverlapsPlatform(obj.x, obj.y, obj.width, obj.depth, obj.rotation || 0, obj);
              if (wallHit || turfHit || platHit) blocked = true;
              if (turfHit || platHit) turfOnTurf = true;
              // Platform single-zone containment: all corners must stay in the same flooring zone
              const _ov3 = window.roomDesignerOverlay;
              if (!blocked && obj.type === 'platform' && _ov3) {
                const _dRot90 = (Math.round((obj.rotation || 0) / (Math.PI / 2)) % 2 !== 0);
                const _dW = _dRot90 ? obj.depth : obj.width, _dD = _dRot90 ? obj.width : obj.depth;
                let _dRoom = null;
                for (const room of (this.rooms || [])) {
                  if (!room.closed) continue;
                  const geo = this._getRoomGeoCache(room);
                  if (geo.roomPolygon && this.pointInPolygon(obj.x, obj.y, geo.roomPolygon)) { _dRoom = room; break; }
                }
                if (_dRoom) {
                  const _dCenter = _ov3._getZoneFlooringAtPoint(_dRoom, obj.x, obj.y);
                  const _dCenterPoly = _dCenter && _dCenter.zone ? _dCenter.zone.polygon : null;
                  const _dCorners = [
                    { x: obj.x - _dW/2, y: obj.y - _dD/2 }, { x: obj.x + _dW/2, y: obj.y - _dD/2 },
                    { x: obj.x + _dW/2, y: obj.y + _dD/2 }, { x: obj.x - _dW/2, y: obj.y + _dD/2 }
                  ];
                  for (const c of _dCorners) {
                    const _cz = _ov3._getZoneFlooringAtPoint(_dRoom, c.x, c.y);
                    const _czPoly = _cz && _cz.zone ? _cz.zone.polygon : null;
                    if (_czPoly !== _dCenterPoly) { blocked = true; turfOnTurf = true; break; }
                  }
                }
              }
            }
          });
          this._equipmentDragBlocked = blocked;
          this._turfDragBlockedByTurf = turfOnTurf;
        } else {
          this._equipmentDragBlocked = false;
          this._turfDragBlockedByTurf = false;
        }
      }
      // Doors slide along walls (snapped to grid)
      let constrainedWorld = world;
      if (this.shiftHeld && this._selectDragStart) {
        const cdx = world.x - this._selectDragStart.x, cdy = world.y - this._selectDragStart.y;
        if (Math.abs(cdx) >= Math.abs(cdy)) constrainedWorld = { x: world.x, y: this._selectDragStart.y };
        else constrainedWorld = { x: this._selectDragStart.x, y: world.y };
      }
      this.selectedDoors.forEach(door => {
        const info = this._selectDragDoorInfo.get(door);
        if (!info) return;
        const wall = door.wall;
        // CUSTOM_ROOMDESIGNER_BY_CLAUDE — a door in a curve opening fills its facet
        // exactly, so projecting onto that facet can't move it. Project onto the ARC
        // and slide the opening instead.
        if (wall.curveId && door.openingId) {
          const CVd = window.RoomDesignerCurve;
          const ownerD = this._findRoomForWall(wall);
          const facetsD = ownerD ? this._curveFacetsOf(ownerD, wall.curveId) : [];
          if (facetsD.length) {
            const endsD = CVd.endpointsOf(facetsD);
            const tcD = CVd.paramAtPoint(facetsD[0].curveArc, endsD.start, endsD.end, constrainedWorld);
            this._slideCurveDoor(door, tcD, this._currentGridStep());
          }
          return;
        }
        // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
        const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
        const lenSq = wdx * wdx + wdy * wdy;
        if (lenSq < 0.0001) return;
        const wallLen = Math.sqrt(lenSq);
        const mouseParam = ((constrainedWorld.x - wall.start.x) * wdx + (constrainedWorld.y - wall.start.y) * wdy) / lenSq;
        const rawMid = mouseParam - info.offset;
        // Snap the hinge (leading edge) distance along wall to grid
        const step = this._currentGridStep();
        const rawHingeDist = (rawMid - info.paramWidth / 2) * wallLen;
        const snappedHingeDist = Math.round(rawHingeDist / step) * step;
        const snappedHingeParam = wallLen > 0 ? snappedHingeDist / wallLen : 0;
        const snappedEndParam = snappedHingeParam + info.paramWidth;
        // Clamp so door stays on wall
        let lowP = snappedHingeParam, highP = snappedEndParam;
        if (lowP < 0) { lowP = 0; highP = info.paramWidth; }
        if (highP > 1) { highP = 1; lowP = 1 - info.paramWidth; }
        // Check overlap with other doors — keep previous position if overlapping
        const room = this._findRoomForWall(wall);
        if (room && this._doorsOverlap(wall, room, lowP, highP, door)) return;
        // Preserve original hinge/end ordering so swing direction doesn't flip
        door.hingeParam = info.hingeFirst ? lowP : highP;
        door.endParam = info.hingeFirst ? highP : lowP;
      });
      this.canvas.style.cursor = 'move';
      this.render(); return;
    }
    // Two-click box select — live preview
    if (this.activeTool === 'select' && this._selectCorner1) {
      this.selectionEnd = world;
      this._updateSelection();
      this.render(); return;
    }
    if (this.activeTool === 'select' && this._selectClickStart && !this._selectDragging) {
      const dsx = sx - this._selectClickStart.sx, dsy = sy - this._selectClickStart.sy;
      if (dsx * dsx + dsy * dsy > 25) {
        const hit = this._selectClickStart.hit;
        // Block dragging turf on Customize tab (select-only, no move)
        const _onCustomizeDrag = window.roomDesignerOverlay && window.roomDesignerOverlay.currentStep === 'finishing';
        if (_onCustomizeDrag && hit && hit.type === 'object' && hit.item && hit.item.type === 'turf') {
          // Do nothing — turf is not draggable on the Customize tab
        } else if (hit && (hit.type === 'object' || hit.type === 'door')) {
          // Start dragging — select the item if not already selected
          const isSelected = (hit.type === 'object' && this.selectedObjects.has(hit.item)) ||
                             (hit.type === 'door' && this.selectedDoors.has(hit.item));
          if (!isSelected) {
            if (!this._selectClickStart.shiftKey) { this.selectedWalls = new Set(); this.selectedDoors = new Set(); this.selectedObjects = new Set(); }
            if (hit.type === 'object') this.selectedObjects.add(hit.item);
            else if (hit.type === 'door') this.selectedDoors.add(hit.item);
          }
          this._expandSelectionToGroups();
          this.saveUndoState();
          this._selectDragging = true;
          this._selectDragStart = world;
          // Store original positions for snap-to-grid
          this._selectDragOrigPositions = new Map();
          this.selectedObjects.forEach(obj => {
            this._selectDragOrigPositions.set(obj, { x: obj.x, y: obj.y });
          });
          // Pre-compute door drag offsets
          this._selectDragDoorInfo = new Map();
          this.selectedDoors.forEach(door => {
            const wall = door.wall;
            const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
            const lenSq = wdx * wdx + wdy * wdy;
            if (lenSq < 0.0001) return;
            const mouseParam = ((world.x - wall.start.x) * wdx + (world.y - wall.start.y) * wdy) / lenSq;
            const midParam = (door.hingeParam + door.endParam) / 2;
            const hingeFirst = door.hingeParam <= door.endParam;
            this._selectDragDoorInfo.set(door, { offset: mouseParam - midParam, paramWidth: Math.abs(door.endParam - door.hingeParam), hingeFirst });
          });
          this.canvas.style.cursor = 'move';
          this.render(); return;
        }
        // Hit was a wall — not draggable, leave _selectClickStart for mouseup click-select
      }
    }
    // (box-select preview now handled above via _selectCorner1)
    if (this.activeTool === 'select' && !this._selectClickStart) {
      if (this._floorMode) {
        // Floor mode: check logo handles, then logo body, then turf handles, then turf objects
        if (this._turfLogoFocusedIdx != null) {
          const logoHandleHover = this._hitTestTurfLogoHandle(sx, sy);
          if (logoHandleHover) {
            if (logoHandleHover.name === 'rotate') {
              this.canvas.style.cursor = 'grab';
            } else {
              // Rotation-aware resize cursor for corner handles
              const obj = logoHandleHover.obj;
              const focusedLogo = obj.turfLogos && obj.turfLogos[this._turfLogoFocusedIdx];
              const totalRot = (obj.rotation || 0) + (focusedLogo ? focusedLogo.rotation || 0 : 0);
              // Base angles for each corner handle
              const baseAngles = { nw: 135, ne: 45, se: 135, sw: 45 };
              const base = baseAngles[logoHandleHover.name] || 0;
              // Normalize combined angle to pick nearest cursor direction
              let deg = ((base + totalRot * 180 / Math.PI) % 360 + 360) % 360;
              // 8 cursor sectors of 22.5° each
              const cursors = ['ns-resize', 'nesw-resize', 'ew-resize', 'nwse-resize'];
              const idx = Math.round(deg / 45) % 4;
              this.canvas.style.cursor = cursors[idx];
            }
          } else if (this._hitTestAnyTurfLogoBody(world)) {
            this.canvas.style.cursor = 'move';
          } else {
            const hover = this._hitTestSelect(world);
            this.canvas.style.cursor = hover ? 'move' : 'default';
          }
        } else if (this._hitTestAnyTurfLogoBody(world)) {
          this.canvas.style.cursor = 'move';
        } else {
          const handleHover = this._hitTestHandles(sx, sy);
          if (handleHover && handleHover.type === 'rotate') {
            this.canvas.style.cursor = 'grab';
          } else if (handleHover && handleHover.type === 'resize') {
            const h = handleHover.handle;
            const cursorMap = { n: 'ns-resize', s: 'ns-resize', e: 'ew-resize', w: 'ew-resize', nw: 'nwse-resize', se: 'nwse-resize', ne: 'nesw-resize', sw: 'nesw-resize' };
            this.canvas.style.cursor = cursorMap[h] || 'pointer';
          } else {
            const hover = this._hitTestSelect(world);
            this.canvas.style.cursor = hover ? 'move' : 'default';
          }
        }
      } else if (this._equipmentMode) {
        const _onCustomize = window.roomDesignerOverlay && window.roomDesignerOverlay.currentStep === 'finishing';
        if (_onCustomize) {
          // Customize tab: logo handles/body get full interaction cursors, turf body = pointer, equipment = move
          if (this._turfLogoFocusedIdx != null) {
            const logoHandleHover = this._hitTestTurfLogoHandle(sx, sy);
            if (logoHandleHover) {
              if (logoHandleHover.name === 'rotate') {
                this.canvas.style.cursor = 'grab';
              } else {
                const obj = logoHandleHover.obj;
                const focusedLogo = obj.turfLogos && obj.turfLogos[this._turfLogoFocusedIdx];
                const totalRot = (obj.rotation || 0) + (focusedLogo ? focusedLogo.rotation || 0 : 0);
                const baseAngles = { nw: 135, ne: 45, se: 135, sw: 45 };
                const base = baseAngles[logoHandleHover.name] || 0;
                let deg = ((base + totalRot * 180 / Math.PI) % 360 + 360) % 360;
                const cursors = ['ns-resize', 'nesw-resize', 'ew-resize', 'nwse-resize'];
                const idx = Math.round(deg / 45) % 4;
                this.canvas.style.cursor = cursors[idx];
              }
            } else if (this._hitTestAnyTurfLogoBody(world)) {
              this.canvas.style.cursor = 'move';
            } else {
              const hover = this._hitTestSelect(world);
              this.canvas.style.cursor = hover ? 'move' : 'default';
            }
          } else if (this._hitTestAnyTurfLogoBody(world)) {
            this.canvas.style.cursor = 'move';
          } else {
            const hover = this._hitTestSelect(world);
            this.canvas.style.cursor = hover ? (hover.item && hover.item.type === 'turf' ? 'pointer' : 'move') : 'default';
          }
        } else {
          // Equipment mode: check rotate handles first (they layer on top), then equipment objects
          const handleHover = this._hitTestHandles(sx, sy);
          if (handleHover && handleHover.type === 'rotate') {
            this.canvas.style.cursor = 'grab';
          } else {
            const hover = this._hitTestSelect(world);
            this.canvas.style.cursor = hover ? 'move' : 'default';
          }
        }
      } else {
        const handleHover = this._hitTestHandles(sx, sy);
        if (handleHover) {
          if (handleHover.type === 'rotate') {
            this.canvas.style.cursor = 'grab';
          } else if (handleHover.type === 'resize') {
            const h = handleHover.handle;
            const cursorMap = { n: 'ns-resize', s: 'ns-resize', e: 'ew-resize', w: 'ew-resize', nw: 'nwse-resize', se: 'nwse-resize', ne: 'nesw-resize', sw: 'nesw-resize' };
            this.canvas.style.cursor = cursorMap[h] || 'pointer';
          }
        } else if (this._hitTestWallEndpoints(sx, sy)) {
          this.canvas.style.cursor = 'crosshair';
        } else if (this._hitTestWallCornerHandles(sx, sy)) {
          this.canvas.style.cursor = 'move';
        } else if (this._hitTestWallGrabHandle(sx, sy)) {
          this.canvas.style.cursor = 'move';
        } else if (this._hitTestCurveBulgeHandle(sx, sy)) {
          this.canvas.style.cursor = 'grab'; // CUSTOM_ROOMDESIGNER_BY_CLAUDE — curve bulge
        } else {
          const hover = this._hitTestSelect(world);
          this.canvas.style.cursor = hover ? (hover.type === 'object' || hover.type === 'door' ? 'move' : 'pointer') : 'default';
        }
      }
    }
    // Keep crosshair cursor during equipment placement hover
    if (this._equipmentMode && this._pendingEquipmentId) {
      this.canvas.style.cursor = 'crosshair';
      // Mouse-driven rotation when position is locked
      if (this._pendingEquipmentPos && this.mouseWorldRaw) {
        const dx = this.mouseWorldRaw.x - this._pendingEquipmentPos.x;
        const dy = this.mouseWorldRaw.y - this._pendingEquipmentPos.y;
        if (Math.abs(dx) > 0.1 || Math.abs(dy) > 0.1) {
          const rawAngle = Math.atan2(dy, dx);
          const step = this._rotationStep(false);
          this._pendingEquipmentRotation = Math.round(rawAngle / step) * step;
          // Normalize to [0, 2π)
          this._pendingEquipmentRotation = ((this._pendingEquipmentRotation % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
          // One decimal: a 7.5° step lands on halves, and rounding to whole degrees
          // would report 8 / 23 for angles that are exactly 7.5 / 22.5.
          const deg = +(this._pendingEquipmentRotation * 180 / Math.PI).toFixed(2);
          this.updateStatus(`Rotation: ${deg}°  —  Move mouse to rotate, click to confirm`);
        }
      }
    }
    // Platform placement: mouse-driven rotation when position is locked
    if (this.activeTool === 'platform-place' && this._pendingPlatformPos && this.mouseWorldRaw) {
      this.canvas.style.cursor = 'crosshair';
      const dx = this.mouseWorldRaw.x - this._pendingPlatformPos.x;
      const dy = this.mouseWorldRaw.y - this._pendingPlatformPos.y;
      if (Math.abs(dx) > 0.1 || Math.abs(dy) > 0.1) {
        const rawAngle = Math.atan2(dy, dx);
        const step = this._rotationStep(true);
        this._pendingPlatformRotation = Math.round(rawAngle / step) * step;
        this._pendingPlatformRotation = ((this._pendingPlatformRotation % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
        const deg = +(this._pendingPlatformRotation * 180 / Math.PI).toFixed(2);
        this.updateStatus(`Rotation: ${deg}°  —  Move mouse to rotate, click to confirm`);
      }
    }
    // Ghost ladder follow / rotation during pending placement
    if (this._pendingLadder && this.mouseWorldRaw) {
      this.canvas.style.cursor = 'crosshair';
      const pl = this._pendingLadder;
      const tObj = pl.turfObj;
      const or = tObj.rotation || 0;
      const co = Math.cos(-or), so = Math.sin(-or);
      const dx = this.mouseWorldRaw.x - tObj.x, dy = this.mouseWorldRaw.y - tObj.y;
      const tlx = dx * co - dy * so, tly = dx * so + dy * co;
      if (!this._pendingLadderPos) {
        // Follow phase — track ghost position, detect if outside turf
        const lr = this._pendingLadderRotation || 0;
        const lhw = pl.lengthFt / 2, lhh = pl.heightFt / 2;
        const absC = Math.abs(Math.cos(lr)), absS = Math.abs(Math.sin(lr));
        const bbHW = lhw * absC + lhh * absS, bbHH = lhw * absS + lhh * absC;
        const halfW = tObj.width / 2, halfD = tObj.depth / 2;
        // Check if mouse is outside turf bounds
        this._pendingLadderBlocked = Math.abs(tlx) > halfW || Math.abs(tly) > halfD;
        // Clamp ghost within turf for rendering (it follows but stays inside)
        this._pendingLadderGhostPos = {
          x: Math.max(-halfW + bbHW, Math.min(halfW - bbHW, tlx)),
          y: Math.max(-halfD + bbHH, Math.min(halfD - bbHH, tly))
        };
      } else {
        // Rotation phase — compute angle from locked pos to mouse, snap to 90°
        const rdx = tlx - this._pendingLadderPos.x, rdy = tly - this._pendingLadderPos.y;
        if (Math.abs(rdx) > 0.1 || Math.abs(rdy) > 0.1) {
          const rawAngle = Math.atan2(rdy, rdx);
          this._pendingLadderRotation = Math.round(rawAngle / (Math.PI / 2)) * (Math.PI / 2);
          // Re-clamp position for new rotation
          const lr = this._pendingLadderRotation;
          const lhw = pl.lengthFt / 2, lhh = pl.heightFt / 2;
          const absC = Math.abs(Math.cos(lr)), absS = Math.abs(Math.sin(lr));
          const bbHW = lhw * absC + lhh * absS, bbHH = lhw * absS + lhh * absC;
          const halfW = tObj.width / 2, halfD = tObj.depth / 2;
          this._pendingLadderPos.x = Math.max(-halfW + bbHW, Math.min(halfW - bbHW, this._pendingLadderPos.x));
          this._pendingLadderPos.y = Math.max(-halfD + bbHH, Math.min(halfD - bbHH, this._pendingLadderPos.y));
          const deg = Math.round(((this._pendingLadderRotation * 180 / Math.PI) % 360 + 360) % 360);
          this.updateStatus(`Rotation: ${deg}°  —  Move mouse to rotate, click to confirm.`);
        }
      }
      this.render();
    }
    // Update paste preview offset to follow mouse (always inch-level snapping)
    if (this._pastePreview) {
      const pp = this._pastePreview;
      const inch = 1 / 12;
      const inchSnapped = { x: Math.round(world.x / inch) * inch, y: Math.round(world.y / inch) * inch };
      let dx = inchSnapped.x - pp.centroid.x;
      let dy = inchSnapped.y - pp.centroid.y;
      // Platform on tiles: snap to tile grid instead of inch grid
      const _ppOv = window.roomDesignerOverlay;
      if (_ppOv) {
        for (const obj of pp.objects) {
          if (obj.type !== 'platform') continue;
          const _ppx = obj.x + dx, _ppy = obj.y + dy;
          let _ppRoom = null;
          for (const room of (this.rooms || [])) {
            if (!room.closed) continue;
            const geo = this._getRoomGeoCache(room);
            if (geo.roomPolygon && this.pointInPolygon(_ppx, _ppy, geo.roomPolygon)) { _ppRoom = room; break; }
          }
          const _ppZone = _ppRoom ? _ppOv._getZoneFlooringAtPoint(_ppRoom, _ppx, _ppy) : null;
          if (_ppZone && _ppZone.product.type === 'tile') {
            const _ppTW = _ppZone.product.widthFt || 2;
            const _ppTH = _ppZone.product.heightFt || _ppTW;
            const _ppGeo = this._getRoomGeoCache(_ppRoom);
            const _ppSnap = _ppOv._snapPlatformToTileGrid(_ppx, _ppy, obj.width, obj.depth, obj.rotation || 0, _ppTW, _ppTH, _ppZone.flooringAngle, _ppGeo.roomPolygon);
            dx += _ppSnap.x - _ppx;
            dy += _ppSnap.y - _ppy;
          }
          break; // snap based on first platform
        }
      }
      // Shift constrains with edge-gap snapping from last paste, or axis lock from original
      if (this.shiftHeld) {
        if (this._lastPastedPos) {
          // Cardinal axis + whole-foot edge-gap snapping from last pasted position
          const lp = this._lastPastedPos;
          const rawDX = world.x - lp.x, rawDY = world.y - lp.y;
          const axis = Math.abs(rawDX) >= Math.abs(rawDY) ? 'x' : 'y';
          const sign = axis === 'x' ? (rawDX >= 0 ? 1 : -1) : (rawDY >= 0 ? 1 : -1);
          const halfO = axis === 'x' ? lp.halfW : lp.halfH;
          const halfG = axis === 'x' ? (pp.bboxHalfW || 0) : (pp.bboxHalfH || 0);
          const rawDist = axis === 'x' ? Math.abs(rawDX) : Math.abs(rawDY);
          const rawGap = rawDist - halfO - halfG;
          const snappedGap = Math.max(0, Math.floor(rawGap));
          const finalDist = snappedGap + halfO + halfG;
          const targetX = axis === 'x' ? lp.x + sign * finalDist : lp.x;
          const targetY = axis === 'y' ? lp.y + sign * finalDist : lp.y;
          dx = targetX - pp.centroid.x;
          dy = targetY - pp.centroid.y;
          // Edge points for measurement line
          const e1 = axis === 'x'
            ? { x: lp.x + sign * halfO, y: lp.y }
            : { x: lp.x, y: lp.y + sign * halfO };
          const e2 = axis === 'x'
            ? { x: targetX - sign * halfG, y: targetY }
            : { x: targetX, y: targetY - sign * halfG };
          this._pasteCardinalSnap = { gap: snappedGap, axis, sign, edge1: e1, edge2: e2 };
        } else {
          // Fallback: find nearest equipment piece and snap to it
          let nearest = null, nearDist = Infinity;
          for (const obj of this.objects) {
            if (obj.type !== 'equipment') continue;
            const d = Math.hypot(world.x - obj.x, world.y - obj.y);
            if (d < nearDist) { nearDist = d; nearest = obj; }
          }
          if (nearest) {
            const rawDX = world.x - nearest.x, rawDY = world.y - nearest.y;
            const axis = Math.abs(rawDX) >= Math.abs(rawDY) ? 'x' : 'y';
            const sign = axis === 'x' ? (rawDX >= 0 ? 1 : -1) : (rawDY >= 0 ? 1 : -1);
            const tw = nearest.tightWidth || nearest.width || 1;
            const td = nearest.tightDepth || nearest.depth || nearest.height || 1;
            const cosR = Math.abs(Math.cos(nearest.rotation || 0));
            const sinR = Math.abs(Math.sin(nearest.rotation || 0));
            const halfO = axis === 'x'
              ? (tw / 2) * cosR + (td / 2) * sinR
              : (tw / 2) * sinR + (td / 2) * cosR;
            const halfG = axis === 'x' ? (pp.bboxHalfW || 0) : (pp.bboxHalfH || 0);
            const rawDistV = axis === 'x' ? Math.abs(rawDX) : Math.abs(rawDY);
            const rawGap = rawDistV - halfO - halfG;
            const snappedGap = Math.max(0, Math.floor(rawGap));
            const finalDist = snappedGap + halfO + halfG;
            const targetX = axis === 'x' ? nearest.x + sign * finalDist : nearest.x;
            const targetY = axis === 'y' ? nearest.y + sign * finalDist : nearest.y;
            dx = targetX - pp.centroid.x;
            dy = targetY - pp.centroid.y;
            const e1 = axis === 'x'
              ? { x: nearest.x + sign * halfO, y: nearest.y }
              : { x: nearest.x, y: nearest.y + sign * halfO };
            const e2 = axis === 'x'
              ? { x: targetX - sign * halfG, y: targetY }
              : { x: targetX, y: targetY - sign * halfG };
            this._pasteCardinalSnap = { gap: snappedGap, axis, sign, edge1: e1, edge2: e2 };
          } else {
            if (Math.abs(dx) >= Math.abs(dy)) dy = 0;
            else dx = 0;
            this._pasteCardinalSnap = null;
          }
        }
      } else {
        this._pasteCardinalSnap = null;
      }
      pp.offset = { x: dx, y: dy };
      this.canvas.style.cursor = 'crosshair';
      this.render(); return;
    }
    if (this.activeTool === 'pen' && this.mode === 'draw' && this.currentWall) {
      this.currentWall.end = snapped; this.render();
      const distance = this.getDistance(this.currentWall.start, snapped);
      this.updateStatus(`Wall length: ${this.formatMeasurement(distance)} ${this.shiftHeld ? '(constrained)' : ''}`);
    } else if (this.mode === 'edit' && this.isDragging && this.selectedElement) {
      const dx = snapped.x - this.dragStart.x; const dy = snapped.y - this.dragStart.y;
      if (this.selectedElement.type === 'wall') { this.selectedElement.wall.start.x += dx; this.selectedElement.wall.start.y += dy; this.selectedElement.wall.end.x += dx; this.selectedElement.wall.end.y += dy; }
      else if (this.selectedElement.type === 'door') { this.selectedElement.door.position.x += dx; this.selectedElement.door.position.y += dy; }
      this.dragStart = snapped; this.render();
    } else { if (!this._popoverActive) this.render(); }
    // Final cursor override: I-beam wins over tool-specific cursors when hovering a label
    if (this._labelHover) this.canvas.style.cursor = 'text';
    // Measurement hover cursors (also called from handleMouseMove as failsafe)
    this._updateMeasureHoverCursor();
  }

  handleMouseUp(e) {
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — a region drag finishes here. A degenerate box
    // (a click, or a stray 1-inch twitch) is treated as a cancel rather than an
    // unusably tiny page.
    if (this._regionPick) {
      const rp = this._regionPick;
      if (rp.c1 && rp.c2) {
        const minX = Math.min(rp.c1.x, rp.c2.x), maxX = Math.max(rp.c1.x, rp.c2.x);
        const minY = Math.min(rp.c1.y, rp.c2.y), maxY = Math.max(rp.c1.y, rp.c2.y);
        if (maxX - minX >= 0.5 && maxY - minY >= 0.5) { this._endRegionPick({ minX, minY, maxX, maxY }); return; }
      }
      this._endRegionPick(null);
      return;
    }
    if (this._floorMode && !this.isPanning && this.activeTool !== 'select' && this.mode !== 'measure') return;
    // Re-enable pointer events on overlaying UI
    if (this._selectRotating || this._selectDragging || this._selectResizing) {
      this._setOverlayPointerEvents(true);
    }
    this._processUp();
  }

  _processUp() {
    if (this._turfLogoDrag) {
      if (this._turfLogoDragObj && this._turfLogoDragLogoIdx != null) {
        const _dLogo = this._turfLogoDragObj.turfLogos && this._turfLogoDragObj.turfLogos[this._turfLogoDragLogoIdx];
        if (_dLogo) {
          _dLogo._snapX = false; _dLogo._snapY = false; _dLogo._snapGapRunY = null; _dLogo._resizing = false;
          // Update center anchor: set if logo lands on field center, clear if off-center
          if (this._turfLogoDrag === 'move') {
            const fc = this._getTurfFieldCenter(this._turfLogoDragObj);
            const tol = 0.05; // ~0.6 inches tolerance
            const atCenter = Math.abs((_dLogo.x || 0) - fc.x) <= tol && Math.abs((_dLogo.y || 0) - fc.y) <= tol;
            _dLogo.anchoredToCenter = atCenter;
          }
        }
      }
      this._ladderSnapGaps = null;
      this._turfLogoDrag = null;
      this._turfLogoDragObj = null;
      this._turfLogoDragStart = null;
      this._turfLogoDragOrigLogo = null;
      this._turfLogoDragGroupOrig = null;
      this._turfLogoDragLogoIdx = null;
      this._turfLogoRawRotation = null;
      this.canvas.style.cursor = 'default';
      this.render();
      return;
    }
    if (this.isPanning) {
      this.isPanning = false;
      // View-only: open equipment/turf color panel only if it was a click (not a drag)
      if (this._viewOnly && (this._viewOnlyPanDist || 0) < 5) {
        const overlay = window.roomDesignerOverlay;
        if (this._viewOnlyHitEquipId && overlay) {
          overlay._openViewOnlyColorPanel(this._viewOnlyHitEquipId);
        } else if (this._viewOnlyHitTurfObj && overlay) {
          overlay._openViewOnlyTurfPanel(this._viewOnlyHitTurfObj);
        } else if (overlay && overlay._viewOnlyPanelOpen) {
          overlay._closeViewOnlyPanel();
        }
      }
      this._viewOnlyHitEquipId = null;
      this._viewOnlyHitTurfObj = null;
      this._viewOnlyPanDist = undefined;
      if (this._viewOnly) { this.canvas.style.cursor = 'grab'; }
      else if (this.activeTool === 'hand' || this._isDoorIdleState()) this.canvas.style.cursor = 'grab';
      else if (this.activeTool === 'select') this.canvas.style.cursor = 'default';
      else if (this.mode === 'measure') this._applyMeasureCursor();
      else this.canvas.style.cursor = 'crosshair';
    }
    if (this._measureDragging) {
      this._measureDragging = null;
      this.canvas.style.cursor = this.mode === 'measure' ? undefined : 'default';
      if (this.mode === 'measure') this._applyMeasureCursor();
      this.render(); return;
    }
    if (this._selectRotating) {
      // Platform blocked rotation: revert to original
      if (this._equipmentRotateBlocked && this._selectRotateInfo && this._selectRotateInfo.obj && this._selectRotateInfo.obj.type === 'platform') {
        const obj = this._selectRotateInfo.obj;
        obj.rotation = this._selectRotateInfo.origRotation;
        obj.x = this._selectRotateInfo.origX || obj.x;
        obj.y = this._selectRotateInfo.origY || obj.y;
        this.updateStatus('Rotation blocked — platform would cross zone boundary', { error: true });
      }
      // Snap turf to closest valid rotation if blocked
      if (this._equipmentRotateBlocked && this._selectRotateInfo && this._selectRotateInfo.obj && this._selectRotateInfo.obj.type === 'turf') {
        const obj = this._selectRotateInfo.obj;
        const origRot = this._selectRotateInfo.origRotation;
        const dropRot = obj.rotation;
        // Binary search: t=0 is original rotation (valid), t=1 is current (invalid)
        let lo = 0, hi = 1;
        for (let i = 0; i < 20; i++) {
          const mid = (lo + hi) / 2;
          const testRot = origRot + (dropRot - origRot) * mid;
          if (this._equipmentOverlapsWall(obj.x, obj.y, obj.width, obj.depth, testRot, null) || this._turfOverlapsTurf(obj.x, obj.y, obj.width, obj.depth, testRot, obj)) {
            hi = mid;
          } else {
            lo = mid;
          }
        }
        obj.rotation = origRot + (dropRot - origRot) * lo;
        this.updateStatus('Turf snapped — overlapped a wall or turf', { error: true });
      }
      this._equipmentRotateBlocked = false;
      this._selectRotating = false; this._selectRotateInfo = null;
      this.canvas.style.cursor = 'default';
      this.updateMeasurements(); this.render(); return;
    }
    if (this._selectResizing) {
      // Snap turf resize to wall edge via binary search
      if (this._equipmentResizeBlocked && this._selectResizeInfo && this._selectResizeInfo.obj.type === 'turf') {
        const info = this._selectResizeInfo;
        const obj = info.obj;
        const dropX = obj.x, dropY = obj.y, dropW = obj.width, dropD = obj.depth;
        // Binary search: t=0 is original (valid), t=1 is current (invalid)
        let lo = 0, hi = 1;
        for (let i = 0; i < 20; i++) {
          const mid = (lo + hi) / 2;
          const tw = info.origWidth + (dropW - info.origWidth) * mid;
          const td = info.origDepth + (dropD - info.origDepth) * mid;
          const tx = info.origX + (dropX - info.origX) * mid;
          const ty = info.origY + (dropY - info.origY) * mid;
          if (this._equipmentOverlapsWall(tx, ty, tw, td, obj.rotation || 0, null) || this._turfOverlapsTurf(tx, ty, tw, td, obj.rotation || 0, obj)) {
            hi = mid;
          } else {
            lo = mid;
          }
        }
        obj.width = info.origWidth + (dropW - info.origWidth) * lo;
        obj.depth = info.origDepth + (dropD - info.origDepth) * lo;
        obj.x = info.origX + (dropX - info.origX) * lo;
        obj.y = info.origY + (dropY - info.origY) * lo;
        this.updateStatus('Turf snapped — overlapped a wall or turf.');
      }
      this._equipmentResizeBlocked = false;
      this._selectResizing = false; this._selectResizeInfo = null;
      this.canvas.style.cursor = 'default';
      this.updateMeasurements(); this.render(); return;
    }
    if (this._wallEndpointDragging && this._wallEndpointDragInfo) {
      const info = this._wallEndpointDragInfo;
      // Detect click (no significant drag) → enter floating mode instead of committing
      const ms = this.mouseScreen || { x: 0, y: 0 };
      const dsx = ms.x - (info.downSx || 0), dsy = ms.y - (info.downSy || 0);
      const distSq = dsx * dsx + dsy * dsy;
      if (distSq < 64) { // < 8px movement = click
        this._wallEndpointFloating = true;
        this._wallEndpointDragging = false;
        this.canvas.style.cursor = 'crosshair';
        this.render(); return;
      }
      this._commitWallEndpointDrag();
      return;
    }
    if (this._wallCornerDragging) {
      const _dcRoom = this._wallCornerDragInfo && this._wallCornerDragInfo.room;
      this._wallCornerDragging = false; this._wallCornerDragInfo = null;
      if (_dcRoom && _dcRoom._savedCutoutPolys) _dcRoom._savedCutoutPolys = null;
      if (_dcRoom && _dcRoom._savedOuterWalls) _dcRoom._savedOuterWalls = null;
      this.canvas.style.cursor = 'default';
      this.updateMeasurements(); this.render(); return;
    }
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — end curve bulge drag
    if (this._curveBulgeDragging) {
      const _cbInfo = this._curveBulgeDragInfo;
      this._curveBulgeDragging = false; this._curveBulgeDragInfo = null;
      this.canvas.style.cursor = 'default';
      if (_cbInfo && _cbInfo.moved && _cbInfo.owner) {
        // Re-close the room: creating or collapsing a curve changes the wall count,
        // and `closed` is derived from wall topology.
        if (_cbInfo.owner.walls) _cbInfo.owner.closed = this.isRoomClosed(_cbInfo.owner);
        const CV = window.RoomDesignerCurve;
        if (_cbInfo.curveId) {
          const facets = this._curveFacetsOf(_cbInfo.owner, _cbInfo.curveId);
          const arc = facets.length ? facets[0].curveArc : null;
          if (arc) {
            this.updateStatus('Curved wall — radius ' + this.formatMeasurement(arc.r) +
                              ', arc ' + this.formatMeasurement(CV.arcLength(arc)) + '.');
          }
        } else {
          this.updateStatus('Wall straightened.');
        }
      }
      this.updateMeasurements(); this.render(); return;
    }
    // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
    if (this._wallGrabDragging) {
      const _dgRoom = this._wallGrabDragInfo && this._wallGrabDragInfo.room;
      this._wallGrabDragging = false; this._wallGrabDragInfo = null;
      if (_dgRoom && _dgRoom._savedCutoutPolys) _dgRoom._savedCutoutPolys = null;
      if (_dgRoom && _dgRoom._savedOuterWalls) _dgRoom._savedOuterWalls = null;
      this.canvas.style.cursor = 'default';
      this.updateMeasurements(); this.render(); return;
    }
    if (this._selectDragging) {
      // Revert equipment / snap turf if they overlap a wall
      if (this._equipmentDragBlocked && this._selectDragOrigPositions) {
        // Check if ALL selected objects are turf (no equipment mixed in)
        let allTurf = true;
        this.selectedObjects.forEach(obj => { if (obj.type !== 'turf') allTurf = false; });
        if (allTurf && this.selectedObjects.size > 0) {
          // Turf: binary search along drag vector to find closest valid position
          this._snapTurfToWallOnDrop();
        } else {
          // Equipment: full revert to original positions
          this.selectedObjects.forEach(obj => {
            const orig = this._selectDragOrigPositions.get(obj);
            if (orig) { obj.x = orig.x; obj.y = orig.y; }
          });
          this.updateStatus('Cannot place — overlaps a wall', { error: true });
        }
      }
      this._equipmentDragBlocked = false;
      this._turfDragBlockedByTurf = false;
      this._selectDragging = false; this._selectClickStart = null; this._selectDragDoorInfo = null; this._selectDragOrigPositions = null;
      this.canvas.style.cursor = 'default';
      this._updateSelectionStatus(); this.updateMeasurements(); this.render(); return;
    }
    if (this._selectClickStart) {
      // Click (not drag) on an item — hit-test for nearest item
      const clickWorld = this._selectClickStart.world;
      const shiftKey = this._selectClickStart.shiftKey;
      const clickSx = this._selectClickStart.sx, clickSy = this._selectClickStart.sy;
      this._selectClickStart = null;
      const hit = this._hitTestSelect(clickWorld);
      if (hit) {
        if (shiftKey) {
          // Toggle item in selection
          // CUSTOM_ROOMDESIGNER_BY_CLAUDE — a curve selects/deselects as one unit
          if (hit.type === 'wall') { if (this.selectedWalls.has(hit.item)) this._deselectCurveGroup(hit.item); else this._selectCurveGroup(hit.item); }
          else if (hit.type === 'door') { if (this.selectedDoors.has(hit.item)) this.selectedDoors.delete(hit.item); else this.selectedDoors.add(hit.item); }
          else if (hit.type === 'object') { if (this.selectedObjects.has(hit.item)) this.selectedObjects.delete(hit.item); else this.selectedObjects.add(hit.item); }
        } else {
          // If clicking on a door that's already the sole selection, enter swing edit
          if (hit.type === 'door' && hit.item.type !== 'roller' && this.selectedDoors.size === 1 && this.selectedDoors.has(hit.item) && this.selectedWalls.size === 0 && this.selectedObjects.size === 0) {
            const room = this._findRoomForWall(hit.item.wall);
            if (room) {
              this._doorSwingEdit = { door: hit.item, room };
              this.updateStatus('Click to choose new swing direction, or click on the door line for closed. Esc to cancel.');
              this.render(); return;
            }
          }
          this.selectedWalls = new Set(); this.selectedDoors = new Set(); this.selectedObjects = new Set();
          if (hit.type === 'wall') this._selectCurveGroup(hit.item); // CUSTOM_ROOMDESIGNER_BY_CLAUDE — whole curve
          else if (hit.type === 'door') this.selectedDoors.add(hit.item);
          else if (hit.type === 'object') {
            this.selectedObjects.add(hit.item);
            /* CUSTOM_ROOMDESIGNER_BY_CLAUDE (Ryan 2026-07-31) — the product info card
               that used to pop up here on selecting equipment / turf / a platform is
               GONE. Its on/off switch left the Display drawer when Equipment Labels
               took the slot (41187074), which left the card firing with no way to stop
               it — worse than the toggle it replaced. Selecting an item now just
               selects it.
               ⚠️ The two OTHER surfaces that showed product info are deliberately
               untouched: the flooring tooltip on the Flooring tab, and the hover
               callout a customer gets on a shared view-only plan (_showHoverCallout) —
               that one is a selling surface, not an editing one. `showProductCards`
               still gates both, so do not delete it. */
            // Sync platform panel when selecting a platform
            if (hit.item.type === 'platform' && window.roomDesignerOverlay) {
              window.roomDesignerOverlay._syncPlatformPanelToSelection();
            }
          }
        }
      } else if (!shiftKey) { this.selectedWalls = new Set(); this.selectedDoors = new Set(); this.selectedObjects = new Set(); }
      this._expandSelectionToGroups();
      this._updateSelectionStatus(); this.render();
      // Sync turf panel controls on selection change
      const _ov = window.roomDesignerOverlay;
      if (_ov) { const _b = document.getElementById('rdSidePanelBody'); if (_b) _ov._syncTurfPanelToSelection(_b); }
      return;
    }
    this.isDragging = false;
  }

  /* ── TOUCH HANDLERS ── */

  _getTouchDist(t1, t2) {
    const dx = t1.clientX - t2.clientX, dy = t1.clientY - t2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  _getTouchMid(t1, t2, rect) {
    return {
      x: (t1.clientX + t2.clientX) / 2 - rect.left,
      y: (t1.clientY + t2.clientY) / 2 - rect.top
    };
  }

  _handleTouchStart(e) {
    // View-only mode: allow only pan (1-finger) and pinch-zoom (2-finger)
    if (this._viewOnly) {
      e.preventDefault();
      const touches = e.touches;
      const rect = this.canvas.getBoundingClientRect();
      if (touches.length >= 2) {
        this._isPinching = true;
        this._pinchStartDist = this._getTouchDist(touches[0], touches[1]);
        this._pinchStartZoom = this.zoomLevel;
        const mid = this._getTouchMid(touches[0], touches[1], rect);
        this._pinchMidX = mid.x; this._pinchMidY = mid.y;
        this._pinchLastMidX = mid.x; this._pinchLastMidY = mid.y;
      } else if (touches.length === 1) {
        const sx = touches[0].clientX - rect.left;
        const sy = touches[0].clientY - rect.top;
        this.isPanning = true;
        this.panStart = { x: sx, y: sy };
        this._viewOnlyPanDist = 0;
        this.canvas.style.cursor = 'grabbing';
      }
      return;
    }
    if (this._floorMode && this.mode !== 'measure') { e.preventDefault(); return; }
    e.preventDefault();
    this._cancelPanAnimation();
    // Dismiss repeat button on any canvas touch
    if (this._showRepeatButton) {
      this._showRepeatButton = false;
      this._updateFloatingRepeat();
    }
    // Track if a popover was open when touch started (for pan-on-drag / tap-to-dismiss)
    this._touchStartedWithPopover = this._mobilePopoverOpen;
    // Block iOS body scroll while touching canvas
    document.body.style.overflow = 'hidden';
    const touches = e.touches;
    const rect = this.canvas.getBoundingClientRect();

    if (touches.length >= 2) {
      // Pinch/pan — cancel any single-finger actions

      this._touchActiveId = null;
      this._touchStartPos = null;
      this._touchMoved = false;
      this._touchIsDragging = false;
      this._isPinching = true;
      this._pinchStartDist = this._getTouchDist(touches[0], touches[1]);
      this._pinchStartZoom = this.zoomLevel;
      const mid = this._getTouchMid(touches[0], touches[1], rect);
      this._pinchMidX = mid.x;
      this._pinchMidY = mid.y;
      this._pinchLastMidX = mid.x;
      this._pinchLastMidY = mid.y;
      // Cancel any single-finger panning or drag operations
      if (this._objectPlacePanning) { this.isPanning = false; this._objectPlacePanning = false; }
      if (this._doorIdlePanning) { this.isPanning = false; this._doorIdlePanning = false; }
      if (this._eraserPanning) { this.isPanning = false; this._eraserPanning = false; }
      if (this._penDrawPanning) { this.isPanning = false; this._penDrawPanning = false; }
      if (this._selectDragging || this._wallEndpointDragging || this._wallEndpointFloating || this._wallCornerDragging || this._wallGrabDragging || this._curveBulgeDragging || this._selectResizing || this._selectRotating) {
        this.handleEscape();
      }
      return;
    }

    // Single finger
    const touch = touches[0];
    const sx = touch.clientX - rect.left;
    const sy = touch.clientY - rect.top;
    this._touchActiveId = touch.identifier;
    this._touchStartPos = { sx, sy };
    this._touchMoved = false;
    this._touchMovedDist = 0;
    this._touchIsDragging = false;

    // Check double-tap (300ms window, 20px proximity)
    const now = Date.now();
    if (this._lastTapTime && (now - this._lastTapTime) < 300 && this._lastTapPos) {
      const dx = sx - this._lastTapPos.sx, dy = sy - this._lastTapPos.sy;
      if (dx * dx + dy * dy < 400) { // 20px radius
        this._lastTapTime = 0;
        this._lastTapPos = null;
        // Double-tap: enter door swing edit (same as dblclick)
        if (this.activeTool === 'select') {
          const world = this.screenToWorld(sx, sy);
          const hit = this._hitTestSelect(world);
          if (hit && hit.type === 'door' && hit.item.type !== 'roller') {
            const room = this._findRoomForWall(hit.item.wall);
            if (room) {
              this._doorSwingEdit = { door: hit.item, room };
              this.selectedWalls = new Set(); this.selectedDoors = new Set([hit.item]); this.selectedObjects = new Set();
              this.updateStatus('Tap to choose new swing direction. Tap door line for closed.');
              this.render();
              if (navigator.vibrate) navigator.vibrate(50);
              return;
            }
          }
        }
        return;
      }
    }

    // For tools that need immediate feedback (pen preview, eraser), start processing
    const world = this.screenToWorld(sx, sy);
    const snapped = this.snapToGrid(world.x, world.y);
    // On mobile, don't update mouseWorld on touchstart for tools that use joystick —
    // prevents finger touches / pinch from shifting the drawing tool's position
    const isMobilePenDraw = this._isMobileToolbar && this.activeTool === 'pen' && this.mode === 'draw';
    const isMobileIdleOrPlace = this._isMobileToolbar && this.activeTool === 'door' &&
      ((this.objectType === 'rectangle' || this.objectType === 'circle') || this._isDoorIdleState());
    if (!isMobileIdleOrPlace && !isMobilePenDraw) {
      this.mouseScreen = { x: sx, y: sy };
      this.mouseWorld = snapped;
      this.mouseWorldRaw = world;
    }
    // Don't call _processDown yet — wait to see if it's a tap or drag
    // Exception: pen tool preview line — update it to touch position immediately
    if (this.activeTool === 'pen' && this.mode === 'draw' && this.currentWall && this.hasPlacedFirstPoint && !this._freehandAutoPlaced && !this._isMobileToolbar) {
      const constrained = this.constrainToAxis(this.currentWall.start, snapped);
      this.currentWall.end = constrained;
      this.render();
    }
  }

  _handleTouchMove(e) {
    if (this._floorMode && this.mode !== 'measure') { e.preventDefault(); return; }
    e.preventDefault();
    const touches = e.touches;
    const rect = this.canvas.getBoundingClientRect();

    if (this._isPinching && touches.length >= 2) {
      // Pinch zoom + pan centered on current finger midpoint
      const dist = this._getTouchDist(touches[0], touches[1]);
      const mid = this._getTouchMid(touches[0], touches[1], rect);

      // Pan by midpoint delta (finger drag)
      this.panX += mid.x - this._pinchLastMidX;
      this.panY += mid.y - this._pinchLastMidY;
      this._pinchLastMidX = mid.x;
      this._pinchLastMidY = mid.y;

      // Zoom centered on current midpoint (tracks finger position)
      const factor = dist / this._pinchStartDist;
      const newZoom = Math.max(this.minZoom, Math.min(this.maxZoom, this._pinchStartZoom * factor));
      const wb = this.screenToWorld(mid.x, mid.y);
      this.zoomLevel = newZoom;
      const wa = this.worldToScreen(wb.x, wb.y);
      this.panX += mid.x - wa.x;
      this.panY += mid.y - wa.y;

      document.getElementById('zoomLevel').textContent = Math.round(this.zoomLevel * 100) + '%';
      if (this._onZoomHook) this._onZoomHook();
      if (this._onPanHook) this._onPanHook();
      this.render();
      return;
    }

    // Single finger
    if (touches.length !== 1) return;

    // View-only: single-finger pan (touchActiveId not set in view-only mode)
    if (this._viewOnly && this.isPanning) {
      const sx = touches[0].clientX - rect.left;
      const sy = touches[0].clientY - rect.top;
      const dx = sx - this.panStart.x, dy = sy - this.panStart.y;
      this.panX += dx; this.panY += dy;
      this.panStart = { x: sx, y: sy };
      if (this._viewOnlyPanDist !== undefined) this._viewOnlyPanDist += Math.abs(dx) + Math.abs(dy);
      if (this._onPanHook) this._onPanHook();
      this.render();
      return;
    }

    const touch = Array.from(touches).find(t => t.identifier === this._touchActiveId);
    if (!touch) return;

    const sx = touch.clientX - rect.left;
    const sy = touch.clientY - rect.top;

    if (this._touchStartPos) {
      const dx = sx - this._touchStartPos.sx;
      const dy = sy - this._touchStartPos.sy;
      this._touchMovedDist = Math.sqrt(dx * dx + dy * dy);
    }

    const dragThreshold = 8; // pixels
    if (!this._touchMoved && this._touchMovedDist > dragThreshold) {
      this._touchMoved = true;


      // Popover was open when touch started — drag to pan (keep menu open)
      if (this._touchStartedWithPopover && this._touchStartPos) {
        this._touchPopoverPanning = true;
        this.isPanning = true;
        this.panStart = { x: this._touchStartPos.sx, y: this._touchStartPos.sy };
        this.canvas.style.cursor = 'grabbing';
        if (navigator.vibrate) navigator.vibrate(15);
      } else if (this._touchStartPos) {
        // Normal drag — send _processDown first with starting position
        const startWorld = this.screenToWorld(this._touchStartPos.sx, this._touchStartPos.sy);
        const startSnapped = this.snapToGrid(startWorld.x, startWorld.y);
        this._touchIsDragging = true;
        if (this._isDoorIdleState()) {
          // Door idle state: drag to pan (like hand tool)
          this._doorIdlePanning = true;
          this.isPanning = true;
          this.panStart = { x: this._touchStartPos.sx, y: this._touchStartPos.sy };
        } else if (this._isMobileToolbar && this.activeTool === 'door' && (this.objectType === 'rectangle' || this.objectType === 'circle')) {
          // Mobile object placement: drag to pan, don't place via drag (checkmark only)
          this._objectPlacePanning = true;
          this.isPanning = true;
          this.panStart = { x: this._touchStartPos.sx, y: this._touchStartPos.sy };
        } else if (this.mode === 'eraser' && this._isMobile) {
          // Eraser drag on mobile: pan instead of erasing (tap still erases)
          this._eraserPanning = true;
          this.isPanning = true;
          this.panStart = { x: this._touchStartPos.sx, y: this._touchStartPos.sy };
        } else if (this.mode === 'eraser' && !this._floorMode) {
          // Eraser drag on desktop: continuous erase on move
          this.handleErase(startWorld);
        } else if (this._isMobileToolbar && this.activeTool === 'pen' && this.mode === 'draw') {
          // Mobile pen/draw: drag to pan (joystick drives drawing, not finger)
          this._penDrawPanning = true;
          this.isPanning = true;
          this.panStart = { x: this._touchStartPos.sx, y: this._touchStartPos.sy };
        } else if (this._floorMode && this.activeTool === 'select' && window.roomDesignerOverlay) {
          // Floor mode select: drag on empty space → floor box select
          const overlay = window.roomDesignerOverlay;
          const hit = overlay._hitTestFlooredRoom(startWorld.x, startWorld.y);
          if (!hit) {
            this._touchFloorBoxSelect = true;
            overlay._floorSelectCorner1 = { x: startWorld.x, y: startWorld.y };
            overlay._floorSelectEnd = { x: startWorld.x, y: startWorld.y };
          } else {
            // Tapped directly on a floored room — handled by touchend
          }
        } else if (this.activeTool === 'select' && !this._hitTestSelect(startWorld) && !this._hitTestHandles(this._touchStartPos.sx, this._touchStartPos.sy) && !this._hitTestWallEndpoints(this._touchStartPos.sx, this._touchStartPos.sy) && !this._hitTestWallCornerHandles(this._touchStartPos.sx, this._touchStartPos.sy)) {
          // Drag on empty space in select tool → start box select
          this._touchBoxSelect = true;
          this._selectCorner1 = startWorld;
          this.selectionStart = startWorld;
          this.selectionEnd = startWorld;
          this.isSelecting = true;
        } else {
          this._processDown(this._touchStartPos.sx, this._touchStartPos.sy, startWorld, startSnapped, 0, this.shiftHeld);
        }
        if (navigator.vibrate) navigator.vibrate(15);
      }
    }

    if (this._touchMoved) {
      // Popover-open panning, eraser panning, or door idle panning — route through normal pan logic
      if (this._touchPopoverPanning || this._eraserPanning || this._doorIdlePanning || this._objectPlacePanning || this._penDrawPanning) {
        this._processMove(sx, sy);
      } else if (this.mode === 'eraser' && this._touchIsDragging && !this._floorMode) {
        // Eraser: continuous erase along drag path (desktop)
        const world = this.screenToWorld(sx, sy);
        this.handleErase(world);
      } else if (this._touchFloorBoxSelect && window.roomDesignerOverlay) {
        // Live-update floor box selection during drag
        const world = this.screenToWorld(sx, sy);
        window.roomDesignerOverlay._floorSelectEnd = { x: world.x, y: world.y };
        this.render();
      } else if (this._touchBoxSelect) {
        // Live-update box selection during drag
        const world = this.screenToWorld(sx, sy);
        this.selectionEnd = world;
        this._updateSelection();
        this.render();
      } else {
        this._processMove(sx, sy);
      }
    } else {
      // Even before drag threshold, update preview for pen tool
      if (!this._touchStartedWithPopover) {
        const world = this.screenToWorld(sx, sy);
        let snapped = this.snapToGrid(world.x, world.y);
        this.mouseScreen = { x: sx, y: sy };
        if (this.activeTool === 'pen' && this.mode === 'draw' && this.currentWall && this.hasPlacedFirstPoint && !this._freehandAutoPlaced && !this._isMobileToolbar) {
          snapped = this.constrainToAxis(this.currentWall.start, snapped);
          this.mouseWorld = snapped;
          this.mouseWorldRaw = world;
          this.currentWall.end = snapped;
          this.render();
        }
      }
    }
  }

  _handleTouchEnd(e) {
    if (this._floorMode && this.mode !== 'measure') return;
    // Restore body scroll
    if (e.touches.length === 0) {
      document.body.style.overflow = '';
    }

    if (this._isPinching) {
      if (e.touches.length < 2) {
        this._isPinching = false;
        // Re-anchor pan state to remaining finger so there's no jump
        if (e.touches.length === 1) {
          const rect = this.canvas.getBoundingClientRect();
          const sx = e.touches[0].clientX - rect.left;
          const sy = e.touches[0].clientY - rect.top;
          if (this._viewOnly) {
            this.isPanning = true;
            this.panStart = { x: sx, y: sy };
          } else {
            this._touchActiveId = e.touches[0].identifier;
            this._touchStartPos = { sx, sy };
            this._touchMoved = true;       // treat as already-moving (no tap action)
            this._touchIsDragging = true;   // go straight to pan mode
            this.isPanning = true;
            this.panStart = { x: sx, y: sy };
          }
        }
      }
      return;
    }

    // Popover was open when touch started — tap dismisses, drag was panning
    if (this._touchStartedWithPopover) {
      if (this._touchPopoverPanning) {
        // End the pan
        this.isPanning = false;
        this.canvas.style.cursor = (this.activeTool === 'hand' || this._isDoorIdleState()) ? 'grab' : this.activeTool === 'select' ? 'default' : 'crosshair';
      } else {
        // Single tap on grid — if a popover input is focused, just dismiss the
        // keyboard (blur) and keep the popover open; next tap will close it.
        const ae = document.activeElement;
        const inPopover = ae && (ae.tagName === 'INPUT' || ae.tagName === 'SELECT') &&
          ae.closest('#penPopover, #objectsPopover, #zoomPopover, #settingsDrawer');
        if (inPopover) {
          ae.blur();
        } else {
          this._dismissMobilePopovers();
        }
      }
      this._touchPopoverPanning = false;
      this._touchStartedWithPopover = false;
      this._touchActiveId = null;
      this._touchStartPos = null;
      return;
    }

    // View-only touch: just pan (no equipment/turf click panels on mobile)
    if (this._viewOnly && this.isPanning) {
      this.isPanning = false;
      this.canvas.style.cursor = 'grab';
      return;
    }

    if (!this._touchMoved && this._touchStartPos) {
      // Tap (not drag) — treat as click
      const sx = this._touchStartPos.sx;
      const sy = this._touchStartPos.sy;
      const world = this.screenToWorld(sx, sy);
      const snapped = this.snapToGrid(world.x, world.y);

      // Record for double-tap detection
      this._lastTapTime = Date.now();
      this._lastTapPos = { sx, sy };

      // Eraser tap (in floor mode, overlay handles it)
      if (this.mode === 'eraser' && !this._floorMode) {
        this.handleErase(world);
        if (navigator.vibrate) navigator.vibrate(50);
        this._touchActiveId = null;
        this._touchStartPos = null;
        return;
      }

      // Pen tool on mobile: canvas taps blocked except for repositioning on non-empty canvas
      if (this.activeTool === 'pen' && this.mode === 'draw' && this._isMobileToolbar) {
        const preConfirmRect = this.penDrawMode === 'rectangle' && this._rectCorner1 && !this._rectCorner1Confirmed;
        const preConfirmFreehand = this.penDrawMode !== 'rectangle' && this._freehandAutoPlaced && !this._freehandStartConfirmed;
        if (preConfirmRect || preConfirmFreehand) {
          // Non-empty canvas, pre-confirm: tap repositions starting point
          this._dismissMobilePopovers();
          if (preConfirmRect) {
            this._rectCorner1 = { x: snapped.x, y: snapped.y };
            this.mouseWorld = { x: snapped.x, y: snapped.y };
          } else {
            this.currentWall.start = { x: snapped.x, y: snapped.y };
            this.currentWall.end = { x: snapped.x, y: snapped.y };
            this.mouseWorld = { x: snapped.x, y: snapped.y };
          }
          if (navigator.vibrate) navigator.vibrate(15);
          this.render();
          this._touchActiveId = null;
          this._touchStartPos = null;
          return;
        }
        // All other taps blocked — joystick drives everything
        this._touchActiveId = null;
        this._touchStartPos = null;
        return;
      }

      // Door/objects on mobile: tap handling
      if (this.activeTool === 'door' && this._isMobileToolbar) {
        // Door idle state with objectType 'door': ignore taps (user must pick sub-type to start new placement)
        if (this._isDoorIdleState()) {
          this._touchActiveId = null;
          this._touchStartPos = null;
          return;
        }
        this._dismissMobilePopovers();
        // Mobile door workflow with _doorMobile state
        if (this._doorMobile && (this.objectType !== 'rectangle' && this.objectType !== 'circle')) {
          if (this._doorMobile.phase === 1) {
            // Phase 1: tap snaps door CENTER to nearest wall
            const nearest = this.findNearestWall(world, 2);
            if (nearest) {
              const wall = nearest.wall;
              const room = nearest.room;
              const doorWidthFeet = this.doorWidth / 12;
              if (this.getWallLength(wall) >= doorWidthFeet - 0.01) {
                const tapWallLen = this.getWallLength(wall);
                const tapHalfParam = tapWallLen > 0 ? (doorWidthFeet / 2) / tapWallLen : 0;
                const param = this.getWallParam(world, wall);
                let snappedParam = this.snapParamToGrid(wall, param);
                snappedParam = Math.max(tapHalfParam, Math.min(1 - tapHalfParam, snappedParam));
                const snapPoint = this.paramToPoint(wall, snappedParam);
                this.mouseWorldRaw = { x: snapPoint.x, y: snapPoint.y };
                this.mouseWorld = { x: snapPoint.x, y: snapPoint.y };
                this._doorMobile.wall = wall;
                this._doorMobile.room = room;
                this._doorMobile.param = snappedParam;
                this._doorMobile.point = { x: snapPoint.x, y: snapPoint.y };
                // Always update arrows to match current wall
                this._setJoystickArrowsDirs(this._getDoorWallArrows(wall));
                if (navigator.vibrate) navigator.vibrate(15);
                this.render();
              }
            }
          }
          // Phase 3: tap on swing dots to select and place
          if (this._doorMobile.phase === 3) {
            const dots = this._getDoorSwingDots();
            let nearestDot = null, nearestDist = Infinity;
            for (const dot of dots) {
              const dist = Math.sqrt((world.x - dot.tipX) ** 2 + (world.y - dot.tipY) ** 2);
              if (dist < nearestDist) { nearestDist = dist; nearestDot = dot; }
            }
            const threshold = Math.max(0.5, 25 / this.pixelsPerFoot);
            if (nearestDot && nearestDist < threshold) {
              this._doorMobile.swingIdx = nearestDot.swingIdx;
              this._doorMobile.swingOption = nearestDot.option;
              this._doorMobileConfirmSwing();
            }
          }
          this._touchActiveId = null;
          this._touchStartPos = null;
          return;
        }
        // Rectangle/circle objects: original tap-to-place behavior
        this.mouseScreen = { x: sx, y: sy };
        this.mouseWorld = snapped;
        this.mouseWorldRaw = world;
        this._mobilePanToWorldPoint(snapped.x, snapped.y);
        this._updateJoystickVisibility();
        if (navigator.vibrate) navigator.vibrate(15);
        this.render();
        this._touchActiveId = null;
        this._touchStartPos = null;
        return;
      }

      // Mobile select tool: handle tap detection for joystick activation
      if (this.activeTool === 'select' && this._isMobileToolbar) {
        const hasSelection = this.selectedWalls.size > 0 || this.selectedDoors.size > 0 || this.selectedObjects.size > 0;
        // doorSwingEdit: tap on swing dot to select, tap elsewhere to cancel
        if (this._selectHandleMode === 'doorSwingEdit') {
          const info = this._selectHandleInfo;
          if (info) {
            const door = info.door;
            const wall = door.wall;
            const doorWidthFeet = door.width / 12;
            const hingePoint = this.paramToPoint(wall, door.hingeParam);
            const endPoint = this.paramToPoint(wall, door.endParam);
            const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
            const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
            if (wLen > 0) {
              const perpX = -wdy / wLen, perpY = wdx / wLen;
              const doorRoom = info.room;
              const off = this._getDoorOffset(wall, perpX, perpY, doorRoom);
              const offHinge = { x: hingePoint.x + off.x, y: hingePoint.y + off.y };
              const offEnd = { x: endPoint.x + off.x, y: endPoint.y + off.y };
              const midX = (offHinge.x + offEnd.x) / 2, midY = (offHinge.y + offEnd.y) / 2;
              // Build dot positions
              const dots = [];
              for (let i = 0; i < info.swingOptions.length; i++) {
                const opt = info.swingOptions[i];
                let tipX, tipY;
                if (door.type === 'double') {
                  tipX = midX + perpX * opt.perpSign * (doorWidthFeet / 2);
                  tipY = midY + perpY * opt.perpSign * (doorWidthFeet / 2);
                } else {
                  const pivotPt = opt.hingeEnd === 'hinge' ? offHinge : offEnd;
                  tipX = pivotPt.x + perpX * opt.perpSign * doorWidthFeet;
                  tipY = pivotPt.y + perpY * opt.perpSign * doorWidthFeet;
                }
                dots.push({ swingIdx: i, option: opt, tipX, tipY });
              }
              dots.push({ swingIdx: -1, option: null, tipX: midX, tipY: midY });
              let nearestDot = null, nearestDist = Infinity;
              for (const dot of dots) {
                const dist = Math.sqrt((world.x - dot.tipX) ** 2 + (world.y - dot.tipY) ** 2);
                if (dist < nearestDist) { nearestDist = dist; nearestDot = dot; }
              }
              const threshold = Math.max(0.5, 25 / this.pixelsPerFoot);
              if (nearestDot && nearestDist < threshold) {
                info.swingIdx = nearestDot.swingIdx;
                info.swingOption = nearestDot.option;
                this._updateDoorEditSwingArrows();
                if (navigator.vibrate) navigator.vibrate(15);
                this.render();
                this._touchActiveId = null;
                this._touchStartPos = null;
                return;
              }
            }
          }
          // Tap elsewhere — cancel swing edit
          this.handleEscape();
          this._touchActiveId = null;
          this._touchStartPos = null;
          return;
        }
        if (hasSelection && this._selectHandleMode && this._selectHandleMode !== 'cycling') {
          // Tap on empty space — deselect all
          if (!this._hitTestSelect(world)) {
            if (this._selectRotateDialActive) this._exitRotateDial();
            this._deactivateSelectHandle();
            this.selectedWalls = new Set();
            this.selectedDoors = new Set();
            this.selectedObjects = new Set();
            this._updateSelectionStatus();
            this._updateJoystickVisibility();
            this.render();
            this._touchActiveId = null;
            this._touchStartPos = null;
            return;
          }
          // Specific handle active — tap on object returns to cycling (no revert)
          if (this._selectRotateDialActive) this._exitRotateDial();
          const tapCycleList = this._buildHandleCycleList();
          if (tapCycleList.length > 1 || (tapCycleList.length === 1 && tapCycleList[0].mode !== 'wholeMove')) {
            this._selectHandleMode = 'cycling';
            this._selectHandleInfo = null;
            this._selectHandleCycleList = tapCycleList;
            this._selectHandleCycleIndex = Math.min(this._selectHandleCycleIndex, tapCycleList.length - 1);
            this._setJoystickArrowsEnabled(true);
          } else {
            this._selectHandleMode = null;
            this._selectHandleInfo = null;
            this._selectHandleCycleList = [];
            this._selectHandleCycleIndex = 0;
            this._evaluateSelectJoystickMode();
          }
          this._clearArrowPulse();
          this._clearCheckPulse();
          this._startArrowPulse(3000);
          this._startCheckPulse(3000);
          if (navigator.vibrate) navigator.vibrate(15);
          this.render();
          this._touchActiveId = null;
          this._touchStartPos = null;
          return;
        }
        if (hasSelection && this._selectHandleMode === 'cycling') {
          // During cycling, finger tap on a handle still works as direct override
          const hh = this._hitTestHandles(sx, sy);
          if (hh && hh.type === 'rotate') {
            this._activateSelectHandle('rotate', { obj: hh.obj, origRotation: hh.obj.rotation || 0 });
            if (navigator.vibrate) navigator.vibrate(30);
            this.render();
            this._touchActiveId = null;
            this._touchStartPos = null;
            return;
          }
          if (hh && hh.type === 'resize') {
            const obj = hh.obj;
            const oppName = this._getOppositeHandle(hh.handle);
            const handles = this._getResizeHandles(obj);
            const oppH = handles.find(h => h.name === oppName);
            this._activateSelectHandle('resize', {
              obj, handle: hh.handle,
              anchorWx: oppH ? oppH.wx : obj.x, anchorWy: oppH ? oppH.wy : obj.y,
              origX: obj.x, origY: obj.y,
              origWidth: obj.width, origDepth: obj.depth,
              origDiameter: obj.diameter, origRotation: obj.rotation || 0
            });
            if (navigator.vibrate) navigator.vibrate(30);
            this.render();
            this._touchActiveId = null;
            this._touchStartPos = null;
            return;
          }
          const epHit = this._hitTestWallEndpoints(sx, sy);
          if (epHit) {
            this._activateSelectHandle('wallEndpoint', {
              wall: epHit.wall, room: epHit.room, whichEnd: epHit.whichEnd,
              origX: epHit.pt.x, origY: epHit.pt.y
            });
            if (navigator.vibrate) navigator.vibrate(30);
            this.render();
            this._touchActiveId = null;
            this._touchStartPos = null;
            return;
          }
          const cornerHit = this._hitTestWallCornerHandles(sx, sy);
          if (cornerHit) {
            this._activateSelectHandle('wallCorner', {
              wall: cornerHit.wall, room: cornerHit.room, whichEnd: cornerHit.whichEnd
            });
            if (navigator.vibrate) navigator.vibrate(30);
            this.render();
            this._touchActiveId = null;
            this._touchStartPos = null;
            return;
          }
          const grabHit = this._hitTestWallGrabHandle(sx, sy);
          if (grabHit) {
            this._activateSelectHandle('wallGrab', { wall: grabHit.wall });
            if (navigator.vibrate) navigator.vibrate(30);
            this.render();
            this._touchActiveId = null;
            this._touchStartPos = null;
            return;
          }
          // CUSTOM_ROOMDESIGNER_BY_CLAUDE — curve bulge handle (tap to activate,
          // then nudge with the perpendicular joystick arrows)
          const bulgeHitT = this._hitTestCurveBulgeHandle(sx, sy);
          if (bulgeHitT) {
            this._activateSelectHandle('curveBulge', { wall: bulgeHitT.wall, chord: bulgeHitT.handle.chord });
            if (navigator.vibrate) navigator.vibrate(30);
            this.render();
            this._touchActiveId = null;
            this._touchStartPos = null;
            return;
          }
          // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
          // No handle hit during cycling — tap on empty space deselects
          if (!this._hitTestSelect(world)) {
            this._deactivateSelectHandle();
            this.selectedWalls = new Set();
            this.selectedDoors = new Set();
            this.selectedObjects = new Set();
            this._updateSelectionStatus();
            this._updateJoystickVisibility();
            this.render();
            this._touchActiveId = null;
            this._touchStartPos = null;
            return;
          }
          // Tap hit an object — fall through to normal select logic
        }
      }

      // Fire as click: processDown + processUp
      this._processDown(sx, sy, world, snapped, 0, this.shiftHeld);
      this._processUp();
      this._updateJoystickVisibility();
      if (navigator.vibrate) navigator.vibrate(15);
    } else if (this._touchFloorBoxSelect && window.roomDesignerOverlay) {
      // End of floor drag box-select — finalize
      this._touchFloorBoxSelect = false;
      const overlay = window.roomDesignerOverlay;
      if (overlay._floorSelectCorner1 && overlay._floorSelectEnd) {
        overlay._finalizeFloorBoxSelection(overlay._floorSelectEnd, false);
      } else {
        overlay._floorSelectCorner1 = null;
        overlay._floorSelectEnd = null;
      }
      this.render();
    } else if (this._touchBoxSelect) {
      // End of drag box-select — finalize selection
      this._touchBoxSelect = false;
      if (this._selectCorner1 && this.selectionEnd) {
        this._finalizeBoxSelection(this._selectCorner1, this.selectionEnd);
      }
      this._selectCorner1 = null;
      this.isSelecting = false;
      this.selectionStart = null;
      this.selectionEnd = null;
      this._updateSelectionStatus();
      this.render();
    } else if (this._touchIsDragging) {
      if (this._doorIdlePanning) {
        // End door idle pan
        this.isPanning = false;
        this._doorIdlePanning = false;
        this.canvas.style.cursor = 'grab';
      } else if (this._eraserPanning) {
        // End eraser pan on mobile
        this.isPanning = false;
        this._eraserPanning = false;
      } else if (this._objectPlacePanning) {
        // End object placement pan on mobile
        this.isPanning = false;
        this._objectPlacePanning = false;
      } else if (this._penDrawPanning) {
        // End pen/draw pan on mobile
        this.isPanning = false;
        this._penDrawPanning = false;
      } else {
        // Normal drag end
        this._processUp();
      }
    }

    this._touchActiveId = null;
    this._touchStartPos = null;
    this._touchMoved = false;
    this._touchIsDragging = false;
    // After any touch interaction, restart appropriate joystick pulse
    this._restartJoystickPulse();
    // Reset popover idle pulse timer on any touch
    if (this._mobilePopoverOpen) this._startPopoverIdlePulse();
  }

  _handleTouchCancel(e) {
    document.body.style.overflow = '';
    this._isPinching = false;
    this._touchActiveId = null;
    this._touchStartPos = null;
    this._touchMoved = false;
    this._touchIsDragging = false;
    this._touchBoxSelect = false;
    this._touchFloorBoxSelect = false;
    if (this._eraserPanning) { this.isPanning = false; this._eraserPanning = false; }
    if (this._doorIdlePanning) { this.isPanning = false; this._doorIdlePanning = false; }
    if (this._objectPlacePanning) { this.isPanning = false; this._objectPlacePanning = false; }
    if (this._penDrawPanning) { this.isPanning = false; this._penDrawPanning = false; }
    // Cancel any in-progress operations
    if (this._selectDragging || this._wallEndpointDragging || this._wallCornerDragging || this._wallGrabDragging || this._curveBulgeDragging || this._selectResizing || this._selectRotating) {
      this.handleEscape();
    }
  }

  /* ── ERASER TOOL ── */

  _flattenRoomCutouts(room) {
    if (!room.cutouts || room.cutouts.length === 0) return;
    const visibleBoundary = this.getVisibleRoomWalls(room);
    const interiorData = this.getInteriorCutoutWalls(room);
    const interiorWalls = interiorData.map(d => d.wall);
    const allVisible = [...visibleBoundary, ...interiorWalls];
    for (const door of room.doors) {
      const hingePoint = this.paramToPoint(door.wall, door.hingeParam);
      const endPoint = this.paramToPoint(door.wall, door.endParam);
      let bestWall = null, bestDist = Infinity;
      for (const w of allVisible) {
        const d = this.distanceToWall(hingePoint, w);
        if (d < bestDist) { bestDist = d; bestWall = w; }
      }
      if (bestWall) {
        door.wall = bestWall;
        door.hingeParam = this.getWallParam(hingePoint, bestWall);
        door.endParam = this.getWallParam(endPoint, bestWall);
      }
    }
    room.walls = allVisible;
    room.cutouts = [];
    room.closed = this.isRoomClosed(room);
  }

  /**
   * Option B: After a subtract operation, replace room.walls with the actual
   * visible outline and clear room.cutouts. The room becomes a clean shape
   * (e.g. an L with 6 walls) where all walls are first-class and draggable.
   * Returns the number of detached sections that were dropped.
   */
  _rebuildRoomAfterSubtract(parent) {
    if (!parent.cutouts || parent.cutouts.length === 0) return 0;

    // Save outer boundary walls (exact coordinates, pre-clipping) for future subtract ops.
    // After rebuild, parent.walls becomes the merged outline (L-shape etc.) which would
    // cause double-subtraction if used as input for the next rebuild.
    parent._savedOuterWalls = parent.walls.map(w => ({
      start: {x: w.start.x, y: w.start.y},
      end: {x: w.end.x, y: w.end.y}
    }));

    // 1. Compute the full visible outline
    const visWalls = this.getVisibleRoomWalls(parent);
    const intData = this.getInteriorCutoutWalls(parent);
    const intWalls = intData.map(d => d.wall);
    const allVis = [...visWalls, ...intWalls];
    // Remove duplicate walls from overlapping cutouts (shared edges appear from both cutouts)
    const dedupedVis = this._removeCoincidentWalls(allVis, 0.02);

    if (dedupedVis.length === 0) {
      const idx = this.rooms.indexOf(parent);
      if (idx >= 0) {
        this.rooms.splice(idx, 1);
        this.selectedWalls = new Set();
        this.selectedDoors = new Set();
      }
      return 0;
    }

    // 2. Detect splits — count components that will be dropped
    const components = this._getConnectedComponents(dedupedVis);
    let removedCount = 0;
    if (components.length > 1) {
      let bigIdx = 0, bigArea = 0;
      for (let c = 0; c < components.length; c++) {
        let mnX = Infinity, mnY = Infinity, mxX = -Infinity, mxY = -Infinity;
        for (const w of components[c]) {
          mnX = Math.min(mnX, w.start.x, w.end.x); mnY = Math.min(mnY, w.start.y, w.end.y);
          mxX = Math.max(mxX, w.start.x, w.end.x); mxY = Math.max(mxY, w.start.y, w.end.y);
        }
        const a = (mxX - mnX) * (mxY - mnY);
        if (a > bigArea) { bigArea = a; bigIdx = c; }
      }
      const outerPoly = this.tracePolygon(components[bigIdx]);
      for (let c = 0; c < components.length; c++) {
        if (c === bigIdx) continue;
        const poly = this.tracePolygon(components[c]);
        if (poly && poly.length >= 3 && outerPoly) {
          let cx = 0, cy = 0;
          poly.forEach(p => { cx += p.x; cy += p.y; }); cx /= poly.length; cy /= poly.length;
          if (!this.pointInPolygon(cx, cy, outerPoly)) removedCount++;
        }
      }
    }

    // 3. Keep only the largest component (plus interior islands/holes)
    const roomPoly = this.tracePolygon(parent.walls);
    const newWalls = this._getLargestComponent(dedupedVis, roomPoly);

    // 4. Re-link doors to new walls; drop orphaned ones
    const survivingDoors = [];
    const orphanedDoors = [];
    for (const door of parent.doors) {
      const hingePoint = this.paramToPoint(door.wall, door.hingeParam);
      const endPoint = this.paramToPoint(door.wall, door.endParam);
      let bestWall = null, bestDist = Infinity;
      for (const w of newWalls) {
        const d = this.distanceToWall(hingePoint, w);
        if (d < bestDist) { bestDist = d; bestWall = w; }
      }
      if (bestWall && bestDist < 0.2) {
        door.wall = bestWall;
        door.hingeParam = this.getWallParam(hingePoint, bestWall);
        door.endParam = this.getWallParam(endPoint, bestWall);
        survivingDoors.push(door);
      } else {
        orphanedDoors.push(door);
      }
    }

    // 5. Apply the rebuild
    parent.walls = newWalls;
    parent.doors = survivingDoors;
    // Save precise cutout polygons for future subtract operations.
    // At this point cutout walls still have exact user-drawn coordinates (pre-clipping).
    parent._savedCutoutPolys = [];
    for (const c of parent.cutouts) {
      if (c.isCleanup) continue;
      const poly = this.tracePolygon(c.walls);
      if (poly && poly.length >= 3) {
        parent._savedCutoutPolys.push(poly.map(p => ({x: p.x, y: p.y})));
      }
    }
    parent.cutouts = [];
    parent.closed = this.isRoomClosed(parent);
    delete parent._geoCache;
    parent._zoneCache = null;

    // If rebuilt room is degenerate (near-zero area), remove it
    if (parent.closed && parent.walls.length >= 3) {
      const _rebuildPoly = this.tracePolygon(parent.walls);
      if (_rebuildPoly && _rebuildPoly.length >= 3) {
        let _rebuildArea = 0;
        for (let i = 0; i < _rebuildPoly.length; i++) {
          const j = (i + 1) % _rebuildPoly.length;
          _rebuildArea += _rebuildPoly[i].x * _rebuildPoly[j].y - _rebuildPoly[j].x * _rebuildPoly[i].y;
        }
        if (Math.abs(_rebuildArea) / 2 < 0.1) {
          const idx = this.rooms.indexOf(parent);
          if (idx >= 0) {
            this.rooms.splice(idx, 1);
            this.selectedWalls = new Set();
            this.selectedDoors = new Set();
          }
          return 0;
        }
      }
    }

    // 6. Fade out orphaned doors
    if (orphanedDoors.length > 0) this._fadeOutDoors(orphanedDoors);

    return removedCount;
  }

  handleErase(point) {
    // Erase measurement lines (M tool) — checked on ALL tabs before anything else
    const hitMeas = this._hitTestMeasurement(point);
    if (hitMeas) {
      this.saveUndoState();
      this._measurements = this._measurements.filter(m => m.id !== hitMeas.id);
      this._selectedMeasurements.delete(hitMeas.id);
      this.render();
      this.updateMeasurements();
      this.updateStatus('Measurement deleted.');
      return;
    }
    // Floor mode: delegate to overlay's floor eraser
    if (this._floorMode && window.roomDesignerOverlay) {
      window.roomDesignerOverlay._eraseFloorAtPoint(point.x, point.y);
      return;
    }
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — area labels erase first, for the same reason
    // they hit-test first: they are drawn over everything, so the pill under the
    // cursor is what the user means to remove.
    const _eraseOnCustomize = window.roomDesignerOverlay && window.roomDesignerOverlay.currentStep === 'finishing';
    const eraseLbl = _eraseOnCustomize ? this._hitTestAreaLabel(point) : null;
    if (eraseLbl) {
      this.saveUndoState();
      this.objects.splice(this.objects.indexOf(eraseLbl), 1);
      this.selectedObjects.delete(eraseLbl);
      this._updateActionButtons();
      this.render();
      this.updateMeasurements();
      this.updateStatus('Label deleted.');
      return;
    }
    // Zoom-adaptive threshold: at least 0.5 ft or 15/25 screen pixels in world units
    const minPx = this._isMobile ? 25 : 15;
    const t = Math.max(0.5, minPx / this.pixelsPerFoot);
    // Erase objects (rectangles, circles, turf)
    for (let i = this.objects.length - 1; i >= 0; i--) {
      const obj = this.objects[i];
      if (obj.type === 'equipment' && !this._equipmentMode) continue;
      if (obj.type === 'turf' && !this._floorMode) continue;
      if (obj.type === 'platform' && !this._floorMode) continue;
      let hit = false;
      if (obj.type === 'rectangle' || obj.type === 'equipment' || obj.type === 'turf' || obj.type === 'platform') {
        const halfW = obj.width / 2, halfD = obj.depth / 2;
        const rot = obj.rotation || 0;
        const local = this._rotatePoint(point.x, point.y, obj.x, obj.y, -rot);
        hit = local.x >= obj.x - halfW - t && local.x <= obj.x + halfW + t &&
              local.y >= obj.y - halfD - t && local.y <= obj.y + halfD + t;
      } else if (obj.type === 'circle') {
        const r = obj.diameter / 2;
        hit = this.getDistance(point, { x: obj.x, y: obj.y }) < r + t;
      }
      if (hit) {
        this.saveUndoState();
        this.objects.splice(i, 1);
        this.render();
        this.updateStatus('Object deleted.');
        return;
      }
    }

    for (let i = this.rooms.length - 1; i >= 0; i--) {
      const room = this.rooms[i];
      for (let j = room.doors.length - 1; j >= 0; j--) {
        const door = room.doors[j];
        const doorMidParam = (door.hingeParam + door.endParam) / 2;
        const doorMidPoint = this.paramToPoint(door.wall, doorMidParam);
        if (this.getDistance(point, doorMidPoint) < Math.max(1, t)) { this.saveUndoState(); room.doors.splice(j, 1); this._reconcileCurveOpenings(room); this.render(); this.updateMeasurements(); this.updateStatus('Door deleted.'); return; }
      }
    }
    // Rooms with cutouts: hit-test against visible walls, flatten on hit, delete single wall
    for (let i = this.rooms.length - 1; i >= 0; i--) {
      const room = this.rooms[i];
      if (!room.cutouts || room.cutouts.length === 0) continue;
      const visibleBoundary = this.getVisibleRoomWalls(room);
      const interiorData = this.getInteriorCutoutWalls(room);
      const interiorWalls = interiorData.map(d => d.wall);
      const allVisible = [...visibleBoundary, ...interiorWalls];
      let nearestIdx = -1, nearestDist = Infinity;
      for (let v = 0; v < allVisible.length; v++) {
        const d = this.distanceToWall(point, allVisible[v]);
        if (d < t && d < nearestDist) { nearestDist = d; nearestIdx = v; }
      }
      if (nearestIdx >= 0) {
        this.saveUndoState();
        this._flattenRoomCutouts(room);
        // Find the closest wall in the now-flattened room
        let bestJ = -1, bestD = Infinity;
        for (let j = 0; j < room.walls.length; j++) {
          const d = this.distanceToWall(point, room.walls[j]);
          if (d < t && d < bestD) { bestD = d; bestJ = j; }
        }
        if (bestJ >= 0) {
          room.walls.splice(bestJ, 1); room.closed = false;
          if (room.walls.length === 0) { this.rooms.splice(i, 1); this.updateStatus('Space removed.'); }
          else this.updateStatus('Wall deleted. Space is now open.');
        }
        this.render(); this.updateMeasurements(); return;
      }
    }
    // Rooms without cutouts: erase from stored walls directly
    for (let i = this.rooms.length - 1; i >= 0; i--) {
      const room = this.rooms[i];
      if (room.cutouts && room.cutouts.length > 0) continue;
      for (let j = room.walls.length - 1; j >= 0; j--) {
        if (this.distanceToWall(point, room.walls[j]) < t) {
          this.saveUndoState(); room.walls.splice(j, 1); room.closed = false;
          if (room.walls.length === 0) { this.rooms.splice(i, 1); this.updateStatus('Space removed.'); }
          else this.updateStatus('Wall deleted. Space is now open.');
          this.render(); this.updateMeasurements(); return;
        }
      }
    }
    if (this.currentRoom && this.currentRoom.walls.length > 0) {
      for (let j = this.currentRoom.walls.length - 1; j >= 0; j--) {
        if (this.distanceToWall(point, this.currentRoom.walls[j]) < t) {
          this.saveUndoState(); this.currentRoom.walls.splice(j, 1);
          if (this.currentRoom.walls.length === 0) { this.currentWall = null; this.hasPlacedFirstPoint = false; }
          this.render(); this.updateMeasurements(); this.updateStatus('Wall deleted.'); return;
        }
      }
    }
    // Erase wall segment group doors first
    for (const group of this.wallSegmentGroups) {
      for (let j = group.doors.length - 1; j >= 0; j--) {
        const door = group.doors[j];
        const doorMidParam = (door.hingeParam + door.endParam) / 2;
        const doorMidPoint = this.paramToPoint(door.wall, doorMidParam);
        if (this.getDistance(point, doorMidPoint) < Math.max(1, t)) {
          this.saveUndoState(); group.doors.splice(j, 1);
          this.render(); this.updateMeasurements(); this.updateStatus('Door deleted.'); return;
        }
      }
    }
    // Erase wall segment individual segments
    for (let gi = this.wallSegmentGroups.length - 1; gi >= 0; gi--) {
      const group = this.wallSegmentGroups[gi];
      for (let si = group.segments.length - 1; si >= 0; si--) {
        if (this.distanceToWall(point, group.segments[si]) < t) {
          this.saveUndoState();
          const deletedSeg = group.segments[si];
          // Remove doors referencing the deleted segment
          group.doors = group.doors.filter(d => d.wall !== deletedSeg);
          group.segments.splice(si, 1);
          if (group.segments.length === 0) {
            this.wallSegmentGroups.splice(gi, 1);
            this.updateStatus('Interior wall group removed.');
          } else {
            // Check if deletion splits the chain into disconnected components
            const components = this._getSegmentConnectedComponents(group.segments);
            if (components.length > 1) {
              // Split into multiple groups, redistribute doors
              this.wallSegmentGroups.splice(gi, 1);
              for (const comp of components) {
                const newGroup = { segments: comp, doors: [], type: group.type };
                // Assign doors to the group that contains their wall
                for (const door of group.doors) {
                  if (comp.includes(door.wall)) newGroup.doors.push(door);
                }
                this.wallSegmentGroups.push(newGroup);
              }
              this.updateStatus('Interior wall deleted. Chain split into parts.');
            } else {
              this.updateStatus('Interior wall deleted.');
            }
          }
          this.render(); this.updateMeasurements(); return;
        }
      }
    }

    // Erase background/trace images (lowest priority — behind all drawing elements)
    if (window.roomDesignerBackground) {
      const bg = window.roomDesignerBackground;
      const hitIdx = bg._hitTestAny(point.x, point.y);
      if (hitIdx >= 0) {
        this.saveUndoState();
        bg._saveCurrent();
        bg._loadImage(hitIdx);
        bg._bgSelected = true;
        bg._removeImage();
        if (this._updateFloatingDelete) this._updateFloatingDelete();
        this.updateStatus('Blueprint image deleted.');
        return;
      }
    }

  }

  _getSegmentConnectedComponents(segments) { return RoomDesignerTools.getSegmentConnectedComponents(segments); }
  _getSwingOptions(doorType) { return RoomDesignerTools.getSwingOptions(doorType || this.doorType); }
  handleObjectPlace(point) {
    // Prevent placing an identical object at the same position
    const isDuplicate = this.objects.some(obj => {
      if (Math.abs(obj.x - point.x) > 0.001 || Math.abs(obj.y - point.y) > 0.001) return false;
      if (this.objectType === 'rectangle' && obj.type === 'rectangle') {
        return Math.abs(obj.width - this.rectWidth / 12) < 0.001 && Math.abs(obj.depth - this.rectDepth / 12) < 0.001;
      }
      if (this.objectType === 'circle' && obj.type === 'circle') {
        return Math.abs(obj.diameter - this.circleDiameter / 12) < 0.001;
      }
      return false;
    });
    if (isDuplicate) {
      this.updateStatus('Object already exists at this position.', { error: true });
      if (navigator.vibrate) navigator.vibrate([20, 30, 20]);
      return;
    }
    this.saveUndoState();
    // Tag the undo entry so undo can restore the positioning preview
    if (this._isMobileToolbar && this.mouseWorldRaw) {
      this.undoStack[this.undoStack.length - 1]._placementRestore = {
        type: 'objectPreview', objectType: this.objectType,
        mouseWorldRaw: { ...this.mouseWorldRaw }, mouseWorld: this.mouseWorld ? { ...this.mouseWorld } : null
      };
    }
    if (this.objectType === 'rectangle') {
      const w = this.rectWidth / 12;  // convert inches to feet
      const d = this.rectDepth / 12;
      this.objects.push({
        type: 'rectangle',
        x: point.x,
        y: point.y,
        width: w,
        depth: d,
        rotation: 0
      });
      this.updateStatus(`Column placed (${this.rectWidth}" × ${this.rectDepth}").`);
    } else if (this.objectType === 'circle') {
      const diam = this.circleDiameter / 12;
      this.objects.push({
        type: 'circle',
        x: point.x,
        y: point.y,
        diameter: diam
      });
      this.updateStatus(`Pillar placed (${this.circleDiameter}" diameter).`);
    }
    if (navigator.vibrate) navigator.vibrate(50);
    this.render();
    // On mobile, enter idle/pan state after placing (don't allow tap-to-place-another)
    if (this._isMobileToolbar) {
      this._lastPlacedObject = {
        objectType: this.objectType,
        rectWidth: this.rectWidth,
        rectDepth: this.rectDepth,
        circleDiameter: this.circleDiameter,
        x: point.x,
        y: point.y
      };
      this._enterDoorIdleState();
      this._showRepeatButton = true;
      this._updateFloatingRepeat();
    }
  }

  /* ── EQUIPMENT IMAGE LOADING ── */

  // Equipment methods installed by RoomDesignerEquipment module (Pattern B)
  // Stubs for reference — actual implementations in custom-room-designer-equipment.js
  _loadEquipmentImages() { /* installed by RoomDesignerEquipment */ }
  _loadTextureOverlays() { /* installed by RoomDesignerEquipment */ }
  _buildEquipmentNormal(entry) { /* installed by RoomDesignerEquipment */ }
  _ensureEquipmentCanvas(entry, which) { /* installed by RoomDesignerEquipment */ }
  _rebuildEquipmentTheme() { /* installed by RoomDesignerEquipment */ }
  placeEquipment(equipmentId, x, y) { /* installed by RoomDesignerEquipment */ }

  _clearPendingEquipment() {
    if (!this._pendingEquipmentId) return;
    this._pendingEquipmentId = null;
    this._pendingEquipmentPos = null;
    this._pendingEquipmentRotation = 0;
    this._lastEquipmentRotation = null;
    this._lastPlacedEquipPos = null;
    const sel = document.querySelector('.rd-equipment-swatch.selected');
    if (sel) sel.classList.remove('selected');
    this._equipmentGhostBlocked = false;
    this.canvas.style.cursor = 'crosshair';
    this.updateStatus('Click an equipment item in the panel to select it.');
    this.render();
  }

  /* CUSTOM_PLANNER_BY_CLAUDE — Equipment wall collision detection */
  _equipmentOverlapsWall(cx, cy, wFt, dFt, rot, ghostInfo) {
    // Compute 4 corners of rotated equipment bounding rect (tight dimensions)
    const hw = wFt / 2, hd = dFt / 2;
    const cosR = Math.cos(rot), sinR = Math.sin(rot);
    const corners = [
      { x: cx + (-hw) * cosR - (-hd) * sinR, y: cy + (-hw) * sinR + (-hd) * cosR },
      { x: cx + ( hw) * cosR - (-hd) * sinR, y: cy + ( hw) * sinR + (-hd) * cosR },
      { x: cx + ( hw) * cosR - ( hd) * sinR, y: cy + ( hw) * sinR + ( hd) * cosR },
      { x: cx + (-hw) * cosR - ( hd) * sinR, y: cy + (-hw) * sinR + ( hd) * cosR }
    ];
    const diagHalf = Math.sqrt(hw * hw + hd * hd);
    // Collect all wall segments: { start, end, ht (halfThick) }
    // Use full visual thickness for broad-phase rect check, but only sample
    // the wall centerline for pixel refinement — lets equipment touch/overlap
    // the visible wall face, only blocking when it crosses through the center.
    const walls = [];
    const roomHT = this.wallThickness / 2;
    for (let i = 0; i < this.rooms.length; i++) {
      const r = this.rooms[i];
      if (!r.closed) continue;
      for (const w of r.walls) walls.push({ start: w.start, end: w.end, ht: roomHT, isRoom: true });
    }
    if (this.currentRoom && this.currentRoom.closed) {
      for (const w of this.currentRoom.walls) walls.push({ start: w.start, end: w.end, ht: roomHT, isRoom: true });
    }
    // Include dividing walls (type 'wall') but skip dividing lines (type 'line')
    const segHT = this.wallSegmentThickness / 2;
    for (const g of this.wallSegmentGroups) {
      if ((g.type || 'wall') === 'line') continue;
      for (const s of g.segments) walls.push({ start: s.start, end: s.end, ht: segHT, isRoom: false });
    }
    if (this.currentWallSegmentGroup && (this._wallSegmentType !== 'line')) {
      for (const s of this.currentWallSegmentGroup.segments) walls.push({ start: s.start, end: s.end, ht: segHT, isRoom: false });
    }
    if (walls.length === 0) return false;
    // ── Pixel-level refinement setup ──
    // Cache silhouette ImageData on ghostInfo so getImageData() runs once per equipment type
    let silData = null, silW = 0, silH = 0, fullWFt = 0, fullDFt = 0;
    const cosNR = Math.cos(-rot), sinNR = Math.sin(-rot);
    if (ghostInfo) {
      const sil = this._ensureEquipmentCanvas(ghostInfo, 'sil');
      if (sil && sil.width > 0 && sil.height > 0) {
        silW = sil.width; silH = sil.height;
        fullWFt = ghostInfo.widthIn / 12;
        fullDFt = ghostInfo.depthIn / 12;
        if (!ghostInfo._silPixelData ||
            ghostInfo._silPixelData.width !== silW ||
            ghostInfo._silPixelData.height !== silH) {
          ghostInfo._silPixelData = sil.getContext('2d').getImageData(0, 0, silW, silH);
        }
        silData = ghostInfo._silPixelData.data;
      }
    }
    // Check if world point hits non-transparent silhouette pixel
    const _pixelHit = (wx, wy) => {
      const dx2 = wx - cx, dy2 = wy - cy;
      const lx = dx2 * cosNR - dy2 * sinNR;
      const ly = dx2 * sinNR + dy2 * cosNR;
      const px = Math.round((lx + fullWFt / 2) / fullWFt * (silW - 1));
      const py = Math.round((ly + fullDFt / 2) / fullDFt * (silH - 1));
      if (px < 0 || px >= silW || py < 0 || py >= silH) return false;
      return silData[(py * silW + px) * 4 + 3] > 10;
    };
    // Helper: point inside convex quad (same-side-of-all-edges test)
    const _inQuad = (p, q) => {
      let pos = 0, neg = 0;
      for (let i = 0; i < 4; i++) {
        const a = q[i], b = q[(i + 1) % 4];
        const cross = (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);
        if (cross > 0) pos++; else if (cross < 0) neg++;
        if (pos > 0 && neg > 0) return false;
      }
      return true;
    };
    // Helper: segment-segment intersection (inclusive endpoints)
    const _segsX = (a1, a2, b1, b2) => {
      const d1x = a2.x - a1.x, d1y = a2.y - a1.y;
      const d2x = b2.x - b1.x, d2y = b2.y - b1.y;
      const denom = d1x * d2y - d1y * d2x;
      if (Math.abs(denom) < 1e-10) return false;
      const t = ((b1.x - a1.x) * d2y - (b1.y - a1.y) * d2x) / denom;
      const u = ((b1.x - a1.x) * d1y - (b1.y - a1.y) * d1x) / denom;
      return t >= 0 && t <= 1 && u >= 0 && u <= 1;
    };
    for (const wall of walls) {
      const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
      const len = Math.sqrt(wdx * wdx + wdy * wdy);
      if (len < 0.01) continue;
      // Broad phase: skip if equipment center is far from wall midpoint
      const wmx = (wall.start.x + wall.end.x) / 2, wmy = (wall.start.y + wall.end.y) / 2;
      const dist = Math.sqrt((cx - wmx) * (cx - wmx) + (cy - wmy) * (cy - wmy));
      if (dist > diagHalf + len / 2 + wall.ht + 0.5) continue;
      // Build wall thickness quad (offset ±halfThick perpendicular to wall direction)
      const nx = -wdy / len * wall.ht, ny = wdx / len * wall.ht;
      const wq = [
        { x: wall.start.x + nx, y: wall.start.y + ny },
        { x: wall.end.x + nx, y: wall.end.y + ny },
        { x: wall.end.x - nx, y: wall.end.y - ny },
        { x: wall.start.x - nx, y: wall.start.y - ny }
      ];
      // Rect-based overlap check (broad phase — catches most non-collisions fast)
      let rectOverlaps = false;
      outer1: for (let i = 0; i < 4; i++) {
        const eA = corners[i], eB = corners[(i + 1) % 4];
        for (let j = 0; j < 4; j++) {
          if (_segsX(eA, eB, wq[j], wq[(j + 1) % 4])) { rectOverlaps = true; break outer1; }
        }
      }
      if (!rectOverlaps) {
        for (const c of corners) { if (_inQuad(c, wq)) { rectOverlaps = true; break; } }
      }
      if (!rectOverlaps) {
        for (const c of wq) { if (_inQuad(c, corners)) { rectOverlaps = true; break; } }
      }
      if (!rectOverlaps) continue;
      // No silhouette pixel data (turf) → collision depends on wall type:
      // - Room (perimeter) walls: path IS the inner edge, wall extends outward.
      //   Turf should butt up against the path/centerline — only check centerline.
      // - Dividing walls (wall segments): centered on path, check both face edges.
      // Shrink turf rect by small epsilon so flush contact doesn't trigger
      if (!silData) {
        const eps = 0.02; // ~1/4 inch tolerance
        const shrinkX = hw > eps ? eps : 0, shrinkY = hd > eps ? eps : 0;
        const sCorners = [
          { x: cx + (-(hw - shrinkX)) * cosR - (-(hd - shrinkY)) * sinR, y: cy + (-(hw - shrinkX)) * sinR + (-(hd - shrinkY)) * cosR },
          { x: cx + ( (hw - shrinkX)) * cosR - (-(hd - shrinkY)) * sinR, y: cy + ( (hw - shrinkX)) * sinR + (-(hd - shrinkY)) * cosR },
          { x: cx + ( (hw - shrinkX)) * cosR - ( (hd - shrinkY)) * sinR, y: cy + ( (hw - shrinkX)) * sinR + ( (hd - shrinkY)) * cosR },
          { x: cx + (-(hw - shrinkX)) * cosR - ( (hd - shrinkY)) * sinR, y: cy + (-(hw - shrinkX)) * sinR + ( (hd - shrinkY)) * cosR }
        ];
        let faceCross = false;
        if (wall.isRoom) {
          // Room wall: check only the centerline (path = inner edge).
          // Turf can sit flush against the inner edge but not cross through it.
          for (let i = 0; i < 4; i++) {
            if (_segsX(sCorners[i], sCorners[(i + 1) % 4], wall.start, wall.end)) {
              faceCross = true; break;
            }
          }
          // Also check if centerline sample points fall inside shrunk turf rect
          if (!faceCross) {
            const fStep = 1 / 6;
            const fSteps = Math.max(1, Math.ceil(len / fStep));
            for (let s = 0; s <= fSteps; s++) {
              const t = s / fSteps;
              if (_inQuad({ x: wall.start.x + wdx * t, y: wall.start.y + wdy * t }, sCorners)) {
                faceCross = true; break;
              }
            }
          }
        } else {
          // Dividing wall: centered on path, check both face edges
          const faceEdges = [
            [wq[0], wq[1]], // plus-side face
            [wq[3], wq[2]]  // minus-side face
          ];
          for (const [fs, fe] of faceEdges) {
            // Edge-edge intersection
            for (let i = 0; i < 4; i++) {
              if (_segsX(sCorners[i], sCorners[(i + 1) % 4], fs, fe)) {
                faceCross = true; break;
              }
            }
            if (faceCross) break;
            // Sample points along face edge — check if any fall inside shrunk turf rect
            const fdx = fe.x - fs.x, fdy = fe.y - fs.y;
            const fLen = Math.sqrt(fdx * fdx + fdy * fdy);
            const fStep = 1 / 6;
            const fSteps = Math.max(1, Math.ceil(fLen / fStep));
            for (let s = 0; s <= fSteps; s++) {
              const t = s / fSteps;
              if (_inQuad({ x: fs.x + fdx * t, y: fs.y + fdy * t }, sCorners)) {
                faceCross = true; break;
              }
            }
            if (faceCross) break;
          }
          // Also check if any shrunk turf corner falls inside the wall quad
          if (!faceCross) {
            for (const c of sCorners) {
              if (_inQuad(c, wq)) { faceCross = true; break; }
            }
          }
        }
        if (faceCross) return true;
        continue;
      }
      // ── Pixel refinement: sample points along wall centerline only ──
      // Only the centerline is checked so equipment can touch/overlap the
      // visible wall face — blocked only when crossing through the center.
      const step = 1 / 6; // sample every 2 inches (1/6 ft)
      const nSteps = Math.max(1, Math.ceil(len / step));
      for (let s = 0; s <= nSteps; s++) {
        const t = s / nSteps;
        const wx = wall.start.x + wdx * t, wy = wall.start.y + wdy * t;
        // Quick skip: if sample is far from equipment center, no need to pixel-test
        const sd = (wx - cx) * (wx - cx) + (wy - cy) * (wy - cy);
        if (sd > (diagHalf + wall.ht + 0.1) * (diagHalf + wall.ht + 0.1)) continue;
        if (_pixelHit(wx, wy)) return true;
      }
    }
    // ── Check collision with columns (rectangles) and pillars (circles) ──
    for (const obj of this.objects) {
      if (obj.type !== 'rectangle' && obj.type !== 'circle') continue;
      if (obj.type === 'rectangle') {
        const oHW = obj.width / 2, oHD = obj.depth / 2;
        const oRot = obj.rotation || 0;
        const cosO = Math.cos(oRot), sinO = Math.sin(oRot);
        const oCorners = [
          { x: obj.x + (-oHW) * cosO - (-oHD) * sinO, y: obj.y + (-oHW) * sinO + (-oHD) * cosO },
          { x: obj.x + ( oHW) * cosO - (-oHD) * sinO, y: obj.y + ( oHW) * sinO + (-oHD) * cosO },
          { x: obj.x + ( oHW) * cosO - ( oHD) * sinO, y: obj.y + ( oHW) * sinO + ( oHD) * cosO },
          { x: obj.x + (-oHW) * cosO - ( oHD) * sinO, y: obj.y + (-oHW) * sinO + ( oHD) * cosO }
        ];
        // Broad phase distance check
        const oDiag = Math.sqrt(oHW * oHW + oHD * oHD);
        const dx = cx - obj.x, dy = cy - obj.y;
        if (Math.sqrt(dx * dx + dy * dy) > diagHalf + oDiag + 0.5) continue;
        // Edge-edge intersection
        let overlap = false;
        outer2: for (let i = 0; i < 4; i++) {
          for (let j = 0; j < 4; j++) {
            if (_segsX(corners[i], corners[(i + 1) % 4], oCorners[j], oCorners[(j + 1) % 4])) { overlap = true; break outer2; }
          }
        }
        if (!overlap) { for (const c of corners) { if (_inQuad(c, oCorners)) { overlap = true; break; } } }
        if (!overlap) { for (const c of oCorners) { if (_inQuad(c, corners)) { overlap = true; break; } } }
        if (!overlap) continue;
        if (!silData) return true;
        // Pixel refinement: sample grid inside column rect
        const stepFt = 1 / 6;
        const nW = Math.max(1, Math.ceil(obj.width / stepFt));
        const nD = Math.max(1, Math.ceil(obj.depth / stepFt));
        for (let si = 0; si <= nW; si++) {
          for (let sj = 0; sj <= nD; sj++) {
            const lx = -oHW + si * obj.width / nW, ly = -oHD + sj * obj.depth / nD;
            const wx = obj.x + lx * cosO - ly * sinO;
            const wy = obj.y + lx * sinO + ly * cosO;
            if (_pixelHit(wx, wy)) return true;
          }
        }
      } else if (obj.type === 'circle') {
        const r = (obj.diameter || 1) / 2;
        const dx = cx - obj.x, dy = cy - obj.y;
        if (Math.sqrt(dx * dx + dy * dy) > diagHalf + r + 0.5) continue;
        // Check if any equipment corner is inside the circle
        let overlap = false;
        for (const c of corners) {
          const cdx = c.x - obj.x, cdy = c.y - obj.y;
          if (cdx * cdx + cdy * cdy <= r * r) { overlap = true; break; }
        }
        // Check if circle center is inside equipment rect
        if (!overlap && _inQuad({ x: obj.x, y: obj.y }, corners)) overlap = true;
        // Check if circle intersects any equipment edge
        if (!overlap) {
          for (let i = 0; i < 4 && !overlap; i++) {
            const a = corners[i], b = corners[(i + 1) % 4];
            const ex = b.x - a.x, ey = b.y - a.y;
            const fx = a.x - obj.x, fy = a.y - obj.y;
            const eLen2 = ex * ex + ey * ey;
            const t = Math.max(0, Math.min(1, -(fx * ex + fy * ey) / eLen2));
            const closestX = a.x + t * ex - obj.x, closestY = a.y + t * ey - obj.y;
            if (closestX * closestX + closestY * closestY <= r * r) overlap = true;
          }
        }
        if (!overlap) continue;
        if (!silData) return true;
        // Pixel refinement: sample grid inside circle
        const stepFt = 1 / 6;
        const nR = Math.max(1, Math.ceil(r / stepFt));
        for (let si = -nR; si <= nR; si++) {
          for (let sj = -nR; sj <= nR; sj++) {
            const sx = si * stepFt, sy = sj * stepFt;
            if (sx * sx + sy * sy > r * r) continue;
            if (_pixelHit(obj.x + sx, obj.y + sy)) return true;
          }
        }
      }
    }
    return false;
  }
  /* CUSTOM_PLANNER_BY_CLAUDE — Turf-on-turf overlap detection (OBB SAT) */
  _turfOverlapsTurf(cx, cy, wFt, dFt, rot, excludeObj) {
    const hw = wFt / 2, hd = dFt / 2;
    const cosR = Math.cos(rot), sinR = Math.sin(rot);
    // Shrink by epsilon so flush edges don't trigger
    const eps = 0.02;
    const shw = hw > eps ? hw - eps : hw, shd = hd > eps ? hd - eps : hd;
    const cornersA = [
      { x: cx + (-shw)*cosR - (-shd)*sinR, y: cy + (-shw)*sinR + (-shd)*cosR },
      { x: cx + ( shw)*cosR - (-shd)*sinR, y: cy + ( shw)*sinR + (-shd)*cosR },
      { x: cx + ( shw)*cosR - ( shd)*sinR, y: cy + ( shw)*sinR + ( shd)*cosR },
      { x: cx + (-shw)*cosR - ( shd)*sinR, y: cy + (-shw)*sinR + ( shd)*cosR }
    ];
    const diagA = Math.sqrt(hw * hw + hd * hd);
    // Helpers (same as _equipmentOverlapsWall)
    const _inQuad = (p, q) => {
      let pos = 0, neg = 0;
      for (let i = 0; i < 4; i++) {
        const a = q[i], b = q[(i + 1) % 4];
        const cross = (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);
        if (cross > 0) pos++; else if (cross < 0) neg++;
        if (pos > 0 && neg > 0) return false;
      }
      return true;
    };
    const _segsX = (a1, a2, b1, b2) => {
      const d1x = a2.x - a1.x, d1y = a2.y - a1.y;
      const d2x = b2.x - b1.x, d2y = b2.y - b1.y;
      const denom = d1x * d2y - d1y * d2x;
      if (Math.abs(denom) < 1e-10) return false;
      const t = ((b1.x - a1.x) * d2y - (b1.y - a1.y) * d2x) / denom;
      const u = ((b1.x - a1.x) * d1y - (b1.y - a1.y) * d1x) / denom;
      return t >= 0 && t <= 1 && u >= 0 && u <= 1;
    };
    for (const other of this.objects) {
      if (other.type !== 'turf' || other === excludeObj) continue;
      const oHW = other.width / 2, oHD = other.depth / 2;
      const oRot = other.rotation || 0;
      const cosO = Math.cos(oRot), sinO = Math.sin(oRot);
      const cornersB = [
        { x: other.x + (-oHW)*cosO - (-oHD)*sinO, y: other.y + (-oHW)*sinO + (-oHD)*cosO },
        { x: other.x + ( oHW)*cosO - (-oHD)*sinO, y: other.y + ( oHW)*sinO + (-oHD)*cosO },
        { x: other.x + ( oHW)*cosO - ( oHD)*sinO, y: other.y + ( oHW)*sinO + ( oHD)*cosO },
        { x: other.x + (-oHW)*cosO - ( oHD)*sinO, y: other.y + (-oHW)*sinO + ( oHD)*cosO }
      ];
      // Broad phase
      const diagB = Math.sqrt(oHW * oHW + oHD * oHD);
      const ddx = cx - other.x, ddy = cy - other.y;
      if (Math.sqrt(ddx * ddx + ddy * ddy) > diagA + diagB + 0.5) continue;
      // Edge-edge intersection
      let overlap = false;
      outer: for (let i = 0; i < 4; i++) {
        for (let j = 0; j < 4; j++) {
          if (_segsX(cornersA[i], cornersA[(i + 1) % 4], cornersB[j], cornersB[(j + 1) % 4])) { overlap = true; break outer; }
        }
      }
      if (!overlap) { for (const c of cornersA) { if (_inQuad(c, cornersB)) { overlap = true; break; } } }
      if (!overlap) { for (const c of cornersB) { if (_inQuad(c, cornersA)) { overlap = true; break; } } }
      if (overlap) return true;
    }
    return false;
  }
  /* END CUSTOM_PLANNER_BY_CLAUDE */

  /* ── Auto-slide equipment out of walls after rotation placement ── */
  _slideEquipmentInBounds(obj) {
    const info = this._equipmentImages && this._equipmentImages[obj.equipmentId];
    const tw = obj.tightWidth || obj.width;
    const td = obj.tightDepth || obj.depth;
    const rot = obj.rotation || 0;
    if (!this._equipmentOverlapsWall(obj.x, obj.y, tw, td, rot, info || null)) return true;
    // Collect wall segments
    const walls = [];
    const roomHT = this.wallThickness / 2;
    for (const r of this.rooms) {
      if (!r.closed) continue;
      for (const w of r.walls) walls.push(w);
    }
    if (this.currentRoom && this.currentRoom.closed) {
      for (const w of this.currentRoom.walls) walls.push(w);
    }
    for (const g of this.wallSegmentGroups) {
      if ((g.type || 'wall') === 'line') continue;
      for (const s of g.segments) walls.push(s);
    }
    if (walls.length === 0) return false;
    const inch = 1 / 12;
    // Iteratively push perpendicular to nearest wall, re-evaluating each step
    for (let iter = 0; iter < 48; iter++) {
      let nearWall = null, nearDist = Infinity;
      for (const w of walls) {
        const dx = w.end.x - w.start.x, dy = w.end.y - w.start.y;
        const len2 = dx * dx + dy * dy;
        if (len2 < 0.0001) continue;
        const t = Math.max(0, Math.min(1, ((obj.x - w.start.x) * dx + (obj.y - w.start.y) * dy) / len2));
        const px = w.start.x + t * dx, py = w.start.y + t * dy;
        const d = (obj.x - px) * (obj.x - px) + (obj.y - py) * (obj.y - py);
        if (d < nearDist) { nearDist = d; nearWall = w; }
      }
      if (!nearWall) break;
      const wdx = nearWall.end.x - nearWall.start.x;
      const wdy = nearWall.end.y - nearWall.start.y;
      const len = Math.sqrt(wdx * wdx + wdy * wdy);
      if (len < 0.01) break;
      const nx = -wdy / len, ny = wdx / len;
      const dot = (obj.x - nearWall.start.x) * nx + (obj.y - nearWall.start.y) * ny;
      obj.x += (dot >= 0 ? nx : -nx) * inch;
      obj.y += (dot >= 0 ? ny : -ny) * inch;
      if (!this._equipmentOverlapsWall(obj.x, obj.y, tw, td, rot, info || null)) return true;
    }
    return false;
  }
  /* END CUSTOM_PLANNER_BY_CLAUDE */

  /* ── SELECTION TOOL ── */

  _updateSelection() {
    if (!this.selectionStart || !this.selectionEnd) return;
    const minX = Math.min(this.selectionStart.x, this.selectionEnd.x); const maxX = Math.max(this.selectionStart.x, this.selectionEnd.x);
    const minY = Math.min(this.selectionStart.y, this.selectionEnd.y); const maxY = Math.max(this.selectionStart.y, this.selectionEnd.y);
    // Start with pre-existing selection if Shift was held, otherwise empty
    const pre = this._selectPreExisting;
    this.selectedWalls = pre ? new Set(pre.walls) : new Set();
    this.selectedDoors = pre ? new Set(pre.doors) : new Set();
    this.selectedObjects = pre ? new Set(pre.objects) : new Set();
    const pointInRect = (p) => p.x >= minX && p.x <= maxX && p.y >= minY && p.y <= maxY;
    // Draw Space only: walls, doors, columns, pillars (skip on floor/equipment tabs)
    if (!this._equipmentMode && !this._floorMode) {
      const wallInRect = (wall) => { const mx = (wall.start.x + wall.end.x) / 2; const my = (wall.start.y + wall.end.y) / 2; return pointInRect({ x: mx, y: my }); };
      [...this.rooms, this.currentRoom].forEach(room => {
        if (!room) return;
        room.walls.forEach(wall => { if (wallInRect(wall)) this.selectedWalls.add(wall); });
        if (room.cutouts) room.cutouts.forEach(cutout => { cutout.walls.forEach(wall => { if (wallInRect(wall)) this.selectedWalls.add(wall); }); });
        room.doors.forEach(door => { const midParam = (door.hingeParam + door.endParam) / 2; const midPoint = this.paramToPoint(door.wall, midParam); if (pointInRect(midPoint)) this.selectedDoors.add(door); });
      });
      // Wall segment groups
      for (const group of this.wallSegmentGroups) {
        group.segments.forEach(seg => { if (wallInRect(seg)) this.selectedWalls.add(seg); });
        group.doors.forEach(door => { const midParam = (door.hingeParam + door.endParam) / 2; const midPoint = this.paramToPoint(door.wall, midParam); if (pointInRect(midPoint)) this.selectedDoors.add(door); });
      }
    }
    for (const obj of this.objects) { if (obj.type === 'equipment' && !this._equipmentMode) continue; if (obj.type === 'turf' && !this._floorMode) continue; if (obj.type === 'platform' && !this._floorMode && !this._equipmentMode) continue; if ((obj.type === 'rectangle' || obj.type === 'circle') && (this._floorMode || this._equipmentMode)) continue; if (this._equipmentMode && obj.type !== 'equipment' && obj.type !== 'platform') continue; if (pointInRect({ x: obj.x, y: obj.y })) this.selectedObjects.add(obj); }
    // Measurements: select by label midpoint inside box
    const preMeasure = (pre && pre.measurements) ? new Set(pre.measurements) : new Set();
    this._selectedMeasurements = preMeasure;
    for (const m of this._measurements) {
      const lp = this._getMeasureLabelPos(m);
      if (pointInRect(lp)) this._selectedMeasurements.add(m.id);
    }
    this._expandSelectionToGroups();
  }

  _finalizeBoxSelection(corner1, corner2) {
    this.selectionStart = corner1;
    this.selectionEnd = corner2;
    this._updateSelection();
  }

  _pointInFlooredRoom(point) {
    for (const room of this.rooms) {
      if (!room.closed || !room.flooring) continue;
      const geo = this._getRoomGeoCache(room);
      if (geo.roomPolygon && this.pointInPolygon(point.x, point.y, geo.roomPolygon)) return true;
    }
    return false;
  }

  _hitTestSelect(point) {
    const minPx = this._isMobile ? 25 : 15;
    const t = Math.max(0.5, minPx / this.pixelsPerFoot);
    const wallMinPx = this._isMobile ? 18 : 8;
    const wallT = Math.max(0.25, wallMinPx / this.pixelsPerFoot);
    let best = null, bestDist = Infinity;
    // Equipment mode: only equipment objects are selectable (not on Customize tab)
    const _onCustomizeTab = window.roomDesignerOverlay && window.roomDesignerOverlay.currentStep === 'finishing';
    const equipOnly = this._equipmentMode && !_onCustomizeTab;
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — an area label is drawn last, over everything,
    // so a click that lands on the pill has to take it before anything underneath
    // gets a look. The distance-to-centre contest below can't express that: a label
    // sitting mid-turf is no nearer its own centre than the turf is to its own.
    //
    // Selectable on CUSTOMIZE only, following its tool. Labels stay visible on every
    // tab — that is the point of them — but a pill you can grab while laying out
    // equipment is a pill you drag by accident and never notice. Columns and pillars
    // are scoped to Draw Space the same way.
    if (_onCustomizeTab) {
      const lblHit = this._hitTestAreaLabel(point);
      if (lblHit) return { type: 'object', item: lblHit };
    }
    // Objects (rectangles, circles) — highest priority
    for (const obj of this.objects) {
      // Skip equipment when not on equipment tab (invisible, non-interactive)
      if (obj.type === 'equipment' && !this._equipmentMode) continue;
      // Skip turf when not on floor or customize tab
      if (obj.type === 'turf' && !this._floorMode && !(window.roomDesignerOverlay && window.roomDesignerOverlay.currentStep === 'finishing')) continue;
      if (obj.type === 'platform' && !this._floorMode && !this._equipmentMode && !(window.roomDesignerOverlay && window.roomDesignerOverlay.currentStep === 'finishing')) continue;
      // Skip columns/pillars on non-draw tabs (only selectable on Draw Space)
      if ((obj.type === 'rectangle' || obj.type === 'circle') && (this._floorMode || this._equipmentMode)) continue;
      // Skip non-equipment when on equipment tab (but allow platforms)
      if (obj.type !== 'equipment' && obj.type !== 'platform' && equipOnly) continue;
      let dist = Infinity;
      if (obj.type === 'rectangle' || obj.type === 'equipment' || obj.type === 'turf' || obj.type === 'platform') {
        const halfW = obj.width / 2, halfD = obj.depth / 2;
        const rot = obj.rotation || 0;
        const local = this._rotatePoint(point.x, point.y, obj.x, obj.y, -rot);
        // Equipment uses tight hit zone (4px padding); other objects use generous padding
        const pad = obj.type === 'equipment' ? Math.max(0, 4 / this.pixelsPerFoot) : t;
        if (local.x >= obj.x - halfW - pad && local.x <= obj.x + halfW + pad && local.y >= obj.y - halfD - pad && local.y <= obj.y + halfD + pad) {
          dist = this.getDistance(point, { x: obj.x, y: obj.y });
        }
        // CUSTOM_ROOMDESIGNER_BY_CLAUDE — a zone-filled piece is only where its ZONE
        // is. The box above is its marking frame, and on an L or a bowed slab the box
        // covers ground the turf doesn't, so clicking bare floor near a corner would
        // otherwise select the turf. Cheap because the box test has already run: this
        // only ever narrows a hit, never adds one.
        if (dist < Infinity && obj.type === 'turf' && obj.turfZone) {
          const z = this._turfZoneShape(obj);
          if (z && z.polygon && z.polygon.length >= 3) {
            const inside = this.pointInPolygon(point.x, point.y, z.polygon)
              && !(z.holes || []).some(h => h && h.length >= 3 && this.pointInPolygon(point.x, point.y, h));
            if (!inside) dist = Infinity;
          }
        }
      } else if (obj.type === 'circle') {
        const r = obj.diameter / 2;
        const d = this.getDistance(point, { x: obj.x, y: obj.y });
        if (d < r + t) dist = d;
      }
      if (dist < bestDist) { bestDist = dist; best = { type: 'object', item: obj }; }
    }
    // Equipment mode: skip doors and walls entirely
    if (equipOnly) return best;
    // Non-draw tabs: walls, doors, columns, pillars are not selectable
    if (this._floorMode || this._equipmentMode) return best;
    // Doors — check BEFORE walls so doors on walls are selectable
    // Use a generous hit zone along the wall segment the door occupies
    let bestDoor = null, bestDoorDist = Infinity;
    for (const room of [...this.rooms, this.currentRoom, ...this.wallSegmentGroups]) {
      if (!room || !room.doors) continue;
      for (const door of room.doors) {
        const hingePoint = this.paramToPoint(door.wall, door.hingeParam);
        const endPoint = this.paramToPoint(door.wall, door.endParam);
        // Distance from point to the door segment on the wall
        const doorSeg = { start: hingePoint, end: endPoint };
        let d = this.distanceToWall(point, doorSeg);
        // Also check swing arc area
        if (door.swing && door.type !== 'roller') {
          const wall = door.wall;
          const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
          const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
          if (wLen > 0) {
            const perpX = -wdy / wLen, perpY = wdx / wLen;
            const doorWidthFt = door.width / 12;
            const perpSign = door.swing.perpSign;
            const _arcHit = (pivot, closedPt, radius) => {
              const dist = this.getDistance(point, pivot);
              if (dist > radius + t * 0.25) return false;
              const clickA = Math.atan2(point.y - pivot.y, point.x - pivot.x);
              const closedA = Math.atan2(closedPt.y - pivot.y, closedPt.x - pivot.x);
              const openA = Math.atan2(perpY * perpSign, perpX * perpSign);
              let span = openA - closedA; while (span > Math.PI) span -= 2 * Math.PI; while (span < -Math.PI) span += 2 * Math.PI;
              let rel = clickA - closedA; while (rel > Math.PI) rel -= 2 * Math.PI; while (rel < -Math.PI) rel += 2 * Math.PI;
              const tol = 0.2;
              const lo = Math.min(0, span) - tol, hi = Math.max(0, span) + tol;
              return rel >= lo && rel <= hi;
            };
            if (door.type === 'double') {
              const midPt = { x: (hingePoint.x + endPoint.x) / 2, y: (hingePoint.y + endPoint.y) / 2 };
              const halfW = doorWidthFt / 2;
              for (const pivot of [hingePoint, endPoint]) {
                if (_arcHit(pivot, midPt, halfW)) { d = 0; }
              }
            } else {
              const hingeEnd = door.swing.hingeEnd || 'hinge';
              const pivotPt = hingeEnd === 'hinge' ? hingePoint : endPoint;
              const otherPt = hingeEnd === 'hinge' ? endPoint : hingePoint;
              if (_arcHit(pivotPt, otherPt, doorWidthFt)) { d = 0; }
            }
          }
        }
        const doorThreshold = t * 0.5;
        if (d < doorThreshold && d < bestDoorDist) { bestDoorDist = d; bestDoor = { type: 'door', item: door }; }
      }
    }
    // If a door was hit, it wins over walls (doors sit on walls, so prioritize them)
    if (bestDoor && (bestDoorDist < bestDist || !best)) {
      bestDist = bestDoorDist;
      best = bestDoor;
    }
    // Walls — only if no door was hit at this location
    if (!bestDoor) {
      for (const room of [...this.rooms, this.currentRoom]) {
        if (!room) continue;
        let walls;
        if (room.cutouts && room.cutouts.length > 0) {
          const vw = this.getVisibleRoomWalls(room);
          const icData = this.getInteriorCutoutWalls(room);
          walls = [...vw, ...icData.map(d => d.wall)];
        } else {
          walls = room.walls;
        }
        for (const wall of walls) {
          const d = this.distanceToWall(point, wall);
          if (d < wallT && d < bestDist) { bestDist = d; best = { type: 'wall', item: wall }; }
        }
      }
      // Wall segment groups — hit test segments as walls.
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — the in-progress chain is included because
      // RENDERING includes it: anything the user can see has to be clickable, or a
      // chain that never committed reads as a wall the tool simply refuses to
      // acknowledge. It costs nothing in normal drawing — this only runs with the
      // select tool active, by which point a chain has normally been committed.
      const _segGroups = this.currentWallSegmentGroup
        ? [...this.wallSegmentGroups, this.currentWallSegmentGroup]
        : this.wallSegmentGroups;
      for (const group of _segGroups) {
        for (const seg of group.segments) {
          const d = this.distanceToWall(point, seg);
          if (d < wallT && d < bestDist) { bestDist = d; best = { type: 'wall', item: seg }; }
        }
      }
    }
    return best;
  }

  _updateActionButtons() {
    const hasObjSel = this.selectedObjects.size > 0 && this.activeTool === 'select';
    // Send to front/back: enabled when any non-platform objects are selected
    const movableObjSel = hasObjSel && [...this.selectedObjects].some(o => o.type !== 'platform');
    if (this._sendToFrontBtn) this._sendToFrontBtn.disabled = !movableObjSel;
    if (this._sendToBackBtn) this._sendToBackBtn.disabled = !movableObjSel;
    // Group: enabled when 2+ equipment/platform selected and not all already in same group
    const equipSel = hasObjSel ? [...this.selectedObjects].filter(o => o.type === 'equipment' || o.type === 'platform') : [];
    let canGroup = equipSel.length >= 2;
    if (canGroup) {
      const gids = new Set(equipSel.map(o => o.groupId).filter(Boolean));
      if (gids.size === 1 && equipSel.every(o => o.groupId)) canGroup = false;
    }
    if (this._groupBtn) this._groupBtn.disabled = !canGroup;
    // Ungroup: enabled when any selected object has a groupId
    const canUngroup = hasObjSel && [...this.selectedObjects].some(o => o.groupId);
    if (this._ungroupBtn) this._ungroupBtn.disabled = !canUngroup;
  }

  _updateSelectionStatus() {
    this._updateJoystickVisibility();
    this._updateFloatingDelete();
    this._updateFloatingCancel();
    this._updateFloatingRepeat();
    this._updateActionButtons();
    const wc = this.selectedWalls.size, dc = this.selectedDoors.size, oc = this.selectedObjects.size;
    const total = wc + dc + oc;
    if (total === 0) { this.updateStatus('Click to select items. Shift+click to multi-select. Click to box-select.'); return; }
    const parts = [];
    if (wc > 0) parts.push(`${wc} wall${wc > 1 ? 's' : ''}`);
    if (dc > 0) parts.push(`${dc} door${dc > 1 ? 's' : ''}`);
    if (oc > 0) parts.push(`${oc} object${oc > 1 ? 's' : ''}`);
    const hints = [];
    if (dc === 1 && wc === 0 && oc === 0) {
      const door = this.selectedDoors.values().next().value;
      hints.push('Drag to slide along wall');
      if (door.type !== 'roller') hints.push('Click again to change swing direction');
      hints.push('Delete to remove');
    } else {
      if (wc > 0) hints.push('Use handles to move or reshape');
      if (dc > 0 && wc === 0) hints.push('Drag to slide');
      hints.push('Delete to remove');
    }
    this.updateStatus(`Selected ${parts.join(', ')}. ${hints.join('. ')}.`);
    // Mobile: reset handle mode on selection changes, evaluate auto-joystick
    if (this._isMobileToolbar) {
      if (this._selectRotateDialActive) this._exitRotateDial();
      this._selectHandleMode = null;
      this._selectHandleInfo = null;
      this._selectHandleCycleList = [];
      this._selectHandleCycleIndex = 0;
      this._evaluateSelectJoystickMode();
    }
  }

  /* Shared helper for keyboard Delete and desktop delete button.
     Handles the hasFloor + hasObjects case with equipment prompt before any state mutation. */
  _deleteWithEquipmentCheck(hasFloor, hasObjects) {
    if (hasFloor && hasObjects) {
      // Check for equipment in doomed rooms BEFORE any state mutation
      if (this.selectedWalls.size > 0) {
        const doomedEquip = this._findEquipmentInDoomedRooms();
        if (doomedEquip.length > 0) {
          const n = doomedEquip.length;
          const msg = n + ' piece' + (n === 1 ? '' : 's') + ' of equipment inside this room. Delete equipment too?';
          this._showConfirm(msg, 'Delete All', () => {
            for (const eq of doomedEquip) this.selectedObjects.add(eq);
            this.saveUndoState();
            window.roomDesignerOverlay.deleteFloorSelection({skipUndo: true});
            this.deleteSelected({skipUndo: true, _equipmentDecision: 'yes'});
          }, {
            altText: 'Keep Equipment',
            onAlt: () => {
              this.saveUndoState();
              window.roomDesignerOverlay.deleteFloorSelection({skipUndo: true});
              this.deleteSelected({skipUndo: true, _equipmentDecision: 'no'});
            }
          });
          return;
        }
      }
      this.saveUndoState();
      window.roomDesignerOverlay.deleteFloorSelection({skipUndo: true});
      this.deleteSelected({skipUndo: true});
    } else if (hasFloor) {
      window.roomDesignerOverlay.deleteFloorSelection();
    } else {
      this.deleteSelected();
    }
  }

  /* Find equipment objects inside rooms that would be fully deleted by current wall selection.
     Returns array of equipment objects (deduplicated, excludes already-selected objects). */
  _findEquipmentInDoomedRooms() {
    const doomedEquip = [];
    const seen = new Set();
    for (let i = 0; i < this.rooms.length; i++) {
      const room = this.rooms[i];
      if (!room.closed) continue;
      // Determine if this room would be fully deleted
      let doomed = false;
      if (room.cutouts && room.cutouts.length > 0) {
        // Check if any boundary walls are selected
        const vw = this.getVisibleRoomWalls(room);
        let boundaryHits = 0;
        for (const sw of this.selectedWalls) {
          const mx = (sw.start.x + sw.end.x) / 2, my = (sw.start.y + sw.end.y) / 2;
          for (const v of vw) {
            if (this.distanceToWall({ x: mx, y: my }, v) < 0.1) { boundaryHits++; break; }
          }
        }
        if (boundaryHits > 0) {
          // Simulate: flatten cutouts, count remaining walls after removing hits
          const allWalls = [...room.walls];
          for (const c of room.cutouts) { for (const w of c.walls) allWalls.push(w); }
          let remaining = 0;
          for (const w of allWalls) {
            const mx = (w.start.x + w.end.x) / 2, my = (w.start.y + w.end.y) / 2;
            let hit = false;
            for (const sw of this.selectedWalls) {
              if (this.distanceToWall({ x: mx, y: my }, sw) < 0.1) { hit = true; break; }
            }
            if (!hit) remaining++;
          }
          if (remaining === 0) doomed = true;
        }
      } else {
        // No cutouts: check if all walls are selected
        let allSelected = true;
        for (const w of room.walls) {
          if (!this.selectedWalls.has(w)) { allSelected = false; break; }
        }
        if (allSelected) doomed = true;
      }
      if (!doomed) continue;
      // Get room polygon and find equipment inside
      const geo = this._getRoomGeoCache(room);
      const poly = geo.roomPolygon;
      if (!poly || poly.length < 3) continue;
      for (const obj of this.objects) {
        if (obj.type !== 'equipment') continue;
        if (this.selectedObjects.has(obj)) continue;
        if (seen.has(obj)) continue;
        if (this.pointInPolygon(obj.x, obj.y, poly)) {
          doomedEquip.push(obj);
          seen.add(obj);
        }
      }
    }
    return doomedEquip;
  }

  deleteSelected(opts) {
    this._deactivateSelectHandle();
    if (this.selectedWalls.size === 0 && this.selectedDoors.size === 0 && this.selectedObjects.size === 0) return;
    // Logo-focused delete: remove selected logos from turf, not the turf itself
    if (this._turfLogoFocusedIdx != null && this.selectedObjects.size === 1) {
      const obj = [...this.selectedObjects][0];
      if (obj.type === 'turf' && obj.turfLogos && obj.turfLogos.length > 0) {
        if (!opts || !opts.skipUndo) this.saveUndoState();
        // Remove all selected logos (reverse order to preserve indices)
        const toRemove = [...this._turfLogoSelectedIdxs].sort((a, b) => b - a);
        if (toRemove.length === 0) toRemove.push(this._turfLogoFocusedIdx);
        for (const idx of toRemove) {
          if (idx >= 0 && idx < obj.turfLogos.length) obj.turfLogos.splice(idx, 1);
        }
        if (obj.turfLogos.length === 0) obj.turfLogos = null;
        this._turfLogoFocusedIdx = null;
        this._turfLogoSelectedIdxs.clear();
        // Force immediate panel thumbnail sync
        if (window.roomDesignerOverlay) {
          const body = document.getElementById('rdSidePanelBody');
          if (body) {
            window.roomDesignerOverlay._syncTurfLogoPanelPreview(body, obj);
            window.roomDesignerOverlay._lastTurfSelFp = null;
          }
        }
        this.render(); return;
      }
    }
    // Prompt before deleting rooms that contain equipment
    if ((!opts || !opts._equipmentDecision) && this.selectedWalls.size > 0) {
      const doomedEquip = this._findEquipmentInDoomedRooms();
      if (doomedEquip.length > 0) {
        const n = doomedEquip.length;
        const msg = n + ' piece' + (n === 1 ? '' : 's') + ' of equipment inside this room. Delete equipment too?';
        this._showConfirm(msg, 'Delete All', () => {
          for (const eq of doomedEquip) this.selectedObjects.add(eq);
          this.deleteSelected(Object.assign({}, opts || {}, { _equipmentDecision: 'yes' }));
        }, {
          altText: 'Keep Equipment',
          onAlt: () => {
            this.deleteSelected(Object.assign({}, opts || {}, { _equipmentDecision: 'no' }));
          }
        });
        return;
      }
    }
    if (!opts || !opts.skipUndo) this.saveUndoState();
    // Delete selected objects
    if (this.selectedObjects.size > 0) {
      for (let i = this.objects.length - 1; i >= 0; i--) { if (this.selectedObjects.has(this.objects[i])) this.objects.splice(i, 1); }
    }
    // Delete selected doors (rooms + wall segment groups)
    [...this.rooms, this.currentRoom].forEach(room => {
      if (!room) return;
      for (let j = room.doors.length - 1; j >= 0; j--) { if (this.selectedDoors.has(room.doors[j])) room.doors.splice(j, 1); }
      this._reconcileCurveOpenings(room); // CUSTOM_ROOMDESIGNER_BY_CLAUDE — close curve openings
    });
    for (const group of this.wallSegmentGroups) {
      for (let j = group.doors.length - 1; j >= 0; j--) { if (this.selectedDoors.has(group.doors[j])) group.doors.splice(j, 1); }
      this._reconcileCurveOpenings(group); // CUSTOM_ROOMDESIGNER_BY_CLAUDE
    }
    // Delete selected wall segments
    for (let gi = this.wallSegmentGroups.length - 1; gi >= 0; gi--) {
      const group = this.wallSegmentGroups[gi];
      let changed = false;
      for (let si = group.segments.length - 1; si >= 0; si--) {
        if (this.selectedWalls.has(group.segments[si])) {
          const deletedSeg = group.segments[si];
          group.doors = group.doors.filter(d => d.wall !== deletedSeg);
          group.segments.splice(si, 1);
          changed = true;
        }
      }
      if (changed) {
        if (group.segments.length === 0) {
          this.wallSegmentGroups.splice(gi, 1);
        } else {
          const components = this._getSegmentConnectedComponents(group.segments);
          if (components.length > 1) {
            const oldDoors = group.doors;
            this.wallSegmentGroups.splice(gi, 1);
            for (const comp of components) {
              const newGroup = { segments: comp, doors: [], type: group.type };
              for (const door of oldDoors) {
                if (comp.includes(door.wall)) newGroup.doors.push(door);
              }
              this.wallSegmentGroups.push(newGroup);
            }
          }
        }
      }
    }
    // Delete selected room walls
    for (let i = this.rooms.length - 1; i >= 0; i--) {
      const room = this.rooms[i];
      if (room.cutouts && room.cutouts.length > 0) {
        // Classify each selected wall as cutout-interior vs room-boundary
        const icData = this.getInteriorCutoutWalls(room);
        const vw = this.getVisibleRoomWalls(room);
        const cutoutsToRemove = new Set();   // indices of cutouts whose walls were selected
        const boundaryHits = [];             // selected walls that hit the room boundary
        for (const sw of this.selectedWalls) {
          const mx = (sw.start.x + sw.end.x) / 2, my = (sw.start.y + sw.end.y) / 2;
          // Check if this wall matches a cutout interior wall
          let matchedCutout = false;
          for (const ic of icData) {
            if (this.distanceToWall({ x: mx, y: my }, ic.wall) < 0.1) {
              // Find which cutout this belongs to
              for (let ci = 0; ci < room.cutouts.length; ci++) {
                if (room.cutouts[ci].isCleanup) continue;
                const cp = this.tracePolygon(room.cutouts[ci].walls);
                if (cp && this.pointInPolygon(ic.cutoutCenter.x, ic.cutoutCenter.y, cp)) {
                  cutoutsToRemove.add(ci);
                  matchedCutout = true;
                  break;
                }
              }
              if (matchedCutout) break;
            }
          }
          if (!matchedCutout) {
            // Check if it hits room boundary
            for (const v of vw) {
              if (this.distanceToWall({ x: mx, y: my }, v) < 0.1) { boundaryHits.push(sw); break; }
            }
          }
        }
        // Remove matched cutouts (fill holes back in)
        if (cutoutsToRemove.size > 0) {
          const sorted = [...cutoutsToRemove].sort((a, b) => b - a);
          for (const ci of sorted) {
            room.cutouts.splice(ci, 1);
          }
          // Also remove any cleanup cutouts that overlap the removed cutout areas
          for (let ki = room.cutouts.length - 1; ki >= 0; ki--) {
            if (!room.cutouts[ki].isCleanup) continue;
            const kp = this.tracePolygon(room.cutouts[ki].walls);
            if (!kp || kp.length < 3) { room.cutouts.splice(ki, 1); continue; }
            // Check if this cleanup cutout's centroid is inside the room boundary
            let cx2 = 0, cy2 = 0;
            kp.forEach(p => { cx2 += p.x; cy2 += p.y; }); cx2 /= kp.length; cy2 /= kp.length;
            const rp = this.tracePolygon(room.walls);
            if (rp && this.pointInPolygon(cx2, cy2, rp)) room.cutouts.splice(ki, 1);
          }
          delete room._geoCache;
          room._zoneCache = null;
        }
        // Handle boundary wall deletions (flatten remaining cutouts first)
        if (boundaryHits.length > 0) {
          this._flattenRoomCutouts(room);
          for (const sw of boundaryHits) {
            const mx = (sw.start.x + sw.end.x) / 2, my = (sw.start.y + sw.end.y) / 2;
            let bestJ = -1, bestD = Infinity;
            for (let j = 0; j < room.walls.length; j++) {
              const d = this.distanceToWall({ x: mx, y: my }, room.walls[j]);
              if (d < 0.1 && d < bestD) { bestD = d; bestJ = j; }
            }
            if (bestJ >= 0) { room.walls.splice(bestJ, 1); room.closed = false; }
          }
          if (room.walls.length === 0) this.rooms.splice(i, 1);
        }
      } else {
        for (let j = room.walls.length - 1; j >= 0; j--) { if (this.selectedWalls.has(room.walls[j])) { room.walls.splice(j, 1); room.closed = false; } }
        if (room.walls.length === 0) this.rooms.splice(i, 1);
      }
    }
    if (this.currentRoom) {
      for (let j = this.currentRoom.walls.length - 1; j >= 0; j--) { if (this.selectedWalls.has(this.currentRoom.walls[j])) this.currentRoom.walls.splice(j, 1); }
      if (this.currentRoom.walls.length === 0) { this.currentWall = null; this.hasPlacedFirstPoint = false; }
    }
    const count = this.selectedWalls.size + this.selectedDoors.size + this.selectedObjects.size;
    this.selectedWalls = new Set(); this.selectedDoors = new Set(); this.selectedObjects = new Set(); this.selectionStart = null; this.selectionEnd = null;
    this.render(); this.updateMeasurements(); this.updateStatus(`Deleted ${count} item(s).`);
    this._updateSelectionStatus();
  }

  sendToFront() {
    if (this.selectedObjects.size === 0) return;
    this.saveUndoState();
    // Collect selected objects in their current relative order (skip platforms — they stay in place)
    const selected = [];
    for (let i = this.objects.length - 1; i >= 0; i--) {
      if (this.selectedObjects.has(this.objects[i]) && this.objects[i].type !== 'platform') {
        selected.unshift(this.objects[i]);
        this.objects.splice(i, 1);
      }
    }
    if (selected.length === 0) return;
    // Push to end (top of draw order)
    for (const obj of selected) this.objects.push(obj);
    this.updateStatus(`Sent ${selected.length} item(s) to front.`);
    this.render();
  }

  sendToBack() {
    if (this.selectedObjects.size === 0) return;
    this.saveUndoState();
    const selected = [];
    for (let i = this.objects.length - 1; i >= 0; i--) {
      if (this.selectedObjects.has(this.objects[i]) && this.objects[i].type !== 'platform') {
        selected.unshift(this.objects[i]);
        this.objects.splice(i, 1);
      }
    }
    if (selected.length === 0) return;
    // Unshift to start (bottom of draw order)
    this.objects.unshift(...selected);
    this.updateStatus(`Sent ${selected.length} item(s) to back.`);
    this.render();
  }

  groupSelected() {
    // Only equipment and platform objects can be grouped
    const equipSel = [...this.selectedObjects].filter(o => o.type === 'equipment' || o.type === 'platform');
    if (equipSel.length < 2) { this.updateStatus('Select 2 or more equipment or platform items to group.', { error: true }); return; }
    this.saveUndoState();
    const gid = 'g_' + Date.now();
    for (const obj of equipSel) obj.groupId = gid;
    this.updateStatus(`Grouped ${equipSel.length} items.`);
    this._updateActionButtons();
    this.render();
  }

  ungroupSelected() {
    const groupIds = new Set();
    this.selectedObjects.forEach(obj => { if (obj.groupId) groupIds.add(obj.groupId); });
    if (groupIds.size === 0) { this.updateStatus('No grouped items in selection.', { error: true }); return; }
    this.saveUndoState();
    let count = 0;
    for (const obj of this.objects) {
      if (obj.groupId && groupIds.has(obj.groupId)) { obj.groupId = undefined; count++; }
    }
    this.updateStatus(`Ungrouped ${count} items.`);
    this._updateActionButtons();
    this.render();
  }

  _expandSelectionToGroups() {
    const groupIds = new Set();
    this.selectedObjects.forEach(obj => { if (obj.groupId) groupIds.add(obj.groupId); });
    if (groupIds.size === 0) return;
    for (const obj of this.objects) {
      if (obj.groupId && groupIds.has(obj.groupId)) this.selectedObjects.add(obj);
    }
  }

  copySelected() {
    // Logo-focused copy: copy selected turf logos, not the turf object
    if (this._turfLogoFocusedIdx != null && this.selectedObjects.size === 1) {
      const obj = [...this.selectedObjects][0];
      if (obj.type === 'turf' && obj.turfLogos && obj.turfLogos.length > 0) {
        const idxs = this._turfLogoSelectedIdxs.size > 0 ? [...this._turfLogoSelectedIdxs] : [this._turfLogoFocusedIdx];
        const logos = idxs.map(i => obj.turfLogos[i]).filter(Boolean).map(l => JSON.parse(JSON.stringify(l)));
        if (logos.length > 0) {
          // Strip runtime properties
          for (const l of logos) { delete l.image; delete l.tightBounds; delete l._snapX; delete l._snapY; delete l._snapGapRunY; delete l._resizing; }
          this._clipboard = { turfLogos: logos };
          this._pasteCount = 0;
          this.updateStatus(`Copied ${logos.length} ladder(s)/logo(s).`);
          return;
        }
      }
    }
    if (this.selectedWalls.size === 0 && this.selectedDoors.size === 0 && this.selectedObjects.size === 0) {
      this.updateStatus('Nothing selected to copy.', { error: true }); return;
    }
    const clip = { objects: [], wallGroups: [], standaloneDoors: [] };
    this._pasteCardinalSnap = null;
    // Clone selected objects — preserve z-order index so paste can restore draw order
    this.selectedObjects.forEach(obj => {
      const clone = JSON.parse(JSON.stringify(obj));
      clone._sourceIndex = this.objects.indexOf(obj);
      clip.objects.push(clone);
    });
    // Group selected walls by room and clone doors that reference those walls
    const allRooms = [...this.rooms];
    if (this.currentRoom) allRooms.push(this.currentRoom);
    const processedDoors = new Set();
    allRooms.forEach(room => {
      const roomSelWalls = room.walls.filter(w => this.selectedWalls.has(w));
      if (roomSelWalls.length === 0) return;
      const wallClones = roomSelWalls.map(w => ({ start: { x: w.start.x, y: w.start.y }, end: { x: w.end.x, y: w.end.y } }));
      const doorClones = [];
      room.doors.forEach(door => {
        if (processedDoors.has(door)) return;
        const wallIdx = roomSelWalls.indexOf(door.wall);
        if (wallIdx >= 0 || this.selectedDoors.has(door)) {
          if (wallIdx >= 0) {
            doorClones.push({ wallIndex: wallIdx, type: door.type, width: door.width, hingeParam: door.hingeParam, endParam: door.endParam, hingeSide: door.hingeSide, openAngle: door.openAngle, swing: door.swing ? JSON.parse(JSON.stringify(door.swing)) : null });
            processedDoors.add(door);
          }
        }
      });
      clip.wallGroups.push({ walls: wallClones, doors: doorClones, closed: room.closed, type: room.type || 'add', cutouts: room.cutouts ? JSON.parse(JSON.stringify(room.cutouts)) : [] });
    });
    // Standalone selected doors (wall not selected) — store with wall geometry for re-matching
    this.selectedDoors.forEach(door => {
      if (processedDoors.has(door)) return;
      const w = door.wall;
      clip.standaloneDoors.push({
        wallStart: { x: w.start.x, y: w.start.y }, wallEnd: { x: w.end.x, y: w.end.y },
        type: door.type, width: door.width, hingeParam: door.hingeParam, endParam: door.endParam, hingeSide: door.hingeSide, openAngle: door.openAngle, swing: door.swing ? JSON.parse(JSON.stringify(door.swing)) : null
      });
    });
    this._clipboard = clip;
    this._pasteCount = 0;
    // Set _lastPastedPos to the SOURCE position so first Ctrl+V already has a chain reference
    let sMinX = Infinity, sMinY = Infinity, sMaxX = -Infinity, sMaxY = -Infinity;
    clip.objects.forEach(obj => {
      const rot = obj.rotation || 0;
      const w = obj.tightWidth || obj.width || 1, d = obj.tightDepth || obj.depth || obj.height || 1;
      const cosR = Math.abs(Math.cos(rot)), sinR = Math.abs(Math.sin(rot));
      const hx = (w / 2) * cosR + (d / 2) * sinR, hy = (w / 2) * sinR + (d / 2) * cosR;
      sMinX = Math.min(sMinX, obj.x - hx); sMaxX = Math.max(sMaxX, obj.x + hx);
      sMinY = Math.min(sMinY, obj.y - hy); sMaxY = Math.max(sMaxY, obj.y + hy);
    });
    clip.wallGroups.forEach(g => {
      g.walls.forEach(w => {
        sMinX = Math.min(sMinX, w.start.x, w.end.x); sMaxX = Math.max(sMaxX, w.start.x, w.end.x);
        sMinY = Math.min(sMinY, w.start.y, w.end.y); sMaxY = Math.max(sMaxY, w.start.y, w.end.y);
      });
    });
    if (sMinX < Infinity) {
      this._lastPastedPos = {
        x: (sMinX + sMaxX) / 2, y: (sMinY + sMaxY) / 2,
        halfW: (sMaxX - sMinX) / 2, halfH: (sMaxY - sMinY) / 2
      };
    } else {
      this._lastPastedPos = null;
    }
    const count = this.selectedWalls.size + this.selectedDoors.size + this.selectedObjects.size;
    this.updateStatus(`Copied ${count} item(s).`);
  }

  pasteClipboard() {
    if (!this._clipboard) { this.updateStatus('Nothing on clipboard. Select items and press Ctrl+C first.', { error: true }); return; }
    const clip = this._clipboard;
    // Turf logo paste: add copied logos to the currently selected turf
    if (clip.turfLogos && clip.turfLogos.length > 0) {
      const selTurf = this.selectedObjects.size === 1 ? [...this.selectedObjects][0] : null;
      if (!selTurf || selTurf.type !== 'turf') {
        this.updateStatus('Select a turf piece to paste ladders/logos onto.', { error: true }); return;
      }
      this.saveUndoState();
      if (!selTurf.turfLogos) selTurf.turfLogos = [];
      this._pasteCount++;
      const offset = this._pasteCount * 1; // 1 ft offset per paste
      const newIdxs = [];
      for (const l of clip.turfLogos) {
        const clone = JSON.parse(JSON.stringify(l));
        clone.x = (clone.x || 0) + offset;
        clone.y = (clone.y || 0) + offset;
        // Rehydrate ladder (no image needed) or logo image from dataURL
        if (clone.dataURL && clone.type !== 'ladder') {
          const img = new Image();
          img.src = clone.dataURL;
          clone.image = img;
        }
        newIdxs.push(selTurf.turfLogos.length);
        selTurf.turfLogos.push(clone);
      }
      // Select the newly pasted logos
      this._turfLogoSelectedIdxs.clear();
      for (const i of newIdxs) this._turfLogoSelectedIdxs.add(i);
      this._turfLogoFocusedIdx = newIdxs[newIdxs.length - 1];
      // Sync panel
      if (window.roomDesignerOverlay) {
        const body = document.getElementById('rdSidePanelBody');
        if (body) { window.roomDesignerOverlay._syncTurfLogoPanelPreview(body, selTurf); window.roomDesignerOverlay._lastTurfSelFp = null; }
      }
      this.updateStatus(`Pasted ${clip.turfLogos.length} ladder(s)/logo(s).`);
      this.render();
      return;
    }
    // If clipboard is only standalone doors, enter door placement mode (existing behavior)
    if (clip.standaloneDoors.length > 0 && clip.wallGroups.length === 0 && clip.objects.length === 0) {
      const sd = clip.standaloneDoors[0];
      this.doorType = sd.type || 'single';
      this.doorWidth = sd.width || 36;
      // Switch to door tool with copied type/width — normal Phase 1/2/3 flow
      this.activeTool = 'door'; this.mode = 'door'; this.objectType = 'door';
      this.doorPlacement = null;
      document.querySelectorAll('.tool-btn').forEach(b => b.classList.remove('active'));
      document.getElementById('objectsToolBtn').classList.add('active');
      const dwInput = document.getElementById('doorWidth');
      if (dwInput) dwInput.value = this.doorWidth;
      this.canvas.style.cursor = 'crosshair';
      this.updateStatus('Click on a wall to place the copied door.');
      this.render();
      return;
    }
    // Build cloned preview items and compute bounding-box centroid
    const previewObjects = clip.objects.map(o => JSON.parse(JSON.stringify(o)));
    const previewWallGroups = clip.wallGroups.map(g => JSON.parse(JSON.stringify(g)));
    // Collect all positions for centroid
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    previewObjects.forEach(obj => {
      const hw = (obj.width || 0) / 2, hd = (obj.depth || obj.height || 0) / 2;
      minX = Math.min(minX, obj.x - hw); maxX = Math.max(maxX, obj.x + hw);
      minY = Math.min(minY, obj.y - hd); maxY = Math.max(maxY, obj.y + hd);
    });
    previewWallGroups.forEach(g => {
      g.walls.forEach(w => {
        minX = Math.min(minX, w.start.x, w.end.x); maxX = Math.max(maxX, w.start.x, w.end.x);
        minY = Math.min(minY, w.start.y, w.end.y); maxY = Math.max(maxY, w.start.y, w.end.y);
      });
    });
    const centroid = { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
    // Rotation-aware bounding box half-extents for edge-gap snapping
    let bMinX = Infinity, bMinY = Infinity, bMaxX = -Infinity, bMaxY = -Infinity;
    previewObjects.forEach(obj => {
      const rot = obj.rotation || 0;
      const w = obj.tightWidth || obj.width || 1, d = obj.tightDepth || obj.depth || obj.height || 1;
      const cosR = Math.abs(Math.cos(rot)), sinR = Math.abs(Math.sin(rot));
      const hx = (w / 2) * cosR + (d / 2) * sinR, hy = (w / 2) * sinR + (d / 2) * cosR;
      bMinX = Math.min(bMinX, obj.x - hx); bMaxX = Math.max(bMaxX, obj.x + hx);
      bMinY = Math.min(bMinY, obj.y - hy); bMaxY = Math.max(bMaxY, obj.y + hy);
    });
    previewWallGroups.forEach(g => {
      g.walls.forEach(w => {
        bMinX = Math.min(bMinX, w.start.x, w.end.x); bMaxX = Math.max(bMaxX, w.start.x, w.end.x);
        bMinY = Math.min(bMinY, w.start.y, w.end.y); bMaxY = Math.max(bMaxY, w.start.y, w.end.y);
      });
    });
    const bboxHalfW = bMinX < Infinity ? (bMaxX - bMinX) / 2 : 0;
    const bboxHalfH = bMinY < Infinity ? (bMaxY - bMinY) / 2 : 0;
    // Enter paste preview mode — ghost follows mouse
    this._pastePreview = { objects: previewObjects, wallGroups: previewWallGroups, centroid, offset: { x: 0, y: 0 }, bboxHalfW, bboxHalfH };
    // Clear current selection and switch to select tool
    this.selectedWalls = new Set(); this.selectedDoors = new Set(); this.selectedObjects = new Set();
    this.canvas.style.cursor = 'crosshair';
    this.updateStatus('Click to place pasted items. Esc to cancel.');
    this.render();
  }

  _commitPastePreview() {
    if (!this._pastePreview) return;
    const pp = this._pastePreview;
    const ox = pp.offset.x, oy = pp.offset.y;
    // Block paste if any platform would overlap a wall or another platform
    const _pasteOv = window.roomDesignerOverlay;
    for (const obj of pp.objects) {
      if (obj.type !== 'platform') continue;
      let px = obj.x + ox, py = obj.y + oy;
      const rot = obj.rotation || 0;
      // Tile grid snap for pasted platforms
      if (_pasteOv) {
        let _pRoom = null;
        for (const room of (this.rooms || [])) {
          if (!room.closed) continue;
          const geo = this._getRoomGeoCache(room);
          if (geo.roomPolygon && this.pointInPolygon(px, py, geo.roomPolygon)) { _pRoom = room; break; }
        }
        const _pZone = _pRoom ? _pasteOv._getZoneFlooringAtPoint(_pRoom, px, py) : null;
        if (_pZone && _pZone.product.type === 'tile') {
          // Block 8x5 on tiles
          if (obj.preset === '8x5') {
            this.updateStatus("8\u2032\u00d75\u2032 platform can\u2019t align to tile grid.", { error: true });
            return;
          }
          const _ptW = _pZone.product.widthFt || 2;
          const _ptH = _pZone.product.heightFt || _ptW;
          const _pGeo = this._getRoomGeoCache(_pRoom);
          const _pSnap = _pasteOv._snapPlatformToTileGrid(px, py, obj.width, obj.depth, rot, _ptW, _ptH, _pZone.flooringAngle, _pGeo.roomPolygon);
          px = _pSnap.x; py = _pSnap.y;
          obj._pasteSnapX = px - (obj.x + ox); // delta to apply on commit
          obj._pasteSnapY = py - (obj.y + oy);
        }
        // Single-zone containment
        if (_pRoom) {
          const _pRot90 = (Math.round(rot / (Math.PI / 2)) % 2 !== 0);
          const _pW = _pRot90 ? obj.depth : obj.width, _pD = _pRot90 ? obj.width : obj.depth;
          const _pCenter = _pasteOv._getZoneFlooringAtPoint(_pRoom, px, py);
          const _pCenterPoly = _pCenter && _pCenter.zone ? _pCenter.zone.polygon : null;
          const _pCorners = [
            { x: px - _pW/2, y: py - _pD/2 }, { x: px + _pW/2, y: py - _pD/2 },
            { x: px + _pW/2, y: py + _pD/2 }, { x: px - _pW/2, y: py + _pD/2 }
          ];
          for (const c of _pCorners) {
            const _cz = _pasteOv._getZoneFlooringAtPoint(_pRoom, c.x, c.y);
            const _czPoly = _cz && _cz.zone ? _cz.zone.polygon : null;
            if (_czPoly !== _pCenterPoly) {
              this.updateStatus('Platform must be entirely within one flooring zone.', { error: true });
              return;
            }
          }
        }
      }
      if (this._platformOverlapsWall(px, py, obj.width, obj.depth, rot) ||
          this._platformOverlapsPlatform(px, py, obj.width, obj.depth, rot, null) ||
          this._turfOverlapsTurf(px, py, obj.width, obj.depth, rot, null)) {
        this.updateStatus('Platform overlaps a wall, platform, or turf. Try a different location.', { error: true });
        return;
      }
    }
    this.saveUndoState();
    // Clear current selection
    this.selectedWalls = new Set(); this.selectedDoors = new Set(); this.selectedObjects = new Set();
    // Remap groupIds so pasted group members stay grouped but don't merge with originals
    const groupIdMap = new Map();
    pp.objects.forEach(obj => {
      if (obj.groupId) {
        if (!groupIdMap.has(obj.groupId)) groupIdMap.set(obj.groupId, 'g_' + Date.now() + '_' + groupIdMap.size);
        obj.groupId = groupIdMap.get(obj.groupId);
      }
    });
    // Commit objects at offset positions, preserving z-order from source
    // Objects that were near the back (sent to back) insert at front; others append to end
    const totalObjs = this.objects.length;
    pp.objects.forEach(obj => {
      obj.x += ox + (obj._pasteSnapX || 0); obj.y += oy + (obj._pasteSnapY || 0);
      delete obj._pasteSnapX; delete obj._pasteSnapY;
      const srcIdx = obj._sourceIndex;
      delete obj._sourceIndex;
      // If source was in the first quarter of the array, insert at front (sent to back)
      if (srcIdx !== undefined && srcIdx >= 0 && totalObjs > 0 && srcIdx < totalObjs * 0.25) {
        this.objects.unshift(obj);
      } else {
        this.objects.push(obj);
      }
      this.selectedObjects.add(obj);
    });
    // Commit wall groups as new rooms
    pp.wallGroups.forEach(group => {
      const newWalls = group.walls.map(w => ({
        start: { x: w.start.x + ox, y: w.start.y + oy },
        end: { x: w.end.x + ox, y: w.end.y + oy }
      }));
      // Merge shared endpoints within the room
      for (let i = 0; i < newWalls.length; i++) {
        for (let j = i + 1; j < newWalls.length; j++) {
          if (Math.abs(newWalls[i].end.x - newWalls[j].start.x) < 0.001 && Math.abs(newWalls[i].end.y - newWalls[j].start.y) < 0.001) {
            newWalls[j].start = newWalls[i].end;
          }
          if (Math.abs(newWalls[j].end.x - newWalls[i].start.x) < 0.001 && Math.abs(newWalls[j].end.y - newWalls[i].start.y) < 0.001) {
            newWalls[i].start = newWalls[j].end;
          }
        }
      }
      const newDoors = group.doors.map(d => ({
        wall: newWalls[d.wallIndex], type: d.type, width: d.width, hingeParam: d.hingeParam, endParam: d.endParam, hingeSide: d.hingeSide, openAngle: d.openAngle, swing: d.swing ? JSON.parse(JSON.stringify(d.swing)) : null
      }));
      const newRoom = { walls: newWalls, doors: newDoors, cutouts: group.cutouts ? JSON.parse(JSON.stringify(group.cutouts)) : [], type: group.type, closed: group.closed };
      // Offset cutouts too
      if (newRoom.cutouts) { newRoom.cutouts.forEach(c => { if (c.walls) c.walls.forEach(w => { w.start.x += ox; w.start.y += oy; w.end.x += ox; w.end.y += oy; }); }); }
      this.rooms.push(newRoom);
      newWalls.forEach(w => this.selectedWalls.add(w));
      newDoors.forEach(d => this.selectedDoors.add(d));
    });
    this._relinkDoorWallRefs();
    // Save reference position for shift-hold cardinal axis chaining on paste
    this._lastPastedPos = { x: pp.centroid.x + ox, y: pp.centroid.y + oy, halfW: pp.bboxHalfW || 0, halfH: pp.bboxHalfH || 0 };
    // Splash effect at paste location
    if (this._animateRectLock) this._animateRectLock({ x: pp.centroid.x + ox, y: pp.centroid.y + oy }, 'blue');
    const count = this.selectedWalls.size + this.selectedDoors.size + this.selectedObjects.size;
    // Stay in paste-preview mode with fresh clones so user can keep stamping
    const clip = this._clipboard;
    if (clip) {
      const freshObjects = clip.objects.map(o => JSON.parse(JSON.stringify(o)));
      const freshWallGroups = clip.wallGroups.map(g => JSON.parse(JSON.stringify(g)));
      this._pastePreview = { objects: freshObjects, wallGroups: freshWallGroups, centroid: pp.centroid, offset: { x: 0, y: 0 }, bboxHalfW: pp.bboxHalfW, bboxHalfH: pp.bboxHalfH };
    } else {
      this._pastePreview = null;
    }
    this.canvas.style.cursor = 'crosshair';
    this.updateMeasurements(); this.render();
    this.updateStatus(`Pasted ${count} item(s). Click to place another, Esc to stop.`);
  }

  /* ── ROOM & WALL MANAGEMENT ── */

  startNewRoom() {
    if (this.currentRoom && this.currentRoom.walls.length > 0) {
      if (!this.currentRoom.closed) this.currentRoom.closed = false;
      this.rooms.push(this.currentRoom);
    }
    this.currentRoom = { walls: [], doors: [], cutouts: [], type: this.roomType, closed: false };
    this.currentWall = null; this.hasPlacedFirstPoint = false; this.render(); this.updateMeasurements();
  }

  saveUndoState() {
    this.undoStack.push({ rooms: JSON.parse(JSON.stringify(this.rooms)), currentRoom: this.currentRoom ? JSON.parse(JSON.stringify(this.currentRoom)) : null, objects: JSON.parse(JSON.stringify(this.objects)), wallSegmentGroups: JSON.parse(JSON.stringify(this.wallSegmentGroups)), currentWallSegmentGroup: this.currentWallSegmentGroup ? JSON.parse(JSON.stringify(this.currentWallSegmentGroup)) : null, measurements: JSON.parse(JSON.stringify(this._measurements)), measureNextId: this._measureNextId });
    this.redoStack = [];
    this._placementRedoStack = [];
    if (this.undoStack.length > 50) this.undoStack.shift();
    this._triggerAutoSave();
    this._markDirty();
  }

  /* ── SAVE / LOAD SYSTEM (delegated to RoomDesignerStorage) ──
     Methods are installed on this instance by the storage module constructor.
     These prototype stubs serve as no-op fallbacks if the module isn't loaded. */

  _serializeDesign() { return { rooms: [], objects: [], currentRoom: null, wallSegmentGroups: [], currentWallSegmentGroup: null, panX: 0, panY: 0, zoomLevel: 1 }; }
  _loadDesignData() {}
  _triggerAutoSave() {}
  _doAutoSave() {}
  _updateAutoSaveIndicator() {}
  _newDesign() {}
  _saveToFile() {}
  _openFromFile() {}
  _updateDesignNameUI() {}
  _showConfirm() {}
  _markDirty() {}
  _setupDesignsPanel() {}

  addWallPoint(point) {
    if (!this.currentRoom) return;
    if (Date.now() - this._lastRoomClosedAt < 400) return;
    if (this.currentRoom.closed) { this.promoteUnclosedRoom(); if (!this.currentRoom || this.currentRoom.closed) return; }
    if (this.currentWall && this.hasPlacedFirstPoint) point = this.constrainToAxis(this.currentWall.start, point);
    const screenPixelsPerInch = this.basePixelsPerFoot * this.zoomLevel / 12;
    const inchGridVisible = screenPixelsPerInch >= 3;
    const snapThreshold = inchGridVisible ? (1 / 12) : (4 / 12);
    const placeSnapThreshold = (this.roomType === 'subtract' && inchGridVisible) ? (1 / 12) : (4 / 12);

    // Check close — also check open endpoints of other unclosed rooms
    if (this.currentWall && this.hasPlacedFirstPoint && this.currentRoom.walls.length >= 1) {
      const currentStart = this.currentWall.start;
      // Collect close candidates from currentRoom + other unclosed rooms
      const closeCandidates = [];
      const crEndpoints = this.getOpenEndpoints(this.currentRoom).filter(ep => this.getDistance(ep, currentStart) > 0.01);
      crEndpoints.forEach(ep => closeCandidates.push({ ep, fromRoom: null }));
      for (let i = 0; i < this.rooms.length; i++) {
        const room = this.rooms[i]; if (room.closed || room.walls.length === 0) continue;
        this.getOpenEndpoints(room).forEach(ep => closeCandidates.push({ ep, fromRoom: room, fromIndex: i }));
      }
      for (const cand of closeCandidates) {
        const rawDist = this.mouseWorldRaw ? this.getDistance(this.mouseWorldRaw, cand.ep) : this.getDistance(point, cand.ep);
        if (rawDist < snapThreshold) {
          this.saveUndoState();
          const _preWalls1 = this._collectAllRoomWallsFlat();
          // If endpoint is from another room, merge it into currentRoom first
          if (cand.fromRoom) {
            this.currentRoom.walls.push(...cand.fromRoom.walls);
            this.currentRoom.doors.push(...cand.fromRoom.doors);
            const idx = this.rooms.indexOf(cand.fromRoom);
            if (idx >= 0) this.rooms.splice(idx, 1);
          }
          this.currentRoom.walls.push({ start: { x: currentStart.x, y: currentStart.y }, end: { x: cand.ep.x, y: cand.ep.y } });
          this.mergeCollinearWalls(this.currentRoom); this._forceCloseRoom(this.currentRoom);
          if (this.isRoomClosed(this.currentRoom)) {
            this._animateRectLock({ x: cand.ep.x, y: cand.ep.y });
            this.currentRoom.closed = true; this.currentRoom._wasClosedOnce = true; this.currentWall = null; this.hasPlacedFirstPoint = false;
            this.finalizeClosedRoom(this.currentRoom);
            this._pruneOrphanedWallSegments(_preWalls1);
            this._lastRoomClosedAt = Date.now(); this._trimmedSnapRoom = null; this._trimmedSnapPoint = null;
            this.currentRoom = { walls: [], doors: [], cutouts: [], type: this.roomType, closed: false };
            if (navigator.vibrate) navigator.vibrate(50);
          } else { this.currentWall = null; this.hasPlacedFirstPoint = false; this.updateStatus('Wall added. Click an open endpoint to continue.'); }
          this.render(); this.updateMeasurements(); return;
        }
      }
    }

    // Snap to endpoints
    if (!this.currentWall) {
      if (this.currentRoom.walls.length > 0) {
        const endpoints = this.getOpenEndpoints(this.currentRoom);
        for (const ep of endpoints) {
          const rawDist = this.mouseWorldRaw ? this.getDistance(this.mouseWorldRaw, ep) : this.getDistance(point, ep);
          if (rawDist < snapThreshold) {
            this.currentWall = { start: { x: ep.x, y: ep.y }, end: { x: ep.x, y: ep.y } };
            this.hasPlacedFirstPoint = true; this.updateStatus('Resumed from open endpoint.'); this.render(); return;
          }
        }
      }
      // Always scan rooms[] for open endpoints (even when currentRoom is empty)
      for (let i = 0; i < this.rooms.length; i++) {
        const room = this.rooms[i]; if (room.closed || room.walls.length === 0) continue;
        const eps = this.getOpenEndpoints(room);
        for (const ep of eps) {
          const rawDist = this.mouseWorldRaw ? this.getDistance(this.mouseWorldRaw, ep) : this.getDistance(point, ep);
          if (rawDist < snapThreshold) {
            if (this.currentRoom && this.currentRoom.walls.length > 0) this.rooms.push(this.currentRoom);
            this.currentRoom = this.rooms.splice(i, 1)[0];
            this.currentWall = { start: { x: ep.x, y: ep.y }, end: { x: ep.x, y: ep.y } };
            this.hasPlacedFirstPoint = true; this.updateStatus('Resumed from open endpoint on another room.'); this.updateMeasurements(); this.render(); return;
          }
        }
      }

      // Trimmed/cutout wall endpoints — only snap to endpoints created by cutout trimming
      // or interior cutout endpoints, NOT natural boundary corners
      for (const room of this.rooms) {
        if (!room.closed || !room.cutouts || room.cutouts.length === 0) continue;
        const boundaryEps = new Set();
        for (const w of room.walls) { boundaryEps.add(`${Math.round(w.start.x * 100)},${Math.round(w.start.y * 100)}`); boundaryEps.add(`${Math.round(w.end.x * 100)},${Math.round(w.end.y * 100)}`); }
        const visWalls = this.getVisibleRoomWalls(room);
        const intData = this.getInteriorCutoutWalls(room);
        const allW = [...visWalls, ...intData.map(d => d.wall)];
        for (const w of allW) {
          for (const ep of [w.start, w.end]) {
            const epKey = `${Math.round(ep.x * 100)},${Math.round(ep.y * 100)}`;
            if (boundaryEps.has(epKey)) continue;
            const rawDist = this.mouseWorldRaw ? this.getDistance(this.mouseWorldRaw, ep) : this.getDistance(point, ep);
            if (rawDist < placeSnapThreshold) {
              this._trimmedSnapRoom = room; this._trimmedSnapPoint = { x: ep.x, y: ep.y };
              this.currentWall = { start: { x: ep.x, y: ep.y }, end: { x: ep.x, y: ep.y } };
              this.hasPlacedFirstPoint = true; this.updateStatus('Snapped to wall point.'); this.render(); return;
            }
          }
        }
      }

      this._trimmedSnapRoom = null; this._trimmedSnapPoint = null;
      this.currentWall = { start: { x: point.x, y: point.y }, end: { x: point.x, y: point.y } };
      this.hasPlacedFirstPoint = true;
      this._animateRectLock({ x: point.x, y: point.y });
    } else {
      // Ending at trimmed endpoint (skip entirely in subtract mode)
      if (this._trimmedSnapRoom && this.roomType !== 'subtract') {
        for (const room of this.rooms) {
          if (room !== this._trimmedSnapRoom) continue;
          if (!room.closed || !room.cutouts || room.cutouts.length === 0) continue;
          const boundaryEps2 = new Set();
          for (const w of room.walls) { boundaryEps2.add(`${Math.round(w.start.x * 100)},${Math.round(w.start.y * 100)}`); boundaryEps2.add(`${Math.round(w.end.x * 100)},${Math.round(w.end.y * 100)}`); }
          const visWalls = this.getVisibleRoomWalls(room);
          const intData = this.getInteriorCutoutWalls(room);
          const allW = [...visWalls, ...intData.map(d => d.wall)];
          for (const w of allW) {
            for (const ep of [w.start, w.end]) {
              const epKey2 = `${Math.round(ep.x * 100)},${Math.round(ep.y * 100)}`;
              if (boundaryEps2.has(epKey2)) continue;
              const rawDist = this.mouseWorldRaw ? this.getDistance(this.mouseWorldRaw, ep) : this.getDistance(point, ep);
              if (rawDist < placeSnapThreshold) {
                const newWall = { start: { x: this.currentWall.start.x, y: this.currentWall.start.y }, end: { x: ep.x, y: ep.y } };
                const rp = this.tracePolygon(room.walls);
                const mainWalls = this._getLargestComponent([...visWalls, ...intData.map(d => d.wall)], rp);
                // Skip if newWall is redundant (both endpoints lie on an existing visible wall)
                const isRedundant = mainWalls.some(mw => this.distanceToWall(newWall.start, mw) < 0.01 && this.distanceToWall(newWall.end, mw) < 0.01);
                if (isRedundant) {
                  // Don't add duplicate wall — reposition cursor so user can continue drawing from this endpoint
                  this.currentWall = { start: { x: ep.x, y: ep.y }, end: { x: ep.x, y: ep.y } };
                  this.hasPlacedFirstPoint = true;
                  this.updateStatus('Continuing from wall endpoint.'); this.render(); return;
                }
                this.saveUndoState();
                room.walls = [...mainWalls, newWall]; room.cutouts = [];
                this.mergeCollinearWalls(room); room.closed = this.isRoomClosed(room);
                this._trimmedSnapRoom = null; this._trimmedSnapPoint = null; this.currentWall = null; this.hasPlacedFirstPoint = false;
                this.currentRoom = { walls: [], doors: [], cutouts: [], type: this.roomType, closed: false };
                this.updateStatus('Wall rebuilt!'); this.render(); this.updateMeasurements(); return;
              }
            }
          }
        }
      }

      // Normal wall placement — skip walls shorter than 1 inch (1/12 ft)
      const wallLen = this.getDistance(this.currentWall.start, this.currentWall.end);
      if (wallLen < 1 / 12) { return; }
      // Skip undo save if this segment continues in the same direction as the
      // last wall (accidental pivot along the same line). The previous undo
      // entry already covers it, so undo removes both collinear segments at once.
      const prevWalls = this.currentRoom.walls;
      const lastW = prevWalls.length > 0 ? prevWalls[prevWalls.length - 1] : null;
      let collinear = false;
      if (lastW) {
        const ldx = lastW.end.x - lastW.start.x, ldy = lastW.end.y - lastW.start.y;
        const ndx = this.currentWall.end.x - this.currentWall.start.x, ndy = this.currentWall.end.y - this.currentWall.start.y;
        const cross = ldx * ndy - ldy * ndx;
        const dot = ldx * ndx + ldy * ndy;
        collinear = Math.abs(cross) < 0.001 && dot > 0;
      }
      if (!collinear) this.saveUndoState();
      const _preWalls2 = this._collectAllRoomWallsFlat();
      const newWallEnd = { x: this.currentWall.end.x, y: this.currentWall.end.y };
      this._animateRectLock({ x: newWallEnd.x, y: newWallEnd.y });
      this.currentRoom.walls.push({ start: { x: this.currentWall.start.x, y: this.currentWall.start.y }, end: { x: newWallEnd.x, y: newWallEnd.y } });
      // Merge any unclosed room whose open endpoint matches the new wall's end
      this._mergeRoomAtEndpoint(newWallEnd, snapThreshold);
      this.mergeCollinearWalls(this.currentRoom); this._forceCloseRoom(this.currentRoom);
      if (this.currentRoom.walls.length >= 3 && this.isRoomClosed(this.currentRoom)) {
        this.currentRoom.closed = true; this.currentRoom._wasClosedOnce = true; this.currentWall = null; this.hasPlacedFirstPoint = false;
        this.finalizeClosedRoom(this.currentRoom);
        this._pruneOrphanedWallSegments(_preWalls2);
        this._lastRoomClosedAt = Date.now(); this._trimmedSnapRoom = null; this._trimmedSnapPoint = null;
        this.currentRoom = { walls: [], doors: [], cutouts: [], type: this.roomType, closed: false };
        if (navigator.vibrate) navigator.vibrate(50);
        this.render(); this.updateMeasurements(); return;
      }
      this.currentWall = { start: { x: newWallEnd.x, y: newWallEnd.y }, end: { x: newWallEnd.x, y: newWallEnd.y } };
      this.render(); this.updateMeasurements();
    }
  }

  _handleRectDraw(point) {
    if (Date.now() - this._lastRoomClosedAt < 400) return;
    if (!this._rectCorner1) {
      this._rectCorner1 = { x: point.x, y: point.y };
      this._animateRectLock({ x: point.x, y: point.y });
      this.updateStatus('Click to place opposite corner.');
      this.render();
      return;
    }
    const c1 = this._rectCorner1;
    const c2 = point;
    if (c1.x === c2.x || c1.y === c2.y) {
      this.updateStatus('Column must have area — corners cannot share an edge.', { error: true });
      return;
    }
    const minX = Math.min(c1.x, c2.x), maxX = Math.max(c1.x, c2.x);
    const minY = Math.min(c1.y, c2.y), maxY = Math.max(c1.y, c2.y);
    const tl = { x: minX, y: minY }, tr = { x: maxX, y: minY };
    const br = { x: maxX, y: maxY }, bl = { x: minX, y: maxY };
    const walls = [
      { start: { x: tl.x, y: tl.y }, end: { x: tr.x, y: tr.y } },
      { start: { x: tr.x, y: tr.y }, end: { x: br.x, y: br.y } },
      { start: { x: br.x, y: br.y }, end: { x: bl.x, y: bl.y } },
      { start: { x: bl.x, y: bl.y }, end: { x: tl.x, y: tl.y } }
    ];
    this.saveUndoState();
    const _preWalls = this._collectAllRoomWallsFlat();
    const room = { walls, doors: [], cutouts: [], type: this.roomType, closed: true };
    this.finalizeClosedRoom(room);
    this._pruneOrphanedWallSegments(_preWalls);
    this._animateRectLock({ x: c2.x, y: c2.y });
    this._resetRectState();
    this._lastRoomClosedAt = Date.now();
    this.currentRoom = { walls: [], doors: [], cutouts: [], type: this.roomType, closed: false };
    this.currentWall = null;
    this.hasPlacedFirstPoint = false;
    if (navigator.vibrate) navigator.vibrate(50);
    this.render();
    this.updateMeasurements();
  }

  /* ── GEOMETRY HELPERS ── */

  getOpenEndpoints(room) { return RoomDesignerMath.getOpenEndpoints(room); }

  mergeCollinearWalls(room) { return RoomDesignerMath.mergeCollinearWalls(room); }

  _forceCloseRoom(room) {
    if (!room || room.walls.length < 3) return;
    const endpoints = this.getOpenEndpoints(room);
    if (endpoints.length !== 2) return;
    const dist = this.getDistance(endpoints[0], endpoints[1]);
    if (dist < 2 / 12) {
      const target = endpoints[0]; const toSnap = endpoints[1];
      room.walls.forEach(wall => {
        if (Math.abs(wall.start.x - toSnap.x) < 0.001 && Math.abs(wall.start.y - toSnap.y) < 0.001) { wall.start.x = target.x; wall.start.y = target.y; }
        if (Math.abs(wall.end.x - toSnap.x) < 0.001 && Math.abs(wall.end.y - toSnap.y) < 0.001) { wall.end.x = target.x; wall.end.y = target.y; }
      });
    }
  }

  getWallParam(point, wall) { return RoomDesignerMath.getWallParam(point, wall); }

  paramToPoint(wall, param) { return RoomDesignerMath.paramToPoint(wall, param); }
  getWallLength(wall) { return RoomDesignerMath.getWallLength(wall); }

  snapParamToGrid(wall, param) {
    const wallLen = this.getWallLength(wall);
    const spi = this.basePixelsPerFoot * this.zoomLevel / 12;
    const fine = spi >= 3;
    const metric = this.unit === 'meters' || this.unit === 'm-cm' || this.unit === 'cm';
    const gridFt = metric
      ? (fine ? 0.032808399 : 0.82020997)
      : (fine ? 0.083333333 : 1);
    const snappedDist = Math.round(param * wallLen / gridFt) * gridFt;
    return wallLen > 0 ? Math.max(0, Math.min(1, snappedDist / wallLen)) : 0;
  }

  _findRoomForWall(wall) {
    for (const room of this.rooms) {
      if (room.walls.includes(wall)) return room;
      if (room.cutouts) { for (const c of room.cutouts) { if (c.walls.includes(wall)) return room; } }
    }
    if (this.currentRoom && this.currentRoom.walls.includes(wall)) return this.currentRoom;
    // Search wall segment groups — if found, return the group (duck-types as room)
    for (const group of this.wallSegmentGroups) {
      if (group.segments.includes(wall)) return group;
    }
    return null;
  }

  // CUSTOM_ROOMDESIGNER_BY_CLAUDE — curved walls (grouped-facet model) ─────────
  // A curve is N straight facet walls sharing a curveId. See
  // custom-room-designer-curve.js for the geometry; these are the editing hooks.

  /**
   * The actual array a wall lives in, so facets can be spliced in and out.
   * `_findRoomForWall` returns the owning object; this returns the list, which
   * differs between rooms (.walls), cutouts (.walls) and segment groups (.segments).
   */
  _findWallContainer(wall) {
    for (const room of this.rooms) {
      if (room.walls.includes(wall)) return { owner: room, list: room.walls };
      if (room.cutouts) { for (const c of room.cutouts) { if (c.walls.includes(wall)) return { owner: c, list: c.walls }; } }
    }
    if (this.currentRoom && this.currentRoom.walls.includes(wall)) return { owner: this.currentRoom, list: this.currentRoom.walls };
    for (const group of this.wallSegmentGroups) {
      if (group.segments.includes(wall)) return { owner: group, list: group.segments };
    }
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — and the in-progress chain, for the same reason
    // _hitTestSelect includes it: a segment you can select has to be one you can curve.
    const cur = this.currentWallSegmentGroup;
    if (cur && cur.segments && cur.segments.includes(wall)) return { owner: cur, list: cur.segments };
    return null;
  }

  /** Expand a selection to every facet of the curve the wall belongs to. */
  _selectCurveGroup(wall, into) {
    const CV = window.RoomDesignerCurve;
    const target = into || this.selectedWalls;
    if (!CV || !CV.isCurved(wall)) { target.add(wall); return target; }
    const owner = this._findRoomForWall(wall);
    const facets = this._curveFacetsOf(owner, wall.curveId);
    if (facets.length === 0) { target.add(wall); return target; }
    for (const f of facets) target.add(f);
    return target;
  }

  /** Remove every facet of a wall's curve from the selection. */
  _deselectCurveGroup(wall) {
    const CV = window.RoomDesignerCurve;
    if (!CV || !CV.isCurved(wall)) { this.selectedWalls.delete(wall); return; }
    const owner = this._findRoomForWall(wall);
    for (const f of this._curveFacetsOf(owner, wall.curveId)) this.selectedWalls.delete(f);
  }

  /**
   * After anything else has moved a wall endpoint (room-corner drag, endpoint
   * drag), re-fit every curve that shares that endpoint so the arc follows its
   * chord instead of tearing away from the corner. `walls` is the set of walls
   * the other operation touched.
   */
  _refitCurvesTouching(owner, walls) {
    const CV = window.RoomDesignerCurve;
    if (!CV || !owner || !walls) return;
    const ids = new Set();
    for (const w of walls) { if (w && w.curveId) ids.add(w.curveId); }
    if (ids.size === 0) return;
    for (const id of ids) {
      const facets = this._curveFacetsOf(owner, id);
      if (facets.length >= 2) CV.refitToChord(facets);
    }
  }

  /**
   * Slide a door along the curve it sits in, to arc position `tc` (0..1).
   *
   * Sliding changes only WHERE an opening is, never how many there are, so the facet
   * count and the opening's facet index are unchanged — applyToFacets mutates in
   * place and `door.wall` stays valid. No rebind needed.
   *
   * Returns 'moved', 'blocked' (clamped against a neighbour or the wall end), or
   * null when this door isn't on a curve.
   */
  _slideCurveDoor(door, tc, snapStep) {
    const CV = window.RoomDesignerCurve;
    const wall = door && door.wall;
    if (!CV || !wall || !wall.curveId || !door.openingId) return null;
    const owner = this._findRoomForWall(wall);
    if (!owner) return null;
    const facets = this._curveFacetsOf(owner, wall.curveId);
    if (facets.length === 0) return null;
    const arc = facets[0].curveArc;
    const ends = CV.endpointsOf(facets);
    const ops = (arc && arc.openings) || [];
    const cur = ops.find(o => o.id === door.openingId);
    if (!cur) return null;

    // Snap along the ARC, so the step is a real distance on the wall rather than a
    // parameter increment that would shrink as the curve deepens.
    let want = tc;
    if (snapStep > 0) {
      const arcLen = CV.arcLength(arc);
      if (arcLen > 0) want = (Math.round((tc * arcLen) / snapStep) * snapStep) / arcLen;
    }
    const moved = CV.clampOpeningCentre(arc, ops, want, cur.w, door.openingId);
    if (!moved) return 'blocked';
    if (Math.abs(moved.tc - cur.tc) < 1e-9) return 'blocked';
    const next = ops.map(o => (o.id === door.openingId ? { tc: moved.tc, w: moved.w, id: o.id } : o));
    const res = CV.applyToFacets(facets, ends.start, ends.end, arc.sag, next);
    if (res === null) return 'blocked';
    return (moved.atStart || moved.atEnd) ? 'blocked' : 'moved';
  }

  /**
   * Stop a bow from pushing the wall through another wall.
   *
   * Two walls curving inward toward each other is the case that breaks a drawing:
   * once their facets cross, the room polygon self-intersects and downstream code
   * starts discarding walls. _removeCoincidentWalls now refuses to touch curve
   * facets, which stops the destructive part, but the overlap still looks wrong —
   * so don't let it happen. Tests the candidate geometry BEFORE anything is mutated
   * and holds the last non-crossing bow.
   *
   * _segmentsIntersect ignores endpoint touches (t in 0.001..0.999), so walls that
   * legitimately meet this curve at a shared corner never trip it.
   */
  _curveClampAgainstWalls(info, sag) {
    const CV = window.RoomDesignerCurve;
    const M = RoomDesignerMath;
    if (!CV || !M || !sag) return sag;
    if (info.lastGoodSag === undefined) info.lastGoodSag = info.baseSag || 0;

    const facets = info.curveId ? this._curveFacetsOf(info.owner, info.curveId) : [];
    const anchor = facets[0] || info.wall;
    const container = this._findWallContainer(anchor);
    if (!container) return sag;
    const own = new Set(facets.length ? facets : [info.wall]);
    // A divider ATTACHED to this wall is not an obstacle — it travels with the bow
    // (see _curveReattachDividers), so it can never really be crossed. Leaving it in
    // was what refused to curve a divider inward toward the one joined to its middle:
    // the two touch by definition, so the very first frame of bow read as a crossing.
    // How far it may be eaten is the attachment test below, not this one.
    const attached = new Set();
    for (const a of (info.attachments || [])) for (const r of a.refs) attached.add(r.seg);
    // Nor is a divider this wall ALREADY runs through. Crossing dividers are how a
    // grid of zones gets drawn — one long wall over several verticals — and the test
    // is meant to stop a bow creating a NEW collision, not to re-report one the user
    // drew on purpose. Without this, a long divider drawn across others could not be
    // curved at all if they happened to share a group.
    const crossed = info.crossings || new Set();
    const others = container.list.filter(w => !own.has(w) && !attached.has(w) && !crossed.has(w));

    const arc = CV.fitArc(info.chordStart, info.chordEnd, sag);
    if (!arc) { info.lastGoodSag = sag; return sag; }
    const srcArc = facets.length ? facets[0].curveArc : null;
    const n = (srcArc && srcArc.n) || CV.DEFAULT_FACETS;
    const openings = (srcArc && srcArc.openings) || [];
    const pts = CV.tessellate(arc, info.chordStart, info.chordEnd, n, openings);
    if (!pts) return sag;

    for (let k = 0; k < pts.length - 1; k++) {
      const seg = { start: pts[k], end: pts[k + 1] };
      for (const w of others) {
        if (M._segmentsIntersect(seg, w)) {
          if (!info.warnedCross) {
            info.warnedCross = true;
            this.updateStatus('Stopped — curving further would run this wall through another.', { error: true });
          }
          return info.lastGoodSag;
        }
      }
    }
    if (!this._curveAttachmentsSurvive(info, pts)) {
      if (!info.warnedSwallow) {
        info.warnedSwallow = true;
        this.updateStatus('Stopped — curving further would swallow the wall joined to this one.', { error: true });
      }
      return info.lastGoodSag;
    }
    info.lastGoodSag = sag;
    return sag;
  }

  /**
   * Change a curve's set of openings — the ONLY operation that alters a curve's
   * facet count, and therefore the only one that has to rebuild rather than mutate.
   *
   * Rebuilding replaces every facet object, which would orphan `door.wall` (a direct
   * object reference). So every door on the curve is re-bound afterwards by matching
   * its `openingId` to the new opening facet. A door whose opening has gone is
   * removed along with it — the caller passes the surviving openings.
   *
   * Returns the new facet array, or null if the layout won't fit (nothing changed).
   */
  _curveSetOpenings(owner, curveId, openings) {
    const CV = window.RoomDesignerCurve;
    if (!CV || !owner) return null;
    const facets = this._curveFacetsOf(owner, curveId);
    if (facets.length === 0) return null;
    const container = this._findWallContainer(facets[0]);
    if (!container) return null;
    const list = container.list;
    const arc = facets[0].curveArc;
    const ends = CV.endpointsOf(facets);
    const start = { x: ends.start.x, y: ends.start.y };
    const end = { x: ends.end.x, y: ends.end.y };

    const built = CV.buildFacets(start, end, arc.sag, arc.n, openings, curveId);
    if (!built) return null;

    const idx = list.indexOf(facets[0]);
    for (const f of facets) { const i = list.indexOf(f); if (i >= 0) list.splice(i, 1); }
    list.splice(Math.max(0, Math.min(idx, list.length)), 0, ...built.facets);

    // Re-bind doors by opening id. Facet objects are all new, so any door still
    // pointing at an old facet would be dangling.
    // resolveOpenings sorts by position and facetPlan emits the opening facets in
    // that same order, so index k lines up between the two.
    const resolved = CV.resolveOpenings(built.arc, built.arc.openings) || [];
    const openFacets = built.facets.filter(f => f.isOpening);
    const byId = new Map();
    for (let k = 0; k < openFacets.length; k++) {
      if (resolved[k]) byId.set(resolved[k].id, openFacets[k]);
    }
    const doors = owner.doors || [];
    for (let i = doors.length - 1; i >= 0; i--) {
      const d = doors[i];
      if (!facets.includes(d.wall)) continue;
      if (d.openingId) {
        const target = byId.get(d.openingId);
        if (!target) { doors.splice(i, 1); continue; }   // its opening is gone
        // A door filling its opening facet runs 0->1, or 1->0 if it was hung the
        // other way round; preserve which end the hinge is on.
        const reversed = d.hingeParam > d.endParam;
        d.wall = target;
        d.hingeParam = reversed ? 1 : 0;
        d.endParam = reversed ? 0 : 1;
        continue;
      }
      // Safety net: a door on a plain facet has no opening to map to, but its wall
      // object is about to be discarded. Re-point it at the nearest new facet so it
      // can never dangle.
      const mid = { x: (d.wall.start.x + d.wall.end.x) / 2, y: (d.wall.start.y + d.wall.end.y) / 2 };
      let best = built.facets[0], bestD = Infinity;
      for (const f of built.facets) {
        const fx = (f.start.x + f.end.x) / 2 - mid.x, fy = (f.start.y + f.end.y) / 2 - mid.y;
        const dd = fx * fx + fy * fy;
        if (dd < bestD) { bestD = dd; best = f; }
      }
      d.wall = best;
    }

    // Selection holds wall object references too.
    if (this.selectedWalls && this.selectedWalls.size) {
      let had = false;
      for (const f of facets) { if (this.selectedWalls.delete(f)) had = true; }
      if (had) for (const f of built.facets) this.selectedWalls.add(f);
    }
    return built.facets;
  }

  /**
   * Clamp a requested sagitta to the bow limits. Inward (toward the room interior)
   * is capped tighter than outward. Emits one status message per drag when it bites,
   * so the handle stopping dead is explained rather than just feeling broken.
   */
  _curveClampSag(info, sag) {
    const CV = window.RoomDesignerCurve;
    if (!CV || !sag) return sag;
    const a = info.chordStart, b = info.chordEnd;
    const dx = b.x - a.x, dy = b.y - a.y;
    const chordLen = Math.sqrt(dx * dx + dy * dy);
    if (chordLen < 1e-6) return sag;

    // sag is signed against the LEFT normal; _getWallOutwardSign reports which way
    // that normal points relative to the room interior. Unknown (open room, wall
    // segment group) → no inside to protect, so allow the looser cap both ways.
    let outSign = 0;
    const room = info.owner;
    if (room && room.closed && room.walls && this._getWallOutwardSign) {
      outSign = this._getWallOutwardSign(info.wall, -dy / chordLen, dx / chordLen, room) || 0;
    }
    const goingOut = outSign === 0 || (sag > 0 ? 1 : -1) === outSign;
    const ratio = goingOut ? CV.MAX_ARC_RATIO_OUT : CV.MAX_ARC_RATIO_IN;
    const maxSag = CV.maxSagForRatio(chordLen, ratio);
    if (Math.abs(sag) <= maxSag) return sag;

    if (!info.warnedLimit) {
      info.warnedLimit = true;
      this.updateStatus(
        'Bow limit reached — a ' + this.formatMeasurement(chordLen) + ' wall can curve ' +
        (goingOut ? 'out' : 'in') + ' to at most ' + this.formatMeasurement(chordLen * ratio) + ' of arc.'
      );
    }
    return (sag > 0 ? 1 : -1) * maxSag;
  }

  /**
   * Core of the bulge drag: set the curve's sagitta, creating, reshaping or
   * collapsing the facet group as needed. Mutates `info` to track the group.
   *
   * Reshaping mutates facet objects in place, so doors anchored to them keep
   * working. Creating and collapsing change the wall SET, which would orphan
   * `door.wall` references — so those two transitions are blocked when doors are
   * present, and the sagitta is clamped instead.
   */
  _curveApplySag(info, sag) {
    const CV = window.RoomDesignerCurve;
    if (!CV || !info) return;
    // Resolve the owning wall array FRESH on every call. It must not be cached:
    // mergeCollinearWalls ends with `room.walls = room.walls.filter(...)`, which
    // REPLACES the array object. A reference captured at drag start can therefore
    // go stale, and splices would silently mutate an orphaned array while the room
    // kept its old walls.
    const anchor = info.curveId
      ? (this._curveFacetsOf(info.owner, info.curveId)[0] || info.wall)
      : info.wall;
    const container = this._findWallContainer(anchor);
    if (!container) return;
    info.owner = container.owner;
    const list = container.list;
    const flatAt = Math.max(CV.FLAT_EPS, info.flatWorld || 0);
    // Snapshot attached dividers on the FIRST call, while the wall is still where
    // the user last saw it. Doing it here rather than at drag start covers the
    // mobile joystick nudge too — both routes reach the reshape only through here.
    if (!info.attachments) {
      info.attachments = this._curveCaptureAttachments(info);
      info.crossings = this._curveCaptureCrossings(info);
    }

    // Cap how far the wall may bow. Curving IN eats floor area fast, so it is held
    // tighter than bulging OUT: a 10ft wall may reach 13ft of arc inward, 16ft
    // outward (35.0% and 51.4% of the wall's length in bow depth). Clamping here
    // covers the desktop drag and the mobile joystick nudge, which both route
    // through this method. A refit after a terminus move can't breach the cap —
    // it preserves the bow RATIO, and the cap is a function of that ratio alone.
    sag = this._curveClampSag(info, sag);
    // ...and stop it short of running through another wall (two facing concave walls
    // is the case that breaks a drawing).
    sag = this._curveClampAgainstWalls(info, sag);

    if (Math.abs(sag) < flatAt) {
      if (!info.curveId) return;                       // already straight
      const facets = this._curveFacetsOf(info.owner, info.curveId);
      if (facets.length === 0) return;
      const straight = CV.collapseToStraight(facets);
      // Straightening dissolves the curve's openings, so carry any doors sitting in
      // them onto the new straight wall at the same relative position and width,
      // rather than refusing the gesture or silently dropping the door.
      const arcC = facets[0].curveArc;
      const opsC = (arcC && CV.resolveOpenings(arcC, arcC.openings)) || [];
      const sLen = Math.hypot(straight.end.x - straight.start.x, straight.end.y - straight.start.y);
      const endsC = CV.endpointsOf(facets);
      for (const d of (info.owner.doors || [])) {
        // Every door on this curve moves, not just ones in openings — a door bound
        // to a plain facet (a legacy design, or anything that slipped through) would
        // otherwise be left pointing at a wall that no longer exists.
        if (!facets.includes(d.wall)) continue;
        const op = d.openingId && opsC.find(o => o.id === d.openingId);
        const w = op ? op.w : (d.width / 12);
        const halfP = sLen > 0 ? (w / 2) / sLen : 0;
        // Position from the opening if it has one, else from where its facet sat.
        let tc = 0.5;
        if (op) tc = op.tc;
        else if (arcC) {
          const mid = { x: (d.wall.start.x + d.wall.end.x) / 2, y: (d.wall.start.y + d.wall.end.y) / 2 };
          tc = CV.paramAtPoint(arcC, endsC.start, endsC.end, mid);
        }
        const c = Math.max(halfP, Math.min(1 - halfP, tc));
        d.wall = straight;
        d.hingeParam = c - halfP;
        d.endParam = c + halfP;
        d.openingId = undefined;
      }
      const idx = list.indexOf(facets[0]);
      for (const f of facets) { const i = list.indexOf(f); if (i >= 0) list.splice(i, 1); }
      list.splice(Math.max(0, Math.min(idx, list.length)), 0, straight);
      this.selectedWalls = new Set([straight]);
      info.curveId = null;
      info.wall = straight;
      this._curveReattachDividers(info);
      return;
    }

    if (!info.curveId) {
      // Curving a wall that already has doors converts each one into an opening at
      // the same position and width, so the doors ride the new curve.
      const straightDoors = (info.owner.doors || []).filter(d => d.wall === info.wall);
      const seedOpenings = straightDoors.map((d, i) => ({
        tc: (d.hingeParam + d.endParam) / 2,
        w: d.width / 12,
        id: 'op_c' + (info.curveSeed = (info.curveSeed || 0) + 1) + '_' + i
      }));
      const built = CV.buildFacets(info.chordStart, info.chordEnd, sag, CV.DEFAULT_FACETS, seedOpenings);
      if (!built) {
        if (straightDoors.length && !info.warnedDoors) {
          info.warnedDoors = true;
          this.updateStatus('Can\'t curve this far with a door on the wall — the opening no longer fits.', { error: true });
        }
        return;
      }
      const idx = list.indexOf(info.wall);
      if (idx < 0) return;
      list.splice(idx, 1, ...built.facets);
      // Re-bind the converted doors onto their opening facets.
      const openFacets = built.facets.filter(f => f.isOpening);
      const resolved = CV.resolveOpenings(built.arc, built.arc.openings) || [];
      for (let k = 0; k < openFacets.length; k++) {
        const op = resolved[k];
        const d = straightDoors.find((_, i) => seedOpenings[i] && seedOpenings[i].id === (op && op.id));
        if (!d) continue;
        d.wall = openFacets[k];
        d.hingeParam = 0;
        d.endParam = 1;
        d.openingId = op.id;
      }
      info.curveId = built.curveId;
      info.wall = built.facets[0];
      this.selectedWalls = new Set(built.facets);
      this._curveReattachDividers(info);
      return;
    }

    const facets = this._curveFacetsOf(info.owner, info.curveId);
    if (!facets.length) return;
    if (CV.applyToFacets(facets, info.chordStart, info.chordEnd, sag) === null && !info.warnedDoors) {
      // Refused: an opening no longer fits this radius. Say so rather than freeze.
      info.warnedDoors = true;
      this.updateStatus('Can\'t curve this far — an opening on this wall no longer fits.', { error: true });
    }
    this._curveReattachDividers(info);
  }

  /* ── DIVIDERS ATTACHED TO A CURVING WALL ──────────────────────────────────
     A divider joins a wall geometrically — its endpoint simply sits on the wall
     line, there is no anchor reference to follow (see _findWallSurfaceSnap, which
     places it there). So a wall that bows away from a join just leaves it behind,
     hanging in space, and the zone it was dividing quietly merges back into one.

     The join is therefore re-solved on every frame of the bow, against the current
     facet polyline rather than the ideal arc — the polyline IS the geometry, and
     computeRoomZones matches divider endpoints to boundary edges within 0.05ft, so
     a join placed on the true arc could sit off the chord of its facet by more than
     that and read as disconnected at exactly the deep bows where it matters most. */

  /** The wall as it stands right now: its facets if curved, else the straight wall. */
  _curvePathOf(info) {
    const f = info && info.curveId ? this._curveFacetsOf(info.owner, info.curveId) : [];
    if (f.length) return f;
    return (info && info.wall) ? [info.wall] : [];
  }

  /**
   * Every divider endpoint sitting on this wall, snapshotted BEFORE the first
   * reshape moves anything. Joins at the same point are recorded once and moved
   * together — chain vertices are plain coordinate copies, not shared objects
   * (undo deep-clones the whole group), so two segments meeting on the wall would
   * otherwise be dragged apart.
   */
  _curveCaptureAttachments(info) {
    const M = RoomDesignerMath;
    const path = this._curvePathOf(info);
    if (!path.length) return [];
    const own = new Set(path);
    const EPS = 0.06;                       // ft — a snapped join lands dead on the line
    const key = (p) => (Math.round(p.x * 100) / 100) + ',' + (Math.round(p.y * 100) / 100);
    const byPoint = new Map();
    const groups = this.currentWallSegmentGroup
      ? [...this.wallSegmentGroups, this.currentWallSegmentGroup]
      : this.wallSegmentGroups;
    // The chord termini don't move — only the bow between them does. A divider
    // joined at the wall's own end is joined to a corner, and belongs there whatever
    // the wall does in between, so it is left alone rather than re-solved.
    const ends = [info.chordStart, info.chordEnd].filter(Boolean);
    const atEnd = (p) => ends.some(e => Math.hypot(p.x - e.x, p.y - e.y) <= EPS);
    for (const g of groups) {
      if (!g || !g.segments) continue;
      for (const seg of g.segments) {
        if (own.has(seg)) continue;
        for (const which of ['start', 'end']) {
          const p = seg[which];
          if (atEnd(p)) continue;
          let on = false;
          for (const f of path) { if (M.distanceToWall(p, f) <= EPS) { on = true; break; } }
          if (!on) continue;
          const k = key(p);
          let a = byPoint.get(k);
          if (!a) { a = { refs: [], lastX: p.x, lastY: p.y }; byPoint.set(k, a); }
          a.refs.push({ seg, which });
        }
      }
    }
    // How the join travels: along the divider's OWN line, from its far end. Keeping
    // the divider's ANGLE is what the drawing means — it gets longer or shorter as
    // the wall bows toward or away from it. Sliding the join along the wall instead
    // would swing a square divider off square.
    //
    // Two joins have no single direction and fall back to sliding to the nearest
    // point on the wall: one where a chain turns a corner ON the wall (two segments,
    // two directions), and one where the divider's far end is ALSO on this wall, in
    // which case the "fixed" end isn't fixed and the captured origin would go stale.
    for (const a of byPoint.values()) {
      if (a.refs.length !== 1) continue;
      const { seg, which } = a.refs[0];
      const far = which === 'start' ? seg.end : seg.start;
      if (byPoint.has(key(far))) continue;
      const dx = a.lastX - far.x, dy = a.lastY - far.y;
      const len = Math.hypot(dx, dy);
      if (len < 1e-6) continue;
      a.farX = far.x; a.farY = far.y; a.ux = dx / len; a.uy = dy / len; a.baseLen = len;
    }
    return [...byPoint.values()];
  }

  /**
   * The walls this one already passes THROUGH before the bow starts — a divider
   * drawn across other dividers, which is how a grid of rooms gets laid out.
   *
   * Uses M._segmentsIntersect deliberately, whose (0.001, 0.999) bounds ignore hits
   * at either end: a neighbouring wall sharing a CORNER must not be mistaken for one
   * being crossed, or bowing a wall into its own neighbour would stop being caught.
   */
  _curveCaptureCrossings(info) {
    const M = RoomDesignerMath;
    const out = new Set();
    const path = this._curvePathOf(info);
    if (!path.length) return out;
    const container = this._findWallContainer(path[0]);
    if (!container) return out;
    const own = new Set(path);
    for (const w of container.list) {
      if (own.has(w)) continue;
      for (const f of path) { if (M._segmentsIntersect(f, w)) { out.add(w); break; } }
    }
    return out;
  }

  /**
   * Distance from (fx,fy) along unit direction (ux,uy) to where it meets a facet,
   * or null. Written out rather than reusing M.segSegIntersectionParams because
   * that rejects hits within 0.001 of either end of either segment: with 24 facets
   * a join can land exactly on a facet boundary, where BOTH neighbours would
   * reject it and a connected divider would read as loose.
   */
  _curveRayFacetHit(fx, fy, ux, uy, facet, minSin) {
    const dx = facet.end.x - facet.start.x, dy = facet.end.y - facet.start.y;
    const den = ux * dy - uy * dx;
    if (Math.abs(den) < 1e-12) return null;                       // parallel
    const t = ((facet.start.x - fx) * dy - (facet.start.y - fy) * dx) / den;   // feet along the ray
    const s = ((facet.start.x - fx) * uy - (facet.start.y - fy) * ux) / den;   // 0..1 along the facet
    if (t <= 0) return null;                     // behind the divider's far end = swallowed
    if (s < -1e-6 || s > 1 + 1e-6) return null;
    // A divider running nearly ALONG the wall meets it at a glancing angle, where a
    // hair of extra bow slides the join a long way. _curveJoinPoint leaves those to
    // the projection (minSin 0.17, ~10°); the survival test passes 0, because there
    // "no hit" has to mean SWALLOWED and must not also mean "too shallow to solve".
    const lim = minSin === undefined ? 0.17 : minSin;
    if (lim > 0 && Math.abs(den) / (Math.hypot(dx, dy) || 1) < lim) return null;
    return t;
  }

  /** Nearest point on the wall — the fallback when the divider's own line can't say. */
  _curveNearestPointOn(path, x, y) {
    let best = null, bestD = Infinity;
    for (const f of path) {
      const dx = f.end.x - f.start.x, dy = f.end.y - f.start.y;
      const L2 = dx * dx + dy * dy;
      const t = L2 > 1e-12 ? Math.max(0, Math.min(1, ((x - f.start.x) * dx + (y - f.start.y) * dy) / L2)) : 0;
      const px = f.start.x + dx * t, py = f.start.y + dy * t;
      const d = Math.hypot(px - x, py - y);
      if (d < bestD) { bestD = d; best = { x: px, y: py }; }
    }
    return best;
  }

  /** Where a captured join belongs on the wall as it stands now. */
  _curveJoinPoint(path, a) {
    if (a.ux !== undefined) {
      let best = null, bestD = Infinity;
      for (const f of path) {
        const t = this._curveRayFacetHit(a.farX, a.farY, a.ux, a.uy, f);
        if (t === null) continue;
        const p = { x: a.farX + a.ux * t, y: a.farY + a.uy * t };
        // A deep bow can cross the divider's line twice. The near crossing is the
        // one the join was already sitting on, so track continuity, not order.
        const d = Math.hypot(p.x - a.lastX, p.y - a.lastY);
        if (d < bestD) { bestD = d; best = p; }
      }
      if (best) return best;
    }
    return this._curveNearestPointOn(path, a.lastX, a.lastY);
  }

  /** Move every captured join back onto the wall. Called after each reshape. */
  _curveReattachDividers(info) {
    if (!info) return;
    const path = this._curvePathOf(info);
    if (!path.length) return;
    for (const a of (info.attachments || [])) {
      const p = this._curveJoinPoint(path, a);
      if (!p) continue;
      for (const r of a.refs) { r.seg[r.which].x = p.x; r.seg[r.which].y = p.y; }
      a.lastX = p.x; a.lastY = p.y;
    }
    this._curveAbsorbNearbyTips(info, path);
  }

  /** How far this wall reaches, for a cheap reject before any per-facet maths. */
  _curvePathBounds(path) {
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const f of path) {
      x0 = Math.min(x0, f.start.x, f.end.x); y0 = Math.min(y0, f.start.y, f.end.y);
      x1 = Math.max(x1, f.start.x, f.end.x); y1 = Math.max(y1, f.start.y, f.end.y);
    }
    return { x0, y0, x1, y1 };
  }

  /**
   * Is this divider end held by something OTHER than the wall being curved? An end
   * sitting on a room wall, on another divider, or shared with the next segment of
   * its own chain is anchored there and must not be dragged off it. Only true loose
   * ends — a wall stopping in mid-air — may be absorbed.
   */
  _curveEndIsAnchored(p, own, seg, groups) {
    const M = RoomDesignerMath;
    const EPS = 0.06;
    for (const room of [...this.rooms, this.currentRoom]) {
      if (!room || !room.walls) continue;
      for (const wl of room.walls) { if (M.distanceToWall(p, wl) <= EPS) return true; }
      if (room.cutouts) for (const c of room.cutouts) for (const wl of c.walls) { if (M.distanceToWall(p, wl) <= EPS) return true; }
    }
    for (const g of groups) {
      if (!g || !g.segments) continue;
      for (const s of g.segments) {
        if (s === seg || own.has(s)) continue;
        if (M.distanceToWall(p, s) <= EPS) return true;
      }
    }
    return false;
  }

  /**
   * Absorb loose divider ends the bow has arrived at.
   *
   * The rule, in Ryan's words: if a wall you're curving reaches the free end of
   * another wall they were meant to meet — and if a SECOND one is within 6in of the
   * arc as well, it was meant to meet it too, so it is trimmed or extended rather
   * than left overshooting by a few inches. Both ends travel along their OWN line,
   * so direction and angle never change; only length does.
   *
   * Note it is not "make them the same length": on an arc, two walls at different
   * positions meet it at different heights. Same JOIN, not same length.
   *
   * An absorbed end becomes a normal attachment, so from that moment it follows the
   * bow like any other join — and the clamp stops treating it as an obstacle.
   */
  _curveAbsorbNearbyTips(info, path) {
    const M = RoomDesignerMath;
    const TOL = 0.5;              // ft — 6in, whether short of the arc or past it
    const EPS = 0.06;             // ft — already joined; nothing to do
    const own = new Set(path);
    const groups = this.currentWallSegmentGroup
      ? [...this.wallSegmentGroups, this.currentWallSegmentGroup]
      : this.wallSegmentGroups;
    const b = this._curvePathBounds(path);
    const held = (seg, which) => (info.attachments || []).some(
      a => a.refs.some(r => r.seg === seg && r.which === which));

    for (const g of groups) {
      if (!g || !g.segments) continue;
      for (const seg of g.segments) {
        if (own.has(seg)) continue;
        for (const which of ['start', 'end']) {
          const p = seg[which];
          if (p.x < b.x0 - TOL || p.x > b.x1 + TOL || p.y < b.y0 - TOL || p.y > b.y1 + TOL) continue;
          if (held(seg, which)) continue;
          let d = Infinity;
          for (const f of path) { d = Math.min(d, M.distanceToWall(p, f)); if (d <= EPS) break; }
          // Within the rule, absorb it — INCLUDING one already touching. An end that
          // merely touches is not yet a join: nothing follows the next bow, and the
          // clamp still counts it as an obstacle. That is why a second wall could be
          // reached but never snapped onto, and why dragging on just refused.
          if (d > TOL) continue;
          if (this._curveEndIsAnchored(p, own, seg, groups)) continue;
          const far = which === 'start' ? seg.end : seg.start;
          const dx = p.x - far.x, dy = p.y - far.y;
          const len = Math.hypot(dx, dy);
          if (len < 1e-6) continue;
          const a = {
            refs: [{ seg, which }], lastX: p.x, lastY: p.y,
            farX: far.x, farY: far.y, ux: dx / len, uy: dy / len
          };
          const hit = this._curveJoinPoint(path, a);
          if (!hit) continue;
          // Refuse a join that would move the end further than the rule allows —
          // a glancing wall can meet the arc a long way from where it now ends.
          if (Math.hypot(hit.x - p.x, hit.y - p.y) > TOL) continue;
          const moved = Math.hypot(hit.x - p.x, hit.y - p.y);
          p.x = hit.x; p.y = hit.y;
          a.lastX = hit.x; a.lastY = hit.y;
          if (!info.attachments) info.attachments = [];
          info.attachments.push(a);
          info.absorbedCount = (info.absorbedCount || 0) + 1;
          if (moved > EPS) {
            this.updateStatus('Joined the wall it met — ' + this.formatMeasurement(moved) +
                              ' of length adjusted so it lands flush.');
          } else if (info.absorbedCount === 1) {
            this.updateStatus('Joined the wall it met — keep dragging and it comes with you.');
          }
        }
      }
    }
  }

  /**
   * Would this bow shorten an attached divider to nothing? Curving toward a divider
   * eats it end-first, and past its far end there is no join left to keep — so the
   * bow stops there instead of swallowing the wall whole.
   * Returns true if the candidate polyline is safe.
   */
  _curveAttachmentsSurvive(info, pts) {
    const list = info && info.attachments;
    if (!list || !list.length) return true;
    const MIN = 0.5;                                   // ft of divider left over
    const path = [];
    for (let k = 0; k < pts.length - 1; k++) path.push({ start: pts[k], end: pts[k + 1] });
    for (const a of list) {
      if (a.ux === undefined) continue;                // projection joins can't shorten
      let hit = null;
      for (const f of path) {
        const t = this._curveRayFacetHit(a.farX, a.farY, a.ux, a.uy, f, 0);
        if (t !== null && (hit === null || t < hit)) hit = t;
      }
      if (hit === null || hit < MIN) return false;
    }
    return true;
  }
  // END CUSTOM_ROOMDESIGNER_BY_CLAUDE

  /** Snap a position to room walls or wall segment surfaces/endpoints.
   *  Used when dragging wall segment endpoints to re-attach them to walls.
   *  opts.rawPos: un-snapped cursor position (for distance checks through thick walls)
   *  opts.isLine: true for dividing lines — uses extended threshold and edge snapping
   *  Returns { x, y, wall, param, room, snapType } or null. */
  _findWallSurfaceSnap(pos, excludeWall, excludeGroup, opts) {
    const isLine = opts && opts.isLine;
    const rawPos = (opts && opts.rawPos) || pos;
    const snapT = isLine ? (4 / 12 + this.wallThickness) : (4 / 12);
    let best = null, bestDist = snapT;

    // 1. Snap to room wall endpoints
    for (const room of [...this.rooms, this.currentRoom]) {
      if (!room || !room.walls) continue;
      for (const wall of room.walls) {
        for (const ep of [wall.start, wall.end]) {
          const d = this.getDistance(isLine ? rawPos : pos, ep);
          if (d < bestDist) { bestDist = d; best = { x: ep.x, y: ep.y, wall, param: ep === wall.start ? 0 : 1, room, snapType: 'endpoint' }; }
        }
      }
    }

    // 2. Snap to room wall midpoints (T-junction)
    for (const room of [...this.rooms, this.currentRoom]) {
      if (!room || !room.walls) continue;
      for (const wall of room.walls) {
        const checkPt = isLine ? rawPos : pos;
        const param = this.getWallParam(checkPt, wall);
        if (param <= 0.001 || param >= 0.999) continue;
        const proj = this.paramToPoint(wall, param);
        const d = this.getDistance(checkPt, proj);
        if (d < bestDist) {
          // Skip if projection lands on a door span
          let onDoor = false;
          for (const door of room.doors) {
            if (door.wall !== wall) continue;
            const lo = Math.min(door.hingeParam, door.endParam);
            const hi = Math.max(door.hingeParam, door.endParam);
            if (param >= lo - 0.02 && param <= hi + 0.02) { onDoor = true; break; }
          }
          if (!onDoor) { bestDist = d; best = { x: proj.x, y: proj.y, wall, param, room, snapType: 'midpoint' }; }
        }
      }
    }

    // 2b. CUSTOM_ROOMDESIGNER_BY_CLAUDE — doorways, so an EXISTING zone line can be
    // dragged onto a door frame as well as drawn onto one. Same targets the drawing
    // path uses (_getDoorSnapPoints); ranked in with the wall snaps so the nearest
    // one wins rather than doors always beating walls or vice versa.
    if (this._getDoorSnapPoints) {
      for (const ds of this._getDoorSnapPoints()) {
        const d = this.getDistance(isLine ? rawPos : pos, ds);
        if (d < bestDist) {
          bestDist = d;
          best = { x: ds.x, y: ds.y, wall: ds.wall, param: ds.param, room: ds.room, snapType: 'door-' + ds.snapType };
        }
      }
    }

    // 3. Snap to wall segment endpoints (exclude own group)
    for (const group of this.wallSegmentGroups) {
      if (group === excludeGroup) continue;
      for (const seg of group.segments) {
        for (const ep of [seg.start, seg.end]) {
          const d = this.getDistance(pos, ep);
          if (d < bestDist) { bestDist = d; best = { x: ep.x, y: ep.y, wall: seg, param: ep === seg.start ? 0 : 1, room: group, snapType: 'endpoint' }; }
        }
      }
    }

    // 4. Snap to wall segment midpoints (exclude own segment)
    for (const group of this.wallSegmentGroups) {
      for (const seg of group.segments) {
        if (seg === excludeWall) continue;
        const param = this.getWallParam(pos, seg);
        if (param <= 0.001 || param >= 0.999) continue;
        const proj = this.paramToPoint(seg, param);
        const d = this.getDistance(pos, proj);
        if (d < bestDist) {
          let onDoor = false;
          for (const door of group.doors) {
            if (door.wall !== seg) continue;
            const lo = Math.min(door.hingeParam, door.endParam);
            const hi = Math.max(door.hingeParam, door.endParam);
            if (param >= lo - 0.02 && param <= hi + 0.02) { onDoor = true; break; }
          }
          if (!onDoor) { bestDist = d; best = { x: proj.x, y: proj.y, wall: seg, param, room: group, snapType: 'midpoint' }; }
        }
      }
    }

    return best;
  }

  /** Commit a wall endpoint drag/float — shared by drag-release and click-move-click flows. */
  _commitWallEndpointDrag() {
    const info = this._wallEndpointDragInfo;
    if (!info) return;
    let room = info.room;
    const isWallSegmentGroup = room && room.segments && !room.walls;
    if (!isWallSegmentGroup) {
      // If connected to a different room, merge them into one
      if (info.snapTarget && info.snapTarget.room && info.snapTarget.room !== room) {
        const otherRoom = info.snapTarget.room;
        room.walls.push(...otherRoom.walls);
        room.doors.push(...otherRoom.doors);
        const idx = this.rooms.indexOf(otherRoom);
        if (idx >= 0) this.rooms.splice(idx, 1);
        if (this.currentRoom === otherRoom) {
          this.currentRoom = { walls: [], doors: [], cutouts: [], type: this.roomType, closed: false };
        }
      }
      // Always check if room is now closed (all endpoints connected)
      const endpoints = this.getOpenEndpoints(room);
      if (endpoints.length === 0 && room.walls.length >= 3) {
        this.mergeCollinearWalls(room);
        room.closed = true; room._wasClosedOnce = true;
        if (this.currentRoom === room) {
          this.rooms.push(room);
          this.currentRoom = { walls: [], doors: [], cutouts: [], type: this.roomType, closed: false };
        }
      }
      if (room && room._savedCutoutPolys) room._savedCutoutPolys = null;
      if (room && room._savedOuterWalls) room._savedOuterWalls = null;
    }
    this._wallEndpointDragging = false;
    this._wallEndpointFloating = false;
    this._wallEndpointDragInfo = null;
    this.canvas.style.cursor = 'default';
    this.updateMeasurements(); this.render();
  }

  _getWallOpenEndpoints(wall, room) { return RoomDesignerTools.getWallOpenEndpoints(wall, room); }

  _getWallsConnectedAt(wall, room, whichEnd) { return RoomDesignerTools.getWallsConnectedAt(wall, room, whichEnd); }

  _doorsOverlap(wall, room, newMinParam, newMaxParam, excludeDoor) { return RoomDesignerTools.doorsOverlap(wall, room, newMinParam, newMaxParam, excludeDoor); }

  // Check if a door is too close to a divider wall corner or T-junction
  _doorBlockedByConstraints(wall, minParam, maxParam, doorWidthFeet) {
    const wallLen = this.getWallLength(wall);
    if (wallLen < 0.01) return true;
    const buf = (4 / 12) / wallLen; // 4 inches in param space

    // Interior T-junctions from divider wall segments (adjust > 0)
    const breaks = this._getWallBreakPoints(wall);
    for (const bp of breaks) {
      if (bp.param < 0.01 || bp.param > 0.99) continue;
      if (bp.adjust <= 0) continue; // only divider wall junctions
      if (maxParam > bp.param - buf && minParam < bp.param + buf) return true;
    }

    // Endpoint corners: check if wall start/end connects to another divider wall
    const connectsToDivider = (pt) => {
      const allGroups = this.currentWallSegmentGroup
        ? [...this.wallSegmentGroups, this.currentWallSegmentGroup]
        : this.wallSegmentGroups;
      for (const grp of allGroups) {
        for (const seg of grp.segments) {
          if (seg === wall) continue;
          if (this.getDistance(pt, seg.start) < 0.05 || this.getDistance(pt, seg.end) < 0.05) return true;
        }
      }
      return false;
    };
    if (connectsToDivider(wall.start) && minParam < buf) return true;
    if (connectsToDivider(wall.end) && maxParam > 1 - buf) return true;

    return false;
  }

  _recomputeAllDoorParamsFromSnapshots(doorSnapshots) { RoomDesignerTools.recomputeAllDoorParamsFromSnapshots(doorSnapshots); }

  _getMinWallLengthForDoors(wall, room) { return RoomDesignerTools.getMinWallLengthForDoors(wall, room); }

  _isWallInCutout(wall) {
    for (const room of this.rooms) {
      if (room.cutouts) { for (const c of room.cutouts) { if (c.walls.includes(wall)) return true; } }
    }
    return false;
  }

  _isWallHiddenByCutout(wall, room) {
    if (!room || !room.cutouts || room.cutouts.length === 0) return false;
    const geo = this._getRoomGeoCache(room);
    const { cutoutPolys } = geo;
    if (!cutoutPolys || cutoutPolys.length === 0) return false;
    const mx = (wall.start.x + wall.end.x) / 2;
    const my = (wall.start.y + wall.end.y) / 2;
    for (const cp of cutoutPolys) {
      if (cp && cp.length >= 3 && this.pointInPolygon(mx, my, cp)) return true;
    }
    return false;
  }

  /* ── VISIBLE SEGMENT MATCHING FOR HANDLES ── */

  _getHandleSegmentsForWall(wall, room) {
    // Returns visible segment(s) for a wall after cutout clipping.
    // For rooms without cutouts, returns the wall itself as a segment.
    // For rooms with cutouts, matches against allVisibleWalls from the geo cache
    // by finding collinear, overlapping segments.
    if (!room || !room.cutouts || room.cutouts.length === 0) {
      return [{ start: { x: wall.start.x, y: wall.start.y }, end: { x: wall.end.x, y: wall.end.y } }];
    }
    const geo = this._getRoomGeoCache(room);
    const allVis = geo.allVisibleWalls;
    if (!allVis || allVis.length === 0) return [];
    const EPS = 0.05;
    const matched = [];
    const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
    const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
    if (wLen < EPS) return [];
    const udx = wdx / wLen, udy = wdy / wLen;
    for (const vs of allVis) {
      const vdx = vs.end.x - vs.start.x, vdy = vs.end.y - vs.start.y;
      const vLen = Math.sqrt(vdx * vdx + vdy * vdy);
      if (vLen < EPS) continue;
      // Check parallelism (cross product of directions)
      const cross = Math.abs(udx * vdy - udy * vdx);
      if (cross > EPS * vLen) continue;
      // Check perpendicular distance (are they on the same line?)
      const dx = vs.start.x - wall.start.x, dy = vs.start.y - wall.start.y;
      const perpDist = Math.abs(-udy * dx + udx * dy);
      if (perpDist > EPS) continue;
      // Check overlap: project vs onto wall's extent [0, wLen]
      const t1 = dx * udx + dy * udy;
      const t2 = (vs.end.x - wall.start.x) * udx + (vs.end.y - wall.start.y) * udy;
      const tMin = Math.min(t1, t2), tMax = Math.max(t1, t2);
      if (tMax < -EPS || tMin > wLen + EPS) continue;
      matched.push(vs);
    }
    return matched;
  }

  _mergeRoomAtEndpoint(pt, threshold) {
    const key = (p) => `${(Math.round(p.x * 1200) / 1200).toFixed(4)},${(Math.round(p.y * 1200) / 1200).toFixed(4)}`;
    const ptKey = key(pt);
    for (let i = this.rooms.length - 1; i >= 0; i--) {
      const room = this.rooms[i];
      if (room.closed || room.walls.length === 0) continue;
      const eps = this.getOpenEndpoints(room);
      for (const ep of eps) {
        if (key(ep) === ptKey || this.getDistance(pt, ep) < threshold) {
          // Snap the endpoint to match exactly
          room.walls.forEach(w => {
            if (this.getDistance(w.start, ep) < 0.001) { w.start.x = pt.x; w.start.y = pt.y; }
            if (this.getDistance(w.end, ep) < 0.001) { w.end.x = pt.x; w.end.y = pt.y; }
          });
          this.currentRoom.walls.push(...room.walls);
          this.currentRoom.doors.push(...room.doors);
          this.rooms.splice(i, 1);
          return true;
        }
      }
    }
    return false;
  }

  findNearestWall(point, threshold) {
    let nearestWall = null, nearestDist = Infinity, nearestRoom = null;
    [...this.rooms, this.currentRoom].forEach(room => {
      if (!room) return;
      const visWalls = (room.cutouts && room.cutouts.length > 0) ? this.getVisibleRoomWalls(room) : room.walls;
      visWalls.forEach(wall => { const dist = this.distanceToWall(point, wall); if (dist < nearestDist && dist < threshold) { nearestDist = dist; nearestWall = wall; nearestRoom = room; } });
      if (room.cutouts && room.cutouts.length > 0) {
        this.getInteriorCutoutWalls(room).forEach(({ wall }) => { const dist = this.distanceToWall(point, wall); if (dist < nearestDist && dist < threshold) { nearestDist = dist; nearestWall = wall; nearestRoom = room; } });
      }
    });
    // Also search wall segment groups — duck-types as room with doors[]
    // Skip line-type groups (no doors allowed on dividing lines)
    for (const group of this.wallSegmentGroups) {
      if (group.type === 'line') continue;
      for (const seg of group.segments) {
        const dist = this.distanceToWall(point, seg);
        if (dist < nearestDist && dist < threshold) { nearestDist = dist; nearestWall = seg; nearestRoom = group; }
      }
    }
    return nearestWall ? { wall: nearestWall, room: nearestRoom, dist: nearestDist } : null;
  }

  isRoomClosed(room) {
    if (!room || room.walls.length < 3) return false;
    const endpoints = this.getOpenEndpoints(room);
    if (endpoints.length === 0) return true;
    if (endpoints.length === 2) { const dist = this.getDistance(endpoints[0], endpoints[1]); if (dist < 1 / 12) { endpoints[1].x = endpoints[0].x; endpoints[1].y = endpoints[0].y; return true; } }
    return false;
  }

  distanceToWall(point, wall) { return RoomDesignerMath.distanceToWall(point, wall); }

  getWallPixelWidth(scale) {
    if (scale === undefined) scale = this.basePixelsPerFoot * this.zoomLevel;
    return Math.max(1.5, this.wallThickness * scale);
  }

  /* ── POLYGON OPERATIONS ── */

  buildRoomPolygon(room, closingStart, closingEnd) { return RoomDesignerMath.buildRoomPolygon(room, closingStart, closingEnd); }
  tracePolygon(walls) { return RoomDesignerMath.tracePolygon(walls); }
  pointInPolygon(px, py, polygon) { return RoomDesignerMath.pointInPolygon(px, py, polygon); }

  findVisualCenter(polygon, holes = []) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of polygon) { minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y); }
    const edges = [];
    for (let i = 0; i < polygon.length; i++) { const j = (i + 1) % polygon.length; edges.push({ x1: polygon[i].x, y1: polygon[i].y, x2: polygon[j].x, y2: polygon[j].y }); }
    const distToEdge = (px, py, e) => {
      const dx = e.x2 - e.x1, dy = e.y2 - e.y1, lenSq = dx * dx + dy * dy;
      if (lenSq === 0) return Math.sqrt((px - e.x1) ** 2 + (py - e.y1) ** 2);
      const t = Math.max(0, Math.min(1, ((px - e.x1) * dx + (py - e.y1) * dy) / lenSq));
      return Math.sqrt((px - (e.x1 + t * dx)) ** 2 + (py - (e.y1 + t * dy)) ** 2);
    };
    const allEdges = [...edges];
    for (const hole of holes) { for (let i = 0; i < hole.length; i++) { const j = (i + 1) % hole.length; allEdges.push({ x1: hole[i].x, y1: hole[i].y, x2: hole[j].x, y2: hole[j].y }); } }
    const minDistToEdges = (px, py) => { let min = Infinity; for (const e of allEdges) min = Math.min(min, distToEdge(px, py, e)); return min; };
    const pointInPoly = (px, py) => {
      let inside = false;
      for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i].x, yi = polygon[i].y, xj = polygon[j].x, yj = polygon[j].y;
        if ((yi > py) !== (yj > py) && px < (xj - xi) * (py - yi) / (yj - yi) + xi) inside = !inside;
      }
      if (!inside) return false;
      for (const hole of holes) { if (this.pointInPolygon(px, py, hole)) return false; }
      if (this._labelCutoutPolys) {
        for (const cp of this._labelCutoutPolys) { if (cp && cp.length >= 3 && this.pointInPolygon(px, py, cp)) return false; }
      }
      return true;
    };
    const width = maxX - minX, height = maxY - minY;
    let bestX = (minX + maxX) / 2, bestY = (minY + maxY) / 2, bestDist = -1;
    for (let yi = 0; yi <= 30; yi++) {
      const py = minY + (yi / 30) * height;
      const spans = []; let spanStart = null;
      for (let xi = 0; xi <= 60; xi++) {
        const px = minX + (xi / 60) * width; const inside = pointInPoly(px, py);
        if (inside && spanStart === null) spanStart = px;
        else if (!inside && spanStart !== null) { spans.push({ start: spanStart, end: px }); spanStart = null; }
      }
      if (spanStart !== null) spans.push({ start: spanStart, end: maxX });
      for (const span of spans) { const px = (span.start + span.end) / 2; if (!pointInPoly(px, py)) continue; const d = minDistToEdges(px, py); if (d > bestDist) { bestDist = d; bestX = px; bestY = py; } }
    }
    for (let xi = 0; xi <= 30; xi++) {
      const px = minX + (xi / 30) * width;
      const spans = []; let spanStart = null;
      for (let yi = 0; yi <= 60; yi++) {
        const py = minY + (yi / 60) * height; const inside = pointInPoly(px, py);
        if (inside && spanStart === null) spanStart = py;
        else if (!inside && spanStart !== null) { spans.push({ start: spanStart, end: py }); spanStart = null; }
      }
      if (spanStart !== null) spans.push({ start: spanStart, end: maxY });
      for (const span of spans) { const py = (span.start + span.end) / 2; if (!pointInPoly(px, py)) continue; const d = minDistToEdges(px, py); if (d > bestDist) { bestDist = d; bestX = px; bestY = py; } }
    }
    for (let pass = 0; pass < 3; pass++) {
      const shrink = Math.pow(0.3, pass + 1);
      const rangeX = width * shrink, rangeY = height * shrink;
      for (let gx = 0; gx <= 15; gx++) {
        for (let gy = 0; gy <= 15; gy++) {
          const px = bestX - rangeX + (gx / 15) * rangeX * 2;
          const py = bestY - rangeY + (gy / 15) * rangeY * 2;
          if (!pointInPoly(px, py)) continue;
          const d = minDistToEdges(px, py); if (d > bestDist) { bestDist = d; bestX = px; bestY = py; }
        }
      }
    }
    return { x: bestX, y: bestY };
  }

  /* ── SEGMENT INTERSECTION & CLIPPING (delegated to RoomDesignerMath) ── */

  _segmentsIntersect(s1, s2) { return RoomDesignerMath._segmentsIntersect(s1, s2); }
  _segmentsCollinearOverlap(s1, s2, eps) { return RoomDesignerMath._segmentsCollinearOverlap(s1, s2, eps); }
  _subtractOverlappingSegments(wall, cutoutWalls, eps) { return RoomDesignerMath._subtractOverlappingSegments(wall, cutoutWalls, eps); }
  _subtractInsideRoom(wall, boundaryWalls, eps) { return RoomDesignerMath._subtractInsideRoom(wall, boundaryWalls, eps); }
  _subtractInsidePolygon(wall, polygon, eps) { return RoomDesignerMath._subtractInsidePolygon(wall, polygon, eps); }
  _clipWallToRoomPoly(wall, roomPoly) { return RoomDesignerMath._clipWallToRoomPoly(wall, roomPoly); }

  /* ── CUTOUT / VISIBLE WALL HELPERS ── */

  getVisibleRoomWalls(room) {
    if (!room.cutouts || room.cutouts.length === 0) return [...room.walls];
    const EPS = 0.01;
    let result = room.walls.map(w => ({ start: { x: w.start.x, y: w.start.y }, end: { x: w.end.x, y: w.end.y } }));
    for (const cutout of room.cutouts) {
      if (cutout.isCleanup) continue;
      const cutoutPoly = this.tracePolygon(cutout.walls);
      let newResult = [];
      for (const rWall of result) {
        // Remove collinear overlapping portions (external wall under cutout edge)
        let segments = this._subtractOverlappingSegments(rWall, cutout.walls, EPS);
        // Remove segments strictly inside cutout polygon
        if (cutoutPoly && cutoutPoly.length >= 3) {
          let clipped = [];
          for (const seg of segments) clipped.push(...this._subtractInsidePolygon(seg, cutoutPoly, EPS));
          segments = clipped;
        }
        newResult.push(...segments);
      }
      result = newResult;
    }
    return result;
  }

  getInteriorCutoutWalls(room) {
    if (!room.cutouts || room.cutouts.length === 0) return [];
    const EPS = 0.01;
    const roomPoly = this.tracePolygon(room.walls);
    const interior = [];

    // Pre-compute all cutout polygons (skip cleanup cutouts for geometry)
    const cutoutPolys = room.cutouts.map(c => c.isCleanup ? null : this.tracePolygon(c.walls));

    for (let ci = 0; ci < room.cutouts.length; ci++) {
      const cutout = room.cutouts[ci];
      if (cutout.isCleanup) continue;
      const cp = cutoutPolys[ci];
      let cx = 0, cy = 0;
      if (cp && cp.length >= 3) { cp.forEach(p => { cx += p.x; cy += p.y; }); cx /= cp.length; cy /= cp.length; }

      for (const cWall of cutout.walls) {
        const wdx = cWall.end.x - cWall.start.x;
        const wdy = cWall.end.y - cWall.start.y;
        const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
        if (wLen < EPS) continue;

        // Collect all polygons to split against (room + other non-cleanup cutouts)
        const allPolys = [];
        if (roomPoly && roomPoly.length >= 3) allPolys.push(roomPoly);
        for (let cj = 0; cj < room.cutouts.length; cj++) {
          if (ci === cj || room.cutouts[cj].isCleanup) continue;
          if (cutoutPolys[cj] && cutoutPolys[cj].length >= 3) allPolys.push(cutoutPolys[cj]);
        }

        // Find all intersection t-values along this wall
        const splits = [];
        for (const poly of allPolys) {
          for (let pi = 0; pi < poly.length; pi++) {
            const pj = (pi + 1) % poly.length;
            const ex = poly[pj].x - poly[pi].x, ey = poly[pj].y - poly[pi].y;
            const denom = wdx * ey - wdy * ex;
            if (Math.abs(denom) < 1e-10) continue;
            const t = ((poly[pi].x - cWall.start.x) * ey - (poly[pi].y - cWall.start.y) * ex) / denom;
            const u = ((poly[pi].x - cWall.start.x) * wdy - (poly[pi].y - cWall.start.y) * wdx) / denom;
            if (t > 0.001 && t < 0.999 && u >= -0.001 && u <= 1.001) splits.push(t);
          }
        }

        // Also inject split points from collinear room walls and other non-cleanup cutout walls
        const allOtherWalls = [...room.walls];
        for (let cj = 0; cj < room.cutouts.length; cj++) {
          if (ci !== cj && !room.cutouts[cj].isCleanup) allOtherWalls.push(...room.cutouts[cj].walls);
        }
        for (const ow of allOtherWalls) {
          const odx = ow.end.x - ow.start.x, ody = ow.end.y - ow.start.y;
          const oLen = Math.sqrt(odx * odx + ody * ody);
          if (oLen < EPS) continue;
          const cross1 = Math.abs((cWall.start.x - ow.start.x) * ody - (cWall.start.y - ow.start.y) * odx) / oLen;
          const cross2 = Math.abs((cWall.end.x - ow.start.x) * ody - (cWall.end.y - ow.start.y) * odx) / oLen;
          if (cross1 < EPS * 3 && cross2 < EPS * 3) {
            const t1 = ((ow.start.x - cWall.start.x) * wdx + (ow.start.y - cWall.start.y) * wdy) / (wLen * wLen);
            const t2 = ((ow.end.x - cWall.start.x) * wdx + (ow.end.y - cWall.start.y) * wdy) / (wLen * wLen);
            if (t1 > 0.001 && t1 < 0.999) splits.push(t1);
            if (t2 > 0.001 && t2 < 0.999) splits.push(t2);
          }
        }

        splits.sort((a, b) => a - b);
        const allT = [0];
        for (const t of splits) { if (t - allT[allT.length - 1] > 0.001) allT.push(t); }
        allT.push(1);

        for (let i = 0; i < allT.length - 1; i++) {
          const tStart = allT[i], tEnd = allT[i + 1];
          if (tEnd - tStart < 0.001) continue;
          const tMid = (tStart + tEnd) / 2;
          const midX = cWall.start.x + tMid * wdx;
          const midY = cWall.start.y + tMid * wdy;

          // Check: is midpoint in filled area?
          // Must be inside room poly (or on boundary)
          const inRoom = roomPoly && roomPoly.length >= 3 ? this.pointInPolygon(midX, midY, roomPoly) : false;
          let onRoomBoundary = false;
          if (!inRoom && roomPoly) {
            for (const rw of room.walls) {
              const rdx = rw.end.x - rw.start.x, rdy = rw.end.y - rw.start.y;
              const rLen = Math.sqrt(rdx * rdx + rdy * rdy);
              if (rLen < EPS) continue;
              const dist = Math.abs((midX - rw.start.x) * rdy - (midY - rw.start.y) * rdx) / rLen;
              if (dist < EPS * 3) {
                const t = ((midX - rw.start.x) * rdx + (midY - rw.start.y) * rdy) / (rLen * rLen);
                if (t >= -0.01 && t <= 1.01) { onRoomBoundary = true; break; }
              }
            }
          }

          if (inRoom || onRoomBoundary) {
            const seg = {
              start: { x: cWall.start.x + tStart * wdx, y: cWall.start.y + tStart * wdy },
              end: { x: cWall.start.x + tEnd * wdx, y: cWall.start.y + tEnd * wdy }
            };
            const len = Math.sqrt((seg.end.x - seg.start.x) ** 2 + (seg.end.y - seg.start.y) ** 2);
            if (len <= EPS) continue;

            // Check: does this wall border filled area on at least one side?
            const nx = -wdy / wLen;
            const ny = wdx / wLen;
            const offsetDist = 0.25;
            const testPts = [
              { x: midX + nx * offsetDist, y: midY + ny * offsetDist },
              { x: midX - nx * offsetDist, y: midY - ny * offsetDist }
            ];
            let hasFillSide = false;
            for (const tp of testPts) {
              const tpInRoom = roomPoly && roomPoly.length >= 3 ? this.pointInPolygon(tp.x, tp.y, roomPoly) : false;
              if (!tpInRoom) continue;
              let tpInAnyCutout = false;
              for (let ck = 0; ck < room.cutouts.length; ck++) {
                if (cutoutPolys[ck] && cutoutPolys[ck].length >= 3 && this.pointInPolygon(tp.x, tp.y, cutoutPolys[ck])) {
                  tpInAnyCutout = true; break;
                }
              }
              if (!tpInAnyCutout) { hasFillSide = true; break; }
            }

            if (hasFillSide) {
              interior.push({ wall: seg, cutoutCenter: { x: cx, y: cy } });
            }
          }
        }
      }
    }
    return interior;
  }

  /* ── CONNECTED COMPONENTS (delegated to RoomDesignerMath) ── */

  _getConnectedComponents(walls) { return RoomDesignerMath._getConnectedComponents(walls); }
  _removeCoincidentWalls(walls, eps) { return RoomDesignerMath._removeCoincidentWalls(walls, eps); }

  _validateRoomIntegrity(room) {
    if (!room.closed || room.walls.length < 3) return false;
    const poly = this.tracePolygon(room.walls);
    if (!poly || poly.length < 3) return false;
    // Shoelace area check — reject degenerate rooms
    let area = 0;
    for (let i = 0; i < poly.length; i++) {
      const j = (i + 1) % poly.length;
      area += poly[i].x * poly[j].y - poly[j].x * poly[i].y;
    }
    if (Math.abs(area) / 2 < 0.5) return false;
    // Wall connectivity — detect "explosion" where walls fragment into many tiny components
    try {
      const vis = this.getVisibleRoomWalls(room);
      const intData = this.getInteriorCutoutWalls(room);
      const allW = [...vis, ...intData.map(d => d.wall)];
      if (allW.length === 0) return false;
      const components = this._getConnectedComponents(allW);
      if (components.length <= 1) return true;
      let maxLen = 0;
      for (const c of components) if (c.length > maxLen) maxLen = c.length;
      if (maxLen < allW.length * 0.5) return false;
    } catch (e) { return false; }
    return true;
  }

  _removeIslands(room) {
    if (!room.cutouts || room.cutouts.length === 0) return;
    const vw = this.getVisibleRoomWalls(room);
    const icData = this.getInteriorCutoutWalls(room);
    const allW = [...vw, ...icData.map(d => d.wall)];
    const dedupedW = this._removeCoincidentWalls(allW, 0.02);
    const components = this._getConnectedComponents(dedupedW);
    if (components.length <= 1) return;
    let largestIdx = 0, largestArea = 0;
    for (let c = 0; c < components.length; c++) {
      let mnX = Infinity, mnY = Infinity, mxX = -Infinity, mxY = -Infinity;
      for (const w of components[c]) { mnX = Math.min(mnX, w.start.x, w.end.x); mnY = Math.min(mnY, w.start.y, w.end.y); mxX = Math.max(mxX, w.start.x, w.end.x); mxY = Math.max(mxY, w.start.y, w.end.y); }
      const area = (mxX - mnX) * (mxY - mnY);
      if (area > largestArea) { largestArea = area; largestIdx = c; }
    }
    const roomPoly = this.tracePolygon(room.walls);
    if (!roomPoly || roomPoly.length < 3) return;
    room.cutouts = [];
    for (let c = 0; c < components.length; c++) {
      if (c === largestIdx) continue;
      const poly = this.tracePolygon(components[c]);
      if (!poly || poly.length < 3) {
        const pts = components[c].map(w => w.start);
        if (pts.length >= 3) room.cutouts.push({ walls: components[c], closed: true });
        continue;
      }
      let cx = 0, cy = 0;
      poly.forEach(p => { cx += p.x; cy += p.y; }); cx /= poly.length; cy /= poly.length;
      if (this.pointInPolygon(cx, cy, roomPoly)) {
        room.cutouts.push({ walls: components[c], closed: true });
      }
    }
  }

  _getLargestComponent(walls, roomPoly) {
    const components = this._getConnectedComponents(walls);
    if (components.length <= 1) return walls;
    let largestIdx = 0, largestSize = 0;
    for (let c = 0; c < components.length; c++) {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const w of components[c]) { minX = Math.min(minX, w.start.x, w.end.x); minY = Math.min(minY, w.start.y, w.end.y); maxX = Math.max(maxX, w.start.x, w.end.x); maxY = Math.max(maxY, w.start.y, w.end.y); }
      const size = (maxX - minX) * (maxY - minY); if (size > largestSize) { largestSize = size; largestIdx = c; }
    }
    const largestPoly = this.tracePolygon(components[largestIdx]);
    const result = [...components[largestIdx]];
    if (largestPoly && largestPoly.length >= 3) {
      for (let c = 0; c < components.length; c++) {
        if (c === largestIdx) continue;
        let cx = 0, cy = 0, count = 0;
        for (const w of components[c]) { cx += w.start.x + w.end.x; cy += w.start.y + w.end.y; count += 2; }
        cx /= count; cy /= count;
        if (this.pointInPolygon(cx, cy, largestPoly)) result.push(...components[c]);
      }
    }
    return result;
  }

  /* ── ROOM FINALIZATION ── */

  _mergeOverlappingCutouts(parent) {
    if (!parent.cutouts || parent.cutouts.length < 2) return;
    const EPS = 0.01;
    let changed = true;
    while (changed) {
      changed = false;
      for (let i = 0; i < parent.cutouts.length && !changed; i++) {
        for (let j = i + 1; j < parent.cutouts.length && !changed; j++) {
          const pi = this.tracePolygon(parent.cutouts[i].walls);
          const pj = this.tracePolygon(parent.cutouts[j].walls);
          if (!pi || pi.length < 3 || !pj || pj.length < 3) continue;
          let overlap = false;
          if (pi.some(p => this.pointInPolygon(p.x, p.y, pj))) overlap = true;
          if (!overlap && pj.some(p => this.pointInPolygon(p.x, p.y, pi))) overlap = true;
          if (!overlap) { for (const wi of parent.cutouts[i].walls) { for (const wj of parent.cutouts[j].walls) { if (this._segmentsIntersect(wi, wj) || this._segmentsCollinearOverlap(wi, wj, 0.05)) { overlap = true; break; } } if (overlap) break; } }
          if (overlap) {
            const wallsI = parent.cutouts[i].walls;
            const wallsJ = parent.cutouts[j].walls;
            let survivingI = [];
            for (const w of wallsI) { let segs = this._subtractOverlappingSegments(w, wallsJ, EPS); let filtered = []; for (const s of segs) filtered.push(...this._subtractInsidePolygon(s, pj, EPS)); survivingI.push(...filtered); }
            let survivingJ = [];
            for (const w of wallsJ) { let segs = this._subtractOverlappingSegments(w, wallsI, EPS); let filtered = []; for (const s of segs) filtered.push(...this._subtractInsidePolygon(s, pi, EPS)); survivingJ.push(...filtered); }
            const mergedWalls = [...survivingI, ...survivingJ].filter(w => Math.sqrt((w.end.x - w.start.x) ** 2 + (w.end.y - w.start.y) ** 2) > 0.02);
            parent.cutouts[i] = { walls: mergedWalls, closed: true };
            parent.cutouts.splice(j, 1);
            const temp = { walls: parent.cutouts[i].walls }; this.mergeCollinearWalls(temp); parent.cutouts[i].walls = temp.walls;
            changed = true;
          }
        }
      }
    }
  }

  findOverlappingRooms(cutoutRoom) {
    const cutoutPoly = this.tracePolygon(cutoutRoom.walls);
    if (!cutoutPoly || cutoutPoly.length < 3) return [];
    const overlapping = [];
    for (const room of this.rooms) {
      if (!room.closed || room.walls.length < 3) continue;
      const roomPoly = this.tracePolygon(room.walls);
      if (!roomPoly || roomPoly.length < 3) continue;

      let found = false;

      // Check if any cutout point is inside room
      for (const p of cutoutPoly) {
        if (this.pointInPolygon(p.x, p.y, roomPoly)) { found = true; break; }
      }

      // Check if any room point is inside cutout
      if (!found) {
        for (const p of roomPoly) {
          if (this.pointInPolygon(p.x, p.y, cutoutPoly)) { found = true; break; }
        }
      }

      // Check if any walls intersect
      if (!found) {
        for (const cw of cutoutRoom.walls) {
          for (const rw of room.walls) {
            if (this._segmentsIntersect(cw, rw)) { found = true; break; }
          }
          if (found) break;
        }
      }

      if (found) overlapping.push(room);
    }
    return overlapping;
  }

  finalizeClosedRoom(room) {
    if (room.type === 'subtract') {
      const overlapping = this.findOverlappingRooms(room);
      if (overlapping.length === 0) { this.updateStatus('Cutout must overlap an existing room.', { error: true }); return false; }
      if (overlapping.length > 1) { this.updateStatus('Can only subtract from one room at a time.', { error: true }); return false; }
      const parent = overlapping[0];
      if (!parent.cutouts) parent.cutouts = [];

      // Prefer saved cutout polygons over re-separation (avoids precision drift)
      if (parent._savedCutoutPolys && parent._savedCutoutPolys.length > 0) {
        // Reconstruct cutouts from saved precise polygon vertices
        for (const poly of parent._savedCutoutPolys) {
          const walls = [];
          for (let i = 0; i < poly.length; i++) {
            const j = (i + 1) % poly.length;
            walls.push({ start: {x: poly[i].x, y: poly[i].y}, end: {x: poly[j].x, y: poly[j].y} });
          }
          parent.cutouts.push({ walls, closed: true });
        }
        parent._savedCutoutPolys = null;

        // Restore outer boundary walls so rebuild doesn't double-subtract
        // (parent.walls is currently the merged L-shape outline)
        if (parent._savedOuterWalls) {
          parent.walls = parent._savedOuterWalls;
          parent._savedOuterWalls = null;
        }
      } else {
        // Fallback: re-separate embedded holes (for rooms without saved polygon data,
        // e.g. after wall drag invalidation or legacy undo states)
        const _parentComps = this._getConnectedComponents(parent.walls);
        if (_parentComps.length > 1) {
          let _outerIdx = 0, _outerSize = 0;
          for (let ci = 0; ci < _parentComps.length; ci++) {
            let mnX = Infinity, mnY = Infinity, mxX = -Infinity, mxY = -Infinity;
            for (const w of _parentComps[ci]) {
              mnX = Math.min(mnX, w.start.x, w.end.x); mnY = Math.min(mnY, w.start.y, w.end.y);
              mxX = Math.max(mxX, w.start.x, w.end.x); mxY = Math.max(mxY, w.start.y, w.end.y);
            }
            const sz = (mxX - mnX) * (mxY - mnY);
            if (sz > _outerSize) { _outerSize = sz; _outerIdx = ci; }
          }
          const _outerPoly = this.tracePolygon(_parentComps[_outerIdx]);
          parent.walls = _parentComps[_outerIdx];
          for (let ci = 0; ci < _parentComps.length; ci++) {
            if (ci === _outerIdx) continue;
            let cx = 0, cy = 0, cnt = 0;
            for (const w of _parentComps[ci]) {
              cx += w.start.x + w.end.x; cy += w.start.y + w.end.y; cnt += 2;
            }
            cx /= cnt; cy /= cnt;
            if (_outerPoly && this.pointInPolygon(cx, cy, _outerPoly))
              parent.cutouts.push({ walls: _parentComps[ci], closed: true });
          }
        }
      }
      const newPoly = this.tracePolygon(room.walls);
      if (!newPoly || newPoly.length < 3) {
        parent.cutouts.push({ walls: room.walls, closed: true });
        this._rebuildRoomAfterSubtract(parent);
        if (!this._validateRoomIntegrity(parent)) {
          this.undo(); this.redoStack.pop();
          this.updateStatus('Cannot place cutout here — it would break the room geometry. Try a different position or size.', { error: true });
          return false;
        }
        this.updateStatus('Cutout placed!'); return true;
      }
      for (const existing of parent.cutouts) {
        if (existing.isCleanup) continue;
        const existingPoly = this.tracePolygon(existing.walls);
        if (!existingPoly || existingPoly.length < 3) continue;
        if (newPoly.every(p => this.pointInPolygon(p.x, p.y, existingPoly))) { this.updateStatus('Cutout is entirely inside an existing hole.', { error: true }); return false; }
      }
      // Remove stale cleanup cutouts (they'll be recomputed below)
      parent.cutouts = parent.cutouts.filter(c => !c.isCleanup);
      parent.cutouts.push({ walls: room.walls, closed: true });

      // Option B: Rebuild the room from its visible outline so all walls
      // are first-class (draggable, correct handles, accurate sq ft).
      const removedCount = this._rebuildRoomAfterSubtract(parent);

      // Guard rail: validate geometry after subtract — undo if broken
      if (!this._validateRoomIntegrity(parent)) {
        this.undo(); this.redoStack.pop();
        this.updateStatus('Cannot place cutout here — it would break the room geometry. Try a different position or size.', { error: true });
        return false;
      }

      if (removedCount > 0) {
        const msg = removedCount === 1 ? 'Detached section removed.' : `${removedCount} detached sections removed.`;
        this.updateStatus('Area subtracted — ' + msg);
      } else {
        this.updateStatus('Area subtracted from ' + (parent.name || 'room') + '.');
      }
      return true;
      }

    // Add mode
    const newPoly = this.tracePolygon(room.walls);
    if (!newPoly || newPoly.length < 3) { this.rooms.push(room); this.updateStatus('Space complete!'); return true; }

    // Check if new room's center is inside any cutout hole — if so, fill the cutout
    // instead of creating a stacked room. An "add" over a hole should remove/shrink the hole.
    { let ncx = 0, ncy = 0; newPoly.forEach(p => { ncx += p.x; ncy += p.y; }); ncx /= newPoly.length; ncy /= newPoly.length;
      for (const existing of this.rooms) {
        if (!existing.closed || !existing.cutouts || existing.cutouts.length === 0) continue;
        const ep = this.tracePolygon(existing.walls);
        if (!ep || ep.length < 3) continue;
        if (!this.pointInPolygon(ncx, ncy, ep)) continue;
        for (let ci = 0; ci < existing.cutouts.length; ci++) {
          const cutout = existing.cutouts[ci];
          const cp = this.tracePolygon(cutout.walls);
          if (!cp || cp.length < 3 || !this.pointInPolygon(ncx, ncy, cp)) continue;
          // Cleanup cutouts represent deleted split fragments — always add as separate room
          if (cutout.isCleanup) { this.rooms.push(room); this.updateStatus('Space added.'); return true; }

          // The add shape overlaps this cutout — fill the hole instead of creating a stacked room.
          this.saveUndoState();
          const _EPS = 0.01;

          // Check if the add shape completely covers the cutout
          const allCovered = cp.every(p => {
            if (this.pointInPolygon(p.x, p.y, newPoly)) return true;
            for (const aw of room.walls) { if (this.distanceToWall(p, aw) < 0.15) return true; }
            return false;
          });

          if (allCovered) {
            // Add fully covers the cutout — remove it and any matching cleanup cutouts
            existing.cutouts.splice(ci, 1);
            // Also remove cleanup cutouts that overlap this same area
            for (let ki = existing.cutouts.length - 1; ki >= 0; ki--) {
              if (!existing.cutouts[ki].isCleanup) continue;
              const kp = this.tracePolygon(existing.cutouts[ki].walls);
              if (!kp || kp.length < 3) { existing.cutouts.splice(ki, 1); continue; }
              // If the cleanup cutout overlaps the filled area, remove it
              const kCovered = kp.every(p => {
                if (this.pointInPolygon(p.x, p.y, newPoly)) return true;
                for (const aw of room.walls) { if (this.distanceToWall(p, aw) < 0.15) return true; }
                return false;
              });
              if (kCovered) existing.cutouts.splice(ki, 1);
            }
            delete existing._geoCache;
            existing._zoneCache = null;
            // Guard rail: validate geometry — undo if broken
            if (!this._validateRoomIntegrity(existing)) {
              this.undo(); this.redoStack.pop();
              this.updateStatus('Cannot fill this cutout — it would break the room geometry.', { error: true });
              return false;
            }
            this.render(); this.updateMeasurements();
            this.updateStatus('Cutout filled — hole removed.');
            return true;
          }

          // Partial coverage — use polygon subtraction (handles arbitrary cutout shapes)
          // Compute remaining hole = cutout minus add-on
          let survivingCutoutWalls = [];
          for (const w of cutout.walls) {
            let segs = this._subtractOverlappingSegments(w, room.walls, _EPS);
            let filtered = [];
            for (const s of segs) filtered.push(...this._subtractInsidePolygon(s, newPoly, _EPS));
            survivingCutoutWalls.push(...filtered);
          }
          // Add-on walls inside cutout become remaining hole boundary
          let addWallsInCutout = [];
          for (const w of room.walls) {
            let segs = this._subtractOverlappingSegments(w, cutout.walls, _EPS);
            for (const s of segs) addWallsInCutout.push(...this._clipWallToRoomPoly(s, cp));
          }
          let remainingWalls = [...survivingCutoutWalls, ...addWallsInCutout];
          remainingWalls = remainingWalls.filter(w =>
            Math.sqrt((w.end.x - w.start.x) ** 2 + (w.end.y - w.start.y) ** 2) > 0.02);

          // Deduplicate near-collinear overlapping walls (epsilon mismatch can produce
          // both a surviving cutout wall and a clipped add-on wall at the same location)
          {
            const DEDUP_EPS = 0.05;
            const markRemove = new Set();
            for (let i = 0; i < remainingWalls.length; i++) {
              if (markRemove.has(i)) continue;
              const wi = remainingWalls[i];
              const dxi = wi.end.x - wi.start.x, dyi = wi.end.y - wi.start.y;
              const leni = Math.sqrt(dxi * dxi + dyi * dyi);
              if (leni < 0.01) continue;
              for (let j = i + 1; j < remainingWalls.length; j++) {
                if (markRemove.has(j)) continue;
                const wj = remainingWalls[j];
                const dxj = wj.end.x - wj.start.x, dyj = wj.end.y - wj.start.y;
                const lenj = Math.sqrt(dxj * dxj + dyj * dyj);
                if (lenj < 0.01) continue;
                const cross1 = Math.abs((wj.start.x - wi.start.x) * dyi - (wj.start.y - wi.start.y) * dxi) / leni;
                const cross2 = Math.abs((wj.end.x - wi.start.x) * dyi - (wj.end.y - wi.start.y) * dxi) / leni;
                if (cross1 > DEDUP_EPS || cross2 > DEDUP_EPS) continue;
                const t1 = ((wj.start.x - wi.start.x) * dxi + (wj.start.y - wi.start.y) * dyi) / (leni * leni);
                const t2 = ((wj.end.x - wi.start.x) * dxi + (wj.end.y - wi.start.y) * dyi) / (leni * leni);
                const tMin = Math.max(0, Math.min(t1, t2));
                const tMax = Math.min(1, Math.max(t1, t2));
                const overlap = (tMax - tMin) * leni;
                if (overlap > Math.min(leni, lenj) * 0.5) {
                  markRemove.add(leni >= lenj ? j : i);
                  if (markRemove.has(i)) break;
                }
              }
            }
            if (markRemove.size > 0)
              remainingWalls = remainingWalls.filter((_, idx) => !markRemove.has(idx));
          }

          // Snap remainingWalls endpoints to room wall geometry — prevents near-collinear
          // duplicates between cutout walls and room walls during rendering
          // (clipping produces endpoints off by 0.01-0.03 ft from room wall lines)
          {
            const SNAP_EPS = 0.05;
            const roomVerts = [];
            for (const rw of existing.walls) roomVerts.push(rw.start, rw.end);
            for (const w of remainingWalls) {
              for (const pt of [w.start, w.end]) {
                // 1. Vertex snap — snap to nearest room wall endpoint
                let bestVD = SNAP_EPS, bestVX = pt.x, bestVY = pt.y, vertSnapped = false;
                for (const v of roomVerts) {
                  const d = Math.sqrt((pt.x - v.x) ** 2 + (pt.y - v.y) ** 2);
                  if (d < bestVD && d > 0.001) { bestVD = d; bestVX = v.x; bestVY = v.y; }
                }
                if (bestVD < SNAP_EPS) { pt.x = bestVX; pt.y = bestVY; vertSnapped = true; }
                if (vertSnapped) continue;
                // 2. Line snap — project onto nearest room wall line
                let bestLD = SNAP_EPS, bestLX = pt.x, bestLY = pt.y;
                for (const rw of existing.walls) {
                  const rdx = rw.end.x - rw.start.x, rdy = rw.end.y - rw.start.y;
                  const rLen = Math.sqrt(rdx * rdx + rdy * rdy);
                  if (rLen < 0.01) continue;
                  const dist = Math.abs((pt.x - rw.start.x) * rdy - (pt.y - rw.start.y) * rdx) / rLen;
                  if (dist >= bestLD || dist < 0.001) continue;
                  const t = ((pt.x - rw.start.x) * rdx + (pt.y - rw.start.y) * rdy) / (rLen * rLen);
                  if (t < -0.01 || t > 1.01) continue;
                  bestLD = dist;
                  const tc = Math.max(0, Math.min(1, t));
                  bestLX = rw.start.x + tc * rdx;
                  bestLY = rw.start.y + tc * rdy;
                }
                if (bestLD < SNAP_EPS) { pt.x = bestLX; pt.y = bestLY; }
              }
            }
          }

          existing.cutouts.splice(ci, 1); // remove old cutout
          if (remainingWalls.length >= 3) {
            const components = this._getConnectedComponents(remainingWalls);
            for (const comp of components) {
              if (comp.length < 3) continue;
              const temp = { walls: comp };
              this.mergeCollinearWalls(temp);
              existing.cutouts.splice(ci, 0, { walls: temp.walls, closed: true });
              ci++;
            }
          }

          // Also remove cleanup cutouts that overlap the filled area
          for (let ki = existing.cutouts.length - 1; ki >= 0; ki--) {
            if (!existing.cutouts[ki].isCleanup) continue;
            const kp = this.tracePolygon(existing.cutouts[ki].walls);
            if (!kp || kp.length < 3) { existing.cutouts.splice(ki, 1); continue; }
            const kCovered = kp.every(p => {
              if (this.pointInPolygon(p.x, p.y, newPoly)) return true;
              for (const aw of room.walls) { if (this.distanceToWall(p, aw) < 0.15) return true; }
              return false;
            });
            if (kCovered) existing.cutouts.splice(ki, 1);
          }
          delete existing._geoCache;
          existing._zoneCache = null;
          // Guard rail: validate geometry after partial add-on — undo if broken
          if (!this._validateRoomIntegrity(existing)) {
            this.undo(); this.redoStack.pop();
            this.updateStatus('Cannot place add-on here — it would break the room geometry. Try a different position or size.', { error: true });
            return false;
          }
          this.render(); this.updateMeasurements();
          this.updateStatus('Cutout partially filled.');
          return true;
        }
      }
    }

    const mergeTargets = [];
    for (const existing of this.rooms) {
      if (!existing.closed || existing.walls.length < 3 || existing.type === 'subtract') continue;
      const existingPoly = this.tracePolygon(existing.walls);
      if (!existingPoly || existingPoly.length < 3) continue;
      const isInFilledArea = (px, py, rm, rmPoly) => {
        if (!this.pointInPolygon(px, py, rmPoly)) return false;
        if (rm.cutouts && rm.cutouts.length > 0) {
          for (const cutout of rm.cutouts) {
            const cp = this.tracePolygon(cutout.walls);
            if (cp && cp.length >= 3 && this.pointInPolygon(px, py, cp)) return false;
          }
        }
        // Also exclude interior holes (from Option B rebuild — walls with no cutouts)
        const rmGeo = this._getRoomGeoCache(rm);
        if (rmGeo.holePolygons) {
          for (const hp of rmGeo.holePolygons) {
            if (this.pointInPolygon(px, py, hp)) return false;
          }
        }
        return true;
      };
      const newInExisting = (() => { let cx = 0, cy = 0; newPoly.forEach(p => { cx += p.x; cy += p.y; }); cx /= newPoly.length; cy /= newPoly.length; return isInFilledArea(cx, cy, existing, existingPoly); })();
      const existingInNew = (() => { let cx = 0, cy = 0; existingPoly.forEach(p => { cx += p.x; cy += p.y; }); cx /= existingPoly.length; cy /= existingPoly.length; return this.pointInPolygon(cx, cy, newPoly); })();
      let wallsIntersect = false;
      let sharesWall = false;
      for (const nw of room.walls) { for (const ew of existing.walls) { if (this._segmentsIntersect(nw, ew)) { wallsIntersect = true; break; } if (!sharesWall && this._segmentsCollinearOverlap(nw, ew, 0.05)) sharesWall = true; } if (wallsIntersect) break; }
      if (!sharesWall && existing.cutouts) { for (const cutout of existing.cutouts) { if (sharesWall) break; if (cutout.isCleanup) continue; for (const nw of room.walls) { for (const cw of cutout.walls) { if (this._segmentsCollinearOverlap(nw, cw, 0.05)) { sharesWall = true; break; } } if (sharesWall) break; } } }
      let newCentroidInExisting = false, existingCentroidInNew = false;
      { let cx = 0, cy = 0; newPoly.forEach(p => { cx += p.x; cy += p.y; }); cx /= newPoly.length; cy /= newPoly.length; newCentroidInExisting = isInFilledArea(cx, cy, existing, existingPoly); }
      { let cx = 0, cy = 0; existingPoly.forEach(p => { cx += p.x; cy += p.y; }); cx /= existingPoly.length; cy /= existingPoly.length; existingCentroidInNew = this.pointInPolygon(cx, cy, newPoly); }
      // Check if any wall midpoint of one room is inside the other — detects overlap
      // when walls share endpoints but don't have collinear segments or proper crossings
      let wallMidpointInside = false;
      if (!wallsIntersect && !sharesWall) {
        for (const nw of room.walls) {
          const mx = (nw.start.x + nw.end.x) / 2, my = (nw.start.y + nw.end.y) / 2;
          if (isInFilledArea(mx, my, existing, existingPoly)) { wallMidpointInside = true; break; }
        }
        if (!wallMidpointInside) {
          for (const ew of existing.walls) {
            const mx = (ew.start.x + ew.end.x) / 2, my = (ew.start.y + ew.end.y) / 2;
            if (this.pointInPolygon(mx, my, newPoly)) { wallMidpointInside = true; break; }
          }
        }
      }
      const startedFromThisRoom = this._trimmedSnapRoom === existing;
      // Proximity check: if a new room wall is very close to and parallel with
      // an existing room wall, snap the new wall to touch and treat as merge.
      // This handles bridges drawn with a different snap grid than the rooms.
      let wallsNearby = false;
      if (!wallsIntersect && !sharesWall && !wallMidpointInside) {
        const PROX = 1/6; // ~2 inches — only catch floating-point misalignment, not intentional gaps
        // Check both outer walls and cutout walls (bridges near holes should merge)
        // Skip cleanup cutout walls — those mark deleted split fragments, not real holes
        const proxWalls = [...existing.walls];
        if (existing.cutouts) for (const c of existing.cutouts) { if (!c.isCleanup) proxWalls.push(...c.walls); }
        for (const nw of room.walls) {
          if (wallsNearby) break;
          const ndx = nw.end.x - nw.start.x, ndy = nw.end.y - nw.start.y;
          const nLen = Math.sqrt(ndx * ndx + ndy * ndy);
          if (nLen < 0.1) continue;
          for (const ew of proxWalls) {
            const edx = ew.end.x - ew.start.x, edy = ew.end.y - ew.start.y;
            const eLen = Math.sqrt(edx * edx + edy * edy);
            if (eLen < 0.1) continue;
            // Check if walls are roughly parallel (cross product near 0)
            const cross = Math.abs(ndx * edy - ndy * edx) / (nLen * eLen);
            if (cross > 0.1) continue; // not parallel
            // Perpendicular distance between lines
            const perpDist = Math.abs((nw.start.x - ew.start.x) * (-edy / eLen) + (nw.start.y - ew.start.y) * (edx / eLen));
            if (perpDist > PROX || perpDist < 0.01) continue; // too far or already touching
            // Check overlap in the parallel direction
            const t1 = ((nw.start.x - ew.start.x) * edx + (nw.start.y - ew.start.y) * edy) / (eLen * eLen);
            const t2 = ((nw.end.x - ew.start.x) * edx + (nw.end.y - ew.start.y) * edy) / (eLen * eLen);
            const tMin = Math.min(t1, t2), tMax = Math.max(t1, t2);
            if (tMax < 0.01 || tMin > 0.99) continue; // no overlap in parallel direction
            wallsNearby = true;
            // Snap the new wall to touch the existing wall
            const nnx = -ndy / nLen, nny = ndx / nLen; // normal to new wall
            const shift = (ew.start.x - nw.start.x) * nnx + (ew.start.y - nw.start.y) * nny;
            if (Math.abs(shift) > 0.01 && Math.abs(shift) <= PROX) {
              const oldSx = nw.start.x, oldSy = nw.start.y;
              const oldEx = nw.end.x, oldEy = nw.end.y;
              nw.start.x += nnx * shift; nw.start.y += nny * shift;
              nw.end.x += nnx * shift; nw.end.y += nny * shift;
              // Reconnect adjacent walls to maintain room closure
              for (const ow of room.walls) {
                if (ow === nw) continue;
                if (Math.abs(ow.end.x - oldSx) < 0.02 && Math.abs(ow.end.y - oldSy) < 0.02) { ow.end.x = nw.start.x; ow.end.y = nw.start.y; }
                if (Math.abs(ow.start.x - oldEx) < 0.02 && Math.abs(ow.start.y - oldEy) < 0.02) { ow.start.x = nw.end.x; ow.start.y = nw.end.y; }
              }
            }
            break;
          }
        }
      }
      if (newCentroidInExisting || existingCentroidInNew || wallsIntersect || sharesWall || startedFromThisRoom || wallMidpointInside || wallsNearby) {
        const allInsideOuter = newPoly.every(p => this.pointInPolygon(p.x, p.y, existingPoly));
if (allInsideOuter) {
  // Check if the add room is inside an interior hole — if so, add as standalone room
  // (the parent already deducts the hole from sq ft; the standalone room adds its own)
  const _existGeo = this._getRoomGeoCache(existing);
  if (_existGeo.holePolygons && _existGeo.holePolygons.length > 0) {
    let _ncx = 0, _ncy = 0; newPoly.forEach(p => { _ncx += p.x; _ncy += p.y; }); _ncx /= newPoly.length; _ncy /= newPoly.length;
    for (const hp of _existGeo.holePolygons) {
      if (this.pointInPolygon(_ncx, _ncy, hp)) {
        // Check if room also extends into filled area (spans hole + filled → should merge, not standalone)
        let _alsoInFilled = false;
        for (const p of newPoly) {
          if (isInFilledArea(p.x, p.y, existing, existingPoly)) { _alsoInFilled = true; break; }
        }
        if (!_alsoInFilled) {
          for (const w of room.walls) {
            const mx = (w.start.x + w.end.x) / 2, my = (w.start.y + w.end.y) / 2;
            if (isInFilledArea(mx, my, existing, existingPoly)) { _alsoInFilled = true; break; }
          }
        }
        if (!_alsoInFilled) {
          this.rooms.push(room); this.updateStatus('Space added inside hole.'); return true;
        }
        break; // Spans both hole + filled → fall through to merge
      }
    }
  }
  let insideCutout = false, crossesCutoutWall = false;
  let entirelyInsideCutout = false;
  if (existing.cutouts && existing.cutouts.length > 0) {
    for (const cutout of existing.cutouts) {
      const cp = this.tracePolygon(cutout.walls);
      if (cp && cp.length >= 3) {
        // Check if entire new room is inside this cutout
        const allInCutout = newPoly.every(p => this.pointInPolygon(p.x, p.y, cp));
        if (allInCutout) {
          // Cleanup cutouts mark deleted split fragments — always add as separate room
          if (cutout.isCleanup) { this.rooms.push(room); this.updateStatus('Space added inside cutout area.'); return true; }
          // Check if new room shares walls with cutout — if so, should merge to shrink hole
          let shareCutoutWall = false;
          for (const nw of room.walls) { for (const cw of cutout.walls) { if (this._segmentsCollinearOverlap(nw, cw, 0.05)) { shareCutoutWall = true; break; } } if (shareCutoutWall) break; }
          if (!shareCutoutWall) {
            entirelyInsideCutout = true;
            // This is a room filling a cutout - keep as separate room
            this.rooms.push(room);
            this.updateStatus('Space added inside cutout area.');
            return true;
          }
        }
        
        if (newPoly.some(p => this.pointInPolygon(p.x, p.y, cp))) { insideCutout = true; break; }
        for (const nw of room.walls) { const mx = (nw.start.x + nw.end.x) / 2, my = (nw.start.y + nw.end.y) / 2; if (this.pointInPolygon(mx, my, cp)) { insideCutout = true; break; } }
        if (insideCutout) break;
        let cx2 = 0, cy2 = 0; newPoly.forEach(p => { cx2 += p.x; cy2 += p.y; }); cx2 /= newPoly.length; cy2 /= newPoly.length;
        if (this.pointInPolygon(cx2, cy2, cp)) { insideCutout = true; break; }
      }
      for (const nw of room.walls) { for (const cw of cutout.walls) { if (this._segmentsIntersect(nw, cw) || this._segmentsCollinearOverlap(nw, cw, 0.05)) { crossesCutoutWall = true; break; } } if (crossesCutoutWall) break; }
      if (crossesCutoutWall) break;
    }
  }
  let touchesCutoutWall = false;
  if (!insideCutout && !crossesCutoutWall && existing.cutouts) {
    for (const cutout of existing.cutouts) { for (const nw of room.walls) { for (const cw of cutout.walls) { if (this.distanceToWall(nw.start, cw) < 0.1 || this.distanceToWall(nw.end, cw) < 0.1 || this.distanceToWall(cw.start, nw) < 0.1 || this.distanceToWall(cw.end, nw) < 0.1) { touchesCutoutWall = true; break; } } if (touchesCutoutWall) break; } if (touchesCutoutWall) break; }
  }
  let overlapsRoomWall = false;
  if (!insideCutout && !crossesCutoutWall && !touchesCutoutWall) { for (const nw of room.walls) { for (const ew of existing.walls) { if (this._segmentsCollinearOverlap(nw, ew, 0.05)) { overlapsRoomWall = true; break; } } if (overlapsRoomWall) break; } }
  const hasCutouts = existing.cutouts && existing.cutouts.length > 0;
  if (!insideCutout && !crossesCutoutWall && !touchesCutoutWall && !overlapsRoomWall && !hasCutouts) {
    // Verify centroid is actually on filled floor before discarding.
    // Bridges in concavities/gaps may appear "inside" the polygon boundary
    // due to floating-point imprecision but are actually in empty space.
    let cx4 = 0, cy4 = 0; newPoly.forEach(p => { cx4 += p.x; cy4 += p.y }); cx4 /= newPoly.length; cy4 /= newPoly.length;
    if (isInFilledArea(cx4, cy4, existing, existingPoly)) {
      // Check if room also overlaps a hole — if so, it's a partial hole fill, not a full overlap
      const _geoDiscard = this._getRoomGeoCache(existing);
      let _overlapsHole = false;
      if (_geoDiscard.holePolygons && _geoDiscard.holePolygons.length > 0) {
        for (const hp of _geoDiscard.holePolygons) {
          for (const p of newPoly) {
            if (this.pointInPolygon(p.x, p.y, hp)) { _overlapsHole = true; break; }
          }
          if (!_overlapsHole) {
            for (const w of room.walls) {
              const mx = (w.start.x + w.end.x) / 2, my = (w.start.y + w.end.y) / 2;
              if (this.pointInPolygon(mx, my, hp)) { _overlapsHole = true; break; }
            }
          }
          if (_overlapsHole) break;
        }
      }
      if (!_overlapsHole) {
        this.updateStatus('Space is entirely inside an existing space. Discarded.', { error: true }); return false;
      }
      // Spans filled + hole → fall through to merge
    }
  }
}
        // Guard: if the existing room has cutouts, verify the overlap is actually
        // in the filled area, not just in a cutout zone (ghost boundary).
        // Without this, drawing a new room over a previously-deleted chunk
        // falsely triggers a merge with the existing room.
        if (existing.cutouts && existing.cutouts.length > 0) {
          let overlapsFilled = false;
          // Check if any new room vertex or wall midpoint is in the filled area
          const checkPts = [...newPoly];
          for (const nw of room.walls) checkPts.push({ x: (nw.start.x + nw.end.x) / 2, y: (nw.start.y + nw.end.y) / 2 });
          for (const pt of checkPts) {
            if (isInFilledArea(pt.x, pt.y, existing, existingPoly)) { overlapsFilled = true; break; }
          }
          // Also check using filled-area walls (clipped against ALL cutouts including cleanup).
          // This avoids ghost walls in deleted zones triggering false merges.
          if (!overlapsFilled) {
            const _EPS = 0.01;
            let filledWalls = this.getVisibleRoomWalls(existing);
            const icData = this.getInteriorCutoutWalls(existing);
            filledWalls = [...filledWalls, ...icData.map(d => d.wall)];
            // Clip against cleanup cutouts (getVisibleRoomWalls skips them)
            for (const cutout of existing.cutouts) {
              if (!cutout.isCleanup) continue;
              const cp = this.tracePolygon(cutout.walls);
              if (!cp || cp.length < 3) continue;
              let clipped = [];
              for (const w of filledWalls) {
                let segs = this._subtractOverlappingSegments(w, cutout.walls, _EPS);
                let trimmed = [];
                for (const seg of segs) trimmed.push(...this._subtractInsidePolygon(seg, cp, _EPS));
                clipped.push(...trimmed);
              }
              filledWalls = clipped;
            }
            // Check: does any filled-area wall midpoint fall inside the new room?
            for (const fw of filledWalls) {
              const mx = (fw.start.x + fw.end.x) / 2, my = (fw.start.y + fw.end.y) / 2;
              if (this.pointInPolygon(mx, my, newPoly)) { overlapsFilled = true; break; }
            }
            // Check: does any filled-area wall intersect a new room wall?
            if (!overlapsFilled) {
              for (const fw of filledWalls) {
                for (const nw of room.walls) {
                  if (this._segmentsIntersect(fw, nw)) { overlapsFilled = true; break; }
                }
                if (overlapsFilled) break;
              }
            }
          }
          if (!overlapsFilled) continue; // overlap is only in cutout zone — skip merge
        }
        mergeTargets.push(existing);
      }
    }
    if (mergeTargets.length === 0) { this.rooms.push(room); this.updateStatus('Space complete!'); return true; }

    const primary = mergeTargets[0]; const EPS = 0.01;

    // Save cutouts from all merge sources before merge destroys them
    const _cw = (walls) => walls.map(w => ({ start: { x: w.start.x, y: w.start.y }, end: { x: w.end.x, y: w.end.y } }));
    const savedCutouts = [];
    if (primary.cutouts && primary.cutouts.length > 0) {
      for (const c of primary.cutouts) { if (!c.isCleanup) savedCutouts.push({ walls: _cw(c.walls), closed: c.closed }); }
    }
    for (let i = 1; i < mergeTargets.length; i++) {
      const mt = mergeTargets[i];
      if (mt.cutouts && mt.cutouts.length > 0) {
        for (const c of mt.cutouts) { if (!c.isCleanup) savedCutouts.push({ walls: _cw(c.walls), closed: c.closed }); }
      }
    }

    // Collect all source rooms with their boundary walls before any modification
    const copyWalls = (walls) => walls.map(w => ({ start: { x: w.start.x, y: w.start.y }, end: { x: w.end.x, y: w.end.y } }));
    const getEffectiveWalls = (rm) => {
      if (rm.cutouts && rm.cutouts.length > 0) {
        const vw = this.getVisibleRoomWalls(rm);
        const icData = this.getInteriorCutoutWalls(rm);
        let walls = [...vw, ...icData.map(d => d.wall)];
        // getVisibleRoomWalls/getInteriorCutoutWalls skip cleanup cutouts (for rendering).
        // For merge, we need the actual filled-area boundary — clip against cleanup cutouts too.
        for (const cutout of rm.cutouts) {
          if (!cutout.isCleanup) continue;
          const cp = this.tracePolygon(cutout.walls);
          if (!cp || cp.length < 3) continue;
          let clipped = [];
          for (const w of walls) {
            let segs = this._subtractOverlappingSegments(w, cutout.walls, EPS);
            let trimmed = [];
            for (const seg of segs) trimmed.push(...this._subtractInsidePolygon(seg, cp, EPS));
            clipped.push(...trimmed);
          }
          walls = clipped;
        }
        return walls;
      }
      return [...rm.walls, ...(rm.cutouts || []).flatMap(c => c.walls)];
    };
    const allSources = [];
    allSources.push({ boundaryWalls: copyWalls(getEffectiveWalls(primary)) });
    allSources.push({ boundaryWalls: copyWalls(room.walls) });
    for (let i = 1; i < mergeTargets.length; i++) {
      allSources.push({ boundaryWalls: copyWalls(getEffectiveWalls(mergeTargets[i])) });
    }

    // Phase 1: For each source, remove wall segments inside another source's filled area
    for (let i = 0; i < allSources.length; i++) {
      let remaining = copyWalls(allSources[i].boundaryWalls);
      for (let j = 0; j < allSources.length; j++) {
        if (i === j) continue;
        let filtered = [];
        for (const w of remaining) {
          filtered.push(...this._subtractInsideRoom(w, allSources[j].boundaryWalls, EPS));
        }
        remaining = filtered;
      }
      allSources[i].survivingWalls = remaining;
    }

    // Phase 2: Collect all surviving walls, then remove truly interior overlaps
    let mergedWalls = [];
    for (const src of allSources) mergedWalls.push(...src.survivingWalls);

    // For overlapping wall pairs, check if the wall is interior (both sides filled)
    // or boundary (one side empty) in the merged shape. Only remove interior overlaps.
    const allOriginalWalls = allSources.flatMap(s => s.boundaryWalls);
    const toRemove = new Set();
    for (let a = 0; a < mergedWalls.length; a++) {
      if (toRemove.has(a)) continue;
      for (let b = a + 1; b < mergedWalls.length; b++) {
        if (toRemove.has(b)) continue;
        if (!this._segmentsCollinearOverlap(mergedWalls[a], mergedWalls[b], EPS)) continue;

        // Found overlapping pair — test if it's interior or boundary
        const wall = mergedWalls[a];
        const wdx = wall.end.x - wall.start.x;
        const wdy = wall.end.y - wall.start.y;
        const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
        if (wLen < EPS) continue;
        const wnx = -wdy / wLen;
        const wny = wdx / wLen;
        const wmx = (wall.start.x + wall.end.x) / 2;
        const wmy = (wall.start.y + wall.end.y) / 2;
        const offset = EPS * 10;

        const rayCastOriginal = (px, py) => {
          let crossings = 0;
          for (const bw of allOriginalWalls) {
            if ((bw.start.y > py) !== (bw.end.y > py)) {
              const xInt = bw.start.x + (py - bw.start.y) * (bw.end.x - bw.start.x) / (bw.end.y - bw.start.y);
              if (px < xInt) crossings++;
            }
          }
          return crossings % 2 === 1;
        };

        const side1In = rayCastOriginal(wmx + wnx * offset, wmy + wny * offset);
        const side2In = rayCastOriginal(wmx - wnx * offset, wmy - wny * offset);

        if (side1In && side2In) {
          // Both sides filled → truly interior → remove both copies
          toRemove.add(a);
          toRemove.add(b);
        } else {
          // One side empty → boundary wall → keep one copy, remove duplicate
          toRemove.add(b);
        }
      }
    }
    mergedWalls = mergedWalls.filter((_, i) => !toRemove.has(i));
    mergedWalls = mergedWalls.filter(w => Math.sqrt((w.end.x - w.start.x) ** 2 + (w.end.y - w.start.y) ** 2) > 0.02);

    // Chain-order walls to form a proper closed polygon.
    // Walls from getEffectiveWalls may have inconsistent directions (e.g. interior
    // cutout walls face inward). Re-orient them so each wall's end connects to the
    // next wall's start, producing a valid closed polygon.
    if (mergedWalls.length > 2) {
      const _EPS2 = 0.05;
      const _ptM = (a, b) => Math.abs(a.x - b.x) < _EPS2 && Math.abs(a.y - b.y) < _EPS2;
      const ordered = [mergedWalls[0]];
      const used = new Set([0]);
      for (let step = 1; step < mergedWalls.length; step++) {
        const tail = ordered[ordered.length - 1].end;
        let found = false;
        for (let k = 0; k < mergedWalls.length; k++) {
          if (used.has(k)) continue;
          if (_ptM(tail, mergedWalls[k].start)) {
            ordered.push(mergedWalls[k]); used.add(k); found = true; break;
          }
          if (_ptM(tail, mergedWalls[k].end)) {
            // Reverse wall to match chain direction
            const w = mergedWalls[k];
            ordered.push({ start: {x:w.end.x,y:w.end.y}, end: {x:w.start.x,y:w.start.y} });
            used.add(k); found = true; break;
          }
        }
        if (!found) break;
      }
      if (ordered.length === mergedWalls.length) mergedWalls = ordered;
    }

    // Transfer cutouts and doors from other merge targets to primary
    for (let i = 1; i < mergeTargets.length; i++) {
      const other = mergeTargets[i];
      if (other.cutouts && other.cutouts.length > 0) { if (!primary.cutouts) primary.cutouts = []; primary.cutouts.push(...other.cutouts); }
      if (other.doors && other.doors.length > 0) primary.doors.push(...other.doors);
      const idx = this.rooms.indexOf(other); if (idx !== -1) this.rooms.splice(idx, 1);
    }
    primary.walls = mergedWalls;

    // Clear old cutouts — cutout walls were included in the merge,
    // so component detection below will rebuild any remaining holes
    primary.cutouts = [];

    // Detect interior holes BEFORE mergeCollinearWalls to prevent
    // collinear merging from bridging outer↔inner wall loops
    const mergedComponents = this._getConnectedComponents(primary.walls);
    if (mergedComponents.length > 1) {
      let outerIdx = 0, outerSize = 0;
      for (let ci = 0; ci < mergedComponents.length; ci++) {
        let mnX = Infinity, mnY = Infinity, mxX = -Infinity, mxY = -Infinity;
        for (const w of mergedComponents[ci]) { mnX = Math.min(mnX, w.start.x, w.end.x); mnY = Math.min(mnY, w.start.y, w.end.y); mxX = Math.max(mxX, w.start.x, w.end.x); mxY = Math.max(mxY, w.start.y, w.end.y); }
        const sz = (mxX - mnX) * (mxY - mnY); if (sz > outerSize) { outerSize = sz; outerIdx = ci; }
      }
      const outerPoly = this.tracePolygon(mergedComponents[outerIdx]);
      primary.walls = mergedComponents[outerIdx];
      if (!primary.cutouts) primary.cutouts = [];
      for (let ci = 0; ci < mergedComponents.length; ci++) {
        if (ci === outerIdx) continue;
        let cx = 0, cy = 0, cnt = 0;
        for (const w of mergedComponents[ci]) { cx += w.start.x + w.end.x; cy += w.start.y + w.end.y; cnt += 2; }
        cx /= cnt; cy /= cnt;
        if (outerPoly && this.pointInPolygon(cx, cy, outerPoly)) {
          // Merge collinear walls within this cutout component separately
          const cutoutRoom = { walls: mergedComponents[ci] };
          this.mergeCollinearWalls(cutoutRoom);
          primary.cutouts.push({ walls: cutoutRoom.walls, closed: true });
        }
      }
    }
    // Now merge collinear walls on outer boundary only (safe — no inner walls to bridge)
    this.mergeCollinearWalls(primary);
    primary.closed = this.isRoomClosed(primary);

    // Restore saved cutouts that are still inside the merged boundary
    // but only if the new room doesn't overlap them (merge handles those)
    if (savedCutouts.length > 0) {
      const mergedPoly = this.tracePolygon(primary.walls);
      if (mergedPoly && mergedPoly.length >= 3) {
        for (const cutout of savedCutouts) {
          const cp = this.tracePolygon(cutout.walls);
          if (!cp || cp.length < 3) continue;

          // Skip if new room overlaps this cutout — merge already handled it
          let newRoomOverlaps = false;
          if (newPoly.some(p => this.pointInPolygon(p.x, p.y, cp))) newRoomOverlaps = true;
          if (!newRoomOverlaps && cp.some(p => this.pointInPolygon(p.x, p.y, newPoly))) newRoomOverlaps = true;
          if (!newRoomOverlaps) { for (const nw of room.walls) { for (const cw of cutout.walls) { if (this._segmentsIntersect(nw, cw)) { newRoomOverlaps = true; break; } } if (newRoomOverlaps) break; } }
          if (newRoomOverlaps) continue;

          let cx = 0, cy = 0;
          cp.forEach(p => { cx += p.x; cy += p.y; }); cx /= cp.length; cy /= cp.length;
          if (this.pointInPolygon(cx, cy, mergedPoly)) {
            let duplicate = false;
            for (const existing of primary.cutouts) {
              const ep = this.tracePolygon(existing.walls);
              if (ep && ep.length >= 3) {
                let ecx = 0, ecy = 0;
                ep.forEach(p => { ecx += p.x; ecy += p.y; }); ecx /= ep.length; ecy /= ep.length;
                if (Math.abs(cx - ecx) < 0.1 && Math.abs(cy - ecy) < 0.1) { duplicate = true; break; }
              }
            }
            if (!duplicate) primary.cutouts.push(cutout);
          }
        }
      }
    }
    if (primary.cutouts && primary.cutouts.length > 0) {
      const finalPoly = this.tracePolygon(primary.walls);
      if (finalPoly && finalPoly.length >= 3) primary.cutouts = primary.cutouts.filter(c => { const ccp = this.tracePolygon(c.walls); if (!ccp || ccp.length < 3) return false; return ccp.some(p => this.pointInPolygon(p.x, p.y, finalPoly)); });
    }
    // Relink doors to surviving walls; remove any that no longer sit on a wall
    const doorsRemoved = this._relinkOrRemoveDoorsAfterMerge(primary);

    const msg = doorsRemoved > 0
      ? `Space enlarged! ${doorsRemoved} Door${doorsRemoved > 1 ? 's' : ''} deleted`
      : 'Space enlarged!';
    this.updateStatus(msg); return true;
  }

  /**
   * After a room merge, doors still reference old wall objects that may have
   * been removed or merged.  For each door, compute its world-space hinge/end
   * positions from the (now stale) wall ref, then find the closest wall in the
   * merged set.  If the door's hinge AND end both project onto that wall within
   * tolerance, relink it; otherwise fade-out and delete it.
   */
  _relinkOrRemoveDoorsAfterMerge(room) {
    if (!room.doors || room.doors.length === 0) return;
    const allWalls = [...room.walls];
    if (room.cutouts) room.cutouts.forEach(c => allWalls.push(...c.walls));

    const surviving = [];
    const orphaned = [];

    for (const door of room.doors) {
      // Compute world positions from old wall ref
      const hingePos = this.paramToPoint(door.wall, door.hingeParam);
      const endPos   = this.paramToPoint(door.wall, door.endParam);

      // Find the wall whose midline is closest to the door's hinge point
      let bestWall = null, bestDist = Infinity;
      for (const w of allWalls) {
        const d = this.distanceToWall(hingePos, w);
        if (d < bestDist) { bestDist = d; bestWall = w; }
      }

      // Tolerance: door must be within 0.15 ft (~1.8 in) of the wall line
      const TOL = 0.15;
      if (!bestWall || bestDist > TOL) {
        orphaned.push(door);
        continue;
      }

      // Also check the end point distance to the same wall
      const endDist = this.distanceToWall(endPos, bestWall);
      if (endDist > TOL) {
        orphaned.push(door);
        continue;
      }

      // Project hinge and end onto the new wall
      const newHingeParam = this.getWallParam(hingePos, bestWall);
      const newEndParam   = this.getWallParam(endPos, bestWall);

      // Check that the door actually fits on the wall (params between 0 and 1
      // with a small margin) — if clamped to boundary, the door is hanging off
      const MIN_P = -0.01, MAX_P = 1.01;
      if (newHingeParam < MIN_P || newHingeParam > MAX_P ||
          newEndParam < MIN_P || newEndParam > MAX_P) {
        orphaned.push(door);
        continue;
      }

      // Relink the door to the new wall
      door.wall = bestWall;
      door.hingeParam = Math.max(0, Math.min(1, newHingeParam));
      door.endParam   = Math.max(0, Math.min(1, newEndParam));
      surviving.push(door);
    }

    room.doors = surviving;

    // Fade-out animation for orphaned doors
    if (orphaned.length > 0) {
      this._fadeOutDoors(orphaned);
    }
    return orphaned.length;
  }

  /**
   * Animate orphaned doors fading out over 400ms, then repaint.
   */
  _fadeOutDoors(doors) {
    const startTime = performance.now();
    const duration = 400;
    // Snapshot each door's world-space drawing data before wall refs go stale
    const snapshots = doors.map(door => {
      const hingePos = this.paramToPoint(door.wall, door.hingeParam);
      const endPos   = this.paramToPoint(door.wall, door.endParam);
      return { door, hingePos, endPos, type: door.type, width: door.width, swing: door.swing ? { ...door.swing } : null };
    });

    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const alpha = 1 - progress;
      this.render();
      if (alpha > 0) {
        const ctx = this.canvas.getContext('2d');
        ctx.save();
        ctx.globalAlpha = alpha;
        for (const snap of snapshots) {
          this._drawFadingDoor(ctx, snap);
        }
        ctx.restore();
      }
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }

  /**
   * Draw a single door at its snapshotted world position (used for fade-out).
   */
  _drawFadingDoor(ctx, snap) {
    const hx = this.toScreenX(snap.hingePos.x);
    const hy = this.toScreenY(snap.hingePos.y);
    const ex = this.toScreenX(snap.endPos.x);
    const ey = this.toScreenY(snap.endPos.y);
    const dx = ex - hx, dy = ey - hy;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 1) return;
    const nx = -dy / len, ny = dx / len;
    const wallThick = Math.max(2, 4 * this.zoomLevel);

    // Draw the gap (white line over wall)
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = wallThick + 2;
    ctx.beginPath(); ctx.moveTo(hx, hy); ctx.lineTo(ex, ey); ctx.stroke();

    if (snap.type === 'roller') {
      // Roller: dashed line across opening
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = '#666';
      ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(hx, hy); ctx.lineTo(ex, ey); ctx.stroke();
      ctx.setLineDash([]);
    } else {
      // Single / double: draw door leaf lines + arcs
      const perpSign = snap.swing ? snap.swing.perpSign : 1;
      const hingeEnd = snap.swing ? snap.swing.hingeEnd : 'start';
      const leaves = snap.type === 'double' ? 2 : 1;
      for (let leaf = 0; leaf < leaves; leaf++) {
        let lhx, lhy, lex, ley;
        if (leaves === 1) { lhx = hx; lhy = hy; lex = ex; ley = ey; }
        else {
          const mx = (hx + ex) / 2, my = (hy + ey) / 2;
          if (leaf === 0) { lhx = hx; lhy = hy; lex = mx; ley = my; }
          else { lhx = ex; lhy = ey; lex = mx; ley = my; }
        }
        const ldx = lex - lhx, ldy = ley - lhy;
        const lLen = Math.sqrt(ldx * ldx + ldy * ldy);
        if (lLen < 1) continue;
        const lnx = -ldy / lLen, lny = ldx / lLen;
        const tipX = lhx + lnx * lLen * perpSign;
        const tipY = lhy + lny * lLen * perpSign;
        ctx.strokeStyle = '#333';
        ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(lhx, lhy); ctx.lineTo(tipX, tipY); ctx.stroke();
        const startAngle = Math.atan2(tipY - lhy, tipX - lhx);
        const endAngle = Math.atan2(ley - lhy, lex - lhx);
        ctx.beginPath(); ctx.arc(lhx, lhy, lLen, startAngle, endAngle, perpSign < 0);
        ctx.stroke();
      }
    }
  }

  /* ── ORPHANED WALL SEGMENT CLEANUP ── */

  _collectAllRoomWallsFlat() {
    const walls = [];
    for (const room of this.rooms) {
      walls.push(...room.walls);
      if (room.cutouts) for (const c of room.cutouts) walls.push(...c.walls);
    }
    if (this.currentRoom && this.currentRoom.walls.length > 0) {
      walls.push(...this.currentRoom.walls);
    }
    return walls;
  }

  /**
   * After a room add/subtract/merge, check wall segment endpoints that were
   * connected to perimeter walls before the operation.  If a perimeter wall
   * they touched is now gone and the endpoint doesn't touch any surviving
   * wall, remove that wall segment (the anchor it connected to was deleted).
   */
  _pruneOrphanedWallSegments(preWalls) {
    const CONNECT_EPS = 0.15; // ~1.8 inches
    const postWalls = this._collectAllRoomWallsFlat();

    const isNearAnyWall = (pt, walls) => {
      for (const w of walls) {
        if (this.distanceToWall(pt, w) < CONNECT_EPS) return true;
      }
      return false;
    };

    // Pass 1: Remove segments whose perimeter-wall anchor was deleted
    let changed = false;
    for (let gi = this.wallSegmentGroups.length - 1; gi >= 0; gi--) {
      const group = this.wallSegmentGroups[gi];
      for (let si = group.segments.length - 1; si >= 0; si--) {
        const seg = group.segments[si];
        const startWasConnected = isNearAnyWall(seg.start, preWalls);
        const endWasConnected = isNearAnyWall(seg.end, preWalls);
        const startStillConnected = isNearAnyWall(seg.start, postWalls);
        const endStillConnected = isNearAnyWall(seg.end, postWalls);

        if ((startWasConnected && !startStillConnected) ||
            (endWasConnected && !endStillConnected)) {
          group.segments.splice(si, 1);
          changed = true;
        }
      }
      if (group.segments.length === 0) {
        this.wallSegmentGroups.splice(gi, 1);
      }
    }

    // Pass 2: Cascade — keep removing segments that have a dangling endpoint
    // (not touching any room wall AND not touching any other segment's line)
    if (changed) {
      let cascade;
      do {
        cascade = false;
        const allSegs = [];
        for (const g of this.wallSegmentGroups) {
          for (const s of g.segments) allSegs.push(s);
        }
        for (let gi = this.wallSegmentGroups.length - 1; gi >= 0; gi--) {
          const group = this.wallSegmentGroups[gi];
          for (let si = group.segments.length - 1; si >= 0; si--) {
            const seg = group.segments[si];
            // Check each endpoint: is it connected to a room wall or another segment's line?
            const endpointAnchored = (pt) => {
              if (isNearAnyWall(pt, postWalls)) return true;
              for (const other of allSegs) {
                if (other === seg) continue;
                if (this.distanceToWall(pt, other) < CONNECT_EPS) return true;
              }
              return false;
            };
            if (!endpointAnchored(seg.start) || !endpointAnchored(seg.end)) {
              group.segments.splice(si, 1);
              const idx = allSegs.indexOf(seg);
              if (idx >= 0) allSegs.splice(idx, 1);
              cascade = true;
            }
          }
          if (group.segments.length === 0) {
            this.wallSegmentGroups.splice(gi, 1);
          }
        }
      } while (cascade);
    }

    // Clean up doors referencing removed segments
    for (const group of this.wallSegmentGroups) {
      if (group.doors && group.doors.length > 0) {
        group.doors = group.doors.filter(d => group.segments.includes(d.wall));
      }
    }
    return changed;
  }

  /* ── AREA CALCULATION ── */

  calculateArea(room) {
    if (!room || room.walls.length < 3) return 0;
    return this._getRoomGeoCache(room).area;
  }

  /* ── MEASUREMENTS UI (Layout Details) ── */

  updateMeasurements() {
    const spaceList = document.getElementById('rdSpaceList');
    if (!spaceList) return;
    // Preserve expansion state across rebuilds (space cards + total summary)
    const expandedIndices = new Set();
    spaceList.querySelectorAll('.rd-space-card.expanded').forEach(el => {
      const idx = el.getAttribute('data-room-index');
      if (idx !== null) expandedIndices.add(idx);
    });
    // Auto-expand first space card on initial build. CUSTOM_GYMLAYOUT_BY_CLAUDE (Ryan
    // 2026-10-10): not on a design shared with you; those open with every card closed.
    if (expandedIndices.size === 0 && !this._collapseLayoutCards) expandedIndices.add('0');
    const totalWasExpanded = !!spaceList.querySelector('.rd-total-summary.expanded');
    spaceList.innerHTML = '';
    let totalArea = 0, totalDoors = 0, totalWallLF = 0, totalZones = 0;
    const cardsToExpand = [];
    this.rooms.forEach((room, index) => {
      if (!room.name) room.name = `Space ${index + 1}`;
      const stats = this._computeSpaceStats(room);
      totalArea += stats.isSubtract ? -stats.area : stats.area;
      totalDoors += stats.doorCount;
      totalWallLF += stats.perimeterLF + stats.dividerLF;
      // Count flooring zones
      if (typeof this._getPerRoomFlooringData === 'function') {
        const fd = this._getPerRoomFlooringData(room);
        if (fd) totalZones += fd.length;
      }
      const card = this._buildSpaceCard(room, index, stats);
      if (expandedIndices.has(String(index))) {
        card.classList.add('expanded');
        cardsToExpand.push(card);
      }
      spaceList.appendChild(card);
    });
    // Depth shading: front card (first) lightest, rear card (last) darkest
    if (this.rooms.length > 1) {
      // Light: front → back shades; Dark: front → back shades
      const lightShades = ['#F6F6F6', '#EFEFEF', '#E8E8E8', '#E2E2E2', '#DCDCDC'];
      const darkShades = [
        'rgba(30,46,72,0.70)', 'rgba(24,38,62,0.75)', 'rgba(18,30,52,0.80)',
        'rgba(14,24,44,0.83)', 'rgba(10,18,36,0.86)',
      ];
      const shades = this.darkMode ? darkShades : lightShades;
      const cards = spaceList.querySelectorAll('.rd-space-card');
      cards.forEach((c, i) => {
        c.style.background = shades[Math.min(i, shades.length - 1)];
      });
    }
    // Append total summary card (only when multiple spaces)
    if (this.rooms.length > 1) {
      const totalCard = this._buildTotalSummaryCard(totalArea, totalDoors, totalWallLF, totalZones);
      spaceList.appendChild(totalCard);
    }
    // Restore expansion after cards are in the DOM so scrollHeight is correct
    for (const card of cardsToExpand) {
      const details = card.querySelector('.rd-space-details');
      if (details) {
        details.style.transition = 'none';
        details.style.maxHeight = details.scrollHeight + 'px';
        requestAnimationFrame(() => { details.style.transition = ''; });
      }
    }
    if (totalWasExpanded) {
      totalCard.classList.add('expanded');
      const totalDetails = totalCard.querySelector('.rd-total-details');
      if (totalDetails) {
        totalDetails.style.transition = 'none';
        totalDetails.style.maxHeight = totalDetails.scrollHeight + 'px';
        requestAnimationFrame(() => { totalDetails.style.transition = ''; });
      }
    }
  }

  _computeSpaceStats(room) {
    const area = this.calculateArea(room);
    const isSubtract = room.type === 'subtract';
    let perimeterLF = 0;
    for (const wall of room.walls) {
      const wLen = this.getWallLength(wall);
      const dx = wall.end.x - wall.start.x, dy = wall.end.y - wall.start.y;

      // Find T-junction breaks from wall-type divider segments (mirrors render module label logic)
      const wallSHT = this.wallSegmentThickness / 2;
      const segBreaks = [];
      const allGroups = this.currentWallSegmentGroup
        ? [...this.wallSegmentGroups, this.currentWallSegmentGroup]
        : [...this.wallSegmentGroups];
      for (const group of allGroups) {
        if ((group.type || 'wall') === 'line') continue;
        for (const seg of group.segments) {
          const d2x = seg.end.x - seg.start.x, d2y = seg.end.y - seg.start.y;
          const cr = dx * d2y - dy * d2x;
          if (Math.abs(cr) > 1e-10) {
            const ox = seg.start.x - wall.start.x, oy = seg.start.y - wall.start.y;
            const t = (ox * d2y - oy * d2x) / cr;
            const u = (ox * dy - oy * dx) / cr;
            if (t * wLen > 0.05 && (1 - t) * wLen > 0.05 && u >= -0.02 && u <= 1.02) {
              segBreaks.push({ param: t, adjust: wallSHT });
            }
          }
          for (const ep of [seg.start, seg.end]) {
            const p = this.getWallParam(ep, wall);
            if (p * wLen > 0.05 && (1 - p) * wLen > 0.05) {
              const proj = this.paramToPoint(wall, p);
              if (this.getDistance(ep, proj) < 0.05) {
                segBreaks.push({ param: p, adjust: wallSHT });
              }
            }
          }
        }
      }

      if (segBreaks.length === 0) {
        perimeterLF += wLen;
      } else {
        segBreaks.sort((a, b) => a.param - b.param);
        const brks = [{ param: 0, adjust: 0 }];
        for (const sb of segBreaks) {
          const last = brks[brks.length - 1];
          if (sb.param - last.param > 0.005) brks.push(sb);
          else last.adjust = Math.max(last.adjust, sb.adjust);
        }
        if (1 - brks[brks.length - 1].param > 0.005) brks.push({ param: 1, adjust: 0 });
        else brks[brks.length - 1].adjust = Math.max(brks[brks.length - 1].adjust, 0);
        for (let bi = 0; bi < brks.length - 1; bi++) {
          const rawLen = (brks[bi + 1].param - brks[bi].param) * wLen;
          const innerLen = rawLen - brks[bi].adjust - brks[bi + 1].adjust;
          if (innerLen > 0) perimeterLF += innerLen;
        }
      }
    }
    let doorCount = room.doors ? room.doors.length : 0;
    let doorwayLF = 0;
    if (room.doors) {
      for (const door of room.doors) doorwayLF += (door.width || 0) / 12;
    }
    // Count doors on dividing walls inside this room
    if (room.closed) {
      const _dGeo = this._getRoomGeoCache(room);
      const _dPoly = _dGeo.roomPolygon;
      if (_dPoly && _dPoly.length >= 3) {
        const _dGroups = [...this.wallSegmentGroups];
        if (this.currentWallSegmentGroup && this.currentWallSegmentGroup.segments.length > 0) _dGroups.push(this.currentWallSegmentGroup);
        for (const grp of _dGroups) {
          if (!grp.doors) continue;
          for (const d of grp.doors) {
            if (!d.wall) continue;
            const midParam = (d.hingeParam + d.endParam) / 2;
            const mx = d.wall.start.x + (d.wall.end.x - d.wall.start.x) * midParam;
            const my = d.wall.start.y + (d.wall.end.y - d.wall.start.y) * midParam;
            if (this.pointInPolygon(mx, my, _dPoly)) {
              doorCount++;
              doorwayLF += (d.width || 0) / 12;
            }
          }
        }
      }
    }
    const dividerLF = this._computeDividerWallLF(room) * 2;
    return {
      area, isSubtract, perimeterLF, doorCount, doorwayLF, dividerLF,
      _transitionStripLF: doorwayLF + dividerLF,
      _coveBaseLF: perimeterLF + dividerLF
    };
  }

  _computeDividerWallLF(room) {
    if (!room.closed) return 0;
    const geo = this._getRoomGeoCache(room);
    const roomPoly = geo.roomPolygon;
    if (!roomPoly || roomPoly.length < 3) return 0;
    let total = 0;
    const groups = [...this.wallSegmentGroups];
    if (this.currentWallSegmentGroup && this.currentWallSegmentGroup.segments.length > 0) {
      groups.push(this.currentWallSegmentGroup);
    }
    for (const group of groups) {
      if (group.type === 'line') continue; // only count wall-type dividers, not lines
      for (const seg of group.segments) {
        const mx = (seg.start.x + seg.end.x) / 2;
        const my = (seg.start.y + seg.end.y) / 2;
        if (this.pointInPolygon(mx, my, roomPoly)) {
          total += this.getWallLength(seg);
        }
      }
    }
    return total;
  }

  /** Build a flooring card DOM element for a single flooring entry.
   *  @param {Object} entry - flooring data from _getPerRoomFlooringData or _getCombinedFlooringData
   *  @param {string} detailsSelector - CSS class used by closest() for height adjustment (e.g. 'rd-space-details' or 'rd-total-details')
   */
  _buildFlooringCardEl(entry, detailsSelector) {
    const fcard = document.createElement('div');
    fcard.className = 'rd-space-flooring-card';

    // Product image
    const img = document.createElement('div');
    img.className = 'rd-space-flooring-img';
    if (entry.imageUrl) {
      img.style.backgroundImage = `url(${entry.imageUrl})`;
    } else if (entry.hasImage) {
      img.style.background = entry.swatch;
      img.style.backgroundSize = 'cover';
      img.style.backgroundPosition = 'center';
    } else {
      img.style.background = entry.swatch;
    }
    fcard.appendChild(img);

    // Info stack
    const info = document.createElement('div');
    info.className = 'rd-space-flooring-info';

    // Line 1: Product — Color (clickable link if product has URL)
    const nameText = entry.colorName
      ? `${entry.productName} \u2014 ${entry.colorName}`
      : entry.productName;
    const productUrl = entry._product && entry._product.url;
    if (productUrl) {
      const nameLink = document.createElement('a');
      nameLink.className = 'rd-space-flooring-name rd-space-flooring-link';
      nameLink.href = productUrl;
      nameLink.target = '_blank';
      nameLink.rel = 'noopener';
      nameLink.textContent = nameText;
      nameLink.addEventListener('click', (e) => e.stopPropagation());
      info.appendChild(nameLink);
    } else {
      const nameDiv = document.createElement('div');
      nameDiv.className = 'rd-space-flooring-name';
      nameDiv.textContent = nameText;
      info.appendChild(nameDiv);
    }

    // Line 2: Per-zone or per-space detail lines, or single summary line
    // Collect total transition linear feet to show inside the details dropdown
    let _transLF = 0;
    let _islandLF = 0; // island cut-out perimeters — reported separately
    if (entry.spaceDetails && entry.spaceDetails.length > 1) {
      for (const sd of entry.spaceDetails) {
        const sdDiv = document.createElement('div');
        sdDiv.className = 'rd-space-flooring-detail';
        // Roll sharing: a space cut from another space's rolls buys no material of its own
        sdDiv.textContent = sd._borrowedFrom
          ? `${sd.spaceName}: Floor: ${this.formatArea(Math.round(sd.floorArea))}  \u00B7  Cut from ${sd._borrowedFrom} rolls`
          : `${sd.spaceName}: Material: ${this.formatArea(Math.ceil(sd.materialArea))}  \u00B7  Floor: ${this.formatArea(Math.round(sd.floorArea))}  \u00B7  Overage: ${sd.wastePercent}%` + (sd.seamLF ? `  \u00B7  Seams: ${sd.seamLF} LF` : '');
        info.appendChild(sdDiv);
        _transLF += (sd.borderFt || 0) + (sd.dividerLineLF || 0);
        _islandLF += (sd.islandTransitionLF || 0);
      }
    } else if (entry.zoneDetails && entry.zoneDetails.length > 1) {
      for (const zd of entry.zoneDetails) {
        const zdDiv = document.createElement('div');
        zdDiv.className = 'rd-space-flooring-detail';
        zdDiv.textContent = `${zd.zoneName}: Material: ${this.formatArea(Math.ceil(zd.materialArea))}  \u00B7  Floor: ${this.formatArea(Math.round(zd.floorArea))}  \u00B7  Overage: ${zd.wastePercent}%` + (zd.seamLF ? `  \u00B7  Seams: ${zd.seamLF} LF` : '');
        info.appendChild(zdDiv);
        _transLF += (zd.borderFt || 0) + (zd.dividerLineLF || 0);
        _islandLF += (zd.islandTransitionLF || 0);
      }
    } else if (entry.turfPieces && entry.turfPieces.length > 0) {
      if (entry.turfPieces.length === 1) {
        const detailDiv = document.createElement('div');
        detailDiv.className = 'rd-space-flooring-detail';
        detailDiv.textContent = `Area: ${this.formatArea(Math.round(entry.floorArea))}  \u00B7  ${entry.turfPieces[0].dims}`;
        info.appendChild(detailDiv);
      } else {
        for (let i = 0; i < entry.turfPieces.length; i++) {
          const p = entry.turfPieces[i];
          const line = document.createElement('div');
          line.className = 'rd-space-flooring-detail';
          line.textContent = `Area ${i + 1}: ${this.formatArea(Math.round(p.area))}  \u00B7  ${p.dims}`;
          info.appendChild(line);
        }
        const totalLine = document.createElement('div');
        totalLine.className = 'rd-space-flooring-detail';
        totalLine.style.fontWeight = '600';
        totalLine.textContent = `Total: ${this.formatArea(Math.round(entry.floorArea))}`;
        info.appendChild(totalLine);
      }
    } else {
      const detailDiv = document.createElement('div');
      detailDiv.className = 'rd-space-flooring-detail';
      // Roll sharing (rep): the borrowing space buys nothing; the lead space's material
      // includes the other spaces' pieces, so its overage is measured against all of them.
      if (entry._borrowedFrom) {
        detailDiv.textContent = `Floor: ${this.formatArea(Math.round(entry.floorArea))}  \u00B7  Cut from ${entry._borrowedFrom} rolls (no rolls bought for this space)`;
      } else {
        detailDiv.textContent = `Material: ${this.formatArea(Math.ceil(entry.materialArea))}  \u00B7  Floor: ${this.formatArea(Math.round(entry.floorArea))}  \u00B7  Overage: ${entry.wastePercent}%` + (entry.seamLF ? `  \u00B7  Seams: ${entry.seamLF} LF` : '');
      }
      info.appendChild(detailDiv);
      if (entry._shareWith && entry._shareWith.length) {
        const shareDiv = document.createElement('div');
        shareDiv.className = 'rd-space-flooring-detail';
        shareDiv.textContent = `Shared rolls: includes the pieces for ${entry._shareWith.map(o => o.spaceName).join(', ')}`;
        info.appendChild(shareDiv);
      }
      _transLF += (entry.borderFt || 0) + (entry.dividerLineLF || 0);
      _islandLF += (entry.islandTransitionLF || 0);
    }

    // Line 3: Details toggle + collapsed content (roll pills + transitions + turf customizations)
    // Only visible in rep mode
    const _hasRolls = entry.purchaseRolls && entry.purchaseRolls.length > 0;
    const _hasTurfCustom = entry.turfCustomizations && entry.turfCustomizations.summary !== 'No customizations';
    if (this._repMode && (_hasRolls || _transLF > 0 || _islandLF > 0 || _hasTurfCustom)) {
      const toggleSpan = document.createElement('span');
      toggleSpan.className = 'rd-space-flooring-rolls-toggle';
      const toggleLabel = entry.detailLabel || 'Details';
      toggleSpan.innerHTML = `${toggleLabel} <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="3 4.5 6 7.5 9 4.5"/></svg>`;
      info.appendChild(toggleSpan);

      const rollsDiv = document.createElement('div');
      rollsDiv.className = 'rd-space-flooring-rolls';
      if (_hasRolls) {
        let hasCutPieces = false;
        for (const r of entry.purchaseRolls) {
          const pill = document.createElement('span');
          pill.className = 'rd-material-roll-pill';
          pill.innerHTML = r.label;
          rollsDiv.appendChild(pill);
          if (r.label.includes('cut into')) hasCutPieces = true;
        }
        if (hasCutPieces) {
          const trimNote = document.createElement('div');
          trimNote.className = 'rd-roll-trim-note';
          trimNote.textContent = 'Each cut piece includes a few extra inches for trimming to fit.';
          rollsDiv.appendChild(trimNote);
        }
      }
      if (_hasTurfCustom) {
        const tc = entry.turfCustomizations;
        // If generic details array (platforms), render as simple detail lines
        if (tc.details && Array.isArray(tc.details)) {
          for (const line of tc.details) {
            const pill = document.createElement('span');
            pill.className = 'rd-material-roll-pill';
            pill.textContent = line;
            rollsDiv.appendChild(pill);
          }
        } else {
          // Turf customizations — build labels from specific fields
          const labels = [];
          if (tc.fieldLines) labels.push('Field Lines: ' + tc.fieldLines.origin);
          if (tc.hashMarks) labels.push('Hash Marks: ' + tc.hashMarks.sides + ' @ ' + tc.hashMarks.spacing + (tc.hashMarks.center ? ' + Center' : ''));
          if (tc.yardNumbers) labels.push('Yard Numbers: ' + tc.yardNumbers.spacing);
          if (tc.borderLine) labels.push('Border Line: ' + tc.borderLine.width);
          if (tc.sledLanes) labels.push(tc.sledLanes.count + ' Sled Lane' + (tc.sledLanes.count > 1 ? 's' : '') + ': ' + tc.sledLanes.edges.join(', '));
          if (tc.logos) {
            const imgs = tc.logos.filter(l => l.type === 'image').length;
            const ladders = tc.logos.filter(l => l.type === 'ladder').length;
            const dots = tc.logos.filter(l => l.type === 'dotdrill').length;
            if (imgs) labels.push(imgs + ' Logo' + (imgs > 1 ? 's' : ''));
            if (ladders) labels.push(ladders + ' Ladder' + (ladders > 1 ? 's' : ''));
            if (dots) labels.push(dots + ' Dot Drill' + (dots > 1 ? 's' : ''));
          }
          if (tc.direction) labels.push('Direction: ' + tc.direction);
          for (const label of labels) {
            const pill = document.createElement('span');
            pill.className = 'rd-material-roll-pill';
            pill.textContent = label;
            rollsDiv.appendChild(pill);
          }
        }
      }
      if (_transLF > 0) {
        const transDiv = document.createElement('div');
        transDiv.className = 'rd-space-flooring-detail';
        transDiv.textContent = `Potential Transitions: ${this.formatMeasurement(_transLF)}`;
        rollsDiv.appendChild(transDiv);
      }
      if (_islandLF > 0) {
        const islDiv = document.createElement('div');
        islDiv.className = 'rd-space-flooring-detail';
        islDiv.textContent = `Cut-Out Transitions: ${this.formatMeasurement(_islandLF)}`;
        rollsDiv.appendChild(islDiv);
      }
      info.appendChild(rollsDiv);

      toggleSpan.addEventListener('click', (e) => {
        e.stopPropagation();
        fcard.classList.toggle('rolls-expanded');
        const det = fcard.closest(`.${detailsSelector}`);
        if (det && det.style.maxHeight) {
          det.style.transition = 'none';
          det.style.maxHeight = 'none';
          const onEnd = () => {
            rollsDiv.removeEventListener('transitionend', onEnd);
            const h = det.scrollHeight;
            det.style.maxHeight = h + 'px';
            requestAnimationFrame(() => { det.style.transition = ''; });
          };
          rollsDiv.addEventListener('transitionend', onEnd);
          setTimeout(onEnd, 350);
        }
      });
    }

    // MOQ label
    if (entry.moqNote) {
      const moqDiv = document.createElement('div');
      moqDiv.className = entry.underMoq ? 'rd-material-moq-warning' : 'rd-material-moq-info';
      moqDiv.textContent = entry.moqNote;
      info.appendChild(moqDiv);
    }

    fcard.appendChild(info);
    return fcard;
  }

  /** Build platform entries compatible with _buildFlooringCardEl */
  _buildPlatformEntries(platformObjs) {
    const _ov = window.roomDesignerOverlay;
    const platGroups = new Map();
    for (const plat of platformObjs) {
      const presetLabel = plat.preset ? plat.preset.replace('x', "' \u00d7 ") + "'" : (this.formatMeasurement(plat.width) + ' \u00d7 ' + this.formatMeasurement(plat.depth));
      const colorKey = (plat.colorConfirmed ? (plat.centerColorName || '') + '|' + (plat.wingColorName || '') : '') + '|' + (plat.wings ? '1' : '0');
      const groupKey = presetLabel + '|' + colorKey;
      if (!platGroups.has(groupKey)) {
        platGroups.set(groupKey, { plat, presetLabel, count: 0 });
      }
      platGroups.get(groupKey).count++;
    }

    const entries = [];
    for (const [, g] of platGroups) {
      const centerHex = _ov && _ov._epdmColors ? (_ov._epdmColors.find(c => c.name === g.plat.centerColorName) || {}).hex : null;
      // Build platform customization details (shown in Details toggle)
      const platformCustom = {};
      const details = [];
      if (g.plat.colorConfirmed) {
        details.push('Center: ' + (g.plat.centerColorName || 'Custom'));
        if (g.plat.wings) details.push('Wings: ' + (g.plat.wingColorName || 'Custom'));
      }
      if (_ov && _ov._platformLogo && _ov._platformLogo.dataURL) {
        details.push('Custom logo \u2014 review required');
      }
      if (details.length > 0) {
        platformCustom.summary = details.join(', ');
        platformCustom.details = details;
      }

      const qtyLabel = g.count > 1 ? ' (\u00d7' + g.count + ')' : '';
      entries.push({
        productName: 'ECORE Inlaid Platform' + qtyLabel,
        colorName: '',
        imageUrl: 'https://cdn.shopify.com/s/files/1/0684/9350/8844/files/Rubber-Inlaid-Platform.webp?v=1773980680',
        swatch: centerHex || '#333',
        hasImage: true,
        _product: null,
        floorArea: g.plat.width * g.plat.depth * g.count,
        materialArea: g.plat.width * g.plat.depth * g.count,
        wastePercent: '0',
        purchaseRolls: [],
        moqNote: null,
        underMoq: false,
        detailLabel: 'Details',
        turfPieces: [{ dims: g.presetLabel + (g.plat.wings ? ' with wings' : ''), area: g.plat.width * g.plat.depth * g.count }],
        turfCustomizations: platformCustom.summary ? platformCustom : null
      });
    }
    return entries;
  }

  _addSeeMoreCollapse(section, visibleCount) {
    // Skip the section title element, count only card children
    const children = Array.from(section.children);
    const cards = children.filter(c => !c.classList.contains('rd-space-section-title'));
    if (cards.length <= visibleCount) return;
    // Hide overflow cards
    const hidden = [];
    for (let i = visibleCount; i < cards.length; i++) {
      cards[i].style.display = 'none';
      hidden.push(cards[i]);
    }
    const toggle = document.createElement('button');
    toggle.className = 'rd-see-more-btn';
    toggle.textContent = `See ${hidden.length} more...`;
    toggle.addEventListener('click', (e) => {
      e.stopPropagation();
      const isHidden = hidden[0].style.display === 'none';
      for (const el of hidden) el.style.display = isHidden ? '' : 'none';
      toggle.textContent = isHidden ? 'Show less' : `See ${hidden.length} more...`;
      // Update parent accordion max-height
      const details = section.closest('.rd-space-details, .rd-total-details');
      if (details) details.style.maxHeight = details.scrollHeight + 'px';
    });
    section.appendChild(toggle);
  }

  _getEquipColorNames(vendor, equipmentId) {
    if (!vendor) return { frame: null, pad: null };
    const overlay = window.roomDesignerOverlay;
    const config = overlay && overlay._getBrandColorConfig ? overlay._getBrandColorConfig() : {};
    const vendorConf = config[vendor];
    if (!vendorConf) return { frame: null, pad: null };
    const brandEntry = this._brandColors && this._brandColors[vendor];
    const info = equipmentId && this._equipmentImages && this._equipmentImages[equipmentId];
    // hasUpholstery is set during SVG load — before that, infer from brandEntry or vendor config
    const hasUph = (info && info.hasUpholstery) || (brandEntry && brandEntry.upholstery) || (vendorConf.upholstery && vendorConf.upholstery.length > 0);
    // No color chosen yet — show dashes
    if (!brandEntry) {
      return {
        frame: (!info || info.hasPowderCoat) ? '-' : null,
        pad: hasUph ? '-' : null
      };
    }
    // At least one color was chosen — resolve names, defaulting upholstery to Jet Black
    const current = {
      powderCoat: brandEntry.powderCoat || (this.darkMode ? '#B1B3B3' : '#004B87'),
      upholstery: brandEntry.upholstery || '#2D2926',
      pcMatte: brandEntry.pcMatte, pcPattern: brandEntry.pcPattern,
      uphPattern: brandEntry.uphPattern, pcSparkle: brandEntry.pcSparkle
    };
    let frame = null, pad = null;
    if (!info || info.hasPowderCoat) {
      if (current.powderCoat && vendorConf.powderCoat) {
        const match = vendorConf.powderCoat.find(sw => sw.hex === current.powderCoat && !!sw.matte === !!current.pcMatte && ((sw.pattern ? sw.swatch : null) || null) === (current.pcPattern || null));
        if (match) frame = match.name;
      }
    }
    if (hasUph) {
      if (current.upholstery && vendorConf.upholstery) {
        const match = vendorConf.upholstery.find(sw => sw.hex === current.upholstery && (((sw.pattern && sw.swatch) ? sw.swatch : null) || null) === (current.uphPattern || null));
        if (match) pad = match.name;
      }
    }
    return { frame, pad };
  }

  /* ── VIEW-ONLY HOVER CALLOUT ── */

  _updateViewOnlyHover(sx, sy) {
    if (this.isPanning) return;
    if (!this.showProductCards) { this.canvas.style.cursor = 'grab'; return; }

    // Equipment hover only on equipment-only view (not combined Full Layout)
    if (!this._viewOnlyHideEquipment && !this._viewOnlyShowFloor) {
      this._updateEquipmentHover(sx, sy);
      return;
    }
    // Flooring hover on flooring-only view
    if (this._viewOnlyShowFloor && this._viewOnlyHideEquipment) {
      this._updateFlooringHover(sx, sy);
      return;
    }
    // All other views (combined, floor plan) — no hover
    if (this._hoverCalloutEl || this._hoverCalloutTimer) {
      this._dismissHoverCallout();
    }
    this.canvas.style.cursor = 'grab';
  }

  _updateEquipmentHover(sx, sy) {
    const world = this.screenToWorld(sx, sy);
    // Hit-test equipment objects with rotated bounding box
    let hitObj = null;
    const pad = Math.max(0, 8 / this.pixelsPerFoot);
    for (const obj of this.objects) {
      if (obj.type !== 'equipment') continue;
      const halfW = obj.width / 2, halfD = obj.depth / 2;
      const rot = obj.rotation || 0;
      const local = this._rotatePoint(world.x, world.y, obj.x, obj.y, -rot);
      if (local.x >= obj.x - halfW - pad && local.x <= obj.x + halfW + pad &&
          local.y >= obj.y - halfD - pad && local.y <= obj.y + halfD + pad) {
        hitObj = obj;
        break;
      }
    }
    // Same object still hovered — cancel any grace timer, no-op
    if (hitObj === this._hoverCalloutObj) {
      if (this._hoverCalloutGraceTimer) { clearTimeout(this._hoverCalloutGraceTimer); this._hoverCalloutGraceTimer = null; }
      this._hsCancelSwitch(); // back on the open piece: nothing is taking over
      this._hsFollow(sx, sy); // the ghost rides the cursor until it opens
      this._hsArmIdle(); // still moving over the same piece: the pause clock restarts
      return;
    }
    if (hitObj) {
      if (this._hoverCalloutGraceTimer) { clearTimeout(this._hoverCalloutGraceTimer); this._hoverCalloutGraceTimer = null; }
      this.canvas.style.cursor = 'pointer';
      const takeOver = () => {
        this._dismissHoverCallout();
        this._hoverCalloutObj = hitObj;
        this._showHoverCallout(hitObj, sx, sy);
        this._hsArmIdle();
      };
      // An OPEN card is sticky: a piece the cursor merely crosses on its way to the card must not
      // replace it (Ryan 2026-09-24 — the "View product" pill was unreachable past another zone).
      // The new piece takes over only once the cursor RESTS on it.
      if (this._hsIsOpen()) { this._hsArmSwitch(takeOver); return; }
      takeOver(); // ghost or nothing showing: the new piece ghosts at once
    } else if (this._hoverCalloutEl || this._hoverCalloutTimer) {
      this._hsCancelSwitch();
      // Cursor left equipment — grace period so user can reach the card
      if (!this._hoverCalloutGraceTimer) {
        this._hoverCalloutGraceTimer = setTimeout(() => {
          this._hoverCalloutGraceTimer = null;
          this._dismissHoverCallout();
          this.canvas.style.cursor = 'grab';
        }, 300);
      }
    } else {
      this.canvas.style.cursor = 'grab';
    }
  }

  _updateFlooringHover(sx, sy) {
    const overlay = window.roomDesignerOverlay;
    if (!overlay) return;
    const world = this.screenToWorld(sx, sy);
    let hitId = null, hitProduct = null, hitColor = null;

    // Turf objects first — they sit on top of rooms visually, so they win
    const pad = Math.max(0, 8 / this.pixelsPerFoot);
    for (let ti = 0; ti < this.objects.length; ti++) {
      const obj = this.objects[ti];
      if (obj.type !== 'turf' || !obj.turfProductId) continue;
      const halfW = obj.width / 2, halfD = obj.depth / 2;
      const rot = obj.rotation || 0;
      const local = this._rotatePoint(world.x, world.y, obj.x, obj.y, -rot);
      if (local.x >= obj.x - halfW - pad && local.x <= obj.x + halfW + pad &&
          local.y >= obj.y - halfD - pad && local.y <= obj.y + halfD + pad) {
        const product = overlay._getProduct(obj.turfProductId);
        if (product) {
          hitId = 'turf:' + ti;
          hitProduct = product;
          hitColor = product.colors.find(c => c.id === obj.turfColorId) || null;
        }
        break;
      }
    }

    // Rooms — only check if no turf was hit (room polygon extends under turf)
    if (!hitId) {
      for (let ri = 0; ri < this.rooms.length; ri++) {
        const room = this.rooms[ri];
        if (!room || !room.closed || room.type !== 'add') continue;
        const geo = this._getRoomGeoCache(room);
        if (!geo || !geo.roomPolygon || geo.roomPolygon.length < 3) continue;
        if (!RoomDesignerMath.pointInPolygon(world.x, world.y, geo.roomPolygon)) continue;
        // Skip subtract-room holes
        let inHole = false;
        for (const rm of this.rooms) {
          if (rm.type !== 'subtract' || !rm.closed) continue;
          const hgeo = this._getRoomGeoCache(rm);
          if (hgeo && hgeo.roomPolygon && RoomDesignerMath.pointInPolygon(world.x, world.y, hgeo.roomPolygon)) { inHole = true; break; }
        }
        if (inHole) continue;

        const zones = (typeof overlay._getZonesForRoom === 'function') ? overlay._getZonesForRoom(room, geo) : [];
        if (zones.length > 1) {
          for (let zi = 0; zi < zones.length; zi++) {
            // _zoneHitTest, not a raw pointInPolygon: a zone that surrounds island rooms carries their rims as
            // holes, and its polygon still CONTAINS the island's points. The first zone in the list is the
            // surrounding one, so the raw test named the main gym's material for a room drawn inside it.
            if (!overlay._zoneHitTest(zones[zi], world.x, world.y)) continue;
            const zf = overlay._getZoneFlooring(room, zones[zi]);
            if (!zf) break;
            const product = overlay._getProduct(zf.productId);
            if (!product) break;
            hitId = 'floor:' + ri + ':' + zi;
            hitProduct = product;
            hitColor = product.colors.find(c => c.id === zf.colorId) || null;
            break;
          }
        } else {
          if (!room.flooring) continue;
          const product = overlay._getProduct(room.flooring.productId);
          if (!product) continue;
          hitId = 'floor:' + ri;
          hitProduct = product;
          hitColor = product.colors.find(c => c.id === room.flooring.colorId) || null;
        }
        break;
      }
    }

    // Same target still hovered
    if (hitId && hitId === this._hoverCalloutObj) {
      if (this._hoverCalloutGraceTimer) { clearTimeout(this._hoverCalloutGraceTimer); this._hoverCalloutGraceTimer = null; }
      this._hsCancelSwitch();
      this._hsFollow(sx, sy);
      this._hsArmIdle();
      return;
    }
    if (hitId) {
      if (this._hoverCalloutGraceTimer) { clearTimeout(this._hoverCalloutGraceTimer); this._hoverCalloutGraceTimer = null; }
      this.canvas.style.cursor = 'pointer';
      const takeOver = () => {
        this._dismissHoverCallout();
        this._hoverCalloutObj = hitId;
        this._showFlooringCallout(hitProduct, hitColor, sx, sy);
        this._hsArmIdle();
      };
      // Sticky open card — see _updateEquipmentHover
      if (this._hsIsOpen()) { this._hsArmSwitch(takeOver); return; }
      takeOver();
    } else if (this._hoverCalloutEl || this._hoverCalloutTimer) {
      this._hsCancelSwitch();
      if (!this._hoverCalloutGraceTimer) {
        this._hoverCalloutGraceTimer = setTimeout(() => {
          this._hoverCalloutGraceTimer = null;
          this._dismissHoverCallout();
          this.canvas.style.cursor = 'grab';
        }, 300);
      }
    } else {
      this.canvas.style.cursor = 'grab';
    }
  }

  /* ═══════════════════════════════════════════════════════════════════════════════════════
     CUSTOM_PROJECTASSEMBLY_BY_CLAUDE (Ryan 2026-09-23) — THE PRESENTATION HOTSPOT
     ═══════════════════════════════════════════════════════════════════════════════════════
     *"I want these labels to update to being more like the ones on blog images... in the GLD
     presentation mode, I don't want the circle already visible. Instead, you'll mouseover objects
     and see a ghosted out preview of the circle (small). when your mouse then stop moving, the
     object your mouse has stopped over will then see the circle grow in position and the label
     slide out from there."* And: *"don't include prices on them, but everything else and all the
     normal colors and glass design and all from the blog is fair game."*

     The blog's recipe (assets/bb-hotspot.css / bb-hotspot.js, UI-DESIGN-SYSTEM §3/§4) rebuilt for
     a canvas: a conic blue→white ring, a four-point star reticle that spins (Web Animations, so it
     decelerates to a stop instead of snapping), a leader line with a lit end node, and the
     liquid-glass card. Two states on one element — `rd-hs--ghost` the moment the cursor is over
     an object (small, faint, no card), `rd-hs--open` once the cursor has been still for
     HS_IDLE_MS over the same object. The ghost sits ON the cursor and rides it (`_hsFollow`)
     while the piece is hovered, so the open grows from wherever the cursor stopped, not from a
     fixed anchor on the piece (Ryan 2026-09-24: "it needs to animate from the mouse"). Cursor
     coordinates are CSS px relative to the canvas, the same space `worldToScreen` returns — no
     devicePixelRatio division. The card follows the blog's shape minus the price line:
     thumbnail, title (trimmed at " - "), a detail line (frame · pad colours, or the flooring
     colour), and the "View product" pill when the catalogue has a URL. `_hoverCalloutObj` /
     `_hoverCalloutEl` / the grace timer keep their old meaning; `_hoverCalloutTimer` is the idle
     timer now. Styles: custom-room-designer.css "PRESENTATION HOTSPOT". */
  _hsEnsureLens() {
    if (document.getElementById('bbhs-liquid')) return;
    const d = document.createElement('div');
    d.setAttribute('aria-hidden', 'true');
    d.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden';
    d.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="0" height="0">' +
      '<filter id="bbhs-liquid" x="-35%" y="-35%" width="170%" height="170%" color-interpolation-filters="sRGB">' +
      '<feTurbulence type="fractalNoise" baseFrequency="0.005 0.007" numOctaves="2" seed="7" result="n"/>' +
      '<feGaussianBlur in="n" stdDeviation="2.4" result="nb"/>' +
      '<feDisplacementMap in="SourceGraphic" in2="nb" scale="46" xChannelSelector="R" yChannelSelector="G"/>' +
      '</filter></svg>';
    document.body.appendChild(d);
  }

  _hsIsOpen() {
    const el = this._hoverCalloutEl;
    return !!(el && el.classList.contains('rd-hs--open') && !el.classList.contains('dismissing'));
  }

  /** While a card is open, another piece takes over only when the cursor RESTS on it: every move
   *  over it re-arms this; leaving it (for the card, the open piece, or empty floor) cancels it. */
  _hsArmSwitch(fn) {
    if (this._hsSwitchTimer) clearTimeout(this._hsSwitchTimer);
    this._hsSwitchTimer = setTimeout(() => { this._hsSwitchTimer = null; fn(); }, 320);
  }

  _hsCancelSwitch() {
    if (this._hsSwitchTimer) { clearTimeout(this._hsSwitchTimer); this._hsSwitchTimer = null; }
  }

  /** Arm (or re-arm) the pause: the cursor has to sit still over the same object before it opens. */
  _hsArmIdle() {
    if (this._hoverCalloutTimer) { clearTimeout(this._hoverCalloutTimer); this._hoverCalloutTimer = null; }
    const el = this._hoverCalloutEl;
    if (!el || el.classList.contains('rd-hs--open')) return;
    this._hoverCalloutTimer = setTimeout(() => {
      this._hoverCalloutTimer = null;
      if (this._hoverCalloutEl === el) this._hsOpen();
    }, 260);
  }

  /** The ghost: a small faint ring at the object's anchor, nothing else. */
  _hsShowGhost(t) {
    const container = document.getElementById('canvasContainer');
    if (!container || !t) return;
    this._hsEnsureLens();
    const wrapper = document.createElement('div');
    wrapper.className = 'rd-hs rd-hs--ghost';
    wrapper.style.left = t.ax + 'px';
    wrapper.style.top = t.ay + 'px';
    const el = (tag, cls) => { const e = document.createElement(tag); e.className = cls; return e; };
    const leader = el('div', 'rd-hs__leader');
    leader.appendChild(el('span', 'rd-hs__end'));
    wrapper.appendChild(leader);
    wrapper.appendChild(el('div', 'rd-hs__ring'));
    const dot = el('div', 'rd-hs__dot');
    wrapper.appendChild(dot);
    // The card is built now and revealed on open, so the open is one class flip.
    const card = el('div', 'rd-hs__card');
    const thumb = el('div', 'rd-hs__thumb');
    if (t.thumb) { thumb.style.backgroundImage = "url('" + String(t.thumb).replace(/'/g, '%27') + "')"; thumb.setAttribute('role', 'img'); thumb.setAttribute('aria-label', t.title || ''); }
    else thumb.classList.add('rd-hs__thumb--none');
    const info = el('div', 'rd-hs__info');
    const h = el('div', 'rd-hs__title'); h.textContent = t.title || '';
    info.appendChild(h);
    if (t.detail) { const dt = el('div', 'rd-hs__detail'); dt.textContent = t.detail; info.appendChild(dt); }
    if (t.url) {
      const a = el('a', 'rd-hs__btn'); a.href = t.url; a.target = '_blank'; a.rel = 'noopener';
      a.innerHTML = '<span class="rd-hs__btnfill"></span><span class="rd-hs__btnt">View product</span>';
      info.appendChild(a);
    }
    card.appendChild(thumb); card.appendChild(info);
    card.addEventListener('mouseenter', () => {
      if (this._hoverCalloutGraceTimer) { clearTimeout(this._hoverCalloutGraceTimer); this._hoverCalloutGraceTimer = null; }
      this._hsCancelSwitch();
    });
    card.addEventListener('mouseleave', (e) => {
      if (!e.relatedTarget || e.relatedTarget !== this.canvas) this._dismissHoverCallout();
    });
    wrapper.appendChild(card);
    container.appendChild(wrapper);
    wrapper._hs = { target: t, dot, card, spin: null, ax: t.ax, ay: t.ay };
    this._hoverCalloutEl = wrapper;
  }

  /** Move the ghost with the cursor. A no-op once it has opened — the open ring is planted where the cursor stopped. */
  _hsFollow(sx, sy) {
    const wrapper = this._hoverCalloutEl;
    if (!wrapper || !wrapper._hs || wrapper.classList.contains('rd-hs--open') || wrapper.classList.contains('dismissing')) return;
    wrapper._hs.ax = sx; wrapper._hs.ay = sy;
    wrapper.style.left = sx + 'px';
    wrapper.style.top = sy + 'px';
  }

  /** The pause ended over the object: grow the ring into place, spin the star, slide the leader, unfold the card. */
  _hsOpen() {
    const wrapper = this._hoverCalloutEl;
    const container = document.getElementById('canvasContainer');
    if (!wrapper || !wrapper._hs || !container) return;
    const t = { ax: wrapper._hs.ax, ay: wrapper._hs.ay }; // where the cursor stopped, not the piece
    const rect = container.getBoundingClientRect();
    const OFFSET = 74, CARD_W = 620, CARD_H = 280;
    // Toward the side with more room, flipped if the card would leave the canvas.
    let side = t.ax < rect.width / 2 ? 'right' : 'left';
    if (side === 'right' && t.ax + OFFSET + CARD_W > rect.width && t.ax - OFFSET - CARD_W > 0) side = 'left';
    else if (side === 'left' && t.ax - OFFSET - CARD_W < 0 && t.ax + OFFSET + CARD_W < rect.width) side = 'right';
    let vdir = t.ay < rect.height / 2 ? 'down' : 'up';
    if (vdir === 'down' && t.ay + CARD_H > rect.height && t.ay - CARD_H > 0) vdir = 'up';
    else if (vdir === 'up' && t.ay - CARD_H < 0 && t.ay + CARD_H < rect.height) vdir = 'down';
    const avail = side === 'right' ? (rect.width - (t.ax + OFFSET) - 10) : (t.ax - OFFSET - 10);
    wrapper._hs.card.style.width = Math.max(420, Math.min(CARD_W, avail)) + 'px';
    wrapper.classList.remove('dir-right', 'dir-left', 'v-up', 'v-down');
    wrapper.classList.add('dir-' + side, 'v-' + vdir);
    wrapper.classList.remove('rd-hs--ghost');
    wrapper.classList.add('rd-hs--open');
    this._hsStartSpin(wrapper);
  }

  _hsStartSpin(wrapper) {
    const dot = wrapper && wrapper._hs && wrapper._hs.dot;
    if (!dot || !dot.animate) return;
    let reduced = false;
    try { reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (_e) {}
    if (reduced) return;
    if (wrapper._hs.spin) { try { wrapper._hs.spin.cancel(); } catch (_e) {} }
    wrapper._hs.spin = dot.animate(
      [{ transform: 'translate(-50%,-50%) rotate(0deg)' }, { transform: 'translate(-50%,-50%) rotate(360deg)' }],
      { duration: 3000, iterations: Infinity, easing: 'linear' }
    );
  }

  /** Decelerate the star to a stop on its original orientation rather than snapping back. */
  _hsStopSpin(wrapper) {
    const hs = wrapper && wrapper._hs;
    if (!hs || !hs.spin || !hs.dot) return;
    const anim = hs.spin; hs.spin = null;
    let cur = 0;
    try { cur = ((anim.currentTime || 0) % 3000) / 3000 * 360; } catch (_e) {}
    try { anim.cancel(); } catch (_e) {}
    let target = Math.ceil(cur / 360) * 360;
    if (target - cur < 200) target += 360;
    try {
      hs.dot.animate(
        [{ transform: 'translate(-50%,-50%) rotate(' + cur + 'deg)' }, { transform: 'translate(-50%,-50%) rotate(' + target + 'deg)' }],
        { duration: 900, easing: 'cubic-bezier(0.2,0.8,0.25,1)', fill: 'none' }
      );
    } catch (_e) {}
  }

  _showFlooringCallout(product, color, cursorSx, cursorSy) {
    if (!product) return;
    const title = String(product.name || 'Flooring').split(' - ')[0].trim();
    this._hsShowGhost({
      ax: cursorSx, ay: cursorSy,
      title,
      detail: color && color.name ? color.name : '',
      thumb: color ? (color.imageUrl || '') : '',
      url: product.url || '',
    });
  }

  _showHoverCallout(obj, cursorSx, cursorSy) {
    const info = this._equipmentImages && this._equipmentImages[obj.equipmentId];
    if (!info) return;
    // Anchor: the cursor itself — the ghost rides it (_hsFollow) and the open grows from where it stopped.
    const colors = info.vendor ? this._getEquipColorNames(info.vendor, obj.equipmentId) : { frame: null, pad: null };
    const cFrame = colors.frame && colors.frame !== '-' ? colors.frame : null;
    const cPad = colors.pad && colors.pad !== '-' ? colors.pad : null;
    const detail = [cFrame, cPad].filter(Boolean).join(' \u00b7 ');
    this._hsShowGhost({
      ax: cursorSx, ay: cursorSy,
      title: String(info.title || obj.equipmentId).split(' - ')[0].trim(),
      detail,
      thumb: info.imageUrl || '',
      url: info.url || '',
    });
  }

  _dismissHoverCallout() {
    this._hsCancelSwitch();
    if (this._hoverCalloutTimer) {
      clearTimeout(this._hoverCalloutTimer);
      this._hoverCalloutTimer = null;
    }
    if (this._hoverCalloutGraceTimer) {
      clearTimeout(this._hoverCalloutGraceTimer);
      this._hoverCalloutGraceTimer = null;
    }
    this._hoverCalloutObj = null;
    const el = this._hoverCalloutEl;
    if (!el) return;
    this._hoverCalloutEl = null;
    this._hsStopSpin(el);
    el.classList.add('dismissing');
    setTimeout(() => el.remove(), 260);
  }

  _startEquipPulse() { /* removed */ }
  _stopEquipPulse() { /* removed */ }

  _buildEquipmentCardEl(entry) {
    const ecard = document.createElement('div');
    ecard.className = 'rd-space-equipment-card';

    // Product image
    const img = document.createElement('div');
    img.className = 'rd-space-equipment-img';
    if (entry.imageUrl) {
      img.style.backgroundImage = `url(${entry.imageUrl})`;
    } else {
      // Placeholder SVG (dumbbell icon)
      img.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="width:28px;height:28px;color:#999;margin:auto;display:block;position:relative;top:50%;transform:translateY(-50%)"><rect x="1" y="7" width="4" height="10" rx="1"/><rect x="19" y="7" width="4" height="10" rx="1"/><line x1="5" y1="12" x2="19" y2="12"/><rect x="5" y="9" width="2" height="6" rx="0.5"/><rect x="17" y="9" width="2" height="6" rx="0.5"/></svg>';
    }
    ecard.appendChild(img);

    // Info stack
    const info = document.createElement('div');
    info.className = 'rd-space-equipment-info';

    if (entry.url) {
      const nameLink = document.createElement('a');
      nameLink.className = 'rd-space-equipment-name rd-space-equipment-link';
      nameLink.href = entry.url;
      nameLink.target = '_blank';
      nameLink.rel = 'noopener';
      nameLink.textContent = entry.title;
      nameLink.addEventListener('click', (e) => e.stopPropagation());
      info.appendChild(nameLink);
    } else {
      const nameDiv = document.createElement('div');
      nameDiv.className = 'rd-space-equipment-name';
      nameDiv.textContent = entry.title;
      info.appendChild(nameDiv);
    }

    // Detail line: Qty · Frame · Pads (single line, dot-separated like flooring)
    const parts = [];
    if (entry.count > 0) parts.push(`Qty: ${entry.count}`);
    const colors = entry.vendor ? this._getEquipColorNames(entry.vendor, entry.equipmentId) : { frame: null, pad: null };
    if (colors.frame) parts.push(`Frame: ${colors.frame}`);
    if (colors.pad) parts.push(`Pads: ${colors.pad}`);
    if (parts.length) {
      const detailDiv = document.createElement('div');
      detailDiv.className = 'rd-space-equipment-detail';
      detailDiv.textContent = parts.join('  \u00B7  ');
      info.appendChild(detailDiv);
    }

    ecard.appendChild(info);
    return ecard;
  }

  _buildSpaceCard(room, index, stats) {
    const card = document.createElement('div');
    card.className = 'rd-space-card' + (stats.isSubtract ? ' subtract' : '');
    card.setAttribute('data-room-index', String(index));

    const displayName = room.name + (stats.isSubtract ? ' (Subtracted)' : '');
    const areaText = this.formatArea(Math.abs(stats.area));
    const totalWallLF = stats.perimeterLF + stats.dividerLF;

    // Compute zones directly from math module (no overlay dependency)
    let zones = null;
    if (room.closed && window.RoomDesignerMath) {
      const geo = this._getRoomGeoCache(room);
      if (geo && geo.roomPolygon && geo.roomPolygon.length >= 3) {
        // Gather divider segments inside this room
        const poly = geo.roomPolygon;
        const divSegs = [];
        const EPS = 0.1;
        const onBoundary = (pt) => {
          for (let i = 0; i < poly.length; i++) {
            if (RoomDesignerMath.distanceToWall(pt, { start: poly[i], end: poly[(i + 1) % poly.length] }) < EPS) return true;
          }
          return false;
        };
        if (this.wallSegmentGroups) {
          for (const group of this.wallSegmentGroups) {
            for (const seg of group.segments) {
              const sIn = RoomDesignerMath.pointInPolygon(seg.start.x, seg.start.y, poly) || onBoundary(seg.start);
              const eIn = RoomDesignerMath.pointInPolygon(seg.end.x, seg.end.y, poly) || onBoundary(seg.end);
              if (sIn && eIn) divSegs.push(seg);
            }
          }
        }
        const holes = [...(geo.cutoutPolys || []), ...(geo.holePolygons || [])];
        zones = RoomDesignerMath.computeRoomZones(poly, divSegs, holes);
      }
    }
    const zoneCount = zones ? zones.length : 1;

    // Header
    const header = document.createElement('div');
    header.className = 'rd-space-header';

    const thumb = document.createElement('canvas');
    thumb.className = 'rd-space-thumb';
    thumb.width = 240; thumb.height = 160;
    header.appendChild(thumb);

    const info = document.createElement('div');
    info.className = 'rd-space-header-info';
    const nameRow = document.createElement('div');
    nameRow.className = 'rd-space-name-row';
    const nameEl = document.createElement('h4');
    nameEl.className = 'rd-space-name';
    nameEl.id = `spaceName_${index}`;
    nameEl.textContent = displayName;
    nameRow.appendChild(nameEl);
    const editBtn = document.createElement('button');
    editBtn.className = 'rd-space-edit-btn';
    editBtn.textContent = '\u270E';
    editBtn.title = 'Rename';
    editBtn.addEventListener('click', (e) => { e.stopPropagation(); this.editRoomName(index); });
    nameRow.appendChild(editBtn);
    info.appendChild(nameRow);

    // Inline space stats — always visible on parent card
    const inlineStats = document.createElement('div');
    inlineStats.className = 'rd-space-inline-stats';
    const statItems = [areaText];
    if (stats.doorCount > 0) statItems.push(`${stats.doorCount} door${stats.doorCount > 1 ? 's' : ''}`);
    statItems.push(this.formatMeasurement(totalWallLF) + ' walls');
    if (zoneCount > 1) statItems.push(`${zoneCount} zones`);
    inlineStats.textContent = statItems.join('  \u00B7  ');
    info.appendChild(inlineStats);

    // Zone breakdown — list each zone with its area when more than 1
    if (zoneCount > 1 && zones) {
      const zoneList = document.createElement('div');
      zoneList.className = 'rd-space-zone-list';
      zones.forEach((z, i) => {
        const zoneEl = document.createElement('span');
        zoneEl.className = 'rd-space-zone-item';
        const znName = (room.zoneNames && room.zoneNames[i]) || ('Zone ' + (i + 1));
        zoneEl.textContent = `${znName}: ${Math.round(Math.abs(z.area)).toLocaleString()} sq ft`;
        zoneList.appendChild(zoneEl);
      });
      info.appendChild(zoneList);
    }

    header.appendChild(info);

    // Down-chevron SVG (replaces +/−)
    const chevron = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    chevron.setAttribute('class', 'rd-space-chevron');
    chevron.setAttribute('viewBox', '0 0 20 20');
    chevron.setAttribute('fill', 'none');
    chevron.setAttribute('stroke', 'currentColor');
    chevron.setAttribute('stroke-width', '2');
    chevron.setAttribute('stroke-linecap', 'round');
    chevron.setAttribute('stroke-linejoin', 'round');
    const chevPath = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
    chevPath.setAttribute('points', '5 8 10 13 15 8');
    chevron.appendChild(chevPath);
    header.appendChild(chevron);

    card.appendChild(header);

    // Details (max-height slide animation)
    const details = document.createElement('div');
    details.className = 'rd-space-details';
    const detailsInner = document.createElement('div');
    detailsInner.className = 'rd-space-details-inner';

    const addStat = (label, value, container) => {
      const row = document.createElement('div');
      row.className = 'rd-space-stat';
      row.innerHTML = `<span class="rd-space-stat-label">${label}</span><span class="rd-space-stat-value">${value}</span>`;
      container.appendChild(row);
    };

    // Section 1 (Space Overview) removed — stats now shown inline on parent card header

    // Section 2: Flooring Summary (conditional)
    if (typeof this._getPerRoomFlooringData === 'function') {
      const flooringData = this._getPerRoomFlooringData(room);
      if (flooringData && flooringData.length > 0) {
        const floorSec = document.createElement('div');
        floorSec.className = 'rd-space-section';
        const floorTitle = document.createElement('div');
        floorTitle.className = 'rd-space-section-title';
        floorTitle.textContent = 'Flooring Breakdown';
        floorSec.appendChild(floorTitle);
        for (const entry of flooringData) {
          floorSec.appendChild(this._buildFlooringCardEl(entry, 'rd-space-details'));
        }
        this._addSeeMoreCollapse(floorSec, 3);
        detailsInner.appendChild(floorSec);
      }
    }

    // Section 2b: Turf Breakdown (conditional)
    if (typeof this._getPerRoomTurfData === 'function') {
      const turfData = this._getPerRoomTurfData(room);
      if (turfData && turfData.length > 0) {
        const turfSec = document.createElement('div');
        turfSec.className = 'rd-space-section';
        const turfTitle = document.createElement('div');
        turfTitle.className = 'rd-space-section-title';
        turfTitle.textContent = 'Turf Breakdown';
        turfSec.appendChild(turfTitle);
        for (const entry of turfData) {
          turfSec.appendChild(this._buildFlooringCardEl(entry, 'rd-space-details'));
        }
        detailsInner.appendChild(turfSec);
      }
    }

    // Section 2c: Platform Breakdown (conditional)
    const platformObjs = this._getObjectsInRoom(room).filter(o => o.type === 'platform');
    if (platformObjs.length > 0) {
      const platSec = document.createElement('div');
      platSec.className = 'rd-space-section';
      const platTitle = document.createElement('div');
      platTitle.className = 'rd-space-section-title';
      platTitle.textContent = 'Platform Breakdown';
      platSec.appendChild(platTitle);

      const platEntries = this._buildPlatformEntries(platformObjs);
      for (const entry of platEntries) {
        platSec.appendChild(this._buildFlooringCardEl(entry, 'rd-space-details'));
      }
      detailsInner.appendChild(platSec);
    }

    // Section 3: Equipment (conditional)
    const roomObjects = this._getObjectsInRoom(room);
    if (roomObjects.length > 0) {
      const equipObjs = roomObjects.filter(o => o.type === 'equipment' && o.equipmentId);

      // Equipment cards grouped by equipmentId
      if (equipObjs.length > 0) {
        const equipSec = document.createElement('div');
        equipSec.className = 'rd-space-section';
        const equipTitle = document.createElement('div');
        equipTitle.className = 'rd-space-section-title';
        equipTitle.textContent = 'Equipment Breakdown';
        equipSec.appendChild(equipTitle);

        const grouped = {};
        for (const obj of equipObjs) {
          if (!grouped[obj.equipmentId]) grouped[obj.equipmentId] = { count: 0 };
          grouped[obj.equipmentId].count++;
        }
        const entries = Object.entries(grouped).map(([eqId, g]) => {
          const meta = this._equipmentImages && this._equipmentImages[eqId];
          return {
            equipmentId: eqId,
            title: (meta && meta.title) || eqId.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            sku: (meta && meta.sku) || '',
            imageUrl: (meta && meta.imageUrl) || null,
            widthIn: meta && meta.widthIn,
            depthIn: meta && meta.depthIn,
            productType: (meta && meta.productType) || '',
            vendor: (meta && meta.vendor) || '',
            url: (meta && meta.url) || '',
            count: g.count
          };
        }).sort((a, b) => a.title.localeCompare(b.title));

        for (const entry of entries) {
          equipSec.appendChild(this._buildEquipmentCardEl(entry));
        }
        this._addSeeMoreCollapse(equipSec, 3);
        detailsInner.appendChild(equipSec);
      }

    }

    const hasExpandableContent = detailsInner.children.length > 0;

    if (hasExpandableContent) {
      details.appendChild(detailsInner);
      card.appendChild(details);

      // Toggle accordion
      header.addEventListener('click', () => {
        const isOpen = card.classList.contains('expanded');
        if (isOpen) {
          details.style.maxHeight = '0';
          card.classList.remove('expanded');
        } else {
          details.style.maxHeight = details.scrollHeight + 'px';
          card.classList.add('expanded');
        }
      });
    } else {
      chevron.style.display = 'none';
      card.classList.add('no-expand');
    }

    // Draw thumbnail
    this._drawSpaceThumb(thumb, room);

    return card;
  }

  _buildTotalSummaryCard(totalArea, totalDoors, totalWallLF, totalZones) {
    const wrap = document.createElement('div');
    wrap.className = 'rd-total-summary';

    // Header
    const header = document.createElement('div');
    header.className = 'rd-total-header';
    const headerInfo = document.createElement('div');
    headerInfo.className = 'rd-total-header-info';
    const label = document.createElement('span');
    label.textContent = `Combined Total`;
    headerInfo.appendChild(label);

    // Inline combined stats
    const areaText = totalArea >= 1 ? `${Math.round(totalArea)} sq ft` : `${Math.round(totalArea * 144)} sq in`;
    const statItems = [areaText];
    if (totalDoors > 0) statItems.push(`${totalDoors} door${totalDoors > 1 ? 's' : ''}`);
    statItems.push(this.formatMeasurement(totalWallLF) + ' walls');
    if (totalZones > 1) statItems.push(`${totalZones} zones`);
    const totalEquip = (this.objects || []).filter(o => o.type === 'equipment' && o.equipmentId).length;
    if (totalEquip > 0) statItems.push(`${totalEquip} item${totalEquip > 1 ? 's' : ''}`);
    const inlineStats = document.createElement('div');
    inlineStats.className = 'rd-space-inline-stats';
    inlineStats.textContent = statItems.join('  \u00B7  ');
    headerInfo.appendChild(inlineStats);
    header.appendChild(headerInfo);

    const chevron = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    chevron.setAttribute('class', 'rd-total-chevron');
    chevron.setAttribute('viewBox', '0 0 20 20');
    chevron.setAttribute('fill', 'none');
    chevron.setAttribute('stroke', 'currentColor');
    chevron.setAttribute('stroke-width', '2');
    chevron.setAttribute('stroke-linecap', 'round');
    chevron.setAttribute('stroke-linejoin', 'round');
    const chevPath = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
    chevPath.setAttribute('points', '5 8 10 13 15 8');
    chevron.appendChild(chevPath);
    header.appendChild(chevron);
    wrap.appendChild(header);

    // Details (expandable)
    const details = document.createElement('div');
    details.className = 'rd-total-details';
    const inner = document.createElement('div');
    inner.className = 'rd-total-details-inner';

    // Combined flooring breakdown
    if (typeof this._getCombinedFlooringData === 'function') {
      const combinedData = this._getCombinedFlooringData();
      if (combinedData && combinedData.length > 0) {
        const floorSec = document.createElement('div');
        floorSec.className = 'rd-space-section';
        const floorTitle = document.createElement('div');
        floorTitle.className = 'rd-space-section-title';
        floorTitle.textContent = 'Flooring Breakdown';
        floorSec.appendChild(floorTitle);
        for (const entry of combinedData) {
          floorSec.appendChild(this._buildFlooringCardEl(entry, 'rd-total-details'));
        }
        this._addSeeMoreCollapse(floorSec, 3);
        inner.appendChild(floorSec);
      }
    }

    // Combined turf breakdown
    if (typeof this._getCombinedTurfData === 'function') {
      const combinedTurf = this._getCombinedTurfData();
      if (combinedTurf && combinedTurf.length > 0) {
        const turfSec = document.createElement('div');
        turfSec.className = 'rd-space-section';
        const turfTitle = document.createElement('div');
        turfTitle.className = 'rd-space-section-title';
        turfTitle.textContent = 'Turf Breakdown';
        turfSec.appendChild(turfTitle);
        for (const entry of combinedTurf) {
          turfSec.appendChild(this._buildFlooringCardEl(entry, 'rd-total-details'));
        }
        inner.appendChild(turfSec);
      }
    }

    // Combined platform breakdown
    const allPlatforms = (this.objects || []).filter(o => o.type === 'platform');
    if (allPlatforms.length > 0) {
      const platSec = document.createElement('div');
      platSec.className = 'rd-space-section';
      const platTitle = document.createElement('div');
      platTitle.className = 'rd-space-section-title';
      platTitle.textContent = 'Platform Breakdown';
      platSec.appendChild(platTitle);

      const platEntries = this._buildPlatformEntries(allPlatforms);
      for (const entry of platEntries) {
        platSec.appendChild(this._buildFlooringCardEl(entry, 'rd-total-details'));
      }
      inner.appendChild(platSec);
    }

    // Combined equipment breakdown (aggregate across all rooms)
    const allEquip = (this.objects || []).filter(o => o.type === 'equipment' && o.equipmentId);
    if (allEquip.length > 0) {
      const grouped = {};
      for (const obj of allEquip) {
        if (!grouped[obj.equipmentId]) grouped[obj.equipmentId] = { count: 0 };
        grouped[obj.equipmentId].count++;
      }
      const entries = Object.entries(grouped).map(([eqId, g]) => {
        const meta = this._equipmentImages && this._equipmentImages[eqId];
        return {
          equipmentId: eqId,
          title: (meta && meta.title) || eqId,
          sku: (meta && meta.sku) || '',
          imageUrl: (meta && meta.imageUrl) || null,
          widthIn: meta && meta.widthIn,
          depthIn: meta && meta.depthIn,
          productType: (meta && meta.productType) || '',
          vendor: (meta && meta.vendor) || '',
          count: g.count
        };
      }).sort((a, b) => a.title.localeCompare(b.title));

      const equipSec = document.createElement('div');
      equipSec.className = 'rd-space-section';
      const equipTitle = document.createElement('div');
      equipTitle.className = 'rd-space-section-title';
      equipTitle.textContent = 'Equipment Breakdown';
      equipSec.appendChild(equipTitle);
      for (const entry of entries) {
        equipSec.appendChild(this._buildEquipmentCardEl(entry));
      }
      this._addSeeMoreCollapse(equipSec, 3);
      inner.appendChild(equipSec);
    }

    details.appendChild(inner);
    wrap.appendChild(details);

    // Toggle accordion
    header.addEventListener('click', () => {
      const isOpen = wrap.classList.contains('expanded');
      if (isOpen) {
        details.style.maxHeight = '0';
        wrap.classList.remove('expanded');
      } else {
        details.style.maxHeight = details.scrollHeight + 'px';
        wrap.classList.add('expanded');
      }
    });

    return wrap;
  }

  _drawSpaceThumb(canvas, room) {
    const ctx = canvas.getContext('2d');
    const w = canvas.width, h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    if (!room.closed || room.walls.length < 3) return;

    const geo = this._getRoomGeoCache(room);
    const poly = geo.roomPolygon;
    if (!poly || poly.length < 3) return;

    // Bounding box
    let mnX = Infinity, mnY = Infinity, mxX = -Infinity, mxY = -Infinity;
    for (const p of poly) {
      if (p.x < mnX) mnX = p.x; if (p.y < mnY) mnY = p.y;
      if (p.x > mxX) mxX = p.x; if (p.y > mxY) mxY = p.y;
    }
    const pad = 16;
    const bw = mxX - mnX || 1, bh = mxY - mnY || 1;
    const scale = Math.min((w - pad * 2) / bw, (h - pad * 2) / bh);
    const ox = (w - bw * scale) / 2;
    const oy = (h - bh * scale) / 2;
    const tx = (px) => (px - mnX) * scale + ox;
    const ty = (py) => (py - mnY) * scale + oy;

    // Fill polygon
    ctx.beginPath();
    ctx.moveTo(tx(poly[0].x), ty(poly[0].y));
    for (let i = 1; i < poly.length; i++) ctx.lineTo(tx(poly[i].x), ty(poly[i].y));
    ctx.closePath();
    ctx.fillStyle = room.type === 'subtract' ? 'rgba(192,57,43,0.1)' : 'rgba(0,107,204,0.08)';
    ctx.fill();

    // Stroke visible walls
    const walls = geo.allVisibleWalls || room.walls;
    ctx.strokeStyle = this.darkMode ? '#fff' : '#000F1C';
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (const wall of walls) {
      ctx.moveTo(tx(wall.start.x), ty(wall.start.y));
      ctx.lineTo(tx(wall.end.x), ty(wall.end.y));
    }
    ctx.stroke();

    // Doors — draw as solid fill matching wall color (no gaps in thumbnail)
    if (room.doors && room.doors.length > 0) {
      ctx.strokeStyle = this.darkMode ? '#fff' : '#000F1C';
      ctx.lineWidth = 2;
      for (const door of room.doors) {
        if (!door.wall) continue;
        const hp = this.paramToPoint(door.wall, door.hingeParam);
        const ep = this.paramToPoint(door.wall, door.endParam);
        ctx.beginPath();
        ctx.moveTo(tx(hp.x), ty(hp.y));
        ctx.lineTo(tx(ep.x), ty(ep.y));
        ctx.stroke();
      }
    }

  }

  editRoomName(index) {
    const room = this.rooms[index];
    const nameEl = document.getElementById(`spaceName_${index}`);
    if (!nameEl) return;
    const currentName = room.name;
    const input = document.createElement('input');
    input.type = 'text';
    input.value = currentName;
    input.className = 'rd-space-name-input';
    const nameRow = nameEl.parentElement;
    nameRow.replaceChild(input, nameEl);
    const editBtn = nameRow.querySelector('.rd-space-edit-btn');
    if (editBtn) editBtn.style.display = 'none';
    input.focus(); input.select();
    const finishEdit = () => {
      const newName = input.value.trim() || `Space ${index + 1}`;
      if (newName !== currentName) this.saveUndoState();
      room.name = newName;
      this.updateMeasurements();
      this.render();
    };
    input.addEventListener('blur', finishEdit);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') input.blur();
      if (e.key === 'Escape') { input.value = currentName; input.blur(); }
    });
  }

  /* ── AREA LABELS ────────────────────────────────────────────────
     CUSTOM_ROOMDESIGNER_BY_CLAUDE
     Free-placed pills that name a part of the plan — "Plate-Loaded",
     "Selectorized", "Free Weights" — so a PDF explains itself.

     A label is an ordinary entry in this.objects with a width/depth, which is
     what buys undo, save/load, drag, nudge, copy/paste and delete without a line
     of new code in any of them. The size it carries is a WORLD size, so the pill
     keeps its proportion to the room at any zoom and on any PDF sheet scale.
     ──────────────────────────────────────────────────────────────── */

  /** True when there is a selection and every item in it is an area label.
   *  The Customize tab blocks delete and nudge because a selection there means
   *  "customize this", not "move or remove it" — but the label tool lives on that
   *  tab, so a pure-label selection is the one case that has to pass through. */
  _selectionIsOnlyLabels() {
    return this.selectedObjects.size > 0 && [...this.selectedObjects].every(o => o.type === 'label');
  }

  /** The label palette — deliberately NOT read from `this._tc`.
   *
   *  ⚠️ This is the fix for "I placed them and they were dark with white text, then
   *  the PDF came out white with dark text". Print prep forces `darkMode = false`
   *  and swaps `_tc` for the print scheme, so every label flipped on export. A
   *  label is an annotation the user AUTHORED — it should look on paper the way it
   *  looked when they placed it — so print stashes the authoring theme in
   *  `_labelAuthorDark` and the pill reads that instead. Everything else on the
   *  plan still re-themes for paper; only the labels are pinned.
   *
   *  Glass mode is the see-through variant: a low-alpha tint over a real blurred
   *  snapshot of the plan (see _paintLabelPill), with the text left fully opaque
   *  and given a halo in the opposite tone so it stays crisp over anything. */
  _labelColors() {
    const dark = (this._labelAuthorDark != null) ? this._labelAuthorDark : this.darkMode;
    const glass = !!this.glassLabels;
    if (dark) {
      return {
        glass,
        bg: 'rgba(18, 26, 46, 1)',
        border: 'rgba(200, 210, 230, 0.15)',
        glassBg: 'rgba(18, 26, 46, 0.42)',
        glassBorder: 'rgba(210, 225, 255, 0.42)',
        text: '#ffffff',
        textHalo: 'rgba(0, 8, 20, 0.85)',
      };
    }
    return {
      glass,
      bg: 'rgba(255, 255, 255, 1)',
      border: 'rgba(0, 15, 28, 0.08)',
      glassBg: 'rgba(255, 255, 255, 0.48)',
      glassBorder: 'rgba(255, 255, 255, 0.75)',
      text: '#000F1C',
      textHalo: 'rgba(255, 255, 255, 0.85)',
    };
  }

  /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — four Display flags have TWO owners, and that is
     the whole of this bug family. The user owns them from the drawer; the tab
     machinery also forces them (the Draw tab shows no flooring, Equipment wants a
     clean view, Customize forces the material overlay on) and remembers what it
     overrode in `_stashedX` so it can hand it back on the way out.

     ⚠️ An explicit toggle has to CLEAR that stash. Otherwise the tab is still holding
     a value the user has since overruled, and hands it back the moment they change
     tab — silently undoing the thing they just clicked. The stash means "the tab
     overrode this"; once the user overrides it themselves, there is nothing to
     restore. Paired with _prefFlag() on the save side, which reads the stash so a
     save made while standing on a tab that forced a flag off does not persist the
     TAB's answer as the user's preference. */
  _clearTabStash(key) {
    const overlay = window.roomDesignerOverlay;
    if (overlay && overlay[key] != null) overlay[key] = null;
  }

  /** The user's preference for a tab-overridable flag: the stashed value when the
   *  tab is currently overriding it, otherwise the live one. */
  _prefFlag(stashKey, live) {
    const overlay = window.roomDesignerOverlay;
    const stashed = overlay ? overlay[stashKey] : null;
    return (stashed == null) ? live : stashed;
  }

  /** Cap height of a label's text, in feet. ON SCREEN only — see _printLabelFontPx. */
  _labelFontSize(size) {
    return { small: 0.75, medium: 1.1, large: 1.7 }[size] || 1.1;
  }

  /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — how big a label is ON PAPER.
     ⚠️ A world-feet size is the RIGHT model on screen and the WRONG one in print,
     and the PDF page list is what exposed it. In world feet a label is part of the
     drawing: zoom in and it grows with everything around it, which is what keeps a
     pill the user sized against a 60ft room looking the same as they pan about.
     But every printed page is fitted to the SAME sheet at whatever scale its content
     needs — a one-zone page might frame 15ft where a whole-space page frames 60 —
     so the identical label comes out four times the size on one sheet as the next.
     Ryan hit exactly that: one zone, two zones and a drawn region, three different
     label sizes in one PDF.

     In print a label is not a thing in the room, it is an ANNOTATION ON THE SHEET,
     so it is sized in sheet units and page framing cannot touch it. Same reasoning
     that stands a label back up on a rotated page (5a06e910): on screen it must stay
     glued to the drawing because hit-testing is a world-space OBB; a printed page has
     nothing to hit-test, it only has to be read.

     The tiers are POINTS on the PDF's 1188x666pt image area, which is why they are
     stated as points rather than pixels — they are a type size on a tabloid sheet,
     and that is the thing that has to stay legible. Deriving px from the canvas
     height means the reduced-quality re-render (3600x2000) prints at the same
     physical size as the full one, for free, instead of needing its own constant.
     ⚠️ The numbers are not invented — they are anchored to the page Ryan already
     accepts. Measured on a 60x40 space, "small" printed at 11.4pt on a whole-space
     page (the one he did not complain about), 14.5pt on a one-zone page and 34.3pt
     on a drawn region (the one that "looks silly"). The world model swings wider
     still at the extremes: the same tier is ~4.7pt on a 150x100 whole-space page,
     which is already too small to read, and ~28pt on a 20x15 one. Small is pinned at
     11pt so the page that looked right keeps looking right and both ends come to it. */
  _printLabelFontPx(size, canvasHOverride) {
    const PT = { small: 11, medium: 16, large: 24.5 };
    const SHEET_IMAGE_H_PT = 666;
    const pt = PT[size] || PT.medium;
    /* The painter reads it off the live canvas; the frame reservation has to ask
       BEFORE the print canvas is installed, so it passes the height explicitly. */
    const canvasH = canvasHOverride || this._cssHeight || 2667;
    return Math.max(7, pt * (canvasH / SHEET_IMAGE_H_PT));
  }

  /** Set obj.width/obj.depth (world feet) from the text and size.
   *  Everything downstream — hit-testing, the rotate handle, marquee select,
   *  bounds for zoom-to-fit — reads those two fields, so this MUST be re-run
   *  whenever the text or the size changes or the box goes stale. */
  _measureLabelBox(obj) {
    const fontFt = this._labelFontSize(obj.size);
    const text = obj.text || '';
    let emW;
    if (this.ctx && this.ctx.measureText) {
      // Measure at a reference 100px and divide — measureText ignores the current
      // transform, so this is a pure em-width and safe to call mid-render.
      this.ctx.save();
      this.ctx.font = '700 100px Arial';
      emW = this.ctx.measureText(text).width / 100;
      this.ctx.restore();
    } else {
      emW = text.length * 0.58; // headless fallback
    }
    const padX = fontFt * 0.62, padY = fontFt * 0.40;
    obj.width = Math.max(fontFt * 1.5, emW * fontFt + padX * 2);
    obj.depth = fontFt * 1.12 + padY * 2;
    return obj;
  }

  /** A label's angle in WORLD space. obj.rotation is its SCREEN angle — the pill reads
   *  upright at 0 however the plan is turned, in the editor and on the sheet alike
   *  (see _paintLabelPill) — so the world-space box the hit test and the rotate handle
   *  work in is that angle with the current canvas turn taken back out. In print the
   *  canvas turn is the page's turn, so the same line holds there. */
  _labelWorldAngle(obj) {
    return (obj.rotation || 0) - (this._canvasRotationRad || 0);
  }

  /** World-space OBB test, matching how _drawAreaLabels orients the pill. */
  _hitTestAreaLabel(point) {
    if (!this.showEquipmentLabels || !this.objects) return null;
    // Last drawn wins — labels render in array order, so the one on top is the
    // one you can see, and that is the one a click should get.
    for (let i = this.objects.length - 1; i >= 0; i--) {
      const obj = this.objects[i];
      if (obj.type !== 'label' || !obj.text) continue;
      if (!obj.width || !obj.depth) this._measureLabelBox(obj);
      const local = this._rotatePoint(point.x, point.y, obj.x, obj.y, -this._labelWorldAngle(obj));
      const halfW = obj.width / 2, halfD = obj.depth / 2;
      if (local.x >= obj.x - halfW && local.x <= obj.x + halfW &&
          local.y >= obj.y - halfD && local.y <= obj.y + halfD) return obj;
    }
    return null;
  }

  _placeAreaLabel(point) {
    const text = (this._labelText || '').trim();
    if (!text) {
      this.updateStatus('Type a label — or pick one — then click the plan.', { urgent: true });
      const input = document.getElementById('labelTextInput');
      if (input) input.focus();
      return;
    }
    this.saveUndoState();
    const obj = { type: 'label', x: point.x, y: point.y, text, size: this._labelSize || 'medium', rotation: 0 };
    this._measureLabelBox(obj);
    this.objects.push(obj);
    if (navigator.vibrate) navigator.vibrate(50);
    this.updateStatus(`"${text}" placed. Click again for another, or press Esc to stop.`);
    this.render();
    this.updateMeasurements();
    this._triggerAutoSave && this._triggerAutoSave();
  }

  /** Inline text editor over an existing label. Clearing the text deletes it —
   *  an empty pill is invisible but still selectable, which reads as a ghost. */
  _openAreaLabelEditor(obj) {
    if (this._labelEditorInput) return;
    const canvasRect = this.canvas.getBoundingClientRect();
    const sp = this.worldToScreen(obj.x, obj.y);
    const scale = this.basePixelsPerFoot * this.zoomLevel;
    const inputW = Math.max((obj.width || 4) * scale + 24, 140);

    const input = document.createElement('input');
    input.type = 'text';
    input.value = obj.text || '';
    input.className = 'rd-canvas-label-editor';
    this._labelEditorInput = input;
    input.style.cssText = `
      position: fixed;
      left: ${canvasRect.left + sp.x - inputW / 2}px;
      top: ${canvasRect.top + sp.y - 16}px;
      width: ${inputW}px;
      height: 32px;
      padding: 4px 8px;
      border: 2px solid #006BCC;
      border-radius: 4px;
      background: ${this.darkMode ? '#1e2a42' : '#fff'};
      color: ${this.darkMode ? '#e0e0e0' : '#000F1C'};
      font: 700 13px Arial;
      text-align: center;
      z-index: 9999;
      outline: none;
      box-shadow: 0 2px 12px rgba(0,0,0,0.25);
    `;
    document.body.appendChild(input);
    let focused = false;
    // Deferred for the same reason as the room-name editor: the browser hands
    // focus back to the mousedown target after this handler returns, which would
    // blur and remove the input before it was ever usable.
    requestAnimationFrame(() => { input.focus(); input.select(); focused = true; });

    let committed = false;
    const finish = () => { this._labelEditorInput = null; input.remove(); this.render(); this.updateMeasurements(); };
    const commit = () => {
      if (committed || !focused) return;
      committed = true;
      const next = input.value.trim();
      if (next !== (obj.text || '')) {
        this.saveUndoState();
        if (!next) {
          const i = this.objects.indexOf(obj);
          if (i >= 0) this.objects.splice(i, 1);
          this.selectedObjects.delete(obj);
          this._updateActionButtons();
          this.updateStatus('Label removed.');
        } else {
          obj.text = next;
          this._measureLabelBox(obj);
        }
        this._triggerAutoSave && this._triggerAutoSave();
      }
      finish();
    };
    input.addEventListener('blur', commit);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); input.blur(); }
      if (e.key === 'Escape') { e.preventDefault(); if (!committed) { committed = true; finish(); } }
      e.stopPropagation();
    });
    input.addEventListener('keyup', (e) => e.stopPropagation());
    input.addEventListener('keypress', (e) => e.stopPropagation());
  }

  /** Push the armed text/size into the popover UI, and apply a size change to a
   *  selected label so the control means the same thing before and after placing. */
  _syncLabelPopover() {
    const input = document.getElementById('labelTextInput');
    if (input && input.value !== this._labelText) input.value = this._labelText || '';
    document.querySelectorAll('#labelPopover .label-size-btn').forEach(b => {
      b.classList.toggle('active', b.dataset.value === (this._labelSize || 'medium'));
    });
    document.querySelectorAll('#labelPopover .label-style-btn').forEach(b => {
      b.classList.toggle('active', b.dataset.value === (this.glassLabels ? 'glass' : 'solid'));
    });
    document.querySelectorAll('#labelPopover .label-preset-btn').forEach(b => {
      b.classList.toggle('active', b.dataset.value === (this._labelText || '').trim());
    });
  }

  _openLabelEditor(lbl) {
    if (this._labelEditorInput) return; // already editing
    const room = lbl.room;
    const isZone = lbl.type === 'zone';

    // Current name
    let currentName;
    if (isZone) {
      currentName = (room.zoneNames && room.zoneNames[lbl.zoneIndex]) || ('Zone ' + (lbl.zoneIndex + 1));
    } else {
      currentName = room.name || '';
    }

    // Create floating input overlay
    const input = document.createElement('input');
    input.type = 'text';
    input.value = currentName;
    input.className = 'rd-canvas-label-editor';
    this._labelEditorInput = input;

    // Position over the canvas at the label's screen location
    const canvasRect = this.canvas.getBoundingClientRect();
    const absX = canvasRect.left + lbl.sx;
    const absY = canvasRect.top + lbl.sy;
    const inputW = Math.max(lbl.hw * 2 + 24, 120);
    input.style.cssText = `
      position: fixed;
      left: ${absX - inputW / 2}px;
      top: ${absY - 16}px;
      width: ${inputW}px;
      height: 32px;
      padding: 4px 8px;
      border: 2px solid #006BCC;
      border-radius: 4px;
      background: ${this.darkMode ? '#1e2a42' : '#fff'};
      color: ${this.darkMode ? '#e0e0e0' : '#000F1C'};
      font: 700 13px Arial;
      text-align: center;
      z-index: 9999;
      outline: none;
      box-shadow: 0 2px 12px rgba(0,0,0,0.25);
    `;
    document.body.appendChild(input);
    // Defer focus — browser refocuses the mousedown target (canvas) after the
    // event handler returns, which would immediately blur and remove the input.
    requestAnimationFrame(() => { input.focus(); input.select(); focused = true; });

    let committed = false;
    let focused = false;
    const commit = () => {
      if (committed) return;
      if (!focused) return; // ignore blur before first focus (browser steals focus from mousedown)
      committed = true;
      const newName = input.value.trim();
      if (isZone) {
        if (!room.zoneNames) room.zoneNames = [];
        const defaultName = 'Zone ' + (lbl.zoneIndex + 1);
        const finalName = newName || defaultName;
        if (finalName !== currentName) {
          this.saveUndoState();
          room.zoneNames[lbl.zoneIndex] = finalName === defaultName ? undefined : finalName;
        }
      } else {
        const roomIdx = this.rooms.indexOf(room);
        const defaultName = roomIdx >= 0 ? `Space ${roomIdx + 1}` : 'Space';
        const finalName = newName || defaultName;
        if (finalName !== currentName) {
          this.saveUndoState();
          room.name = finalName;
        }
      }
      this._labelEditorInput = null;
      input.remove();
      this.updateMeasurements();
      this.render();
    };
    const cancel = () => {
      if (committed) return;
      committed = true;
      this._labelEditorInput = null;
      input.remove();
    };
    input.addEventListener('blur', commit);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); input.blur(); }
      if (e.key === 'Escape') { e.preventDefault(); cancel(); }
      e.stopPropagation(); // prevent canvas shortcuts while editing
    });
    input.addEventListener('keyup', (e) => e.stopPropagation());
    input.addEventListener('keypress', (e) => e.stopPropagation());
  }

  _getObjectsInRoom(room) {
    if (!room.closed || !this.objects || this.objects.length === 0) return [];
    const geo = this._getRoomGeoCache(room);
    const poly = geo.roomPolygon;
    if (!poly || poly.length < 3) return [];
    return this.objects.filter(obj => this.pointInPolygon(obj.x, obj.y, poly));
  }

  getLayoutDetailsData() {
    const spaces = [];
    let totalArea = 0;
    for (let i = 0; i < this.rooms.length; i++) {
      const room = this.rooms[i];
      const stats = this._computeSpaceStats(room);
      totalArea += stats.isSubtract ? -stats.area : stats.area;
      spaces.push({
        name: room.name || `Space ${i + 1}`,
        area: stats.area,
        isSubtract: stats.isSubtract,
        perimeterLF: stats.perimeterLF,
        doorCount: stats.doorCount,
        doorwayLF: stats.doorwayLF,
        dividerLF: stats.dividerLF,
        transitionStripLF: stats._transitionStripLF,
        coveBaseLF: stats._coveBaseLF
      });
    }
    return { spaces, totalArea };
  }

  /* ── UNDO / REDO / CLEAR ── */

  _relinkDoorWallRefs() {
    const relink = (doors, allWalls) => {
      for (const door of doors) {
        if (!door.wall) continue;
        const dw = door.wall;
        const match = allWalls.find(w =>
          Math.abs(w.start.x - dw.start.x) < 0.001 && Math.abs(w.start.y - dw.start.y) < 0.001 &&
          Math.abs(w.end.x - dw.end.x) < 0.001 && Math.abs(w.end.y - dw.end.y) < 0.001
        );
        if (match) door.wall = match;
      }
    };
    for (const room of this.rooms) {
      const allWalls = [...room.walls];
      if (room.cutouts) room.cutouts.forEach(c => allWalls.push(...c.walls));
      relink(room.doors, allWalls);
    }
    if (this.currentRoom) {
      const allWalls = [...this.currentRoom.walls];
      if (this.currentRoom.cutouts) this.currentRoom.cutouts.forEach(c => allWalls.push(...c.walls));
      relink(this.currentRoom.doors || [], allWalls);
    }
    // Relink wall segment group doors
    for (const group of this.wallSegmentGroups) {
      if (group.doors && group.doors.length > 0) {
        relink(group.doors, group.segments);
      }
    }
  }

  /* ── PLACEMENT SNAPSHOT HELPERS ── */

  _captureDoorMobileSnapshot() {
    const dm = this._doorMobile;
    if (!dm) return null;
    // Find wall/room indices for serialization safety
    let roomIdx = -1, wallIdx = -1, isCurrentRoom = false;
    for (let ri = 0; ri < this.rooms.length; ri++) {
      const room = this.rooms[ri];
      for (let wi = 0; wi < room.walls.length; wi++) {
        if (room.walls[wi] === dm.wall) { roomIdx = ri; wallIdx = wi; break; }
      }
      if (roomIdx >= 0) break;
    }
    if (roomIdx < 0 && this.currentRoom) {
      for (let wi = 0; wi < this.currentRoom.walls.length; wi++) {
        if (this.currentRoom.walls[wi] === dm.wall) { wallIdx = wi; isCurrentRoom = true; break; }
      }
    }
    return {
      type: 'doorMobile',
      phase: dm.phase, param: dm.param, point: { x: dm.point.x, y: dm.point.y },
      roomIdx, wallIdx, isCurrentRoom,
      endDir: dm.endDir, endParam: dm.endParam,
      swingIdx: dm.swingIdx,
      swingOption: dm.swingOption ? { ...dm.swingOption } : null,
      travelDir: dm.travelDir,
      doorPlacement: this.doorPlacement ? {
        hingeParam: this.doorPlacement.hingeParam,
        endParam: this.doorPlacement.endParam,
        phase: this.doorPlacement.phase
      } : null,
      doorType: this.doorType, doorWidth: this.doorWidth, objectType: this.objectType
    };
  }

  _restoreDoorMobileFromSnapshot(snap) {
    if (!snap || snap.type !== 'doorMobile') return false;
    // Re-find wall and room by indices
    let wall, room;
    if (snap.isCurrentRoom && this.currentRoom) {
      room = this.currentRoom;
      wall = room.walls[snap.wallIdx];
    } else if (snap.roomIdx >= 0 && snap.roomIdx < this.rooms.length) {
      room = this.rooms[snap.roomIdx];
      wall = room ? room.walls[snap.wallIdx] : null;
    }
    if (!wall || !room) {
      this.updateStatus('Cannot restore — wall no longer exists.', { error: true });
      this.setActiveTool('hand');
      this.render();
      return false;
    }
    this.doorType = snap.doorType;
    this.doorWidth = snap.doorWidth;
    this.objectType = snap.objectType;
    this.activeTool = 'door';
    this.mode = 'door';
    // Update toolbar highlights
    document.getElementById('objectsToolBtn').classList.add('active');
    document.getElementById('selectToolBtn').classList.remove('active');
    document.getElementById('handToolBtn').classList.remove('active');
    document.getElementById('penToolBtn').classList.remove('active');
    document.getElementById('eraserBtn').classList.remove('active');
    this._doorMobile = {
      phase: snap.phase, wall, room, param: snap.param,
      point: { x: snap.point.x, y: snap.point.y },
      endDir: snap.endDir, endParam: snap.endParam,
      swingOptions: null, swingIdx: snap.swingIdx,
      swingOption: snap.swingOption, travelDir: snap.travelDir
    };
    this.mouseWorldRaw = { x: snap.point.x, y: snap.point.y };
    this.mouseWorld = { x: snap.point.x, y: snap.point.y };
    if (snap.doorPlacement) {
      this.doorPlacement = {
        wall, room,
        hingeParam: snap.doorPlacement.hingeParam,
        hingePoint: this.paramToPoint(wall, snap.doorPlacement.hingeParam),
        endParam: snap.doorPlacement.endParam,
        phase: snap.doorPlacement.phase
      };
    } else {
      this.doorPlacement = null;
    }
    // Recompute swing options for Phase 3
    if (snap.phase === 3) {
      this._doorMobile.swingOptions = this._getSwingOptions();
    }
    // Restore UI for the phase
    this._updateDoorMobileArrows();
    this._clearCheckPulse();
    this._clearArrowPulse();
    this._updateJoystickVisibility();
    this._updateFloatingCancel();
    this._updateFloatingRepeat();
    if (snap.phase === 1) {
      this._startArrowPulse(3000);
      this._startCheckPulse(3000);
      this.updateStatus('Slide door along wall, then tap \u2713.');
    } else if (snap.phase === 3) {
      this._updateDoorSwingArrows();
      this._startArrowPulse(1500);
      this._startCheckPulse(3000);
      this.updateStatus('Tap a dot or use arrows, then \u2713.');
    }
    this.render();
    this.updateMeasurements();
    return true;
  }

  undo() {
    // === DOOR MOBILE PHASE UNDO (navigate backward within placement workflow) ===
    if (this._doorMobile) {
      const dm = this._doorMobile;
      if (dm.phase === 1) {
        // Phase 1 → cancel placement
        this._placementRedoStack.push({
          type: 'doorPhase',
          dm: { phase: 1, wall: dm.wall, room: dm.room, param: dm.param, point: { ...dm.point }, travelDir: dm.travelDir },
          doorPlacement: null,
          mouseWorldRaw: this.mouseWorldRaw ? { ...this.mouseWorldRaw } : null,
          mouseWorld: this.mouseWorld ? { ...this.mouseWorld } : null,
          doorType: this.doorType, doorWidth: this.doorWidth, objectType: this.objectType
        });
        this._resetDoorMobileState();
        this.mouseWorldRaw = null; this.mouseWorld = null;
        this._enterDoorIdleState();
        this.updateStatus('Door placement cancelled.');
        this.render();
        return;
      }
      if (dm.phase === 2 || dm.phase === 3) {
        // Phase 2/3 → back to Phase 1
        this._placementRedoStack.push({
          type: 'doorPhase',
          dm: {
            phase: dm.phase, wall: dm.wall, room: dm.room, param: dm.param, point: { ...dm.point },
            endDir: dm.endDir, endParam: dm.endParam,
            swingOptions: dm.swingOptions, swingIdx: dm.swingIdx,
            swingOption: dm.swingOption ? { ...dm.swingOption } : null, travelDir: dm.travelDir
          },
          doorPlacement: this.doorPlacement ? {
            wall: this.doorPlacement.wall, room: this.doorPlacement.room,
            hingeParam: this.doorPlacement.hingeParam, endParam: this.doorPlacement.endParam, phase: this.doorPlacement.phase
          } : null,
          mouseWorldRaw: this.mouseWorldRaw ? { ...this.mouseWorldRaw } : null,
          mouseWorld: this.mouseWorld ? { ...this.mouseWorld } : null,
          doorType: this.doorType, doorWidth: this.doorWidth, objectType: this.objectType
        });
        dm.phase = 1;
        this.doorPlacement = null;
        dm.swingOptions = null; dm.swingIdx = null; dm.swingOption = null; dm.endDir = null;
        this._updateDoorMobileArrows();
        this._clearCheckPulse(); this._clearArrowPulse();
        this._startArrowPulse(3000); this._startCheckPulse(3000);
        this.updateStatus('Slide door along wall, then tap \u2713.');
        this.render();
        return;
      }
    }

    // === OBJECT PREVIEW UNDO (pillar/column positioning → cancel) ===
    if (this._isMobileToolbar && this.activeTool === 'door' && !this._doorMobile && this.mouseWorldRaw
        && (this.objectType === 'rectangle' || this.objectType === 'circle')) {
      // Check if top of undo stack is a post-placement entry — if so, fall through to normal undo
      const top = this.undoStack.length > 0 ? this.undoStack[this.undoStack.length - 1] : null;
      if (!top || !top._placementRestore || top._placementRestore.type !== 'objectPreview') {
        // No placement to undo — cancel the preview
        this._placementRedoStack.push({
          type: 'objectPreview', objectType: this.objectType,
          mouseWorldRaw: { ...this.mouseWorldRaw }, mouseWorld: { ...this.mouseWorld }
        });
        this.mouseWorldRaw = null; this.mouseWorld = null;
        this._enterDoorIdleState();
        this.updateStatus('Placement cancelled.');
        this._updateJoystickVisibility();
        this.render();
        return;
      }
      // Has _placementRestore — fall through to normal undo (removes object + restores preview)
    }

    // === NORMAL UNDO ===
    if (this.undoStack.length === 0) return;
    // Push current state to redo (capture any active placement context)
    const redoEntry = { rooms: JSON.parse(JSON.stringify(this.rooms)), currentRoom: this.currentRoom ? JSON.parse(JSON.stringify(this.currentRoom)) : null, objects: JSON.parse(JSON.stringify(this.objects)), wallSegmentGroups: JSON.parse(JSON.stringify(this.wallSegmentGroups)), currentWallSegmentGroup: this.currentWallSegmentGroup ? JSON.parse(JSON.stringify(this.currentWallSegmentGroup)) : null, measurements: JSON.parse(JSON.stringify(this._measurements)), measureNextId: this._measureNextId };
    if (this._doorMobile) {
      redoEntry._placementRestore = this._captureDoorMobileSnapshot();
    } else if (this._isMobileToolbar && this.activeTool === 'door' && !this._doorMobile && this.mouseWorldRaw
        && (this.objectType === 'rectangle' || this.objectType === 'circle')) {
      redoEntry._placementRestore = { type: 'objectPreview', objectType: this.objectType,
        mouseWorldRaw: { ...this.mouseWorldRaw }, mouseWorld: { ...this.mouseWorld } };
    }
    this.redoStack.push(redoEntry);
    const state = this.undoStack.pop();
    this.rooms = JSON.parse(JSON.stringify(state.rooms));
    this.objects = JSON.parse(JSON.stringify(state.objects || []));
    this.currentRoom = state.currentRoom ? JSON.parse(JSON.stringify(state.currentRoom)) : { walls: [], doors: [], cutouts: [], type: this.roomType, closed: false };
    this.wallSegmentGroups = state.wallSegmentGroups ? JSON.parse(JSON.stringify(state.wallSegmentGroups)) : [];
    this.currentWallSegmentGroup = state.currentWallSegmentGroup ? JSON.parse(JSON.stringify(state.currentWallSegmentGroup)) : null;
    if (state.measurements) { this._measurements = JSON.parse(JSON.stringify(state.measurements)); this._measureNextId = state.measureNextId || 1; }
    this._selectedMeasurements.clear(); this._measurePhase = 0; this._measurePendingEnd = null; this._measureDragging = null;
    this._updateMeasureClearBtn();
    this._relinkDoorWallRefs();
    this._turfLogoFocusedIdx = null;
    this._turfLogoSelectedIdxs.clear();
    for (const r of this.rooms) { r._zoneCache = null; r._zoneLayouts = null; }
    if (window.roomDesignerOverlay) { window.roomDesignerOverlay._floorSelections = []; window.roomDesignerOverlay._floorSelectCorner1 = null; window.roomDesignerOverlay._floorSelectEnd = null; window.roomDesignerOverlay._floorInfoPinned = false; window.roomDesignerOverlay._hideFloorInfo(); }
    // Check for placement restore context on the undo entry
    if (state._placementRestore) {
      if (state._placementRestore.type === 'doorMobile') {
        this._resetDoorMobileState();
        this._restoreDoorMobileFromSnapshot(state._placementRestore);
        return;
      }
      if (state._placementRestore.type === 'objectPreview') {
        const pr = state._placementRestore;
        this.objectType = pr.objectType;
        this.activeTool = 'door'; this.mode = 'door';
        document.getElementById('objectsToolBtn').classList.add('active');
        document.getElementById('selectToolBtn').classList.remove('active');
        document.getElementById('handToolBtn').classList.remove('active');
        document.getElementById('penToolBtn').classList.remove('active');
        document.getElementById('eraserBtn').classList.remove('active');
        this.mouseWorldRaw = pr.mouseWorldRaw ? { ...pr.mouseWorldRaw } : null;
        this.mouseWorld = pr.mouseWorld ? { ...pr.mouseWorld } : null;
        this._updateJoystickVisibility();
        this._clearCheckPulse(); this._clearArrowPulse();
        this._startArrowPulse(3000); this._startCheckPulse(3000);
        const label = pr.objectType === 'circle' ? 'pillar' : 'column';
        this.updateStatus(`Move ${label} with arrows or tap to reposition, then tap \u2713 to place.`);
        this.render(); this.updateMeasurements();
        return;
      }
    }
    // Standard restore
    this._resetDoorMobileState();
    this.currentWall = null; this.hasPlacedFirstPoint = false; this._wsSnapSticky = null; this._resetRectState(); this._resetFreehandState();
    this.selectedWalls = new Set(); this.selectedDoors = new Set(); this.selectedObjects = new Set();
    this._refreshLastPlacedEquipPos();
    this.render(); this.updateMeasurements(); this.updateStatus('Undo performed.');
    this._updateSelectionStatus();
  }

  redo() {
    // === WALL SEGMENT CHAIN REDO (re-add segment undone during chain drawing) ===
    if (this.redoStack.length > 0 && this.redoStack[this.redoStack.length - 1]._wsChainRestore) {
      const state = this.redoStack.pop();
      // Push current state to undo stack
      const undoEntry = {
        rooms: JSON.parse(JSON.stringify(this.rooms)),
        currentRoom: this.currentRoom ? JSON.parse(JSON.stringify(this.currentRoom)) : null,
        objects: JSON.parse(JSON.stringify(this.objects)),
        wallSegmentGroups: JSON.parse(JSON.stringify(this.wallSegmentGroups)),
        currentWallSegmentGroup: this.currentWallSegmentGroup ? JSON.parse(JSON.stringify(this.currentWallSegmentGroup)) : null,
        measurements: JSON.parse(JSON.stringify(this._measurements)),
        measureNextId: this._measureNextId
      };
      this.undoStack.push(undoEntry);
      // Restore from redo entry
      this.rooms = JSON.parse(JSON.stringify(state.rooms));
      this.objects = JSON.parse(JSON.stringify(state.objects || []));
      this.currentRoom = state.currentRoom ? JSON.parse(JSON.stringify(state.currentRoom)) : { walls: [], doors: [], cutouts: [], type: this.roomType, closed: false };
      this.wallSegmentGroups = state.wallSegmentGroups ? JSON.parse(JSON.stringify(state.wallSegmentGroups)) : [];
      this.currentWallSegmentGroup = state.currentWallSegmentGroup ? JSON.parse(JSON.stringify(state.currentWallSegmentGroup)) : null;
      if (state.measurements) { this._measurements = JSON.parse(JSON.stringify(state.measurements)); this._measureNextId = state.measureNextId || 1; }
      this._relinkDoorWallRefs();
      for (const r of this.rooms) { r._zoneCache = null; r._zoneLayouts = null; }
      // Re-establish drawing state from restored chain
      if (this.currentWallSegmentGroup && this.currentWallSegmentGroup.segments.length > 0) {
        const lastSeg = this.currentWallSegmentGroup.segments[this.currentWallSegmentGroup.segments.length - 1];
        this.currentWall = { start: { x: lastSeg.end.x, y: lastSeg.end.y }, end: { x: lastSeg.end.x, y: lastSeg.end.y } };
        this.hasPlacedFirstPoint = true;
        this.updateStatus('Segment restored. Continue drawing or Esc to finish.');
      } else {
        this.currentWall = null;
        this.hasPlacedFirstPoint = false;
      }
      this.render(); this.updateMeasurements();
      return;
    }

    // === PLACEMENT REDO (phase-level redo within placement workflow) ===
    if (this._placementRedoStack.length > 0) {
      const entry = this._placementRedoStack.pop();
      if (entry.type === 'doorPhase') {
        // Restore the door placement phase (uses live wall/room references — data model unchanged)
        this.doorType = entry.doorType;
        this.doorWidth = entry.doorWidth;
        this.objectType = entry.objectType;
        this.activeTool = 'door'; this.mode = 'door';
        document.getElementById('objectsToolBtn').classList.add('active');
        document.getElementById('selectToolBtn').classList.remove('active');
        document.getElementById('handToolBtn').classList.remove('active');
        document.getElementById('penToolBtn').classList.remove('active');
        document.getElementById('eraserBtn').classList.remove('active');
        this._doorMobile = {
          phase: entry.dm.phase, wall: entry.dm.wall, room: entry.dm.room,
          param: entry.dm.param, point: { ...entry.dm.point },
          endDir: entry.dm.endDir, endParam: entry.dm.endParam,
          swingOptions: entry.dm.swingOptions, swingIdx: entry.dm.swingIdx,
          swingOption: entry.dm.swingOption ? { ...entry.dm.swingOption } : null,
          travelDir: entry.dm.travelDir
        };
        this.mouseWorldRaw = entry.mouseWorldRaw ? { ...entry.mouseWorldRaw } : null;
        this.mouseWorld = entry.mouseWorld ? { ...entry.mouseWorld } : null;
        if (entry.doorPlacement) {
          this.doorPlacement = { ...entry.doorPlacement };
        } else {
          this.doorPlacement = null;
        }
        // Recompute swing options for Phase 3
        if (entry.dm.phase === 3) {
          this._doorMobile.swingOptions = this._getSwingOptions();
        }
        this._updateDoorMobileArrows();
        this._clearCheckPulse(); this._clearArrowPulse();
        this._updateJoystickVisibility();
        this._updateFloatingCancel();
        this._updateFloatingRepeat();
        if (entry.dm.phase === 1) {
          this._startArrowPulse(3000); this._startCheckPulse(3000);
          this.updateStatus('Slide door along wall, then tap \u2713.');
        } else if (entry.dm.phase === 3) {
          this._updateDoorSwingArrows();
          this._startArrowPulse(1500); this._startCheckPulse(3000);
          this.updateStatus('Tap a dot or use arrows, then \u2713.');
        }
        this.render();
        return;
      }
      if (entry.type === 'objectPreview') {
        // Restore the object positioning preview
        this.objectType = entry.objectType;
        this.activeTool = 'door'; this.mode = 'door';
        document.getElementById('objectsToolBtn').classList.add('active');
        document.getElementById('selectToolBtn').classList.remove('active');
        document.getElementById('handToolBtn').classList.remove('active');
        document.getElementById('penToolBtn').classList.remove('active');
        document.getElementById('eraserBtn').classList.remove('active');
        this.mouseWorldRaw = entry.mouseWorldRaw ? { ...entry.mouseWorldRaw } : null;
        this.mouseWorld = entry.mouseWorld ? { ...entry.mouseWorld } : null;
        this._updateJoystickVisibility();
        this._clearCheckPulse(); this._clearArrowPulse();
        this._startArrowPulse(3000); this._startCheckPulse(3000);
        const label = entry.objectType === 'circle' ? 'pillar' : 'column';
        this.updateStatus(`Move ${label} with arrows or tap to reposition, then tap \u2713 to place.`);
        this.render();
        return;
      }
    }

    // === NORMAL REDO ===
    if (this.redoStack.length === 0) return;
    // Push current state to undo (capture any active placement context for round-tripping)
    const undoEntry = { rooms: JSON.parse(JSON.stringify(this.rooms)), currentRoom: this.currentRoom ? JSON.parse(JSON.stringify(this.currentRoom)) : null, objects: JSON.parse(JSON.stringify(this.objects)), wallSegmentGroups: JSON.parse(JSON.stringify(this.wallSegmentGroups)), currentWallSegmentGroup: this.currentWallSegmentGroup ? JSON.parse(JSON.stringify(this.currentWallSegmentGroup)) : null, measurements: JSON.parse(JSON.stringify(this._measurements)), measureNextId: this._measureNextId };
    if (this._doorMobile) {
      undoEntry._placementRestore = this._captureDoorMobileSnapshot();
    } else if (this._isMobileToolbar && this.activeTool === 'door' && !this._doorMobile && this.mouseWorldRaw
        && (this.objectType === 'rectangle' || this.objectType === 'circle')) {
      undoEntry._placementRestore = { type: 'objectPreview', objectType: this.objectType,
        mouseWorldRaw: { ...this.mouseWorldRaw }, mouseWorld: { ...this.mouseWorld } };
    }
    this.undoStack.push(undoEntry);
    const state = this.redoStack.pop();
    this.rooms = JSON.parse(JSON.stringify(state.rooms));
    this.objects = JSON.parse(JSON.stringify(state.objects || []));
    this.currentRoom = state.currentRoom ? JSON.parse(JSON.stringify(state.currentRoom)) : { walls: [], doors: [], cutouts: [], type: this.roomType, closed: false };
    this.wallSegmentGroups = state.wallSegmentGroups ? JSON.parse(JSON.stringify(state.wallSegmentGroups)) : [];
    this.currentWallSegmentGroup = state.currentWallSegmentGroup ? JSON.parse(JSON.stringify(state.currentWallSegmentGroup)) : null;
    if (state.measurements) { this._measurements = JSON.parse(JSON.stringify(state.measurements)); this._measureNextId = state.measureNextId || 1; }
    this._selectedMeasurements.clear(); this._measurePhase = 0; this._measurePendingEnd = null; this._measureDragging = null;
    this._updateMeasureClearBtn();
    this._relinkDoorWallRefs();
    this._turfLogoFocusedIdx = null;
    this._turfLogoSelectedIdxs.clear();
    for (const r of this.rooms) { r._zoneCache = null; r._zoneLayouts = null; }
    if (window.roomDesignerOverlay) { window.roomDesignerOverlay._floorSelections = []; window.roomDesignerOverlay._floorSelectCorner1 = null; window.roomDesignerOverlay._floorSelectEnd = null; window.roomDesignerOverlay._floorInfoPinned = false; window.roomDesignerOverlay._hideFloorInfo(); }
    // Check for placement restore context on the redo entry
    if (state._placementRestore) {
      if (state._placementRestore.type === 'doorMobile') {
        this._resetDoorMobileState();
        this._restoreDoorMobileFromSnapshot(state._placementRestore);
        return;
      }
      if (state._placementRestore.type === 'objectPreview') {
        const pr = state._placementRestore;
        this.objectType = pr.objectType;
        this.activeTool = 'door'; this.mode = 'door';
        document.getElementById('objectsToolBtn').classList.add('active');
        document.getElementById('selectToolBtn').classList.remove('active');
        document.getElementById('handToolBtn').classList.remove('active');
        document.getElementById('penToolBtn').classList.remove('active');
        document.getElementById('eraserBtn').classList.remove('active');
        this.mouseWorldRaw = pr.mouseWorldRaw ? { ...pr.mouseWorldRaw } : null;
        this.mouseWorld = pr.mouseWorld ? { ...pr.mouseWorld } : null;
        this._updateJoystickVisibility();
        this.render(); this.updateMeasurements();
        return;
      }
    }
    // Standard restore — clean up any active placement
    this._resetDoorMobileState();
    this.mouseWorldRaw = null; this.mouseWorld = null;
    this.currentWall = null; this.hasPlacedFirstPoint = false; this._resetRectState(); this._resetFreehandState();
    this.selectedWalls = new Set(); this.selectedDoors = new Set(); this.selectedObjects = new Set();
    this._refreshLastPlacedEquipPos();
    this.render(); this.updateMeasurements(); this.updateStatus('Redo performed.');
    this._updateSelectionStatus();
  }

  // After undo/redo, re-derive _lastPlacedEquipPos from the last matching equipment in objects
  _refreshLastPlacedEquipPos() {
    if (!this._equipmentMode || !this._pendingEquipmentId) return;
    for (let i = this.objects.length - 1; i >= 0; i--) {
      const obj = this.objects[i];
      if (obj.type === 'equipment' && obj.equipmentId === this._pendingEquipmentId) {
        const tw = obj.tightWidth || obj.width, td = obj.tightDepth || obj.depth;
        this._lastPlacedEquipPos = { x: obj.x, y: obj.y, widthFt: tw, depthFt: td, rotation: obj.rotation || 0 };
        return;
      }
    }
    this._lastPlacedEquipPos = null;
  }

  clearAll() {
    const overlay = document.getElementById('rdConfirmOverlay');
    const okBtn = document.getElementById('rdConfirmOk');
    const cancelBtn = document.getElementById('rdConfirmCancel');
    overlay.classList.add('visible');
    const cleanup = () => { overlay.classList.remove('visible'); okBtn.removeEventListener('click', onOk); cancelBtn.removeEventListener('click', onCancel); };
    const onOk = () => {
      cleanup();
      this.rooms = []; this.objects = []; this.currentRoom = null;
      this.wallSegmentGroups = []; this.currentWallSegmentGroup = null;
      this.selectedWalls = new Set(); this.selectedDoors = new Set(); this.selectedObjects = new Set();
      this.selectionStart = null; this.selectionEnd = null;
      this.currentWall = null; this.hasPlacedFirstPoint = false;
      this._measurements = []; this._measureNextId = 1; this._selectedMeasurements.clear(); this._measureStart = null; this._measureEnd = null;
      this._measurePhase = 0; this._measurePendingEnd = null; this._measureDragging = null;
      this._updateMeasureClearBtn();
      this._resetRectState(); this._resetFreehandState();
      this._brandColors = {};
      // Reset tool tracking flags so popover children are deselected
      this._penToolClicked = false;
      this._mobileActionChosen = false;
      this._doorToolClicked = false;
      this._mobileObjectChosen = false;
      // Deselect all child items inside popovers
      document.querySelectorAll('.pen-draw-mode-btn').forEach(b => b.classList.remove('active'));
      document.querySelectorAll('.pen-mode-btn').forEach(b => b.classList.remove('active'));
      document.querySelectorAll('.objects-type-btn').forEach(b => b.classList.remove('active'));
      document.querySelectorAll('.door-type-btn').forEach(b => b.classList.remove('active'));
      document.getElementById('penActionOptions').style.display = 'none';
      document.getElementById('objectsDoorOptions').style.display = 'none';
      document.getElementById('objectsRectOptions').style.display = 'none';
      document.getElementById('objectsCircleOptions').style.display = 'none';
      // Reset pen badge
      const badge = document.getElementById('penBadge');
      if (badge) { badge.textContent = ''; badge.className = 'pen-badge'; }
      this.startNewRoom();
      // Apply hand tool functionality but don't highlight any button
      this.activeTool = 'hand';
      this.mode = 'hand';
      this.canvas.style.cursor = 'grab';
      document.getElementById('selectToolBtn').classList.remove('active');
      document.getElementById('handToolBtn').classList.remove('active');
      document.getElementById('penToolBtn').classList.remove('active');
      document.getElementById('objectsToolBtn').classList.remove('active');
      document.getElementById('eraserBtn').classList.remove('active');
      document.getElementById('penPopover').classList.remove('visible');
      document.getElementById('objectsPopover').classList.remove('visible');
      document.getElementById('zoomPopover').classList.remove('visible');
      document.getElementById('zoomToggleBtn').classList.remove('active');
      // Close settings drawer
      const sd = document.getElementById('settingsDrawer');
      const st = document.getElementById('settingsToggle');
      if (sd) sd.classList.remove('open');
      if (st) st.classList.remove('open');
      // Close designs drawer
      const dd = document.getElementById('designsDrawer');
      const dt = document.getElementById('designsToggle');
      if (dd) dd.classList.remove('open');
      if (dt) dt.classList.remove('open');
      // Clear session cache
      localStorage.removeItem('rd_autosave');
      localStorage.removeItem('rd_autosave_time');
      this._updateDesignNameUI('');
      this._pinnedPopover = null;
      if (this._syncDrawerPins) this._syncDrawerPins();
      this._updateSelectionStatus();
      if (this._isMobileToolbar) this._updateMobilePopoverState();
      this.updateStatus('Canvas cleared.');
      this.render();
    };
    const onCancel = () => { cleanup(); };
    okBtn.addEventListener('click', onOk);
    cancelBtn.addEventListener('click', onCancel);
  }
  _setOverlayPointerEvents(enabled) {
    const val = enabled ? '' : 'none';
    const panel = document.querySelector('.settings-panel');
    if (panel) panel.style.pointerEvents = val;
    const toolControls = document.querySelector('.tool-controls');
    if (toolControls) toolControls.style.pointerEvents = val;
    const stepBar = document.querySelector('.rd-step-bar');
    if (stepBar) stepBar.style.pointerEvents = val;
  }
  updateStatus(message, opts) {
    const bar = document.getElementById('statusBar');
    let textEl = bar.querySelector('.status-bar-text');
    if (!textEl) {
      textEl = document.createElement('span');
      textEl.className = 'status-bar-text';
      bar.textContent = '';
      bar.appendChild(textEl);
    }
    // Errors-only mode: only show the bar for error/urgent messages, auto-hide after timeout
    const isError = opts && (opts.error || opts.urgent);
    if (!isError) {
      // Hint message — silently store text but don't show the bar
      // Don't overwrite text while an error/urgent message is still visible
      if (!this._statusErrorUntil || Date.now() >= this._statusErrorUntil) {
        textEl.textContent = message;
      }
      return;
    }
    const prev = textEl.textContent;
    textEl.textContent = message;
    bar.classList.add('visible');
    this._statusErrorUntil = Date.now() + 3000;
    clearTimeout(this._statusHideTimer);
    this._statusHideTimer = setTimeout(() => { bar.classList.remove('visible'); this._statusErrorUntil = 0; }, 3000);
    if (message && message !== prev) {
      const cls = opts.urgent ? 'flash-urgent' : 'flash';
      bar.classList.remove('flash', 'flash-urgent');
      void bar.offsetWidth;
      bar.classList.add(cls);
    }
    this._updateStatusScroll(bar, textEl);
  }
  _updateStatusScroll(bar, textEl) {
    if (!this._isMobile) { bar.classList.remove('scrolling'); return; }
    requestAnimationFrame(() => {
      bar.classList.remove('scrolling');
      void bar.offsetWidth;
      const cs = getComputedStyle(bar);
      const available = bar.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
      const overflow = textEl.offsetWidth - available;
      if (overflow > 4) {
        const dur = Math.max(6, overflow / 20 + 4);
        bar.style.setProperty('--scroll-distance', `-${overflow + 8}px`);
        bar.style.setProperty('--scroll-duration', `${dur}s`);
        bar.classList.add('scrolling');
      }
    });
  }

  /* ── RENDERING ── */

  _themeColors() {
    if (this.darkMode) {
      return {
        wallColor: '#c0c8d8',
        gridMajor: '#283656',
        gridMinor: '#1e2e48',
        objectStroke: '#c0c8d8',
        objectFill: 'rgba(18, 26, 46, 0.55)',
        labelBg: 'rgba(18, 26, 46, 1)',
        labelBorder: 'rgba(200, 210, 230, 0.15)',
        labelText: '#c0c8d8',
        roomLabelText: '#c0c8d8',
        previewFill: 'rgba(18, 26, 46, 0.8)',
        roomFill: 'rgba(0, 107, 204, 0.22)',
        roomFillAlpha: 0.22,
        roomFlooredFill: 'rgba(10, 16, 32, 0.32)',
        subtractFill: 'rgba(204, 0, 0, 0.18)',
        roomGridDim: 'rgba(20, 28, 46, 0.55)',
      };
    }
    return {
      wallColor: '#000F1C',
      gridMajor: '#c8c8c8',
      gridMinor: '#e8e8e8',
      objectStroke: '#000F1C',
      objectFill: 'rgba(255, 255, 255, 0.55)',
      labelBg: 'rgba(255, 255, 255, 1)',
      labelBorder: 'rgba(0, 15, 28, 0.08)',
      labelText: '#666',
      roomLabelText: '#000F1C',
      previewFill: 'rgba(255, 255, 255, 0.8)',
      roomFill: 'rgba(0, 107, 204, 0.18)',
      roomFillAlpha: 0.18,
      roomFlooredFill: 'rgba(0, 107, 204, 0.28)',
      subtractFill: 'rgba(204, 0, 0, 0.08)',
      roomGridDim: 'rgba(255, 255, 255, 0.75)',
    };
  }

  render() {
    if (this._popoverActive) return;
    this._syncWallSegmentBtn();
    if (this._renderFrame) return;
    this._renderFrame = requestAnimationFrame(() => { this._renderFrame = null; this._doRender(); });
  }

  _drawZoneLabels(scale) {
    if (!window.RoomDesignerMath) return;
    // View-only zone label rules:
    // - Multi-view Floor Plan tab: show with zoom fade
    // - Multi-view other tabs (Equipment, Flooring, Full Layout): hide completely
    // - Current-view share with space labels on: show with zoom fade
    const isViewOnlyFloorPlan = this._viewOnly && !this._viewOnlyShowFloor && !this._equipmentMode;
    const isMultiView = this._viewOnly && !!this._viewOnlyViews;
    if (this._viewOnly) {
      if (isMultiView) { if (!isViewOnlyFloorPlan) return; }
      else { if (!this.showRoomLabels) return; }
    }
    // Fade out zone labels between 40% and 25% zoom (skip fade in print mode only)
    const skipFade = this._printMode;
    const zoomPct = (this.zoomLevel || 1) * 100;
    if (!skipFade && zoomPct <= 25) return;
    const zoneFade = skipFade ? 1 : (zoomPct >= 40 ? 1 : (zoomPct - 25) / 15);
    const overlay = window.roomDesignerOverlay;
    const allRooms = this.rooms.concat(this.currentRoom && this.currentRoom.closed ? [this.currentRoom] : []);
    const dpr = this._dpr || 1;
    const isDark = this.darkMode;

    for (const room of allRooms) {
      if (!room || !room.closed || room.walls.length < 3 || room.type !== 'add') continue;

      // Compute zones
      let zones;
      if (overlay && typeof overlay._getZonesForRoom === 'function') {
        zones = overlay._getZonesForRoom(room);
      } else {
        const geo = this._getRoomGeoCache(room);
        if (!geo || !geo.roomPolygon || geo.roomPolygon.length < 3) continue;
        zones = RoomDesignerMath.computeRoomZones(geo.roomPolygon, [], []);
      }
      if (!zones || zones.length < 2) continue;

      this.ctx.save();
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const fontSize = Math.round((this.largeUI ? 13 : 11) / scale * scale);
      if (fontSize < 4) { this.ctx.restore(); continue; }
      const lineH = fontSize * 1.2;

      for (let i = 0; i < zones.length; i++) {
        const z = zones[i];
        const poly = z.polygon;
        if (!poly || poly.length < 3) continue;

        // Position label at the zone's interior anchor, then nudge to avoid walls/labels.
        // labelPoint is annulus-aware (never lands inside an island hole); zones
        // without holes anchor on the centroid exactly as before.
        const anchor = z.labelPoint || z.centroid;
        let centerX = anchor.x;
        const wallHalf = 0.25; // half wall thickness (generous)
        const textH = (lineH * 2) / scale; // label height in world ft
        const textHalfW = (fontSize * 5) / scale; // rough label half-width in world ft
        let labelY = anchor.y - textH / 2; // center vertically on the anchor

        // Safety: if the anchor is outside zone polygon (concave shapes),
        // scan horizontally at anchor.y for the longest interior segment
        if (!RoomDesignerMath.pointInPolygon(centerX, anchor.y, poly)) {
          const scanY = anchor.y;
          const xs = [];
          for (let pi = 0, pj = poly.length - 1; pi < poly.length; pj = pi++) {
            const yi = poly[pi].y, yj = poly[pj].y;
            if ((yi <= scanY && yj > scanY) || (yj <= scanY && yi > scanY)) {
              const t = (scanY - yi) / (yj - yi);
              xs.push(poly[pi].x + t * (poly[pj].x - poly[pi].x));
            }
          }
          xs.sort((a, b) => a - b);
          let bestMid = centerX, bestLen = 0;
          for (let si = 0; si + 1 < xs.length; si += 2) {
            const len = xs[si + 1] - xs[si];
            if (len > bestLen) { bestLen = len; bestMid = (xs[si] + xs[si + 1]) / 2; }
          }
          if (bestLen > 0) { centerX = bestMid; labelY = scanY - textH / 2; }
        }

        // Collect wall segments for overlap checking
        const allWalls = [];
        for (const w of room.walls) allWalls.push(w);
        for (const c of (room.cutouts || [])) { for (const w of c.walls) allWalls.push(w); }
        for (const g of this.wallSegmentGroups) { for (const s of g.segments) allWalls.push(s); }

        // Check perimeter walls + divider segments for overlap; nudge down if needed
        const sampleXs = [centerX - textHalfW * 0.8, centerX - textHalfW * 0.4, centerX, centerX + textHalfW * 0.4, centerX + textHalfW * 0.8];
        for (const w of allWalls) {
          const wMinX = Math.min(w.start.x, w.end.x), wMaxX = Math.max(w.start.x, w.end.x);
          const wMinY = Math.min(w.start.y, w.end.y), wMaxY = Math.max(w.start.y, w.end.y);
          if (wMaxX < centerX - textHalfW || wMinX > centerX + textHalfW) continue;
          if (wMaxY + wallHalf < labelY - 0.5 || wMinY - wallHalf > labelY + textH + 0.5) continue;
          const wdx = w.end.x - w.start.x;
          const wdy = w.end.y - w.start.y;
          if (Math.abs(wdx) < 0.001) {
            if (Math.abs(w.start.x - centerX) < wallHalf + textHalfW * 0.3) {
              const wTop = Math.min(w.start.y, w.end.y);
              const wBot = Math.max(w.start.y, w.end.y);
              if (wBot > labelY && wTop < labelY + textH) labelY = wBot + wallHalf + 0.1;
            }
          } else {
            for (const sampX of sampleXs) {
              const t = (sampX - w.start.x) / wdx;
              if (t >= -0.05 && t <= 1.05) {
                const wallY = w.start.y + t * wdy;
                if (wallY >= labelY - wallHalf && wallY <= labelY + textH + wallHalf) {
                  labelY = Math.max(labelY, wallY + wallHalf + 0.15);
                }
              }
            }
          }
        }
        // Check against placed wall dimension labels; nudge if overlapping
        const wallLabels = this._placedWallLabels || [];
        for (const wl of wallLabels) {
          if (Math.abs(centerX - wl.x) < textHalfW + wl.halfW) {
            const wlBot = wl.y + wl.halfH;
            if (labelY + textH > wl.y - wl.halfH - 0.15 && labelY < wlBot + 0.15) {
              labelY = Math.max(labelY, wlBot + 0.2);
            }
          }
        }
        // Find zone bottom edge to cap — don't push label outside the zone
        let maxY = -Infinity;
        for (const p of poly) { if (p.y > maxY) maxY = p.y; }
        if (labelY + textH > maxY - wallHalf) labelY = z.centroid.y - textH / 2;

        const _zls = this.worldToScreen(centerX, labelY);
        const screenX = _zls.x;
        const screenY = _zls.y;
        const labelText = (room.zoneNames && room.zoneNames[i]) || ('Zone ' + (i + 1));
        const areaNum = Math.round(Math.abs(z.area));
        const areaText = areaNum.toLocaleString() + ' sq ft';
        const areaFontSize = Math.max(fontSize - 1, 4);

        // Measure widths for hit-box
        this.ctx.font = '600 ' + fontSize + 'px Arial';
        const labelW = this.ctx.measureText(labelText).width;
        this.ctx.font = areaFontSize + 'px Arial';
        const areaW = this.ctx.measureText(areaText).width;
        const maxW = Math.max(labelW, areaW);

        // Two-line label: name (bold) on top, area below
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'top';
        const baseAlpha = isDark ? 0.85 : 0.75;
        const alpha = (baseAlpha * zoneFade).toFixed(2);
        this.ctx.fillStyle = isDark ? 'rgba(160,185,230,' + alpha + ')' : 'rgba(50,50,70,' + alpha + ')';
        this.ctx.font = '600 ' + fontSize + 'px Arial';
        this.ctx.fillText(labelText, screenX, screenY);
        this.ctx.font = areaFontSize + 'px Arial';
        this.ctx.fillText(areaText, screenX, screenY + lineH);

        // Persist hit-box for click-to-edit (covers both lines)
        const boxH = lineH * 2;
        if (this._clickableLabels) {
          this._clickableLabels.push({
            type: 'zone', room, zoneIndex: i,
            sx: screenX, sy: screenY + boxH / 2, hw: maxW / 2 + 4, hh: boxH / 2 + 4
          });
        }
      }

      this.ctx.restore();
    }
  }

  _doRender() {
    if (this._selectOpen) return;
    if (!this._printMode) this._tc = this._themeColors();
    const dpr = this._dpr || 1;
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    // Opaque background fill so door-gap snapshot compositing works correctly
    // (without this, transparent areas let wall lines bleed through)
    this.ctx.fillStyle = this.darkMode ? '#141c2e' : '#ffffff';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.ctx.save();
    this.ctx.translate(this.panX, this.panY);
    if (this._canvasRotationRad) this.ctx.rotate(this._canvasRotationRad);
    const scale = this.basePixelsPerFoot * this.zoomLevel;
    this.ctx.scale(scale, scale);
    if (this.showGrid) this.drawGrid();
    if (this._drawBackgroundHook) this._drawBackgroundHook();
    if (!this._printMode && !this._viewOnly) this._drawLightSource();
    // Mask grid lines behind joystick so only walls/rooms show through its transparency
    if (!this._printMode && this._joystickEl && this._joystickEl.classList.contains('visible') && this.showGrid) {
      this.ctx.save();
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0); // reset to screen coords (DPR-aware)
      const jw = 132, jh = 132, jRight = 6, jBottom = 72;
      const cx = this._cssWidth - jRight - jw / 2;
      const cy = this._cssHeight - jBottom - jh / 2;
      this.ctx.beginPath();
      this.ctx.arc(cx, cy, jw / 2 + 2, 0, Math.PI * 2);
      this.ctx.fillStyle = this.darkMode ? '#141c2e' : '#ffffff';
      this.ctx.fill();
      this.ctx.restore();
    }
    this._pendingWallLabels = [];
    this._pendingRoomLabels = [];
    this._clickableLabels = [];
    // Compute exterior zones (cached) — must run before drawRoom so labels can use area
    const _extResult = this._getExteriorZones();
    this._exteriorZoneAreaByRoom = _extResult.areaByRoom;
    if (this._beforeRoomsHook) this._beforeRoomsHook();
    // Room drop shadows — draw combined silhouette shadow
    this.ctx.save();
    let shadowRooms = this.rooms.filter(r => r.closed && r.walls.length >= 3);
    if (this._printFocusRoom) shadowRooms = shadowRooms.filter(r => r === this._printFocusRoom);
    if (this.currentRoom && this.currentRoom.closed && this.currentRoom.walls.length >= 3) shadowRooms.push(this.currentRoom);
    const _traceOverlay = window.roomDesignerOverlay;
    const _tracing = this._bgTraceActive && (!_traceOverlay || _traceOverlay.currentStep === 'draw');
    if (shadowRooms.length > 0 && !_tracing) {
      const bgFill = this.darkMode ? '#141c2e' : '#ffffff';
      const _roomShadScale = this._printMode ? (this.zoomLevel / 1.0) : 1;
      this.ctx.shadowColor = this.darkMode ? 'rgba(0,0,0,0.77)' : 'rgba(0,0,0,0.51)';
      this.ctx.shadowBlur = 40 * _roomShadScale;
      this.ctx.shadowOffsetX = -3 * _roomShadScale;
      this.ctx.shadowOffsetY = 5 * _roomShadScale;
      this.ctx.fillStyle = bgFill;
      // Combine all room polygons into one path so shadows only show on outer edges
      // (prevents adjacent rooms from covering each other's shadows)
      this.ctx.beginPath();
      for (const room of shadowRooms) {
        const geo = this._getRoomGeoCache(room);
        const poly = geo.roomPolygon;
        if (!poly || poly.length < 3) continue;
        this.ctx.moveTo(poly[0].x, poly[0].y);
        for (let i = 1; i < poly.length; i++) this.ctx.lineTo(poly[i].x, poly[i].y);
        this.ctx.closePath();
      }
      this.ctx.fill();
    }
    this.ctx.restore();
    const _roomsToDraw = this._printFocusRoom ? this.rooms.filter(r => r === this._printFocusRoom) : this.rooms;
    _roomsToDraw.forEach(room => this.drawRoom(room, this._tc.wallColor));
    if (this.currentRoom && this.currentRoom.walls.length > 0) this.drawRoom(this.currentRoom, this.roomType === 'subtract' ? '#CC4429' : '#006BCC');
    if (this._afterRoomsHook) this._afterRoomsHook();
    // Fill exterior zones (after room fills, before wall segment lines) — skip in per-space print
    if (!this._printFocusRoom) this._drawExteriorZoneFills(_extResult.zones);
    this._drawWallSegmentGroups();
    if (this._afterWallSegmentsHook) this._afterWallSegmentsHook();
    this._pendingTurfLabels = [];
    this._drawObjects(scale);
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — turf UNDERLAPS the walls.
    //
    // Objects draw after walls, so turf painted over the wall's inner edge. With
    // SOLID walls that is worse than an edge: the band is a thick stroke centred on
    // the centreline offset by HALF a thickness, so half of it lies inside the room
    // and turf covered it — the wall read thinner wherever turf met it. With hollow
    // or trace walls the band sits fully outside, so only the thin inner line was
    // covered, which on a 24-facet curve is 24 mitred joints and looks ragged.
    //
    // Redrawing the band over the top fixes both, and it re-runs drawRoom's OWN wall
    // code rather than mimicking it — the band is built per connected wall component
    // with a miter-joined offset and a per-component outward sign, so a second
    // implementation would eventually diverge and show as a mismatched edge exactly
    // where turf meets the wall.
    //
    // 2026-09-24 (Ryan): ALWAYS, not only when turf exists. Equipment, pillar and column drop
    // shadows are painted after the walls, so on a plan without turf they fell ONTO the wall band
    // and the dividing walls; turf plans never showed it because this repaint covered them. The
    // repaint is what makes a shadow stop short at the wall face, in every mode including print.
    {
      _roomsToDraw.forEach(room => this.drawRoom(room, this._tc.wallColor, true));
      if (this.currentRoom && this.currentRoom.walls.length > 0) {
        this.drawRoom(this.currentRoom, this.roomType === 'subtract' ? '#CC4429' : '#006BCC', true);
      }
      this._drawWallSegmentGroups(true);
    }
    if (this._afterObjectsHook) this._afterObjectsHook();
    // Draw deferred turf labels on top of turf overage overlay
    if (this._drawTurfLabels) this._drawTurfLabels();
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — area labels last of all, and deliberately
    // ABOVE the print-mode split so the PDF gets them from this one call rather
    // than a second path that could drift out of step with the on-screen one.
    if (this._drawAreaLabels) this._drawAreaLabels(scale);
    // Print mode: skip all interactive UI (ghosts, paste preview, selection handles, snap cursor)
    if (this._printMode) {
      if (this.showRoomLabels) this._drawRoomLabels(scale);
      // CUSTOM_PROJECTASSEMBLY_BY_CLAUDE — print-mode wall dim labels in seam-plan style
      // (outside perimeter, white pill, bold black text, ~28-40px). Used by legacy save
      // pipeline AND Path B variant gen whenever showMeasurements is true.
      if (this.showMeasurements) {
        this._drawPrintWallLabels(scale);
      }
      // END CUSTOM_PROJECTASSEMBLY_BY_CLAUDE
      // Draw hand-placed measurement lines (M tool) in print mode
      if (this._measurements && this._measurements.length > 0) {
        this._drawMeasurementLines(scale);
      }
      this.ctx.restore();
      return;
    }
    // Ghost preview for pending equipment placement
    if (this._equipmentMode && this._pendingEquipmentId) {
      const ghostInfo = this._equipmentImages[this._pendingEquipmentId];
      let ghostPos = this._pendingEquipmentPos || this.mouseWorld;
      // Shift-hold cardinal axis constraint with edge-gap snapping (whole-foot gaps)
      let _cardinalSnap = null;
      if (!this._pendingEquipmentPos && this.shiftHeld && this._lastPlacedEquipPos && this.mouseWorldRaw && ghostInfo) {
        const gWft = (ghostInfo.tightWidthIn || ghostInfo.widthIn) / 12;
        const gDft = (ghostInfo.tightDepthIn || ghostInfo.depthIn) / 12;
        const gRot = this._pendingEquipmentRotation || 0;
        _cardinalSnap = this._computeCardinalEquipSnap(this.mouseWorldRaw.x, this.mouseWorldRaw.y, gWft, gDft, gRot);
        if (_cardinalSnap) ghostPos = { x: _cardinalSnap.x, y: _cardinalSnap.y };
      }
      if (ghostPos && ghostInfo && ghostInfo.loaded && ghostInfo.normalCanvas) {
        const widthFt = ghostInfo.widthIn / 12;
        const depthFt = ghostInfo.depthIn / 12;
        const rot = this._pendingEquipmentRotation || 0;
        // Wall collision check for ghost tint — skip during rotation phase (auto-slide on place)
        if (this._pendingEquipmentPos) {
          this._equipmentGhostBlocked = false;
        } else {
          const _tightW = (ghostInfo.tightWidthIn || ghostInfo.widthIn) / 12;
          const _tightD = (ghostInfo.tightDepthIn || ghostInfo.depthIn) / 12;
          const _ghostKey = ghostPos.x + ',' + ghostPos.y + ',' + rot;
          if (_ghostKey !== this._lastGhostCollisionKey) {
            this._lastGhostCollisionKey = _ghostKey;
            this._equipmentGhostBlocked = this._equipmentOverlapsWall(ghostPos.x, ghostPos.y, _tightW, _tightD, rot, ghostInfo);
          }
        }
        this.ctx.save();
        this.ctx.translate(ghostPos.x, ghostPos.y);
        if (rot) this.ctx.rotate(rot);
        {
          // Ghost silhouette fill — higher opacity while following mouse, lower after first click
          const ghostAlpha = this._pendingEquipmentPos ? 0.65 : 0.3;
          const sil = this._ensureEquipmentCanvas(ghostInfo, 'sil');
          if (sil) {
            // Determine ghost fill color — blocked = red, else powder coat or default
            let ghostFill;
            if (this._equipmentGhostBlocked) {
              ghostFill = this.darkMode ? '#ff4444' : '#cc0000';
            } else {
              const brandConf = this._brandColors && this._brandColors[ghostInfo.vendor];
              const tkoDefGhost = ghostInfo.vendor === 'TKO' ? '#ffffff' : null;
              const spiritDefGhost = ghostInfo.vendor === 'Spirit Fitness' ? '#333333' : null; // Spirit = black only
              const pcColor = (brandConf && brandConf.powderCoat) || tkoDefGhost || spiritDefGhost || (this.darkMode ? '#B1B3B3' : '#004B87');
              ghostFill = pcColor;
            }
            if (!this._ghostRecolorCanvas) this._ghostRecolorCanvas = document.createElement('canvas');
            const gc = this._ghostRecolorCanvas;
            if (gc.width !== sil.width || gc.height !== sil.height) {
              gc.width = sil.width; gc.height = sil.height;
            }
            const gctx = gc.getContext('2d');
            gctx.clearRect(0, 0, gc.width, gc.height);
            gctx.drawImage(sil, 0, 0);
            gctx.globalCompositeOperation = 'source-in';
            gctx.fillStyle = ghostFill;
            gctx.fillRect(0, 0, gc.width, gc.height);
            // Per-panel glossy sheen on ghost preview (if not blocked and not matte)
            if (!this._equipmentGhostBlocked) {
              const bc = this._brandColors && this._brandColors[ghostInfo.vendor];
              const glossy = bc ? !(bc.pcMatte) : true;
              if (glossy) {
                const gloss = this._buildGlossCanvas(ghostInfo);
                if (gloss) {
                  gctx.globalCompositeOperation = 'source-atop';
                  gctx.drawImage(gloss, 0, 0);
                }
              }
              // Grain texture for matte/textured coats
              if (bc && bc.pcMatte) {
                const grain = this._buildGrainCanvas(ghostInfo);
                if (grain) {
                  gctx.globalCompositeOperation = 'source-atop';
                  gctx.drawImage(grain, 0, 0);
                }
              }
            }
            gctx.globalCompositeOperation = 'source-over';
            // Upholstery overlay on ghost (if not blocked)
            if (!this._equipmentGhostBlocked) {
              const brandConf2 = this._brandColors && this._brandColors[ghostInfo.vendor];
              const uphColor = (brandConf2 && brandConf2.upholstery) || (ghostInfo.vendor === 'Spirit Fitness' ? '#141414' : null) || '#2D2926';
              if (ghostInfo.hasUpholstery && ghostInfo.upholsteryCanvas) {
                // Camo pattern: tile swatch image instead of solid color
                const patKey = brandConf2 && brandConf2.uphPattern;
                const patEntry = patKey && this._patternImages && this._patternImages[patKey];
                const isGhostCamo = patEntry && patEntry.loaded;
                if (isGhostCamo) {
                  const patUph = this._getPatternedCanvas(ghostInfo.upholsteryCanvas, patEntry.img, patKey, ghostInfo);
                  gctx.drawImage(patUph, 0, 0);
                } else {
                  const coloredUph = this._getColoredCanvas(ghostInfo.upholsteryCanvas, uphColor, ghostInfo, '_coloredUphCanvas', '_upholsteryCachedColor');
                  gctx.drawImage(coloredUph, 0, 0);
                }
                // Crinkle leather texture on ghost (skip for camo)
                if (!isGhostCamo) {
                  const crinkle = this._buildCrinkleUphCanvas(ghostInfo);
                  if (crinkle) gctx.drawImage(crinkle, 0, 0);
                }
                // Edge highlight for cushioned look
                const edgeHLg = this._buildUphEdgeHighlight(ghostInfo);
                if (edgeHLg) gctx.drawImage(edgeHLg, 0, 0);
              }
            }
            // Rubber overlay on ghost (if not blocked): always charcoal
            if (!this._equipmentGhostBlocked && ghostInfo.hasRubber && ghostInfo.rubberCanvas) {
              const coloredRubber = this._getColoredCanvas(ghostInfo.rubberCanvas, '#1A1A1A', ghostInfo, '_coloredRubberCanvas', '_rubberCachedColor');
              gctx.drawImage(coloredRubber, 0, 0);
            }
            // Wood overlay on ghost (if not blocked): always oak
            if (!this._equipmentGhostBlocked && ghostInfo.hasWood && ghostInfo.woodCanvas) {
              const coloredWood = this._getColoredCanvas(ghostInfo.woodCanvas, '#C4A46B', ghostInfo, '_coloredWoodCanvas', '_woodCachedColor');
              gctx.drawImage(coloredWood, 0, 0);
            }
            // Chrome overlay on ghost (if not blocked): always silver + checker plate
            if (!this._equipmentGhostBlocked && ghostInfo.hasChrome && ghostInfo.chromeCanvas) {
              const coloredChrome = this._getColoredCanvas(ghostInfo.chromeCanvas, '#C0C0C0', ghostInfo, '_coloredChromeCanvas', '_chromeCachedColor', true);
              gctx.drawImage(coloredChrome, 0, 0);
              const checkerG = this._buildCheckerChromeCanvas(ghostInfo);
              if (checkerG) gctx.drawImage(checkerG, 0, 0);
            }
            this.ctx.globalAlpha = ghostAlpha;
            this.ctx.drawImage(gc, -widthFt / 2, -depthFt / 2, widthFt, depthFt);
            // Line art outlines on ghost at same alpha as fill
            const ghostBitmap = this._ensureEquipmentCanvas(ghostInfo, 'normal');
            if (ghostBitmap) {
              this.ctx.drawImage(ghostBitmap, -widthFt / 2, -depthFt / 2, widthFt, depthFt);
            }
          }
          this.ctx.globalAlpha = 1.0;
        }
        this.ctx.restore();
        // Proximity dimension lines (gray dashed) — 2 closest walls/equipment
        if (!this._pendingEquipmentPos) {
          const tightWFt = (ghostInfo.tightWidthIn || ghostInfo.widthIn) / 12;
          const tightDFt = (ghostInfo.tightDepthIn || ghostInfo.depthIn) / 12;
          const proxExclude = _cardinalSnap ? { axis: _cardinalSnap.axis, sign: _cardinalSnap.sign } : null;
          const proxSource = _cardinalSnap && this._lastPlacedEquipPos ? this._lastPlacedEquipPos : null;
          this._drawEquipProximityDims(ghostPos, tightWFt / 2, tightDFt / 2, rot, scale, proxExclude, proxSource);
        }
        // Edge-gap measurement line (dotted, with nubs + label) for cardinal chaining
        if (_cardinalSnap && _cardinalSnap.gap > 0) {
          const cs = _cardinalSnap;
          const e1 = cs.edge1, e2 = cs.edge2;
          const nubLen = Math.max(this.wallThickness * 0.4, 4 / scale);
          // Nub perpendicular direction (perpendicular to the constrained axis)
          const nubX = cs.axis === 'x' ? 0 : 1;
          const nubY = cs.axis === 'x' ? 1 : 0;
          const _csLineColor = this.darkMode ? 'rgba(80, 170, 255, 0.75)' : 'rgba(0, 90, 180, 0.65)';
          const _csLabelColor = this.darkMode ? '#50aaff' : '#005ab4';
          this.ctx.save();
          this.ctx.strokeStyle = _csLineColor;
          this.ctx.lineWidth = 1.5 / scale;
          this.ctx.setLineDash([2 / scale, 2 / scale]);
          this.ctx.beginPath();
          this.ctx.moveTo(e1.x, e1.y);
          this.ctx.lineTo(e2.x, e2.y);
          this.ctx.stroke();
          this.ctx.setLineDash([]);
          // Nub marks at each edge
          this.ctx.beginPath();
          this.ctx.moveTo(e1.x - nubX * nubLen, e1.y - nubY * nubLen);
          this.ctx.lineTo(e1.x + nubX * nubLen, e1.y + nubY * nubLen);
          this.ctx.moveTo(e2.x - nubX * nubLen, e2.y - nubY * nubLen);
          this.ctx.lineTo(e2.x + nubX * nubLen, e2.y + nubY * nubLen);
          this.ctx.stroke();
          // Gap label at midpoint
          const fontSize = (11 + Math.min(7, Math.max(0, (scale - 20) / 12))) / scale;
          this.ctx.font = `${fontSize}px Arial`;
          this.ctx.fillStyle = _csLabelColor;
          this.ctx.textAlign = 'center';
          this.ctx.textBaseline = 'middle';
          const lblX = (e1.x + e2.x) / 2;
          const lblY = (e1.y + e2.y) / 2;
          // Offset label perpendicular to the line so it doesn't overlap the line
          const lblOff = Math.max(this.wallThickness * 2, 16 / scale);
          // Label above for horizontal chains, right for vertical — consistent with equidistant labels
          this.ctx.textAlign = nubX > 0 ? 'left' : 'center';
          this.ctx.textBaseline = nubY > 0 ? 'bottom' : 'middle';
          this.ctx.fillText(this.formatMeasurement(cs.gap), lblX + nubX * lblOff, lblY - nubY * lblOff);
          this.ctx.textAlign = 'start';
          this.ctx.textBaseline = 'alphabetic';
          this.ctx.restore();
        }
      }
    }
    // Ghost preview for pending ladder/dot drill placement
    if (this._pendingLadder) {
      const pl = this._pendingLadder;
      const tObj = pl.turfObj;
      const ghostLocal = this._pendingLadderPos || this._pendingLadderGhostPos;
      if (ghostLocal) {
        const ghostAlpha = this._pendingLadderPos ? 0.65 : 0.35;
        const isBlocked = !this._pendingLadderPos && this._pendingLadderBlocked;
        const _lColor = isBlocked ? '#cc0000' : (tObj.turfLineColor || '#FFFFFF');
        const _lLen = pl.lengthFt, _lWid = pl.heightFt;
        const _hLen = _lLen / 2, _hWid = _lWid / 2;
        const turfStrokeW = 2 / (this.basePixelsPerFoot * this.zoomLevel);
        this.ctx.save();
        this.ctx.globalAlpha = ghostAlpha;
        this.ctx.translate(tObj.x, tObj.y);
        if (tObj.rotation) this.ctx.rotate(tObj.rotation);
        this.ctx.beginPath();
        this.ctx.rect(-tObj.width / 2 + turfStrokeW, -tObj.depth / 2 + turfStrokeW,
          tObj.width - turfStrokeW * 2, tObj.depth - turfStrokeW * 2);
        this.ctx.clip();
        this.ctx.translate(ghostLocal.x, ghostLocal.y);
        if (this._pendingLadderRotation) this.ctx.rotate(this._pendingLadderRotation);
        // Background tint (red when blocked) — only for ladders, not dot drills
        if (pl.itemType !== 'dotdrill') {
          this.ctx.fillStyle = isBlocked ? 'rgba(204,0,0,0.15)' : 'rgba(0,0,0,0.08)';
          this.ctx.fillRect(-_hLen, -_hWid, _lLen, _lWid);
        }
        this.ctx.fillStyle = _lColor;
        if (pl.itemType === 'dotdrill') {
          // 5 dots: center + 4 corners (edges tangent to rectangle boundary)
          const _dotR = 2.5 / 12;
          const _ddPts = [[0, 0], [-_hLen + _dotR, -_hWid + _dotR], [_hLen - _dotR, -_hWid + _dotR],
            [-_hLen + _dotR, _hWid - _dotR], [_hLen - _dotR, _hWid - _dotR]];
          for (let _di = 0; _di < 5; _di++) {
            this.ctx.beginPath();
            this.ctx.arc(_ddPts[_di][0], _ddPts[_di][1], _dotR, 0, Math.PI * 2);
            this.ctx.fill();
          }
        } else {
          // Ladder: side rails + rungs
          const _railW = 1.5 / 12, _rungW = 1.5 / 12;
          const _gapFt = 15 / 12;
          const _stepFt = _rungW + _gapFt;
          this.ctx.fillRect(-_hLen, -_hWid, _lLen, _railW);
          this.ctx.fillRect(-_hLen, _hWid - _railW, _lLen, _railW);
          const _nGaps = Math.round((_lLen - _rungW) / _stepFt);
          for (let _ri = 0; _ri <= _nGaps; _ri++) {
            const _rx = -_hLen + _rungW / 2 + _ri * ((_lLen - _rungW) / _nGaps);
            this.ctx.fillRect(_rx - _rungW / 2, -_hWid, _rungW, _lWid);
          }
        }
        this.ctx.restore();
      }
    }
    // Ghost preview for paste-to-place
    if (this._pastePreview) {
      const pp = this._pastePreview;
      const ox = pp.offset.x, oy = pp.offset.y;
      this.ctx.save();
      this.ctx.globalAlpha = 0.5;
      const wallPx = this.getWallPixelWidth(scale);
      // Draw ghost wall groups
      pp.wallGroups.forEach(group => {
        // Fill closed rooms
        if (group.closed && group.walls.length >= 3) {
          this.ctx.fillStyle = group.type === 'subtract' ? 'rgba(204, 68, 41, 0.15)' : 'rgba(0, 107, 204, 0.15)';
          this.ctx.beginPath();
          this.ctx.moveTo(group.walls[0].start.x + ox, group.walls[0].start.y + oy);
          for (let i = 0; i < group.walls.length; i++) this.ctx.lineTo(group.walls[i].end.x + ox, group.walls[i].end.y + oy);
          this.ctx.closePath();
          this.ctx.fill();
        }
        // Draw wall lines
        this.ctx.strokeStyle = this._tc.wallColor;
        this.ctx.lineWidth = wallPx / scale;
        this.ctx.lineCap = 'round';
        group.walls.forEach(w => {
          this.ctx.beginPath();
          this.ctx.moveTo(w.start.x + ox, w.start.y + oy);
          this.ctx.lineTo(w.end.x + ox, w.end.y + oy);
          this.ctx.stroke();
        });
      });
      // Draw ghost objects
      pp.objects.forEach(obj => {
        const gx = obj.x + ox, gy = obj.y + oy;
        if (obj.type === 'equipment') {
          const info = this._equipmentImages && this._equipmentImages[obj.equipmentId];
          const halfW = obj.width / 2, halfD = obj.depth / 2;
          const rot = obj.rotation || 0;
          this.ctx.save();
          this.ctx.globalAlpha = 1.0;
          this.ctx.translate(gx, gy);
          if (rot) this.ctx.rotate(rot);
          if (info && info.loaded) {
            // Recolored silhouette fill at 50%, no outlines — matches swatch placement ghost
            const sil = this._ensureEquipmentCanvas(info, 'sil');
            if (sil) {
              if (!this._ghostRecolorCanvas) this._ghostRecolorCanvas = document.createElement('canvas');
              const gc = this._ghostRecolorCanvas;
              if (gc.width !== sil.width || gc.height !== sil.height) {
                gc.width = sil.width; gc.height = sil.height;
              }
              const gctx = gc.getContext('2d');
              gctx.clearRect(0, 0, gc.width, gc.height);
              gctx.drawImage(sil, 0, 0);
              gctx.globalCompositeOperation = 'source-in';
              gctx.fillStyle = this.darkMode ? '#ffffff' : '#000F1C';
              gctx.fillRect(0, 0, gc.width, gc.height);
              gctx.globalCompositeOperation = 'source-over';
              this.ctx.globalAlpha = 0.5;
              this.ctx.drawImage(gc, -halfW, -halfD, obj.width, obj.depth);
            }
          } else {
            this.ctx.globalAlpha = 0.5;
            this.ctx.fillStyle = this.darkMode ? '#ffffff' : '#000F1C';
            this.ctx.fillRect(-halfW, -halfD, obj.width, obj.depth);
          }
          this.ctx.restore();
        } else if (obj.type === 'platform' || obj.type === 'turf') {
          const halfW = obj.width / 2, halfD = obj.depth / 2;
          const rot = obj.rotation || 0;
          // Check overlap for red tint
          const _pasteWall = obj.type === 'platform' && this._platformOverlapsWall(gx, gy, obj.width, obj.depth, rot);
          const _pasteTurf = obj.type === 'platform' && this._turfOverlapsTurf(gx, gy, obj.width, obj.depth, rot, null);
          const _pastePlat = obj.type === 'platform' && this._platformOverlapsPlatform(gx, gy, obj.width, obj.depth, rot, null);
          // Zone containment check for paste ghost
          let _pasteZoneBlocked = false;
          if (obj.type === 'platform' && window.roomDesignerOverlay) {
            const _pgOv = window.roomDesignerOverlay;
            let _pgRoom = null;
            for (const room of (this.rooms || [])) {
              if (!room.closed) continue;
              const geo = this._getRoomGeoCache(room);
              if (geo.roomPolygon && this.pointInPolygon(gx, gy, geo.roomPolygon)) { _pgRoom = room; break; }
            }
            const _pgZone = _pgRoom ? _pgOv._getZoneFlooringAtPoint(_pgRoom, gx, gy) : null;
            if (_pgZone && _pgZone.product.type === 'tile' && obj.preset === '8x5') _pasteZoneBlocked = true;
            if (!_pasteZoneBlocked && _pgRoom) {
              const _pgRot90 = (Math.round(rot / (Math.PI / 2)) % 2 !== 0);
              const _pgW = _pgRot90 ? obj.depth : obj.width, _pgD = _pgRot90 ? obj.width : obj.depth;
              const _pgCenter = _pgOv._getZoneFlooringAtPoint(_pgRoom, gx, gy);
              const _pgCPoly = _pgCenter && _pgCenter.zone ? _pgCenter.zone.polygon : null;
              const _pgCorners = [
                { x: gx - _pgW/2, y: gy - _pgD/2 }, { x: gx + _pgW/2, y: gy - _pgD/2 },
                { x: gx + _pgW/2, y: gy + _pgD/2 }, { x: gx - _pgW/2, y: gy + _pgD/2 }
              ];
              for (const c of _pgCorners) {
                const _cz = _pgOv._getZoneFlooringAtPoint(_pgRoom, c.x, c.y);
                if ((_cz && _cz.zone ? _cz.zone.polygon : null) !== _pgCPoly) { _pasteZoneBlocked = true; break; }
              }
            }
          }
          const _pasteBlocked = _pasteWall || _pasteTurf || _pastePlat || _pasteZoneBlocked;
          this.ctx.save();
          this.ctx.translate(gx, gy);
          if (rot) this.ctx.rotate(rot);
          this.ctx.globalAlpha = 0.45;
          this.ctx.fillStyle = _pasteBlocked ? 'rgba(200, 40, 40, 0.4)' : (obj.centerColor || (obj.type === 'platform' ? '#1C1C1C' : this._tc.objectFill));
          this.ctx.fillRect(-halfW, -halfD, obj.width, obj.depth);
          if (obj.type === 'platform' && obj.wings && !_pasteBlocked) {
            this.ctx.fillStyle = obj.wingColor || '#D32F2F';
            this.ctx.fillRect(-halfW, -halfD, 2, obj.depth);
            this.ctx.fillRect(halfW - 2, -halfD, 2, obj.depth);
          }
          this.ctx.globalAlpha = 1.0;
          this.ctx.strokeStyle = _pasteBlocked ? '#cc2222' : 'rgba(0,0,0,0.3)';
          this.ctx.lineWidth = 1 / scale;
          this.ctx.strokeRect(-halfW, -halfD, obj.width, obj.depth);
          this.ctx.restore();
        } else if (obj.shape === 'circle') {
          const r = (obj.width || 1) / 2;
          this.ctx.fillStyle = this._tc.objectFill;
          this.ctx.strokeStyle = this._tc.objectStroke;
          this.ctx.lineWidth = wallPx / scale;
          this.ctx.beginPath(); this.ctx.arc(gx, gy, r, 0, Math.PI * 2); this.ctx.fill(); this.ctx.stroke();
        } else {
          // Rectangle (column, etc.)
          const w = obj.width || 1, h = obj.height || 1;
          const rot = obj.rotation || 0;
          this.ctx.save();
          this.ctx.translate(gx, gy);
          if (rot) this.ctx.rotate(rot);
          this.ctx.fillStyle = this._tc.objectFill;
          this.ctx.strokeStyle = this._tc.objectStroke;
          this.ctx.lineWidth = wallPx / scale;
          this.ctx.fillRect(-w / 2, -h / 2, w, h);
          this.ctx.strokeRect(-w / 2, -h / 2, w, h);
          this.ctx.restore();
        }
      });
      this.ctx.restore();
      // Proximity dimension lines for paste ghost (gray dashed)
      if (pp.objects.length > 0 || pp.wallGroups.length > 0) {
        const pasteCenter = { x: pp.centroid.x + ox, y: pp.centroid.y + oy };
        const pasteHW = pp.bboxHalfW || 0, pasteHD = pp.bboxHalfH || 0;
        if (pasteHW > 0 && pasteHD > 0) {
          const proxExclude = this._pasteCardinalSnap ? { axis: this._pasteCardinalSnap.axis, sign: this._pasteCardinalSnap.sign } : null;
          const proxSource = this._pasteCardinalSnap && this._lastPastedPos ? this._lastPastedPos : null;
          this._drawEquipProximityDims(pasteCenter, pasteHW, pasteHD, 0, scale, proxExclude, proxSource);
        }
      }
      // Edge-gap measurement line for paste cardinal chaining
      if (this._pasteCardinalSnap && this._pasteCardinalSnap.gap > 0) {
        const cs = this._pasteCardinalSnap;
        const e1 = cs.edge1, e2 = cs.edge2;
        const nubLen = Math.max(this.wallThickness * 0.4, 4 / scale);
        const nubX = cs.axis === 'x' ? 0 : 1;
        const nubY = cs.axis === 'x' ? 1 : 0;
        const _pcsLineColor = this.darkMode ? 'rgba(80, 170, 255, 0.75)' : 'rgba(0, 90, 180, 0.65)';
        const _pcsLabelColor = this.darkMode ? '#50aaff' : '#005ab4';
        this.ctx.save();
        this.ctx.globalAlpha = 1.0;
        this.ctx.strokeStyle = _pcsLineColor;
        this.ctx.lineWidth = 1.5 / scale;
        this.ctx.setLineDash([2 / scale, 2 / scale]);
        this.ctx.beginPath();
        this.ctx.moveTo(e1.x, e1.y);
        this.ctx.lineTo(e2.x, e2.y);
        this.ctx.stroke();
        this.ctx.setLineDash([]);
        this.ctx.beginPath();
        this.ctx.moveTo(e1.x - nubX * nubLen, e1.y - nubY * nubLen);
        this.ctx.lineTo(e1.x + nubX * nubLen, e1.y + nubY * nubLen);
        this.ctx.moveTo(e2.x - nubX * nubLen, e2.y - nubY * nubLen);
        this.ctx.lineTo(e2.x + nubX * nubLen, e2.y + nubY * nubLen);
        this.ctx.stroke();
        const fontSize = (11 + Math.min(7, Math.max(0, (scale - 20) / 12))) / scale;
        this.ctx.font = `${fontSize}px Arial`;
        this.ctx.fillStyle = _pcsLabelColor;
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        const lblX = (e1.x + e2.x) / 2, lblY = (e1.y + e2.y) / 2;
        const lblOff = Math.max(this.wallThickness * 2, 16 / scale);
        // Label above for horizontal chains, right for vertical — consistent with equidistant labels
        this.ctx.textAlign = nubX > 0 ? 'left' : 'center';
        this.ctx.textBaseline = nubY > 0 ? 'bottom' : 'middle';
        this.ctx.fillText(this.formatMeasurement(cs.gap), lblX + nubX * lblOff, lblY - nubY * lblOff);
        this.ctx.textAlign = 'start';
        this.ctx.textBaseline = 'alphabetic';
        this.ctx.restore();
      }
    }
    this._placedWallLabels = [];
    if (this.showMeasurements) this._drawWallLabels(scale);
    if (this.showRoomLabels) this._drawRoomLabels(scale);
    if (this.showRoomLabels && !this._floorMode && !this._equipmentMode) this._drawZoneLabels(scale);
    this._pendingWallLabels = [];
    this._pendingRoomLabels = [];

    // Highlight open endpoints when pen active
    if (this.activeTool === 'pen' && this.mode === 'draw' && !this.currentWall && !this.hasPlacedFirstPoint) {
      const allUnclosed = [];
      if (this.currentRoom && this.currentRoom.walls.length > 0 && !this.currentRoom.closed) allUnclosed.push(...this.getOpenEndpoints(this.currentRoom));
      this.rooms.forEach(room => { if (!room.closed && room.walls.length > 0) allUnclosed.push(...this.getOpenEndpoints(room)); });
      allUnclosed.forEach(ep => {
        let highlight = this.mouseWorld && this.getDistance(this.mouseWorld, ep) < (4 / 12);
        this.ctx.fillStyle = highlight ? 'rgba(0, 200, 0, 0.7)' : 'rgba(0, 107, 204, 0.4)';
        this.ctx.beginPath(); this.ctx.arc(ep.x, ep.y, (highlight ? 6 : 4) / scale, 0, Math.PI * 2); this.ctx.fill();
        this.ctx.strokeStyle = highlight ? '#00aa00' : '#006BCC'; this.ctx.lineWidth = 1.5 / scale;
        this.ctx.beginPath(); this.ctx.arc(ep.x, ep.y, (highlight ? 6 : 4) / scale, 0, Math.PI * 2); this.ctx.stroke();
      });
    }

    // Close preview — check currentRoom endpoints + other unclosed rooms
    let nearClose = false;
    if (this.activeTool === 'pen' && this.mode === 'draw' && this.mouseWorld && this.currentRoom && this.currentRoom.walls.length >= 1 && this.currentWall && this.hasPlacedFirstPoint) {
      const screenPixelsPerInch = this.basePixelsPerFoot * this.zoomLevel / 12; const snapThreshold = screenPixelsPerInch >= 3 ? (1 / 12) : (4 / 12); const currentStart = this.currentWall.start;
      const closeCandidates = [];
      this.getOpenEndpoints(this.currentRoom).filter(ep => this.getDistance(ep, currentStart) > 0.01).forEach(ep => closeCandidates.push({ ep, otherRoom: null }));
      this.rooms.forEach(room => { if (room.closed || room.walls.length === 0) return; this.getOpenEndpoints(room).forEach(ep => closeCandidates.push({ ep, otherRoom: room })); });
      for (const cand of closeCandidates) {
        if (this.getDistance(this.mouseWorld, cand.ep) < snapThreshold) {
          nearClose = true;
          // Build polygon preview: if from another room, simulate merged walls for preview
          let previewWalls = this.currentRoom.walls;
          if (cand.otherRoom) previewWalls = [...previewWalls, ...cand.otherRoom.walls];
          const wallPoints = this.buildRoomPolygon({ walls: previewWalls }, currentStart, cand.ep);
          if (wallPoints && wallPoints.length >= 3) {
            this.ctx.fillStyle = this.currentRoom.type === 'subtract' ? 'rgba(204, 0, 0, 0.15)' : 'rgba(0, 107, 204, 0.15)';
            this.ctx.beginPath(); this.ctx.moveTo(wallPoints[0].x, wallPoints[0].y);
            for (let i = 1; i < wallPoints.length; i++) this.ctx.lineTo(wallPoints[i].x, wallPoints[i].y);
            this.ctx.closePath(); this.ctx.fill();
          }
          this.ctx.strokeStyle = this.roomType === 'subtract' ? '#CC4429' : '#006BCC';
          this.ctx.lineWidth = 3 / scale; this.ctx.setLineDash([5 / scale, 5 / scale]);
          this.ctx.beginPath(); this.ctx.moveTo(currentStart.x, currentStart.y); this.ctx.lineTo(cand.ep.x, cand.ep.y); this.ctx.stroke(); this.ctx.setLineDash([]);
          this.ctx.fillStyle = 'rgba(0, 200, 0, 0.7)'; this.ctx.beginPath(); this.ctx.arc(cand.ep.x, cand.ep.y, 6 / scale, 0, Math.PI * 2); this.ctx.fill();
          this.ctx.strokeStyle = '#00aa00'; this.ctx.lineWidth = 1.5 / scale; this.ctx.beginPath(); this.ctx.arc(cand.ep.x, cand.ep.y, 6 / scale, 0, Math.PI * 2); this.ctx.stroke();
          break;
        }
      }
    }

    // Subtract mode wall distance overlay
    if (this.activeTool === 'pen' && this.mode === 'draw' && this.roomType === 'subtract' && this.mouseWorldRaw) {
      const overlayPoint = (this.currentWall && this.hasPlacedFirstPoint) ? this.currentWall.end : this.mouseWorld;
      if (overlayPoint) {
        const nearest = this.findNearestWall(overlayPoint, 1.5);
        if (nearest && (!this.currentRoom || nearest.room !== this.currentRoom)) {
          const wall = nearest.wall; const param = this.getWallParam(overlayPoint, wall);
          const wallLen = this.getWallLength(wall);
          const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y, wLen = Math.sqrt(wdx * wdx + wdy * wdy);
          if (wLen > 0) {
            const perpX = -wdy / wLen, perpY = wdx / wLen; const labelOffset = 18 / scale; const smallFont = 11 / scale;
            const snapPt = this.paramToPoint(wall, param);
            this.ctx.fillStyle = 'rgba(204, 68, 41, 0.5)'; this.ctx.beginPath(); this.ctx.arc(snapPt.x, snapPt.y, 5 / scale, 0, Math.PI * 2); this.ctx.fill();
            this.ctx.font = `${smallFont}px Arial`; this.ctx.fillStyle = 'rgba(204, 68, 41, 0.7)'; this.ctx.textAlign = 'center'; this.ctx.textBaseline = 'middle';
            const midToStart = this.paramToPoint(wall, param / 2);
            const _sl1 = this._clampLabelToViewport(midToStart.x + perpX * labelOffset, midToStart.y + perpY * labelOffset, scale);
            this.ctx.fillText(this.formatMeasurement(param * wallLen), _sl1.x, _sl1.y);
            const midToEnd = this.paramToPoint(wall, param + (1 - param) / 2);
            const _sl2 = this._clampLabelToViewport(midToEnd.x + perpX * labelOffset, midToEnd.y + perpY * labelOffset, scale);
            this.ctx.fillText(this.formatMeasurement((1 - param) * wallLen), _sl2.x, _sl2.y);
            this.ctx.textAlign = 'start'; this.ctx.textBaseline = 'alphabetic';
          }
        }
      }
    }

    // Alignment guide lines while drawing
    if (this.activeTool === 'pen' && this.mode === 'draw' && this.currentWall && this.hasPlacedFirstPoint && this.mouseWorld && !nearClose) {
      const guideEps = [];
      const activeType = this.roomType;
      if (this.currentRoom && this.currentRoom.walls.length > 0 && !this.currentRoom.closed && this.currentRoom.type === activeType) guideEps.push(...this.getOpenEndpoints(this.currentRoom));
      this.rooms.forEach(room => { if (!room.closed && room.walls.length > 0 && room.type === activeType) guideEps.push(...this.getOpenEndpoints(room)); });
      const wallStart = this.currentWall.start;
      const mousePos = this.currentWall.end;
      const epsilon = 0.001;
      const guideColor = activeType === 'subtract' ? 'rgba(204, 68, 41, 0.85)' : 'rgba(0, 107, 204, 0.85)';
      this.ctx.strokeStyle = guideColor;
      this.ctx.lineWidth = 2.5 / scale;
      this.ctx.setLineDash([4 / scale, 3 / scale]);
      guideEps.forEach(ep => {
        if (this.getDistance(ep, wallStart) < 0.01) return;
        if (Math.abs(ep.x - mousePos.x) < epsilon) {
          this.ctx.beginPath(); this.ctx.moveTo(mousePos.x, mousePos.y); this.ctx.lineTo(ep.x, ep.y); this.ctx.stroke();
        }
        if (Math.abs(ep.y - mousePos.y) < epsilon) {
          this.ctx.beginPath(); this.ctx.moveTo(mousePos.x, mousePos.y); this.ctx.lineTo(ep.x, ep.y); this.ctx.stroke();
        }
      });
      this.ctx.setLineDash([]);
    }

    // Current wall being drawn
    if (this.currentWall && this.activeTool === 'pen' && this.mode === 'draw' && !nearClose) {
      const _isSubtract = this.roomType === 'subtract' && this.penDrawMode !== 'wallSegment';
      this.ctx.strokeStyle = _isSubtract ? '#CC4429' : '#006BCC';
      this.ctx.lineWidth = 3 / scale; this.ctx.setLineDash([5 / scale, 5 / scale]);
      this.ctx.beginPath(); this.ctx.moveTo(this.currentWall.start.x, this.currentWall.start.y); this.ctx.lineTo(this.currentWall.end.x, this.currentWall.end.y);
      this.ctx.stroke(); this.ctx.setLineDash([]);
      if (this.mouseWorld) {
        const dist = this.getDistance(this.currentWall.start, this.currentWall.end);
        if (dist > 0.1) {
            const fontSize = (this.largeUI ? 17 : 14) / scale;
            this.ctx.font = `${fontSize}px Arial`;
            this.ctx.fillStyle = _isSubtract ? '#CC4429' : '#006BCC';
            const _p = this._clampLabelToViewport(this.currentWall.end.x + 15 / scale, this.currentWall.end.y - 15 / scale, scale);
            this.ctx.fillText(this.formatMeasurement(dist), _p.x, _p.y);
          }
      }
      // Gray locked dot at freehand start point when confirmed
      if (this._freehandStartConfirmed) {
        const sr = 4 / scale;
        this.ctx.fillStyle = '#999'; this.ctx.globalAlpha = 0.7;
        this.ctx.beginPath(); this.ctx.arc(this.currentWall.start.x, this.currentWall.start.y, sr, 0, Math.PI * 2); this.ctx.fill();
        this.ctx.globalAlpha = 1.0;
      }
    }

    // Door placement preview
    if (this.mode === 'door' && this.mouseWorldRaw) this._drawDoorPreview(scale);

    // Door swing edit overlay
    if (this._doorSwingEdit && this.mouseWorldRaw) this._drawDoorSwingEdit(scale);

    // Door drag distance measurements
    if (this._selectDragging && this.selectedDoors.size > 0) this._drawDoorDragMeasurements(scale);

    // Equipment / turf / platform drag/nudge proximity measurements
    if ((this._selectDragging || this._equipmentNudgeShowDims) && this.selectedObjects.size > 0) {
      // Collect all group IDs that contain mixed types (equipment + platform/turf)
      // so we draw one combined AABB for the whole group instead of separate dims
      const selArr = [...this.selectedObjects];
      const drawnGroups = new Set(); // groupIds already handled

      // First pass: draw combined AABB per group that has ANY members in selection
      const groupIds = new Set();
      for (const obj of selArr) { if (obj.groupId) groupIds.add(obj.groupId); }
      for (const gid of groupIds) {
        if (drawnGroups.has(gid)) continue;
        drawnGroups.add(gid);
        const members = selArr.filter(o => o.groupId === gid);
        // Build combined AABB from all group members (equipment + turf + platform)
        let gMinX = Infinity, gMinY = Infinity, gMaxX = -Infinity, gMaxY = -Infinity;
        for (const m of members) {
          const r = m.rotation || 0;
          const cA = Math.abs(Math.cos(r)), sA = Math.abs(Math.sin(r));
          const w = (m.type === 'equipment' ? (m.tightWidth || m.width) : m.width);
          const d = (m.type === 'equipment' ? (m.tightDepth || m.depth) : m.depth);
          const hx = (w / 2) * cA + (d / 2) * sA;
          const hy = (w / 2) * sA + (d / 2) * cA;
          gMinX = Math.min(gMinX, m.x - hx); gMaxX = Math.max(gMaxX, m.x + hx);
          gMinY = Math.min(gMinY, m.y - hy); gMaxY = Math.max(gMaxY, m.y + hy);
        }
        const groupObj = { x: (gMinX + gMaxX) / 2, y: (gMinY + gMaxY) / 2,
          width: gMaxX - gMinX, depth: gMaxY - gMinY, rotation: 0, _groupMembers: members };
        this._drawTurfProximityDims(groupObj, scale);
      }

      // Second pass: ungrouped equipment → combined AABB (existing behavior)
      let eMinX = Infinity, eMinY = Infinity, eMaxX = -Infinity, eMaxY = -Infinity;
      let hasEquip = false;
      this.selectedObjects.forEach(obj => {
        if (obj.type !== 'equipment') return;
        if (obj.groupId && drawnGroups.has(obj.groupId)) return; // already in group dims
        hasEquip = true;
        const r = obj.rotation || 0;
        const cA = Math.abs(Math.cos(r)), sA = Math.abs(Math.sin(r));
        const tw = (obj.tightWidth || obj.width), td = (obj.tightDepth || obj.depth);
        const hx = (tw / 2) * cA + (td / 2) * sA;
        const hy = (tw / 2) * sA + (td / 2) * cA;
        eMinX = Math.min(eMinX, obj.x - hx); eMaxX = Math.max(eMaxX, obj.x + hx);
        eMinY = Math.min(eMinY, obj.y - hy); eMaxY = Math.max(eMaxY, obj.y + hy);
      });
      if (hasEquip) {
        const cx = (eMinX + eMaxX) / 2, cy = (eMinY + eMaxY) / 2;
        const halfW = (eMaxX - eMinX) / 2, halfH = (eMaxY - eMinY) / 2;
        this._drawEquipProximityDims({ x: cx, y: cy }, halfW, halfH, 0, scale, null, null, this.selectedObjects);
      }

      // Third pass: ungrouped turf/platforms → individual dims
      for (const obj of selArr) {
        if (obj.type !== 'turf' && obj.type !== 'platform') continue;
        if (obj.groupId && drawnGroups.has(obj.groupId)) continue; // already in group dims
        this._drawTurfProximityDims(obj, scale);
      }
    }

    // Selection rectangle
    if (this.activeTool === 'select') this._drawSelection(scale);
    if (this._regionPick) this._drawRegionPick(scale);

    // ── Measurement tool rendering (architectural dimension lines) ──
    // Visible when wall dims toggle is on, when user has drawn a measurement independently,
    // or when actively in measure mode (so the rubber-band preview renders while drawing).
    if (this.showMeasurements || this._measurementsVisible || this.mode === 'measure') {
    this.ctx.save();
    {
      const _drawArrowhead = (ctx, tipX, tipY, angle, sz, color) => {
        ctx.save();
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.moveTo(tipX, tipY);
        ctx.lineTo(tipX - sz * Math.cos(angle - 0.4), tipY - sz * Math.sin(angle - 0.4));
        ctx.lineTo(tipX - sz * Math.cos(angle + 0.4), tipY - sz * Math.sin(angle + 0.4));
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      };
      const _drawDimensionLine = (start, end, dist, offset, selected, labelOverrideWorld) => {
        const ctx = this.ctx;
        ctx.save();
        const color = selected ? '#006BCC' : (this.darkMode ? '#8AB4D6' : '#5A7A9B');
        const dx = end.x - start.x, dy = end.y - start.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        const lw = (selected ? 1.2 : 0.8) / scale;
        const arrowSize = 8 / scale;
        const extOvershoot = 6 / scale; // extension lines extend past dimension line
        let dimStart, dimEnd;
        const absOffset = Math.abs(offset || 0);
        if (absOffset > 0.01 && len > 0.01) {
          const nx = -dy / len, ny = dx / len; // perpendicular
          dimStart = { x: start.x + nx * offset, y: start.y + ny * offset };
          dimEnd = { x: end.x + nx * offset, y: end.y + ny * offset };
          // Extension lines — only draw if offset is significant (>= 1 ft)
          if (absOffset >= 1) {
            ctx.strokeStyle = color;
            ctx.lineWidth = lw;
            ctx.globalAlpha = 0.6;
            ctx.setLineDash([]);
            const extDir = offset > 0 ? 1 : -1;
            ctx.beginPath();
            ctx.moveTo(start.x + nx * extDir * (2 / scale), start.y + ny * extDir * (2 / scale));
            ctx.lineTo(dimStart.x + nx * extDir * extOvershoot, dimStart.y + ny * extDir * extOvershoot);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(end.x + nx * extDir * (2 / scale), end.y + ny * extDir * (2 / scale));
            ctx.lineTo(dimEnd.x + nx * extDir * extOvershoot, dimEnd.y + ny * extDir * extOvershoot);
            ctx.stroke();
            ctx.globalAlpha = 1.0;
          }
        } else {
          dimStart = start;
          dimEnd = end;
        }
        // Dimension line (solid, same weight as extension lines)
        ctx.strokeStyle = color;
        ctx.lineWidth = lw;
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(dimStart.x, dimStart.y);
        ctx.lineTo(dimEnd.x, dimEnd.y);
        ctx.stroke();
        ctx.setLineDash([]);
        // Arrowheads at both ends (outward-pointing)
        if (len > 0.01) {
          const ux = dx / len, uy = dy / len; // unit vector along start→end
          // Arrow at dimStart pointing outward (away from dimEnd)
          const a1 = Math.atan2(-uy, -ux);
          _drawArrowhead(ctx, dimStart.x, dimStart.y, a1, arrowSize, color);
          // Arrow at dimEnd pointing outward (away from dimStart)
          const a2 = Math.atan2(uy, ux);
          _drawArrowhead(ctx, dimEnd.x, dimEnd.y, a2, arrowSize, color);
        }
        // Label (pill at midpoint of dimension line, or overridden position)
        if (dist != null) {
          let mx = (dimStart.x + dimEnd.x) / 2, my = (dimStart.y + dimEnd.y) / 2;
          if (labelOverrideWorld) { mx = labelOverrideWorld.x; my = labelOverrideWorld.y; }
          const text = this.formatMeasurement(dist);
          const fontSize = (this.largeUI ? 14.5 : 12) / scale;
          ctx.font = '600 ' + fontSize + 'px Inter, system-ui, sans-serif';
          const tw = ctx.measureText(text).width;
          const pad = 4 / scale;
          ctx.fillStyle = this.darkMode ? 'rgba(0,0,0,0.85)' : 'rgba(255,255,255,0.92)';
          const rr = 3 / scale;
          const bx = mx - tw / 2 - pad, by = my - fontSize / 2 - pad;
          const bw = tw + pad * 2, bh = fontSize + pad * 2;
          ctx.beginPath();
          ctx.moveTo(bx + rr, by); ctx.lineTo(bx + bw - rr, by);
          ctx.quadraticCurveTo(bx + bw, by, bx + bw, by + rr);
          ctx.lineTo(bx + bw, by + bh - rr);
          ctx.quadraticCurveTo(bx + bw, by + bh, bx + bw - rr, by + bh);
          ctx.lineTo(bx + rr, by + bh);
          ctx.quadraticCurveTo(bx, by + bh, bx, by + bh - rr);
          ctx.lineTo(bx, by + rr);
          ctx.quadraticCurveTo(bx, by, bx + rr, by);
          ctx.closePath();
          ctx.fill();
          if (selected) { ctx.strokeStyle = '#006BCC'; ctx.lineWidth = 1.5 / scale; ctx.setLineDash([]); ctx.stroke(); }
          ctx.fillStyle = color;
          ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          ctx.fillText(text, mx, my);
          // Grab handle above label when selected
          if (selected) {
            const hSize = this._handleSize / scale;
            const hGap = 3 / scale;
            const hx = mx - hSize / 2, hy = by - hGap - hSize;
            ctx.fillStyle = '#fff';
            ctx.strokeStyle = '#006BCC';
            ctx.lineWidth = 1.5 / scale;
            ctx.fillRect(hx, hy, hSize, hSize);
            ctx.strokeRect(hx, hy, hSize, hSize);
          }
        }
        ctx.restore();
      };
      // Draw all committed measurements (only when dims toggle is on or measurements were drawn independently)
      if (this.showMeasurements || this._measurementsVisible) {
        for (const m of this._measurements) {
          _drawDimensionLine(m.start, m.end, m.dist, m.offset || 0, this._selectedMeasurements.has(m.id));
        }
      }
      // Rubber-band preview during 3-click flow
      if (this.mode === 'measure' && this._measurePhase === 1 && this._measureStart && this.mouseWorld) {
        // Phase 1: dashed line from start to mouse (preview endpoint)
        let previewEnd = { x: this.mouseWorld.x, y: this.mouseWorld.y };
        if (this.shiftHeld) previewEnd = this.constrainToAxis(this._measureStart, previewEnd);
        const dx = previewEnd.x - this._measureStart.x, dy = previewEnd.y - this._measureStart.y;
        const d = Math.sqrt(dx * dx + dy * dy);
        // Clamp label to viewport so it stays visible when zoomed in
        let labelOverride = null;
        const lmx = (this._measureStart.x + previewEnd.x) / 2;
        const lmy = (this._measureStart.y + previewEnd.y) / 2;
        const lsx = this.worldToScreen(lmx, lmy);
        const _mb = this._getLabelClampBounds();
        const clampedSx = Math.max(_mb.left, Math.min(_mb.right, lsx.x));
        const clampedSy = Math.max(_mb.top, Math.min(_mb.bottom, lsx.y));
        if (Math.abs(clampedSx - lsx.x) > 1 || Math.abs(clampedSy - lsx.y) > 1) {
          labelOverride = this.screenToWorld(clampedSx, clampedSy);
        }
        _drawDimensionLine(this._measureStart, previewEnd, d, 0, false, labelOverride);
      }
      if (this.mode === 'measure' && this._measurePhase === 2 && this._measureStart && this._measurePendingEnd && this.mouseWorldRaw) {
        // Phase 2: full dimension preview with live offset from mouse position
        const s = this._measureStart, e = this._measurePendingEnd;
        const dx = e.x - s.x, dy = e.y - s.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        let offset = 0;
        if (dist > 0.01) {
          const nx = -dy / dist, ny = dx / dist;
          offset = (this.mouseWorldRaw.x - s.x) * nx + (this.mouseWorldRaw.y - s.y) * ny;
          if (this.shiftHeld) offset = 0;
          else offset = Math.round(offset * 12) / 12;
        }
        _drawDimensionLine(s, e, dist, offset, false);
      }
      // Snap cursor dot in measure mode
      if (this.mode === 'measure' && this.mouseWorld && this._measurePhase <= 1) {
        const dotColor = this.darkMode ? '#8AB4D6' : '#5A7A9B';
        const dotR = 4 / scale;
        let snapPt = this.mouseWorld;
        if (this._measurePhase === 1 && this.shiftHeld) snapPt = this.constrainToAxis(this._measureStart, snapPt);
        this.ctx.save();
        this.ctx.fillStyle = dotColor;
        this.ctx.strokeStyle = this.darkMode ? '#000' : '#fff';
        this.ctx.lineWidth = 1.2 / scale;
        this.ctx.beginPath(); this.ctx.arc(snapPt.x, snapPt.y, dotR, 0, Math.PI * 2);
        this.ctx.fill(); this.ctx.stroke();
        this.ctx.restore();
      }
    }
    this.ctx.restore(); // end measurement block
    } // end measurements gate

    // Snap cursor (skip if currentWall is zero-length — no phantom dot on first tap, UNLESS freehand auto-placed)
    const _isZeroLenWall = this.currentWall && this.currentWall.start.x === this.currentWall.end.x && this.currentWall.start.y === this.currentWall.end.y;
    const _showFreehandDot = this._freehandAutoPlaced && _isZeroLenWall;
    if (this.activeTool === 'pen' && this.mode === 'draw' && this.mouseWorld && !nearClose && (!_isZeroLenWall || _showFreehandDot)) {
      const _isSub = this.roomType === 'subtract' && this.penDrawMode !== 'wallSegment';
      let cursorColor = _isSub ? 'rgba(204, 68, 41, 0.5)' : 'rgba(0, 107, 204, 0.5)';
      let cursorStroke = _isSub ? '#CC4429' : '#006BCC';
      let snapWall = null, snapParam = 0, snapRoom = null;
      if (_isSub && this.mouseWorldRaw) {
        const spi = this.basePixelsPerFoot * this.zoomLevel / 12; const gt = spi >= 3 ? (1 / 12) : (1 / 4);
        const near = this.findNearestWall(this.mouseWorldRaw, gt);
        if (near && (!this.currentRoom || near.room !== this.currentRoom)) { cursorColor = 'rgba(0, 200, 0, 0.6)'; cursorStroke = '#00aa00'; }
      }
      let _wsEndpointSnap = null;
      if (this.penDrawMode === 'wallSegment' && this.mouseWorldRaw) {
        const spi = this.basePixelsPerFoot * this.zoomLevel / 12;
        const snapT = spi >= 3 ? (1 / 12) : (4 / 12);
        let wsSnapped = false;
        this._wsVisualSnap = null; // reset each frame
        // Use mouseWorldRaw for endpoint proximity — mouseWorld is grid-snapped and may miss off-grid endpoints
        // Endpoint snap radius is 2× normal — endpoints are discrete targets users aim for
        const rawPtSnap = this.mouseWorldRaw || this.mouseWorld;
        const epSnapT = snapT * 2;
        // Priority 1: snap to wall segment group endpoints (exact tip — beats wall projection)
        if (!wsSnapped) {
          for (const group of this.wallSegmentGroups) {
            if (this._wallSegmentType === 'wall' && (group.type || 'wall') === 'line') continue;
            for (const seg of group.segments) {
              for (const ep of [seg.start, seg.end]) {
                if (this.getDistance(rawPtSnap, ep) < epSnapT) {
                  cursorColor = 'rgba(0, 200, 0, 0.6)'; cursorStroke = '#00aa00';
                  _wsEndpointSnap = { x: ep.x, y: ep.y };
                  wsSnapped = true; break;
                }
              }
              if (wsSnapped) break;
            }
            if (wsSnapped) break;
          }
        }
        // Priority 2: snap to in-progress chain endpoints
        if (!wsSnapped && this.currentWallSegmentGroup) {
          for (const seg of this.currentWallSegmentGroup.segments) {
            for (const ep of [seg.start, seg.end]) {
              if (this.getDistance(rawPtSnap, ep) < epSnapT) {
                cursorColor = 'rgba(0, 200, 0, 0.6)'; cursorStroke = '#00aa00';
                _wsEndpointSnap = { x: ep.x, y: ep.y };
                wsSnapped = true; break;
              }
            }
            if (wsSnapped) break;
          }
        }
        // Priority 3: snap to room wall endpoints — lines snap exact, dividing walls snap 2" inset
        if (!wsSnapped) {
          const isLine = this._wallSegmentType === 'line';
          if (isLine) {
            for (const room of [...this.rooms, this.currentRoom]) {
              if (!room || !room.walls) continue;
              for (const wall of room.walls) {
                const epCheckPt = this.mouseWorldRaw;
                const epThreshold = epSnapT + this.wallThickness;
                for (const ep of [wall.start, wall.end]) {
                  if (this.getDistance(epCheckPt, ep) < epThreshold) {
                    cursorColor = 'rgba(0, 200, 0, 0.6)'; cursorStroke = '#00aa00';
                    _wsEndpointSnap = { x: ep.x, y: ep.y };
                    snapWall = wall; snapParam = this.getWallParam(ep, wall); snapRoom = room;
                    wsSnapped = true; break;
                  }
                }
                if (wsSnapped) break;
              }
              if (wsSnapped) break;
            }
          } else {
            const INSET = 2 / 12;
            let bestDist = Infinity;
            for (const room of [...this.rooms, this.currentRoom]) {
              if (!room || !room.walls) continue;
              for (const wall of room.walls) {
                for (const ep of [wall.start, wall.end]) {
                  if (this.getDistance(rawPtSnap, ep) < epSnapT && !this._hasDoorAtPoint(room, ep)) {
                    const toward = ep === wall.start ? wall.end : wall.start;
                    const dx = toward.x - ep.x, dy = toward.y - ep.y;
                    const len = Math.sqrt(dx * dx + dy * dy);
                    if (len > INSET) {
                      const candidate = { x: ep.x + (dx / len) * INSET, y: ep.y + (dy / len) * INSET };
                      const d = this.getDistance(rawPtSnap, candidate);
                      if (d < bestDist) {
                        bestDist = d;
                        cursorColor = 'rgba(0, 200, 0, 0.6)'; cursorStroke = '#00aa00';
                        _wsEndpointSnap = candidate;
                        snapWall = wall; snapParam = this.getWallParam(candidate, wall); snapRoom = room;
                        wsSnapped = true;
                      }
                    }
                  }
                }
              }
            }
          }
        }
        // Priority 4: sticky wall (mouseWorld already projected there by _processMove)
        if (!wsSnapped && this._wsSnapSticky) {
          const sw = this._wsSnapSticky.wall;
          let param = this.getWallParam(this.mouseWorld, sw);
          // Dividing walls: clamp param away from room wall endpoints (2" inset)
          if (this._wallSegmentType !== 'line' && this._wsSnapSticky.room) {
            const _wl = this.getDistance(sw.start, sw.end);
            if (_wl > 0) { const _ip = (2/12) / _wl; param = Math.max(_ip, Math.min(1 - _ip, param)); }
          }
          const proj = this.paramToPoint(sw, param);
          if (this.getDistance(this.mouseWorld, proj) < snapT + 0.05) {
            let doorBlocked = false;
            if (this._wallSegmentType !== 'line') {
              const doors = this._wsSnapSticky.room ? (this._wsSnapSticky.room.doors || []) : (this._wsSnapSticky.group ? this._wsSnapSticky.group.doors || [] : []);
              for (const door of doors) {
                if (door.wall !== sw) continue;
                const lo = Math.min(door.hingeParam, door.endParam);
                const hi = Math.max(door.hingeParam, door.endParam);
                if (param >= lo - 0.02 && param <= hi + 0.02) { doorBlocked = true; break; }
              }
            }
            if (doorBlocked) { cursorColor = 'rgba(204, 50, 50, 0.6)'; cursorStroke = '#cc3232'; }
            else { cursorColor = 'rgba(0, 200, 0, 0.6)'; cursorStroke = '#00aa00'; snapWall = sw; snapParam = param; snapRoom = this._wsSnapSticky.room; }
            wsSnapped = true;
          }
        }
        // Check room walls (midpoint projection / T-intersection)
        if (!wsSnapped) for (const room of [...this.rooms, this.currentRoom]) {
          if (!room || !room.walls) continue;
          for (const wall of room.walls) {
            // For dividing lines, use raw cursor for proximity (handles outside of thick walls)
            const isLineSnap = this._wallSegmentType === 'line';
            const wallCheckPt = isLineSnap ? this.mouseWorldRaw : this.mouseWorld;
            let param = this.getWallParam(wallCheckPt, wall);
            // Dividing walls: clamp param away from room wall endpoints (2" inset)
            if (!isLineSnap) {
              const _wl = this.getDistance(wall.start, wall.end);
              if (_wl > 0) { const _ip = (2/12) / _wl; param = Math.max(_ip, Math.min(1 - _ip, param)); }
            }
            const proj = this.paramToPoint(wall, param);
            if (this.getDistance(wallCheckPt, proj) < snapT + (isLineSnap ? this.wallThickness : 0)) {
              // On a wall — check if a door occupies this point (lines exempt)
              let doorBlocked = false;
              if (this._wallSegmentType !== 'line') {
                for (const door of room.doors) {
                  if (door.wall !== wall) continue;
                  const lo = Math.min(door.hingeParam, door.endParam);
                  const hi = Math.max(door.hingeParam, door.endParam);
                  if (param >= lo - 0.02 && param <= hi + 0.02) { doorBlocked = true; break; }
                }
              }
              if (doorBlocked) { cursorColor = 'rgba(204, 50, 50, 0.6)'; cursorStroke = '#cc3232'; }
              else {
                // Inside/outside enforcement for dividing lines
                let sideBlocked = false;
                if (this._wallSegmentType === 'line' && this._wsLineSide && this.hasPlacedFirstPoint
                    && room.closed && room.type === 'add') {
                  const geo = this._getRoomGeoCache(room);
                  if (geo.roomPolygon && geo.roomPolygon.length >= 3) {
                    const cursorSide = this.pointInPolygon(this.mouseWorldRaw.x, this.mouseWorldRaw.y, geo.roomPolygon) ? 'inside' : 'outside';
                    if (cursorSide !== this._wsLineSide) sideBlocked = true;
                  }
                }
                if (sideBlocked) { cursorColor = 'rgba(204, 50, 50, 0.6)'; cursorStroke = '#cc3232'; }
                else { cursorColor = 'rgba(0, 200, 0, 0.6)'; cursorStroke = '#00aa00'; snapWall = wall; snapParam = param; snapRoom = room; }
              }
              wsSnapped = true; break;
            }
          }
          if (wsSnapped) break;
        }
        // Check wall segment group segments
        if (!wsSnapped) {
          for (const group of this.wallSegmentGroups) {
            // Divider walls can't start on divider lines
            if (this._wallSegmentType === 'wall' && (group.type || 'wall') === 'line') continue;
            for (const seg of group.segments) {
              const param = this.getWallParam(this.mouseWorld, seg);
              const proj = this.paramToPoint(seg, param);
              if (this.getDistance(this.mouseWorld, proj) < snapT) {
                let doorBlocked = false;
                if (this._wallSegmentType !== 'line') {
                  for (const door of group.doors) {
                    if (door.wall !== seg) continue;
                    const lo = Math.min(door.hingeParam, door.endParam);
                    const hi = Math.max(door.hingeParam, door.endParam);
                    if (param >= lo - 0.02 && param <= hi + 0.02) { doorBlocked = true; break; }
                  }
                }
                if (doorBlocked) { cursorColor = 'rgba(204, 50, 50, 0.6)'; cursorStroke = '#cc3232'; }
                else { cursorColor = 'rgba(0, 200, 0, 0.6)'; cursorStroke = '#00aa00'; snapWall = seg; snapParam = param; }
                wsSnapped = true; break;
              }
            }
            if (wsSnapped) break;
          }
        }
        // Check own chain start point (loop-close green dot). Snap the dot exactly
        // onto the start so the closing click lands on it — an island only becomes a
        // cut-out zone if the loop closes precisely.
        if (!wsSnapped && this.currentWallSegmentGroup && this.currentWallSegmentGroup.segments.length > 0 && this.hasPlacedFirstPoint) {
          const chainStart = this.currentWallSegmentGroup.segments[0].start;
          if (this.getDistance(rawPtSnap, chainStart) < epSnapT) {
            cursorColor = 'rgba(0, 200, 0, 0.6)'; cursorStroke = '#00aa00';
            _wsEndpointSnap = { x: chainStart.x, y: chainStart.y };
            wsSnapped = true;
          }
        }
        // First point: green anywhere inside a room (free-standing island start),
        // red only outside every room
        if (!wsSnapped && !this.hasPlacedFirstPoint) {
          if (this._isPointInsideAnyRoom(this.mouseWorld)) {
            cursorColor = 'rgba(0, 200, 0, 0.6)'; cursorStroke = '#00aa00';
          } else {
            cursorColor = 'rgba(204, 50, 50, 0.6)'; cursorStroke = '#cc3232';
          }
        }
        // Free-standing dividing lines must stay inside the room they carve
        if (this.hasPlacedFirstPoint && this._wallSegmentType === 'line' && !this._wsLineSide
            && !this._isPointInsideAnyRoom(this.mouseWorld)) {
          cursorColor = 'rgba(204, 50, 50, 0.6)'; cursorStroke = '#cc3232';
        }
        // Dividing walls: red dot if cursor would land outside all rooms (lines exempt)
        if (this.hasPlacedFirstPoint && this._wallSegmentType === 'wall') {
          let testPt = { x: this.mouseWorld.x, y: this.mouseWorld.y };
          if (this.shiftHeld || this._shiftToggle) testPt = this.constrainToAxis(this.currentWall.start, testPt);
          if (!this._isPointInsideAnyRoom(testPt)) {
            cursorColor = 'rgba(204, 50, 50, 0.6)'; cursorStroke = '#cc3232';
          }
        }
      }
      // For dividing lines, show ONE dot on the correct wall face (inside or outside).
      // Inner face = centerline; Outer face = centerline + outward * wallThickness.
      // Which face: determined by pointInPolygon (cursor inside room → inside face).
      // After first click, _wsLineSide locks face; cursor on wrong face → red dot.
      let dotX = this.mouseWorld.x, dotY = this.mouseWorld.y;
      // Apply endpoint snap position (takes priority over wall projection)
      if (_wsEndpointSnap) { dotX = _wsEndpointSnap.x; dotY = _wsEndpointSnap.y; }
      if (snapWall && this._wallSegmentType === 'line' && !_wsEndpointSnap) {
        const qPt = this._quantizeWallSnap(snapWall, snapParam);
        const wdx = snapWall.end.x - snapWall.start.x, wdy = snapWall.end.y - snapWall.start.y;
        const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
        if (wLen > 0.01 && snapRoom) {
          const nx = -wdy / wLen, ny = wdx / wLen;
          const geo = this._getRoomGeoCache(snapRoom);
          if (geo.roomPolygon && geo.roomPolygon.length >= 3) {
            // Determine outward direction from room polygon
            let outSign = 1;
            const testPt = { x: qPt.x + nx * 0.05, y: qPt.y + ny * 0.05 };
            if (this.pointInPolygon(testPt.x, testPt.y, geo.roomPolygon)) outSign = -1;
            // Inner face = centerline (offset 0), Outer face = full wallThickness outward
            const insideEdge = { x: qPt.x, y: qPt.y };
            const outsideEdge = { x: qPt.x + nx * outSign * this.wallThickness, y: qPt.y + ny * outSign * this.wallThickness };
            // Determine face from cursor position
            const rawPt = this.mouseWorldRaw || this.mouseWorld;
            const cursorOnInside = this.pointInPolygon(rawPt.x, rawPt.y, geo.roomPolygon);
            if (this._wsLineSide && this.hasPlacedFirstPoint) {
              // Side locked — position dot where cursor is, red if wrong side
              const showInside = cursorOnInside;
              if (showInside) { dotX = insideEdge.x; dotY = insideEdge.y; }
              else { dotX = outsideEdge.x; dotY = outsideEdge.y; }
              const cursorSide = cursorOnInside ? 'inside' : 'outside';
              if (cursorSide !== this._wsLineSide) {
                cursorColor = 'rgba(204, 50, 50, 0.6)'; cursorStroke = '#cc3232';
              }
            } else {
              // Before first click — always show dot on inside face
              // (exterior dividing lines are disabled for now)
              dotX = insideEdge.x; dotY = insideEdge.y;
            }
          } else {
            dotX = qPt.x; dotY = qPt.y;
          }
        } else {
          dotX = qPt.x; dotY = qPt.y;
        }
        this._lineSnapEdgePt = { x: dotX, y: dotY };
      } else if (this._wallSegmentType === 'line' && this._wsLineSide && this.hasPlacedFirstPoint) {
        // Fallback: cursor not near any wall but side is locked — check for wrong side
        const rawPt = this.mouseWorldRaw || this.mouseWorld;
        const cursorSide = this._isPointInsideAnyRoom(rawPt) ? 'inside' : 'outside';
        if (cursorSide !== this._wsLineSide) {
          cursorColor = 'rgba(204, 50, 50, 0.6)'; cursorStroke = '#cc3232';
        }
      }
      // Store the visual snap target so the click handler can use it directly
      if (cursorStroke === '#00aa00') this._wsVisualSnap = { x: dotX, y: dotY };
      // Freehand intro animation: bounce + fade (same as rectangle corner 1 intro)
      const baseR = 3 / scale;
      let dotScale = 1, dotAlpha = 1, fhRipple = 0;
      if (this._freehandAutoPlaced && this._rectIntroAnimating && !this._freehandStartConfirmed) {
        const elapsed = performance.now() - this._rectIntroAnimStart;
        const t = Math.min(elapsed / 400, 1);
        if (t < 0.6) { dotScale = t / 0.6 * 1.15; }
        else { dotScale = 1.15 - 0.15 * ((t - 0.6) / 0.4); }
        dotAlpha = Math.min(elapsed / 150, 1);
        fhRipple = t;
      }
      this.ctx.globalAlpha = dotAlpha;
      this.ctx.fillStyle = cursorColor; this.ctx.beginPath(); this.ctx.arc(dotX, dotY, baseR * dotScale, 0, Math.PI * 2); this.ctx.fill();
      this.ctx.strokeStyle = cursorStroke; this.ctx.lineWidth = 1.5 / scale; this.ctx.beginPath(); this.ctx.arc(dotX, dotY, baseR * dotScale, 0, Math.PI * 2); this.ctx.stroke();
      this.ctx.globalAlpha = 1.0;
      // Intro ring ripple for freehand
      if (fhRipple > 0 && fhRipple < 1) {
        const rippleR = baseR * (1 + fhRipple * 4);
        this.ctx.strokeStyle = cursorStroke;
        this.ctx.lineWidth = 1.5 / scale;
        this.ctx.globalAlpha = 0.5 * (1 - fhRipple);
        this.ctx.beginPath(); this.ctx.arc(dotX, dotY, rippleR, 0, Math.PI * 2); this.ctx.stroke();
        this.ctx.globalAlpha = 1.0;
      }
      // Snap measurement lines: show distance from green dot to nearest break points on each side
      if (snapWall && this.penDrawMode === 'wallSegment') {
        const wdx = snapWall.end.x - snapWall.start.x, wdy = snapWall.end.y - snapWall.start.y;
        const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
        if (wLen > 0.01) {
          const wux = wdx / wLen, wuy = wdy / wLen;
          const wnx = -wuy, wny = wux; // perpendicular (left normal)
          const sht = this._wallSegmentType === 'line' ? 0 : this.wallSegmentThickness / 2;
          const allBPs = this._getWallBreakPoints(snapWall);
          const dimPerp = Math.max(this.wallThickness * 2.0, 16 / scale);
          const labelOffset = Math.max(this.wallThickness * 2.5, 22 / scale);
          const nubLen = Math.max(this.wallThickness * 0.4, 4 / scale);
          const fontSize = (11 + Math.min(7, Math.max(0, (scale - 20) / 12))) / scale;
          // Draw measurement line on one perpendicular side
          const drawSnapMeasure = (bp, snapP, pnx, pny) => {
            const adjP = bp.adjust > 0
              ? (bp.param < snapP ? bp.param + bp.adjust / wLen : bp.param - bp.adjust / wLen)
              : bp.param;
            const fromP = Math.min(adjP, snapP), toP = Math.max(adjP, snapP);
            const dist = (toP - fromP) * wLen;
            if (dist < 0.05) return;
            const fPt = this.paramToPoint(snapWall, fromP);
            const tPt = this.paramToPoint(snapWall, toP);
            const ox = pnx * dimPerp, oy = pny * dimPerp;
            const x1 = fPt.x + ox, y1 = fPt.y + oy;
            const x2 = tPt.x + ox, y2 = tPt.y + oy;
            this.ctx.strokeStyle = 'rgba(0, 107, 204, 0.55)';
            this.ctx.lineWidth = 1.5 / scale;
            this.ctx.setLineDash([2 / scale, 2 / scale]);
            this.ctx.beginPath(); this.ctx.moveTo(x1, y1); this.ctx.lineTo(x2, y2); this.ctx.stroke();
            this.ctx.setLineDash([]);
            this.ctx.beginPath();
            // At wall endpoints (param near 0 or 1), draw half-nub (outward only) to avoid crossing connecting walls
            const bpIsEnd = bp.param < 0.02 || bp.param > 0.98;
            const bpAtX1 = adjP <= snapP;
            if (bpIsEnd && bpAtX1) { this.ctx.moveTo(x1, y1); this.ctx.lineTo(x1 + pnx * nubLen, y1 + pny * nubLen); }
            else { this.ctx.moveTo(x1 - pnx * nubLen, y1 - pny * nubLen); this.ctx.lineTo(x1 + pnx * nubLen, y1 + pny * nubLen); }
            if (bpIsEnd && !bpAtX1) { this.ctx.moveTo(x2, y2); this.ctx.lineTo(x2 + pnx * nubLen, y2 + pny * nubLen); }
            else { this.ctx.moveTo(x2 - pnx * nubLen, y2 - pny * nubLen); this.ctx.lineTo(x2 + pnx * nubLen, y2 + pny * nubLen); }
            this.ctx.stroke();
            const midPt = this.paramToPoint(snapWall, (fromP + toP) / 2);
            this.ctx.font = `${fontSize}px Arial`; this.ctx.fillStyle = '#006BCC';
            this.ctx.textAlign = 'center'; this.ctx.textBaseline = 'middle';
            this.ctx.fillText(this.formatMeasurement(dist), midPt.x + pnx * labelOffset, midPt.y + pny * labelOffset);
          };
          // For room walls, only draw on the interior side (skip exterior)
          let interiorSign = 0; // 0 = draw both, 1 = pos only, -1 = neg only
          if (snapRoom && snapRoom.closed) {
            const geo = this._getRoomGeoCache(snapRoom);
            if (geo && geo.roomPolygon && geo.roomPolygon.length >= 3) {
              const midP = this.paramToPoint(snapWall, 0.5);
              const testDist = 0.1;
              const posInside = this.pointInPolygon(midP.x + wnx * testDist, midP.y + wny * testDist, geo.roomPolygon);
              interiorSign = posInside ? 1 : -1;
            }
          }
          // Draw on interior side only (or both sides for segment walls)
          for (const sign of [1, -1]) {
            if (interiorSign !== 0 && sign !== interiorSign) continue;
            const pnx = wnx * sign, pny = wny * sign;
            const sideKey = sign === 1 ? 'pos' : 'neg';
            const sideBPs = allBPs.filter(bp => bp.sides === sideKey || bp.sides === 'both');
            let sideLeft = null, sideRight = null;
            for (const bp of sideBPs) { if (bp.param < snapParam - 0.005) sideLeft = bp; }
            for (const bp of sideBPs) { if (bp.param > snapParam + 0.005) { sideRight = bp; break; } }
            // Measure to dividing wall edge, not center (offset by half thickness)
            const shtParam = sht / wLen;
            if (sideLeft) drawSnapMeasure(sideLeft, snapParam - shtParam, pnx, pny);
            if (sideRight) drawSnapMeasure(sideRight, snapParam + shtParam, pnx, pny);
          }
          this.ctx.textAlign = 'start'; this.ctx.textBaseline = 'alphabetic';
        }
      }
    }

    // Rectangle draw mode preview
    if (this.penDrawMode === 'rectangle' && this._rectCorner1 && this.mouseWorld && this.activeTool === 'pen' && this.mode === 'draw') {
      const c1 = this._rectCorner1, c2 = this._rectCorner2 || this.mouseWorld;
      const minX = Math.min(c1.x, c2.x), maxX = Math.max(c1.x, c2.x);
      const minY = Math.min(c1.y, c2.y), maxY = Math.max(c1.y, c2.y);
      const w = maxX - minX, h = maxY - minY;
      const rectColor = this.roomType === 'subtract' ? '#CC4429' : '#006BCC';
      const rectFill = this.roomType === 'subtract' ? 'rgba(204, 0, 0, 0.08)' : 'rgba(0, 107, 204, 0.08)';
      // Filled preview
      this.ctx.fillStyle = rectFill;
      this.ctx.fillRect(minX, minY, w, h);
      // Dashed outline — when one dimension is ~0, strokeRect draws two overlapping
      // dashed lines whose gaps fill each other in, appearing solid. Use a single line instead.
      this.ctx.strokeStyle = rectColor;
      this.ctx.lineWidth = 2.5 / scale;
      this.ctx.setLineDash([6 / scale, 4 / scale]);
      const thinThresh = 0.05;
      if (w < thinThresh && h < thinThresh) {
        // Both dimensions zero — nothing to stroke
      } else if (h < thinThresh) {
        // Horizontal line only
        this.ctx.beginPath(); this.ctx.moveTo(minX, minY); this.ctx.lineTo(maxX, minY); this.ctx.stroke();
      } else if (w < thinThresh) {
        // Vertical line only
        this.ctx.beginPath(); this.ctx.moveTo(minX, minY); this.ctx.lineTo(minX, maxY); this.ctx.stroke();
      } else {
        this.ctx.strokeRect(minX, minY, w, h);
      }
      this.ctx.setLineDash([]);
      // Dimension labels
      if (w > 0.01 || h > 0.01) {
        const fontSize = 13 / scale;
        this.ctx.font = `600 ${fontSize}px Arial`;
        this.ctx.fillStyle = rectColor;
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        const _rb = this._getLabelClampBounds();
        // Dynamic label placement: labels stick to the two edges closest to the mouse
        const mx = this.mouseWorld ? this.mouseWorld.x : c2.x;
        const my = this.mouseWorld ? this.mouseWorld.y : c2.y;
        const nearTop = Math.abs(my - minY) <= Math.abs(my - maxY);
        const nearLeft = Math.abs(mx - minX) <= Math.abs(mx - maxX);
        const labelOff = 14 / scale;
        if (this._isMobile && this._rectCorner2) {
          // Mobile: labels near corner2, biased toward nearest walls
          const wallBias = 0.25;
          const biasMaxPx = 50 / scale;
          // Width label on nearest horizontal edge
          const widthOffY = nearTop ? -24/scale : 24/scale;
          const rawBiasX = (c1.x - c2.x) * wallBias;
          const widthX = c2.x + (Math.abs(rawBiasX) > biasMaxPx ? Math.sign(rawBiasX) * biasMaxPx : rawBiasX);
          if (w > 0.01) { const _p = this._clampLabelToViewport(widthX, c2.y + widthOffY, scale, _rb); this.ctx.fillText(this.formatMeasurement(w), _p.x, _p.y); }
          // Height label on nearest vertical edge
          if (h > 0.01) {
            const heightOffX = nearLeft ? -24/scale : 24/scale;
            const rawBiasY = (c1.y - c2.y) * wallBias;
            const heightY = c2.y + (Math.abs(rawBiasY) > biasMaxPx ? Math.sign(rawBiasY) * biasMaxPx : rawBiasY);
            const _p = this._clampLabelToViewport(c2.x + heightOffX, heightY, scale, _rb); this.ctx.fillText(this.formatMeasurement(h), _p.x, _p.y);
          }
        } else {
          // Desktop: width label on nearest horizontal edge, height label on nearest vertical edge
          if (w > 0.01) {
            const wLabelY = nearTop ? minY - labelOff : maxY + labelOff;
            const _p = this._clampLabelToViewport(minX + w / 2, wLabelY, scale, _rb);
            this.ctx.fillText(this.formatMeasurement(w), _p.x, _p.y);
          }
          if (h > 0.01) {
            if (nearLeft) {
              this.ctx.textAlign = 'right';
              const _p = this._clampLabelToViewport(minX - 10 / scale, minY + h / 2, scale, _rb); this.ctx.fillText(this.formatMeasurement(h), _p.x, _p.y);
            } else {
              this.ctx.textAlign = 'left';
              const _p = this._clampLabelToViewport(maxX + 10 / scale, minY + h / 2, scale, _rb); this.ctx.fillText(this.formatMeasurement(h), _p.x, _p.y);
            }
            this.ctx.textAlign = 'center';
          }
        }
        this.ctx.textAlign = 'start';
        this.ctx.textBaseline = 'alphabetic';
      }
      // Corner 1 dot (with intro animation if active)
      const baseR = 3 / scale;
      let c1Scale = 1, c1Alpha = 0.7, introRipple = 0;
      if (this._rectIntroAnimating && !this._rectCorner1Confirmed) {
        const elapsed = performance.now() - this._rectIntroAnimStart;
        const t = Math.min(elapsed / 400, 1);
        // Scale bounce: 0 → 1.15 → 1.0
        if (t < 0.6) { c1Scale = t / 0.6 * 1.15; }
        else { c1Scale = 1.15 - 0.15 * ((t - 0.6) / 0.4); }
        // Opacity fade: 0 → 1 over first 150ms
        c1Alpha = Math.min(elapsed / 150, 1) * 0.7;
        // Ring ripple
        introRipple = t;
      }
      // Draw corner 2 first (blue, active) so corner 1 (gray, locked) renders on top when stacked
      this.ctx.fillStyle = this.roomType === 'subtract' ? 'rgba(204, 68, 41, 0.5)' : 'rgba(0, 107, 204, 0.5)';
      this.ctx.beginPath(); this.ctx.arc(c2.x, c2.y, baseR, 0, Math.PI * 2); this.ctx.fill();
      this.ctx.strokeStyle = rectColor; this.ctx.lineWidth = 1.5 / scale;
      this.ctx.beginPath(); this.ctx.arc(c2.x, c2.y, baseR, 0, Math.PI * 2); this.ctx.stroke();
      // Corner 1 dot — gray when confirmed, colored when still active
      const c1Color = this._rectCorner1Confirmed ? '#999' : rectColor;
      this.ctx.fillStyle = c1Color;
      this.ctx.globalAlpha = c1Alpha;
      this.ctx.beginPath(); this.ctx.arc(c1.x, c1.y, baseR * c1Scale, 0, Math.PI * 2); this.ctx.fill();
      this.ctx.globalAlpha = 1.0;
      // Intro ring ripple
      if (introRipple > 0 && introRipple < 1) {
        const rippleR = baseR * (1 + introRipple * 4);
        this.ctx.strokeStyle = c1Color;
        this.ctx.lineWidth = 1.5 / scale;
        this.ctx.globalAlpha = 0.5 * (1 - introRipple);
        this.ctx.beginPath(); this.ctx.arc(c1.x, c1.y, rippleR, 0, Math.PI * 2); this.ctx.stroke();
        this.ctx.globalAlpha = 1.0;
      }
    }

    /* CUSTOM_PLANNER_BY_CLAUDE — Turf draw hover dot (red if overlapping existing turf) */
    if (this.activeTool === 'turf-draw' && this.mouseWorld && this._turfBrush) {
      const tSnap = this._snapTurfToWallEdge(this.mouseWorld);
      const tDotBaseR = 3 / scale;
      const dotOverlapsTurf = this.objects.some(o => {
        if (o.type !== 'turf') return false;
        const dx = tSnap.x - o.x, dy = tSnap.y - o.y;
        const cos = Math.cos(-o.rotation), sin = Math.sin(-o.rotation);
        const lx = Math.abs(dx * cos - dy * sin);
        const ly = Math.abs(dx * sin + dy * cos);
        return lx < o.width / 2 && ly < o.depth / 2;
      });
      this.ctx.fillStyle = dotOverlapsTurf ? 'rgba(200, 40, 40, 0.5)' : 'rgba(0, 180, 60, 0.5)';
      this.ctx.beginPath(); this.ctx.arc(tSnap.x, tSnap.y, tDotBaseR, 0, Math.PI * 2); this.ctx.fill();
      this.ctx.strokeStyle = dotOverlapsTurf ? '#cc2222' : '#00aa00';
      this.ctx.lineWidth = 1.5 / scale;
      this.ctx.beginPath(); this.ctx.arc(tSnap.x, tSnap.y, tDotBaseR, 0, Math.PI * 2); this.ctx.stroke();
    }

    /* CUSTOM_PLANNER_BY_CLAUDE — Turf draw preview */
    if (this.activeTool === 'turf-draw' && this._turfCorner1 && this.mouseWorld && this._turfBrush) {
      const tc1 = this._turfCorner1, tc2 = this._snapTurfToWallEdge(this.mouseWorld);
      const tMinX = Math.min(tc1.x, tc2.x), tMaxX = Math.max(tc1.x, tc2.x);
      const tMinY = Math.min(tc1.y, tc2.y), tMaxY = Math.max(tc1.y, tc2.y);
      const tw = tMaxX - tMinX, th = tMaxY - tMinY;
      // Check wall/turf/platform collision for red tint
      const tcx = (tc1.x + tc2.x) / 2, tcy = (tc1.y + tc2.y) / 2;
      let _turfHitsPlatform = false;
      if (tw >= 1/12 && th >= 1/12) {
        for (const _po of this.objects) {
          if (_po.type !== 'platform') continue;
          const _r90 = (Math.round((_po.rotation || 0) / (Math.PI / 2)) % 2 !== 0);
          const _pw2 = (_r90 ? _po.depth : _po.width) / 2;
          const _pd2 = (_r90 ? _po.width : _po.depth) / 2;
          if (tMaxX > _po.x - _pw2 + 0.01 && tMinX < _po.x + _pw2 - 0.01 &&
              tMaxY > _po.y - _pd2 + 0.01 && tMinY < _po.y + _pd2 - 0.01) {
            _turfHitsPlatform = true; break;
          }
        }
      }
      const _turfOnTurf = tw >= 1/12 && th >= 1/12 && this._turfOverlapsTurf(tcx, tcy, tw, th, 0, null);
      const tFullBlock = tw >= 1/12 && th >= 1/12 && (_turfOnTurf || _turfHitsPlatform);
      // Find room polygon for split preview (green inside room, red outside)
      const _turfPreviewPoly = !tFullBlock ? this._findRoomPolyContaining(tcx, tcy) : null;
      if (tFullBlock || !_turfPreviewPoly) {
        // Full red (turf-on-turf, platform hit, or no room)
        this.ctx.fillStyle = 'rgba(200, 40, 40, 0.3)';
        this.ctx.globalAlpha = 0.5;
        this.ctx.fillRect(tMinX, tMinY, tw, th);
        this.ctx.globalAlpha = 1.0;
      } else {
        // Split preview: green inside room, red outside
        // Green pass — clip to room polygon
        this.ctx.save();
        this.ctx.beginPath();
        for (let _pi = 0; _pi < _turfPreviewPoly.length; _pi++) {
          if (_pi === 0) this.ctx.moveTo(_turfPreviewPoly[_pi].x, _turfPreviewPoly[_pi].y);
          else this.ctx.lineTo(_turfPreviewPoly[_pi].x, _turfPreviewPoly[_pi].y);
        }
        this.ctx.closePath();
        this.ctx.clip();
        this.ctx.fillStyle = 'rgba(0, 180, 60, 0.35)';
        this.ctx.globalAlpha = 0.4;
        this.ctx.fillRect(tMinX, tMinY, tw, th);
        this.ctx.restore();
        // Red pass — inverse clip (outside room) using evenodd
        this.ctx.save();
        this.ctx.beginPath();
        this.ctx.moveTo(-1e5, -1e5); this.ctx.lineTo(1e5, -1e5); this.ctx.lineTo(1e5, 1e5); this.ctx.lineTo(-1e5, 1e5); this.ctx.closePath();
        for (let _pi = 0; _pi < _turfPreviewPoly.length; _pi++) {
          if (_pi === 0) this.ctx.moveTo(_turfPreviewPoly[_pi].x, _turfPreviewPoly[_pi].y);
          else this.ctx.lineTo(_turfPreviewPoly[_pi].x, _turfPreviewPoly[_pi].y);
        }
        this.ctx.closePath();
        this.ctx.clip('evenodd');
        this.ctx.fillStyle = 'rgba(200, 40, 40, 0.3)';
        this.ctx.globalAlpha = 0.5;
        this.ctx.fillRect(tMinX, tMinY, tw, th);
        this.ctx.restore();
        this.ctx.globalAlpha = 1.0;
      }
      const tBlocked = tFullBlock;
      this.ctx.strokeStyle = tBlocked ? '#cc2222' : '#00aa00';
      this.ctx.lineWidth = 2 / scale;
      this.ctx.setLineDash([6 / scale, 4 / scale]);
      this.ctx.strokeRect(tMinX, tMinY, tw, th);
      this.ctx.setLineDash([]);
      // Dimension labels
      if (tw > 0.01 || th > 0.01) {
        const tFs = 13 / scale;
        this.ctx.font = `600 ${tFs}px Arial`;
        this.ctx.fillStyle = tBlocked ? '#cc2222' : '#fff';
        this.ctx.textAlign = 'center'; this.ctx.textBaseline = 'middle';
        if (tw > 0.01) { const _p = this._clampLabelToViewport(tMinX + tw / 2, tMinY - 12 / scale, scale); this.ctx.fillText(this.formatMeasurement(tw), _p.x, _p.y); }
        if (th > 0.01) {
          const _p = this._clampLabelToViewport(tMaxX + 12 / scale, tMinY + th / 2, scale);
          this.ctx.save(); this.ctx.translate(_p.x, _p.y);
          this.ctx.rotate(Math.PI / 2); this.ctx.fillText(this.formatMeasurement(th), 0, 0);
          this.ctx.restore();
        }
      }
      // Corner dots
      const tDotR = 4 / scale;
      this.ctx.fillStyle = '#999';
      this.ctx.beginPath(); this.ctx.arc(tc1.x, tc1.y, tDotR, 0, Math.PI * 2); this.ctx.fill();
      this.ctx.fillStyle = tBlocked ? '#cc2222' : '#00b43c';
      this.ctx.beginPath(); this.ctx.arc(tc2.x, tc2.y, tDotR, 0, Math.PI * 2); this.ctx.fill();
    }
    /* END CUSTOM_PLANNER_BY_CLAUDE */

    /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — Platform placement ghost preview */
    if (this.activeTool === 'platform-place' && this.mouseWorld) {
      const _ov = window.roomDesignerOverlay;
      if (_ov) {
        _ov._initPlatformDefaults();
        const _pd = _ov._platformDefaults;
        const _presets = _ov._getPlatformPresets();
        const _preset = _presets.find(p => p.id === _pd.preset) || _presets[0];
        const _pw = _preset.width, _ph = _preset.depth;
        const _pRot = this._pendingPlatformPos ? (this._pendingPlatformRotation || 0)
          : (this._platformAnchor ? this._platformAnchor.rotation : 0);

        // Use locked position if in rotation phase, otherwise follow mouse
        let _gx, _gy;
        if (this._pendingPlatformPos) {
          _gx = this._pendingPlatformPos.x;
          _gy = this._pendingPlatformPos.y;
        } else if (this._platformAnchor && this.mouseWorldRaw) {
          // Subsequent platforms: snap to alignment axis if within 1 ft, otherwise free inch-grid
          const _nRot90 = (Math.round(_pRot / (Math.PI / 2)) % 2 !== 0);
          const _snapped = this._snapToPlatformGrid(this.mouseWorldRaw.x, this.mouseWorldRaw.y, _nRot90 ? _ph : _pw, _nRot90 ? _pw : _ph);
          _gx = _snapped.x;
          _gy = _snapped.y;
          // Track whether we're axis-aligned (for measurement line)
          const _dxA = Math.abs(this.mouseWorldRaw.y - this._platformAnchor.y);
          const _dyA = Math.abs(this.mouseWorldRaw.x - this._platformAnchor.x);
          var _platformAligned = (_dxA <= 1.0 || _dyA <= 1.0);
        } else if (this.mouseWorldRaw) {
          // First platform: inch-scale snap
          const _inchGrid = 1 / 12;
          _gx = Math.round(this.mouseWorldRaw.x / _inchGrid) * _inchGrid;
          _gy = Math.round(this.mouseWorldRaw.y / _inchGrid) * _inchGrid;
        } else {
          _gx = this.mouseWorld.x;
          _gy = this.mouseWorld.y;
        }
        // Find ghost room + zone info for tile snap and zone checks
        let _ghostRoom = null, _ghostZoneInfo = null;
        if (!this._pendingPlatformPos && _ov) {
          const _compatRooms = _ov._getPlatformCompatibleRooms();
          for (const { room } of _compatRooms) {
            const _gGeo = this._getRoomGeoCache(room);
            if (_gGeo.roomPolygon && this.pointInPolygon(_gx, _gy, _gGeo.roomPolygon)) { _ghostRoom = room; break; }
          }
          if (_ghostRoom) _ghostZoneInfo = _ov._getZoneFlooringAtPoint(_ghostRoom, _gx, _gy);
        }
        // During rotation phase, use the locked room
        if (this._pendingPlatformPos && this._pendingPlatformRoom && _ov) {
          _ghostRoom = this._pendingPlatformRoom;
          _ghostZoneInfo = _ov._getZoneFlooringAtPoint(_ghostRoom, _gx, _gy);
        }

        // Tile grid snap: override ghost position so corners land on tile seam intersections
        // Re-snaps during rotation phase too (rotation changes which corners hit the grid)
        if (_ghostZoneInfo && _ghostZoneInfo.product.type === 'tile') {
          const _tW = _ghostZoneInfo.product.widthFt || 2;
          const _tH = _ghostZoneInfo.product.heightFt || _tW;
          const _ghostGeo = this._getRoomGeoCache(_ghostRoom);
          const _tSnap = _ov._snapPlatformToTileGrid(_gx, _gy, _pw, _ph, _pRot, _tW, _tH, _ghostZoneInfo.flooringAngle, _ghostGeo.roomPolygon);
          _gx = _tSnap.x;
          _gy = _tSnap.y;
        }

        // Check blocked state — show red for wall/turf/platform overlap or incompatible zone
        const _wallBlocked = this._platformOverlapsWall(_gx, _gy, _pw, _ph, _pRot);
        const _turfBlocked = this._turfOverlapsTurf(_gx, _gy, _pw, _ph, _pRot, null);
        const _platBlocked = this._platformOverlapsPlatform(_gx, _gy, _pw, _ph, _pRot, null);
        // Zone compatibility: 8x5 on tile, single-zone containment, compatible flooring
        let _zoneBlocked = false;
        if (!this._pendingPlatformPos && _ov) {
          if (!_ghostRoom) {
            _zoneBlocked = true;
          } else if (_ghostZoneInfo && _ghostZoneInfo.product.type === 'tile' && _pd.preset === '8x5') {
            // 8'×5' can't align to tile grid
            _zoneBlocked = true;
          } else {
            // Check center + all 4 corners: compatible flooring + same zone
            const _zRot90 = (Math.round(_pRot / (Math.PI / 2)) % 2 !== 0);
            const _zW = _zRot90 ? _ph : _pw, _zD = _zRot90 ? _pw : _ph;
            const _zPts = [
              { x: _gx - _zW/2, y: _gy - _zD/2 }, { x: _gx + _zW/2, y: _gy - _zD/2 },
              { x: _gx + _zW/2, y: _gy + _zD/2 }, { x: _gx - _zW/2, y: _gy + _zD/2 }
            ];
            const _centerZonePoly = _ghostZoneInfo && _ghostZoneInfo.zone ? _ghostZoneInfo.zone.polygon : null;
            for (const _zp of _zPts) {
              if (!_ov._isPointOnCompatibleZone(_ghostRoom, _zp.x, _zp.y)) { _zoneBlocked = true; break; }
              const _cpZone = _ov._getZoneFlooringAtPoint(_ghostRoom, _zp.x, _zp.y);
              const _cpZonePoly = _cpZone && _cpZone.zone ? _cpZone.zone.polygon : null;
              if (_cpZonePoly !== _centerZonePoly) { _zoneBlocked = true; break; }
            }
          }
        }
        let _blocked = _wallBlocked || _turfBlocked || _platBlocked || _zoneBlocked;
        const _showRed = _wallBlocked || _platBlocked || _turfBlocked || _zoneBlocked;
        // During rotation phase, also check if corners extend outside the room or cross zones
        if (!_blocked && this._pendingPlatformPos && this._pendingPlatformRoom) {
          const _geo = this._getRoomGeoCache(this._pendingPlatformRoom);
          if (_geo.roomPolygon) {
            const _cRot90 = (Math.round(_pRot / (Math.PI / 2)) % 2 !== 0);
            const _cW = _cRot90 ? _ph : _pw, _cD = _cRot90 ? _pw : _ph;
            const _corners = [
              { x: _gx - _cW/2, y: _gy - _cD/2 }, { x: _gx + _cW/2, y: _gy - _cD/2 },
              { x: _gx + _cW/2, y: _gy + _cD/2 }, { x: _gx - _cW/2, y: _gy + _cD/2 }
            ];
            for (const _c of _corners) {
              if (!this.pointInPolygon(_c.x, _c.y, _geo.roomPolygon)) { _blocked = true; break; }
            }
            // Single-zone containment during rotation
            if (!_blocked && _ov) {
              const _rotCenterZone = _ov._getZoneFlooringAtPoint(this._pendingPlatformRoom, _gx, _gy);
              const _rotCenterPoly = _rotCenterZone && _rotCenterZone.zone ? _rotCenterZone.zone.polygon : null;
              for (const _c of _corners) {
                const _rotCornerZone = _ov._getZoneFlooringAtPoint(this._pendingPlatformRoom, _c.x, _c.y);
                const _rotCornerPoly = _rotCornerZone && _rotCornerZone.zone ? _rotCornerZone.zone.polygon : null;
                if (_rotCornerPoly !== _rotCenterPoly) { _blocked = true; break; }
              }
            }
          }
        }
        const _ghostAlpha = this._pendingPlatformPos ? 0.65 : 0.45;

        this.ctx.save();
        this.ctx.translate(_gx, _gy);
        if (_pRot) this.ctx.rotate(_pRot);
        this.ctx.globalAlpha = _ghostAlpha;

        // Center fill — red when overlapping platform/turf, otherwise platform colors
        this.ctx.fillStyle = _showRed ? 'rgba(200, 40, 40, 0.4)' : (_pd.centerColor || '#1C1C1C');
        this.ctx.fillRect(-_pw / 2, -_ph / 2, _pw, _ph);

        // Wing fills (skip when showing red)
        if (!_showRed && _pd.wings && _preset.wingsAvail) {
          this.ctx.fillStyle = _pd.wingColor || '#D32F2F';
          this.ctx.fillRect(-_pw / 2, -_ph / 2, 2, _ph);
          this.ctx.fillRect(_pw / 2 - 2, -_ph / 2, 2, _ph);
        }

        // Solid border
        this.ctx.globalAlpha = 1.0;
        this.ctx.strokeStyle = _showRed ? '#cc2222' : 'rgba(0,0,0,0.3)';
        this.ctx.lineWidth = 1 / scale;
        this.ctx.strokeRect(-_pw / 2, -_ph / 2, _pw, _ph);

        // Dimension label removed — preset size is already shown in the side panel

        this.ctx.restore();

        // Proximity dimension lines to 2 closest walls/equipment/platforms
        if (!this._pendingPlatformPos) {
          this._drawEquipProximityDims({ x: _gx, y: _gy }, _pw / 2, _ph / 2, _pRot, scale, null, null);
        }

        // Blue alignment line removed — gray proximity lines handle platform-to-platform measurement
      }
    }
    /* END CUSTOM_ROOMDESIGNER_BY_CLAUDE */

    // Lock-in animation (plays independently)
    if (this._rectLockAnimating && this._rectLockAnimCorner) {
      const elapsed = performance.now() - this._rectLockAnimStart;
      const t = Math.min(elapsed / 400, 1);
      const lc = this._rectLockAnimCorner;
      const isBlue = this._rectLockAnimColor === 'blue';
      // Outer expanding ring
      const outerR = (6 / scale) * (1 + t * 7);
      const chanVal = Math.round(200 + 55 * Math.sin(t * Math.PI));
      const outerAlpha = 0.85 * (1 - t);
      this.ctx.strokeStyle = isBlue
        ? `rgba(30, 100, ${chanVal}, ${outerAlpha})`
        : `rgba(40, ${chanVal}, 90, ${outerAlpha})`;
      this.ctx.lineWidth = 3 / scale;
      this.ctx.beginPath(); this.ctx.arc(lc.x, lc.y, outerR, 0, Math.PI * 2); this.ctx.stroke();
      // Inner glow ring
      const innerR = (4 / scale) * (1 + t * 3);
      this.ctx.strokeStyle = isBlue
        ? `rgba(80, 160, 255, ${0.6 * (1 - t)})`
        : `rgba(100, 255, 140, ${0.6 * (1 - t)})`;
      this.ctx.lineWidth = 2 / scale;
      this.ctx.beginPath(); this.ctx.arc(lc.x, lc.y, innerR, 0, Math.PI * 2); this.ctx.stroke();
      // Solid dot flash
      this.ctx.fillStyle = isBlue
        ? `rgba(40, 130, 230, ${0.9 * (1 - t * 0.5)})`
        : `rgba(60, 230, 100, ${0.9 * (1 - t * 0.5)})`;
      this.ctx.beginPath(); this.ctx.arc(lc.x, lc.y, 6 / scale * (1 - t * 0.3), 0, Math.PI * 2); this.ctx.fill();
    }

    this.ctx.restore();
    this._updateScaleBar();
  }

  // Collect break points along a wall: divider T-junctions, crossings, and endpoints
  // Returns sorted, deduped array of { param, adjust, sides } where:
  //   adjust = sht for divider breaks, 0 for room wall/free endpoints
  //   sides = 'both' | 'pos' | 'neg' — which side of the wall the obstruction extends to
  //   (pos = left normal direction, neg = opposite)
  _getWallBreakPoints(wall) {
    const wallSht = this.wallSegmentThickness / 2;
    const bpThresh = 0.05;
    const breaks = [];
    const d1x = wall.end.x - wall.start.x, d1y = wall.end.y - wall.start.y;
    const wLen = Math.sqrt(d1x * d1x + d1y * d1y);
    const nx = wLen > 0 ? -d1y / wLen : 0, ny = wLen > 0 ? d1x / wLen : 0;
    // Half-thickness for a group (line-type = 0, wall-type = wallSht)
    const grpSht = (grp) => (grp.type || 'wall') === 'line' ? 0 : wallSht;
    // Determine which side(s) of the wall a segment extends to at intersection param
    const getSides = (s, param) => {
      const ix = wall.start.x + param * d1x, iy = wall.start.y + param * d1y;
      const dot1 = (s.start.x - ix) * nx + (s.start.y - iy) * ny;
      const dot2 = (s.end.x - ix) * nx + (s.end.y - iy) * ny;
      const eps = 0.02;
      const pos = dot1 > eps || dot2 > eps;
      const neg = dot1 < -eps || dot2 < -eps;
      if (pos && neg) return 'both';
      if (pos) return 'pos';
      if (neg) return 'neg';
      return 'both';
    };
    // Wall endpoints — always visible from both sides
    const checkEndpoint = (pt, param) => {
      const allGroups = this.currentWallSegmentGroup
        ? [...this.wallSegmentGroups, this.currentWallSegmentGroup]
        : this.wallSegmentGroups;
      for (const grp of allGroups) {
        const sht = grpSht(grp);
        for (const s of grp.segments) {
          if (s === wall) continue;
          for (const ep of [s.start, s.end]) {
            if (this.getDistance(pt, ep) < bpThresh) return { param, adjust: sht, sides: 'both' };
          }
          const sp = this.getWallParam(pt, s);
          if (sp > 0.01 && sp < 0.99) {
            const proj = this.paramToPoint(s, sp);
            if (this.getDistance(pt, proj) < bpThresh) return { param, adjust: sht, sides: 'both' };
          }
        }
      }
      return { param, adjust: 0, sides: 'both' };
    };
    breaks.push(checkEndpoint(wall.start, 0));
    breaks.push(checkEndpoint(wall.end, 1));
    // Segment breaks — intersections + endpoint fallback (with side info)
    const collectSegBreaks = (segs, sht) => {
      for (const s of segs) {
        if (s === wall) continue;
        const d2x = s.end.x - s.start.x, d2y = s.end.y - s.start.y;
        const cross = d1x * d2y - d1y * d2x;
        if (Math.abs(cross) > 1e-10) {
          const dx = s.start.x - wall.start.x, dy = s.start.y - wall.start.y;
          const t = (dx * d2y - dy * d2x) / cross;
          const u = (dx * d1y - dy * d1x) / cross;
          if (t * wLen > 0.05 && (1 - t) * wLen > 0.05 && u >= -0.02 && u <= 1.02) breaks.push({ param: t, adjust: sht, sides: getSides(s, t) });
        }
        for (const ep of [s.start, s.end]) {
          const p = this.getWallParam(ep, wall);
          const proj = this.paramToPoint(wall, p);
          if (this.getDistance(ep, proj) < bpThresh && p * wLen > 0.05 && (1 - p) * wLen > 0.05) breaks.push({ param: p, adjust: sht, sides: getSides(s, p) });
        }
      }
    };
    for (const grp of this.wallSegmentGroups) collectSegBreaks(grp.segments, grpSht(grp));
    if (this.currentWallSegmentGroup) collectSegBreaks(this.currentWallSegmentGroup.segments, grpSht(this.currentWallSegmentGroup));
    // Room wall breaks — intersections + endpoint fallback (with side info)
    for (const room of [...this.rooms, this.currentRoom]) {
      if (!room || !room.walls) continue;
      for (const w of room.walls) {
        if (w === wall) continue;
        const rd2x = w.end.x - w.start.x, rd2y = w.end.y - w.start.y;
        const rcross = d1x * rd2y - d1y * rd2x;
        if (Math.abs(rcross) > 1e-10) {
          const rdx = w.start.x - wall.start.x, rdy = w.start.y - wall.start.y;
          const rt = (rdx * rd2y - rdy * rd2x) / rcross;
          const ru = (rdx * d1y - rdy * d1x) / rcross;
          if (rt * wLen > 0.05 && (1 - rt) * wLen > 0.05 && ru >= -0.02 && ru <= 1.02) breaks.push({ param: rt, adjust: 0, sides: getSides(w, rt) });
        }
        for (const ep of [w.start, w.end]) {
          const p = this.getWallParam(ep, wall);
          const proj = this.paramToPoint(wall, p);
          if (this.getDistance(ep, proj) < bpThresh && p * wLen > 0.05 && (1 - p) * wLen > 0.05) breaks.push({ param: p, adjust: 0, sides: getSides(w, p) });
        }
      }
    }
    // Sort + deduplicate (prefer higher adjust, merge sides when params collide)
    breaks.sort((a, b) => a.param - b.param);
    const uniq = [breaks[0]];
    for (let bi = 1; bi < breaks.length; bi++) {
      if (breaks[bi].param - uniq[uniq.length - 1].param > 0.005) {
        uniq.push(breaks[bi]);
      } else {
        const last = uniq[uniq.length - 1];
        if (breaks[bi].adjust > last.adjust) last.adjust = breaks[bi].adjust;
        if (last.sides !== 'both') {
          if (breaks[bi].sides === 'both' || breaks[bi].sides !== last.sides) last.sides = 'both';
        }
      }
    }
    return uniq;
  }
  // ── Proximity dimension lines for equipment / paste ghosts ──
  // Shows 2 gray dashed lines to nearest walls or equipment neighbors.
  // excludeSnap: { axis:'x'|'y', sign:1|-1 } to skip the cardinal-snap direction.
  // excludeSourcePos: {x,y} to skip the equipment piece the cardinal-snap already measures to.
  // excludeObjs: Set of objects to skip (e.g. the pieces being dragged).

  _drawEquipProximityDims(ghostPos, hw, hd, rotation, scale, excludeSnap, excludeSourcePos, excludeObjs) {
    const allWalls = [];
    for (const room of this.rooms) {
      if (!room.closed) continue;
      const vis = this.getVisibleRoomWalls(room);
      for (const w of vis) allWalls.push(w);
      const icData = this.getInteriorCutoutWalls(room);
      for (const d of icData) allWalls.push(d.wall);
    }
    for (const group of this.wallSegmentGroups) {
      if (group.type === 'line') continue;
      for (const seg of group.segments) allWalls.push(seg);
    }

    const candidates = [];
    const cosR = Math.cos(rotation), sinR = Math.sin(rotation);

    // ── Wall candidates (perpendicular projection) ──
    for (const wall of allWalls) {
      const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
      const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
      if (wLen < 0.01) continue;
      const param = this.getWallParam(ghostPos, wall);
      if (param < 0.01 || param > 0.99) continue;
      const proj = this.paramToPoint(wall, param);
      const dx = proj.x - ghostPos.x, dy = proj.y - ghostPos.y;
      const centerDist = Math.sqrt(dx * dx + dy * dy);
      if (centerDist < 0.01) continue;
      const nx = dx / centerDist, ny = dy / centerDist;
      // Rotation-aware edge offset
      const localNx = nx * cosR + ny * sinR;
      const localNy = -nx * sinR + ny * cosR;
      const tx = Math.abs(localNx) > 0.001 ? hw / Math.abs(localNx) : Infinity;
      const ty = Math.abs(localNy) > 0.001 ? hd / Math.abs(localNy) : Infinity;
      const edgeOffset = Math.min(tx, ty);
      const edgeDist = centerDist - edgeOffset;
      if (edgeDist < 0.5 || edgeDist > 30) continue; // skip walls closer than 6" (flush/touching)
      const edgePt = { x: ghostPos.x + nx * edgeOffset, y: ghostPos.y + ny * edgeOffset };
      candidates.push({ edgePt, targetPt: proj, edgeDist, nx, ny });
    }

    // ── Equipment + platform neighbor candidates (AABB edge-to-edge) ──
    const gCosA = Math.abs(cosR), gSinA = Math.abs(sinR);
    const gHX = hw * gCosA + hd * gSinA; // ghost AABB half-width
    const gHY = hw * gSinA + hd * gCosA; // ghost AABB half-height
    for (const obj of this.objects) {
      if (obj.type !== 'equipment' && obj.type !== 'platform') continue;
      // Skip the piece the cardinal-snap line already measures to
      if (excludeSourcePos && Math.abs(obj.x - excludeSourcePos.x) < 0.01 && Math.abs(obj.y - excludeSourcePos.y) < 0.01) continue;
      // Skip the pieces being dragged (they ARE the ghost)
      if (excludeObjs && excludeObjs.has(obj)) continue;
      const oRot = obj.rotation || 0;
      const oCos = Math.abs(Math.cos(oRot)), oSin = Math.abs(Math.sin(oRot));
      // Equipment uses tightWidth/tightDepth (inches→ft already halved); platforms use width/depth (ft)
      const oHW = obj.type === 'platform' ? obj.width / 2 : (obj.tightWidth || obj.width) / 2;
      const oHD = obj.type === 'platform' ? obj.depth / 2 : (obj.tightDepth || obj.depth) / 2;
      const oHX = oHW * oCos + oHD * oSin;
      const oHY = oHW * oSin + oHD * oCos;
      // Horizontal neighbors — check Y-range overlap
      const yOvlp = Math.min(ghostPos.y + gHY, obj.y + oHY) - Math.max(ghostPos.y - gHY, obj.y - oHY);
      if (yOvlp > 0) {
        // Clamp midY to ghost's vertical extent so line doesn't extend beyond the shape
        const midY = Math.max(ghostPos.y - gHY, Math.min(ghostPos.y + gHY, (ghostPos.y + obj.y) / 2));
        if (obj.x > ghostPos.x) {
          const gap = (obj.x - oHX) - (ghostPos.x + gHX);
          if (gap >= 0.05 && gap <= 30)
            candidates.push({ edgePt: { x: ghostPos.x + gHX, y: midY }, targetPt: { x: obj.x - oHX, y: midY }, edgeDist: gap, nx: 1, ny: 0 });
        } else {
          const gap = (ghostPos.x - gHX) - (obj.x + oHX);
          if (gap >= 0.05 && gap <= 30)
            candidates.push({ edgePt: { x: ghostPos.x - gHX, y: midY }, targetPt: { x: obj.x + oHX, y: midY }, edgeDist: gap, nx: -1, ny: 0 });
        }
      }
      // Vertical neighbors — check X-range overlap
      const xOvlp = Math.min(ghostPos.x + gHX, obj.x + oHX) - Math.max(ghostPos.x - gHX, obj.x - oHX);
      if (xOvlp > 0) {
        // Clamp midX to ghost's horizontal extent so line doesn't extend beyond the shape
        const midX = Math.max(ghostPos.x - gHX, Math.min(ghostPos.x + gHX, (ghostPos.x + obj.x) / 2));
        if (obj.y > ghostPos.y) {
          const gap = (obj.y - oHY) - (ghostPos.y + gHY);
          if (gap >= 0.05 && gap <= 30)
            candidates.push({ edgePt: { x: midX, y: ghostPos.y + gHY }, targetPt: { x: midX, y: obj.y - oHY }, edgeDist: gap, nx: 0, ny: 1 });
        } else {
          const gap = (ghostPos.y - gHY) - (obj.y + oHY);
          if (gap >= 0.05 && gap <= 30)
            candidates.push({ edgePt: { x: midX, y: ghostPos.y - gHY }, targetPt: { x: midX, y: obj.y + oHY }, edgeDist: gap, nx: 0, ny: -1 });
        }
      }
    }

    // Classify each candidate into a cardinal quadrant (right/left/down/up)
    // and exclude the quadrant the blue shift-held line already occupies.
    // E.g. blue goes left → skip all "left" candidates → pick from right/up/down.
    for (const c of candidates) {
      const isHoriz = Math.abs(c.nx) >= Math.abs(c.ny);
      if (isHoriz) c._dir = c.nx > 0 ? 'right' : 'left';
      else c._dir = c.ny > 0 ? 'down' : 'up';
    }
    // excludeSnap.sign = direction ghost moved FROM source, but the blue line
    // points FROM the ghost TOWARD the source — the opposite direction.
    // Flip the sign so we exclude the direction the blue line visually goes.
    let excludeDir = null;
    if (excludeSnap) {
      if (excludeSnap.axis === 'x') excludeDir = excludeSnap.sign > 0 ? 'left' : 'right';
      else excludeDir = excludeSnap.sign > 0 ? 'up' : 'down';
    }
    let filtered = excludeDir ? candidates.filter(c => c._dir !== excludeDir) : candidates;
    filtered.sort((a, b) => a.edgeDist - b.edgeDist);
    // Pick at most 4 lines but never 2 in the same direction
    const nearest = [];
    const usedDirs = new Set();
    for (const c of filtered) {
      if (nearest.length >= 4) break;
      if (usedDirs.has(c._dir)) continue;
      usedDirs.add(c._dir);
      nearest.push(c);
    }
    if (nearest.length === 0) return;

    // Style: gray dashed (distinct from blue cardinal-snap line)
    const dimColor = this.darkMode ? 'rgba(170, 185, 210, 0.7)' : 'rgba(90, 90, 90, 0.6)';
    const labelColor = this.darkMode ? '#a8bcd4' : '#666';
    const fontSize = (11 + Math.min(7, Math.max(0, (scale - 20) / 12))) / scale;
    const maxStub = Math.max(2, 200 / scale);

    for (const c of nearest) {
      let lineEnd = c.targetPt;
      if (this._isMobile && c.edgeDist > maxStub) {
        const dx = c.targetPt.x - c.edgePt.x, dy = c.targetPt.y - c.edgePt.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len > 0.01) lineEnd = { x: c.edgePt.x + dx / len * maxStub, y: c.edgePt.y + dy / len * maxStub };
      }
      this.ctx.strokeStyle = dimColor;
      this.ctx.lineWidth = 1.5 / scale;
      this.ctx.setLineDash([4 / scale, 3 / scale]);
      this.ctx.beginPath();
      this.ctx.moveTo(c.edgePt.x, c.edgePt.y);
      this.ctx.lineTo(lineEnd.x, lineEnd.y);
      this.ctx.stroke();
      this.ctx.setLineDash([]);
      // Label near ghost edge (25% along line)
      let lx, ly;
      if (this._isMobile) {
        const lblDist = Math.min(c.edgeDist * 0.3, 20 / scale);
        const dx = c.targetPt.x - c.edgePt.x, dy = c.targetPt.y - c.edgePt.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len > 0.01) { lx = c.edgePt.x + dx / len * lblDist; ly = c.edgePt.y + dy / len * lblDist; }
        else { lx = c.edgePt.x; ly = c.edgePt.y; }
      } else {
        lx = c.edgePt.x + (c.targetPt.x - c.edgePt.x) * 0.25;
        ly = c.edgePt.y + (c.targetPt.y - c.edgePt.y) * 0.25;
      }
      const lblOff = Math.max(this.wallThickness * 2, 16 / scale);
      // Axis-aligned gaps: fixed label side (above for horizontal, right for vertical)
      // so labels stay consistent with equidistant chain labels.
      // Diagonal (wall) gaps: perpendicular offset based on line direction.
      const offX = Math.abs(c.nx) < 0.01 ? lblOff : (-c.ny) * lblOff;
      const offY = Math.abs(c.ny) < 0.01 ? -lblOff : (c.nx) * lblOff;
      this.ctx.font = `${fontSize}px Arial`;
      this.ctx.fillStyle = labelColor;
      this.ctx.textAlign = Math.abs(offX) > Math.abs(offY) ? (offX > 0 ? 'left' : 'right') : 'center';
      this.ctx.textBaseline = Math.abs(offY) > Math.abs(offX) ? (offY < 0 ? 'bottom' : 'top') : 'middle';
      this.ctx.fillText(this.formatMeasurement(c.edgeDist), lx + offX, ly + offY);
    }

    // ── Equidistant pattern: highlight matching gaps between existing equipment ──
    // When ghost→nearest gap matches the gap between consecutive existing pieces,
    // draw those gaps so the user can confirm equal spacing in the chain.
    const eqTol = 0.01; // ~1/8" tolerance for gap matching
    const eqLblOff = Math.max(this.wallThickness * 2, 16 / scale);
    const tolerance = 1 / 12; // 1 inch — matches snap grid

    // Collect aligned equipment neighbors
    const eqAlignedV = []; // same-X vertical neighbors
    const eqAlignedH = []; // same-Y horizontal neighbors
    let eqClosestAbove = null, eqClosestBelow = null;
    let eqClosestLeft = null, eqClosestRight = null;

    for (const obj of this.objects) {
      if (obj.type !== 'equipment') continue;
      if (excludeObjs && excludeObjs.has(obj)) continue;
      const oRot = obj.rotation || 0;
      const oCos = Math.abs(Math.cos(oRot)), oSin = Math.abs(Math.sin(oRot));
      const oHW = (obj.tightWidth || obj.width) / 2, oHD = (obj.tightDepth || obj.depth) / 2;
      const oHX = oHW * oCos + oHD * oSin;
      const oHY = oHW * oSin + oHD * oCos;

      if (Math.abs(obj.x - ghostPos.x) < tolerance && Math.abs(obj.y - ghostPos.y) > tolerance) {
        eqAlignedV.push({ y: obj.y, hY: oHY, x: obj.x });
        const ghostEdgeY = ghostPos.y < obj.y ? ghostPos.y + gHY : ghostPos.y - gHY;
        const objEdgeY = ghostPos.y < obj.y ? obj.y - oHY : obj.y + oHY;
        const edgeDist = Math.abs(objEdgeY - ghostEdgeY);
        if (edgeDist < 0.01) continue;
        if (obj.y > ghostPos.y) {
          if (!eqClosestBelow || edgeDist < eqClosestBelow.edgeDist)
            eqClosestBelow = { oy: obj.y, oHY, ghostEdgeY, objEdgeY, edgeDist };
        } else {
          if (!eqClosestAbove || edgeDist < eqClosestAbove.edgeDist)
            eqClosestAbove = { oy: obj.y, oHY, ghostEdgeY, objEdgeY, edgeDist };
        }
      }
      if (Math.abs(obj.y - ghostPos.y) < tolerance && Math.abs(obj.x - ghostPos.x) > tolerance) {
        eqAlignedH.push({ x: obj.x, hX: oHX, y: obj.y });
        const ghostEdgeX = ghostPos.x < obj.x ? ghostPos.x + gHX : ghostPos.x - gHX;
        const objEdgeX = ghostPos.x < obj.x ? obj.x - oHX : obj.x + oHX;
        const edgeDist = Math.abs(objEdgeX - ghostEdgeX);
        if (edgeDist < 0.01) continue;
        if (obj.x > ghostPos.x) {
          if (!eqClosestRight || edgeDist < eqClosestRight.edgeDist)
            eqClosestRight = { ox: obj.x, oHX, ghostEdgeX, objEdgeX, edgeDist };
        } else {
          if (!eqClosestLeft || edgeDist < eqClosestLeft.edgeDist)
            eqClosestLeft = { ox: obj.x, oHX, ghostEdgeX, objEdgeX, edgeDist };
        }
      }
    }

    // Vertical chain (same X)
    if (eqAlignedV.length >= 2) {
      eqAlignedV.sort((a, b) => a.y - b.y);
      // Walk upward from closestAbove
      if (eqClosestAbove) {
        const refGap = eqClosestAbove.edgeDist;
        const above = eqAlignedV.filter(o => o.y < ghostPos.y).reverse();
        for (let i = 0; i < above.length - 1; i++) {
          const a = above[i], b = above[i + 1];
          const gap = (a.y - a.hY) - (b.y + b.hY);
          if (gap < 0.01 || Math.abs(gap - refGap) > eqTol) break;
          const ey1 = b.y + b.hY, ey2 = a.y - a.hY;
          this.ctx.strokeStyle = dimColor; this.ctx.lineWidth = 1.5 / scale;
          this.ctx.setLineDash([4 / scale, 3 / scale]);
          this.ctx.beginPath(); this.ctx.moveTo(ghostPos.x, ey1); this.ctx.lineTo(ghostPos.x, ey2); this.ctx.stroke();
          this.ctx.setLineDash([]);
          this.ctx.font = `${fontSize}px Arial`; this.ctx.fillStyle = labelColor;
          this.ctx.textAlign = 'left'; this.ctx.textBaseline = 'middle';
          this.ctx.fillText(this.formatMeasurement(gap), ghostPos.x + eqLblOff, (ey1 + ey2) / 2);
        }
      }
      // Walk downward from closestBelow
      if (eqClosestBelow) {
        const refGap = eqClosestBelow.edgeDist;
        const below = eqAlignedV.filter(o => o.y > ghostPos.y);
        for (let i = 0; i < below.length - 1; i++) {
          const a = below[i], b = below[i + 1];
          const gap = (b.y - b.hY) - (a.y + a.hY);
          if (gap < 0.01 || Math.abs(gap - refGap) > eqTol) break;
          const ey1 = a.y + a.hY, ey2 = b.y - b.hY;
          this.ctx.strokeStyle = dimColor; this.ctx.lineWidth = 1.5 / scale;
          this.ctx.setLineDash([4 / scale, 3 / scale]);
          this.ctx.beginPath(); this.ctx.moveTo(ghostPos.x, ey1); this.ctx.lineTo(ghostPos.x, ey2); this.ctx.stroke();
          this.ctx.setLineDash([]);
          this.ctx.font = `${fontSize}px Arial`; this.ctx.fillStyle = labelColor;
          this.ctx.textAlign = 'left'; this.ctx.textBaseline = 'middle';
          this.ctx.fillText(this.formatMeasurement(gap), ghostPos.x + eqLblOff, (ey1 + ey2) / 2);
        }
      }
    }

    // Horizontal chain (same Y)
    if (eqAlignedH.length >= 2) {
      eqAlignedH.sort((a, b) => a.x - b.x);
      // Walk leftward from closestLeft
      if (eqClosestLeft) {
        const refGap = eqClosestLeft.edgeDist;
        const left = eqAlignedH.filter(o => o.x < ghostPos.x).reverse();
        for (let i = 0; i < left.length - 1; i++) {
          const a = left[i], b = left[i + 1];
          const gap = (a.x - a.hX) - (b.x + b.hX);
          if (gap < 0.01 || Math.abs(gap - refGap) > eqTol) break;
          const ex1 = b.x + b.hX, ex2 = a.x - a.hX;
          this.ctx.strokeStyle = dimColor; this.ctx.lineWidth = 1.5 / scale;
          this.ctx.setLineDash([4 / scale, 3 / scale]);
          this.ctx.beginPath(); this.ctx.moveTo(ex1, ghostPos.y); this.ctx.lineTo(ex2, ghostPos.y); this.ctx.stroke();
          this.ctx.setLineDash([]);
          this.ctx.font = `${fontSize}px Arial`; this.ctx.fillStyle = labelColor;
          this.ctx.textAlign = 'center'; this.ctx.textBaseline = 'bottom';
          this.ctx.fillText(this.formatMeasurement(gap), (ex1 + ex2) / 2, ghostPos.y - eqLblOff);
        }
      }
      // Walk rightward from closestRight
      if (eqClosestRight) {
        const refGap = eqClosestRight.edgeDist;
        const right = eqAlignedH.filter(o => o.x > ghostPos.x);
        for (let i = 0; i < right.length - 1; i++) {
          const a = right[i], b = right[i + 1];
          const gap = (b.x - b.hX) - (a.x + a.hX);
          if (gap < 0.01 || Math.abs(gap - refGap) > eqTol) break;
          const ex1 = a.x + a.hX, ex2 = b.x - b.hX;
          this.ctx.strokeStyle = dimColor; this.ctx.lineWidth = 1.5 / scale;
          this.ctx.setLineDash([4 / scale, 3 / scale]);
          this.ctx.beginPath(); this.ctx.moveTo(ex1, ghostPos.y); this.ctx.lineTo(ex2, ghostPos.y); this.ctx.stroke();
          this.ctx.setLineDash([]);
          this.ctx.font = `${fontSize}px Arial`; this.ctx.fillStyle = labelColor;
          this.ctx.textAlign = 'center'; this.ctx.textBaseline = 'bottom';
          this.ctx.fillText(this.formatMeasurement(gap), (ex1 + ex2) / 2, ghostPos.y - eqLblOff);
        }
      }
    }

    this.ctx.textAlign = 'start';
    this.ctx.textBaseline = 'alphabetic';
  }

  // ── Turf proximity dimensions (all 4 sides to nearest wall/divider) ──

  _drawTurfProximityDims(obj, scale) {
    // Gather walls: visible room walls + interior cutout walls + wall segment groups (including dividing lines)
    const allWalls = [];
    for (const room of this.rooms) {
      if (!room.closed) continue;
      const vis = this.getVisibleRoomWalls(room);
      for (const w of vis) allWalls.push(w);
      const icData = this.getInteriorCutoutWalls(room);
      for (const d of icData) allWalls.push(d.wall);
    }
    for (const group of this.wallSegmentGroups) {
      // Include both walls AND dividing lines (unlike equipment which skips lines)
      for (const seg of group.segments) allWalls.push(seg);
    }

    // Add edges of other platforms and turf as virtual wall segments
    // Skip group members when drawing dims for a group AABB
    const skipSet = obj._groupMembers ? new Set(obj._groupMembers) : null;
    if (this.objects) {
      for (const other of this.objects) {
        if (other === obj) continue;
        if (skipSet && skipSet.has(other)) continue;
        if (other.type !== 'platform' && other.type !== 'turf') continue;
        const oRot = other.rotation || 0;
        const oC = Math.cos(oRot), oS = Math.sin(oRot);
        const oHW = other.width / 2, oHD = other.depth / 2;
        const oCorners = [
          { x: other.x + (-oHW) * oC - (-oHD) * oS, y: other.y + (-oHW) * oS + (-oHD) * oC },
          { x: other.x + ( oHW) * oC - (-oHD) * oS, y: other.y + ( oHW) * oS + (-oHD) * oC },
          { x: other.x + ( oHW) * oC - ( oHD) * oS, y: other.y + ( oHW) * oS + ( oHD) * oC },
          { x: other.x + (-oHW) * oC - ( oHD) * oS, y: other.y + (-oHW) * oS + ( oHD) * oC }
        ];
        for (let i = 0; i < 4; i++) {
          allWalls.push({ start: oCorners[i], end: oCorners[(i + 1) % 4] });
        }
      }
    }

    // Compute turf AABB corners (rotation-aware)
    const rot = obj.rotation || 0;
    const cosR = Math.cos(rot), sinR = Math.sin(rot);
    const hw = obj.width / 2, hd = obj.depth / 2;
    // 4 local-space corners → world
    const corners = [
      { lx: -hw, ly: -hd }, { lx: hw, ly: -hd },
      { lx: hw, ly: hd }, { lx: -hw, ly: hd }
    ].map(c => ({
      x: obj.x + c.lx * cosR - c.ly * sinR,
      y: obj.y + c.lx * sinR + c.ly * cosR
    }));

    // 4 edge midpoints and outward normals
    const edges = [
      { mid: { x: (corners[1].x + corners[2].x) / 2, y: (corners[1].y + corners[2].y) / 2 }, nx: cosR, ny: sinR },       // right
      { mid: { x: (corners[3].x + corners[0].x) / 2, y: (corners[3].y + corners[0].y) / 2 }, nx: -cosR, ny: -sinR },      // left
      { mid: { x: (corners[2].x + corners[3].x) / 2, y: (corners[2].y + corners[3].y) / 2 }, nx: -sinR, ny: cosR },       // bottom
      { mid: { x: (corners[0].x + corners[1].x) / 2, y: (corners[0].y + corners[1].y) / 2 }, nx: sinR, ny: -cosR }        // top
    ];

    // Style
    const dimColor = this.darkMode ? 'rgba(170, 185, 210, 0.7)' : 'rgba(90, 90, 90, 0.6)';
    const labelColor = this.darkMode ? '#a8bcd4' : '#666';
    const fontSize = (11 + Math.min(7, Math.max(0, (scale - 20) / 12))) / scale;
    const maxStub = Math.max(2, 200 / scale);

    for (const edge of edges) {
      // Ray-cast from edge midpoint in outward normal direction against all walls
      let bestDist = Infinity, bestHit = null;
      for (const wall of allWalls) {
        const wx = wall.end.x - wall.start.x, wy = wall.end.y - wall.start.y;
        const denom = edge.nx * wy - edge.ny * wx;
        if (Math.abs(denom) < 1e-9) continue; // parallel
        const dx = wall.start.x - edge.mid.x, dy = wall.start.y - edge.mid.y;
        const t = (dx * wy - dy * wx) / denom; // ray param (distance along normal)
        const u = (dx * edge.ny - dy * edge.nx) / denom; // wall param (0-1)
        if (t < 0.001 || u < 0.001 || u > 0.999) continue; // behind edge or off wall ends
        if (t < bestDist) {
          bestDist = t;
          bestHit = { x: edge.mid.x + edge.nx * t, y: edge.mid.y + edge.ny * t };
        }
      }

      if (!bestHit || bestDist < 0.04) continue; // skip touching/overlapping (< ~half inch)

      // Draw dashed line
      let lineEnd = bestHit;
      if (this._isMobile && bestDist > maxStub) {
        lineEnd = { x: edge.mid.x + edge.nx * maxStub, y: edge.mid.y + edge.ny * maxStub };
      }
      this.ctx.strokeStyle = dimColor;
      this.ctx.lineWidth = 1.5 / scale;
      this.ctx.setLineDash([4 / scale, 3 / scale]);
      this.ctx.beginPath();
      this.ctx.moveTo(edge.mid.x, edge.mid.y);
      this.ctx.lineTo(lineEnd.x, lineEnd.y);
      this.ctx.stroke();
      this.ctx.setLineDash([]);

      // Measurement label at 25% along line, offset perpendicular
      let lx, ly;
      if (this._isMobile) {
        const lblDist = Math.min(bestDist * 0.3, 20 / scale);
        lx = edge.mid.x + edge.nx * lblDist;
        ly = edge.mid.y + edge.ny * lblDist;
      } else {
        lx = edge.mid.x + (bestHit.x - edge.mid.x) * 0.25;
        ly = edge.mid.y + (bestHit.y - edge.mid.y) * 0.25;
      }
      const lblOff = Math.max(this.wallThickness * 2, 16 / scale);
      const offX = Math.abs(edge.nx) < 0.01 ? lblOff : (-edge.ny) * lblOff;
      const offY = Math.abs(edge.ny) < 0.01 ? -lblOff : (edge.nx) * lblOff;
      this.ctx.font = `${fontSize}px Arial`;
      this.ctx.fillStyle = labelColor;
      this.ctx.textAlign = Math.abs(offX) > Math.abs(offY) ? (offX > 0 ? 'left' : 'right') : 'center';
      this.ctx.textBaseline = Math.abs(offY) > Math.abs(offX) ? (offY < 0 ? 'bottom' : 'top') : 'middle';
      this.ctx.fillText(this.formatMeasurement(bestDist), lx + offX, ly + offY);
    }

    this.ctx.textAlign = 'start';
    this.ctx.textBaseline = 'alphabetic';
  }

  // ── Placement preview dimension lines ──

  _drawObjectPlacementDimensions(snapped, scale) {
    // Ghost object half-extents (feet)
    let hw, hd, isCircle = false;
    if (this.objectType === 'rectangle') {
      hw = (this.rectWidth / 12) / 2;
      hd = (this.rectDepth / 12) / 2;
    } else {
      hw = hd = (this.circleDiameter / 12) / 2;
      isCircle = true;
    }

    // Collect all walls from all closed rooms (visible boundary + interior cutout walls)
    // plus dividing walls (not dividing lines)
    const allWalls = [];
    for (const room of this.rooms) {
      if (!room.closed) continue;
      const vis = this.getVisibleRoomWalls(room);
      for (const w of vis) allWalls.push(w);
      const icData = this.getInteriorCutoutWalls(room);
      for (const d of icData) allWalls.push(d.wall);
    }
    for (const group of this.wallSegmentGroups) {
      if (group.type === 'line') continue;
      for (const seg of group.segments) allWalls.push(seg);
    }

    // Find closest perpendicular walls (edge-to-edge distance)
    const candidates = [];
    for (const wall of allWalls) {
      const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
      const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
      if (wLen < 0.01) continue;
      const param = this.getWallParam(snapped, wall);
      if (param < 0.01 || param > 0.99) continue; // projection off-segment
      const proj = this.paramToPoint(wall, param);
      const dx = proj.x - snapped.x, dy = proj.y - snapped.y;
      const centerDist = Math.sqrt(dx * dx + dy * dy);
      if (centerDist < 0.01) continue;
      const nx = dx / centerDist, ny = dy / centerDist;
      // Edge offset: distance from center to object edge along direction to wall
      let edgeOffset;
      if (isCircle) {
        edgeOffset = hw;
      } else {
        const tx = Math.abs(nx) > 0.001 ? hw / Math.abs(nx) : Infinity;
        const ty = Math.abs(ny) > 0.001 ? hd / Math.abs(ny) : Infinity;
        edgeOffset = Math.min(tx, ty);
      }
      const edgeDist = centerDist - edgeOffset;
      if (edgeDist < 0.05 || edgeDist > 30) continue;
      const edgePt = { x: snapped.x + nx * edgeOffset, y: snapped.y + ny * edgeOffset };
      candidates.push({ proj, edgePt, edgeDist, nx, ny });
    }
    // Classify by cardinal direction and pick at most 1 per direction
    for (const c of candidates) {
      const isHoriz = Math.abs(c.nx) >= Math.abs(c.ny);
      if (isHoriz) c._dir = c.nx > 0 ? 'right' : 'left';
      else c._dir = c.ny > 0 ? 'down' : 'up';
    }
    candidates.sort((a, b) => a.edgeDist - b.edgeDist);
    const nearest = [];
    const _wallUsedDirs = new Set();
    for (const c of candidates) {
      if (nearest.length >= 4) break;
      if (_wallUsedDirs.has(c._dir)) continue;
      _wallUsedDirs.add(c._dir);
      nearest.push(c);
    }

    // Style constants
    const dimColor = this.darkMode ? 'rgba(80, 170, 255, 0.75)' : 'rgba(0, 90, 180, 0.65)';
    const labelColor = this.darkMode ? '#50aaff' : '#005ab4';
    const fontSize = (11 + Math.min(7, Math.max(0, (scale - 20) / 12))) / scale;
    const maxStub = Math.max(2, 200 / scale); // mobile: cap line length, min 2 ft
    const lblOff = Math.max(this.wallThickness * 2, 16 / scale); // label perpendicular offset (matches equipment proximity dims)

    // ── Collect aligned object neighbors FIRST (needed to filter wall dims) ──
    const tolerance = 0.005; // ~1/16" — only exact center-to-center alignment
    let closestAbove = null, closestBelow = null; // same-X vertical neighbors
    let closestLeft = null, closestRight = null;  // same-Y horizontal neighbors
    const alignedV = []; // all same-X objects (for equidistant check)
    const alignedH = []; // all same-Y objects (for equidistant check)
    for (const obj of this.objects) {
      if (obj.type !== 'rectangle' && obj.type !== 'circle' && obj.type !== 'equipment') continue;
      if (obj.type === 'equipment' && !this._equipmentMode) continue;
      const ox = obj.x, oy = obj.y;
      let objHW, objHD;
      if (obj.type === 'rectangle' || obj.type === 'equipment' || obj.type === 'turf' || obj.type === 'platform') { objHW = obj.width / 2; objHD = obj.depth / 2; }
      else { objHW = objHD = obj.diameter / 2; }

      if (Math.abs(ox - snapped.x) < tolerance && Math.abs(oy - snapped.y) > tolerance) {
        alignedV.push({ y: oy, hd: objHD });
        const ghostEdgeY = snapped.y < oy ? snapped.y + hd : snapped.y - hd;
        const objEdgeY = snapped.y < oy ? oy - objHD : oy + objHD;
        const edgeDist = Math.abs(objEdgeY - ghostEdgeY);
        if (edgeDist < 0.01) continue;
        if (oy > snapped.y) {
          if (!closestBelow || edgeDist < closestBelow.edgeDist)
            closestBelow = { ox, oy, objHD, ghostEdgeY, objEdgeY, edgeDist };
        } else {
          if (!closestAbove || edgeDist < closestAbove.edgeDist)
            closestAbove = { ox, oy, objHD, ghostEdgeY, objEdgeY, edgeDist };
        }
      }
      if (Math.abs(oy - snapped.y) < tolerance && Math.abs(ox - snapped.x) > tolerance) {
        alignedH.push({ x: ox, hw: objHW });
        const ghostEdgeX = snapped.x < ox ? snapped.x + hw : snapped.x - hw;
        const objEdgeX = snapped.x < ox ? ox - objHW : ox + objHW;
        const edgeDist = Math.abs(objEdgeX - ghostEdgeX);
        if (edgeDist < 0.01) continue;
        if (ox > snapped.x) {
          if (!closestRight || edgeDist < closestRight.edgeDist)
            closestRight = { ox, oy, objHW, ghostEdgeX, objEdgeX, edgeDist };
        } else {
          if (!closestLeft || edgeDist < closestLeft.edgeDist)
            closestLeft = { ox, oy, objHW, ghostEdgeX, objEdgeX, edgeDist };
        }
      }
    }

    // ── Draw dimension lines to nearest perpendicular walls ──
    // Skip a wall direction if an aligned object on the same axis sits between
    // ghost and wall — the object-to-object measurement already covers it.
    // Track which directions are drawn to prevent object-neighbor duplicates.
    const drawnDirs = new Set();
    for (const c of nearest) {
      let blocked = false;
      if (Math.abs(c.ny) >= Math.abs(c.nx)) {
        // Wall is primarily above or below — check vertical aligned neighbors
        if (c.ny < 0 && closestAbove && closestAbove.edgeDist < c.edgeDist) blocked = true;
        if (c.ny > 0 && closestBelow && closestBelow.edgeDist < c.edgeDist) blocked = true;
      } else {
        // Wall is primarily left or right — check horizontal aligned neighbors
        if (c.nx < 0 && closestLeft && closestLeft.edgeDist < c.edgeDist) blocked = true;
        if (c.nx > 0 && closestRight && closestRight.edgeDist < c.edgeDist) blocked = true;
      }
      if (blocked) continue;
      drawnDirs.add(c._dir);

      let lineEnd;
      if (this._isMobile && c.edgeDist > maxStub) {
        lineEnd = { x: c.edgePt.x + c.nx * maxStub, y: c.edgePt.y + c.ny * maxStub };
      } else {
        lineEnd = c.proj;
      }
      this.ctx.strokeStyle = dimColor;
      this.ctx.lineWidth = 1.5 / scale;
      this.ctx.setLineDash([2 / scale, 2 / scale]);
      this.ctx.beginPath();
      this.ctx.moveTo(c.edgePt.x, c.edgePt.y);
      this.ctx.lineTo(lineEnd.x, lineEnd.y);
      this.ctx.stroke();
      this.ctx.setLineDash([]);
      let lx, ly;
      if (this._isMobile) {
        const lblDist = Math.min(c.edgeDist * 0.3, 20 / scale);
        lx = c.edgePt.x + c.nx * lblDist;
        ly = c.edgePt.y + c.ny * lblDist;
      } else {
        lx = c.edgePt.x + (c.proj.x - c.edgePt.x) * 0.25;
        ly = c.edgePt.y + (c.proj.y - c.edgePt.y) * 0.25;
      }
      // Axis-aligned walls: fixed label side (above for horizontal, right for vertical)
      // so labels stay consistent with neighbor/equidistant labels.
      // Diagonal walls: perpendicular offset based on line direction.
      const offX = Math.abs(c.nx) < 0.01 ? lblOff : (-c.ny) * lblOff;
      const offY = Math.abs(c.ny) < 0.01 ? -lblOff : (c.nx) * lblOff;
      this.ctx.font = `${fontSize}px Arial`;
      this.ctx.fillStyle = labelColor;
      this.ctx.textAlign = Math.abs(offX) > Math.abs(offY) ? (offX > 0 ? 'left' : 'right') : 'center';
      this.ctx.textBaseline = Math.abs(offY) > Math.abs(offX) ? (offY < 0 ? 'bottom' : 'top') : 'middle';
      this.ctx.fillText(this.formatMeasurement(c.edgeDist), lx + offX, ly + offY);
    }
    // Draw the immediate vertical neighbors (at most 1 above + 1 below)
    // Skip if that direction was already drawn by a wall line
    for (const c of [closestAbove, closestBelow]) {
      if (!c) continue;
      const vDir = c.oy > snapped.y ? 'down' : 'up';
      if (drawnDirs.has(vDir)) continue;
      drawnDirs.add(vDir);
      const dir = c.objEdgeY > c.ghostEdgeY ? 1 : -1;
      const drawEndY = (this._isMobile && c.edgeDist > maxStub) ? c.ghostEdgeY + dir * maxStub : c.objEdgeY;
      this.ctx.strokeStyle = dimColor;
      this.ctx.lineWidth = 1.5 / scale;
      this.ctx.setLineDash([2 / scale, 2 / scale]);
      this.ctx.beginPath();
      this.ctx.moveTo(snapped.x, c.ghostEdgeY);
      this.ctx.lineTo(snapped.x, drawEndY);
      this.ctx.stroke();
      this.ctx.setLineDash([]);
      const labelY = this._isMobile ? c.ghostEdgeY + dir * Math.min(c.edgeDist * 0.3, 20 / scale) : (c.ghostEdgeY + c.objEdgeY) / 2;
      this.ctx.font = `${fontSize}px Arial`;
      this.ctx.fillStyle = labelColor;
      this.ctx.textAlign = 'left';
      this.ctx.textBaseline = 'middle';
      this.ctx.fillText(this.formatMeasurement(c.edgeDist), snapped.x + lblOff, labelY);
    }
    // Draw the immediate horizontal neighbors (at most 1 left + 1 right)
    // Skip if that direction was already drawn by a wall line
    for (const c of [closestLeft, closestRight]) {
      if (!c) continue;
      const hDir = c.ox > snapped.x ? 'right' : 'left';
      if (drawnDirs.has(hDir)) continue;
      drawnDirs.add(hDir);
      const dir = c.objEdgeX > c.ghostEdgeX ? 1 : -1;
      const drawEndX = (this._isMobile && c.edgeDist > maxStub) ? c.ghostEdgeX + dir * maxStub : c.objEdgeX;
      this.ctx.strokeStyle = dimColor;
      this.ctx.lineWidth = 1.5 / scale;
      this.ctx.setLineDash([2 / scale, 2 / scale]);
      this.ctx.beginPath();
      this.ctx.moveTo(c.ghostEdgeX, snapped.y);
      this.ctx.lineTo(drawEndX, snapped.y);
      this.ctx.stroke();
      this.ctx.setLineDash([]);
      const labelX = this._isMobile ? c.ghostEdgeX + dir * Math.min(c.edgeDist * 0.3, 20 / scale) : (c.ghostEdgeX + c.objEdgeX) / 2;
      this.ctx.font = `${fontSize}px Arial`;
      this.ctx.fillStyle = labelColor;
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'bottom';
      this.ctx.fillText(this.formatMeasurement(c.edgeDist), labelX, snapped.y - lblOff);
    }

    // ── Equidistant pattern: highlight matching gaps between existing objects ──
    // When ghost→nearest gap matches the gap between existing consecutive objects,
    // draw those gaps so the user can confirm equal spacing in the chain.
    const eqTol = 0.01; // ~1/8" tolerance for gap matching

    // Vertical chain (same X)
    if (alignedV.length >= 2) {
      alignedV.sort((a, b) => a.y - b.y);
      // Walk upward from closestAbove (objects above ghost, closest first)
      if (closestAbove) {
        const refGap = closestAbove.edgeDist;
        const above = alignedV.filter(o => o.y < snapped.y).reverse();
        for (let i = 0; i < above.length - 1; i++) {
          const a = above[i], b = above[i + 1]; // a.y > b.y
          const gap = (a.y - a.hd) - (b.y + b.hd);
          if (gap < 0.01 || Math.abs(gap - refGap) > eqTol) break;
          const ey1 = b.y + b.hd, ey2 = a.y - a.hd;
          this.ctx.strokeStyle = dimColor; this.ctx.lineWidth = 1.5 / scale;
          this.ctx.setLineDash([2 / scale, 2 / scale]);
          this.ctx.beginPath(); this.ctx.moveTo(snapped.x, ey1); this.ctx.lineTo(snapped.x, ey2); this.ctx.stroke();
          this.ctx.setLineDash([]);
          this.ctx.font = `${fontSize}px Arial`; this.ctx.fillStyle = labelColor;
          this.ctx.textAlign = 'left'; this.ctx.textBaseline = 'middle';
          this.ctx.fillText(this.formatMeasurement(gap), snapped.x + lblOff, (ey1 + ey2) / 2);
        }
      }
      // Walk downward from closestBelow (objects below ghost, closest first)
      if (closestBelow) {
        const refGap = closestBelow.edgeDist;
        const below = alignedV.filter(o => o.y > snapped.y);
        for (let i = 0; i < below.length - 1; i++) {
          const a = below[i], b = below[i + 1]; // a.y < b.y
          const gap = (b.y - b.hd) - (a.y + a.hd);
          if (gap < 0.01 || Math.abs(gap - refGap) > eqTol) break;
          const ey1 = a.y + a.hd, ey2 = b.y - b.hd;
          this.ctx.strokeStyle = dimColor; this.ctx.lineWidth = 1.5 / scale;
          this.ctx.setLineDash([2 / scale, 2 / scale]);
          this.ctx.beginPath(); this.ctx.moveTo(snapped.x, ey1); this.ctx.lineTo(snapped.x, ey2); this.ctx.stroke();
          this.ctx.setLineDash([]);
          this.ctx.font = `${fontSize}px Arial`; this.ctx.fillStyle = labelColor;
          this.ctx.textAlign = 'left'; this.ctx.textBaseline = 'middle';
          this.ctx.fillText(this.formatMeasurement(gap), snapped.x + lblOff, (ey1 + ey2) / 2);
        }
      }
    }

    // Horizontal chain (same Y)
    if (alignedH.length >= 2) {
      alignedH.sort((a, b) => a.x - b.x);
      // Walk leftward from closestLeft (objects left of ghost, closest first)
      if (closestLeft) {
        const refGap = closestLeft.edgeDist;
        const left = alignedH.filter(o => o.x < snapped.x).reverse();
        for (let i = 0; i < left.length - 1; i++) {
          const a = left[i], b = left[i + 1]; // a.x > b.x
          const gap = (a.x - a.hw) - (b.x + b.hw);
          if (gap < 0.01 || Math.abs(gap - refGap) > eqTol) break;
          const ex1 = b.x + b.hw, ex2 = a.x - a.hw;
          this.ctx.strokeStyle = dimColor; this.ctx.lineWidth = 1.5 / scale;
          this.ctx.setLineDash([2 / scale, 2 / scale]);
          this.ctx.beginPath(); this.ctx.moveTo(ex1, snapped.y); this.ctx.lineTo(ex2, snapped.y); this.ctx.stroke();
          this.ctx.setLineDash([]);
          this.ctx.font = `${fontSize}px Arial`; this.ctx.fillStyle = labelColor;
          this.ctx.textAlign = 'center'; this.ctx.textBaseline = 'bottom';
          this.ctx.fillText(this.formatMeasurement(gap), (ex1 + ex2) / 2, snapped.y - lblOff);
        }
      }
      // Walk rightward from closestRight (objects right of ghost, closest first)
      if (closestRight) {
        const refGap = closestRight.edgeDist;
        const right = alignedH.filter(o => o.x > snapped.x);
        for (let i = 0; i < right.length - 1; i++) {
          const a = right[i], b = right[i + 1]; // a.x < b.x
          const gap = (b.x - b.hw) - (a.x + a.hw);
          if (gap < 0.01 || Math.abs(gap - refGap) > eqTol) break;
          const ex1 = a.x + a.hw, ex2 = b.x - b.hw;
          this.ctx.strokeStyle = dimColor; this.ctx.lineWidth = 1.5 / scale;
          this.ctx.setLineDash([2 / scale, 2 / scale]);
          this.ctx.beginPath(); this.ctx.moveTo(ex1, snapped.y); this.ctx.lineTo(ex2, snapped.y); this.ctx.stroke();
          this.ctx.setLineDash([]);
          this.ctx.font = `${fontSize}px Arial`; this.ctx.fillStyle = labelColor;
          this.ctx.textAlign = 'center'; this.ctx.textBaseline = 'bottom';
          this.ctx.fillText(this.formatMeasurement(gap), (ex1 + ex2) / 2, snapped.y - lblOff);
        }
      }
    }

    this.ctx.textAlign = 'start';
    this.ctx.textBaseline = 'alphabetic';
  }
  /* ──────────────────────────────────────────
     DOOR DRAWING
     ────────────────────────────────────────── */
  /* ──────────────────────────────────────────
     HANDLE HELPERS (Resize / Rotate)
     ────────────────────────────────────────── */

  _rotatePoint(px, py, cx, cy, angle) { return RoomDesignerMath._rotatePoint(px, py, cx, cy, angle); }

  _currentGridStep() { return RoomDesignerTools.currentGridStep(this.basePixelsPerFoot, this.zoomLevel, this._isMetric()); }

  _getResizeHandles(obj) { return RoomDesignerTools.getResizeHandles(obj, this._isMobile); }

  _getRotateHandle(obj, mouseWorld) {
    // A label's stored rotation is a SCREEN angle (_labelWorldAngle); the formula wants world.
    const o = (obj && obj.type === 'label' && this._canvasRotationRad) ? Object.assign({}, obj, { rotation: this._labelWorldAngle(obj) }) : obj;
    return RoomDesignerTools.getRotateHandle(o, this.basePixelsPerFoot * this.zoomLevel, mouseWorld);
  }

  _getOppositeHandle(name) { return RoomDesignerTools.getOppositeHandle(name); }
  /* ──────────────────────────────────────────
     WALL LABELS
     ────────────────────────────────────────── */
  /** Test whether `angle` lies within the arc from `start` to `end`.
   *  `ccw` true means the arc sweeps counter-clockwise (canvas convention). */
  _angleInArc(angle, start, end, ccw) { return RoomDesignerMath._angleInArc(angle, start, end, ccw); }
  _arcAABB(cx, cy, radius, startAngle, endAngle, ccw) { return RoomDesignerMath._arcAABB(cx, cy, radius, startAngle, endAngle, ccw); }

  /** Return array of AABBs for every door swing arc across all rooms. */
  closestPointOnWall(point, wall) { return RoomDesignerMath.closestPointOnWall(point, wall); }
  /* ── POPOVER IDLE PULSE ──
     When a pop-out menu is open on mobile and no screen activity for 4s,
     pulse the parent button to prompt the user to interact.
     Exceptions for draw/object menus: if only the top row is selected
     but the second row hasn't been picked, pulse the 2nd-row options instead. */
  /* ── NUDGE SELECTED ITEMS ── */

  _nudgeSelection(dir) {
    // User is interacting with joystick — restart appropriate pulse timer for next pause
    this._restartJoystickPulse();
    const step = this._currentGridStep(); // 1 inch or 1 foot
    let dx = 0, dy = 0;
    if (dir === 'up') dy = -step;
    else if (dir === 'down') dy = step;
    else if (dir === 'left') dx = -step;
    else if (dir === 'right') dx = step;

    // Rectangle mode: nudge the active corner
    if (this.activeTool === 'pen' && this.penDrawMode === 'rectangle') {
      if (this._rectCorner2) {
        this._rectCorner2.x += dx;
        this._rectCorner2.y += dy;
        this._rectCorner2 = this.snapToGrid(this._rectCorner2.x, this._rectCorner2.y);
        this.mouseWorld = { x: this._rectCorner2.x, y: this._rectCorner2.y };
        this._updateRectCheckmark();
        this._mobilePanCompensate(dx, dy);
        this.render();
        return;
      }
      if (this._rectCorner1 && !this._rectCorner1Confirmed) {
        this._rectCorner1.x += dx;
        this._rectCorner1.y += dy;
        this._rectCorner1 = this.snapToGrid(this._rectCorner1.x, this._rectCorner1.y);
        this.mouseWorld = { x: this._rectCorner1.x, y: this._rectCorner1.y };
        this._mobilePanCompensate(dx, dy);
        this.render();
        return;
      }
    }

    // Freehand Phase 1: reposition both start and end together
    if (this.activeTool === 'pen' && this._freehandAutoPlaced && !this._freehandStartConfirmed && this.currentWall) {
      this.currentWall.start.x += dx; this.currentWall.start.y += dy;
      this.currentWall.start = this.snapToGrid(this.currentWall.start.x, this.currentWall.start.y);
      this.currentWall.end = { x: this.currentWall.start.x, y: this.currentWall.start.y };
      this.mouseWorld = { x: this.currentWall.start.x, y: this.currentWall.start.y };
      this._mobilePanCompensate(dx, dy);
      this.render();
      return;
    }

    // Pen tool: nudge the current wall endpoint
    if (this.activeTool === 'pen' && this.currentWall && this.hasPlacedFirstPoint) {
      this.currentWall.end.x += dx;
      this.currentWall.end.y += dy;
      this.currentWall.end = this.snapToGrid(this.currentWall.end.x, this.currentWall.end.y);
      this.mouseWorld = { x: this.currentWall.end.x, y: this.currentWall.end.y };
      this._updateFreehandCheckmark();
      this._mobilePanCompensate(dx, dy);
      this.render();
      return;
    }

    // Door tool on mobile: wall-following movement
    if (this.activeTool === 'door' && this._doorMobile) {
      const dm = this._doorMobile;
      if (dm.phase === 1) {
        // Phase 1: move along wall
        const delta = this._arrowToWallDelta(dir, dm.wall);
        this._moveDoorAlongWall(delta);
        return;
      }
      if (dm.phase === 2) {
        // Phase 2: arrow selects which end is the hinge (+1 = toward wall.end, -1 = toward wall.start)
        const delta = this._arrowToWallDelta(dir, dm.wall);
        dm.endDir = delta;
        const wallLen = this.getWallLength(dm.wall);
        const doorWidthFeet = this.doorWidth / 12;
        const halfParam = wallLen > 0 ? (doorWidthFeet / 2) / wallLen : 0.5;
        const leftParam = dm.param - halfParam;
        const rightParam = dm.param + halfParam;
        // Update doorPlacement to show correct hinge highlight
        if (this.doorPlacement) {
          if (delta === 1) {
            this.doorPlacement.hingeParam = rightParam;
            this.doorPlacement.hingePoint = this.paramToPoint(dm.wall, rightParam);
            this.doorPlacement.endParam = leftParam;
          } else {
            this.doorPlacement.hingeParam = leftParam;
            this.doorPlacement.hingePoint = this.paramToPoint(dm.wall, leftParam);
            this.doorPlacement.endParam = rightParam;
          }
        }
        // Un-gray checkmark (always valid — door span already fits)
        const ph2PlaceBtn = document.querySelector('.ring-center[data-dir="place"]');
        if (ph2PlaceBtn) ph2PlaceBtn.classList.remove('inactive');
        this.render();
        return;
      }
      if (dm.phase === 3) {
        // Phase 3: arrow selects swing direction
        this._doorMobileCycleSwing(dir);
        this._updateDoorSwingArrows();
        this._clearArrowPulse();
        this._startArrowPulse(1500);
        this.render();
        return;
      }
      return;
    }
    // Door/objects tool: nudge the mouse position for placement preview (desktop / rect+circle on mobile)
    if (this.activeTool === 'door' && this.mouseWorldRaw) {
      this.mouseWorldRaw.x += dx;
      this.mouseWorldRaw.y += dy;
      const snapped = this.snapToGrid(this.mouseWorldRaw.x, this.mouseWorldRaw.y);
      this.mouseWorld = snapped;
      this._mobilePanCompensate(dx, dy);
      this.render();
      return;
    }

    // Move selected objects
    this.selectedObjects.forEach(obj => {
      obj.x += dx;
      obj.y += dy;
    });
    // Block nudge if any equipment now overlaps a wall
    let _nudgeBlocked = false;
    this.selectedObjects.forEach(obj => {
      if (_nudgeBlocked) return;
      if (obj.type === 'equipment') {
        const tw = obj.tightWidth || obj.width, td = obj.tightDepth || obj.depth;
        const info = this._equipmentImages && this._equipmentImages[obj.equipmentId];
        if (this._equipmentOverlapsWall(obj.x, obj.y, tw, td, obj.rotation || 0, info || null)) _nudgeBlocked = true;
      } else if (obj.type === 'turf' || obj.type === 'platform') {
        if (this._equipmentOverlapsWall(obj.x, obj.y, obj.width, obj.depth, obj.rotation || 0, null) || this._turfOverlapsTurf(obj.x, obj.y, obj.width, obj.depth, obj.rotation || 0, obj)) _nudgeBlocked = true;
        if (!_nudgeBlocked && obj.type === 'platform' && this._platformOverlapsPlatform(obj.x, obj.y, obj.width, obj.depth, obj.rotation || 0, obj)) _nudgeBlocked = true;
      }
    });
    if (_nudgeBlocked) {
      this.selectedObjects.forEach(obj => { obj.x -= dx; obj.y -= dy; });
      this._equipmentNudgeBlocked = true;
      clearTimeout(this._equipmentNudgeBlockedTimer);
      this._equipmentNudgeBlockedTimer = setTimeout(() => { this._equipmentNudgeBlocked = false; this.render(); }, 300);
      this.updateStatus('Cannot move — overlaps a wall, turf, or platform', { error: true });
      this.render(); return;
    }

    // Move selected doors along their wall (with corner snaking)
    this.selectedDoors.forEach(door => {
      const wall = door.wall;
      const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
      const lenSq = wdx * wdx + wdy * wdy;
      if (lenSq < 0.0001) return;
      const wallLen = Math.sqrt(lenSq);
      const wallDirX = wdx / wallLen, wallDirY = wdy / wallLen;
      const proj = dx * wallDirX + dy * wallDirY;
      if (Math.abs(proj) < 1e-6) return;
      const paramShift = proj / wallLen;
      let newHinge = door.hingeParam + paramShift;
      let newEnd = door.endParam + paramShift;
      const lo = Math.min(newHinge, newEnd), hi = Math.max(newHinge, newEnd);
      if (lo >= 0 && hi <= 1) {
        // Still on same wall
        const room = this._findRoomForWall(wall);
        if (room && this._doorsOverlap(wall, room, lo, hi, door)) return;
        door.hingeParam = newHinge;
        door.endParam = newEnd;
      } else {
        // Past wall boundary — try to snake to next wall
        const direction = paramShift > 0 ? 1 : -1;
        const whichEnd = direction > 0 ? 'end' : 'start';
        const room = this._findRoomForWall(wall);
        const nextWall = room ? this._findNextWallAtCorner(wall, room, whichEnd) : null;
        if (!nextWall) {
          // Dead end — clamp
          if (lo < 0) { newHinge -= lo; newEnd -= lo; }
          if (hi > 1) { const over = hi - 1; newHinge -= over; newEnd -= over; }
          const cLo = Math.min(newHinge, newEnd), cHi = Math.max(newHinge, newEnd);
          if (room && this._doorsOverlap(wall, room, cLo, cHi, door)) return;
          door.hingeParam = newHinge;
          door.endParam = newEnd;
          return;
        }
        // Transition to next wall
        const doorWidthFeet = door.width / 12;
        const nextWallLen = this.getWallLength(nextWall);
        if (nextWallLen < doorWidthFeet - 0.01) return; // door doesn't fit
        const cornerPt = whichEnd === 'end' ? wall.end : wall.start;
        const pkey = (p) => `${(Math.round(p.x * 1200) / 1200).toFixed(4)},${(Math.round(p.y * 1200) / 1200).toFixed(4)}`;
        const ck = pkey(cornerPt);
        const enteredFromStart = pkey(nextWall.start) === ck;
        const overshoot = direction > 0 ? (hi - 1) : (-lo);
        const halfParam = nextWallLen > 0 ? (doorWidthFeet / 2) / nextWallLen : 0;
        const overshootParam = nextWallLen > 0 ? (overshoot * wallLen) / nextWallLen : 0;
        let centerParam = enteredFromStart ? (halfParam + overshootParam) : ((1 - halfParam) - overshootParam);
        centerParam = Math.max(halfParam, Math.min(1 - halfParam, centerParam));
        const hingeFirst = door.hingeParam <= door.endParam;
        const newParamWidth = nextWallLen > 0 ? doorWidthFeet / nextWallLen : 0;
        let nHinge, nEnd;
        if (hingeFirst) { nHinge = centerParam - newParamWidth / 2; nEnd = centerParam + newParamWidth / 2; }
        else { nEnd = centerParam - newParamWidth / 2; nHinge = centerParam + newParamWidth / 2; }
        // Clamp to [0, 1]
        const nLo = Math.min(nHinge, nEnd), nHi = Math.max(nHinge, nEnd);
        if (nLo < 0) { nHinge -= nLo; nEnd -= nLo; }
        const nHi2 = Math.max(nHinge, nEnd);
        if (nHi2 > 1) { const ov = nHi2 - 1; nHinge -= ov; nEnd -= ov; }
        // Overlap check on new wall
        const newRoom = this._findRoomForWall(nextWall);
        if (newRoom && this._doorsOverlap(nextWall, newRoom, Math.min(nHinge, nEnd), Math.max(nHinge, nEnd), door)) return;
        // Move door from old room to new
        if (room) { const idx = room.doors.indexOf(door); if (idx !== -1) room.doors.splice(idx, 1); }
        door.wall = nextWall;
        door.hingeParam = nHinge;
        door.endParam = nEnd;
        if (newRoom && !newRoom.doors.includes(door)) newRoom.doors.push(door);
        // Update joystick arrows for new wall direction
        if (this._isMobileToolbar && this.selectedDoors.size === 1 && this._selectHandleMode === 'wholeMove') {
          this._setJoystickArrowsDirs(this._getDoorWallArrows(nextWall));
        }
      }
    });

    // Move selected walls (translate endpoints)
    this.selectedWalls.forEach(wall => {
      wall.start.x += dx;
      wall.start.y += dy;
      wall.end.x += dx;
      wall.end.y += dy;
    });

    this._mobilePanCompensate(dx, dy);
    // Show proximity dims briefly after nudge
    if (this.selectedObjects.size > 0) {
      this._equipmentNudgeShowDims = true;
      clearTimeout(this._equipmentNudgeDimsTimer);
      this._equipmentNudgeDimsTimer = setTimeout(() => { this._equipmentNudgeShowDims = false; this.render(); }, 1000);
    }
    this.updateMeasurements();
    this.render();
  }

  /* ── TURF DRAW ── */

  /* CUSTOM_PLANNER_BY_CLAUDE — On drop, snap turf to wall edge instead of full revert */
  _snapTurfToWallOnDrop() {
    // Binary search along the drag vector: find the furthest valid position
    // between the original (known valid) and the dropped (invalid) position.
    this.selectedObjects.forEach(obj => {
      if (obj.type !== 'turf') return;
      const orig = this._selectDragOrigPositions.get(obj);
      if (!orig) return;
      const dropX = obj.x, dropY = obj.y;
      // If original == drop, nothing to do
      if (Math.abs(dropX - orig.x) < 0.001 && Math.abs(dropY - orig.y) < 0.001) return;
      // Binary search: t=0 is original (valid), t=1 is dropped (invalid)
      let lo = 0, hi = 1;
      for (let i = 0; i < 20; i++) {
        const mid = (lo + hi) / 2;
        const tx = orig.x + (dropX - orig.x) * mid;
        const ty = orig.y + (dropY - orig.y) * mid;
        if (this._equipmentOverlapsWall(tx, ty, obj.width, obj.depth, obj.rotation || 0, null) || this._turfOverlapsTurf(tx, ty, obj.width, obj.depth, obj.rotation || 0, obj)) {
          hi = mid;
        } else {
          lo = mid;
        }
      }
      // Use the last valid position (lo)
      obj.x = orig.x + (dropX - orig.x) * lo;
      obj.y = orig.y + (dropY - orig.y) * lo;
    });
    this._equipmentDragBlocked = false;
    this._turfDragBlockedByTurf = false;
    this.updateStatus('Turf snapped — overlapped a wall or turf.');
  }

  /* CUSTOM_PLANNER_BY_CLAUDE — Snap turf corner to nearest wall inner edge */
  _snapTurfToWallEdge(point) {
    const snapThreshold = 0.5; // 6 inches
    const segHT = this.wallSegmentThickness / 2;
    // Collect wall segments with type flag
    // Room walls: path IS the inner edge (wall extends outward), so snap to path directly
    // Wall segments: centered on path, so snap to ±segHT face edges
    const walls = [];
    for (const r of this.rooms) {
      if (!r.closed) continue;
      for (const w of r.walls) walls.push({ start: w.start, end: w.end, isRoom: true });
    }
    if (this.currentRoom && this.currentRoom.closed) {
      for (const w of this.currentRoom.walls) walls.push({ start: w.start, end: w.end, isRoom: true });
    }
    for (const g of this.wallSegmentGroups) {
      if ((g.type || 'wall') === 'line') continue;
      for (const s of g.segments) walls.push({ start: s.start, end: s.end, isRoom: false });
    }
    if (this.currentWallSegmentGroup && (this._wallSegmentType !== 'line')) {
      for (const s of this.currentWallSegmentGroup.segments) walls.push({ start: s.start, end: s.end, isRoom: false });
    }
    let bestDist = snapThreshold;
    let bestPoint = null;
    // Helper: closest point on segment to point, return distance and closest
    const closestOnSeg = (s, e, p) => {
      const sdx = e.x - s.x, sdy = e.y - s.y;
      const sLen2 = sdx * sdx + sdy * sdy;
      if (sLen2 < 0.0001) return { dist: Math.sqrt((p.x - s.x) ** 2 + (p.y - s.y) ** 2), pt: { x: s.x, y: s.y } };
      let t = ((p.x - s.x) * sdx + (p.y - s.y) * sdy) / sLen2;
      t = Math.max(0, Math.min(1, t));
      const closest = { x: s.x + sdx * t, y: s.y + sdy * t };
      return { dist: Math.sqrt((p.x - closest.x) ** 2 + (p.y - closest.y) ** 2), pt: closest };
    };
    for (const wall of walls) {
      const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
      const len = Math.sqrt(wdx * wdx + wdy * wdy);
      if (len < 0.01) continue;
      if (wall.isRoom) {
        // Room wall: path IS the inner edge — snap directly to it
        const r = closestOnSeg(wall.start, wall.end, point);
        if (r.dist < bestDist) { bestDist = r.dist; bestPoint = r.pt; }
      } else {
        // Wall segment: centered, compute ±segHT face edges and snap to closer one
        const nx = -wdy / len * segHT, ny = wdx / len * segHT;
        const faces = [
          { s: { x: wall.start.x + nx, y: wall.start.y + ny }, e: { x: wall.end.x + nx, y: wall.end.y + ny } },
          { s: { x: wall.start.x - nx, y: wall.start.y - ny }, e: { x: wall.end.x - nx, y: wall.end.y - ny } }
        ];
        for (const face of faces) {
          const r = closestOnSeg(face.s, face.e, point);
          if (r.dist < bestDist) { bestDist = r.dist; bestPoint = r.pt; }
        }
      }
    }
    return bestPoint || point;
  }

  /* CUSTOM_PLANNER_BY_CLAUDE — Clamp turf AABB to room polygon boundary */
  _clampTurfToRoom(minX, minY, maxX, maxY) {
    const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
    // Find room containing the rectangle center
    let targetRoom = null, targetPoly = null;
    for (const room of this.rooms) {
      if (!room.closed) continue;
      const geo = this._getRoomGeoCache(room);
      const poly = geo.roomPolygon;
      if (poly && poly.length >= 3 && this.pointInPolygon(cx, cy, poly)) {
        targetRoom = room; targetPoly = poly; break;
      }
    }
    if (this.currentRoom && this.currentRoom.closed && !targetRoom) {
      const geo = this._getRoomGeoCache(this.currentRoom);
      const poly = geo.roomPolygon;
      if (poly && poly.length >= 3 && this.pointInPolygon(cx, cy, poly)) {
        targetRoom = this.currentRoom; targetPoly = poly;
      }
    }
    if (!targetRoom) return null;
    // For each of the 4 axis directions, cast a ray from center and find
    // where it exits the room polygon (first segment intersection with t > 0).
    // Then clamp the rect edge to that exit point.
    const rayExitDist = (dirX, dirY) => {
      let best = Infinity;
      for (let i = 0; i < targetPoly.length; i++) {
        const a = targetPoly[i], b = targetPoly[(i + 1) % targetPoly.length];
        const edx = b.x - a.x, edy = b.y - a.y;
        const denom = dirX * edy - dirY * edx;
        if (Math.abs(denom) < 1e-10) continue; // parallel
        const t = ((a.x - cx) * edy - (a.y - cy) * edx) / denom;
        const s = ((a.x - cx) * dirY - (a.y - cy) * dirX) / denom;
        if (t > 0.001 && s >= -0.001 && s <= 1.001) {
          if (t < best) best = t;
        }
      }
      return best;
    };
    const leftDist  = rayExitDist(-1,  0);
    const rightDist = rayExitDist( 1,  0);
    const topDist   = rayExitDist( 0, -1);
    const botDist   = rayExitDist( 0,  1);
    return {
      minX: leftDist  < Infinity ? Math.max(minX, cx - leftDist)  : minX,
      minY: topDist   < Infinity ? Math.max(minY, cy - topDist)   : minY,
      maxX: rightDist < Infinity ? Math.min(maxX, cx + rightDist) : maxX,
      maxY: botDist   < Infinity ? Math.min(maxY, cy + botDist)   : maxY,
      room: targetRoom
    };
  }

  /* CUSTOM_PLANNER_BY_CLAUDE — Find room polygon containing a point (for turf split preview) */
  _findRoomPolyContaining(px, py) {
    for (const room of this.rooms) {
      if (!room.closed) continue;
      const geo = this._getRoomGeoCache(room);
      const poly = geo.roomPolygon;
      if (poly && poly.length >= 3 && this.pointInPolygon(px, py, poly)) return poly;
    }
    if (this.currentRoom && this.currentRoom.closed) {
      const geo = this._getRoomGeoCache(this.currentRoom);
      const poly = geo.roomPolygon;
      if (poly && poly.length >= 3 && this.pointInPolygon(px, py, poly)) return poly;
    }
    return null;
  }

  /* CUSTOM_PLANNER_BY_CLAUDE — Custom Turf two-click draw interaction */
  /* ── CUSTOM_ROOMDESIGNER_BY_CLAUDE — turf that FILLS a zone ──────────────────
     Turf is a rotated rectangle everywhere: hit-testing, the resize/rotate handles,
     logo placement and every marking in _drawTurfMarkingsInner work in rect-local
     space. Rather than replace that, a zone-filled piece keeps the rectangle as its
     MARKING FRAME and carries a clip: the rectangle is the zone's minimum-area box,
     and drawing clips to the zone outline.

     That is what makes hash lines work on an irregular shape without touching the
     markings code at all — lines are still generated across the full rectangle and
     the clip trims them into the bowed ends. The wall it dies into can be any shape.

     The zone is stored by CENTROID, the same identity key zone flooring already uses
     (_setZoneFlooring matches the nearest centroid within 2ft), so the fill follows
     the zone when a wall is later moved or bowed rather than going stale. */

  /** The room + zone under a point, or null. */
  _zoneAtPoint(point) {
    const ov = window.roomDesignerOverlay;
    if (!ov || !ov._getZonesForRoom) return null;
    for (const room of this.rooms) {
      if (!room.closed || room.type !== 'add') continue;
      const geo = this._getRoomGeoCache(room);
      if (!geo.roomPolygon || geo.roomPolygon.length < 3) continue;
      if (!this.pointInPolygon(point.x, point.y, geo.roomPolygon)) continue;
      const zones = ov._getZonesForRoom(room, geo) || [];
      for (const z of zones) {
        if (!z.polygon || z.polygon.length < 3) continue;
        if (!this.pointInPolygon(point.x, point.y, z.polygon)) continue;
        if (z.holes && z.holes.some(h => this.pointInPolygon(point.x, point.y, h))) continue;
        return { room, zone: z };
      }
    }
    return null;
  }

  /** Live outline of the zone a turf piece fills, re-resolved so edits carry it. */
  /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — turf carries on into a doorway.
     ⚠️ The reason two earlier attempts at this did nothing is worth stating, because
     it is the same trap either way: the white strip at a door is NOT turf being
     covered up — it is turf that was never painted there at all. A zone-filled piece
     clips to its ZONE, and a zone's boundary is the wall CENTRELINE, so the turf
     stops dead at the middle of the band. Un-erasing more of the opening (widening
     the gap clear, widening the snapshot restore) reveals nothing, because there is
     nothing underneath to reveal.

     So the clip itself has to reach further, and only across the openings. Each door
     on a wall bounding this zone contributes a "lip": a rectangle sitting on the wall
     line and running OUTWARD to the middle of the door frame — a quarter of the wall
     thickness, which is halfway across the visible white strip.

     ⚠️ The lips live entirely OUTSIDE the zone polygon, sharing only its boundary
     edge. That is what makes them safe to add to the same even-odd path: an overlap
     would XOR into a hole and punch the turf out at every doorway — the exact
     opposite of the request. */
  _turfDoorLips(zonePoly) {
    if (!zonePoly || zonePoly.length < 3) return [];
    const lips = [];
    const EPS = 0.05;
    const add = (door, thickness) => {
      const wall = door && door.wall;
      if (!wall) return;
      const p1 = this.paramToPoint(wall, Math.min(door.hingeParam, door.endParam));
      const p2 = this.paramToPoint(wall, Math.max(door.hingeParam, door.endParam));
      const dx = p2.x - p1.x, dy = p2.y - p1.y;
      const L = Math.sqrt(dx * dx + dy * dy);
      if (L < 1e-6) return;
      const px = -dy / L, py = dx / L;
      const mx = (p1.x + p2.x) / 2, my = (p1.y + p2.y) / 2;
      /* Which side the zone is on, asked of the ZONE rather than the room: a door
         can sit on a wall this zone does not touch, and its lip would then float in
         another zone's turf. Straddling the boundary is the test — if both sides or
         neither are inside, this door is not on this zone's edge. */
      const inPos = this.pointInPolygon(mx + px * EPS, my + py * EPS, zonePoly);
      const inNeg = this.pointInPolygon(mx - px * EPS, my - py * EPS, zonePoly);
      if (inPos === inNeg) return;
      const s = inPos ? -1 : 1;            // outward = away from the turf
      const d = (thickness || 0) / 4;      // centreline → middle of the door frame
      if (!(d > 0)) return;
      lips.push([
        { x: p1.x, y: p1.y }, { x: p2.x, y: p2.y },
        { x: p2.x + px * s * d, y: p2.y + py * s * d },
        { x: p1.x + px * s * d, y: p1.y + py * s * d },
      ]);
    };
    for (const room of this.rooms) {
      if (!room.closed || room.type !== 'add') continue;
      for (const door of (room.doors || [])) add(door, this.wallThickness);
    }
    // Dividers bound zones too, and Ryan draws windows as rollers on them.
    for (const g of (this.wallSegmentGroups || [])) {
      if (!g || g.type === 'line') continue;
      for (const door of (g.doors || [])) add(door, this.wallSegmentThickness);
    }
    return lips;
  }

  _turfZoneShape(obj) {
    if (!obj || !obj.turfZone) return null;
    const ov = window.roomDesignerOverlay;
    if (!ov || !ov._getZonesForRoom) return null;
    const key = obj.turfZone;
    for (const room of this.rooms) {
      if (!room.closed || room.type !== 'add') continue;
      const geo = this._getRoomGeoCache(room);
      if (!geo.roomPolygon || geo.roomPolygon.length < 3) continue;
      const zones = ov._getZonesForRoom(room, geo) || [];
      let best = null, bestD = Infinity;
      for (const z of zones) {
        if (!z.centroid) continue;
        const d = Math.hypot(z.centroid.x - key.cx, z.centroid.y - key.cy);
        if (d < bestD) { bestD = d; best = z; }
      }
      // 2ft, matching _setZoneFlooring's tolerance — a zone that moved further than
      // that is a different zone, not the same one nudged.
      if (best && bestD < 2) return best;
    }
    return null;
  }

  /** Fill the zone under `point` with the armed turf brush. */
  _fillZoneWithTurf(point) {
    if (!this._turfBrush) return false;
    const hit = this._zoneAtPoint(point);
    if (!hit) { this.updateStatus('Double-click inside a room or zone to fill it with turf.', { error: true }); return false; }
    const zone = hit.zone;
    const box = RoomDesignerMath.minAreaRect(zone.polygon);
    if (!box) { this.updateStatus('That area is too thin to fill.', { error: true }); return false; }
    // One fill per zone — a second would stack on the first.
    for (const o of this.objects) {
      if (o.type === 'turf' && o.turfZone &&
          Math.hypot(o.turfZone.cx - zone.centroid.x, o.turfZone.cy - zone.centroid.y) < 2) {
        this.updateStatus('That area is already filled with turf.', { error: true });
        return false;
      }
    }
    this.saveUndoState();
    const obj = this._newTurfObject(box.cx, box.cy, box.width, box.depth);
    obj.rotation = box.rotation;
    obj.turfZone = { cx: zone.centroid.x, cy: zone.centroid.y };
    this.objects.push(obj);
    this._resetTurfState();
    this.selectedObjects = new Set([obj]);
    this.setActiveTool('select');
    const sqft = Math.round(Math.abs(RoomDesignerMath.signedPolygonArea(zone.polygon)));
    this.updateStatus('Area filled with turf — ' + sqft + ' sq ft. Markings follow its long axis; rotate to turn them.');
    this.render();
    this.updateMeasurements();
    return true;
  }
  // END CUSTOM_ROOMDESIGNER_BY_CLAUDE

  _handleTurfDraw(point) {
    if (!this._turfBrush) return;
    // Snap point to wall inner edge if within 6"
    const snapped = this._snapTurfToWallEdge(point);
    if (!this._turfCorner1) {
      // Block first click on existing turf (can't stack turf on turf)
      for (const obj of this.objects) {
        if (obj.type !== 'turf') continue;
        const rot = obj.rotation || 0;
        const cos = Math.cos(-rot), sin = Math.sin(-rot);
        const dx = snapped.x - obj.x, dy = snapped.y - obj.y;
        const lx = dx * cos - dy * sin, ly = dx * sin + dy * cos;
        if (Math.abs(lx) <= obj.width / 2 && Math.abs(ly) <= obj.depth / 2) return;
      }
      // Click 1: set first corner
      this._turfCorner1 = { x: snapped.x, y: snapped.y };
      if (this._animateRectLock) this._animateRectLock({ x: snapped.x, y: snapped.y }, 'green');
      this.updateStatus('Click to place opposite corner of turf.');
      this.render();
      return;
    }
    // Click 2: compute rectangle from two corners, clamp to room
    const c1 = this._turfCorner1;
    const c2 = { x: snapped.x, y: snapped.y };
    let _rawMinX = Math.min(c1.x, c2.x), _rawMaxX = Math.max(c1.x, c2.x);
    let _rawMinY = Math.min(c1.y, c2.y), _rawMaxY = Math.max(c1.y, c2.y);
    // Minimum size check on raw rect
    if ((_rawMaxX - _rawMinX) < 1/12 || (_rawMaxY - _rawMinY) < 1/12) {
      this.updateStatus('Turf piece too small. Click a different corner.');
      return;
    }
    // Clamp to room polygon
    const _clamped = this._clampTurfToRoom(_rawMinX, _rawMinY, _rawMaxX, _rawMaxY);
    if (!_clamped) {
      this.updateStatus('Turf must be placed inside a room.');
      this.render();
      return;
    }
    const w = _clamped.maxX - _clamped.minX;
    const d = _clamped.maxY - _clamped.minY;
    if (w < 1/12 || d < 1/12) {
      this.updateStatus('Clamped turf too small. Try a wider placement.');
      this.render();
      return;
    }
    const cx = (_clamped.minX + _clamped.maxX) / 2;
    const cy = (_clamped.minY + _clamped.maxY) / 2;
    // Turf/platform collision check (wall check skipped — clamping ensures no wall overlap)
    let _turfPlacePlatHit = false;
    for (const _po of this.objects) {
      if (_po.type !== 'platform') continue;
      const _r90 = (Math.round((_po.rotation || 0) / (Math.PI / 2)) % 2 !== 0);
      const _pw2 = (_r90 ? _po.depth : _po.width) / 2;
      const _pd2 = (_r90 ? _po.width : _po.depth) / 2;
      if (_clamped.maxX > _po.x - _pw2 + 0.01 && _clamped.minX < _po.x + _pw2 - 0.01 &&
          _clamped.maxY > _po.y - _pd2 + 0.01 && _clamped.minY < _po.y + _pd2 - 0.01) {
        _turfPlacePlatHit = true; break;
      }
    }
    if (this._turfOverlapsTurf(cx, cy, w, d, 0, null) || _turfPlacePlatHit) {
      this.updateStatus('Turf overlaps existing turf or platform. Try a different placement.');
      this.render();
      return;
    }
    this.saveUndoState();
    const _ov = window.roomDesignerOverlay;
    // New turf is always a clean slate — no lines, no markings.
    // User customizes via the Customize tab after placement.
    this.objects.push(this._newTurfObject(cx, cy, w, d));
    if (this._animateRectLock) this._animateRectLock({ x: c2.x, y: c2.y }, 'green');
    // Auto-select the newly placed turf so user can immediately customize
    const newTurf = this.objects[this.objects.length - 1];
    this.selectedObjects.clear();
    this.selectedObjects.add(newTurf);
    this.setActiveTool('select');
    if (_ov) { _ov._activeBrush = null; _ov._updatePanelSelection(); }
    this.updateStatus('Turf placed and selected. Customize it in the side panel, or click a swatch to place another.');
    this.render();
    this.updateMeasurements();
  }

  /**
   * A fresh turf piece. CUSTOM_ROOMDESIGNER_BY_CLAUDE — extracted so the rectangle
   * and zone-fill paths cannot drift: a new marking default added to one of them
   * would otherwise be missing from the other.
   */
  _newTurfObject(cx, cy, w, d) {
    return {
      type: 'turf',
      x: cx, y: cy, width: w, depth: d, rotation: 0,
      turfProductId: this._turfBrush.productId,
      turfColorId: this._turfBrush.colorId,
      turfColorHex: this._turfBrush.hex,
      // New turf is a clean slate — no lines, no markings. The user customises it
      // from the side panel after placement.
      turfLines: false,
      turfHashes: false,
      turfHashSides: 'long',
      turfHashSpacing: 3,
      turfCenterHashes: false,
      turfMarkingsRotated: false,
      turfLineDirection: 'lengthwise',
      turfLineColor: '#FFFFFF',
      turfHalfwayLine: false,
      turfSledLanes: false,
      turfSledLaneCount: 2,
      turfSledLaneEdges: ['right'],
      turfSledLaneInterval: 5,
      turfSledLaneStartFinish: true,
      turfSledLaneNumbers: false,
      turfAgilityLadderLength: 15
    };
  }

  _resetTurfState() {
    this._turfCorner1 = null;
    this._turfBrush = null;
  }
  /* END CUSTOM_PLANNER_BY_CLAUDE */

  /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — Inlaid platform placement */
  _handlePlatformPlace(point) {
    const _ov = window.roomDesignerOverlay;
    if (!_ov) return;
    _ov._initPlatformDefaults();
    const pd = _ov._platformDefaults;
    const presets = _ov._getPlatformPresets();
    const preset = presets.find(p => p.id === pd.preset) || presets[0];
    const rot = this._pendingPlatformRotation || 0;

    if (!this._pendingPlatformPos) {
      // ── FIRST CLICK: validate position, lock it, enter rotation phase ──
      const compatRooms = _ov._getPlatformCompatibleRooms();
      let targetRoom = null;
      for (const { room } of compatRooms) {
        const geo = this._getRoomGeoCache(room);
        if (geo.roomPolygon && this.pointInPolygon(point.x, point.y, geo.roomPolygon)) {
          targetRoom = room;
          break;
        }
      }
      if (!targetRoom) {
        this.updateStatus('Click inside a room with compatible flooring to place a platform.');
        return;
      }
      // Per-zone check: ensure the specific zone under the click has compatible flooring
      if (!_ov._isPointOnCompatibleZone(targetRoom, point.x, point.y)) {
        this.updateStatus('This zone has incompatible flooring for platforms. Place on a compatible zone.');
        return;
      }
      // Get flooring info for the zone under the click
      const _clickZoneInfo = _ov._getZoneFlooringAtPoint(targetRoom, point.x, point.y);
      const _clickZoneIsTile = !!(_clickZoneInfo && _clickZoneInfo.product.type === 'tile');

      // Block 8'×5' on tile flooring (5' not divisible by 2' tile grid)
      if (_clickZoneIsTile && pd.preset === '8x5') {
        this.updateStatus("8\u2032\u00d75\u2032 platform can\u2019t align to tile grid. Use 8\u2032\u00d76\u2032, 8\u2032\u00d74\u2032, or 4\u2032\u00d74\u2032.");
        return;
      }

      const w = preset.width, d = preset.depth;
      let cx, cy;
      if (this._platformAnchor) {
        // Subsequent platforms: snap to 1' edge-to-edge grid from anchor (use effective dims for rotation)
        const _nRot90 = (Math.round(this._platformAnchor.rotation / (Math.PI / 2)) % 2 !== 0);
        const snapped = this._snapToPlatformGrid(point.x, point.y, _nRot90 ? d : w, _nRot90 ? w : d);
        cx = snapped.x;
        cy = snapped.y;
      } else {
        // First platform: inch-scale snap
        const inchGrid = 1 / 12;
        cx = Math.round(point.x / inchGrid) * inchGrid;
        cy = Math.round(point.y / inchGrid) * inchGrid;
      }

      // Use anchor rotation for subsequent platforms, 0 for first
      const placeRot = this._platformAnchor ? this._platformAnchor.rotation : 0;

      // Tile grid snap: override position so corners land on tile seam intersections
      if (_clickZoneIsTile) {
        const _tW = _clickZoneInfo.product.widthFt || 2;
        const _tH = _clickZoneInfo.product.heightFt || _tW;
        const _clickGeo = this._getRoomGeoCache(targetRoom);
        const _snapped = _ov._snapPlatformToTileGrid(cx, cy, w, d, placeRot, _tW, _tH, _clickZoneInfo.flooringAngle, _clickGeo.roomPolygon);
        cx = _snapped.x;
        cy = _snapped.y;
      }

      if (this._platformOverlapsWall(cx, cy, w, d, placeRot) || this._platformOverlapsPlatform(cx, cy, w, d, placeRot, null) || this._turfOverlapsTurf(cx, cy, w, d, placeRot, null)) {
        this.updateStatus('Platform overlaps a wall, platform, or turf. Try a different location.');
        this.render();
        return;
      }
      const geo = this._getRoomGeoCache(targetRoom);
      // Check corners accounting for rotation
      const _cRot90 = (Math.round(placeRot / (Math.PI / 2)) % 2 !== 0);
      const _cW = _cRot90 ? d : w, _cD = _cRot90 ? w : d;
      const corners = [
        { x: cx - _cW/2, y: cy - _cD/2 }, { x: cx + _cW/2, y: cy - _cD/2 },
        { x: cx + _cW/2, y: cy + _cD/2 }, { x: cx - _cW/2, y: cy + _cD/2 }
      ];
      for (const corner of corners) {
        if (!this.pointInPolygon(corner.x, corner.y, geo.roomPolygon)) {
          this.updateStatus('Platform extends outside room. Try a different location.');
          this.render();
          return;
        }
      }
      // Verify all corners are on the SAME flooring zone (no straddling)
      for (const corner of corners) {
        const _cornerZone = _ov._getZoneFlooringAtPoint(targetRoom, corner.x, corner.y);
        const _cornerZonePoly = _cornerZone && _cornerZone.zone ? _cornerZone.zone.polygon : null;
        const _clickZonePoly = _clickZoneInfo && _clickZoneInfo.zone ? _clickZoneInfo.zone.polygon : null;
        if (_cornerZonePoly !== _clickZonePoly) {
          this.updateStatus('Platform must be entirely within one flooring zone.');
          this.render();
          return;
        }
        if (!_ov._isPointOnCompatibleZone(targetRoom, corner.x, corner.y)) {
          this.updateStatus('Platform overlaps incompatible flooring. Place entirely on compatible flooring.');
          this.render();
          return;
        }
      }

      // ── 4x4 is a perfect square — skip rotation, place immediately ──
      if (pd.preset === '4x4') {
        let targetRoomIdx = -1;
        for (let i = 0; i < this.rooms.length; i++) {
          if (this.rooms[i] === targetRoom) { targetRoomIdx = i; break; }
        }
        this.saveUndoState();
        const platformObj = {
          type: 'platform', x: cx, y: cy, width: w, depth: d,
          rotation: 0, preset: pd.preset,
          wings: pd.wings && preset.wingsAvail,
          centerColor: pd.centerColor, centerColorName: pd.centerColorName,
          centerColorSource: pd.centerColorSource || 'stock', centerTextureUrl: pd.centerTextureUrl || null,
          wingColor: pd.wingColor, wingColorName: pd.wingColorName,
          wingColorSource: pd.wingColorSource || 'stock', wingTextureUrl: pd.wingTextureUrl || null,
          colorConfirmed: pd.colorConfirmed || false,
          floorProductId: targetRoom.flooring ? targetRoom.flooring.productId : null, roomIdx: targetRoomIdx
        };
        this.objects.push(platformObj);
        this._platformAnchor = { x: cx, y: cy, width: w, depth: d, rotation: 0 };
        this.updateStatus('Platform placed. Click to place another, or press Escape to finish.');
        this.render();
        this.updateMeasurements();
        return;
      }
      // All other presets: lock position, enter rotation phase (use anchor rotation as starting angle)
      const initRot = this._platformAnchor ? this._platformAnchor.rotation : 0;
      this._pendingPlatformPos = { x: cx, y: cy };
      this._pendingPlatformRotation = initRot;
      this._pendingPlatformRoom = targetRoom;
      const degLabel = Math.round(initRot * 180 / Math.PI);
      this.updateStatus('Rotation: ' + degLabel + '\u00B0  \u2014  Move mouse to rotate, click to confirm');
      this.render();
    } else {
      // ── SECOND CLICK: finalize placement with rotation ──
      let cx = this._pendingPlatformPos.x;
      let cy = this._pendingPlatformPos.y;
      const finalRot = this._pendingPlatformRotation || 0;
      const w = preset.width, d = preset.depth;

      // Re-snap to tile grid with final rotation (rotation changes which corners hit the grid)
      const _secRoom = this._pendingPlatformRoom;
      if (_secRoom && _ov) {
        const _secZoneInfo = _ov._getZoneFlooringAtPoint(_secRoom, cx, cy);
        if (_secZoneInfo && _secZoneInfo.product.type === 'tile') {
          const _stW = _secZoneInfo.product.widthFt || 2;
          const _stH = _secZoneInfo.product.heightFt || _stW;
          const _secGeo = this._getRoomGeoCache(_secRoom);
          const _sSnap = _ov._snapPlatformToTileGrid(cx, cy, w, d, finalRot, _stW, _stH, _secZoneInfo.flooringAngle, _secGeo.roomPolygon);
          cx = _sSnap.x;
          cy = _sSnap.y;
        }
      }
      const targetRoom = this._pendingPlatformRoom;

      // Re-check overlap, room bounds, and zone containment with final rotation
      let _secBlocked = this._platformOverlapsWall(cx, cy, w, d, finalRot) || this._platformOverlapsPlatform(cx, cy, w, d, finalRot, null);
      if (!_secBlocked && targetRoom) {
        const _secGeo = this._getRoomGeoCache(targetRoom);
        if (_secGeo.roomPolygon) {
          const _sRot90 = (Math.round(finalRot / (Math.PI / 2)) % 2 !== 0);
          const _sW = _sRot90 ? d : w, _sD = _sRot90 ? w : d;
          const _sCorners = [
            { x: cx - _sW/2, y: cy - _sD/2 }, { x: cx + _sW/2, y: cy - _sD/2 },
            { x: cx + _sW/2, y: cy + _sD/2 }, { x: cx - _sW/2, y: cy + _sD/2 }
          ];
          for (const _sc of _sCorners) {
            if (!this.pointInPolygon(_sc.x, _sc.y, _secGeo.roomPolygon)) { _secBlocked = true; break; }
          }
          // Single-zone containment check after rotation
          if (!_secBlocked && _ov) {
            const _secCenterZone = _ov._getZoneFlooringAtPoint(targetRoom, cx, cy);
            const _secCenterPoly = _secCenterZone && _secCenterZone.zone ? _secCenterZone.zone.polygon : null;
            for (const _sc of _sCorners) {
              const _scZone = _ov._getZoneFlooringAtPoint(targetRoom, _sc.x, _sc.y);
              const _scPoly = _scZone && _scZone.zone ? _scZone.zone.polygon : null;
              if (_scPoly !== _secCenterPoly) { _secBlocked = true; break; }
              if (!_ov._isPointOnCompatibleZone(targetRoom, _sc.x, _sc.y)) { _secBlocked = true; break; }
            }
          }
        }
      }
      if (_secBlocked) {
        // Don't place — ghost is already red, just stay in rotation phase
        this.render();
        return;
      }

      // Find room index
      let targetRoomIdx = -1;
      if (targetRoom) {
        for (let i = 0; i < this.rooms.length; i++) {
          if (this.rooms[i] === targetRoom) { targetRoomIdx = i; break; }
        }
      }

      this.saveUndoState();
      const platformObj = {
        type: 'platform',
        x: cx,
        y: cy,
        width: w,
        depth: d,
        rotation: finalRot,
        preset: pd.preset,
        wings: pd.wings && preset.wingsAvail,
        centerColor: pd.centerColor,
        centerColorName: pd.centerColorName,
        centerColorSource: pd.centerColorSource || 'stock',
        centerTextureUrl: pd.centerTextureUrl || null,
        wingColor: pd.wingColor,
        wingColorName: pd.wingColorName,
        wingColorSource: pd.wingColorSource || 'stock',
        wingTextureUrl: pd.wingTextureUrl || null,
        colorConfirmed: pd.colorConfirmed || false,
        floorProductId: targetRoom ? targetRoom.flooring.productId : null,
        roomIdx: targetRoomIdx
      };
      this.objects.push(platformObj);

      // Update anchor to the latest placed platform (subsequent ones snap to its grid)
      this._platformAnchor = { x: cx, y: cy, width: w, depth: d, rotation: finalRot };
      // Stay in placement mode — clear pending pos for next placement
      this._pendingPlatformPos = null;
      this._pendingPlatformRotation = 0;
      this._pendingPlatformRoom = null;
      this.updateStatus('Platform placed. Click to place another, or press Escape to finish.');
      this.render();
      this.updateMeasurements();
    }
  }

  _platformOverlapsWall(cx, cy, w, d, rot) {
    // Check if a rectangle overlaps any room walls
    return this._equipmentOverlapsWall(cx, cy, w, d, rot, null);
  }

  _platformOverlapsPlatform(cx, cy, w, d, rot, excludeObj) {
    for (const obj of this.objects) {
      if (obj.type !== 'platform' || obj === excludeObj) continue;
      // Transform both rects to axis-aligned by accounting for rotation
      // For 90° snaps (platforms only rotate in 90° increments), swap w/d when rotated
      const effW = (Math.round(rot / (Math.PI / 2)) % 2 !== 0) ? d : w;
      const effD = (Math.round(rot / (Math.PI / 2)) % 2 !== 0) ? w : d;
      const oRot = obj.rotation || 0;
      const oEffW = (Math.round(oRot / (Math.PI / 2)) % 2 !== 0) ? obj.depth : obj.width;
      const oEffD = (Math.round(oRot / (Math.PI / 2)) % 2 !== 0) ? obj.width : obj.depth;
      const dx = Math.abs(cx - obj.x);
      const dy = Math.abs(cy - obj.y);
      if (dx < (effW + oEffW) / 2 - 0.01 && dy < (effD + oEffD) / 2 - 0.01) return true;
    }
    return false;
  }

  _resetPlatformState() {
    this._platformPlaceMode = false;
    this._pendingPlatformPos = null;
    this._pendingPlatformRotation = 0;
    this._pendingPlatformRoom = null;
    this._platformAnchor = null;
  }

  /** Snap position to 1' edge-to-edge grid aligned to the anchor platform.
   *  Only snaps when mouse is within ~1 ft of an alignment axis; otherwise free inch-grid placement. */
  _snapToPlatformGrid(rawX, rawY, newW, newD) {
    const a = this._platformAnchor;
    if (!a) return null;
    const SNAP_THRESHOLD = 1.0; // 1 foot proximity to snap to alignment axis
    // Effective dimensions accounting for 90° rotation
    const aRot90 = (Math.round(a.rotation / (Math.PI / 2)) % 2 !== 0);
    const aW = aRot90 ? a.depth : a.width;
    const aD = aRot90 ? a.width : a.depth;
    const dxRaw = rawX - a.x;
    const dyRaw = rawY - a.y;
    // Check proximity to each alignment axis
    const nearHoriz = Math.abs(dyRaw) <= SNAP_THRESHOLD; // close to horizontal axis (same Y as anchor)
    const nearVert = Math.abs(dxRaw) <= SNAP_THRESHOLD;  // close to vertical axis (same X as anchor)
    // If not near either axis, free placement on inch grid
    if (!nearHoriz && !nearVert) {
      const inch = 1 / 12;
      return { x: Math.round(rawX / inch) * inch, y: Math.round(rawY / inch) * inch };
    }
    let sx, sy;
    // If near both axes (close to anchor center), pick the primary direction
    const useHoriz = nearHoriz && (!nearVert || Math.abs(dxRaw) >= Math.abs(dyRaw));
    if (useHoriz) {
      // Snap Y to anchor center, snap X edge-to-edge at 1' increments
      sy = a.y;
      const halfSumX = aW / 2 + newW / 2;
      const signX = dxRaw >= 0 ? 1 : -1;
      const edgeGapX = Math.abs(dxRaw) - halfSumX;
      const snappedGapX = Math.max(0, Math.round(edgeGapX));
      sx = a.x + signX * (halfSumX + snappedGapX);
    } else {
      // Snap X to anchor center, snap Y edge-to-edge at 1' increments
      sx = a.x;
      const halfSumY = aD / 2 + newD / 2;
      const signY = dyRaw >= 0 ? 1 : -1;
      const edgeGapY = Math.abs(dyRaw) - halfSumY;
      const snappedGapY = Math.max(0, Math.round(edgeGapY));
      sy = a.y + signY * (halfSumY + snappedGapY);
    }
    return { x: sx, y: sy };
  }
  /* END CUSTOM_ROOMDESIGNER_BY_CLAUDE */

  /* ── RECTANGLE STATE RESET ── */

  _resetRectState() {
    this._rectCorner1 = null;
    this._rectCorner1Confirmed = false;
    this._rectCorner2 = null;
    this._rectAutoPlaced = false;
    this._rectIntroAnimating = false;
    this._rectIntroAnimStart = 0;
    if (this._rectPulseTimer) { clearTimeout(this._rectPulseTimer); this._rectPulseTimer = null; }
    const penBtn = document.getElementById('penToolBtn');
    if (penBtn) penBtn.classList.remove('rect-pulse-hint', 'rect-pulse-subtract');
    this._clearCheckPulse();
    this._clearArrowPulse();
    this._setJoystickArrowsEnabled(true);
    // Restore checkmark to active state
    const placeBtn = document.querySelector('.ring-center[data-dir="place"]');
    if (placeBtn) placeBtn.classList.remove('inactive');
  }

  /* ── FREEHAND STATE RESET ── */

  _resetFreehandState() {
    this._freehandAutoPlaced = false;
    this._freehandStartConfirmed = false;
    this._clearCheckPulse();
    this._clearArrowPulse();
    this._setJoystickArrowsEnabled(true);
    const placeBtn = document.querySelector('.ring-center[data-dir="place"]');
    if (placeBtn) placeBtn.classList.remove('inactive');
  }
  /* ── WALL SEGMENT HELPERS ── */

  _getSegmentGroupEndpoints(group) { return RoomDesignerTools.getSegmentGroupEndpoints(group); }
  _segmentIntersectsCircle(start, end, center, r) { return RoomDesignerTools.segmentIntersectsCircle(start, end, center, r); }

  _segmentIntersectsRect(start, end, corners) { return RoomDesignerTools.segmentIntersectsRect(start, end, corners); }

  _segmentsIntersectPts(a1, a2, b1, b2) { return RoomDesignerTools.segmentsIntersectPts(a1, a2, b1, b2); }

  _pointInPolygon(pt, polygon) { return RoomDesignerTools.pointInPolygon(pt, polygon); }

  _hasDoorAtPoint(room, point) { return RoomDesignerTools.hasDoorAtPoint(room, point); }

  /* ── PRINT-MODE CANVAS EXPORT (Phase 5A) ── */

  /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — ONE rule for floor seams in every export.
     It lives here rather than inline because it was written out three times, in
     three exporters, and had already drifted into three different answers: the
     per-space PDF forced seams ON, the fallback PDF forced them OFF, and the
     share image forced them ON with its own alpha. So whether your seams reached
     the PDF depended on which code path the export happened to take — never on
     the Settings toggle you set it with.

     The rule, in Ryan's words: seams on in Settings means seams in the PDF, off
     means no seams; and if seams ARE showing and the drawing has equipment, they
     dim by half so they don't compete with it. No equipment, no dimming.

     ⚠️ The dimming is a MULTIPLIER on the base, not a fixed value, because the
     base already varies: a dark floor drops to 0.23 on its own (a light seam on
     dark rubber is high-contrast to begin with). Writing the equipment case as a
     flat number — which is what the old `_hasEquip ? 0.60 : 0.80` did — silently
     overrode that and made dark floors *louder* under equipment, not quieter.

     "Has equipment" is asked of the whole DRAWING, not the page being rendered.
     Per-space PDFs would otherwise dim page 2 and not page 3, which reads as a
     rendering fault rather than a deliberate choice.

     @param overlay  the flooring overlay, for the dark-floor test
     @param forceOn  export types that are ABOUT the floor and always want seams
                     regardless of the toggle (the share preview image) */
  _applyPrintSeamRule(overlay, forceOn) {
    overlay = overlay || window.roomDesignerOverlay;
    if (forceOn) this.showSeamLines = true;
    if (!this.showSeamLines) {
      // Null, not 0 — the value is read as "an override is in force", and a stale
      // one from the previous export would follow the seams into the next.
      this._printSeamAlpha = null;
      return;
    }
    let alpha = 0.80;
    if (overlay) {
      const darkPartials = ['Buff Blue','Grippin Gray','Rippin Red','Whey Protein','Jet Black','Blue Jays','Cardinals','Raiders','Urban Slate'];
      const darkExact = ['Navy','Maroon','Green'];
      const isDark = (cn) => darkExact.includes(cn) || darkPartials.some(d => cn.includes(d));
      const colorName = (pid, cid) => {
        const p = overlay._getProduct(pid);
        return p && p.colors ? (p.colors.find(c => c.id === cid) || {}).name || '' : '';
      };
      const anyDark = [...this.rooms, this.currentRoom].some(room => {
        if (!room || !room.closed || room.type !== 'add') return false;
        if (room.flooring && isDark(colorName(room.flooring.productId, room.flooring.colorId))) return true;
        if (room.zoneFlooring) return room.zoneFlooring.some(zf => isDark(colorName(zf.productId, zf.colorId)));
        return false;
      });
      if (anyDark) alpha = 0.23;
    }
    if (this.objects && this.objects.some(o => o.type === 'equipment')) alpha *= 0.5;
    this._printSeamAlpha = alpha;
  }

  async _generatePrintCanvas() {
    // Landscape tabloid at 200 DPI: 17" × 11"
    const PRINT_W = 3400, PRINT_H = 2200;
    const MARGIN = 53; // 0.35" at 150 DPI

    // Compute bounding box of all content (rooms + objects + wall segments)
    const allPoints = [];
    [...this.rooms, this.currentRoom].forEach(room => {
      if (!room) return;
      room.walls.forEach(w => { allPoints.push(w.start); allPoints.push(w.end); });
    });
    for (const obj of this.objects) {
      if (obj.type === 'rectangle' || obj.type === 'equipment' || obj.type === 'turf' || obj.type === 'platform') {
        const hw = obj.width / 2, hd = obj.depth / 2;
        const rot = obj.rotation || 0;
        for (const [lx, ly] of [[-hw, -hd], [hw, -hd], [hw, hd], [-hw, hd]]) {
          allPoints.push(this._rotatePoint(obj.x + lx, obj.y + ly, obj.x, obj.y, rot));
        }
      } else if (obj.type === 'circle') {
        const r = obj.diameter / 2;
        allPoints.push({ x: obj.x - r, y: obj.y - r }); allPoints.push({ x: obj.x + r, y: obj.y + r });
      }
    }
    for (const group of this.wallSegmentGroups) {
      for (const seg of group.segments) { allPoints.push(seg.start); allPoints.push(seg.end); }
    }
    if (allPoints.length === 0) return null;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    allPoints.forEach(p => { minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y); });
    const PAD = 3; minX -= PAD; minY -= PAD; maxX += PAD; maxY += PAD;

    // Reserve space at bottom for legend (scale bar + branding + swatch legend)
    const LEGEND_H = 120;
    const drawAreaW = PRINT_W - 2 * MARGIN;
    const drawAreaH = PRINT_H - 2 * MARGIN - LEGEND_H;
    const contentW = maxX - minX, contentH = maxY - minY;
    if (contentW <= 0 || contentH <= 0) return null;

    const scaleX = drawAreaW / (contentW * this.basePixelsPerFoot);
    const scaleY = drawAreaH / (contentH * this.basePixelsPerFoot);
    const printZoom = Math.min(scaleX, scaleY);
    const centerX = (minX + maxX) / 2, centerY = (minY + maxY) / 2;
    const printPanX = MARGIN + drawAreaW / 2 - centerX * this.basePixelsPerFoot * printZoom;
    const printPanY = MARGIN + drawAreaH / 2 - centerY * this.basePixelsPerFoot * printZoom;

    // Save all state we'll override
    const saved = {
      ctx: this.ctx, canvas: this.canvas, panX: this.panX, panY: this.panY,
      zoomLevel: this.zoomLevel, darkMode: this.darkMode, _tc: this._tc,
      _cssWidth: this._cssWidth, _cssHeight: this._cssHeight, _dpr: this._dpr,
      showGrid: this.showGrid, _printMode: this._printMode,
      _drawRoomOverlayHook: this._drawRoomOverlayHook,
      _beforeRoomsHook: this._beforeRoomsHook,
      _afterRoomsHook: this._afterRoomsHook,
      _afterWallSegmentsHook: this._afterWallSegmentsHook,
      _afterObjectsHook: this._afterObjectsHook,
      _drawBackgroundHook: this._drawBackgroundHook,
      _selectOpen: this._selectOpen,
      _popoverActive: this._popoverActive,
      _renderFrame: this._renderFrame,
      _floorDimObjects: this._floorDimObjects,
      showMeasurements: this.showMeasurements,
      showRoomLabels: this.showRoomLabels,
      _canvasRotationRad: this._canvasRotationRad,
      _floorMode: this._floorMode,
      showSeamLines: this.showSeamLines,
      hollowWalls: this.hollowWalls,
      _printSeamAlpha: this._printSeamAlpha,
    };

    try {
      this._canvasRotationRad = 0;
      this._floorMode = false;
      // Routed through the shared rule for consistency, though it changes nothing
      // here today: this is the emergency fallback and it nulls every flooring hook
      // below, so there is no flooring layer for a seam to sit on. It used to say
      // `showSeamLines = false`, which read as a decision about seams when it was
      // really a consequence of drawing no floor at all.
      this._applyPrintSeamRule();
      this.hollowWalls = false;
      // Create offscreen canvas
      const offscreen = document.createElement('canvas');
      offscreen.width = PRINT_W;
      offscreen.height = PRINT_H;
      const ctx = offscreen.getContext('2d');

      // White background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, PRINT_W, PRINT_H);

      // Override RD state for print rendering
      this.canvas = offscreen;
      this.ctx = ctx;
      this._dpr = 1;
      this._cssWidth = PRINT_W;
      this._cssHeight = PRINT_H;
      this.panX = printPanX;
      this.panY = printPanY;
      this.zoomLevel = printZoom;
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — labels keep the look they were AUTHORED in.
      // Print swaps the whole scheme for paper, which used to flip every label from
      // dark-with-white-text to white-with-dark-text on export. _labelColors() reads
      // this instead of darkMode; cleared on restore below.
      this._labelAuthorDark = this.darkMode;
      this.darkMode = false;
      this.showGrid = true;
      this.showMeasurements = saved.showMeasurements;
      this.showRoomLabels = false;
      this._printMode = true;
      this._selectOpen = false;
      this._popoverActive = false;
      this._renderFrame = null;
      this._floorDimObjects = false;

      // Print color scheme — use #EAEAEA for gray fills/backgrounds
      this._tc = {
        wallColor: '#000000',
        gridMajor: 'rgba(0,0,0,0.06)',
        gridMinor: 'rgba(0,0,0,0.03)',
        objectStroke: '#000000',
        objectFill: '#EAEAEA',
        labelBg: '#EAEAEA',
        labelBorder: 'rgba(0,0,0,0.12)',
        labelText: '#333333',
        roomLabelText: '#000000',
        previewFill: 'rgba(255,255,255,0.8)',
        roomFill: 'rgba(0,0,0,0.015)',
        roomFillAlpha: 0.015,
        roomFlooredFill: 'rgba(0,0,0,0.04)',
        subtractFill: 'rgba(0,0,0,0.03)',
        roomGridDim: 'rgba(255,255,255,0.6)',
      };

      // Null out overlay hooks (no flooring colors on print)
      this._drawRoomOverlayHook = null;
      this._beforeRoomsHook = null;
      this._afterRoomsHook = null;
      this._afterWallSegmentsHook = null;
      this._afterObjectsHook = null;
      this._drawBackgroundHook = null;

      // Run the render pipeline directly
      this._doRender();

      // ── Print-only elements on top ──

      // Scale bar — bottom-left of drawing area
      this._drawPrintScaleBar(ctx, MARGIN, PRINT_H - MARGIN - LEGEND_H + 20, printZoom);

      let dataUrl = offscreen.toDataURL('image/png');
      // If PNG exceeds sessionStorage budget (~3.5MB), re-render at lower resolution
      if (dataUrl.length > 3.5 * 1024 * 1024) {
        const scale2 = 1800 / PRINT_W;
        const small = document.createElement('canvas');
        small.width = Math.round(PRINT_W * scale2);
        small.height = Math.round(PRINT_H * scale2);
        const sctx = small.getContext('2d');
        sctx.drawImage(offscreen, 0, 0, small.width, small.height);
        dataUrl = small.toDataURL('image/png');
      }
      return dataUrl;
    } finally {
      // Restore all saved state
      this.ctx = saved.ctx;
      this.canvas = saved.canvas;
      this.panX = saved.panX;
      this.panY = saved.panY;
      this.zoomLevel = saved.zoomLevel;
      this.darkMode = saved.darkMode;
      this._labelAuthorDark = null;
      this._tc = saved._tc;
      this._cssWidth = saved._cssWidth;
      this._cssHeight = saved._cssHeight;
      this._dpr = saved._dpr;
      this.showGrid = saved.showGrid;
      this._printMode = saved._printMode;
      this._drawRoomOverlayHook = saved._drawRoomOverlayHook;
      this._beforeRoomsHook = saved._beforeRoomsHook;
      this._afterRoomsHook = saved._afterRoomsHook;
      this._afterWallSegmentsHook = saved._afterWallSegmentsHook;
      this._afterObjectsHook = saved._afterObjectsHook;
      this._selectOpen = saved._selectOpen;
      this._popoverActive = saved._popoverActive;
      this._renderFrame = saved._renderFrame;
      this._floorDimObjects = saved._floorDimObjects;
      this.showMeasurements = saved.showMeasurements;
      this.showRoomLabels = saved.showRoomLabels;
      this._canvasRotationRad = saved._canvasRotationRad;
      this._floorMode = saved._floorMode;
      this.showSeamLines = saved.showSeamLines;
      this._printSeamAlpha = saved._printSeamAlpha;
      this.hollowWalls = saved.hollowWalls;
    }
  }

  /* ── OG IMAGE FOR SOCIAL SHARING ── */

  async _generateOgImage() {
    // Standard OG image: 1200×630 JPEG showing all rooms + equipment + flooring
    const OG_W = 1200, OG_H = 630, JPEG_Q = 0.80;
    const PAD = 2; // feet of padding around content

    // Collect all closed add-room wall points + equipment
    const hasClosedRoom = this.rooms.some(r => r.closed && r.type === 'add');
    if (!hasClosedRoom) return null;

    const points = [];
    for (const room of this.rooms) {
      if (!room.closed || room.type !== 'add') continue;
      for (const w of room.walls) { points.push(w.start); points.push(w.end); }
      // Include door swing arcs
      if (room.doors) {
        for (const door of room.doors) {
          if (!door.wall || !door.swing) continue;
          const wall = door.wall;
          const hingePoint = this.paramToPoint(wall, door.hingeParam);
          const endPoint = this.paramToPoint(wall, door.endParam);
          const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
          const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
          if (wLen === 0) continue;
          const perpX = -wdy / wLen, perpY = wdx / wLen;
          const doorWidthFt = door.width / 12;
          const s = door.swing;
          if (door.type === 'double') {
            const midX = (hingePoint.x + endPoint.x) / 2, midY = (hingePoint.y + endPoint.y) / 2;
            points.push({ x: midX + perpX * s.perpSign * (doorWidthFt / 2), y: midY + perpY * s.perpSign * (doorWidthFt / 2) });
          } else {
            const pivot = s.hingeEnd === 'hinge' ? hingePoint : endPoint;
            points.push({ x: pivot.x + perpX * s.perpSign * doorWidthFt, y: pivot.y + perpY * s.perpSign * doorWidthFt });
          }
        }
      }
    }
    // Include equipment objects
    for (const obj of this.objects) {
      if (obj.type !== 'equipment') continue;
      const hw = obj.width / 2, hd = obj.depth / 2;
      const rot = obj.rotation || 0;
      for (const [lx, ly] of [[-hw, -hd], [hw, -hd], [hw, hd], [-hw, hd]]) {
        points.push(this._rotatePoint(obj.x + lx, obj.y + ly, obj.x, obj.y, rot));
      }
    }

    if (points.length === 0) return null;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of points) {
      minX = Math.min(minX, p.x); minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y);
    }
    minX -= PAD; minY -= PAD; maxX += PAD; maxY += PAD;

    const contentW = maxX - minX, contentH = maxY - minY;
    if (contentW <= 0 || contentH <= 0) return null;

    // Fit content into 1200×(630 - strip) area
    const drawAreaW = OG_W, drawAreaH = OG_H;
    const scaleX = drawAreaW / (contentW * this.basePixelsPerFoot);
    const scaleY = drawAreaH / (contentH * this.basePixelsPerFoot);
    const printZoom = Math.min(scaleX, scaleY);
    const centerX = (minX + maxX) / 2, centerY = (minY + maxY) / 2;
    const printPanX = drawAreaW / 2 - centerX * this.basePixelsPerFoot * printZoom;
    const printPanY = drawAreaH / 2 - centerY * this.basePixelsPerFoot * printZoom;

    // Save state
    const overlay = window.roomDesignerOverlay;
    const saved = {
      ctx: this.ctx, canvas: this.canvas, panX: this.panX, panY: this.panY,
      zoomLevel: this.zoomLevel, darkMode: this.darkMode, _tc: this._tc,
      _cssWidth: this._cssWidth, _cssHeight: this._cssHeight, _dpr: this._dpr,
      showGrid: this.showGrid, _printMode: this._printMode,
      _drawRoomOverlayHook: this._drawRoomOverlayHook,
      _beforeRoomsHook: this._beforeRoomsHook,
      _afterRoomsHook: this._afterRoomsHook,
      _afterWallSegmentsHook: this._afterWallSegmentsHook,
      _afterObjectsHook: this._afterObjectsHook,
      _drawBackgroundHook: this._drawBackgroundHook,
      _selectOpen: this._selectOpen,
      _popoverActive: this._popoverActive,
      _renderFrame: this._renderFrame,
      _floorDimObjects: this._floorDimObjects,
      showMeasurements: this.showMeasurements,
      _measurementsVisible: this._measurementsVisible,
      _measurements: this._measurements,
      mode: this.mode,
      showRoomLabels: this.showRoomLabels,
      _canvasRotationRad: this._canvasRotationRad,
      _floorMode: this._floorMode,
      showSeamLines: this.showSeamLines,
      hollowWalls: this.hollowWalls,
      _viewOnly: this._viewOnly,
      _viewOnlyShowFloor: this._viewOnlyShowFloor,
      showMaterialOverlay: this.showMaterialOverlay,
      _overlayDetailSuppressed: this._overlayDetailSuppressed,
      _printSeamAlpha: this._printSeamAlpha,
    };
    const savedOverlayAlpha = overlay ? overlay._overlayDetailAlpha : null;
    const savedOverlayTarget = overlay ? overlay._overlayDetailTarget : null;
    const savedOverlayShow = overlay ? overlay.showOverlay : null;

    try {
      this._canvasRotationRad = 0;
      this._floorMode = false;
      // The share preview is a picture OF the floor, so it keeps its seams
      // whatever the toggle says — but it dims them for equipment like everything else.
      this._applyPrintSeamRule(overlay, true);
      this.hollowWalls = false;
      this._viewOnly = true;
      this._viewOnlyShowFloor = true;
      this._printMode = true;
      this.showMaterialOverlay = true;
      this._overlayDetailSuppressed = false;
      if (overlay) {
        overlay.showOverlay = true;
        overlay._overlayDetailAlpha = 1;
        overlay._overlayDetailTarget = 1;
        clearTimeout(overlay._overlayDetailTimer);
        clearTimeout(overlay._overlayDetailFadeTimer);
        overlay._overlayDetailTimer = null;
        overlay._overlayDetailFadeTimer = null;
      }

      const offscreen = document.createElement('canvas');
      offscreen.width = OG_W;
      offscreen.height = OG_H;
      const ctx = offscreen.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, OG_W, OG_H);

      this.canvas = offscreen;
      this.ctx = ctx;
      this._dpr = 1;
      this._cssWidth = OG_W;
      this._cssHeight = OG_H;
      this.panX = printPanX;
      this.panY = printPanY;
      this.zoomLevel = printZoom;
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — labels keep the look they were AUTHORED in.
      // Print swaps the whole scheme for paper, which used to flip every label from
      // dark-with-white-text to white-with-dark-text on export. _labelColors() reads
      // this instead of darkMode; cleared on restore below.
      this._labelAuthorDark = this.darkMode;
      this.darkMode = false;
      this.showGrid = false;
      this.showMeasurements = false;
      this._measurementsVisible = false;
      this._measurements = [];
      this.showRoomLabels = false;
      this.mode = 'draw';
      this._printMode = true;
      this._selectOpen = false;
      this._popoverActive = false;
      this._renderFrame = null;
      this._floorDimObjects = false;
      this._printFocusRoom = null; // render ALL rooms

      this._tc = {
        wallColor: '#000000',
        gridMajor: 'rgba(0,0,0,0.06)',
        gridMinor: 'rgba(0,0,0,0.03)',
        objectStroke: '#000000',
        objectFill: '#EAEAEA',
        labelBg: '#EAEAEA',
        labelBorder: 'rgba(0,0,0,0.12)',
        labelText: '#333333',
        roomLabelText: '#000000',
        previewFill: 'rgba(255,255,255,0.8)',
        roomFill: 'rgba(0,0,0,0.015)',
        roomFillAlpha: 0.015,
        roomFlooredFill: 'rgba(0,0,0,0.04)',
        subtractFill: 'rgba(0,0,0,0.03)',
        roomGridDim: 'rgba(255,255,255,0.6)',
      };

      // Keep flooring overlay, clear other hooks
      this._beforeRoomsHook = null;
      this._afterRoomsHook = null;
      this._afterWallSegmentsHook = null;
      this._afterObjectsHook = null;
      this._drawBackgroundHook = null;

      this._doRender();

      return offscreen.toDataURL('image/jpeg', JPEG_Q);
    } finally {
      this.ctx = saved.ctx;
      this.canvas = saved.canvas;
      this.panX = saved.panX;
      this.panY = saved.panY;
      this.zoomLevel = saved.zoomLevel;
      this.darkMode = saved.darkMode;
      this._labelAuthorDark = null;
      this._tc = saved._tc;
      this._cssWidth = saved._cssWidth;
      this._cssHeight = saved._cssHeight;
      this._dpr = saved._dpr;
      this.showGrid = saved.showGrid;
      this._printMode = saved._printMode;
      this._drawRoomOverlayHook = saved._drawRoomOverlayHook;
      this._beforeRoomsHook = saved._beforeRoomsHook;
      this._afterRoomsHook = saved._afterRoomsHook;
      this._afterWallSegmentsHook = saved._afterWallSegmentsHook;
      this._afterObjectsHook = saved._afterObjectsHook;
      this._drawBackgroundHook = saved._drawBackgroundHook;
      this._selectOpen = saved._selectOpen;
      this._popoverActive = saved._popoverActive;
      this._renderFrame = saved._renderFrame;
      this._floorDimObjects = saved._floorDimObjects;
      this.showMeasurements = saved.showMeasurements;
      this._measurementsVisible = saved._measurementsVisible;
      this._measurements = saved._measurements;
      this.mode = saved.mode;
      this.showRoomLabels = saved.showRoomLabels;
      this._canvasRotationRad = saved._canvasRotationRad;
      this._floorMode = saved._floorMode;
      this.showSeamLines = saved.showSeamLines;
      this.hollowWalls = saved.hollowWalls;
      this._viewOnly = saved._viewOnly;
      this._viewOnlyShowFloor = saved._viewOnlyShowFloor;
      this.showMaterialOverlay = saved.showMaterialOverlay;
      this._overlayDetailSuppressed = saved._overlayDetailSuppressed;
      this._printSeamAlpha = saved._printSeamAlpha;
      this._printFocusRoom = null;
      if (overlay) {
        overlay._overlayDetailAlpha = savedOverlayAlpha;
        overlay._overlayDetailTarget = savedOverlayTarget;
        overlay.showOverlay = savedOverlayShow;
      }
    }
  }

  /* ── PER-SPACE PRINT CANVASES (Multi-Page PDF) ── */

  /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — what pages a PDF is made of.
     Default: one page per closed additive space, which is what it has always been.
     `_printPagePlan` (set by the portal PDF dialog) overrides it, and is the FULL
     definition of the document — a space missing from it does not appear. That is
     deliberate: the picker is either the whole answer or it is a second thing to
     reason about alongside an invisible default.

     A page is { room, zoneIdxs } — zoneIdxs null means the whole space, an array
     means frame on those zones of that room, together, on this one page. "A page
     each" is not a mode down here; the dialog just emits one entry per zone. Keeping
     the plan dumb means the generator has exactly two cases instead of three.

     Zone polygons are resolved HERE rather than stored in the plan, because zones
     are recomputed from the dividers and an index is the only stable handle — a
     saved polygon would go stale the moment a wall moved. */
  /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — "drag a box on the plan" for a close-up PDF page.
     A deliberately tiny mode rather than a tool: no toolbar button, no tab rules, no
     entry in setActiveTool. The PDF dialog steps aside, arms this, and gets a world
     rectangle back; nothing else in the app can reach it. The alternative — teaching
     the select tool a fourth kind of drag — would have put print-only behaviour in
     the middle of the most heavily used code path in the file. */
  beginRegionPick(onDone) {
    this._regionPick = { onDone, c1: null, c2: null };
    this._regionPrevCursor = this.canvas.style.cursor;
    this.canvas.style.cursor = 'crosshair';
    this.updateStatus('Drag a box around the area you want on its own page. Esc to cancel.');
    this.render();
  }

  _endRegionPick(rect) {
    const rp = this._regionPick;
    this._regionPick = null;
    if (this._regionPrevCursor != null) { this.canvas.style.cursor = this._regionPrevCursor; this._regionPrevCursor = null; }
    this.render();
    if (rp && rp.onDone) rp.onDone(rect || null);
  }

  _drawRegionPick(scale) {
    const rp = this._regionPick;
    if (!rp || !rp.c1 || !rp.c2) return;
    const x = Math.min(rp.c1.x, rp.c2.x), y = Math.min(rp.c1.y, rp.c2.y);
    const w = Math.abs(rp.c2.x - rp.c1.x), h = Math.abs(rp.c2.y - rp.c1.y);
    this.ctx.save();
    this.ctx.fillStyle = 'rgba(0, 107, 204, 0.10)';
    this.ctx.fillRect(x, y, w, h);
    this.ctx.strokeStyle = '#006BCC';
    this.ctx.lineWidth = 1.5 / scale;
    this.ctx.setLineDash([6 / scale, 4 / scale]);
    this.ctx.strokeRect(x, y, w, h);
    this.ctx.restore();
  }

  /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — the picker's saved UI state → a print page plan.
     Extracted rather than left in the dialog because there are now TWO callers: the
     dialog itself, and the post-save variant generation that feeds Project Assembly
     packets. Two copies of this mapping is how the packet ends up framing pages
     differently from the PDF the rep looked at before ordering it. */
  _pagePlanFromSelection(sel) {
    const pages = sel && sel.pages;
    if (!Array.isArray(pages) || !pages.length) return null;
    return pages.map(p => ({
      roomIndex: p.roomIndex,
      zoneIdxs: p.kind === 'zones' ? (p.zoneIdxs || []).slice() : null,
      rect: p.kind === 'rect' ? p.rect : null,
      rotateTurns: (p.rotateTurns == null) ? null : p.rotateTurns,
    }));
  }

  /* Is there anything about this design's page list worth a separate packet variant?
     "Nothing special" means the list says exactly what the default already does —
     one whole-space page per space, in room order, no rotation overrides — in which
     case the designer variant would be byte-identical to the normal one and the
     packet checkbox has nothing to offer. This is the single definition of "special":
     the portal greys the box on it, and variant generation skips the extra four
     renders on it, so the two can never disagree about when the feature applies. */
  _hasDesignerLayout() {
    const pages = this._pdfPageSelection && this._pdfPageSelection.pages;
    if (!Array.isArray(pages) || !pages.length) return false;
    const rooms = this.rooms.filter(r => r.closed && r.type === 'add');
    if (pages.length !== rooms.length) return true;
    return pages.some((p, i) => p.kind !== 'space' || p.roomIndex !== i || p.rotateTurns != null);
  }

  _buildPrintPages() {
    const rooms = this.rooms.filter(r => r.closed && r.type === 'add');
    const plan = this._printPagePlan;
    if (!plan || !plan.length) return rooms.map(room => ({ room, zonePolys: null, rect: null, rotateTurns: null, label: null }));

    const overlay = window.roomDesignerOverlay;
    const pages = [];
    for (const entry of plan) {
      const room = (typeof entry.roomIndex === 'number') ? rooms[entry.roomIndex] : entry.room;
      if (!room) continue;
      const turns = (entry.rotateTurns == null) ? null : entry.rotateTurns;
      const base = room.name || ('Space ' + (rooms.indexOf(room) + 1));

      /* A drawn region: the frame is the rectangle and nothing else. It is stored in
         WORLD feet, so it survives everything except the user moving the room out
         from under it — there is no index to go stale. */
      if (entry.rect) {
        const r = entry.rect;
        if (!(r.maxX > r.minX) || !(r.maxY > r.minY)) continue;
        pages.push({
          room, zonePolys: null, rotateTurns: turns,
          rect: [{ x: r.minX, y: r.minY }, { x: r.maxX, y: r.minY }, { x: r.maxX, y: r.maxY }, { x: r.minX, y: r.maxY }],
          label: entry.label || (base + ' — close-up')
        });
        continue;
      }

      if (!entry.zoneIdxs || !entry.zoneIdxs.length || !overlay || !overlay._getZonesForRoom) {
        pages.push({ room, zonePolys: null, rect: null, rotateTurns: turns, label: entry.label || null });
        continue;
      }
      const zones = overlay._getZonesForRoom(room) || [];
      const polys = [];
      const names = [];
      for (const zi of entry.zoneIdxs) {
        const z = zones[zi];
        if (!z || !z.polygon || z.polygon.length < 3) continue;
        polys.push(z.polygon);
        names.push((room.zoneNames && room.zoneNames[zi]) || ('Zone ' + (zi + 1)));
      }
      // Every zone index went stale (a divider was deleted since the dialog opened).
      // Falling back to the whole space beats emitting a page framed on nothing.
      if (!polys.length) { pages.push({ room, zonePolys: null, rect: null, rotateTurns: turns, label: entry.label || null }); continue; }
      pages.push({ room, zonePolys: polys, rect: null, rotateTurns: turns, label: entry.label || (base + ' — ' + names.join(' + ')) });
    }
    return pages;
  }

  async _generatePerSpacePrintCanvases(qualityLevel = 0) {
    // qualityLevel: 0 = high-res JPEG, 1 = reduced fallback, 2 = skip images
    // Canvas aspect ~1.8 matches PDF image area (1188×666pt) so image fills full width
    // 4800×2667 ≈ 282 DPI on tabloid landscape — sharp equipment detail on large designs
    /* ⚠️ Three levels, not two. With only two, a combo that was still too big at
       level 1 was dropped entirely — which is how a 3-page designer layout vanished
       while the 1-page plain combo passed. 2600x1445 is ~153 DPI on tabloid: a real
       step down, and still far better than the page not existing.
       Keep LEVELS in _renderPrintImagesWithinBudget in step with this array. */
    const CONFIGS = [
      { w: 4800, h: 2667, quality: 0.82 },
      { w: 3600, h: 2000, quality: 0.72 },
      { w: 2600, h: 1445, quality: 0.66 },
    ];
    if (qualityLevel >= CONFIGS.length) return null; // strip images
    const { w: CANVAS_W, h: CANVAS_H, quality: JPEG_Q } = CONFIGS[qualityLevel];
    const MARGIN = 0;
    const LEGEND_H = 100;
    // CUSTOM_PROJECTASSEMBLY_BY_CLAUDE — bump padding to 3ft when outside-perimeter wall dim
    // labels are rendering, so labels right against the canvas edge don't get clipped.
    // Matches the seam plan canvas (which uses PAD=3 for the same reason). 1ft otherwise.
    const PAD = this.showMeasurements ? 3 : 1;
    // END CUSTOM_PROJECTASSEMBLY_BY_CLAUDE

    const results = [];
    let spaceIdx = 0;

    // ── Pre-load texture images before rendering ──
    // _getTextureImage() returns null on first call (starts async load, returns
    // the image on subsequent calls). Collect all texture URLs for rooms with
    // flooring and await them so the PDF render gets full textures on first save.
    const overlay = window.roomDesignerOverlay;
    if (overlay) {
      const texUrls = new Set();
      const _collectTex = (productId, colorId) => {
        if (!productId) return;
        const prod = overlay._getProduct(productId);
        if (!prod || !prod.colors) return;
        const col = prod.colors.find(c => c.id === colorId);
        if (col && col.textureUrl) texUrls.add(col.textureUrl);
      };
      for (const room of this.rooms) {
        if (!room.closed || room.type !== 'add') continue;
        if (room.flooring) _collectTex(room.flooring.productId, room.flooring.colorId);
        if (room.zoneFlooring) room.zoneFlooring.forEach(zf => _collectTex(zf.productId, zf.colorId));
      }
      // Turf is stored as objects, not room.flooring
      for (const obj of (this.objects || [])) {
        if (obj.type === 'turf' && obj.turfProductId) _collectTex(obj.turfProductId, obj.turfColorId);
      }
      if (texUrls.size > 0) {
        // Trigger loads (idempotent — _getTextureImage caches)
        for (const url of texUrls) overlay._getTextureImage(url);
        // Wait for all to finish (loaded or failed), max 5s timeout
        await Promise.race([
          Promise.all([...texUrls].map(url => new Promise(resolve => {
            const check = () => {
              const e = overlay._textureImages[url];
              if (e && (e.loaded || e.failed)) return resolve();
              setTimeout(check, 50);
            };
            check();
          }))),
          new Promise(resolve => setTimeout(resolve, 5000))
        ]);
      }
    }

    // ── Pre-build equipment silhouette canvases synchronously ──
    // _ensureEquipmentCanvas('sil') defers silhouette generation via setTimeout
    // and returns null on first call. During interactive renders this is fine
    // (re-render fires after build), but our print render is a synchronous
    // one-shot — the setTimeout would fire AFTER the canvas is already exported
    // to JPEG, leaving equipment as wireframe outlines with no color fill.
    // Force-build all silhouettes now so the print render has them available.
    if (this._equipmentImages) {
      for (const id in this._equipmentImages) {
        const entry = this._equipmentImages[id];
        if (!entry || !entry.loaded) continue;
        if (!entry.silCanvas) {
          this._buildSilhouetteSync(entry);
        }
      }
    }

    for (const page of this._buildPrintPages()) {
      const room = page.room;
      spaceIdx++;
      const spaceName = page.label || room.name || `Space ${spaceIdx}`;

      const geo = this._getRoomGeoCache(room);
      const roomPoly = geo.roomPolygon;

      /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — a zone page is framed on its zones and
         nothing else. The box is the selected zone polygons plus the same PAD every
         page gets, and the aspect-fit below then guarantees Ryan's rule — AT LEAST
         that box makes the page. It will almost always be more: the box has to be
         fitted into a fixed sheet aspect, so whichever axis is not the binding one
         gains real content on both sides. That is expected, not slop.

         Note it does NOT take in the space's walls, doors, equipment or measurement
         labels the way a whole-space page does. Widening the box to reach a machine
         half outside the zone would quietly break the one guarantee the feature
         makes about where the frame sits.

         The page still RENDERS the whole space — _printFocusRoom is the room, not
         the zone — so the surrounding content that lands inside the frame is real
         drawing rather than white space. This is a camera, not a crop. */
      const points = [];
      if (page.rect) {
        for (const p of page.rect) points.push(p);
      } else if (page.zonePolys) {
        for (const poly of page.zonePolys) for (const p of poly) points.push(p);
      } else {
      // Compute bounding box from room walls + doors + equipment inside
      for (const w of room.walls) { points.push(w.start); points.push(w.end); }
      // Include door swing arc tips so they don't get clipped
      if (room.doors) {
        for (const door of room.doors) {
          if (!door.wall || !door.swing) continue;
          const wall = door.wall;
          const hingePoint = this.paramToPoint(wall, door.hingeParam);
          const endPoint = this.paramToPoint(wall, door.endParam);
          const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
          const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
          if (wLen === 0) continue;
          const perpX = -wdy / wLen, perpY = wdx / wLen;
          const doorWidthFt = door.width / 12;
          const s = door.swing;
          if (door.type === 'double') {
            const midX = (hingePoint.x + endPoint.x) / 2, midY = (hingePoint.y + endPoint.y) / 2;
            points.push({ x: midX + perpX * s.perpSign * (doorWidthFt / 2), y: midY + perpY * s.perpSign * (doorWidthFt / 2) });
          } else {
            const pivot = s.hingeEnd === 'hinge' ? hingePoint : endPoint;
            points.push({ x: pivot.x + perpX * s.perpSign * doorWidthFt, y: pivot.y + perpY * s.perpSign * doorWidthFt });
          }
        }
      }
      if (roomPoly && roomPoly.length >= 3) {
        for (const obj of this.objects) {
          if (obj.type !== 'equipment') continue;
          if (!this.pointInPolygon(obj.x, obj.y, roomPoly)) continue;
          const hw = obj.width / 2, hd = obj.depth / 2;
          const rot = obj.rotation || 0;
          for (const [lx, ly] of [[-hw, -hd], [hw, -hd], [hw, hd], [-hw, hd]]) {
            points.push(this._rotatePoint(obj.x + lx, obj.y + ly, obj.x, obj.y, rot));
          }
        }
      }
      // Include measurement line endpoints so they aren't clipped from the frame
      if ((this.showMeasurements || this._measurementsVisible) && this._measurements && this._measurements.length > 0) {
        for (const m of this._measurements) {
          const mPts = [m.start, m.end];
          const offset = m.offset || 0;
          if (Math.abs(offset) > 0.01) {
            const dx = m.end.x - m.start.x, dy = m.end.y - m.start.y;
            const len = Math.sqrt(dx * dx + dy * dy);
            if (len > 0.01) {
              const nx = -dy / len, ny = dx / len;
              mPts.push({ x: m.start.x + nx * offset, y: m.start.y + ny * offset });
              mPts.push({ x: m.end.x + nx * offset, y: m.end.y + ny * offset });
            }
          }
          // Include if at least one endpoint is inside/near this room
          let near = false;
          if (roomPoly && roomPoly.length >= 3) {
            for (const p of mPts) {
              if (this.pointInPolygon(p.x, p.y, roomPoly)) { near = true; break; }
            }
          }
          // If no endpoint inside, check proximity — within 10 ft of room bounds
          if (!near && roomPoly && roomPoly.length >= 3) {
            const rmMinX = Math.min(...roomPoly.map(p => p.x)), rmMaxX = Math.max(...roomPoly.map(p => p.x));
            const rmMinY = Math.min(...roomPoly.map(p => p.y)), rmMaxY = Math.max(...roomPoly.map(p => p.y));
            for (const p of mPts) {
              if (p.x >= rmMinX - 10 && p.x <= rmMaxX + 10 && p.y >= rmMinY - 10 && p.y <= rmMaxY + 10) { near = true; break; }
            }
          }
          if (near) {
            // Add all points with 2ft padding for label pills
            for (const p of mPts) {
              points.push({ x: p.x - 2, y: p.y - 2 });
              points.push({ x: p.x + 2, y: p.y + 2 });
            }
          }
        }
      }

      } // end whole-space point collection

      const drawAreaW = CANVAS_W - 2 * MARGIN;
      const drawAreaH = CANVAS_H - 2 * MARGIN - LEGEND_H;
      const canvasAspect = CANVAS_W / CANVAS_H;

      /* ONE definition of how a set of world points becomes a sheet fit. It is a
         closure rather than four inlined copies because it is now called TWICE per
         page — once to get a scale the area labels can be sized against, and once
         with those labels included — and two copies of a fit is how a page ends up
         framed differently from the frame it was measured for. */
      const fitFor = (pts) => {
        if (!pts.length) return null;
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const p of pts) {
          minX = Math.min(minX, p.x); minY = Math.min(minY, p.y);
          maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y);
        }
        minX -= PAD; minY -= PAD; maxX += PAD; maxY += PAD;
        const contentW = maxX - minX, contentH = maxY - minY;
        if (contentW <= 0 || contentH <= 0) return null;

        // Auto-rotate portrait content to landscape for better fit
        const autoRotate = contentH > contentW * 1.15 && canvasAspect > 1;
        /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — the auto-fit is a HEURISTIC, and it needed an
           override. It turns a portrait page a quarter turn to fill a landscape sheet,
           which is right for the drawing and wrong for everything on it that has a
           fixed up: a turf logo, and area labels, which are deliberately glued to the
           plan. page.rotateTurns is that override — null means let the heuristic
           decide, 0-3 means exactly this many quarter turns and nothing else.
           Counter-rotating the logo instead was considered and rejected: a logo is
           painted on a real floor at a real angle, so holding it upright would make the
           page disagree with the room. */
        const turns = (page.rotateTurns == null)
          ? (autoRotate ? 1 : 0)
          : ((page.rotateTurns % 4) + 4) % 4;
        const needsRotation = turns % 2 === 1;   // only quarter turns swap the axes
        // If rotating, the content effectively swaps W/H for fitting
        const fitW = needsRotation ? contentH : contentW;
        const fitH = needsRotation ? contentW : contentH;

        const scaleX = drawAreaW / (fitW * this.basePixelsPerFoot);
        const scaleY = drawAreaH / (fitH * this.basePixelsPerFoot);
        return { minX, minY, maxX, maxY, turns, printZoom: Math.min(scaleX, scaleY) };
      };

      /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — a whole-space page's frame takes in the area
         labels near it, so a label placed just OUTSIDE a wall to point at something
         is not cropped off the sheet it belongs to. Same treatment the measurement
         labels already get, and the same proximity rule (inside the room, or within
         10ft of its bounds) so a label belonging to a distant space cannot drag this
         page's frame across the drawing.

         ⚠️ Deliberately NOT applied to zone or close-up pages. Those frame on their
         zones or their rectangle and nothing else, and that is the guarantee the
         feature makes about where the frame sits — a stray label must not widen it.

         ⚠️ And what is reserved is the label's PRINTED footprint, not obj.width /
         obj.depth. Those are world feet, which is what the label measures on screen;
         in print it is sized in sheet units (see _printLabelFontPx), so on a large
         plan the printed pill is several times its world box and reserving the world
         box would crop exactly the labels this block exists to protect. The provisional
         fit below is what converts sheet units back into world feet. Adding the labels
         can only zoom the page out a little further, which makes the reservation
         slightly generous rather than slightly short — the safe direction. */
      const preFit = fitFor(points);
      if (!preFit) continue;
      if (!page.zonePolys && !page.rect && this.showEquipmentLabels) {
        const preScale = this.basePixelsPerFoot * preFit.printZoom;
        let rMinX = Infinity, rMinY = Infinity, rMaxX = -Infinity, rMaxY = -Infinity;
        for (const w of room.walls) {
          rMinX = Math.min(rMinX, w.start.x, w.end.x); rMaxX = Math.max(rMaxX, w.start.x, w.end.x);
          rMinY = Math.min(rMinY, w.start.y, w.end.y); rMaxY = Math.max(rMaxY, w.start.y, w.end.y);
        }
        for (const obj of this.objects) {
          if (obj.type !== 'label' || !obj.text) continue;
          const inside = roomPoly && roomPoly.length >= 3 && this.pointInPolygon(obj.x, obj.y, roomPoly);
          const near = obj.x >= rMinX - 10 && obj.x <= rMaxX + 10 && obj.y >= rMinY - 10 && obj.y <= rMaxY + 10;
          if (!inside && !near) continue;
          if (!obj.width || !obj.depth) this._measureLabelBox(obj);
          // World box → printed box: the ratio of the two font sizes, since every
          // dimension of the pill is a multiple of its font.
          const worldFt = this._labelFontSize(obj.size);
          const printFt = (preScale > 0) ? (this._printLabelFontPx(obj.size, CANVAS_H) / preScale) : worldFt;
          const k = (worldFt > 0) ? Math.max(1, printFt / worldFt) : 1;
          const hw = (obj.width || 0) * k / 2, hd = (obj.depth || 0) * k / 2;
          // obj.rotation is a SCREEN angle; on a page turned `turns` quarter-turns the printed
          // pill's world angle is that plus the turn back (printRotRad = -turns * PI/2, and world
          // = screen - printRotRad). Reserving it at obj.rotation alone swapped the box's axes
          // on every rotated page, which is exactly the page the reservation exists for.
          const rot = (obj.rotation || 0) + preFit.turns * Math.PI / 2;
          for (const [lx, ly] of [[-hw, -hd], [hw, -hd], [hw, hd], [-hw, hd]]) {
            points.push(this._rotatePoint(obj.x + lx, obj.y + ly, obj.x, obj.y, rot));
          }
        }
      }

      const fit = fitFor(points);
      if (!fit) continue;
      const { minX, minY, maxX, maxY, turns, printZoom } = fit;
      const centerX = (minX + maxX) / 2, centerY = (minY + maxY) / 2;
      /* The transform chain is T(pan) * R(a) * S(scale), so:
             sx = panX + s*(wx*cos a - wy*sin a)
             sy = panY + s*(wx*sin a + wy*cos a)
         and the pan that puts the content centre on the sheet centre falls straight
         out of it. This replaces a hard-coded pair of cases that only knew 0 and
         -90°; it reproduces both exactly (at a = -π/2, cos = 0 and sin = -1, giving
         panX = tX - s*cy and panY = tY + s*cx — the old numbers) and now also handles
         the half and three-quarter turns the per-page override can ask for. */
      const printRotRad = -turns * Math.PI / 2;
      const cosA = Math.cos(printRotRad), sinA = Math.sin(printRotRad);
      const sPx = this.basePixelsPerFoot * printZoom;
      const printPanX = MARGIN + drawAreaW / 2 - sPx * (centerX * cosA - centerY * sinA);
      const printPanY = MARGIN + drawAreaH / 2 - sPx * (centerX * sinA + centerY * cosA);

      // Save state
      const saved = {
        ctx: this.ctx, canvas: this.canvas, panX: this.panX, panY: this.panY,
        zoomLevel: this.zoomLevel, darkMode: this.darkMode, _tc: this._tc,
        _cssWidth: this._cssWidth, _cssHeight: this._cssHeight, _dpr: this._dpr,
        showGrid: this.showGrid, _printMode: this._printMode,
        _drawRoomOverlayHook: this._drawRoomOverlayHook,
        _beforeRoomsHook: this._beforeRoomsHook,
        _afterRoomsHook: this._afterRoomsHook,
        _afterWallSegmentsHook: this._afterWallSegmentsHook,
        _afterObjectsHook: this._afterObjectsHook,
        _drawBackgroundHook: this._drawBackgroundHook,
        _selectOpen: this._selectOpen,
        _popoverActive: this._popoverActive,
        _renderFrame: this._renderFrame,
        _floorDimObjects: this._floorDimObjects,
        showMeasurements: this.showMeasurements,
        showRoomLabels: this.showRoomLabels,
        _canvasRotationRad: this._canvasRotationRad,
        _floorMode: this._floorMode,
        showSeamLines: this.showSeamLines,
        hollowWalls: this.hollowWalls,
        _viewOnly: this._viewOnly,
        _viewOnlyShowFloor: this._viewOnlyShowFloor,
        showMaterialOverlay: this.showMaterialOverlay,
        _overlayDetailSuppressed: this._overlayDetailSuppressed,
        _printSeamAlpha: this._printSeamAlpha,
      };

      // Force overlay state for print — save/restore all overlay-critical properties
      const savedOverlayAlpha = overlay ? overlay._overlayDetailAlpha : null;
      const savedOverlayTarget = overlay ? overlay._overlayDetailTarget : null;
      const savedOverlayShow = overlay ? overlay.showOverlay : null;

      try {
        this._canvasRotationRad = printRotRad;
        this._floorMode = false;
        this._applyPrintSeamRule(overlay);
        this.hollowWalls = false;
        this._viewOnly = true;
        this._viewOnlyShowFloor = true;
        this._printMode = true;
        this.showMaterialOverlay = true;
        this._overlayDetailSuppressed = false;
        if (overlay) {
          overlay.showOverlay = true;
          overlay._overlayDetailAlpha = 1;
          overlay._overlayDetailTarget = 1;
          clearTimeout(overlay._overlayDetailTimer);
          clearTimeout(overlay._overlayDetailFadeTimer);
          overlay._overlayDetailTimer = null;
          overlay._overlayDetailFadeTimer = null;
        }
        const offscreen = document.createElement('canvas');
        offscreen.width = CANVAS_W;
        offscreen.height = CANVAS_H;
        const ctx = offscreen.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

        this.canvas = offscreen;
        this.ctx = ctx;
        this._dpr = 1;
        this._cssWidth = CANVAS_W;
        this._cssHeight = CANVAS_H;
        this.panX = printPanX;
        this.panY = printPanY;
        this.zoomLevel = printZoom;
        // CUSTOM_ROOMDESIGNER_BY_CLAUDE — labels keep the look they were AUTHORED in.
        // Print swaps the whole scheme for paper, which used to flip every label
        // from dark-with-white-text to white-with-dark-text on export.
        // _labelColors() reads this instead of darkMode; cleared on restore below.
        this._labelAuthorDark = this.darkMode;
        this.darkMode = false;
        this.showGrid = false;
        this.showMeasurements = saved.showMeasurements;
        this.showRoomLabels = false;
        this._printMode = true;
        this._selectOpen = false;
        this._popoverActive = false;
        this._renderFrame = null;
        this._floorDimObjects = false;
        this._printFocusRoom = room;

        this._tc = {
          wallColor: '#000000',
          gridMajor: 'rgba(0,0,0,0.06)',
          gridMinor: 'rgba(0,0,0,0.03)',
          objectStroke: '#000000',
          objectFill: '#EAEAEA',
          labelBg: '#EAEAEA',
          labelBorder: 'rgba(0,0,0,0.12)',
          labelText: '#333333',
          roomLabelText: '#000000',
          previewFill: 'rgba(255,255,255,0.8)',
          roomFill: 'rgba(0,0,0,0.015)',
          roomFillAlpha: 0.015,
          roomFlooredFill: 'rgba(0,0,0,0.04)',
          subtractFill: 'rgba(0,0,0,0.03)',
          roomGridDim: 'rgba(255,255,255,0.6)',
        };

        // Keep _drawRoomOverlayHook so flooring renders in PDF
        this._beforeRoomsHook = null;
        this._afterRoomsHook = null;
        this._afterWallSegmentsHook = null;
        this._afterObjectsHook = null;
        this._drawBackgroundHook = null;

        this._doRender();

        // Scale bar
        this._drawPrintScaleBar(ctx, MARGIN, CANVAS_H - MARGIN - LEGEND_H + 20, printZoom);

        const dataUrl = offscreen.toDataURL('image/jpeg', JPEG_Q);
        /* CUSTOM_PROJECTASSEMBLY_BY_CLAUDE (Ryan 2026-09-23) — the quarter turns this page
           printed with ride along with the image. The packet's "CLICK FOR INTERACTIVE LAYOUT"
           opens the plan in presentation mode, and Ryan wants it pre-rotated to the orientation
           the PDF shows; the turn is decided HERE (the auto-fit heuristic or the page's own
           rotateTurns) and nowhere else, so this is the one place that can say what it was. */
        results.push({ spaceName, dataUrl, spaceIndex: spaceIdx, rotateTurns: turns });
      } finally {
        this.ctx = saved.ctx;
        this.canvas = saved.canvas;
        this.panX = saved.panX;
        this.panY = saved.panY;
        this.zoomLevel = saved.zoomLevel;
        this.darkMode = saved.darkMode;
        this._labelAuthorDark = null;
        this._tc = saved._tc;
        this._cssWidth = saved._cssWidth;
        this._cssHeight = saved._cssHeight;
        this._dpr = saved._dpr;
        this.showGrid = saved.showGrid;
        this._printMode = saved._printMode;
        this._drawRoomOverlayHook = saved._drawRoomOverlayHook;
        this._beforeRoomsHook = saved._beforeRoomsHook;
        this._afterRoomsHook = saved._afterRoomsHook;
        this._afterWallSegmentsHook = saved._afterWallSegmentsHook;
        this._afterObjectsHook = saved._afterObjectsHook;
        this._drawBackgroundHook = saved._drawBackgroundHook;
        this._selectOpen = saved._selectOpen;
        this._popoverActive = saved._popoverActive;
        this._renderFrame = saved._renderFrame;
        this._floorDimObjects = saved._floorDimObjects;
        this.showMeasurements = saved.showMeasurements;
        this.showRoomLabels = saved.showRoomLabels;
        this._canvasRotationRad = saved._canvasRotationRad;
        this._floorMode = saved._floorMode;
        this.showSeamLines = saved.showSeamLines;
        this.hollowWalls = saved.hollowWalls;
        this._viewOnly = saved._viewOnly;
        this._viewOnlyShowFloor = saved._viewOnlyShowFloor;
        this.showMaterialOverlay = saved.showMaterialOverlay;
        this._overlayDetailSuppressed = saved._overlayDetailSuppressed;
        this._printSeamAlpha = saved._printSeamAlpha;
        this._printFocusRoom = null;
        if (overlay) {
          overlay._overlayDetailAlpha = savedOverlayAlpha;
          overlay._overlayDetailTarget = savedOverlayTarget;
          overlay.showOverlay = savedOverlayShow;
        }
      }
    }

    return results.length > 0 ? results : null;
  }

  /** Real image bytes behind a data URL. ⚠️ NOT dataUrl.length — that is base64,
   *  which is 4/3 of the bytes it encodes. Every size guard here used to measure
   *  the string, so the old "5MB ceiling" was really 3.75MB of image, and nothing
   *  anywhere said so. */
  _dataUrlBytes(dataUrl) {
    const s = String(dataUrl || '');
    const comma = s.indexOf(',');
    if (comma < 0) return 0;
    const chars = s.length - comma - 1;
    const pad = s.endsWith('==') ? 2 : (s.endsWith('=') ? 1 : 0);
    return Math.max(0, Math.floor(chars * 3 / 4) - pad);
  }

  /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — render the pages at the best quality that fits.
     ⚠️ ONE definition. This existed twice, in _uploadSpaceImages and in the overlay's
     quote handoff, already the shape that turned the seam rule into three answers to
     one question — and the two copies could disagree about whether a design was
     exportable at all.

     What the budget is actually protecting, since the old one protected nothing:
     each image is POSTed to /upload as its OWN request (worker limit 10MB), so a
     PER-IMAGE cap is the real constraint; the total only bounds the PDF the packet
     later builds from them. An aggregate cap alone means "the more pages you ask
     for, the less quality each page may have", which is backwards for a multi-page
     document — and it is exactly how a 3-page designer layout got dropped while the
     1-page plain combo sailed through, silently, leaving the packet to fall back to
     a layout the rep did not ask for.

     ⚠️ Degrade before giving up. There is now a third, smaller config, so a big
     multi-page combo loses resolution instead of vanishing. Returning nothing is the
     LAST resort, not the second. */
  async _renderPrintImagesWithinBudget(note) {
    note = note || {};
    const TOTAL_MAX = 8 * 1024 * 1024;      // real bytes, across the whole document
    const PER_IMAGE_MAX = 6 * 1024 * 1024;  // real bytes, one upload request
    const mb = (n) => (n / (1024 * 1024)).toFixed(1) + 'MB';
    const LEVELS = 3;                        // keep in step with CONFIGS above
    let firstTotal = 0, lastTotal = 0, lastPages = 0, lastBiggest = 0;

    for (let q = 0; q < LEVELS; q++) {
      const imgs = await this._generatePerSpacePrintCanvases(q);
      if (!imgs || !imgs.length) {
        note.reason = (q === 0)
          ? 'no printable pages — the page plan resolved to nothing'
          : 'the reduced-quality re-render produced no pages';
        return null;
      }
      let total = 0, biggest = 0;
      for (const img of imgs) {
        const b = this._dataUrlBytes(img.dataUrl);
        total += b;
        if (b > biggest) biggest = b;
      }
      if (q === 0) firstTotal = total;
      lastTotal = total; lastPages = imgs.length; lastBiggest = biggest;
      if (total <= TOTAL_MAX && biggest <= PER_IMAGE_MAX) {
        note.quality = q;
        note.bytes = total;
        note.pages = imgs.length;
        return imgs;
      }
    }
    note.reason = 'too large at every quality — ' + lastPages + ' page(s), ' +
      mb(firstTotal) + ' at full quality, ' + mb(lastTotal) + ' at the lowest' +
      (lastBiggest > PER_IMAGE_MAX ? ' (largest single page ' + mb(lastBiggest) + ')' : '');
    return null;
  }

  _drawMeasurementLines(scale) {
    if (!this._measurements || this._measurements.length === 0) return;
    const ctx = this.ctx;
    // Per-space print focus: only draw measurements near the focus room's walls.
    // pointInPolygon is unreliable for points ON the boundary (wall corners), so
    // we check proximity to wall segments instead — 0.5 ft epsilon covers snapped endpoints.
    const _mFocusRoom = this._printFocusRoom || null;
    const _mFocusWalls = _mFocusRoom ? _mFocusRoom.walls : null;
    const _mPointNearRoom = _mFocusWalls ? (px, py) => {
      const EPS = 0.5; // ft
      for (const w of _mFocusWalls) {
        const ax = w.start.x, ay = w.start.y, bx = w.end.x, by = w.end.y;
        const abx = bx - ax, aby = by - ay;
        const len2 = abx * abx + aby * aby;
        if (len2 < 1e-8) { // degenerate wall
          if ((px - ax) * (px - ax) + (py - ay) * (py - ay) <= EPS * EPS) return true;
          continue;
        }
        const t = Math.max(0, Math.min(1, ((px - ax) * abx + (py - ay) * aby) / len2));
        const cx = ax + t * abx, cy = ay + t * aby;
        if ((px - cx) * (px - cx) + (py - cy) * (py - cy) <= EPS * EPS) return true;
      }
      return false;
    } : null;
    // In print mode, use fixed pixel sizes on the output canvas so measurement
    // lines look consistent regardless of room size. On screen, scale normally.
    const _drawArrowhead = (tipX, tipY, angle, sz, color) => {
      ctx.save();
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(tipX, tipY);
      ctx.lineTo(tipX - sz * Math.cos(angle - 0.4), tipY - sz * Math.sin(angle - 0.4));
      ctx.lineTo(tipX - sz * Math.cos(angle + 0.4), tipY - sz * Math.sin(angle + 0.4));
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    };
    const color = this.darkMode ? '#8AB4D6' : '#5A7A9B';
    const lw = (this._printMode ? 2 : 0.8) / scale;
    const arrowSize = (this._printMode ? 16 : 8) / scale;
    const extOvershoot = (this._printMode ? 12 : 6) / scale;
    for (const m of this._measurements) {
      // Per-space print: skip measurements not touching the focus room
      if (_mPointNearRoom) {
        if (!_mPointNearRoom(m.start.x, m.start.y) &&
            !_mPointNearRoom(m.end.x, m.end.y)) continue;
      }
      ctx.save();
      const dx = m.end.x - m.start.x, dy = m.end.y - m.start.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      let dimStart, dimEnd;
      const offset = m.offset || 0;
      const absOffset = Math.abs(offset);
      if (absOffset > 0.01 && len > 0.01) {
        const nx = -dy / len, ny = dx / len;
        dimStart = { x: m.start.x + nx * offset, y: m.start.y + ny * offset };
        dimEnd = { x: m.end.x + nx * offset, y: m.end.y + ny * offset };
        if (absOffset >= 1) {
          ctx.strokeStyle = color; ctx.lineWidth = lw; ctx.globalAlpha = 0.6; ctx.setLineDash([]);
          const extDir = offset > 0 ? 1 : -1;
          ctx.beginPath();
          ctx.moveTo(m.start.x + nx * extDir * (2 / scale), m.start.y + ny * extDir * (2 / scale));
          ctx.lineTo(dimStart.x + nx * extDir * extOvershoot, dimStart.y + ny * extDir * extOvershoot);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(m.end.x + nx * extDir * (2 / scale), m.end.y + ny * extDir * (2 / scale));
          ctx.lineTo(dimEnd.x + nx * extDir * extOvershoot, dimEnd.y + ny * extDir * extOvershoot);
          ctx.stroke();
          ctx.globalAlpha = 1.0;
        }
      } else {
        dimStart = m.start; dimEnd = m.end;
      }
      ctx.strokeStyle = color; ctx.lineWidth = lw; ctx.setLineDash([]);
      ctx.beginPath(); ctx.moveTo(dimStart.x, dimStart.y); ctx.lineTo(dimEnd.x, dimEnd.y); ctx.stroke();
      if (len > 0.01) {
        const ux = dx / len, uy = dy / len;
        _drawArrowhead(dimStart.x, dimStart.y, Math.atan2(-uy, -ux), arrowSize, color);
        _drawArrowhead(dimEnd.x, dimEnd.y, Math.atan2(uy, ux), arrowSize, color);
      }
      if (m.dist != null) {
        let mx = (dimStart.x + dimEnd.x) / 2, my = (dimStart.y + dimEnd.y) / 2;
        if (m.labelPos) { mx = m.labelPos.x; my = m.labelPos.y; }
        const text = this.formatMeasurement(m.dist);
        // In print mode, use a fixed pixel size on the output canvas so labels
        // are consistent regardless of room size (small rooms vs large rooms).
        const fontSize = this._printMode ? 36 / scale : 12 / scale;
        ctx.font = '600 ' + fontSize + 'px Inter, system-ui, sans-serif';
        const tw = ctx.measureText(text).width;
        const pad = this._printMode ? 8 / scale : 4 / scale;
        const rr = this._printMode ? 6 / scale : 3 / scale;
        // In print mode, counter-rotate the label so it's always right-side up
        // on the output PDF (canvas may be rotated -90° for portrait rooms).
        const labelRot = this._printMode && this._canvasRotationRad ? -this._canvasRotationRad : 0;
        ctx.save();
        ctx.translate(mx, my);
        if (labelRot) ctx.rotate(labelRot);
        const bx = -tw / 2 - pad, by = -fontSize / 2 - pad;
        const bw = tw + pad * 2, bh = fontSize + pad * 2;
        ctx.fillStyle = 'rgba(255,255,255,0.92)';
        ctx.beginPath();
        ctx.moveTo(bx + rr, by); ctx.lineTo(bx + bw - rr, by);
        ctx.quadraticCurveTo(bx + bw, by, bx + bw, by + rr);
        ctx.lineTo(bx + bw, by + bh - rr);
        ctx.quadraticCurveTo(bx + bw, by + bh, bx + bw - rr, by + bh);
        ctx.lineTo(bx + rr, by + bh);
        ctx.quadraticCurveTo(bx, by + bh, bx, by + bh - rr);
        ctx.lineTo(bx, by + rr);
        ctx.quadraticCurveTo(bx, by, bx + rr, by);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = color;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(text, 0, 0);
        ctx.restore();
      }
      ctx.restore();
    }
  }

  _drawPrintScaleBar(ctx, x, y, printZoom) {
    const pxPerFoot = this.basePixelsPerFoot * printZoom;
    /* CUSTOM_PROJECTASSEMBLY_BY_CLAUDE (Ryan 2026-09-23) — *"The scale on the PDF for floor
       plans is too tiny to read … make the line 2-3x longer and then change the label to read
       4' or 6' in a larger font."* The bar used to be the first round length that fit 100-300px
       of a 4800px canvas (a 2 ft nub at 80px/ft) under a 32px label — about 9pt on the printed
       tabloid. It now aims at ~9% of the canvas width (a 6 ft bar on that same plan), the label
       is sized off the canvas height so it prints at ~17pt, and the tick and stroke grow with
       it. The candidates gained 3, 4, 6, 8 and 12 so the rounder lengths Ryan named are
       reachable rather than jumping 2 → 5 → 10. */
    const canvasW = this._cssWidth || 4800;
    const targetPx = canvasW * 0.09;
    let barFeet = 1;
    const candidates = [1, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30, 40, 50];
    for (const f of candidates) {
      barFeet = f;
      if (f * pxPerFoot >= targetPx) break;
    }
    const barW = barFeet * pxPerFoot;
    const labelPx = Math.max(32, Math.round((this._cssHeight || 2667) * 0.022));
    const tickH = Math.round(labelPx * 0.4);

    // Clean minimal scale bar — single line with end ticks
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = Math.max(2, Math.round(labelPx / 16));
    ctx.beginPath();
    // Horizontal line
    ctx.moveTo(x, y); ctx.lineTo(x + barW, y);
    // Left tick
    ctx.moveTo(x, y - tickH / 2); ctx.lineTo(x, y + tickH / 2);
    // Right tick
    ctx.moveTo(x + barW, y - tickH / 2); ctx.lineTo(x + barW, y + tickH / 2);
    ctx.stroke();

    // Label — centered below bar
    ctx.font = '600 ' + labelPx + 'px Arial';
    ctx.fillStyle = '#000000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(`${barFeet} ft`, x + barW / 2, y + tickH / 2 + 4);

    ctx.textAlign = 'left';
  }

  _drawPrintContactInfo(ctx, rightX, y) {
    ctx.textAlign = 'right';
    ctx.textBaseline = 'top';

    ctx.font = '600 18px Arial';
    ctx.fillStyle = '#000000';
    ctx.fillText('blueskyfitsupply.com', rightX, y);

    ctx.font = '400 16px Arial';
    ctx.fillStyle = '#333333';
    ctx.fillText('info@blueskyfitsupply.com  |  720-650-0055', rightX, y + 24);
  }

  _drawPrintSwatchLegend(ctx, x, y, maxWidth) {
    // Collect unique flooring combos from rooms
    const overlay = window.roomDesignerOverlay;
    if (!overlay) return;
    const combos = [];
    const seen = new Set();
    let spaceIdx = 0;
    for (const room of this.rooms) {
      if (!room.closed || room.type !== 'add') continue;
      spaceIdx++;
      const geo = this._getRoomGeoCache(room);
      const zones = overlay._getZonesForRoom ? overlay._getZonesForRoom(room, geo) : null;
      if (zones && zones.length > 1 && room.zoneFlooring && room.zoneFlooring.length > 0) {
        // Multi-zone room
        for (const zf of room.zoneFlooring) {
          const key = zf.productId + '|' + zf.colorId;
          if (seen.has(key)) continue;
          seen.add(key);
          const product = overlay._getProduct(zf.productId);
          if (!product) continue;
          const color = product.colors.find(c => c.id === zf.colorId);
          combos.push({ product, color, spaceName: room.name || `Space ${spaceIdx}` });
        }
      } else if (room.flooring) {
        const key = room.flooring.productId + '|' + room.flooring.colorId;
        if (seen.has(key)) continue;
        seen.add(key);
        const product = overlay._getProduct(room.flooring.productId);
        if (!product) continue;
        const color = product.colors.find(c => c.id === room.flooring.colorId);
        combos.push({ product, color, spaceName: room.name || `Space ${spaceIdx}` });
      }
    }
    if (combos.length === 0) return;

    const swatchSize = 18;
    const gap = 20;
    let cx = x;
    for (const combo of combos) {
      const label = `${combo.product.name}${combo.color ? ' — ' + combo.color.name : ''}`;
      ctx.font = '400 12px Arial';
      const labelW = ctx.measureText(label).width;
      const itemW = swatchSize + 6 + labelW;

      // Wrap to next line if needed
      if (cx + itemW > x + maxWidth && cx > x) {
        cx = x; y += swatchSize + 8;
      }

      // Draw swatch square
      if (combo.color && combo.color.hex) {
        ctx.fillStyle = combo.color.hex;
      } else {
        ctx.fillStyle = '#EAEAEA';
      }
      ctx.fillRect(cx, y, swatchSize, swatchSize);
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 0.5;
      ctx.strokeRect(cx, y, swatchSize, swatchSize);

      // Label
      ctx.fillStyle = '#333333';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, cx + swatchSize + 6, y + swatchSize / 2);

      cx += itemW + gap;
    }
  }

  // Restart the appropriate pulse based on current draw state
  // Gray out checkmark when rect has no area; active when multi-dimensional
  // Gray out checkmark when freehand wall has zero length; active when it has length
  startDrag(point) {
    for (let room of [...this.rooms, this.currentRoom]) {
      if (!room) continue;
      for (let door of room.doors) {
        const dist = this.getDistance(point, door.position);
        if (dist < 1) { this.selectedElement = { type: 'door', door }; this.isDragging = true; this.dragStart = point; return; }
      }
    }
    for (let room of [...this.rooms, this.currentRoom]) {
      if (!room) continue;
      for (let wall of room.walls) {
        const dist = this.distanceToWall(point, wall);
        if (dist < 0.5) { this.selectedElement = { type: 'wall', wall }; this.isDragging = true; this.dragStart = point; return; }
      }
    }
  }
}

/* ──────────────────────────────────────────
   INITIALIZATION
   ────────────────────────────────────────── */

document.addEventListener('DOMContentLoaded', () => {
  window.roomDesigner = new RoomDesigner();
});